import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, IndianRupee, Calendar, Plus, Search, Filter, X, Mail, Phone, Building, Building2, User, MapPin, Briefcase, Target, Clock, ChevronDown, ChevronUp, FileText, Edit, Edit2, Edit3, Trash2, CheckCircle, Activity, Bell, MessageSquare, Send, Bot, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, AlertCircle, AlertTriangle, CalendarDays, CheckSquare, Square, Upload, Settings, Sparkles, XCircle, RefreshCw, HelpCircle, LogOut, Moon, Sun, Globe, Type, ExternalLink, Layers, Package, FileSpreadsheet, Download, BarChart2, Flame, ArrowRight, Zap, Flag, Tag, Mic, MicOff, Volume2, VolumeX, Ban, PauseCircle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, LabelList, ComposedChart } from 'recharts';
import pierianLogo from '../public/image.png';
import pieLogoFooter from '../public/image-3.png';
import vaultLogo from '../public/vault.png';
import vaultLogoNew from '../public/vault-new.png';
import vaultLogoHeader from '../public/vault-favicon.png';
import AICopilot from './components/AICopilot';
import ICPQualifierWizard from './components/ICPQualifierWizard';
import { ThemeProvider, useTheme } from './theme/ThemeProvider';
import { leadsApi } from './api/apiService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// ========================================
// API BASE URL - Environment Configuration
// ========================================
// Development: defaults to '/api' (relative) so Vite proxy handles routing to backend.
//   This avoids mixed-content blocking when the dev server runs HTTPS.
//   Override with VITE_API_BASE_URL if needed (e.g., http://localhost:5024/api for local HTTP dev).
// Production: VITE_API_PRODUCTION_PATH (e.g., /dev-pieragsalesconnect/api for nginx proxy)
const _envApiUrl = import.meta.env.MODE === 'production'
  ? import.meta.env.VITE_API_PRODUCTION_PATH || '/dev-pieragsalesconnect/api'
  : import.meta.env.VITE_API_BASE_URL || '/api';

// Prevent mixed-content blocking: if page is HTTPS but API URL is HTTP, use relative path
const API_BASE_URL = (window.location.protocol === 'https:' && _envApiUrl?.startsWith('http://'))
  ? '/api'
  : _envApiUrl;

// SSO Service URL for direct frontend calls
// Production: https://appstore.pierianservices.com
// Development: Can use same URL or local SSO service
const SSO_SERVICE_URL = import.meta.env.VITE_SSO_SERVICE_URL
  || 'https://appstore.pierianservices.com';

console.log('SSO Service URL:', SSO_SERVICE_URL);

// Import SheetJS for Excel processing
// SheetJS is available globally as window.XLSX

// ========================================
// ID GENERATION HELPERS
// ========================================
// Generate opportunity ID in format: CRM-Opp-MonYY-NN
const generateOppId = (seqNum: number): string => {
  const now = new Date();
  const mon = now.toLocaleString('en-US', { month: 'short' });
  const yr = String(now.getFullYear()).slice(-2);
  return `CRM-Opp-${mon}${yr}-${String(seqNum).padStart(2, '0')}`;
};

// Generate lead ID in format: CRM-Lead-MonYY-NN
const generateLeadId = (seqNum: number): string => {
  const now = new Date();
  const mon = now.toLocaleString('en-US', { month: 'short' });
  const yr = String(now.getFullYear()).slice(-2);
  return `CRM-Lead-${mon}${yr}-${String(seqNum).padStart(2, '0')}`;
};

// Get current month prefix for opportunity IDs
const getOppPrefix = (): string => {
  const now = new Date();
  const mon = now.toLocaleString('en-US', { month: 'short' });
  const yr = String(now.getFullYear()).slice(-2);
  return `CRM-Opp-${mon}${yr}-`;
};

// ========================================
// ENGAGEMENT HISTORY HELPERS
// ========================================
// Format a single engagement history line: "<DD-Mon-YYYY> by <User Name>: <text>"
// If userName is empty, falls back to "<DD-Mon-YYYY> <text>" (legacy format).
// If text already starts with a DD-Mon-YYYY date, that date is used and the leading
// date+space is stripped from text (keeps hand-typed dates working).
const formatEngagementEntry = (text: string, userName?: string, dateOverride?: Date): string => {
  const trimmed = (text || '').trim();
  if (!trimmed) return '';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const d = dateOverride || new Date();
  const dateStr = `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
  // If the text already leads with a DD-Mon-YYYY date, strip it and use that date instead.
  const leadMatch = trimmed.match(/^(\d{1,2}[-\s]+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[-\s]+\d{4})\s+(.*)$/i);
  const actualDate = leadMatch ? leadMatch[1].replace(/\s+/g, '-') : dateStr;
  const body = leadMatch ? leadMatch[3] : trimmed;
  const who = (userName || '').trim();
  return who ? `${actualDate} by ${who}: ${body}` : `${actualDate} ${body}`;
};

// Prepend a new engagement entry to an existing history string (newest on top).
const prependEngagementEntry = (existing: string, text: string, userName?: string, dateOverride?: Date): string => {
  const entry = formatEngagementEntry(text, userName, dateOverride);
  if (!entry) return existing || '';
  return existing ? `${entry}\n${existing}` : entry;
};

// ========================================
// SALES STATUS CALCULATION HELPER
// ========================================
// Rules (only applied when lastEditedDate is NOT null - i.e., after user updates):
// Hot: Final Proposal sent & awaiting client feedback (salesStage = Proposal)
// Warm: From Lead to Proposal Submission & active engagement (activity within 1 month)
// Cold: No activity for more than 1 month (but less than 3 months)
// Inactive: No activity for more than 3 months
//
// For Excel imports: lastEditedDate is null, so return null to preserve imported value

const calculateSalesStatus = (opportunity: { salesStage?: string; lastEditedDate?: string | null; initiationDate?: string; salesStatus?: string; salesStatusOverride?: string | null }): string | null => {
  const { salesStage, lastEditedDate, salesStatus, salesStatusOverride } = opportunity;

  // If lastEditedDate is null (Excel import), don't calculate - return null to preserve imported value
  // The calling code should use salesStatusOverride or salesStatus from the data
  if (!lastEditedDate) {
    return null;
  }

  // Calculate days since last activity (stage change)
  const lastActivity = new Date(lastEditedDate);
  const today = new Date();
  const daysSinceActivity = Math.floor((today.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));

  // Define hot stages (Proposal)
  const hotStages = ['Proposal'];
  const isHotStage = hotStages.includes(salesStage || '');

  // Rule 1: Inactive - No activity for more than 3 months (90 days)
  // This applies to ALL opportunities regardless of stage
  if (daysSinceActivity > 90) {
    return 'Inactive';
  }

  // Rule 2: Cold - No activity for more than 1 month (30 days)
  // This applies to ALL opportunities regardless of stage
  if (daysSinceActivity > 30) {
    return 'Cold';
  }

  // Rule 3: Hot - Final Proposal sent & awaiting client feedback
  // Only if there has been recent activity (within 30 days)
  if (isHotStage) {
    return 'Hot';
  }

  // Rule 4: Warm - From Lead to Proposal Submission & active engagement (within 1 month)
  return 'Warm';
};

// Helper function to get effective sales status (handles null from calculateSalesStatus)
const getEffectiveSalesStatus = (opportunity: { salesStage?: string; lastEditedDate?: string | null; initiationDate?: string; salesStatus?: string; salesStatusOverride?: string | null }): string => {
  // If there's a manual override, use it
  if (opportunity.salesStatusOverride) {
    return opportunity.salesStatusOverride;
  }

  // Try to calculate status (returns null if lastEditedDate is null)
  const calculatedStatus = calculateSalesStatus(opportunity);

  // If calculation returned a value, use it
  if (calculatedStatus) {
    return calculatedStatus;
  }

  // Fall back to existing salesStatus or default to 'Warm'
  return opportunity.salesStatus || 'Warm';
};

// ========================================
// PROBABILITY CALCULATION HELPER
// ========================================
// Converts probability value to midpoint number for display
const sanitizeProbabilityPercent = (value: string | null | undefined): string => {
  if (!value) return '10';

  // Extract numeric value from the string
  const numericValue = parseInt(String(value).replace(/[^0-9]/g, ''), 10);

  // If we can't parse a number, return default
  if (isNaN(numericValue)) return '10';

  // Return the number as string
  return String(numericValue);
};

// Calculates probability based on sales stage, starting at 10%
// Returns { percent: number, label: string, category: string }
// Label is now the midpoint number (e.g., "10", "20", "50") instead of a range
const calculateProbabilityByStage = (salesStage: string): { percent: number; label: string; category: string } => {
  const stageProbabilities: { [key: string]: { percent: number; label: string; category: string } } = {
    'Qualified': { percent: 20, label: '20', category: 'Low' },
    'Discovery': { percent: 30, label: '30', category: 'Low' },
    'Proposal': { percent: 70, label: '70', category: 'High' },
    'Closed-Won': { percent: 90, label: '90', category: 'High' },
    'Closed-Lost': { percent: 0, label: '0', category: 'Lost' },
    'Contract': { percent: 100, label: '100', category: 'Won' }
  };

  return stageProbabilities[salesStage] || { percent: 20, label: '20', category: 'Low' }; // Default matches Qualified
};

// ========================================
// UTILITY COMPONENTS
// ========================================
const Card = ({ children, className = "", ...rest }: any) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${className}`} style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }} {...rest}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", size = "md", disabled = false, className = "" }) => {
  const baseClasses = "font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2";
  const variants = {
    primary: "bg-pierian text-white hover:bg-scarlet focus:ring-pierian",
    secondary: "bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-500",
    success: "bg-sage text-white hover:bg-sage-shade focus:ring-sage",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
  };
  const sizes = {
    sm: "px-2 py-1 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

// Timeline Component for displaying engagement history
const TimelineDisplay = ({ entries, emptyMessage = "No entries yet" }) => {
  if (!entries || entries.trim() === '') {
    return (
      <div className="text-sm text-gray-500 italic py-2">
        {emptyMessage}
      </div>
    );
  }

  // Helper function to extract date from text content (not the timestamp)
  const extractDateFromContent = (content: string): Date | null => {
    const currentYear = new Date().getFullYear();
    const monthNames: { [key: string]: number } = {
      'jan': 0, 'january': 0,
      'feb': 1, 'february': 1,
      'mar': 2, 'march': 2,
      'apr': 3, 'april': 3,
      'may': 4,
      'jun': 5, 'june': 5,
      'jul': 6, 'july': 6,
      'aug': 7, 'august': 7,
      'sep': 8, 'sept': 8, 'september': 8,
      'oct': 9, 'october': 9,
      'nov': 10, 'november': 10,
      'dec': 11, 'december': 11
    };

    // Format: D MMM YYYY or DD MMM YYYY (e.g., "4 Nov 2025", "28 Nov 2025")
    const dMyMatch = content.match(/(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/i);
    if (dMyMatch) {
      const month = monthNames[dMyMatch[2].toLowerCase()];
      return new Date(parseInt(dMyMatch[3]), month, parseInt(dMyMatch[1]));
    }

    // Format: DD-MMM or DD-MMM-YYYY (e.g., "28-Nov", "17-Jun", "28-Nov-2025")
    const ddMmmMatch = content.match(/(\d{1,2})-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)(?:-(\d{4}))?/i);
    if (ddMmmMatch) {
      const month = monthNames[ddMmmMatch[2].toLowerCase()];
      const year = ddMmmMatch[3] ? parseInt(ddMmmMatch[3]) : currentYear;
      return new Date(year, month, parseInt(ddMmmMatch[1]));
    }

    // Format: YYYY-MM-DD (e.g., "2025-01-15")
    const isoMatch = content.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
    }

    // Format: DD-MM-YYYY or DD/MM/YYYY
    const dmyMatch = content.match(/(\d{2})[-\/](\d{2})[-\/](\d{4})/);
    if (dmyMatch) {
      return new Date(parseInt(dmyMatch[3]), parseInt(dmyMatch[2]) - 1, parseInt(dmyMatch[1]));
    }

    // Format: Month DD, YYYY (e.g., "January 15, 2025")
    const monthDdYyyyMatch = content.match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})/i);
    if (monthDdYyyyMatch) {
      const month = monthNames[monthDdYyyyMatch[1].toLowerCase()];
      return new Date(parseInt(monthDdYyyyMatch[3]), month, parseInt(monthDdYyyyMatch[2]));
    }

    return null;
  };

  // Parse entries - split by newlines or semicolons, format: [timestamp] text
  const timelineEntries = entries.split(/[\n;]/)
    .filter(line => line.trim() !== '')
    .map((line, index) => {
      const trimmedLine = line.trim();
      const match = trimmedLine.match(/^\[(.*?)\]\s*(.*)$/);
      if (match) {
        // Extract date from the content text, not the timestamp
        return { timestamp: match[1], text: match[2], id: index, dateObj: extractDateFromContent(match[2]) };
      }
      return { timestamp: '', text: trimmedLine, id: index, dateObj: extractDateFromContent(trimmedLine) };
    })
    .sort((a, b) => {
      // Sort by date in descending order (newest first)
      if (a.dateObj && b.dateObj) {
        return b.dateObj.getTime() - a.dateObj.getTime();
      }
      // Entries with dates come before entries without dates
      if (a.dateObj && !b.dateObj) return -1;
      if (!a.dateObj && b.dateObj) return 1;
      // Keep original order for entries without dates
      return 0;
    });

  // Helper function to format date as "DD-Mon-YY" (e.g., "08-Jan-26")
  const formatDateForDisplay = (dateObj: Date | null): string | null => {
    if (!dateObj) return null;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = dateObj.getDate().toString().padStart(2, '0');
    const year = dateObj.getFullYear().toString().slice(-2);
    return `${day}-${months[dateObj.getMonth()]}-${year}`;
  };

  // Helper function to strip date prefix from text content
  const stripDatePrefix = (text: string): string => {
    // Remove DD-MMM-YYYY or DD-MMM format at start (e.g., "08-Jan-2026 " or "26-Dec ")
    let cleaned = text.replace(/^\d{1,2}-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)(?:-\d{4})?\s+/i, '');
    // Remove D MMM YYYY or DD MMM YYYY format at start (e.g., "8 Jan 2026 " or "26 Dec 2025 ")
    cleaned = cleaned.replace(/^\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\s+/i, '');
    // Remove YYYY-MM-DD format at start
    cleaned = cleaned.replace(/^\d{4}-\d{2}-\d{2}\s+/i, '');
    return cleaned;
  };

  // Group entries by date
  const groupedEntries: { [key: string]: typeof timelineEntries } = {};
  const entriesWithoutDate: typeof timelineEntries = [];

  timelineEntries.forEach(entry => {
    if (entry.dateObj) {
      const dateKey = formatDateForDisplay(entry.dateObj) || 'Unknown';
      if (!groupedEntries[dateKey]) {
        groupedEntries[dateKey] = [];
      }
      groupedEntries[dateKey].push(entry);
    } else {
      entriesWithoutDate.push(entry);
    }
  });

  // Get sorted date keys (newest first)
  const sortedDateKeys = Object.keys(groupedEntries).sort((a, b) => {
    const dateA = groupedEntries[a][0]?.dateObj;
    const dateB = groupedEntries[b][0]?.dateObj;
    if (dateA && dateB) return dateB.getTime() - dateA.getTime();
    return 0;
  });

  return (
    <div className="space-y-3 max-h-60 overflow-y-auto">
      {/* Grouped entries by date */}
      {sortedDateKeys.map(dateKey => (
        <div key={dateKey} className="border-l-2 border-pierian pl-3">
          <div className="text-xs text-pierian font-semibold mb-1.5">{dateKey}</div>
          <div className="space-y-1.5">
            {groupedEntries[dateKey].map((entry) => (
              <div key={entry.id} className="flex gap-2">
                <div className="flex-shrink-0 w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5"></div>
                <div className="text-sm text-gray-700 flex-1" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word'}}>
                  {stripDatePrefix(entry.text)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {/* Entries without dates */}
      {entriesWithoutDate.length > 0 && (
        <div className="pl-3">
          <div className="space-y-1.5">
            {entriesWithoutDate.map((entry) => (
              <div key={entry.id} className="flex gap-2">
                <div className="flex-shrink-0 w-1.5 h-1.5 bg-gray-300 rounded-full mt-1.5"></div>
                <div className="text-sm text-gray-600 flex-1" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word'}}>
                  {entry.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper components for form fields - MUST be defined outside the modal component to prevent recreation on each render
const OppFormDisplayField = ({ label, value, className = '' }) => (
  <div className={className}>
    <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
    <div className="text-sm font-medium text-gray-800">{value || '-'}</div>
  </div>
);

const OppFormEditField = ({ label, children, className = '' }) => {
  const isRequired = label.endsWith(' *');
  const displayLabel = isRequired ? label.slice(0, -2) : label;
  return (
    <div className={className}>
      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
        {displayLabel}{isRequired && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
};

// CSS class constants - defined outside component to avoid recreation
const OPP_FORM_INPUT_CLASS = "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00BBA2] focus:border-[#00BBA2]";
const OPP_FORM_SELECT_CLASS = "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00BBA2] focus:border-[#00BBA2]";

// Opportunity Form Modal Component - Moved out of Button component
// Use React.memo to prevent unnecessary re-renders from parent
const OpportunityFormModal = React.memo(({ selectedOpportunity, setSelectedOpportunity, opportunities, setOpportunities, users, setShowOpportunityModal, geographies, serviceLines, industries, salesStages, serviceOfferings, entities, tags, convertFromLead, setConvertFromLead, currentUser }: any) => {

    // Refs for text inputs to use uncontrolled pattern (prevents cursor jumping)
    const opportunityNameRef = React.useRef<HTMLInputElement>(null);
    const accountRef = React.useRef<HTMLInputElement>(null);
    const primaryContactRef = React.useRef<HTMLInputElement>(null);
    const phoneNumberRef = React.useRef<HTMLInputElement>(null);
    const executiveSponsorRef = React.useRef<HTMLInputElement>(null);
    const natureOfWorkRef = React.useRef<HTMLInputElement>(null);
    const acvRef = React.useRef<HTMLInputElement>(null);
    const tcvRef = React.useRef<HTMLInputElement>(null);
    const solutionScopeRef = React.useRef<HTMLTextAreaElement>(null);

    const [newComment, setNewComment] = useState('');
    const [newEngagementEntry, setNewEngagementEntry] = useState('');
    const [stageRegressed, setStageRegressed] = useState(false); // Track if stage was moved backward
    const [selectedTagNames, setSelectedTagNames] = useState<string[]>([]); // Selected tag names (allows free text)
    const [tagInput, setTagInput] = useState(''); // Current tag input text
    const [showTagDropdown, setShowTagDropdown] = useState(false); // Tag dropdown visibility

    // Generate unique opportunity ID based on max existing ID: CRM-Opp-MonYY-NN
    const getNextOpportunityId = React.useCallback(() => {
      const now = new Date();
      const mon = now.toLocaleString('en-US', { month: 'short' });
      const yr = String(now.getFullYear()).slice(-2);
      const prefix = `CRM-Opp-${mon}${yr}-`;
      const maxNum = opportunities.reduce((max, opp) => {
        const id = opp.opportunityId || '';
        if (!id.startsWith(prefix)) return max;
        const numPart = id.split('-').pop();
        const num = parseInt(numPart, 10);
        return !isNaN(num) && num > max ? num : max;
      }, 0);
      return `${prefix}${String(maxNum + 1).padStart(2, '0')}`;
    }, [opportunities]);

    // Use lazy initialization for useState to compute initial state only once
    const [formData, setFormData] = useState(() => {
      console.log('📝 OpportunityFormModal: useState INITIALIZER running (should only run once!)');
      if (selectedOpportunity) return selectedOpportunity;

      // Calculate initial opportunity ID from local data: CRM-Opp-MonYY-NN
      const now = new Date();
      const mon = now.toLocaleString('en-US', { month: 'short' });
      const yr = String(now.getFullYear()).slice(-2);
      const oppPrefix = `CRM-Opp-${mon}${yr}-`;
      const maxId = opportunities.reduce((max, opp) => {
        const id = opp.opportunityId || '';
        if (!id.startsWith(oppPrefix)) return max;
        const numPart = id.split('-').pop();
        const num = parseInt(numPart, 10);
        return !isNaN(num) && num > max ? num : max;
      }, 0);
      const initialOppId = `${oppPrefix}${String(maxId + 1).padStart(2, '0')}`;

      // Pre-fill from lead data if converting from a lead
      const lead = convertFromLead || {};
      const hasLead = !!convertFromLead;

      // No default values for dropdowns - user must select all values
      // Exception: Stage defaults to "Prospect" for new opportunities
      return {
        sn: opportunities.length + 1,
        opportunityId: initialOppId,
        opportunityName: hasLead ? lead.companyName || '' : '',
        opportunityType: '',
        opportunityCategory: hasLead ? 'New Business' : '',
        opportunityClassification: hasLead ? 'NC, NB' : '',
        geography: hasLead ? lead.geography || '' : '',
        accountOwner: hasLead ? lead.leadOwner || '' : '',
        associatedAccount: hasLead ? lead.companyName || '' : '',
        primaryContact: hasLead ? lead.contactPerson || '' : '',
        primaryContactEmail: hasLead ? lead.email || '' : '',
        phoneNumber: hasLead ? lead.phone || '' : '',
        email: hasLead ? lead.email || '' : '',
        industryVertical: hasLead ? lead.industry || '' : '',
        industrySubVertical: '',
        serviceLine: hasLead ? lead.serviceLine || '' : '',
        serviceOffering: hasLead ? lead.serviceOffering || '' : '',
        entity: hasLead ? lead.entity || '' : '',
        annualContractValue: hasLead ? lead.estimatedRevenue || '' : '',
        totalContractValue: hasLead ? lead.estimatedRevenue || '' : '',
        priorityLevel: hasLead ? lead.priority || '' : '',
        initiationDate: new Date().toISOString().split('T')[0],
        aging: 0,
        estimatedCloseDate: '',
        salesStatus: '',
        salesStage: 'Qualified',
        solutionScope: hasLead ? lead.painPoints || '' : '',
        engagementHistory: hasLead ? prependEngagementEntry(lead.engagementHistory || '', `Qualified from lead ${lead.leadId}`, currentUser?.name) : '',
        leadSupporter: hasLead ? lead.leadSupporter || '' : '',
        solutionArchitect: hasLead ? lead.solutionArchitect || '' : '',
        executiveSponsor: '',
        probabilityToClose: 'Low',
        probabilityPercent: '20%',
        probability: 20,
        probabilityOverride: null,
        salesStatusOverride: null,
        forecastMultiplier: 0.20,
        actualValue: 0,
        probabilityValue: 0,
        comments: hasLead ? lead.comments || '' : '',
        lastEditedDate: new Date().toISOString().split('T')[0],
        leadHistory: hasLead ? lead.engagementHistory || '' : '',
        sourceLeadId: hasLead ? lead.leadId || '' : '',
        city: hasLead ? lead.city || '' : '',
        leadGenerator: hasLead ? lead.leadGenerator || '' : '',
        natureOfWork: '',
        _convertedFromLeadId: hasLead ? lead.leadId : null
      };
    });

    // Get all opportunity stages from master data (sorted by stage_order)
    // This ensures the dropdown always shows valid stages from the database
    const allStagesOrdered = React.useMemo(() => {
      if (salesStages && salesStages.length > 0) {
        // Sort by stageOrder and extract names
        return [...salesStages]
          .filter(s => s.isActive !== false) // Only active stages
          .sort((a, b) => (a.stageOrder || 0) - (b.stageOrder || 0))
          .map(s => s.name);
      }
      // Fallback to default stages if master data not loaded
      return ['Qualified', 'Discovery', 'Proposal', 'Contract', 'Closed-Lost', 'Closed-Won'];
    }, [salesStages]);

    // Get all stages - now allows moving both forward and backward
    const getAllowedStages = (currentStage) => {
      // Return all stages from master data to allow both forward and backward movement
      return allStagesOrdered;
    };

    // Check if a stage change is a regression (moving backward)
    const isStageRegression = (oldStage: string, newStage: string): boolean => {
      const oldIndex = allStagesOrdered.indexOf(oldStage);
      const newIndex = allStagesOrdered.indexOf(newStage);
      // If new stage index is lower (excluding terminal stages), it's a regression
      // Closed-Won, Closed-Lost, and Contract are terminal
      if (oldStage === 'Closed-Won' || oldStage === 'Closed-Lost' || oldStage === 'Contract') return false;
      return newIndex < oldIndex && newIndex >= 0 && oldIndex >= 0;
    };

    // Track if we've already fetched the next ID (to prevent overwriting user input)
    const hasFetchedIdRef = React.useRef(false);

    // For EDIT mode only: Update formData and ref values when selectedOpportunity is provided
    React.useEffect(() => {
      if (selectedOpportunity) {
        // Calculate probability based on sales stage (unless manually overridden)
        const stageProbInfo = calculateProbabilityByStage(selectedOpportunity.salesStage);
        const hasOverride = selectedOpportunity.probabilityOverride !== null &&
                            selectedOpportunity.probabilityOverride !== undefined;

        setFormData({
          ...selectedOpportunity,
          probability: hasOverride
            ? `${selectedOpportunity.probabilityOverride}%`
            : `${stageProbInfo.percent}%`,
          probabilityPercent: hasOverride
            ? `${selectedOpportunity.probabilityOverride}%`
            : `${stageProbInfo.percent}%`,
          probabilityToClose: hasOverride
            ? selectedOpportunity.probabilityToClose
            : stageProbInfo.category,
          forecastMultiplier: hasOverride
            ? selectedOpportunity.probabilityOverride / 100
            : stageProbInfo.percent / 100
        });

        // Update ref values for uncontrolled inputs (critical for edit mode)
        if (opportunityNameRef.current) opportunityNameRef.current.value = selectedOpportunity.opportunityName || '';
        if (accountRef.current) accountRef.current.value = selectedOpportunity.associatedAccount || '';
        if (primaryContactRef.current) primaryContactRef.current.value = selectedOpportunity.primaryContact || '';
        if (phoneNumberRef.current) phoneNumberRef.current.value = selectedOpportunity.phoneNumber || '';
        if (executiveSponsorRef.current) executiveSponsorRef.current.value = selectedOpportunity.executiveSponsor || '';
        if (natureOfWorkRef.current) natureOfWorkRef.current.value = selectedOpportunity.natureOfWork || '';
        if (acvRef.current) acvRef.current.value = selectedOpportunity.annualContractValue || '';
        if (tcvRef.current) tcvRef.current.value = selectedOpportunity.totalContractValue || '';
        if (solutionScopeRef.current) solutionScopeRef.current.value = selectedOpportunity.solutionScope || '';

        // Initialize selected tags from opportunity (use names for free-text support)
        if (selectedOpportunity.tags && selectedOpportunity.tags.length > 0) {
          setSelectedTagNames(selectedOpportunity.tags.map(t => t.name));
        }
      }
    }, [selectedOpportunity]);

    // For NEW mode only: Fetch next ID from database (only updates the ID field, not the whole form)
    React.useEffect(() => {
      // Only run for NEW opportunities (not edit mode) and only once
      if (selectedOpportunity || hasFetchedIdRef.current) {
        return;
      }
      hasFetchedIdRef.current = true;

      const fetchNextId = async () => {
        try {
          const allOppsResponse = await fetch(`${API_BASE_URL}/opportunities`);
          const allOppsResult = await allOppsResponse.json();
          const allOpps = allOppsResult.data || [];

          console.log(`🔍 Fetched ${allOpps.length} opportunities from API`);

          // Parse opportunity_id field (CRM-Opp-MonYY-NN format) to find the highest number for current month
          const apiNow = new Date();
          const apiMon = apiNow.toLocaleString('en-US', { month: 'short' });
          const apiYr = String(apiNow.getFullYear()).slice(-2);
          const apiPrefix = `CRM-Opp-${apiMon}${apiYr}-`;

          const maxOppNumber = allOpps.reduce((max, opp) => {
            const oppId = opp.opportunity_id || '';
            if (!oppId.startsWith(apiPrefix)) return max;
            const numPart = oppId.split('-').pop();
            const num = parseInt(numPart, 10);
            return !isNaN(num) && num > max ? num : max;
          }, 0);

          console.log(`📊 Max opportunity_id found: ${apiPrefix}${String(maxOppNumber).padStart(2, '0')}`);
          console.log(`➡️  Next ID will be: ${apiPrefix}${String(maxOppNumber + 1).padStart(2, '0')}`);

          const nextId = `${apiPrefix}${String(maxOppNumber + 1).padStart(2, '0')}`;

          // ONLY update the opportunityId field - preserve all other user input
          setFormData(prev => ({ ...prev, opportunityId: nextId }));
        } catch (error) {
          console.error('Error fetching next opportunity ID from API:', error);
          // Keep the locally generated ID from useState initialization
        }
      };

      fetchNextId();
    }, [selectedOpportunity]);

    const handleSubmit = async () => {
      // Guard: opportunities can only be created from a qualified lead — block direct creation
      if (!selectedOpportunity && !convertFromLead) {
        alert('Opportunities can only be created by qualifying a lead.\n\nPlease go to the Leads tab, select a lead, and use the "Qualify" or "Convert" action.');
        return;
      }

      // Read values from refs (uncontrolled inputs) for text fields
      const oppName = opportunityNameRef.current?.value?.trim() || '';
      const account = accountRef.current?.value?.trim() || '';
      const acvValue = acvRef.current?.value || '';
      const tcvValue = tcvRef.current?.value || '';
      const primaryContact = primaryContactRef.current?.value?.trim() || '';
      const phoneNumber = phoneNumberRef.current?.value?.trim() || '';
      const executiveSponsor = executiveSponsorRef.current?.value?.trim() || '';
      const natureOfWork = natureOfWorkRef.current?.value?.trim() || '';
      const solutionScope = solutionScopeRef.current?.value || '';

      // Validate required fields - all dropdowns must be selected for new opportunities
      const missingFields = [];
      if (!oppName) missingFields.push('Opportunity Name');
      if (!account) missingFields.push('Account');
      if (acvValue === '' || acvValue === null || acvValue === undefined) missingFields.push('ACV');
      if (!formData.geography) missingFields.push('Geography');
      if (!formData.industryVertical) missingFields.push('Industry');
      if (!formData.opportunityCategory) missingFields.push('Category');
      if (!formData.accountOwner) missingFields.push('Account Owner');
      if (!formData.serviceLine) missingFields.push('Service Line');
      if (!formData.entity) missingFields.push('Entity');

      if (missingFields.length > 0) {
        alert(`Please fill in all required fields:\n• ${missingFields.join('\n• ')}`);
        return;
      }

      const acv = parseFloat(acvValue) || 0;
      const tcv = parseFloat(tcvValue) || acv;
      const probValue = acv * formData.forecastMultiplier;

      // Append new comment with timestamp if provided
      const timestamp = new Date().toLocaleString();
      const updatedComments = newComment.trim()
        ? `${formData.comments || ''}\n[${timestamp}] ${newComment}`.trim()
        : formData.comments;

      // Track probability override in engagement history
      let probabilityChangeEntry = '';
      if (selectedOpportunity) {
        const oldProbOverride = selectedOpportunity.probabilityOverride;
        const newProbOverride = formData.probabilityOverride;

        // Check if probability is being overridden (new override set or override value changed)
        const isNewOverride = newProbOverride !== null && newProbOverride !== undefined;
        const wasOverridden = oldProbOverride !== null && oldProbOverride !== undefined;

        if (isNewOverride && (!wasOverridden || oldProbOverride !== newProbOverride)) {
          // Probability is being overridden - add entry to engagement history
          const updateDate = new Date().toISOString().split('T')[0];
          probabilityChangeEntry = `Probability Overridden with ${newProbOverride}% on ${updateDate}`;
        }
      }

      // Track sales status override in engagement history
      let salesStatusChangeEntry = '';
      if (selectedOpportunity) {
        const oldStatusOverride = selectedOpportunity.salesStatusOverride;
        const newStatusOverride = formData.salesStatusOverride;

        // Check if sales status is being overridden (new override set or override value changed)
        const isNewStatusOverride = newStatusOverride !== null && newStatusOverride !== undefined;
        const wasStatusOverridden = oldStatusOverride !== null && oldStatusOverride !== undefined;

        if (isNewStatusOverride && (!wasStatusOverridden || oldStatusOverride !== newStatusOverride)) {
          // Sales status is being overridden - add entry to engagement history
          const updateDate = new Date().toISOString().split('T')[0];
          salesStatusChangeEntry = `Sales Status Overridden with ${newStatusOverride} on ${updateDate}`;
        }
      }

      // Track ALL stage changes in engagement history
      let stageChangeEntry = '';
      if (selectedOpportunity) {
        const oldStage = selectedOpportunity.salesStage;
        const newStage = formData.salesStage;

        // Only add entry if stage actually changed
        if (oldStage !== newStage) {
          // Format date as "DD-MMM-YYYY" for proper extraction (e.g., "19-Jan-2026")
          const now = new Date();
          const day = now.getDate();
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const month = monthNames[now.getMonth()];
          const year = now.getFullYear();
          const formattedDate = `${day}-${month}-${year}`;

          if (stageRegressed) {
            // Regression - moving backward
            stageChangeEntry = `${formattedDate} ⭐ Stage Regressed: Moved back from "${oldStage}" to "${newStage}"`;
          } else {
            // Progression - moving forward
            stageChangeEntry = `${formattedDate} Stage Changed: Moved from "${oldStage}" to "${newStage}"`;
          }
        }
      }

      // Append new engagement entries — attribute each to the current user
      let updatedEngagementHistory = formData.engagementHistory || '';
      const who = currentUser?.name;

      if (probabilityChangeEntry) {
        updatedEngagementHistory = prependEngagementEntry(updatedEngagementHistory, probabilityChangeEntry, who);
      }
      if (salesStatusChangeEntry) {
        updatedEngagementHistory = prependEngagementEntry(updatedEngagementHistory, salesStatusChangeEntry, who);
      }
      if (stageChangeEntry) {
        updatedEngagementHistory = prependEngagementEntry(updatedEngagementHistory, stageChangeEntry, who);
      }
      if (newEngagementEntry.trim()) {
        updatedEngagementHistory = prependEngagementEntry(updatedEngagementHistory, newEngagementEntry, who);
      }

      const newLastEditedDate = new Date().toISOString().split('T')[0];

      // Calculate sales status based on rules (use override if set)
      const computedSalesStatus = calculateSalesStatus({
        salesStage: formData.salesStage,
        lastEditedDate: newLastEditedDate,
        initiationDate: formData.initiationDate
      });
      // Use overridden status if set, otherwise use computed status
      const finalSalesStatus = formData.salesStatusOverride || computedSalesStatus;

      // Debug: Log values before creating oppToSave
      console.log('📋 Ref values - oppName:', oppName, 'account:', account, 'acv:', acv);

      const oppToSave = {
        ...formData,
        // Override with values from refs (uncontrolled inputs)
        opportunityName: oppName,
        associatedAccount: account,
        primaryContact: primaryContact,
        phoneNumber: phoneNumber,
        executiveSponsor: executiveSponsor,
        natureOfWork: natureOfWork,
        solutionScope: solutionScope,
        annualContractValue: acv,
        totalContractValue: tcv,
        probabilityValue: probValue,
        budget: acv,
        estimatedRevenue: acv,
        comments: updatedComments,
        engagementHistory: updatedEngagementHistory,
        lastEditedDate: newLastEditedDate,
        salesStatus: finalSalesStatus,
        salesStatusOverride: formData.salesStatusOverride
      };

      // Debug: Log oppToSave.executiveSponsor
      console.log('📋 oppToSave.executiveSponsor:', oppToSave.executiveSponsor);

      // Check for duplicate opportunity ID (new opportunities only)
      if (!selectedOpportunity) {
        const isDuplicate = opportunities.some(
          opp => opp.opportunityId === formData.opportunityId
        );
        if (isDuplicate) {
          alert(
            `Error: Opportunity ID "${formData.opportunityId}" already exists.\n\n` +
            `Please close and reopen this form to generate a new unique ID.`
          );
          return;
        }
      }

      try {
        // Using global API_BASE_URL defined at top of file
        // Map frontend field names (camelCase) to backend field names (snake_case)
        // Sanitize numeric fields to ensure valid numbers
        const safeNum = (v: any): number => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
        // Sanitize date fields — ensure valid date string or null
        const safeDate = (v: any): string | null => {
          if (!v) return null;
          const s = String(v);
          if (s === 'Invalid Date' || s === 'undefined' || s === 'null') return null;
          return s;
        };
        // Sanitize probability override — preserve 0 as valid, only null out truly empty
        const safeProbOverride = (v: any): number | null => {
          if (v === null || v === undefined || v === '') return null;
          const n = parseInt(String(v), 10);
          return isNaN(n) ? null : n;
        };

        const apiPayload = {
          opportunity_id: oppToSave.opportunityId,
          opportunity_name: oppToSave.opportunityName,
          associated_account: oppToSave.associatedAccount,
          sales_stage: oppToSave.salesStage,
          probability: String(oppToSave.probabilityPercent || '10'),
          probability_override: safeProbOverride(oppToSave.probabilityOverride),
          estimated_revenue: safeNum(oppToSave.estimatedRevenue),
          annual_contract_value: safeNum(oppToSave.annualContractValue),
          total_contract_value: safeNum(oppToSave.totalContractValue),
          service_line: oppToSave.serviceLine || '',
          service_offering: oppToSave.serviceOffering || '',
          entity: oppToSave.entity || '',
          geography: oppToSave.geography || '',
          industry: oppToSave.industryVertical || oppToSave.industry || '',
          opportunity_type: oppToSave.opportunityType || 'New Logo',
          opportunity_category: oppToSave.opportunityCategory || 'New Business',
          opportunity_classification: oppToSave.opportunityClassification || null,
          nature_of_work: oppToSave.natureOfWork || null,
          priority: oppToSave.priorityLevel || 'Medium',
          status: oppToSave.salesStatus || 'Warm',
          sales_status_override: oppToSave.salesStatusOverride || null,
          account_owner: oppToSave.accountOwner || '',
          lead_supporter: oppToSave.leadSupporter || '',
          solution_architect: oppToSave.solutionArchitect || '',
          executive_sponsor: oppToSave.executiveSponsor || '',
          primary_contact: oppToSave.primaryContact || '',
          primary_contact_email: oppToSave.primaryContactEmail || '',
          phone_number: oppToSave.phoneNumber || null,
          solution_scope: oppToSave.solutionScope || '',
          initiation_date: safeDate(oppToSave.initiationDate),
          expected_close_date: safeDate(oppToSave.estimatedCloseDate),
          actual_close_date: null,
          win_loss_reason: null,
          engagement_history: oppToSave.engagementHistory || '',
          comments: oppToSave.comments || '',
          latest_comment: newComment.trim() || null,
          last_edited_date: safeDate(oppToSave.lastEditedDate) || new Date().toISOString().split('T')[0],
          marked_for_review: oppToSave.markedForReview || false,
          lead_history: oppToSave.leadHistory || null,
          source_lead_id: oppToSave.sourceLeadId || null,
          city: oppToSave.city || null,
          lead_generator: oppToSave.leadGenerator || null
        };

        // Debug: Log the exact payload being sent
        console.log('📤 Opportunity API Payload:', JSON.stringify(apiPayload, null, 2));
        console.log('📊 Probability field type:', typeof apiPayload.probability, 'value:', apiPayload.probability);

        if (selectedOpportunity) {
          // Update existing opportunity
          const response = await fetch(`${API_BASE_URL}/opportunities/${selectedOpportunity.opportunityId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(apiPayload)
          });

          if (!response.ok) {
            let errorMsg = `HTTP ${response.status}`;
            try {
              const errorData = await response.json();
              errorMsg = errorData.message || errorData.error || errorMsg;
            } catch (parseErr) {
              errorMsg = await response.text().catch(() => errorMsg);
            }
            throw new Error(errorMsg);
          }

          const result = await response.json();

          // Save tags for this opportunity (by name, creating new tags if needed)
          let savedTagObjects = [];
          try {
            const tagSaveResponse = await fetch(`${API_BASE_URL}/master/opportunity-tags-by-name/${selectedOpportunity.opportunityId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ tag_names: selectedTagNames })
            });
            const tagSaveResult = await tagSaveResponse.json();
            if (tagSaveResult.success && tagSaveResult.data) {
              savedTagObjects = tagSaveResult.data;
            }
          } catch (tagError) {
            console.error('Error saving opportunity tags:', tagError);
          }

          // Use the returned tag objects for state update
          const selectedTagObjects = savedTagObjects;

          // Map API response back to frontend format for state update
          const updatedOpp = result.data ? {
            ...oppToSave,
            opportunityId: result.data.opportunity_id,
            annualContractValue: result.data.annual_contract_value || 0,
            totalContractValue: result.data.total_contract_value || 0,
            industryVertical: result.data.industry || '',
            industry: result.data.industry || '',
            executiveSponsor: result.data.executive_sponsor || '',
            solutionArchitect: result.data.solution_architect || '',
            solutionScope: result.data.solution_scope || oppToSave.solutionScope,
            opportunityCategory: result.data.opportunity_category || oppToSave.opportunityCategory || 'New Business',
            opportunityClassification: result.data.opportunity_classification || oppToSave.opportunityClassification || null,
            tags: selectedTagObjects
          } : oppToSave;
          setOpportunities(prevOpportunities => prevOpportunities.map(o => o.opportunityId === selectedOpportunity.opportunityId ? updatedOpp : o));
          alert('Opportunity updated successfully!');
        } else {
          // Create new opportunity
          const response = await fetch(`${API_BASE_URL}/opportunities`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify([apiPayload])
          });

          if (!response.ok) {
            const error = await response.json();

            // Handle duplicate ID error specifically (HTTP 409 Conflict or database constraint violation)
            if (response.status === 409 ||
                (error.message && (
                  error.message.toLowerCase().includes('duplicate') ||
                  error.message.toLowerCase().includes('unique constraint') ||
                  error.message.toLowerCase().includes('already exists')
                ))) {
              alert(
                `Error: Opportunity ID "${oppToSave.opportunityId}" already exists in the database.\n\n` +
                `This may happen if another user created an opportunity at the same time.\n\n` +
                `Please close and reopen this form to generate a new unique ID.`
              );
              return;
            }

            throw new Error(error.message || 'Failed to create opportunity');
          }

          const result = await response.json();

          // Save tags for the new opportunity (by name, creating new tags if needed)
          const newOppId = result.data?.[0]?.opportunity_id || oppToSave.opportunityId;
          let savedTagObjects = [];
          if (selectedTagNames.length > 0 && newOppId) {
            try {
              const tagSaveResponse = await fetch(`${API_BASE_URL}/master/opportunity-tags-by-name/${newOppId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tag_names: selectedTagNames })
              });
              const tagSaveResult = await tagSaveResponse.json();
              if (tagSaveResult.success && tagSaveResult.data) {
                savedTagObjects = tagSaveResult.data;
              }
            } catch (tagError) {
              console.error('Error saving opportunity tags:', tagError);
            }
          }

          // Use the returned tag objects for state update
          const selectedTagObjects = savedTagObjects;

          // Map API response back to frontend format for state update
          const newOpp = result.data && result.data[0] ? {
            ...oppToSave,
            opportunityId: result.data[0].opportunity_id,
            annualContractValue: result.data[0].annual_contract_value || 0,
            totalContractValue: result.data[0].total_contract_value || 0,
            industryVertical: result.data[0].industry || '',
            industry: result.data[0].industry || '',
            executiveSponsor: result.data[0].executive_sponsor || '',
            solutionArchitect: result.data[0].solution_architect || '',
            solutionScope: result.data[0].solution_scope || oppToSave.solutionScope,
            opportunityCategory: result.data[0].opportunity_category || oppToSave.opportunityCategory || 'New Business',
            opportunityClassification: result.data[0].opportunity_classification || oppToSave.opportunityClassification || null,
            tags: selectedTagObjects
          } : oppToSave;
          setOpportunities(prevOpportunities => [...prevOpportunities, newOpp]);

          // If this opportunity was created from a lead conversion, mark the lead as Converted
          // and link it to this opportunity. This is the ONLY place that flips lead.lead_status
          // to 'Converted' — guaranteeing the lead only changes once the opportunity row exists.
          if (formData._convertedFromLeadId && newOppId) {
            try {
              const updatedLeadHistory = prependEngagementEntry(
                formData.leadHistory || '',
                `Lead converted to opportunity ${newOppId}`,
                currentUser?.name
              );
              await leadsApi.markConverted(formData._convertedFromLeadId, newOppId, updatedLeadHistory);
              if (_fetchLeads) await _fetchLeads();
            } catch (linkError) {
              console.error('Error linking lead to opportunity:', linkError);
              // Non-fatal: opportunity was created successfully, just the link failed
            }
          }

          alert('Opportunity created successfully!');
        }

        setShowOpportunityModal(false);
        setSelectedOpportunity(null);
        if (setConvertFromLead) setConvertFromLead(null);
        setNewComment('');
        setNewEngagementEntry('');
      } catch (error) {
        console.error('Error saving opportunity:', error);
        alert(`Error: ${error.message}`);
      }
    };

    // Use the externally defined helper components and classes
    const EditField = OppFormEditField;
    const inputClass = OPP_FORM_INPUT_CLASS;
    const selectClass = OPP_FORM_SELECT_CLASS;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{formData.opportunityId}</span>
                  {formData.salesStage && (
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      formData.salesStage === 'Closed-Won' ? 'bg-green-100 text-green-700' :
                      formData.salesStage === 'Closed-Lost' ? 'bg-red-100 text-red-700' :
                      formData.salesStage === 'Contract' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-[rgba(0,187,162,0.12)] text-[#006B5E]'
                    }`}>{formData.salesStage}</span>
                  )}
                </div>
                <h2 className="text-sm font-bold text-gray-900 mt-1">{selectedOpportunity ? formData.opportunityName : 'New Opportunity'}</h2>
                <p className="text-sm text-gray-600">{selectedOpportunity ? formData.associatedAccount : 'Enter opportunity details below'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => {setShowOpportunityModal(false); setSelectedOpportunity(null); if (setConvertFromLead) setConvertFromLead(null);}} className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={handleSubmit} className="px-4 py-2 text-sm font-medium rounded-lg text-white hover:opacity-90 shadow-sm" style={{backgroundColor: '#00BBA2'}}>
                {selectedOpportunity ? 'Save Changes' : 'Create Opportunity'}
              </button>
              <button onClick={() => {setShowOpportunityModal(false); setSelectedOpportunity(null); if (setConvertFromLead) setConvertFromLead(null);}} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Warning banner: direct creation without lead is not allowed */}
          {!selectedOpportunity && !convertFromLead && (
            <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-3">
              <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                <strong>Direct opportunity creation is not permitted.</strong> Please go to the <strong>Leads</strong> tab, select a lead, and use the <strong>Qualify</strong> or <strong>Convert</strong> action to create an opportunity.
              </p>
            </div>
          )}

          {/* Quick Actions Bar - with engagement entry input */}
          <div className="px-6 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-4">
              <div className="flex-1 flex items-center gap-2">
                <input
                  type="text"
                  value={newEngagementEntry}
                  onChange={(e) => setNewEngagementEntry(e.target.value)}
                  placeholder="Add engagement update (e.g., '15-Jan-2026 Meeting scheduled with client...')"
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BBA2] focus:border-[#00BBA2]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newEngagementEntry.trim()) {
                      const updatedHistory = prependEngagementEntry(formData.engagementHistory, newEngagementEntry, currentUser?.name);
                      setFormData({...formData, engagementHistory: updatedHistory});
                      setNewEngagementEntry('');
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (newEngagementEntry.trim()) {
                      const updatedHistory = prependEngagementEntry(formData.engagementHistory, newEngagementEntry, currentUser?.name);
                      setFormData({...formData, engagementHistory: updatedHistory});
                      setNewEngagementEntry('');
                    }
                  }}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg bg-[#00BBA2] text-white hover:bg-cyan-700 flex items-center gap-1.5"
                >
                  <Plus size={14} /> Add
                </button>
              </div>
            </div>
          </div>

          {/* Content - Scrollable - Two Column Layout matching edit modal */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* LEFT COLUMN: Basic Info + Financial & Stage + Solution Scope */}
              <div className="space-y-4">
                {/* Combined: Basic Information + Ownership + Service Details */}
                <div className="bg-white border border-gray-100 rounded-lg p-4">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Building2 size={16} /> Basic Information, Ownership & Service Details
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
                    <EditField label="Opportunity Name *">
                      <input type="text" ref={opportunityNameRef} defaultValue={formData.opportunityName || ''} placeholder="Enter opportunity name" className={inputClass} />
                    </EditField>
                    <EditField label="Account *">
                      <input type="text" ref={accountRef} defaultValue={formData.associatedAccount || ''} placeholder="Enter account name" className={inputClass} />
                    </EditField>
                    <EditField label="Geography *">
                      <select value={formData.geography || ''} onChange={(e) => setFormData({...formData, geography: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {geographies?.filter(g => g.isActive).map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Industry *">
                      <select value={formData.industryVertical || ''} onChange={(e) => setFormData({...formData, industryVertical: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {industries?.filter(i => i.isActive).map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Category *">
                      <select value={formData.opportunityCategory || ''} onChange={(e) => setFormData({...formData, opportunityCategory: e.target.value, opportunityClassification: e.target.value === 'Renewals' ? 'EC, EB' : ''})} className={selectClass}>
                        <option value="">Select</option>
                        <option value="New Business">New Business</option>
                        <option value="Renewals">Renewals</option>
                      </select>
                    </EditField>
                    <EditField label="Classification">
                      <select value={formData.opportunityClassification || ''} onChange={(e) => setFormData({...formData, opportunityClassification: e.target.value})} disabled={formData.opportunityCategory === 'Renewals'} className={`${selectClass} disabled:bg-gray-100`}>
                        <option value="">Select</option>
                        <option value="EC, EB">EC, EB</option>
                        <option value="EC, NB">EC, NB</option>
                        <option value="NC, NB">NC, NB</option>
                      </select>
                    </EditField>
                    <EditField label="Nature of Work">
                      <input type="text" ref={natureOfWorkRef} defaultValue={formData.natureOfWork || ''} className={inputClass} placeholder="Enter nature of work" />
                    </EditField>
                    <EditField label="Primary Contact">
                      <input type="text" ref={primaryContactRef} defaultValue={formData.primaryContact || ''} className={inputClass} />
                    </EditField>
                    <EditField label="Phone Number">
                      <input type="tel" ref={phoneNumberRef} defaultValue={formData.phoneNumber || ''} className={inputClass} placeholder="+1-555-0101" />
                    </EditField>
                    <EditField label="Account Owner *">
                      <select value={formData.accountOwner || ''} onChange={(e) => setFormData({...formData, accountOwner: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {users?.filter(u => u.status === 'Active').map(u => <option key={u.userId} value={u.name}>{u.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Lead Supporter">
                      <input type="text" value={formData.leadSupporter || ''} onChange={(e) => setFormData({...formData, leadSupporter: e.target.value})} className={inputClass} placeholder="Enter lead supporter name" />
                    </EditField>
                    <EditField label="Solution Architect">
                      <select value={formData.solutionArchitect || ''} onChange={(e) => setFormData({...formData, solutionArchitect: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {users?.filter(u => u.status === 'Active').map(u => <option key={u.userId} value={u.name}>{u.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Executive Sponsor">
                      <input type="text" ref={executiveSponsorRef} defaultValue={formData.executiveSponsor || ''} className={inputClass} />
                    </EditField>
                    <EditField label="Service Line *">
                      <select value={formData.serviceLine || ''} onChange={(e) => setFormData({...formData, serviceLine: e.target.value, serviceOffering: ''})} className={selectClass}>
                        <option value="">Select</option>
                        {serviceLines?.filter(s => s.isActive).map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Service Offering">
                      <select value={formData.serviceOffering || ''} onChange={(e) => setFormData({...formData, serviceOffering: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {serviceOfferings?.filter(s => s.isActive && (!formData.serviceLine || s.serviceLine === formData.serviceLine)).map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Entity *">
                      <select value={formData.entity || ''} onChange={(e) => setFormData({...formData, entity: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {entities?.filter(e => e.isActive).map(ent => <option key={ent.id} value={ent.name}>{ent.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="City">
                      <input type="text" value={formData.city || ''} onChange={(e) => setFormData({...formData, city: e.target.value})} className={inputClass} placeholder="Enter city" />
                    </EditField>
                    <EditField label="Lead Generator">
                      <input type="text" value={formData.leadGenerator || ''} onChange={(e) => setFormData({...formData, leadGenerator: e.target.value})} className={inputClass} placeholder="Enter lead generator" />
                    </EditField>
                    <EditField label="Tags">
                      <div className="relative">
                        <div className={`${inputClass} min-h-[38px] flex flex-wrap gap-1 items-center p-1`}>
                          {selectedTagNames.map((tagName, idx) => {
                            const existingTag = tags?.find(t => t.name.toLowerCase() === tagName.toLowerCase());
                            const tagColor = existingTag?.color || '#6A7CA5';
                            return (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                style={{ backgroundColor: `${tagColor}20`, color: tagColor }}
                              >
                                {tagName}
                                <button
                                  type="button"
                                  onClick={() => setSelectedTagNames(selectedTagNames.filter((_, i) => i !== idx))}
                                  className="ml-1 hover:opacity-70"
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            );
                          })}
                          <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => {
                              setTagInput(e.target.value);
                              setShowTagDropdown(true);
                            }}
                            onFocus={() => setShowTagDropdown(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && tagInput.trim()) {
                                e.preventDefault();
                                const newTag = tagInput.trim();
                                if (!selectedTagNames.some(t => t.toLowerCase() === newTag.toLowerCase())) {
                                  setSelectedTagNames([...selectedTagNames, newTag]);
                                }
                                setTagInput('');
                                setShowTagDropdown(false);
                              } else if (e.key === 'Backspace' && !tagInput && selectedTagNames.length > 0) {
                                setSelectedTagNames(selectedTagNames.slice(0, -1));
                              } else if (e.key === 'Escape') {
                                setShowTagDropdown(false);
                              }
                            }}
                            placeholder={selectedTagNames.length === 0 ? "Type to add tags..." : ""}
                            className="flex-1 min-w-[100px] outline-none text-sm bg-transparent"
                          />
                        </div>
                        {showTagDropdown && (
                          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                            {/* Show filtered existing tags */}
                            {tags?.filter(t => t.isActive &&
                              (tagInput === '' || t.name.toLowerCase().includes(tagInput.toLowerCase())) &&
                              !selectedTagNames.some(st => st.toLowerCase() === t.name.toLowerCase())
                            ).map(tag => (
                              <div
                                key={tag.id}
                                onClick={() => {
                                  setSelectedTagNames([...selectedTagNames, tag.name]);
                                  setTagInput('');
                                  setShowTagDropdown(false);
                                }}
                                className="px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2"
                              >
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: tag.color }}
                                />
                                <span className="flex-1 text-sm">{tag.name}</span>
                              </div>
                            ))}
                            {/* Show option to create new tag if input doesn't match existing */}
                            {tagInput.trim() && !tags?.some(t => t.name.toLowerCase() === tagInput.trim().toLowerCase()) && (
                              <div
                                onClick={() => {
                                  const newTag = tagInput.trim();
                                  if (!selectedTagNames.some(t => t.toLowerCase() === newTag.toLowerCase())) {
                                    setSelectedTagNames([...selectedTagNames, newTag]);
                                  }
                                  setTagInput('');
                                  setShowTagDropdown(false);
                                }}
                                className="px-3 py-2 cursor-pointer hover:bg-[rgba(0,187,162,0.07)] flex items-center gap-2 text-[#00BBA2] border-t"
                              >
                                <Plus size={14} />
                                <span className="text-sm">Create "{tagInput.trim()}"</span>
                              </div>
                            )}
                            {!tagInput && (!tags || tags.filter(t => t.isActive).length === 0) && (
                              <div className="px-3 py-2 text-sm text-gray-500">Type to create a new tag</div>
                            )}
                          </div>
                        )}
                      </div>
                    </EditField>
                  </div>
                </div>

                {/* Financial & Sales Stage */}
                <div className="bg-white border border-gray-100 rounded-lg p-4">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                    <IndianRupee size={16} /> Financial & Stage
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
                    <EditField label="ACV (₹) *">
                      <input type="number" ref={acvRef} defaultValue={formData.annualContractValue || ''} placeholder="0" className={inputClass} />
                    </EditField>
                    <EditField label="TCV (₹)">
                      <input type="number" ref={tcvRef} defaultValue={formData.totalContractValue || ''} placeholder="0" className={inputClass} />
                    </EditField>
                    <EditField label="Stage">
                      <select
                        value={formData.salesStage || 'Qualified'}
                        onChange={(e) => {
                          const newStage = e.target.value;
                          const stageProbInfo = calculateProbabilityByStage(newStage);
                          const isRegression = selectedOpportunity && isStageRegression(selectedOpportunity.salesStage, newStage);
                          setStageRegressed(isRegression);
                          if (formData.probabilityOverride === null || formData.probabilityOverride === undefined) {
                            setFormData({...formData, salesStage: newStage, probability: `${stageProbInfo.percent}%`, probabilityPercent: `${stageProbInfo.percent}%`, probabilityToClose: stageProbInfo.category, forecastMultiplier: stageProbInfo.percent / 100});
                          } else {
                            setFormData({...formData, salesStage: newStage});
                          }
                        }}
                        className={`${selectClass} ${stageRegressed ? 'border-amber-400 bg-amber-100' : ''}`}
                      >
                        {getAllowedStages(formData.salesStage || 'Qualified').map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </EditField>
                    <EditField label={formData.probabilityOverride !== null && formData.probabilityOverride !== undefined ? "Probability *" : "Probability"}>
                      <select
                        value={formData.probabilityOverride !== null && formData.probabilityOverride !== undefined ? `${formData.probabilityOverride}%` : (formData.probabilityPercent || '10%')}
                        onChange={(e) => {
                          const newProbStr = e.target.value;
                          const newProb = parseInt(newProbStr.replace('%', ''), 10);
                          const stageProbInfo = calculateProbabilityByStage(formData.salesStage || 'Qualified');
                          const isOverriding = newProb !== stageProbInfo.percent;
                          setFormData({...formData, probability: newProbStr, probabilityPercent: newProbStr, probabilityOverride: isOverriding ? newProb : null, forecastMultiplier: newProb / 100});
                        }}
                        className={selectClass}
                      >
                        <option value="10%">10%</option>
                        <option value="20%">20%</option>
                        <option value="30%">30%</option>
                        <option value="40%">40%</option>
                        <option value="50%">50%</option>
                        <option value="60%">60%</option>
                        <option value="75%">75%</option>
                        <option value="90%">90%</option>
                        <option value="100%">100%</option>
                      </select>
                    </EditField>
                    <EditField label="Initiation Date">
                      <input type="date" value={formData.initiationDate || ''} onChange={(e) => setFormData({...formData, initiationDate: e.target.value})} className={inputClass} />
                    </EditField>
                    <EditField label="Est. Close Date">
                      <input type="date" value={formData.estimatedCloseDate || ''} onChange={(e) => setFormData({...formData, estimatedCloseDate: e.target.value})} className={inputClass} />
                    </EditField>
                  </div>
                </div>

                {/* Solution Scope */}
                <div className="bg-white border border-gray-100 rounded-lg p-4">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-2">
                    <Target size={16} /> Solution Scope
                  </div>
                  <textarea
                    ref={solutionScopeRef}
                    defaultValue={formData.solutionScope || ''}
                    rows={3}
                    placeholder="Describe the solution scope..."
                    className="w-full px-3 py-2 text-sm border border-teal-200 rounded-lg resize-none focus:ring-2 focus:ring-[#00BBA2]"
                  />
                </div>
              </div>

              {/* RIGHT COLUMN: Engagement History + Comments */}
              <div className="space-y-4">
                <div className="bg-white border border-gray-100 rounded-lg p-4">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                    <FileText size={16} /> Engagement History
                  </div>
                  <div className="bg-white border border-cyan-100 rounded-lg p-3 overflow-y-auto text-sm" style={{maxHeight: 'calc(100vh - 450px)'}}>
                    <TimelineDisplay entries={formData.engagementHistory} emptyMessage="No engagement history yet" />
                  </div>
                </div>

                {/* Lead History - shown only for opportunities converted from leads */}
                {formData.leadHistory && formData.leadHistory.trim() && (
                  <div className="bg-white border border-gray-100 rounded-lg p-4">
                    <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                      <FileText size={16} /> Lead History {formData.sourceLeadId && <span className="text-xs font-normal normal-case text-amber-500">({formData.sourceLeadId})</span>}
                    </div>
                    <div className="bg-white border border-amber-100 rounded-lg p-3 max-h-40 overflow-y-auto text-sm">
                      <TimelineDisplay entries={formData.leadHistory} emptyMessage="No lead history" />
                    </div>
                  </div>
                )}

                {/* Comments - Below Engagement History */}
                <div className="bg-white border border-gray-100 rounded-lg p-4">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-2">
                    <MessageSquare size={16} /> Comments
                  </div>
                  <div className="bg-white border border-rose-100 rounded-lg p-3 max-h-32 overflow-y-auto text-sm mb-2">
                    <TimelineDisplay entries={formData.comments} emptyMessage="No comments yet" />
                  </div>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={2}
                    placeholder="Add a comment..."
                    className="w-full px-3 py-2 text-sm border border-rose-200 rounded-lg resize-none focus:ring-2 focus:ring-[#00BBA2]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer with timestamps */}
          <div className="px-6 py-2 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">
                <span className="text-red-500">*</span> Required fields | Override fields marked with *
              </p>
              <div className="text-xs text-gray-500">
                Created: {formData.initiationDate ? new Date(formData.initiationDate).toLocaleDateString() : 'Today'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
});

// ======================================================================
// LEAD FORM MODAL — Extracted outside CRMApplication with React.memo
// to prevent re-mounting on every parent state change (fixes input bug)
// ======================================================================
const LEAD_FORM_INPUT_CLASS = "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00BBA2] focus:border-[#00BBA2]";
const LEAD_FORM_SELECT_CLASS = "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00BBA2] focus:border-[#00BBA2]";

const LeadDisplayField = ({ label, value, className = '', wrap = false }: { label: string; value: any; className?: string; wrap?: boolean }) => (
  <div className={`min-w-0 ${className}`}>
    <span className="text-[10px] text-gray-500 uppercase tracking-wide leading-none">{label}</span>
    <div className={`text-sm font-medium text-gray-800 ${wrap ? 'whitespace-pre-wrap break-words' : 'truncate'}`} title={typeof value === 'string' ? value : undefined}>{value || '-'}</div>
  </div>
);

const LeadEditField = ({ label, children, className = '' }: { label: string; children: any; className?: string }) => (
  <div className={className}>
    <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1 h-4 leading-4 truncate" title={label}>{label}</label>
    {children}
  </div>
);

const LeadFormModal = React.memo(({
  selectedLead, setSelectedLead, leadViewMode, setLeadViewMode, setShowLeadModal,
  leads, currentUser, setActiveTab, setSelectedOpportunity, setShowOpportunityModal,
  setConvertFromLead, openLostModal, onDeleteLead, openDisqualifyModal,
  leadSources, users, geographies, industries, serviceLines, entities, tags,
  prefilledLead, clearPrefilledLead
}: any) => {
  const isViewMode = leadViewMode === 'view';
  const isEditMode = leadViewMode === 'edit';
  const isCreateMode = !selectedLead && !isViewMode;

  // When creating a new lead, use prefilledLead (from ICP wizard) as the seed
  const seed = selectedLead || (isCreateMode ? prefilledLead : null);

  const safeFormDefaults = (lead: any) => ({
    leadId: lead?.leadId || 'NEW',
    companyName: lead?.companyName || '', contactPerson: lead?.contactPerson || '',
    email: lead?.email || '', phone: lead?.phone || '', designation: lead?.designation || '',
    city: lead?.city || '', geography: lead?.geography || '',
    industry: lead?.industry || '', serviceLine: lead?.serviceLine || '',
    serviceOffering: lead?.serviceOffering || '', entity: lead?.entity || '',
    pierianGeography: lead?.pierianGeography || '',
    leadSource: lead?.leadSource || '', sourceComments: lead?.sourceComments || '', leadOwner: lead ? (lead.leadOwner || '') : (currentUser?.name || ''),
    leadStatus: lead?.leadStatus || 'Open', estimatedRevenue: lead?.estimatedRevenue ?? '',
    budget: lead?.budget ?? '', painPoints: lead?.painPoints || '', comments: lead?.comments || '',
    priority: lead?.priority || 'Medium',
    dateCaptured: lead?.dateCaptured ? String(lead.dateCaptured).split('T')[0] : new Date().toISOString().split('T')[0],
    lastContacted: lead?.lastContacted ? String(lead.lastContacted).split('T')[0] : '',
    convertedOpportunityId: lead?.convertedOpportunityId || '', convertedAt: lead?.convertedAt || '',
    markedForReview: lead?.markedForReview || false,
    lossReason: lead?.lossReason || null,
    engagementHistory: lead?.engagementHistory || '',
    leadSupporter1: lead?.leadSupporter ? String(lead.leadSupporter).split(',')[0].trim() : '',
    leadSupporter2: lead?.leadSupporter && String(lead.leadSupporter).split(',').length > 1 ? String(lead.leadSupporter).split(',').slice(1).join(',').trim() : '',
    solutionArchitect: lead?.solutionArchitect || '',
    leadRating: lead?.leadRating || '',
    businessType: lead?.businessType || '',
    leadGenerator: lead?.leadGenerator || '',
  });
  const [formData, setFormData] = useState(safeFormDefaults(seed));
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [newEngagementEntry, setNewEngagementEntry] = useState('');
  // Tag management state
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>(
    selectedLead?.tags?.map((t: any) => t.name) || []
  );
  const [tagInput, setTagInput] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);

  const closeModal = () => {
    setShowLeadModal(false);
    setSelectedLead(null);
    setLeadViewMode(null);
    if (clearPrefilledLead) clearPrefilledLead();
  };

  // Format date string (YYYY-MM-DD) to dd-MMM-yy (e.g. 16-Feb-26)
  const fmtDate = (d: string | null | undefined): string => {
    if (!d) return '-';
    const dt = new Date(d + 'T00:00:00');
    if (isNaN(dt.getTime())) return d;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${dt.getDate().toString().padStart(2,'0')}-${months[dt.getMonth()]}-${dt.getFullYear().toString().slice(-2)}`;
  };

  const handleSubmit = async () => {
    const requiredFields = { companyName: 'Company Name', industry: 'Industry', geography: 'Geography', designation: 'Designation', phone: 'Phone Number', leadOwner: 'Lead Owner', leadSupporter1: 'Lead Supporter 1', leadRating: 'Lead Rating', businessType: 'Business Type', serviceLine: 'Service Line', city: 'City', leadGenerator: 'Lead Generator', entity: 'Entity' };
    const missingFields: string[] = [];
    for (const [field, label] of Object.entries(requiredFields)) { if (!formData[field] || formData[field] === '') missingFields.push(label); }
    if (missingFields.length > 0) { alert(`Please fill in:\n\n${missingFields.join('\n')}`); return; }
    setSaving(true);
    try {
      const payload = {
        lead_id: formData.leadId, company_name: formData.companyName, contact_person: formData.contactPerson,
        designation: formData.designation || null, email: formData.email, phone: formData.phone || null,
        city: formData.city || null, geography: formData.geography,
        industry: formData.industry, service_line: formData.serviceLine, service_offering: formData.serviceOffering || null,
        entity: formData.entity || null,
        pierian_geography: formData.pierianGeography || formData.geography,
        lead_source: formData.leadSource, source_comments: formData.sourceComments || null,
        lead_owner: formData.leadOwner, sales_representative: formData.leadOwner,
        lead_status: (formData.leadStatus === 'Converted' && !formData.convertedOpportunityId) ? (selectedLead?.leadStatus || 'Open') : (formData.leadStatus || 'Open'),
        estimated_revenue: parseFloat(formData.estimatedRevenue) || 0, budget: parseFloat(formData.budget) || 0,
        pain_points: formData.painPoints || null, comments: formData.comments || null,
        priority: formData.priority || 'Medium',
        date_captured: formData.dateCaptured || new Date().toISOString().split('T')[0],
        last_contacted: formData.lastContacted || null,
        converted_opportunity_id: formData.convertedOpportunityId || null, converted_at: formData.convertedAt || null,
        marked_for_review: formData.markedForReview || false,
        engagement_history: formData.engagementHistory || null,
        lead_supporter: [formData.leadSupporter1, formData.leadSupporter2].filter(Boolean).join(', ') || null,
        solution_architect: formData.solutionArchitect || null,
        lead_rating: formData.leadRating || null,
        business_type: formData.businessType || null,
        lead_generator: formData.leadGenerator || null,
        sales_status_override: formData.salesStatusOverride || null
      };
      // For new leads, append "Lead created" as the oldest engagement entry
      if (!selectedLead) {
        const createdEntry = formatEngagementEntry('Lead created', currentUser?.name);
        payload.engagement_history = payload.engagement_history
          ? `${payload.engagement_history}\n${createdEntry}`
          : createdEntry;
      }
      // Track status changes in engagement history (newest on top)
      if (selectedLead && formData.leadStatus !== selectedLead.leadStatus) {
        payload.engagement_history = prependEngagementEntry(
          payload.engagement_history,
          `Status changed to ${formData.leadStatus}`,
          currentUser?.name
        );
      }
      let result;
      if (selectedLead) { result = await leadsApi.update(selectedLead.leadId, payload); }
      else { result = await leadsApi.create(payload); }
      if (result.success) {
        // Save tags for this lead
        const leadId = selectedLead?.leadId || (result.data?.lead_id);
        if (leadId && selectedTagNames.length >= 0) {
          try {
            await fetch(`${API_BASE_URL}/master/lead-tags-by-name/${leadId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ tag_names: selectedTagNames })
            });
          } catch (tagError) { console.error('Error saving lead tags:', tagError); }
        }
        if (_fetchLeads) await _fetchLeads();
        closeModal();
      }
      else { alert(`Error: ${result.error || result.message || 'Failed to save lead'}`); }
    } catch (error) { console.error('Error saving lead:', error); alert(`Error saving lead: ${error instanceof Error ? error.message : 'Please try again.'}`); }
    finally { setSaving(false); }
  };

  // Convert lead to opportunity — open create opportunity screen.
  // Lead status is flipped to Converted only after the opportunity row is actually inserted
  // (handled in OpportunityFormModal save flow). If the user cancels, the lead is unchanged.
  const handleConvertAndOpen = async () => {
    if (!confirm(`Convert "${formData.companyName}" to an opportunity?\n\nThis opens the opportunity creation screen. The lead is marked Converted only after you save the opportunity.`)) return;
    closeModal();
    setSelectedOpportunity(null); // null = create mode
    setConvertFromLead(formData); // pass lead data to pre-fill the form
    setShowOpportunityModal(true);
  };

  // Mark lead as Not Qualified from view modal
  const handleMarkNotQualified = async () => {
    if (!confirm(`Disqualify lead "${formData.companyName}"?`)) return;
    setSaving(true);
    try {
      const payload = {
        lead_id: formData.leadId, company_name: formData.companyName, contact_person: formData.contactPerson,
        designation: formData.designation || null, email: formData.email, phone: formData.phone || null,
        city: formData.city || null, geography: formData.geography,
        industry: formData.industry, service_line: formData.serviceLine, service_offering: formData.serviceOffering || null,
        entity: formData.entity || null,
        pierian_geography: formData.pierianGeography || formData.geography,
        lead_source: formData.leadSource, source_comments: formData.sourceComments || null,
        lead_owner: formData.leadOwner, sales_representative: formData.leadOwner,
        lead_status: 'Unqualified',
        estimated_revenue: parseFloat(formData.estimatedRevenue) || 0, budget: parseFloat(formData.budget) || 0,
        pain_points: formData.painPoints || null, comments: formData.comments || null,
        priority: formData.priority || 'Medium',
        date_captured: formData.dateCaptured || null, last_contacted: formData.lastContacted || null,
        converted_opportunity_id: formData.convertedOpportunityId || null, converted_at: formData.convertedAt || null,
        marked_for_review: formData.markedForReview || false,
        engagement_history: formData.engagementHistory || null,
        lead_supporter: [formData.leadSupporter1, formData.leadSupporter2].filter(Boolean).join(', ') || null,
        solution_architect: formData.solutionArchitect || null,
        lead_rating: formData.leadRating || null,
        business_type: formData.businessType || null
      };
      const result = await leadsApi.update(formData.leadId, payload);
      if (result.success) { if (_fetchLeads) await _fetchLeads(); closeModal(); }
      else { alert(`Error: ${result.error || result.message || 'Failed to update lead'}`); }
    } catch (error) { console.error('Error updating lead:', error); alert('Error updating lead. Please try again.'); }
    finally { setSaving(false); }
  };

  const getStatusBadgeInline = (status: string) => {
    const cls = status === 'Qualified' ? 'bg-green-100 text-green-700' : status === 'Converted' ? 'bg-green-100 text-green-700' : status === 'Unqualified' ? 'bg-orange-100 text-orange-700' : status === 'Lost' ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-700';
    return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{status || 'Open'}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget && isViewMode) closeModal(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header — matching opportunity modal */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div>
            {(isViewMode || isEditMode) && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{formData.leadId}</span>
                {getStatusBadgeInline(formData.leadStatus)}
              </div>
            )}
            <h2 className="text-sm font-bold text-gray-900 mt-1">
              {isCreateMode ? 'New Lead' : formData.companyName || 'Lead Details'}
            </h2>
            {(isViewMode || isEditMode) && <p className="text-sm text-gray-600">{formData.contactPerson} · {formData.email}</p>}
            {isViewMode && formData.leadStatus === 'Lost' && formData.lossReason && (
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><Ban size={12} className="text-gray-400" /> {formData.lossReason}</p>
            )}
            {isViewMode && formData.leadStatus === 'Unqualified' && formData.lossReason && (
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><XCircle size={12} className="text-orange-400" /> {formData.lossReason}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isViewMode && formData.leadStatus !== 'Converted' && (
              <button onClick={() => setLeadViewMode('edit')} className="px-4 py-2 text-sm font-medium rounded-lg bg-[#00BBA2] text-white hover:bg-[#009E8A] flex items-center gap-2 shadow-sm">
                <Edit3 size={16} /> Edit
              </button>
            )}
            {isViewMode && formData.leadStatus === 'Converted' && formData.convertedOpportunityId && (
              <button onClick={() => { closeModal(); setActiveTab('opportunities'); }} className="px-4 py-2 text-sm font-medium rounded-lg bg-green-100 text-green-700 hover:bg-green-200 flex items-center gap-2">
                <ExternalLink size={16} /> View Opportunity
              </button>
            )}
            {isEditMode && (
              <>
                <button onClick={() => setLeadViewMode('view')} className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Cancel</button>
                <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 text-sm font-medium rounded-lg text-white hover:opacity-90 disabled:opacity-50 shadow-sm" style={{ backgroundColor: '#00BBA2' }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
            {isCreateMode && (
              <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 text-sm font-medium rounded-lg text-white hover:opacity-90 disabled:opacity-50 shadow-sm" style={{ backgroundColor: '#00BBA2' }}>
                {saving ? 'Creating...' : 'Create Lead'}
              </button>
            )}
            <button onClick={closeModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>
        </div>

        {/* Quick Actions Bar (view + edit mode) — matches opportunity modal layout */}
        {(isViewMode || isEditMode) && (
          <div className="px-6 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            {/* Row 1: Action buttons */}
            {formData.leadStatus !== 'Converted' && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5"><Zap size={14} /> Quick Actions:</span>
                {formData.leadStatus !== 'Lost' && formData.leadStatus !== 'Unqualified' && (
                  <button onClick={handleConvertAndOpen} disabled={converting}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-50">
                    {converting ? <RefreshCw size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                    {converting ? 'Converting...' : 'Convert to Opportunity'}
                  </button>
                )}
                <div className="border-l border-gray-300 h-5 mx-1"></div>
                {formData.leadStatus !== 'Unqualified' && formData.leadStatus !== 'Lost' && (
                  <button onClick={() => { if (openDisqualifyModal && selectedLead) { openDisqualifyModal(selectedLead); closeModal(); } }} disabled={saving}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 disabled:opacity-50">
                    <XCircle size={14} /> Disqualify
                  </button>
                )}
                {formData.leadStatus !== 'Lost' && formData.leadStatus !== 'Unqualified' && (
                  <button onClick={() => { if (selectedLead) { openLostModal(selectedLead); closeModal(); } }} disabled={saving}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-200 disabled:opacity-50">
                    <Ban size={14} /> Mark as Lost
                  </button>
                )}
                {currentUser?.isAdmin && (
                  <button onClick={() => { if (onDeleteLead && selectedLead) { onDeleteLead(selectedLead); closeModal(); } }} disabled={saving}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50">
                    <Trash2 size={14} /> Delete
                  </button>
                )}
              </div>
            )}
            {/* Row 2: Engagement entry input */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newEngagementEntry}
                onChange={(e) => setNewEngagementEntry(e.target.value)}
                placeholder="Add engagement update (e.g., '15-Jan-2026 Meeting scheduled with client...')"
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00BBA2] focus:border-[#00BBA2]"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && newEngagementEntry.trim()) {
                    const updatedHistory = prependEngagementEntry(formData.engagementHistory, newEngagementEntry, currentUser?.name);
                    setFormData({...formData, engagementHistory: updatedHistory});
                    setNewEngagementEntry('');
                    try {
                      await leadsApi.update(formData.leadId, {
                        lead_id: formData.leadId, company_name: formData.companyName, contact_person: formData.contactPerson,
                        designation: formData.designation || null, email: formData.email, phone: formData.phone || null,
                        city: formData.city || null, geography: formData.geography,
                        industry: formData.industry, service_line: formData.serviceLine, service_offering: formData.serviceOffering || null,
                        entity: formData.entity || null,
                        pierian_geography: formData.pierianGeography || formData.geography,
                        lead_source: formData.leadSource, source_comments: formData.sourceComments || null,
                        lead_owner: formData.leadOwner, sales_representative: formData.leadOwner,
                        lead_status: formData.leadStatus,
                        revenue: parseFloat(selectedLead?.revenue) || parseFloat(formData.estimatedRevenue) || 0,
                        estimated_revenue: parseFloat(formData.estimatedRevenue) || 0, budget: parseFloat(formData.budget) || 0,
                        pain_points: formData.painPoints || null, comments: formData.comments || null,
                        priority: formData.priority || 'Medium', timeline: formData.timeline || selectedLead?.timeline || null,
                        date_captured: formData.dateCaptured || null, last_contacted: formData.lastContacted || null,
                        converted_opportunity_id: formData.convertedOpportunityId || null, converted_at: formData.convertedAt || null,
                        marked_for_review: formData.markedForReview || false, loss_reason: formData.lossReason || null,
                        lead_supporter: [formData.leadSupporter1, formData.leadSupporter2].filter(Boolean).join(', ') || null, solution_architect: formData.solutionArchitect || null,
                        lead_rating: formData.leadRating || null,
                        business_type: formData.businessType || null,
                        lead_generator: formData.leadGenerator || null,
                        engagement_history: updatedHistory
                      });
                      if (_fetchLeads) await _fetchLeads();
                    } catch (err) { console.error('Error saving engagement entry:', err); }
                  }
                }}
              />
              <button
                onClick={async () => {
                  if (newEngagementEntry.trim()) {
                    const updatedHistory = prependEngagementEntry(formData.engagementHistory, newEngagementEntry, currentUser?.name);
                    setFormData({...formData, engagementHistory: updatedHistory});
                    setNewEngagementEntry('');
                    try {
                      await leadsApi.update(formData.leadId, {
                        lead_id: formData.leadId, company_name: formData.companyName, contact_person: formData.contactPerson,
                        designation: formData.designation || null, email: formData.email, phone: formData.phone || null,
                        city: formData.city || null, geography: formData.geography,
                        industry: formData.industry, service_line: formData.serviceLine, service_offering: formData.serviceOffering || null,
                        entity: formData.entity || null,
                        pierian_geography: formData.pierianGeography || formData.geography,
                        lead_source: formData.leadSource, source_comments: formData.sourceComments || null,
                        lead_owner: formData.leadOwner, sales_representative: formData.leadOwner,
                        lead_status: formData.leadStatus,
                        revenue: parseFloat(selectedLead?.revenue) || parseFloat(formData.estimatedRevenue) || 0,
                        estimated_revenue: parseFloat(formData.estimatedRevenue) || 0, budget: parseFloat(formData.budget) || 0,
                        pain_points: formData.painPoints || null, comments: formData.comments || null,
                        priority: formData.priority || 'Medium', timeline: formData.timeline || selectedLead?.timeline || null,
                        date_captured: formData.dateCaptured || null, last_contacted: formData.lastContacted || null,
                        converted_opportunity_id: formData.convertedOpportunityId || null, converted_at: formData.convertedAt || null,
                        marked_for_review: formData.markedForReview || false, loss_reason: formData.lossReason || null,
                        lead_supporter: [formData.leadSupporter1, formData.leadSupporter2].filter(Boolean).join(', ') || null, solution_architect: formData.solutionArchitect || null,
                        lead_rating: formData.leadRating || null,
                        business_type: formData.businessType || null,
                        lead_generator: formData.leadGenerator || null,
                        engagement_history: updatedHistory
                      });
                      if (_fetchLeads) await _fetchLeads();
                    } catch (err) { console.error('Error saving engagement entry:', err); }
                  }
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-[#00BBA2] text-white hover:bg-cyan-700 flex items-center gap-1.5"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4">
          {isViewMode ? (
            /* ===== VIEW MODE — Compact layout ===== */
            <div className="grid grid-cols-2 gap-3">
              {/* LEFT: Lead Info + Classification */}
              <div className="space-y-3">
                {/* Lead & Contact Information */}
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Building2 size={14} /> Lead & Contact
                  </div>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
                    <LeadDisplayField label="Company" value={formData.companyName} />
                    <LeadDisplayField label="Contact" value={formData.contactPerson} />
                    <LeadDisplayField label="Email" value={formData.email} />
                    <LeadDisplayField label="Phone" value={formData.phone} />
                    <LeadDisplayField label="Designation" value={formData.designation} />
                    <LeadDisplayField label="Lead Owner" value={formData.leadOwner} />
                    <LeadDisplayField label="Lead Supporter 1" value={formData.leadSupporter1} />
                    <LeadDisplayField label="Lead Supporter 2" value={formData.leadSupporter2} />
                    <LeadDisplayField label="Solution Architect" value={formData.solutionArchitect} />
                    <LeadDisplayField label="Priority" value={formData.priority} />
                    <LeadDisplayField label="Source" value={formData.leadSource} wrap={true} />
                    <LeadDisplayField label="Date Captured" value={fmtDate(formData.dateCaptured)} />
                    {formData.sourceComments && (
                      <LeadDisplayField label="Source Comments" value={formData.sourceComments} className="col-span-3" wrap={true} />
                    )}
                  </div>
                </div>

                {/* Classification & Financial */}
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <IndianRupee size={14} /> Classification & Financial
                  </div>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
                    <LeadDisplayField label="Est. Revenue" value={formData.estimatedRevenue ? `₹${parseFloat(formData.estimatedRevenue).toLocaleString()}` : '-'} />
                    <LeadDisplayField label="Geography" value={formData.geography || 'Domestic'} />
                    <LeadDisplayField label="City" value={formData.city} />
                    <LeadDisplayField label="Industry" value={formData.industry} wrap={true} />
                    <LeadDisplayField label="Service Line" value={formData.serviceLine} wrap={true} />
                    <LeadDisplayField label="Lead Rating" value={formData.leadRating} />
                    <LeadDisplayField label="Business Type" value={formData.businessType} />
                    <LeadDisplayField label="Lead Generator" value={formData.leadGenerator} />
                  </div>
                </div>
              </div>

              {/* RIGHT: Engagement History, Pain Points, Comments, Tags */}
              <div className="space-y-3">
                {/* Converted Banner */}
                {formData.leadStatus === 'Converted' && formData.convertedOpportunityId && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
                    <CheckCircle size={14} className="text-green-600" />
                    <span className="font-medium">Converted to Opportunity {formData.convertedOpportunityId}</span>
                  </div>
                )}

                {/* Engagement History */}
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <FileText size={14} /> Engagement History
                  </div>
                  <div className="bg-white border border-cyan-100 rounded p-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 450px)' }}>
                    <TimelineDisplay entries={formData.engagementHistory} emptyMessage="No engagement history yet" />
                  </div>
                </div>

                {/* Pain Points */}
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Target size={14} /> Pain Points / Requirements
                  </div>
                  <div className="bg-white border border-teal-100 rounded p-2 max-h-28 overflow-y-auto">
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{formData.painPoints || 'No pain points recorded.'}</p>
                  </div>
                </div>

                {/* Comments */}
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <MessageSquare size={14} /> Comments
                  </div>
                  <div className="bg-white border border-rose-100 rounded p-2 max-h-28 overflow-y-auto">
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{formData.comments || 'No comments.'}</p>
                  </div>
                </div>

                {/* Tags */}
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Tag size={14} /> Tags
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {selectedLead?.tags && selectedLead.tags.length > 0 ? selectedLead.tags.map((tag: any) => (
                      <span key={tag.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: `${tag.color || '#6A7CA5'}20`, color: tag.color || '#6A7CA5' }}>
                        {tag.name}
                      </span>
                    )) : <span className="text-xs text-gray-400">No tags</span>}
                  </div>
                </div>
              </div>
            </div>
          ) : isCreateMode ? (
            /* ===== CREATE MODE — Required fields first, then optional by category ===== */
            <div className="space-y-4">
              {/* REQUIRED INFORMATION — full width, highlighted */}
              {(() => { const hasAllRequired = !!(formData.companyName && formData.industry && formData.geography && formData.designation && formData.phone && formData.leadOwner && formData.leadSupporter1 && formData.leadRating && formData.businessType && formData.serviceLine && formData.city && formData.leadGenerator && formData.entity); return (
              <div className={hasAllRequired ? "bg-gray-50 border border-gray-200 rounded-lg p-4" : "bg-red-50 border border-red-200 rounded-lg p-4"}>
                <div className={`text-sm font-semibold uppercase tracking-wide mb-3 flex items-center gap-2 ${hasAllRequired ? "text-gray-500" : "text-red-700"}`}>
                  <AlertCircle size={16} /> Required Information
                </div>
                <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                  <LeadEditField label="Company *">
                    <input type="text" value={formData.companyName} onChange={(e) => setFormData({...formData, companyName: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Company name" />
                  </LeadEditField>
                  <LeadEditField label="Contact Person *">
                    <input type="text" value={formData.contactPerson} onChange={(e) => setFormData({...formData, contactPerson: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Contact name" />
                  </LeadEditField>
                  <LeadEditField label="Email *">
                    <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="email@co.com" />
                  </LeadEditField>
                  <LeadEditField label="Source *">
                    <select value={formData.leadSource} onChange={(e) => setFormData({...formData, leadSource: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                      <option value="">Select</option>
                      {leadSources?.filter(ls => ls.isActive).map(source => (<option key={source.id} value={source.name}>{source.name}</option>))}
                    </select>
                  </LeadEditField>
                  <LeadEditField label="Geography *">
                    <select value={formData.geography || ''} onChange={(e) => setFormData({...formData, geography: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                      <option value="">Select</option>
                      {geographies?.filter(g => g.isActive).map(geo => (<option key={geo.id} value={geo.name}>{geo.name}</option>))}
                    </select>
                  </LeadEditField>
                  <LeadEditField label="Entity *">
                    <select value={formData.entity || ''} onChange={(e) => setFormData({...formData, entity: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                      <option value="">Select</option>
                      {entities?.filter((e: any) => e.isActive).map((ent: any) => (<option key={ent.id} value={ent.name}>{ent.name}</option>))}
                    </select>
                  </LeadEditField>
                </div>
              </div>
              ); })()}

              {/* OPTIONAL FIELDS — two columns */}
              <div className="grid grid-cols-2 gap-4">
                {/* LEFT column */}
                <div className="space-y-4">
                  {/* Contact & Source Details */}
                  <div className="bg-white border border-gray-100 rounded-lg p-4">
                    <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Building2 size={16} /> Contact & Source Details
                    </div>
                    <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                      <LeadEditField label="Phone *">
                        <input type="tel" value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Phone" />
                      </LeadEditField>
                      <LeadEditField label="Designation *">
                        <input type="text" value={formData.designation || ''} onChange={(e) => setFormData({...formData, designation: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Job title" />
                      </LeadEditField>
                      <LeadEditField label="Lead Owner *">
                        <select value={formData.leadOwner || ''} onChange={(e) => setFormData({...formData, leadOwner: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                          <option value="">Select</option>
                          {users?.filter(u => u.status === 'Active').map(user => (<option key={user.userId} value={user.name}>{user.name}</option>))}
                        </select>
                      </LeadEditField>
                      <LeadEditField label="Lead Supporter 1 *">
                        <input type="text" value={formData.leadSupporter1 || ''} onChange={(e) => setFormData({...formData, leadSupporter1: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Enter lead supporter name" />
                      </LeadEditField>
                      <LeadEditField label="Lead Supporter 2">
                        <input type="text" value={formData.leadSupporter2 || ''} onChange={(e) => setFormData({...formData, leadSupporter2: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Enter second lead supporter name (optional)" />
                      </LeadEditField>
                      <LeadEditField label="Solution Architect">
                        <select value={formData.solutionArchitect || ''} onChange={(e) => setFormData({...formData, solutionArchitect: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                          <option value="">Select</option>
                          {users?.filter(u => u.status === 'Active').map(user => (<option key={user.userId} value={user.name}>{user.name}</option>))}
                        </select>
                      </LeadEditField>
                      <LeadEditField label="Priority">
                        <select value={formData.priority || 'Medium'} onChange={(e) => setFormData({...formData, priority: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                          <option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option>
                        </select>
                      </LeadEditField>
                      <LeadEditField label="Lead Rating *">
                        <select value={formData.leadRating || ''} onChange={(e) => setFormData({...formData, leadRating: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                          <option value="">Select</option>
                          <option value="Hot">Hot</option>
                          <option value="Warm">Warm</option>
                          <option value="Cold">Cold</option>
                        </select>
                      </LeadEditField>
                      <LeadEditField label="Business Type *">
                        <select value={formData.businessType || ''} onChange={(e) => setFormData({...formData, businessType: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                          <option value="">Select</option>
                          <option value="Existing Business">Existing Business</option>
                          <option value="New Business">New Business</option>
                        </select>
                      </LeadEditField>
                      <LeadEditField label="Lead Generator *">
                        <input type="text" value={formData.leadGenerator || ''} onChange={(e) => setFormData({...formData, leadGenerator: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Enter lead generator name" />
                      </LeadEditField>
                      <LeadEditField label="Source Comments" className="col-span-3">
                        <input type="text" value={formData.sourceComments || ''} onChange={(e) => setFormData({...formData, sourceComments: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="e.g. Referred by John, met at AWS Summit..." />
                      </LeadEditField>
                    </div>
                  </div>

                  {/* Classification & Financial */}
                  <div className="bg-white border border-gray-100 rounded-lg p-4">
                    <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                      <IndianRupee size={16} /> Classification & Financial
                    </div>
                    <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                      <LeadEditField label="Industry *">
                        <select value={formData.industry || ''} onChange={(e) => setFormData({...formData, industry: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                          <option value="">Select</option>
                          {industries?.filter(ind => ind.isActive).map(ind => (<option key={ind.id} value={ind.name}>{ind.name}</option>))}
                        </select>
                      </LeadEditField>
                      <LeadEditField label="Service Line *">
                        <select value={formData.serviceLine || ''} onChange={(e) => setFormData({...formData, serviceLine: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                          <option value="">Select</option>
                          {serviceLines?.filter(sl => sl.isActive).map(sl => (<option key={sl.id} value={sl.name}>{sl.name}</option>))}
                        </select>
                      </LeadEditField>
                      <LeadEditField label="Est. Revenue (₹)">
                        <input type="number" value={formData.estimatedRevenue} onChange={(e) => setFormData({...formData, estimatedRevenue: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Revenue" />
                      </LeadEditField>
                      <LeadEditField label="City *">
                        <input type="text" value={formData.city || ''} onChange={(e) => setFormData({...formData, city: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="City" />
                      </LeadEditField>
                      <LeadEditField label="Status">
                        {formData.leadStatus === 'Converted' ? (
                          <div className="w-full px-2 py-1.5 text-xs border border-green-300 rounded-md bg-green-50 text-green-800 font-medium">Converted</div>
                        ) : formData.leadStatus === 'Lost' ? (
                          <div className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md bg-gray-50 text-gray-600 font-medium">Lost</div>
                        ) : (
                          <select value={formData.leadStatus} onChange={(e) => setFormData({...formData, leadStatus: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                            <option value="Open">Open</option><option value="Unqualified">Unqualified</option><option value="Lost">Lost</option>
                          </select>
                        )}
                      </LeadEditField>
                    </div>
                  </div>
                </div>

                {/* RIGHT column */}
                <div className="space-y-4">
                  <div className="bg-white border border-gray-100 rounded-lg p-4">
                    <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Target size={16} /> Pain Points / Requirements
                    </div>
                    <textarea value={formData.painPoints || ''} onChange={(e) => setFormData({...formData, painPoints: e.target.value})} rows={4}
                      className="w-full px-3 py-2 text-sm border border-teal-200 rounded-lg focus:ring-2 focus:ring-[#00BBA2] bg-white" placeholder="Describe pain points and requirements" />
                  </div>
                  <div className="bg-white border border-gray-100 rounded-lg p-4">
                    <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                      <MessageSquare size={16} /> Comments
                    </div>
                    <textarea value={formData.comments || ''} onChange={(e) => setFormData({...formData, comments: e.target.value})} rows={4}
                      className="w-full px-3 py-2 text-sm border border-rose-200 rounded-lg focus:ring-2 focus:ring-[#00BBA2] bg-white" placeholder="Additional notes" />
                  </div>
                  {/* Tags Input */}
                  <div className="bg-white border border-gray-100 rounded-lg p-4">
                    <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Tag size={16} /> Tags
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {selectedTagNames.map((tagName, idx) => {
                        const matchingTag = (tags || []).find((t: any) => t.name.toLowerCase() === tagName.toLowerCase());
                        const tagColor = matchingTag?.color || '#6A7CA5';
                        return (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                            style={{ backgroundColor: `${tagColor}20`, color: tagColor }}>
                            {tagName}
                            <button onClick={() => setSelectedTagNames(prev => prev.filter((_, i) => i !== idx))} className="hover:opacity-70 ml-0.5">
                              <X size={10} />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                    <div className="relative">
                      <input type="text" value={tagInput}
                        onChange={(e) => { setTagInput(e.target.value); setShowTagDropdown(true); }}
                        onFocus={() => setShowTagDropdown(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && tagInput.trim()) {
                            e.preventDefault();
                            const trimmed = tagInput.trim();
                            if (!selectedTagNames.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
                              setSelectedTagNames(prev => [...prev, trimmed]);
                            }
                            setTagInput(''); setShowTagDropdown(false);
                          }
                        }}
                        className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 bg-white"
                        placeholder="Type to add tags (Enter to add)..."
                      />
                      {showTagDropdown && tagInput.trim() && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                          {(tags || []).filter((t: any) => t.isActive && t.name.toLowerCase().includes(tagInput.toLowerCase()) && !selectedTagNames.some(s => s.toLowerCase() === t.name.toLowerCase()))
                            .map((tag: any) => (
                              <button key={tag.id} onClick={() => { setSelectedTagNames(prev => [...prev, tag.name]); setTagInput(''); setShowTagDropdown(false); }}
                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-[rgba(0,187,162,0.07)] flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color || '#6A7CA5' }}></span>
                                {tag.name}
                              </button>
                            ))
                          }
                          {!(tags || []).some((t: any) => t.name.toLowerCase() === tagInput.trim().toLowerCase()) && tagInput.trim() && (
                            <button onClick={() => { setSelectedTagNames(prev => [...prev, tagInput.trim()]); setTagInput(''); setShowTagDropdown(false); }}
                              className="w-full px-3 py-1.5 text-left text-sm hover:bg-[rgba(0,187,162,0.07)] text-[#00BBA2] font-medium">
                              + Create "{tagInput.trim()}"
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ===== EDIT MODE — Compact layout ===== */
            <div className="grid grid-cols-2 gap-3">
              {/* LEFT: Lead Info */}
              <div className="space-y-3">
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Building2 size={14} /> Lead & Contact
                  </div>
                  <div className="grid grid-cols-4 gap-x-2 gap-y-1.5">
                    <LeadEditField label="Company *">
                      <input type="text" value={formData.companyName} onChange={(e) => setFormData({...formData, companyName: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Company name" />
                    </LeadEditField>
                    <LeadEditField label="Contact *">
                      <input type="text" value={formData.contactPerson} onChange={(e) => setFormData({...formData, contactPerson: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Contact name" />
                    </LeadEditField>
                    <LeadEditField label="Email *" className="col-span-2">
                      <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="email@co.com" />
                    </LeadEditField>
                    <LeadEditField label="Phone *">
                      <input type="tel" value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Phone" />
                    </LeadEditField>
                    <LeadEditField label="Designation *">
                      <input type="text" value={formData.designation || ''} onChange={(e) => setFormData({...formData, designation: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Job title" />
                    </LeadEditField>
                    <LeadEditField label="Source *">
                      <select value={formData.leadSource} onChange={(e) => setFormData({...formData, leadSource: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="">Select</option>
                        {leadSources?.filter(ls => ls.isActive).map(source => (<option key={source.id} value={source.name}>{source.name}</option>))}
                      </select>
                    </LeadEditField>
                    <LeadEditField label="Priority">
                      <select value={formData.priority || 'Medium'} onChange={(e) => setFormData({...formData, priority: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option>
                      </select>
                    </LeadEditField>
                    <LeadEditField label="Lead Owner *">
                      <select value={formData.leadOwner || ''} onChange={(e) => setFormData({...formData, leadOwner: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="">Select</option>
                        {users?.filter(u => u.status === 'Active').map(user => (<option key={user.userId} value={user.name}>{user.name}</option>))}
                      </select>
                    </LeadEditField>
                    <LeadEditField label="Lead Supporter 1 *">
                      <input type="text" value={formData.leadSupporter1 || ''} onChange={(e) => setFormData({...formData, leadSupporter1: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Enter lead supporter name" />
                    </LeadEditField>
                    <LeadEditField label="Lead Supporter 2">
                      <input type="text" value={formData.leadSupporter2 || ''} onChange={(e) => setFormData({...formData, leadSupporter2: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Enter second lead supporter name (optional)" />
                    </LeadEditField>
                    <LeadEditField label="Solution Architect">
                      <select value={formData.solutionArchitect || ''} onChange={(e) => setFormData({...formData, solutionArchitect: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="">Select</option>
                        {users?.filter(u => u.status === 'Active').map(user => (<option key={user.userId} value={user.name}>{user.name}</option>))}
                      </select>
                    </LeadEditField>
                    <LeadEditField label="Date Captured">
                      <input type="date" value={formData.dateCaptured || ''} onChange={(e) => setFormData({...formData, dateCaptured: e.target.value})} className={LEAD_FORM_INPUT_CLASS} />
                    </LeadEditField>
                    <LeadEditField label="Source Comments" className="col-span-4">
                      <input type="text" value={formData.sourceComments || ''} onChange={(e) => setFormData({...formData, sourceComments: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="e.g. Referred by John..." />
                    </LeadEditField>
                  </div>
                </div>

                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <IndianRupee size={14} /> Classification & Financial
                  </div>
                  <div className="grid grid-cols-4 gap-x-2 gap-y-1.5">
                    <LeadEditField label="Geography *">
                      <select value={formData.geography || ''} onChange={(e) => setFormData({...formData, geography: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="">Select</option>
                        {geographies?.filter(g => g.isActive).map(geo => (<option key={geo.id} value={geo.name}>{geo.name}</option>))}
                      </select>
                    </LeadEditField>
                    <LeadEditField label="City *">
                      <input type="text" value={formData.city || ''} onChange={(e) => setFormData({...formData, city: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="City" />
                    </LeadEditField>
                    <LeadEditField label="Lead Rating *">
                      <select value={formData.leadRating || ''} onChange={(e) => setFormData({...formData, leadRating: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="">Select</option>
                        <option value="Hot">Hot</option>
                        <option value="Warm">Warm</option>
                        <option value="Cold">Cold</option>
                      </select>
                    </LeadEditField>
                    <LeadEditField label="Business Type *">
                      <select value={formData.businessType || ''} onChange={(e) => setFormData({...formData, businessType: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="">Select</option>
                        <option value="Existing Business">Existing Business</option>
                        <option value="New Business">New Business</option>
                      </select>
                    </LeadEditField>
                    <LeadEditField label="Lead Generator *">
                      <input type="text" value={formData.leadGenerator || ''} onChange={(e) => setFormData({...formData, leadGenerator: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Enter lead generator name" />
                    </LeadEditField>
                    <LeadEditField label="Entity *">
                      <select value={formData.entity || ''} onChange={(e) => setFormData({...formData, entity: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="">Select</option>
                        {entities?.filter((e: any) => e.isActive).map((ent: any) => (<option key={ent.id} value={ent.name}>{ent.name}</option>))}
                      </select>
                    </LeadEditField>
                    <LeadEditField label="Est. Revenue (₹)">
                      <input type="number" value={formData.estimatedRevenue} onChange={(e) => setFormData({...formData, estimatedRevenue: e.target.value})} className={LEAD_FORM_INPUT_CLASS} placeholder="Revenue" />
                    </LeadEditField>
                    <LeadEditField label="Industry *">
                      <select value={formData.industry || ''} onChange={(e) => setFormData({...formData, industry: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="">Select</option>
                        {industries?.filter(ind => ind.isActive).map(ind => (<option key={ind.id} value={ind.name}>{ind.name}</option>))}
                      </select>
                    </LeadEditField>
                    <LeadEditField label="Service Line *">
                      <select value={formData.serviceLine || ''} onChange={(e) => setFormData({...formData, serviceLine: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                        <option value="">Select</option>
                        {serviceLines?.filter(sl => sl.isActive).map(sl => (<option key={sl.id} value={sl.name}>{sl.name}</option>))}
                      </select>
                    </LeadEditField>
                    <LeadEditField label="Status">
                      {formData.leadStatus === 'Converted' ? (
                        <div className="w-full px-2 py-1.5 text-xs border border-green-300 rounded-md bg-green-50 text-green-800 font-medium">Converted</div>
                      ) : formData.leadStatus === 'Lost' ? (
                        <div className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md bg-gray-50 text-gray-600 font-medium">Lost</div>
                      ) : (
                        <select value={formData.leadStatus} onChange={(e) => setFormData({...formData, leadStatus: e.target.value})} className={LEAD_FORM_SELECT_CLASS}>
                          <option value="Open">Open</option><option value="Unqualified">Unqualified</option>
                        </select>
                      )}
                    </LeadEditField>
                  </div>
                </div>
              </div>

              {/* RIGHT: Engagement History, Pain Points, Comments, Tags */}
              <div className="space-y-3">
                {/* Engagement History (read-only in edit mode — entries added via input bar above) */}
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <FileText size={14} /> Engagement History
                  </div>
                  <div className="bg-white border border-cyan-100 rounded p-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 450px)' }}>
                    <TimelineDisplay entries={formData.engagementHistory} emptyMessage="No engagement history yet" />
                  </div>
                </div>

                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Target size={14} /> Pain Points / Requirements
                  </div>
                  <textarea value={formData.painPoints || ''} onChange={(e) => setFormData({...formData, painPoints: e.target.value})} rows={4}
                    className="w-full px-2 py-1.5 text-sm border border-teal-200 rounded-lg focus:ring-2 focus:ring-[#00BBA2] bg-white" placeholder="Describe pain points and requirements" />
                </div>
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <MessageSquare size={14} /> Comments
                  </div>
                  <textarea value={formData.comments || ''} onChange={(e) => setFormData({...formData, comments: e.target.value})} rows={4}
                    className="w-full px-2 py-1.5 text-sm border border-rose-200 rounded-lg focus:ring-2 focus:ring-[#00BBA2] bg-white" placeholder="Additional notes" />
                </div>

                {/* Tags Input */}
                <div className="bg-white border border-gray-100 rounded-lg p-3">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Tag size={14} /> Tags
                  </div>
                  {/* Selected tags */}
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {selectedTagNames.map((tagName, idx) => {
                      const matchingTag = (tags || []).find((t: any) => t.name.toLowerCase() === tagName.toLowerCase());
                      const tagColor = matchingTag?.color || '#6A7CA5';
                      return (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: `${tagColor}20`, color: tagColor }}>
                          {tagName}
                          <button onClick={() => setSelectedTagNames(prev => prev.filter((_, i) => i !== idx))} className="hover:opacity-70 ml-0.5">
                            <X size={10} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  {/* Tag input with autocomplete */}
                  <div className="relative">
                    <input
                      type="text" value={tagInput}
                      onChange={(e) => { setTagInput(e.target.value); setShowTagDropdown(true); }}
                      onFocus={() => setShowTagDropdown(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && tagInput.trim()) {
                          e.preventDefault();
                          const trimmed = tagInput.trim();
                          if (!selectedTagNames.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
                            setSelectedTagNames(prev => [...prev, trimmed]);
                          }
                          setTagInput(''); setShowTagDropdown(false);
                        }
                      }}
                      className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 bg-white"
                      placeholder="Type to add tags (Enter to add)..."
                    />
                    {showTagDropdown && tagInput.trim() && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                        {(tags || []).filter((t: any) => t.isActive && t.name.toLowerCase().includes(tagInput.toLowerCase()) && !selectedTagNames.some(s => s.toLowerCase() === t.name.toLowerCase()))
                          .map((tag: any) => (
                            <button key={tag.id} onClick={() => { setSelectedTagNames(prev => [...prev, tag.name]); setTagInput(''); setShowTagDropdown(false); }}
                              className="w-full px-3 py-1.5 text-left text-sm hover:bg-[rgba(0,187,162,0.07)] flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color || '#6A7CA5' }}></span>
                              {tag.name}
                            </button>
                          ))
                        }
                        {!(tags || []).some((t: any) => t.name.toLowerCase() === tagInput.trim().toLowerCase()) && tagInput.trim() && (
                          <button onClick={() => { setSelectedTagNames(prev => [...prev, tagInput.trim()]); setTagInput(''); setShowTagDropdown(false); }}
                            className="w-full px-3 py-1.5 text-left text-sm hover:bg-[rgba(0,187,162,0.07)] text-[#00BBA2] font-medium">
                            + Create "{tagInput.trim()}"
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isCreateMode && (
          <div className="px-6 py-2 border-t border-gray-200 bg-gray-50 flex justify-between items-center flex-shrink-0">
            <span className="text-xs text-gray-500">* Required fields</span>
            <button onClick={closeModal} className="px-4 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
});

// Customer Form Modal Component
  const CustomerFormModal = ({ serviceLines = [], geographies = [], customers, setCustomers, setShowCustomerModal, selectedCustomer, setSelectedCustomer, users = [] }) => {
    const defaultServiceLine = serviceLines?.find?.(sl => sl.isActive)?.name || 'Customer Support';
    const defaultGeography = geographies?.find?.(g => g.isActive)?.name || 'United States';

    const [formData, setFormData] = useState(selectedCustomer || {
      customerId: `CUST-${String(customers.length + 1).padStart(3, '0')}`,
      companyName: '',
      primaryContact: '',
      contactDesignation: '',
      email: '',
      phone: '',
      secondaryContact: '',
      secondaryContactEmail: '',
      secondaryContactPhone: '',
      secondaryContactDesignation: '',
      serviceLine: defaultServiceLine,
      geography: defaultGeography,
      contractValue: '',
      startDate: new Date().toISOString().split('T')[0],
      renewalDate: '',
      status: 'Active',
      healthScore: 85,
      projectCount: 1,
      accountManager: ''
    });

    // Update formData when selectedCustomer changes
    React.useEffect(() => {
      if (selectedCustomer) {
        setFormData(selectedCustomer);
      }
    }, [selectedCustomer]);
    
    const handleSubmit = async () => {
      if (!formData.companyName || !formData.primaryContact || !formData.email || !formData.phone || !formData.contractValue || !formData.renewalDate) {
        alert('Please fill in all required fields');
        return;
      }

      // Check if customer exists in the customers array
      const existingCustomer = customers.find(c => c.customerId === formData.customerId);

      // Map frontend fields to backend format
      const customerPayload = {
        customer_id: formData.customerId,
        company_name: formData.companyName,
        primary_contact: formData.primaryContact,
        contact_designation: formData.contactDesignation || null,
        email: formData.email,
        phone: formData.phone,
        secondary_contact: formData.secondaryContact || null,
        secondary_contact_email: formData.secondaryContactEmail || null,
        secondary_contact_phone: formData.secondaryContactPhone || null,
        secondary_contact_designation: formData.secondaryContactDesignation || null,
        service_line: formData.serviceLine,
        geography: formData.geography,
        contract_value: parseFloat(formData.contractValue) || 0,
        start_date: formData.startDate || null,
        renewal_date: formData.renewalDate,
        status: formData.status || 'Active',
        health_score: parseInt(formData.healthScore) || 0,
        project_count: parseInt(formData.projectCount) || 0,
        account_manager: formData.accountManager || null
      };

      try {
        const url = existingCustomer
          ? `${API_BASE_URL}/customers/${formData.customerId}`
          : `${API_BASE_URL}/customers`;

        const response = await fetch(url, {
          method: existingCustomer ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(customerPayload)
        });

        const result = await response.json();

        if (result.success) {
          if (existingCustomer) {
            // Edit mode - update in local state
            setCustomers(customers.map(c => c.customerId === formData.customerId ? formData : c));
          } else {
            // Create mode - add to local state
            setCustomers([...customers, formData]);
          }
          setShowCustomerModal(false);
          setSelectedCustomer(null);
          alert(`Customer ${existingCustomer ? 'updated' : 'created'} successfully!`);
        } else {
          alert(`Failed to ${existingCustomer ? 'update' : 'create'} customer: ${result.message}`);
        }
      } catch (error) {
        console.error('Error saving customer:', error);
        alert(`Error saving customer: ${error.message}`);
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">{selectedCustomer ? 'Edit Customer' : 'New Customer'}</h3>
              <button onClick={() => {setShowCustomerModal(false); setSelectedCustomer(null);}} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Customer ID</label>
                <input type="text" value={formData.customerId} disabled className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-gray-50" />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Company Name*</label>
                <input type="text" value={formData.companyName} onChange={(e) => setFormData({...formData, companyName: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Primary Contact*</label>
                <input type="text" value={formData.primaryContact} onChange={(e) => setFormData({...formData, primaryContact: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Contact Designation</label>
                <input type="text" value={formData.contactDesignation || ''} onChange={(e) => setFormData({...formData, contactDesignation: e.target.value})} placeholder="e.g., CTO, VP Operations" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email*</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone*</label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              {/* Secondary Contact Section */}
              <div className="col-span-2 border-t border-gray-200 pt-3 mt-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Secondary Contact (Optional)</h4>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Secondary Contact Name</label>
                <input type="text" value={formData.secondaryContact || ''} onChange={(e) => setFormData({...formData, secondaryContact: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Secondary Contact Designation</label>
                <input type="text" value={formData.secondaryContactDesignation || ''} onChange={(e) => setFormData({...formData, secondaryContactDesignation: e.target.value})} placeholder="e.g., CFO, Director" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Secondary Contact Email</label>
                <input type="email" value={formData.secondaryContactEmail || ''} onChange={(e) => setFormData({...formData, secondaryContactEmail: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Secondary Contact Phone</label>
                <input type="tel" value={formData.secondaryContactPhone || ''} onChange={(e) => setFormData({...formData, secondaryContactPhone: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              {/* Contract Details Section */}
              <div className="col-span-2 border-t border-gray-200 pt-3 mt-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Contract Details</h4>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Service Line</label>
                <select value={formData.serviceLine} onChange={(e) => setFormData({...formData, serviceLine: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md">
                  {serviceLines && serviceLines.length > 0 ? (
                    serviceLines.filter(sl => sl.isActive).map(sl => (
                      <option key={sl.id} value={sl.name}>{sl.name}</option>
                    ))
                  ) : (
                    <option>Customer Support</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Geography</label>
                <select value={formData.geography} onChange={(e) => setFormData({...formData, geography: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md">
                  {geographies && geographies.length > 0 ? (
                    geographies.filter(g => g.isActive).map(g => (
                      <option key={g.id} value={g.name}>{g.name}</option>
                    ))
                  ) : (
                    <option>United States</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Contract Value*</label>
                <input type="number" value={formData.contractValue} onChange={(e) => setFormData({...formData, contractValue: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                <input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Renewal Date*</label>
                <input type="date" value={formData.renewalDate} onChange={(e) => setFormData({...formData, renewalDate: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md">
                  <option>Active</option>
                  <option>Inactive</option>
                  <option>Churned</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Health Score (0-100)</label>
                <input type="number" min="0" max="100" value={formData.healthScore} onChange={(e) => setFormData({...formData, healthScore: parseInt(e.target.value) || 0})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project Count</label>
                <input type="number" min="0" value={formData.projectCount} onChange={(e) => setFormData({...formData, projectCount: parseInt(e.target.value) || 0})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Account Manager</label>
                <select value={formData.accountManager || ''} onChange={(e) => setFormData({...formData, accountManager: e.target.value})} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md">
                  <option value="">Select account manager</option>
                  {users.filter(u => u.status === 'Active').map(user => (
                    <option key={user.userId} value={user.name}>{user.name}{user.role ? ` (${user.role})` : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="secondary" onClick={() => {setShowCustomerModal(false); setSelectedCustomer(null);}}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                {selectedCustomer ? 'Update' : 'Create'} Customer
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

// ========================================
// TASK SHEET MODAL COMPONENT
// ========================================
// Displays a task sheet grouped by salesperson with stage-based actions,
// send functionality with email preview, and visual indicators for repeating tasks

interface TaskSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: any[];
  opportunities: any[];
  users: any[];
}

const TaskSheetModal = ({ isOpen, onClose, tasks, opportunities, users }: TaskSheetModalProps) => {
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [sendPreview, setSendPreview] = useState<{ owner: string; email: string; tasks: any[] } | null>(null);
  const [sendingStatus, setSendingStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');

  if (!isOpen) return null;

  // Stage-based action recommendations
  const stageActions: { [key: string]: { action: string; tips: string[] } } = {
    'Qualified': {
      action: 'Validate & Plan Discovery',
      tips: ['Review lead qualification notes', 'Confirm ICP criteria met', 'Schedule initial discovery call', 'Prepare account research', 'Identify key stakeholders']
    },
    'Discovery': {
      action: 'Understand Needs & Pain Points',
      tips: ['Schedule discovery meeting', 'Confirm budget availability', 'Prepare discovery questions', 'Document requirements', 'Identify budget holder']
    },
    'Proposal': {
      action: 'Follow Up on Proposal',
      tips: ['Schedule proposal review', 'Address initial questions', 'Clarify scope/pricing', 'Identify competitors']
    },
    'Contract': {
      action: 'Finalise Contract',
      tips: ['Review contract terms', 'Coordinate with legal', 'Get internal approvals', 'Set implementation date']
    },
    'Closed-Won': {
      action: 'Complete Contracting',
      tips: ['Finalize all terms', 'Get executive sign-off', 'Prepare kickoff plan', 'Initiate contracting']
    }
  };

  // Get task data with opportunity details
  const tasksWithDetails = tasks
    .filter(task => task.status !== 'Completed' && task.status !== 'Cancelled')
    .map(task => {
      const opp = opportunities?.find(o => o.opportunityId === task.relatedId);
      return {
        ...task,
        opportunity: opp,
        salesStage: opp?.salesStage || 'Unknown',
        companyName: opp?.associatedAccount || task.relatedTo || '',
        geography: opp?.geography || '',
        serviceLine: opp?.serviceLine || '',
        annualContractValue: opp?.annualContractValue || 0
      };
    })
    .filter(task => !['Closed-Won', 'Closed-Lost', 'Contract'].includes(task.salesStage));

  // Detect repeating tasks (same task type/description for same opportunity over multiple weeks)
  const getRepeatCount = (task: any): number => {
    const allTasksForOpp = tasks.filter(t =>
      t.relatedId === task.relatedId &&
      t.relatedType === task.relatedType &&
      (t.task === task.task || t.type === task.type)
    );
    return allTasksForOpp.length;
  };

  // Group tasks by salesperson only
  const groupTasks = () => {
    const grouped: { [key: string]: any[] } = {};

    tasksWithDetails.forEach(task => {
      const key = task.owner || 'Unassigned';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ ...task, repeatCount: getRepeatCount(task) });
    });

    // Sort groups and tasks within groups
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, tasks]) => ({
        key,
        tasks: tasks.sort((a, b) => {
          // Sort by repeat count (descending), then by due date
          if (b.repeatCount !== a.repeatCount) return b.repeatCount - a.repeatCount;
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        })
      }));
  };

  const groupedTasks = groupTasks();

  // Get all unique task owners from actual tasks (not just from users list)
  // This ensures all opportunity owners with tasks are shown in the filter
  const allTaskOwners = groupedTasks.map(g => g.key).filter(k => k !== 'Unassigned').sort();

  // Filter by selected owners if any
  const filteredGroups = selectedOwners.length > 0
    ? groupedTasks.filter(g => selectedOwners.includes(g.key))
    : groupedTasks;

  // Get repeat indicator color based on count
  const getRepeatColor = (count: number) => {
    if (count >= 4) return { bg: '#FEE2E2', border: '#EF4444', text: '#B91C1C' }; // Red - critical
    if (count >= 3) return { bg: '#FEF3C7', border: '#F59E0B', text: '#B45309' }; // Amber - warning
    if (count >= 2) return { bg: '#E0E7FF', border: '#6366F1', text: '#4338CA' }; // Indigo - attention
    return null;
  };

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'No date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Calculate days until due
  const getDaysUntil = (dateStr: string) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dateStr);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Get user email
  const getUserEmail = (ownerName: string) => {
    const user = users?.find(u => u.name === ownerName);
    return user?.email || `${ownerName.toLowerCase().replace(/\s+/g, '.')}@pierianservices.com`;
  };

  // Handle send button click - show preview
  const handleSendClick = (ownerName: string, ownerTasks: any[]) => {
    const email = getUserEmail(ownerName);
    setSendPreview({ owner: ownerName, email, tasks: ownerTasks });
    setSendingStatus('idle');
  };

  // Handle actual send
  const handleConfirmSend = async () => {
    if (!sendPreview) return;

    setSendingStatus('sending');

    // Simulate sending email (in production, this would call an API)
    try {
      // Build email content
      const emailContent = buildEmailContent(sendPreview.owner, sendPreview.tasks);

      console.log('Sending task sheet email:', {
        to: sendPreview.email,
        subject: `Weekly Task Sheet - ${sendPreview.tasks.length} Action Items`,
        body: emailContent
      });

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSendingStatus('sent');

      // Auto-close preview after 2 seconds
      setTimeout(() => {
        setSendPreview(null);
        setSendingStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
      setSendingStatus('idle');
    }
  };

  // Build email content
  const buildEmailContent = (owner: string, ownerTasks: any[]) => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const totalValue = ownerTasks.reduce((sum, t) => sum + (t.annualContractValue || 0), 0);
    const repeatingCount = ownerTasks.filter(t => t.repeatCount > 1).length;

    let content = `Weekly Task Sheet - ${today}\n`;
    content += `=====================================\n\n`;
    content += `Hi ${owner.split(' ')[0]},\n\n`;
    content += `Here are your ${ownerTasks.length} action items for this week:\n`;
    content += `Total Pipeline Value: ₹${(totalValue / 1000).toFixed(0)}K\n`;
    if (repeatingCount > 0) {
      content += `⚠️ ${repeatingCount} task(s) are repeating from previous weeks and need attention.\n`;
    }
    content += `\n`;

    ownerTasks.forEach((task, idx) => {
      const stageAction = stageActions[task.salesStage];
      const daysUntil = getDaysUntil(task.dueDate);
      const dueStatus = daysUntil !== null
        ? (daysUntil < 0 ? `OVERDUE by ${Math.abs(daysUntil)} days` : daysUntil === 0 ? 'Due TODAY' : `Due in ${daysUntil} days`)
        : '';

      content += `${idx + 1}. ${task.companyName}\n`;
      content += `   Stage: ${task.salesStage} | Action: ${stageAction?.action || task.task}\n`;
      content += `   Due: ${formatDate(task.dueDate)} (${dueStatus})\n`;
      content += `   Value: ₹${((task.annualContractValue || 0) / 1000).toFixed(0)}K | Priority: ${task.priority}\n`;
      if (task.repeatCount > 1) {
        content += `   ⚠️ This task has been repeating for ${task.repeatCount} weeks\n`;
      }
      if (stageAction) {
        content += `   Tips: ${stageAction.tips.slice(0, 2).join(', ')}\n`;
      }
      content += `\n`;
    });

    content += `\nBest regards,\nPierag Sales Connect CRM`;
    return content;
  };

  // Build HTML email content for copying (formats nicely in email clients)
  const buildHtmlEmailContent = (owner: string, ownerTasks: any[]) => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const totalValue = ownerTasks.reduce((sum, t) => sum + (t.annualContractValue || 0), 0);
    const repeatingCount = ownerTasks.filter(t => t.repeatCount > 1).length;

    let html = `<div style="font-family: Arial, sans-serif; max-width: 600px;">`;
    html += `<h2 style="color: #1f2937; margin-bottom: 5px;">Weekly Task Sheet</h2>`;
    html += `<p style="color: #6b7280; margin-top: 0;">${today}</p>`;
    html += `<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 15px 0;">`;

    html += `<p style="color: #374151;">Hi ${owner.split(' ')[0]},</p>`;
    html += `<p style="color: #374151;">Here are your <strong>${ownerTasks.length} action items</strong> for this week:</p>`;
    html += `<p style="color: #374151;">Total Pipeline Value: <strong style="color: #059669;">₹${(totalValue / 1000).toFixed(0)}K</strong>`;
    if (repeatingCount > 0) {
      html += ` <span style="color: #d97706;">⚠️ ${repeatingCount} task(s) repeating</span>`;
    }
    html += `</p>`;

    html += `<table style="width: 100%; border-collapse: collapse; margin-top: 15px;">`;
    html += `<thead><tr style="background-color: #f3f4f6;">`;
    html += `<th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151;">#</th>`;
    html += `<th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151;">Company</th>`;
    html += `<th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151;">Stage / Action</th>`;
    html += `<th style="padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151;">Due Date</th>`;
    html += `<th style="padding: 10px; text-align: right; border-bottom: 2px solid #e5e7eb; color: #374151;">Value</th>`;
    html += `</tr></thead><tbody>`;

    ownerTasks.forEach((task, idx) => {
      const stageAction = stageActions[task.salesStage];
      const daysUntil = getDaysUntil(task.dueDate);
      const isOverdue = daysUntil !== null && daysUntil < 0;
      const isDueToday = daysUntil === 0;
      const isRepeating = task.repeatCount > 1;

      const rowBg = isRepeating ? (task.repeatCount >= 4 ? 'rgba(0,187,162,0.08)' : task.repeatCount >= 3 ? '#fef3c7' : 'rgba(106,124,165,0.18)') : (idx % 2 === 0 ? '#ffffff' : '#f9fafb');
      const borderLeft = isRepeating ? `3px solid ${task.repeatCount >= 4 ? '#ef4444' : task.repeatCount >= 3 ? '#f59e0b' : '#6366f1'}` : 'none';

      html += `<tr style="background-color: ${rowBg};">`;
      html += `<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; border-left: ${borderLeft}; vertical-align: top;">${idx + 1}</td>`;
      html += `<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top;">`;
      html += `<strong style="color: #1f2937;">${task.companyName}</strong>`;
      if (isRepeating) {
        html += `<br><span style="color: ${task.repeatCount >= 4 ? '#b91c1c' : task.repeatCount >= 3 ? '#b45309' : '#4B5B7A'}; font-size: 12px;">🔄 Repeating ${task.repeatCount}x</span>`;
      }
      html += `</td>`;
      html += `<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top;">`;
      html += `<span style="background-color: #e5e7eb; padding: 2px 8px; border-radius: 4px; font-size: 12px;">${task.salesStage}</span><br>`;
      html += `<span style="color: #374151;">${stageAction?.action || task.task}</span>`;
      if (stageAction) {
        html += `<br><span style="color: #9ca3af; font-size: 12px;">Tips: ${stageAction.tips.slice(0, 2).join(', ')}</span>`;
      }
      html += `</td>`;
      html += `<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top;">`;
      html += `<span style="color: #1f2937;">${formatDate(task.dueDate)}</span><br>`;
      if (isOverdue) {
        html += `<span style="color: #dc2626; font-weight: bold; font-size: 12px;">${Math.abs(daysUntil)} days overdue</span>`;
      } else if (isDueToday) {
        html += `<span style="color: #d97706; font-weight: bold; font-size: 12px;">Due today</span>`;
      } else if (daysUntil !== null) {
        html += `<span style="color: #6b7280; font-size: 12px;">${daysUntil} days left</span>`;
      }
      html += `</td>`;
      html += `<td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; vertical-align: top;">`;
      html += `<strong style="color: #1f2937;">₹${((task.annualContractValue || 0) / 1000).toFixed(0)}K</strong><br>`;
      html += `<span style="color: #6b7280; font-size: 12px;">${task.priority}</span>`;
      html += `</td>`;
      html += `</tr>`;
    });

    html += `</tbody></table>`;

    html += `<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 20px 0;">`;
    html += `<p style="color: #374151;">Best regards,<br><strong>Pierag Sales Connect CRM</strong></p>`;
    html += `</div>`;

    return html;
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = async () => {
    if (!sendPreview) return;

    try {
      const htmlContent = buildHtmlEmailContent(sendPreview.owner, sendPreview.tasks);
      const plainTextContent = buildEmailContent(sendPreview.owner, sendPreview.tasks);

      // Try to copy as HTML (works in most modern browsers)
      if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const textBlob = new Blob([plainTextContent], { type: 'text/plain' });
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        });
        await navigator.clipboard.write([clipboardItem]);
      } else {
        // Fallback to plain text
        await navigator.clipboard.writeText(plainTextContent);
      }

      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Final fallback using execCommand
      try {
        const textArea = document.createElement('textarea');
        textArea.value = buildEmailContent(sendPreview.owner, sendPreview.tasks);
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopyStatus('copied');
        setTimeout(() => setCopyStatus('idle'), 2000);
      } catch (e) {
        alert('Failed to copy to clipboard. Please select and copy manually.');
      }
    }
  };

  // Calculate totals
  const totalTasks = filteredGroups.reduce((sum, g) => sum + g.tasks.length, 0);
  const repeatingTasks = filteredGroups.reduce((sum, g) => sum + g.tasks.filter(t => t.repeatCount > 1).length, 0);
  const totalValue = filteredGroups.reduce((sum, g) => sum + g.tasks.reduce((s, t) => s + (t.annualContractValue || 0), 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-pierian-10 rounded-lg">
              <FileSpreadsheet className="text-pierian" size={24} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Task Sheet</h2>
              <p className="text-sm text-gray-500">Weekly action items grouped by salesperson</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Filter Bar */}
        <div className="px-6 py-3 bg-gray-50 border-b flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Filter by:</span>
            <div className="flex gap-1 flex-wrap">
              {allTaskOwners.map(owner => (
                <button
                  key={owner}
                  onClick={() => setSelectedOwners(prev =>
                    prev.includes(owner) ? prev.filter(u => u !== owner) : [...prev, owner]
                  )}
                  className="px-2 py-1 text-xs rounded-md transition-colors"
                  style={{
                    backgroundColor: selectedOwners.includes(owner) ? '#00BBA2' : 'var(--dm-btn-bg)',
                    color: selectedOwners.includes(owner) ? 'white' : 'var(--dm-btn-text)'
                  }}
                >
                  {owner}
                </button>
              ))}
              {selectedOwners.length > 0 && (
                <button
                  onClick={() => setSelectedOwners([])}
                  className="px-2 py-1 text-xs rounded-md bg-gray-200 text-gray-600 hover:bg-gray-300"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="flex-1" />

          {/* Summary Stats */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Tasks:</span>
              <span className="font-semibold">{totalTasks}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Repeating:</span>
              <span className="font-semibold text-amber-600">{repeatingTasks}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Value:</span>
              <span className="font-semibold text-green-600">₹{(totalValue / 1000000).toFixed(2)}M</span>
            </div>
          </div>
        </div>

        {/* Legend for repeat indicators */}
        <div className="px-6 py-2 bg-gray-50 border-b flex items-center gap-4 text-xs">
          <span className="text-gray-500 font-medium">Repeat Indicators:</span>
          <div className="flex items-center gap-1 px-2 py-0.5 rounded" style={{ backgroundColor: '#E0E7FF', border: '1px solid #6366F1' }}>
            <RefreshCw size={10} className="text-[#4B5B7A]" />
            <span className="text-[#4B5B7A]">2x - Attention</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-0.5 rounded" style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}>
            <RefreshCw size={10} className="text-amber-700" />
            <span className="text-amber-700">3x - Warning</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-0.5 rounded" style={{ backgroundColor: '#FEE2E2', border: '1px solid #EF4444' }}>
            <RefreshCw size={10} className="text-red-700" />
            <span className="text-red-700">4x+ - Critical</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {filteredGroups.length === 0 ? (
            <div className="text-center py-12">
              <FileSpreadsheet size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No pending tasks found</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredGroups.map(({ key, tasks: groupTasks }) => (
                <div key={key} className="border rounded-lg overflow-hidden">
                  {/* Group Header with Send Button */}
                  <div className="bg-gray-100 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-pierian rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {key.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{key}</h3>
                        <p className="text-xs text-gray-500">
                          {groupTasks.length} task{groupTasks.length !== 1 ? 's' : ''} • ${(groupTasks.reduce((s, t) => s + (t.annualContractValue || 0), 0) / 1000).toFixed(0)}K pipeline
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {groupTasks.filter(t => t.repeatCount > 1).length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          <AlertCircle size={12} />
                          {groupTasks.filter(t => t.repeatCount > 1).length} repeating
                        </div>
                      )}
                      <Button
                        variant="secondary"
                        onClick={() => handleSendClick(key, groupTasks)}
                        className="text-xs px-3 py-1.5"
                      >
                        <Send size={14} className="mr-1" /> Send
                      </Button>
                    </div>
                  </div>

                  {/* Tasks Table */}
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="px-4 py-2 font-medium text-gray-600 w-1/4">Company / Opportunity</th>
                        <th className="px-4 py-2 font-medium text-gray-600 w-1/6">Stage</th>
                        <th className="px-4 py-2 font-medium text-gray-600 w-1/4">Action Required</th>
                        <th className="px-4 py-2 font-medium text-gray-600 w-1/6">Due Date</th>
                        <th className="px-4 py-2 font-medium text-gray-600 w-1/6 text-right">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupTasks.map((task, idx) => {
                        const repeatColor = getRepeatColor(task.repeatCount);
                        const daysUntil = getDaysUntil(task.dueDate);
                        const stageAction = stageActions[task.salesStage];

                        return (
                          <tr
                            key={task.id || idx}
                            className="border-t hover:bg-gray-50"
                            style={repeatColor ? { backgroundColor: repeatColor.bg } : {}}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-start gap-2">
                                {repeatColor && (
                                  <div
                                    className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium shrink-0"
                                    style={{ backgroundColor: repeatColor.bg, border: `1px solid ${repeatColor.border}`, color: repeatColor.text }}
                                  >
                                    <RefreshCw size={10} />
                                    {task.repeatCount}x
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-gray-900">{task.companyName}</p>
                                  <p className="text-xs text-gray-500">{task.geography} • {task.serviceLine}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                {task.salesStage}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-800">{stageAction?.action || task.task}</p>
                              {stageAction && (
                                <ul className="text-xs text-gray-500 mt-1 space-y-0.5">
                                  {stageAction.tips.slice(0, 2).map((tip, i) => (
                                    <li key={i} className="flex items-start gap-1">
                                      <span className="text-pierian">•</span> {tip}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium">{formatDate(task.dueDate)}</p>
                              <p className={`text-xs ${daysUntil !== null && daysUntil < 0 ? 'text-red-600 font-medium' : daysUntil === 0 ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
                                {daysUntil === null ? '' : daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : daysUntil === 0 ? 'Due today' : `${daysUntil} days left`}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <p className="font-medium text-gray-900">₹{((task.annualContractValue || 0) / 1000).toFixed(0)}K</p>
                              <p className="text-xs text-gray-500">{task.priority} priority</p>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-between text-sm text-gray-500">
          <p>Generated on {new Date().toLocaleString()}</p>
          <p>Tasks repeating 2+ weeks may need escalation or strategy review</p>
        </div>
      </div>

      {/* Send Preview Modal */}
      {sendPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Preview Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pierian-10 rounded-lg">
                  <Mail className="text-pierian" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Email Preview</h3>
                  <p className="text-sm text-gray-500">Review before sending to {sendPreview.owner}</p>
                </div>
              </div>
              <button onClick={() => setSendPreview(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={18} />
              </button>
            </div>

            {/* Email Details */}
            <div className="px-6 py-3 border-b bg-white">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-gray-600 w-12">To:</span>
                <span className="text-gray-900">{sendPreview.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <span className="font-medium text-gray-600 w-12">Subject:</span>
                <span className="text-gray-900">Weekly Task Sheet - {sendPreview.tasks.length} Action Items</span>
              </div>
            </div>

            {/* Email Content Preview */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="bg-gray-50 rounded-lg p-4 border">
                <p className="text-sm text-gray-700 mb-3">
                  Hi {sendPreview.owner.split(' ')[0]},
                </p>
                <p className="text-sm text-gray-700 mb-3">
                  Here are your <strong>{sendPreview.tasks.length} action items</strong> for this week:
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Total Pipeline Value: <strong>₹{(sendPreview.tasks.reduce((s, t) => s + (t.annualContractValue || 0), 0) / 1000).toFixed(0)}K</strong>
                  {sendPreview.tasks.filter(t => t.repeatCount > 1).length > 0 && (
                    <span className="ml-2 text-amber-600">
                      ⚠️ {sendPreview.tasks.filter(t => t.repeatCount > 1).length} task(s) repeating
                    </span>
                  )}
                </p>

                <div className="space-y-3">
                  {sendPreview.tasks.map((task, idx) => {
                    const stageAction = stageActions[task.salesStage];
                    const daysUntil = getDaysUntil(task.dueDate);
                    const repeatColor = getRepeatColor(task.repeatCount);

                    return (
                      <div
                        key={idx}
                        className="bg-white rounded border p-3"
                        style={repeatColor ? { borderLeftWidth: 3, borderLeftColor: repeatColor.border } : {}}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{idx + 1}. {task.companyName}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {task.salesStage} • {stageAction?.action || task.task}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">₹{((task.annualContractValue || 0) / 1000).toFixed(0)}K</p>
                            <p className={`text-xs ${daysUntil !== null && daysUntil < 0 ? 'text-red-600' : daysUntil === 0 ? 'text-amber-600' : 'text-gray-500'}`}>
                              {formatDate(task.dueDate)}
                            </p>
                          </div>
                        </div>
                        {task.repeatCount > 1 && (
                          <div className="flex items-center gap-1 mt-2 text-xs" style={{ color: repeatColor?.text }}>
                            <RefreshCw size={10} />
                            Repeating for {task.repeatCount} weeks - needs attention
                          </div>
                        )}
                        {stageAction && (
                          <p className="text-xs text-gray-500 mt-2">
                            Tips: {stageAction.tips.slice(0, 2).join(', ')}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="text-sm text-gray-600 mt-4">
                  Best regards,<br />
                  Pierag Sales Connect CRM
                </p>
              </div>
            </div>

            {/* Preview Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-500">Copy to paste into your email client</p>
              <div className="flex items-center gap-3">
                <Button variant="secondary" onClick={() => setSendPreview(null)}>
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCopyToClipboard}
                  className={copyStatus === 'copied' ? 'bg-green-100 text-green-700 border-green-300' : ''}
                >
                  {copyStatus === 'idle' ? (
                    <>
                      <FileText size={16} className="mr-1" /> Copy Content
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} className="mr-1" /> Copied!
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleConfirmSend}
                  disabled={sendingStatus !== 'idle'}
                  className={sendingStatus === 'sent' ? 'bg-green-600 hover:bg-green-600' : ''}
                >
                  {sendingStatus === 'idle' && (
                    <>
                      <Send size={16} className="mr-1" /> Send Email
                    </>
                  )}
                  {sendingStatus === 'sending' && (
                    <>
                      <RefreshCw size={16} className="mr-1 animate-spin" /> Sending...
                    </>
                  )}
                  {sendingStatus === 'sent' && (
                    <>
                      <CheckCircle size={16} className="mr-1" /> Sent!
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Calendar View with Tasks
const CalendarView = ({ currentUser, tasks: propTasks, setTasks: propSetTasks, opportunities, users, serviceLines = [], salesStages = [], geographies = [] }) => {
  const { theme: calTheme } = useTheme();
  const isDark = calTheme.mode === 'dark';
  const [calendarSubTab, setCalendarSubTab] = useState('tasks');
  const [tasks, setTasks] = useState(propTasks || []);
  const [taskComments, setTaskComments] = useState({});
  const [taskPostpone, setTaskPostpone] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [taskReassignOpen, setTaskReassignOpen] = useState({});
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showTaskSheet, setShowTaskSheet] = useState(false);
  const [activityGroupBy, setActivityGroupBy] = useState('salesperson');

  // Debug: Log tasks when component renders
  console.log('CalendarView - Total tasks:', tasks?.length || 0);
  console.log('CalendarView - Tasks:', tasks);
  const [newTask, setNewTask] = useState({
    task: '',
    owner: users && users.length > 0 && users.filter(u => u.status === 'Active').length > 0
      ? users.filter(u => u.status === 'Active')[0].name
      : '',
    dueDate: new Date().toISOString().split('T')[0],
    priority: 'Medium',
    status: 'Pending',
    type: 'Follow-up',
    relatedTo: '',
    relatedId: '',
    relatedType: 'Opportunity'
  });

  // Filter states - multi-select arrays like Opportunities tab
  const [searchTask, setSearchTask] = useState('');
  const [activeTaskFilterType, setActiveTaskFilterType] = useState(null); // 'geography', 'salesperson', 'serviceline', 'salesstage'
  const [selectedTaskGeographies, setSelectedTaskGeographies] = useState([]);
  const [selectedTaskOwners, setSelectedTaskOwners] = useState([]);
  const [selectedTaskServiceLines, setSelectedTaskServiceLines] = useState([]);
  const [selectedTaskSalesStages, setSelectedTaskSalesStages] = useState([]);
  const [expandedTaskFilterOptions, setExpandedTaskFilterOptions] = useState({});
  const [selectedStatFilter, setSelectedStatFilter] = useState('all'); // 'all', 'dueToday', 'highPriority', 'completed'

  // Check if any task filters are active
  const hasTaskActiveFilters = selectedTaskGeographies.length > 0 || selectedTaskOwners.length > 0 ||
    selectedTaskServiceLines.length > 0 || selectedTaskSalesStages.length > 0;

  // Toggle filter selection for tasks
  const toggleTaskFilterSelection = (filterType, value) => {
    switch (filterType) {
      case 'geography':
        setSelectedTaskGeographies(prev =>
          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        break;
      case 'salesperson':
        setSelectedTaskOwners(prev =>
          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        break;
      case 'serviceline':
        setSelectedTaskServiceLines(prev =>
          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        break;
      case 'salesstage':
        setSelectedTaskSalesStages(prev =>
          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        break;
    }
  };

  // Clear all task filters
  const clearTaskAllFilters = () => {
    setSelectedTaskGeographies([]);
    setSelectedTaskOwners([]);
    setSelectedTaskServiceLines([]);
    setSelectedTaskSalesStages([]);
    setActiveTaskFilterType(null);
    setSearchTask('');
    setSelectedStatFilter('all');
  };
  
  // Sync with prop changes
  React.useEffect(() => {
    if (propTasks) {
      setTasks(propTasks);
    }
  }, [propTasks]);

  // Helper function to calculate days until due date
  const getDaysUntil = (dueDate) => {
    if (!dueDate) return 999; // Return a large number for invalid dates

    // Get today's date in local timezone as YYYY-MM-DD
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    // Compare date strings directly to avoid timezone issues
    if (dueDate === todayStr) {
      return 0;
    }

    // Parse both dates in local time for accurate comparison
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dueParts = dueDate.split('-');
    const dueYear = parseInt(dueParts[0]);
    const dueMonth = parseInt(dueParts[1]) - 1; // Month is 0-indexed
    const dueDay = parseInt(dueParts[2]);
    const dueLocalDate = new Date(dueYear, dueMonth, dueDay);

    const diffTime = dueLocalDate - todayDate;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get unique filter values
  const uniqueOwners = [...new Set(tasks.map(t => t.owner).filter(Boolean))];

  // Get data from related opportunities and filter out Won/Lost opportunities
  const tasksWithOppData = tasks.map(task => {
    if (task.relatedType === 'Opportunity' && task.relatedId) {
      const opp = opportunities?.find(o => o.opportunityId === task.relatedId);
      return {
        ...task,
        serviceLine: opp?.serviceLine || '',
        salesStage: opp?.salesStage || '',
        geography: opp?.geography || '',
        companyName: opp?.associatedAccount || task.relatedTo || '',
        isWonOrLost: opp?.salesStage === 'Closed-Won' || opp?.salesStage === 'Closed-Lost'
      };
    }
    return {
      ...task,
      serviceLine: '',
      salesStage: '',
      geography: '',
      companyName: task.relatedTo || '',
      isWonOrLost: false
    };
  }).filter(task => !task.isWonOrLost && task.status !== 'Cancelled'); // Exclude tasks for Won/Lost opportunities and Cancelled tasks

  // Show all tasks — no deduplication, every created task is visible
  const deduplicatedTasks = tasksWithOppData;

  // Use master data for dropdowns (filter active items only)
  const masterServiceLines = serviceLines.filter(sl => sl.isActive).map(sl => sl.name?.trim());
  const masterSalesStages = salesStages.filter(ss => ss.isActive).map(ss => ss.name?.trim());
  const masterGeographies = geographies.filter(geo => geo.isActive).map(geo => geo.name?.trim());

  // Get unique sales stages from actual task data (as fallback if master data is empty or doesn't match)
  const uniqueTaskSalesStages = [...new Set(tasksWithOppData.map(t => t.salesStage?.trim()).filter(Boolean))];

  // Use master stages if available, otherwise use stages from tasks
  const effectiveSalesStages = masterSalesStages.length > 0 ? masterSalesStages : uniqueTaskSalesStages;

  console.log('CalendarView - masterSalesStages:', masterSalesStages);
  console.log('CalendarView - uniqueTaskSalesStages:', uniqueTaskSalesStages);
  console.log('CalendarView - Selected stat filter:', selectedStatFilter);
  console.log('CalendarView - deduplicatedTasks count:', deduplicatedTasks.length);

  // Apply filters (multi-select arrays)
  const filteredTasks = deduplicatedTasks.filter(task => {
    const matchesSearch = searchTask === '' ||
                        task.task?.toLowerCase().includes(searchTask.toLowerCase()) ||
                        task.companyName?.toLowerCase().includes(searchTask.toLowerCase()) ||
                        task.owner?.toLowerCase().includes(searchTask.toLowerCase());
    const matchesOwner = selectedTaskOwners.length === 0 || selectedTaskOwners.includes(task.owner);
    const matchesServiceLine = selectedTaskServiceLines.length === 0 || selectedTaskServiceLines.includes(task.serviceLine);
    const matchesSalesStage = selectedTaskSalesStages.length === 0 || selectedTaskSalesStages.some(s => s?.toLowerCase() === task.salesStage?.trim()?.toLowerCase());
    const matchesGeography = selectedTaskGeographies.length === 0 || selectedTaskGeographies.includes(task.geography);

    // Apply user geography filter - only show records for geographies mapped to the user
    // Admin users see all records
    // Non-admin users with no geography mapped see NO records
    // Non-admin users with geography mapped see only their geography records
    const matchesUserGeography =
      currentUser?.isAdmin || // Admin sees all records
      !task.geography || // Tasks with no geography are visible to everyone
      (currentUser?.geographies?.length > 0 && // User has geographies mapped
       currentUser.geographies.includes(task.geography)); // Geography matches
    const matchesUserServiceLine =
      currentUser?.isAdmin ||
      (currentUser?.serviceLines?.length > 0 && // User must have service lines assigned
       (!task.serviceLine || currentUser.serviceLines.includes(task.serviceLine)));

    // Apply stat filter
    let matchesStatFilter = true;
    if (selectedStatFilter === 'dueToday') {
      const daysUntil = getDaysUntil(task.dueDate);
      matchesStatFilter = daysUntil === 0;
    } else if (selectedStatFilter === 'highPriority') {
      matchesStatFilter = task.priority === 'High';
    } else if (selectedStatFilter === 'completed') {
      matchesStatFilter = task.status === 'Completed';
    }

    return matchesSearch && matchesOwner &&
           matchesServiceLine && matchesSalesStage && matchesGeography && matchesUserGeography && matchesUserServiceLine && matchesStatFilter;
  });

  console.log('CalendarView - Filtered tasks count:', filteredTasks.length);
  console.log('CalendarView - Filtered tasks:', filteredTasks);

  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };
  
  const handlePostpone = async (taskId) => {
    const days = parseInt(taskPostpone[taskId] || 1);
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const newDate = new Date(task.dueDate);
    newDate.setDate(newDate.getDate() + days);
    const newDueDate = newDate.toISOString().split('T')[0];

    try {
      // Update in database
      await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task: task.task,
          owner: task.owner,
          due_date: newDueDate,
          priority: task.priority,
          status: task.status,
          type: task.type,
          related_to: task.relatedTo || task.companyName,
          related_id: task.relatedId,
          related_type: task.relatedType
        })
      });

      // Update local state
      setTasks(tasks.map(t => {
        if (t.id === taskId) {
          return { ...t, dueDate: newDueDate };
        }
        return t;
      }));
      setTaskPostpone(prev => ({ ...prev, [taskId]: '' }));
      alert(`Task postponed by ${days} day(s)`);
    } catch (error) {
      console.error('Error postponing task:', error);
      alert(`Failed to postpone task: ${error.message}`);
    }
  };
  
  const handleAddComment = (taskId) => {
    const comment = taskComments[taskId];
    if (!comment) {
      alert('Please enter a comment');
      return;
    }

    alert(`Comment added: "${comment}"`);
    setTaskComments(prev => ({ ...prev, [taskId]: '' }));
  };

  const sendTaskAssignmentEmail = (task, newOwner) => {
    // Get the user's email from the users list
    const user = users.find(u => u.name === newOwner);
    const userEmail = user?.email || `${newOwner.toLowerCase().replace(' ', '.')}@pierian.com`;

    // In a real application, this would call an API endpoint to send the email
    // For now, we'll simulate it with a console log and alert
    const emailContent = {
      to: userEmail,
      subject: `New Task Assigned: ${task.task}`,
      body: `
Hello ${newOwner},

A new task has been assigned to you:

Task: ${task.task}
Type: ${task.type}
Related To: ${task.relatedTo}
Due Date: ${new Date(task.dueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
Priority: ${task.priority}

Please review and take the necessary action.

Best regards,
Pierag Sales Connect CRM
      `
    };

    console.log('Email sent:', emailContent);
    return true;
  };

  const handleQuickAction = async (taskId, action) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const newStatus = action === 'complete' ? 'Completed' : action === 'cancel' ? 'Cancelled' : task.status;

    try {
      // Update in database
      await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task: task.task,
          owner: task.owner,
          due_date: task.dueDate,
          priority: task.priority,
          status: newStatus,
          type: task.type,
          related_to: task.relatedTo || task.companyName,
          related_id: task.relatedId,
          related_type: task.relatedType
        })
      });

      // Update local state (the database update succeeds even if response status is not ok due to server bug)
      setTasks(tasks.map(t => {
        if (t.id === taskId) {
          return { ...t, status: newStatus };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error updating task:', error);
      alert(`Failed to update task: ${error.message}`);
    }
  };

  const handleReassignTask = async (taskId, newOwner) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    try {
      // Update in database
      await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task: task.task,
          owner: newOwner,
          due_date: task.dueDate,
          priority: task.priority,
          status: task.status,
          type: task.type,
          related_to: task.relatedTo || task.companyName,
          related_id: task.relatedId,
          related_type: task.relatedType
        })
      });

      // Update the task with new owner in local state
      setTasks(tasks.map(t => {
        if (t.id === taskId) {
          return { ...t, owner: newOwner };
        }
        return t;
      }));

      // Send email notification
      const updatedTask = { ...task, owner: newOwner };
      sendTaskAssignmentEmail(updatedTask, newOwner);

      // Close dropdown and show confirmation
      setTaskReassignOpen(prev => ({ ...prev, [taskId]: false }));
      alert(`Task reassigned to ${newOwner}.\n\nEmail notification sent to ${newOwner}.`);
    } catch (error) {
      console.error('Error reassigning task:', error);
      alert(`Failed to reassign task: ${error.message}`);
    }
  };

  const handleCreateFollowups = async () => {
    if (!opportunities || opportunities.length === 0) {
      alert('No opportunities found to create follow-ups.');
      return;
    }

    if (!window.confirm(`This will create follow-up tasks for all ${opportunities.length} opportunities.\n\nEach opportunity will have one active task. Existing pending tasks will be replaced.\n\nContinue?`)) {
      return;
    }

    let createdCount = 0;
    let replacedCount = 0;
    const newTasks = [...tasks];

    // Task descriptions based on opportunity stage
    const stageTaskMapping = {
      'Qualified': 'Validate qualification and plan discovery',
      'Discovery': 'Understand needs and pain points',
      'Proposal': 'Follow up on submitted proposal',
      'Contract': 'Finalise contract terms and get approvals',
      'Closed-Won': 'Complete contracting and handover',
      'Closed-Lost': 'Document learnings and maintain relationship'
    };

    opportunities.forEach(opp => {
      // Skip closed opportunities
      if (opp.salesStage === 'Closed-Won' || opp.salesStage === 'Closed-Lost' || opp.salesStage === 'Contract') {
        return;
      }

      // Calculate due date (1 week from now)
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 7);

      // Get task description based on stage
      const taskDescription = stageTaskMapping[opp.salesStage] || `Follow up on ${opp.opportunityName}`;
      const owner = opp.leadSupporter || opp.accountOwner || 'Unassigned';

      // Check if there's an existing pending task for this opportunity
      const existingTaskIndex = newTasks.findIndex(t =>
        t.relatedType === 'Opportunity' &&
        t.relatedId === opp.opportunityId &&
        t.status === 'Pending'
      );

      if (existingTaskIndex !== -1) {
        // Replace existing task and mark as replacement
        newTasks[existingTaskIndex] = {
          ...newTasks[existingTaskIndex],
          task: taskDescription,
          owner: owner,
          dueDate: dueDate.toISOString().split('T')[0],
          priority: 'High',
          type: 'Follow-up',
          relatedTo: opp.associatedAccount,
          isReplacement: true, // Flag to show red label
          replacedDate: new Date().toISOString()
        };
        replacedCount++;
      } else {
        // Create new task
        const newTask = {
          id: Math.max(...newTasks.map(t => t.id || 0), 0) + 1,
          task: taskDescription,
          owner: owner,
          dueDate: dueDate.toISOString().split('T')[0],
          priority: 'High',
          status: 'Pending',
          type: 'Follow-up',
          relatedTo: opp.associatedAccount,
          relatedId: opp.opportunityId,
          relatedType: 'Opportunity',
          isReplacement: false
        };
        newTasks.push(newTask);
        createdCount++;
      }
    });

    // Update local state first (original functionality)
    setTasks(newTasks);
    propSetTasks(newTasks);

    // Save to database in background with duplicate checking
    // Using global API_BASE_URL defined at top of file

    // Fetch existing tasks from database to check for duplicates
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/tasks`);
        const result = await response.json();

        let existingTasks = [];
        if (result.success && result.data) {
          existingTasks = result.data;
        }

        // Save all tasks to database asynchronously with duplicate check
        await Promise.all(
          newTasks.map(async (task) => {
            try {
              const taskData = {
                task: task.task,
                owner: task.owner,
                due_date: task.dueDate,
                priority: task.priority,
                status: task.status,
                type: task.type,
                related_to: task.relatedTo,
                related_id: task.relatedId,
                related_type: task.relatedType
              };

              // Check if task already exists in database (duplicate check)
              const existingTask = existingTasks.find(
                dbTask => dbTask.related_id === task.relatedId &&
                          dbTask.related_type === task.relatedType &&
                          dbTask.status === 'Pending'
              );

              if (existingTask) {
                // Update existing task in database
                await fetch(`${API_BASE_URL}/tasks/${existingTask.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(taskData)
                });
              } else {
                // Create new task in database (no duplicate found)
                await fetch(`${API_BASE_URL}/tasks`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(taskData)
                });
              }
            } catch (error) {
              console.error('Error saving task to database:', error);
            }
          })
        );

        console.log('✅ All tasks saved to database with duplicate checking');
      } catch (error) {
        console.error('❌ Error fetching/saving tasks to database:', error);
      }
    })();

    const message = `Follow-ups created successfully!\n\n` +
      `• ${createdCount} new tasks created\n` +
      `• ${replacedCount} existing tasks replaced (marked in red)\n` +
      `• All tasks due: ${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}`;

    alert(message);
  };

  const getTasksByDate = () => {
    const grouped = {};
    tasks.forEach(task => {
      const date = task.dueDate;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(task);
    });
    return Object.entries(grouped).sort(([a], [b]) => new Date(a) - new Date(b));
  };
  
  const handleCreateTask = async () => {
    if (!newTask.task || !newTask.relatedTo || !newTask.owner) {
      alert('Please fill in Task Name, Task Owner, and Related To fields');
      return;
    }

    try {
      // Save to database
      const taskData = {
        task: newTask.task,
        owner: newTask.owner,
        due_date: newTask.dueDate,
        priority: newTask.priority,
        status: newTask.status || 'Pending',
        type: newTask.type,
        related_to: newTask.relatedTo,
        related_id: newTask.relatedId,
        related_type: newTask.relatedType
      };

      const response = await fetch(`${API_BASE_URL}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskData)
      });

      if (!response.ok) {
        throw new Error('Failed to create task');
      }

      const result = await response.json();

      const taskToAdd = {
        id: result.data?.id || tasks.length + 1,
        ...newTask
      };

      setTasks([...tasks, taskToAdd]);
      setShowTaskModal(false);
      setNewTask({
        task: '',
        owner: users && users.length > 0 && users.filter(u => u.status === 'Active').length > 0
          ? users.filter(u => u.status === 'Active')[0].name
          : '',
        dueDate: new Date().toISOString().split('T')[0],
        priority: 'Medium',
        status: 'Pending',
        type: 'Follow-up',
        relatedTo: '',
        relatedId: '',
        relatedType: 'Opportunity'
      });
      alert('Task created successfully!');
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Error creating task. Please try again.');
    }
  };

  // Collect all activities from leads, opportunities, and customers
  const getAllActivities = () => {
    const activities = [];

    // Add lead activities
    (leads || []).forEach(lead => {
      if (lead.engagementHistory) {
        const history = lead.engagementHistory.split('\n').filter(h => h.trim());
        history.forEach(entry => {
          const match = entry.match(/\[(.*?)\]\s*(.*?):\s*(.*)/);
          if (match) {
            activities.push({
              id: `lead-${lead.leadId}-${activities.length}`,
              date: match[1],
              action: match[2],
              description: match[3],
              type: 'Lead',
              relatedTo: lead.companyName,
              relatedId: lead.leadId,
              salesperson: lead.leadOwner || 'Unknown',
              customer: lead.companyName,
              geography: lead.geography || 'Unknown',
              serviceLine: lead.serviceLine || 'Unknown'
            });
          } else {
            activities.push({
              id: `lead-${lead.leadId}-${activities.length}`,
              date: lead.dateCaptured || '',
              action: 'Activity',
              description: entry.trim(),
              type: 'Lead',
              relatedTo: lead.companyName,
              relatedId: lead.leadId,
              salesperson: lead.leadOwner || 'Unknown',
              customer: lead.companyName,
              geography: lead.geography || 'Unknown',
              serviceLine: lead.serviceLine || 'Unknown'
            });
          }
        });
      }
    });

    // Add opportunity activities
    (opportunities || []).forEach(opp => {
      if (opp.engagementHistory) {
        const history = opp.engagementHistory.split('\n').filter(h => h.trim());
        history.forEach(entry => {
          activities.push({
            id: `opp-${opp.opportunityId}-${activities.length}`,
            date: opp.initiationDate || '',
            action: 'Opportunity Activity',
            description: entry.trim(),
            type: 'Opportunity',
            relatedTo: opp.opportunityName,
            relatedId: opp.opportunityId,
            salesperson: opp.accountOwner || 'Unknown',
            customer: opp.associatedAccount || 'Unknown',
            geography: opp.geography || 'Unknown',
            serviceLine: opp.serviceLine || 'Unknown'
          });
        });
      }
    });

    // Add task activities
    (tasks || []).forEach(task => {
      activities.push({
        id: `task-${task.id}`,
        date: task.dueDate,
        action: task.type || 'Task',
        description: task.task,
        type: 'Task',
        relatedTo: task.relatedTo,
        relatedId: task.id,
        salesperson: task.owner || 'Unknown',
        customer: task.relatedTo || 'Unknown',
        geography: 'N/A',
        serviceLine: 'N/A'
      });
    });

    return activities.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getGroupedActivities = () => {
    const activities = getAllActivities();
    const grouped = {};

    activities.forEach(activity => {
      let key;
      switch (activityGroupBy) {
        case 'salesperson':
          key = activity.salesperson;
          break;
        case 'customer':
          key = activity.customer;
          break;
        case 'geography':
          key = activity.geography;
          break;
        case 'serviceLine':
          key = activity.serviceLine;
          break;
        default:
          key = activity.salesperson;
      }

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(activity);
    });

    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  return (
    <div className="space-y-4">
      {/* Header with New Task Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-sm font-bold text-gray-900">Upcoming Tasks</h2>
          <p className="text-sm text-gray-500">Manage and track your scheduled activities</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowTaskSheet(true)}>
            <FileSpreadsheet size={16} className="mr-1" /> Task Sheet
          </Button>
          <Button variant="secondary" onClick={handleCreateFollowups}>
            <RefreshCw size={16} className="mr-1" /> Create Follow-ups
          </Button>
          <Button onClick={() => setShowTaskModal(true)}>
            <Plus size={16} className="mr-1" /> New Task
          </Button>
        </div>
      </div>

      {/* Filters Section - Compact Single Row matching Dashboard */}
      <div className="rounded-md shadow-sm px-2 py-1.5" style={{ backgroundColor: 'var(--dm-surface)', border: '1px solid var(--dm-border)' }}>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter size={14} className="text-gray-400 flex-shrink-0" />
          {/* Search Bar - inline */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={12} />
            <input
              type="text"
              placeholder="Search..."
              value={searchTask}
              onChange={(e) => setSearchTask(e.target.value)}
              className="w-32 pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-pierian focus:border-pierian"
            />
          </div>
          {/* Geography Filter */}
          <button
            onClick={() => setActiveTaskFilterType(activeTaskFilterType === 'geography' ? null : 'geography')}
            className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
            style={{
              backgroundColor: selectedTaskGeographies.length > 0 ? '#00BBA2' : activeTaskFilterType === 'geography' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
              color: selectedTaskGeographies.length > 0 ? 'white' : activeTaskFilterType === 'geography' ? '#009E8A' : '#374151',
              border: activeTaskFilterType === 'geography' && selectedTaskGeographies.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
            }}
          >
            Geo{selectedTaskGeographies.length > 0 && ` (${selectedTaskGeographies.length})`}
          </button>

          {/* Salesperson Filter */}
          <button
            onClick={() => setActiveTaskFilterType(activeTaskFilterType === 'salesperson' ? null : 'salesperson')}
            className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
            style={{
              backgroundColor: selectedTaskOwners.length > 0 ? '#00BBA2' : activeTaskFilterType === 'salesperson' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
              color: selectedTaskOwners.length > 0 ? 'white' : activeTaskFilterType === 'salesperson' ? '#009E8A' : '#374151',
              border: activeTaskFilterType === 'salesperson' && selectedTaskOwners.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
            }}
          >
            Owner{selectedTaskOwners.length > 0 && ` (${selectedTaskOwners.length})`}
          </button>

          {/* Service Lines Filter */}
          <button
            onClick={() => setActiveTaskFilterType(activeTaskFilterType === 'serviceline' ? null : 'serviceline')}
            className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
            style={{
              backgroundColor: selectedTaskServiceLines.length > 0 ? '#00BBA2' : activeTaskFilterType === 'serviceline' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
              color: selectedTaskServiceLines.length > 0 ? 'white' : activeTaskFilterType === 'serviceline' ? '#009E8A' : '#374151',
              border: activeTaskFilterType === 'serviceline' && selectedTaskServiceLines.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
            }}
          >
            Service Line{selectedTaskServiceLines.length > 0 && ` (${selectedTaskServiceLines.length})`}
          </button>

          {/* Sales Stage Filter */}
          <button
            onClick={() => setActiveTaskFilterType(activeTaskFilterType === 'salesstage' ? null : 'salesstage')}
            className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
            style={{
              backgroundColor: selectedTaskSalesStages.length > 0 ? '#00BBA2' : activeTaskFilterType === 'salesstage' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
              color: selectedTaskSalesStages.length > 0 ? 'white' : activeTaskFilterType === 'salesstage' ? '#009E8A' : '#374151',
              border: activeTaskFilterType === 'salesstage' && selectedTaskSalesStages.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
            }}
          >
            Stage{selectedTaskSalesStages.length > 0 && ` (${selectedTaskSalesStages.length})`}
          </button>

          {/* Clear Filters */}
          {hasTaskActiveFilters && (
            <button
              onClick={clearTaskAllFilters}
              className="px-2.5 py-1 rounded text-xs font-medium ml-1"
              style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Filter Options - Show when a filter type is selected (multi-select toggle) */}
            {activeTaskFilterType === 'geography' && (() => {
              // Filter geographies to only show those mapped to the current user
              // Admin users see all geographies, non-admin users see only their mapped geographies
              const userAccessibleGeographies = currentUser?.isAdmin
                ? masterGeographies
                : masterGeographies.filter(geo => currentUser?.geographies?.includes(geo));

              const geoWithCounts = userAccessibleGeographies
                .map(geo => ({ value: geo, count: tasksWithOppData.filter(t => t.geography === geo).length }))
                .filter(g => g.count > 0)
                .sort((a, b) => b.count - a.count);
              const isExpanded = expandedTaskFilterOptions['geography'];
              const visibleItems = isExpanded ? geoWithCounts : geoWithCounts.slice(0, 8);
              const hiddenCount = geoWithCounts.length - 8;

              return (
                <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                  {visibleItems.map(({ value: geo, count }) => {
                    const isSelected = selectedTaskGeographies.includes(geo);
                    return (
                      <button
                        key={geo}
                        onClick={() => toggleTaskFilterSelection('geography', geo)}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: isSelected ? '#00BBA2' : 'white',
                          color: isSelected ? 'white' : 'var(--dm-btn-text)',
                          border: isSelected ? 'none' : '1px solid #d1d5db'
                        }}
                      >
                        {geo} ({count})
                      </button>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <button
                      onClick={() => setExpandedTaskFilterOptions(prev => ({ ...prev, geography: !prev.geography }))}
                      className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                      style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                    >
                      {isExpanded ? 'Less' : `+${hiddenCount}`}
                      {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  )}
                </div>
              );
            })()}

            {activeTaskFilterType === 'salesperson' && (() => {
              const ownerWithCounts = (users || [])
                .filter(u => u.status === 'Active')
                .map(u => u.name)
                .map(owner => ({ value: owner, count: tasksWithOppData.filter(t => t.owner === owner).length }))
                .filter(o => o.count > 0)
                .sort((a, b) => b.count - a.count);
              const isExpanded = expandedTaskFilterOptions['salesperson'];
              const visibleItems = isExpanded ? ownerWithCounts : ownerWithCounts.slice(0, 8);
              const hiddenCount = ownerWithCounts.length - 8;

              return (
                <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                  {visibleItems.map(({ value: owner, count }) => {
                    const isSelected = selectedTaskOwners.includes(owner);
                    return (
                      <button
                        key={owner}
                        onClick={() => toggleTaskFilterSelection('salesperson', owner)}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: isSelected ? '#00BBA2' : 'white',
                          color: isSelected ? 'white' : 'var(--dm-btn-text)',
                          border: isSelected ? 'none' : '1px solid #d1d5db'
                        }}
                      >
                        {owner} ({count})
                      </button>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <button
                      onClick={() => setExpandedTaskFilterOptions(prev => ({ ...prev, salesperson: !prev.salesperson }))}
                      className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                      style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                    >
                      {isExpanded ? 'Less' : `+${hiddenCount}`}
                      {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  )}
                </div>
              );
            })()}

            {activeTaskFilterType === 'serviceline' && (() => {
              const slWithCounts = masterServiceLines
                .map(sl => ({ value: sl, count: tasksWithOppData.filter(t => t.serviceLine === sl).length }))
                .filter(s => s.count > 0)
                .sort((a, b) => b.count - a.count);
              const isExpanded = expandedTaskFilterOptions['serviceline'];
              const visibleItems = isExpanded ? slWithCounts : slWithCounts.slice(0, 8);
              const hiddenCount = slWithCounts.length - 8;

              return (
                <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                  {visibleItems.map(({ value: sl, count }) => {
                    const isSelected = selectedTaskServiceLines.includes(sl);
                    return (
                      <button
                        key={sl}
                        onClick={() => toggleTaskFilterSelection('serviceline', sl)}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: isSelected ? '#00BBA2' : 'white',
                          color: isSelected ? 'white' : 'var(--dm-btn-text)',
                          border: isSelected ? 'none' : '1px solid #d1d5db'
                        }}
                      >
                        {sl} ({count})
                      </button>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <button
                      onClick={() => setExpandedTaskFilterOptions(prev => ({ ...prev, serviceline: !prev.serviceline }))}
                      className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                      style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                    >
                      {isExpanded ? 'Less' : `+${hiddenCount}`}
                      {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  )}
                </div>
              );
            })()}

            {activeTaskFilterType === 'salesstage' && (() => {
              const stageWithCounts = effectiveSalesStages
                .map(stage => ({
                  value: stage,
                  count: tasksWithOppData.filter(t => t.salesStage?.trim()?.toLowerCase() === stage?.toLowerCase()).length
                }))
                .filter(s => s.count > 0)
                .sort((a, b) => b.count - a.count);
              const isExpanded = expandedTaskFilterOptions['salesstage'];
              const visibleItems = isExpanded ? stageWithCounts : stageWithCounts.slice(0, 8);
              const hiddenCount = stageWithCounts.length - 8;

              return (
                <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                  {visibleItems.map(({ value: stage, count }) => {
                    const isSelected = selectedTaskSalesStages.includes(stage);
                    return (
                      <button
                        key={stage}
                        onClick={() => toggleTaskFilterSelection('salesstage', stage)}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: isSelected ? '#00BBA2' : 'white',
                          color: isSelected ? 'white' : 'var(--dm-btn-text)',
                          border: isSelected ? 'none' : '1px solid #d1d5db'
                        }}
                      >
                        {stage} ({count})
                      </button>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <button
                      onClick={() => setExpandedTaskFilterOptions(prev => ({ ...prev, salesstage: !prev.salesstage }))}
                      className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                      style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                    >
                      {isExpanded ? 'Less' : `+${hiddenCount}`}
                      {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  )}
                </div>
              );
            })()}
      </div>

      {/* Task Creation Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="border-b border-gray-200 p-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold">Create New Task</h3>
                <button onClick={() => setShowTaskModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Task Name*</label>
                  <input 
                    type="text" 
                    value={newTask.task}
                    onChange={(e) => setNewTask({...newTask, task: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    placeholder="Enter task description"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Task Type*</label>
                  <select 
                    value={newTask.type}
                    onChange={(e) => setNewTask({...newTask, type: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option>Follow-up</option>
                    <option>Proposal</option>
                    <option>Demo</option>
                    <option>Contract</option>
                    <option>Meeting</option>
                    <option>Call</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Related To Opportunity*</label>
                  <select
                    value={newTask.relatedId || ''}
                    onChange={(e) => {
                      const selectedOpp = opportunities?.find(o => o.opportunityId === e.target.value);
                      setNewTask({
                        ...newTask,
                        relatedTo: selectedOpp?.associatedAccount || '',
                        relatedId: e.target.value,
                        relatedType: 'Opportunity'
                      });
                    }}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="">Select Opportunity</option>
                    {opportunities && opportunities
                      .filter(opp => opp.salesStage !== 'Closed-Won' && opp.salesStage !== 'Closed-Lost')
                      .map(opp => (
                        <option key={opp.opportunityId} value={opp.opportunityId}>
                          {opp.associatedAccount} - {opp.opportunityName} ({opp.salesStage})
                        </option>
                      ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Task Owner*</label>
                  <select
                    value={newTask.owner}
                    onChange={(e) => setNewTask({...newTask, owner: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="">Select Task Owner</option>
                    {users && users.filter(u => u.status === 'Active').map(user => (
                      <option key={user.userId} value={user.name}>{user.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Due Date*</label>
                  <input 
                    type="date" 
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Priority*</label>
                  <select 
                    value={newTask.priority}
                    onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Status*</label>
                  <select 
                    value={newTask.status}
                    onChange={(e) => setNewTask({...newTask, status: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option>Pending</option>
                    <option>In Progress</option>
                    <option>Completed</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="secondary" onClick={() => setShowTaskModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateTask}>
                  Create Task
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-start gap-3">
        {[
          { key: 'all', label: 'Total Tasks', desc: 'All tasks', count: tasksWithOppData.length, bg: isDark ? 'rgba(100,130,180,0.3)' : '#d1d9e6' },
          { key: 'dueToday', label: 'Due Today', desc: 'Due today', count: tasksWithOppData.filter(t => getDaysUntil(t.dueDate) === 0).length, bg: isDark ? 'rgba(209,86,86,0.38)' : 'rgba(209,86,86,0.12)' },
          { key: 'highPriority', label: 'High Priority', desc: 'Needs attention', count: tasksWithOppData.filter(t => t.priority === 'High').length, bg: isDark ? 'rgba(234,179,8,0.28)' : '#fef3c7' },
          { key: 'completed', label: 'Completed', desc: 'Done', count: tasksWithOppData.filter(t => t.status === 'Completed').length, bg: isDark ? 'rgba(134,188,134,0.3)' : '#C7D8C1' },
        ].map(card => {
          const isActive = selectedStatFilter === card.key;
          return (
            <div key={card.key} className="flex flex-col items-center">
              <button
                onClick={() => setSelectedStatFilter(selectedStatFilter === card.key ? 'all' : card.key)}
                className={`rounded-lg px-3 py-1.5 text-center hover:shadow-md transition-all min-w-[80px] ${isActive ? 'shadow-lg ring-2' : ''}`}
                style={{
                  backgroundColor: isActive ? '#00BBA2' : card.bg,
                  borderColor: isActive ? '#009E8A' : 'transparent',
                  ringColor: isActive ? '#009E8A' : undefined
                }}
                title={card.desc}
              >
                <span className="text-[10px] font-semibold block leading-tight" style={{ color: isActive ? '#ffffff' : 'var(--dm-btn-text)' }}>{card.label}</span>
                <span className="text-lg font-bold block leading-tight" style={{ color: isActive ? '#ffffff' : 'var(--dm-text)' }}>{card.count}</span>
              </button>
              <span className="text-[8px] text-gray-400 italic leading-tight mt-0.5">{card.desc}</span>
            </div>
          );
        })}
      </div>

      <Card>
        <div className="w-full overflow-auto">
          <table className="w-full text-xs" style={{tableLayout: 'fixed'}}>
            <thead>
                  <tr className="border-b border-gray-200" style={{ backgroundColor: '#131A25' }}>
                    <th className="w-8 px-1 py-2 text-left"></th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-white">Company Name</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-white">Task</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-white">Status</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-white">Type</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-white">Due Date</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-white">Priority</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-white">Owner</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-white">Quick Actions</th>
                  </tr>
                </thead>
                <tbody>
              {filteredTasks.map((task) => {
                const daysUntil = getDaysUntil(task.dueDate);
                const isOverdue = daysUntil < 0;
                const isDueToday = daysUntil === 0;

                return (
                  <React.Fragment key={task.id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="w-8 px-1 py-2">
                        <button
                          onClick={() => toggleTaskExpansion(task.id)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          {expandedTasks[task.id] ? (
                            <ChevronUp size={14} className="text-gray-600" />
                          ) : (
                            <ChevronDown size={14} className="text-gray-600" />
                          )}
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        <p className="font-semibold text-gray-900">{task.companyName || '-'}</p>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{task.task}</p>
                          {task.isReplacement && (
                            <span className="px-2 py-0.5 bg-pierian-20 text-scarlet rounded text-xs font-semibold">
                              REPLACED
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          task.status === 'Completed' ? 'bg-sage-tint text-sage-shade' :
                          task.status === 'Cancelled' ? 'bg-gray-100 text-gray-500' :
                          'bg-slate-tint text-slate-shade'
                        }`}>
                          {task.status}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                          {task.type}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <div>
                          <p className={`font-medium ${
                            isOverdue ? 'text-pierian' :
                            isDueToday ? 'text-orange-600' :
                            'text-gray-900'
                          }`}>
                            {new Date(task.dueDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-gray-500">
                            {isOverdue ? `${Math.abs(daysUntil)} days overdue` :
                             isDueToday ? 'Due today' :
                             `In ${daysUntil} days`}
                          </p>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          task.priority === 'High' ? 'bg-red-100 text-red-700' :
                          task.priority === 'Medium' ? 'bg-amber-tint text-amber-shade' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-gray-600">{task.owner}</td>
                      <td className="px-2 py-2 relative">
                        <div className="flex gap-1 items-center">
                          <button
                            onClick={() => handleQuickAction(task.id, 'complete')}
                            className="px-2 py-1 bg-sage text-white rounded hover:bg-sage-shade text-xs"
                            title="Mark Complete"
                            disabled={task.status === 'Completed'}
                          >
                            <CheckCircle size={12} />
                          </button>
                          <button
                            onClick={() => handleQuickAction(task.id, 'cancel')}
                            className="px-2 py-1 bg-pierian text-white rounded hover:bg-scarlet text-xs"
                            title="Cancel Task"
                          >
                            <X size={12} />
                          </button>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setTaskReassignOpen(prev => ({ ...prev, [task.id]: !prev[task.id] }));
                              }}
                              className="px-2 py-1 bg-slate text-white rounded hover:bg-slate-shade text-xs"
                              title="Reassign"
                            >
                              <User size={12} />
                            </button>
                          </div>
                          {taskReassignOpen[task.id] && (
                            <div
                              style={{
                                position: 'fixed',
                                right: '20px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 9999
                              }}
                              className="bg-white border border-gray-300 rounded-md shadow-xl min-w-[250px] max-w-[300px]"
                            >
                              <div className="p-3">
                                <p className="text-sm font-semibold text-gray-700 mb-3 px-2">Reassign Task to:</p>
                                <div className="max-h-64 overflow-y-auto">
                                  {users && users.filter(u => u.status === 'Active').map(user => (
                                    <button
                                      key={user.userId}
                                      onClick={() => handleReassignTask(task.id, user.name)}
                                      className="w-full text-left px-3 py-2 hover:bg-pierian-10 text-sm text-gray-700 hover:text-pierian transition-colors rounded"
                                    >
                                      <div className="font-medium">{user.name}</div>
                                      <div className="text-xs text-gray-500">{user.role}</div>
                                    </button>
                                  ))}
                                </div>
                                <button
                                  onClick={() => setTaskReassignOpen(prev => ({ ...prev, [task.id]: false }))}
                                  className="w-full mt-3 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 border-t border-gray-200 rounded"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {expandedTasks[task.id] && (
                      <tr className="bg-gray-50">
                        <td colSpan={9} className="px-4 py-3" style={{maxWidth: '0', overflow: 'hidden'}}>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <h5 className="text-xs font-semibold text-gray-700">Postpone Task</h5>
                              <div className="flex gap-2">
                                <input
                                  type="number"
                                  min="1"
                                  placeholder="Days"
                                  value={taskPostpone[task.id] || ''}
                                  onChange={(e) => setTaskPostpone(prev => ({ ...prev, [task.id]: e.target.value }))}
                                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                />
                                <button
                                  onClick={() => handlePostpone(task.id)}
                                  className="px-3 py-1 bg-slate text-white rounded hover:bg-slate-shade text-xs"
                                >
                                  Postpone
                                </button>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <h5 className="text-xs font-semibold text-gray-700">Add Comment</h5>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder="Enter comment..."
                                  value={taskComments[task.id] || ''}
                                  onChange={(e) => setTaskComments(prev => ({ ...prev, [task.id]: e.target.value }))}
                                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                />
                                <button
                                  onClick={() => handleAddComment(task.id)}
                                  className="px-3 py-1 bg-sage text-white rounded hover:bg-sage-shade text-xs"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Task Sheet Modal */}
      <TaskSheetModal
        isOpen={showTaskSheet}
        onClose={() => setShowTaskSheet(false)}
        tasks={tasks}
        opportunities={opportunities}
        users={users}
      />
    </div>
  );
};

// ========================================
// COPILOT VIEW
// ========================================
/* ========================================
   COPILOT VIEW - COMMENTED OUT (Coming Soon)
   ========================================
   This functionality is temporarily disabled and will be enabled in a future release.

const CopilotView = ({ opportunities, leads, customers }) => {

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m your Sales Copilot. I can help you analyze your sales team performance, pipeline data, and opportunity insights. Ask me anything about your CRM data!',
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const quickQuestions = [
    "What's our total pipeline value?",
    "How is Sarah Johnson performing?",
    "Which service line generates the most revenue?",
    "Show me opportunities in negotiation stage",
    "What's our lead conversion rate?",
    "Which team member has the highest win rate?"
  ];

  const prepareCRMContext = () => {
    const totalLeads = leads.length;
    const qualifiedOpps = opportunities.filter(o => o.salesStage === 'Qualified').length;

    // Active opportunities exclude Won, Lost, and Inactive
    const activeOpportunities = opportunities.filter(opp =>
      opp.salesStage !== 'Closed-Won' &&
      opp.salesStage !== 'Closed-Lost' &&
      getEffectiveSalesStatus(opp) !== 'Inactive'
    );

    const totalOpportunities = activeOpportunities.length;
    const totalPipelineValue = activeOpportunities.reduce((sum, opp) => sum + (opp.estimatedRevenue || 0), 0);
    const avgDealSize = totalPipelineValue / totalOpportunities || 0;

    const teamPerformance = {};
    leads.forEach(lead => {
      if (!teamPerformance[lead.leadOwner]) {
        teamPerformance[lead.leadOwner] = { leads: 0, opportunities: 0, revenue: 0 };
      }
      teamPerformance[lead.leadOwner].leads++;
    });

    // Team performance uses active opportunities only
    activeOpportunities.forEach(opp => {
      const owner = opp.leadSupporter || 'Unassigned';
      if (!teamPerformance[owner]) {
        teamPerformance[owner] = { leads: 0, opportunities: 0, revenue: 0 };
      }
      teamPerformance[owner].opportunities++;
      teamPerformance[owner].revenue += opp.estimatedRevenue || 0;
    });

    const serviceLineRevenue = {};
    const serviceLineNameMap = {}; // lowercase -> canonical name mapping for case-insensitive grouping
    // Service line revenue uses active opportunities only
    activeOpportunities.forEach(opp => {
      const sl = opp.serviceLine?.trim();
      if (sl) {
        const slLower = sl.toLowerCase();
        if (!serviceLineNameMap[slLower]) {
          serviceLineNameMap[slLower] = sl;
          serviceLineRevenue[sl] = { revenue: 0, count: 0 };
        }
        const canonicalName = serviceLineNameMap[slLower];
        serviceLineRevenue[canonicalName].revenue += opp.estimatedRevenue || 0;
        serviceLineRevenue[canonicalName].count++;
      }
    });

    // Stage distribution uses active opportunities only
    const stageDistribution = {};
    activeOpportunities.forEach(opp => {
      if (!stageDistribution[opp.salesStage]) {
        stageDistribution[opp.salesStage] = { count: 0, value: 0 };
      }
      stageDistribution[opp.salesStage].count++;
      stageDistribution[opp.salesStage].value += opp.estimatedRevenue || 0;
    });

    return {
      summary: {
        totalLeads,
        qualifiedLeads,
        totalOpportunities,
        totalPipelineValue,
        avgDealSize,
        leadConversionRate: ((qualifiedLeads / totalLeads) * 100).toFixed(1) + '%'
      },
      teamPerformance,
      serviceLineRevenue,
      stageDistribution,
      leads: leads.slice(0, 10),
      opportunities: opportunities.slice(0, 10),
      customers: customers.slice(0, 5)
    };
  };

  const analyzeQuery = (query, crmContext) => {
    const q = query.toLowerCase();
    let response = '';

    // Pipeline Value Questions
    if (q.includes('pipeline') && (q.includes('value') || q.includes('worth') || q.includes('total'))) {
      response = `📊 **Pipeline Analysis**\n\n`;
      response += `Your total pipeline value is **₹${crmContext.summary.totalPipelineValue.toLocaleString()}** across ${crmContext.summary.totalOpportunities} active opportunities.\n\n`;
      response += `**Breakdown by Stage:**\n`;
      Object.entries(crmContext.stageDistribution).forEach(([stage, data]) => {
        response += `• ${stage}: ${data.count} opportunities worth ₹${data.value.toLocaleString()}\n`;
      });
      response += `\n**Average Deal Size:** ₹${crmContext.summary.avgDealSize.toLocaleString()}`;
      return response;
    }

    // Team Performance Questions
    if (q.includes('performing') || q.includes('performance') || q.includes('top') || q.includes('best')) {
      response = `👥 **Team Performance Analysis**\n\n`;
      const sortedTeam = Object.entries(crmContext.teamPerformance)
        .sort((a, b) => b[1].revenue - a[1].revenue)
        .slice(0, 5);

      response += `**Top Performers by Revenue:**\n`;
      sortedTeam.forEach(([name, data], idx) => {
        response += `${idx + 1}. **${name}**\n`;
        response += `   • Revenue: ₹${data.revenue.toLocaleString()}\n`;
        response += `   • Opportunities: ${data.opportunities}\n`;
        response += `   • Leads: ${data.leads}\n\n`;
      });

      return response;
    }

    // Specific Team Member Questions
    const teamMembers = Object.keys(crmContext.teamPerformance);
    const mentionedMember = teamMembers.find(name => q.includes(name.toLowerCase()));

    if (mentionedMember) {
      const memberData = crmContext.teamPerformance[mentionedMember];
      response = `👤 **Performance Report: ${mentionedMember}**\n\n`;
      response += `• **Total Revenue:** ₹${memberData.revenue.toLocaleString()}\n`;
      response += `• **Active Opportunities:** ${memberData.opportunities}\n`;
      response += `• **Leads Managed:** ${memberData.leads}\n`;
      response += `• **Average Deal Size:** ₹${(memberData.revenue / memberData.opportunities || 0).toLocaleString()}\n\n`;

      const memberOpps = opportunities.filter(o =>
        (o.leadSupporter || '').toLowerCase() === mentionedMember.toLowerCase() ||
        (o.accountOwner || '').toLowerCase() === mentionedMember.toLowerCase()
      );

      if (memberOpps.length > 0) {
        response += `**Recent Opportunities:**\n`;
        memberOpps.slice(0, 3).forEach(opp => {
          response += `• ${opp.opportunityName} - ${opp.salesStage} (₹${opp.estimatedRevenue.toLocaleString()})\n`;
        });
      }

      return response;
    }

    // Service Line Questions
    if (q.includes('service') || q.includes('service line') || q.includes('product')) {
      response = `🎯 **Service Line Performance**\n\n`;
      const sortedServices = Object.entries(crmContext.serviceLineRevenue)
        .sort((a, b) => b[1].revenue - a[1].revenue);

      sortedServices.forEach(([service, data]) => {
        const percentage = ((data.revenue / crmContext.summary.totalPipelineValue) * 100).toFixed(1);
        response += `**${service}**\n`;
        response += `• Revenue: ₹${data.revenue.toLocaleString()} (${percentage}%)\n`;
        response += `• Opportunities: ${data.count}\n`;
        response += `• Avg Deal: ₹${(data.revenue / data.count).toLocaleString()}\n\n`;
      });

      return response;
    }

    // Stage/Pipeline Questions
    if (q.includes('stage') || q.includes('negotiation') || q.includes('proposal') || q.includes('closing') || q.includes('qualified')) {
      let targetStage = '';
      if (q.includes('qualified')) targetStage = 'Qualified';
      if (q.includes('contract')) targetStage = 'Contract';
      if (q.includes('proposal')) targetStage = 'Proposal';
      if (q.includes('discovery')) targetStage = 'Discovery';
      if (q.includes('contract')) targetStage = 'Contract';
      if (q.includes('contracting')) targetStage = 'Contract';

      if (targetStage && crmContext.stageDistribution[targetStage]) {
        const stageData = crmContext.stageDistribution[targetStage];
        const stageOpps = opportunities.filter(o => o.salesStage === targetStage);

        response = `📋 **${targetStage} Stage Analysis**\n\n`;
        response += `• **Opportunities:** ${stageData.count}\n`;
        response += `• **Total Value:** ₹${stageData.value.toLocaleString()}\n`;
        response += `• **Average Deal Size:** ₹${(stageData.value / stageData.count).toLocaleString()}\n\n`;

        if (stageOpps.length > 0) {
          response += `**Active Opportunities:**\n`;
          stageOpps.slice(0, 5).forEach(opp => {
            response += `• ${opp.opportunityName} - ₹${opp.estimatedRevenue.toLocaleString()} (${opp.accountOwner})\n`;
          });
        }

        return response;
      }

      // General stage distribution
      response = `📊 **Sales Stage Distribution**\n\n`;
      Object.entries(crmContext.stageDistribution).forEach(([stage, data]) => {
        const percentage = ((data.count / crmContext.summary.totalOpportunities) * 100).toFixed(1);
        response += `**${stage}**\n`;
        response += `• Count: ${data.count} (${percentage}%)\n`;
        response += `• Value: ₹${data.value.toLocaleString()}\n\n`;
      });

      return response;
    }

    // Lead/Conversion Questions
    if (q.includes('lead') || q.includes('conversion') || q.includes('qualify')) {
      response = `📈 **Lead Performance Metrics**\n\n`;
      response += `• **Total Leads:** ${crmContext.summary.totalLeads}\n`;
      response += `• **Qualified Leads:** ${crmContext.summary.qualifiedLeads}\n`;
      response += `• **Conversion Rate:** ${crmContext.summary.leadConversionRate}\n`;
      response += `• **Total Opportunities:** ${crmContext.summary.totalOpportunities}\n\n`;

      const leadsByStatus = {};
      leads.forEach(lead => {
        leadsByStatus[lead.leadStatus] = (leadsByStatus[lead.leadStatus] || 0) + 1;
      });

      response += `**Lead Status Breakdown:**\n`;
      Object.entries(leadsByStatus).forEach(([status, count]) => {
        const percentage = ((count / crmContext.summary.totalLeads) * 100).toFixed(1);
        response += `• ${status}: ${count} (${percentage}%)\n`;
      });

      return response;
    }

    // Win Rate Questions
    if (q.includes('win') || q.includes('won') || q.includes('success')) {
      const wonOpps = opportunities.filter(o => o.salesStage === 'Closed-Won');
      const lostOpps = opportunities.filter(o => o.salesStage === 'Closed-Lost');
      const closedOpps = wonOpps.length + lostOpps.length;
      const winRate = closedOpps > 0 ? ((wonOpps.length / closedOpps) * 100).toFixed(1) : 0;

      response = `🏆 **Win Rate Analysis**\n\n`;
      response += `• **Win Rate:** ${winRate}%\n`;
      response += `• **Closed-Won:** ${wonOpps.length} opportunities\n`;
      response += `• **Closed-Lost:** ${lostOpps.length} opportunities\n`;
      response += `• **Won Revenue:** ₹${wonOpps.reduce((sum, o) => sum + (o.actualRevenue || o.estimatedRevenue || 0), 0).toLocaleString()}\n\n`;

      // Win rate by team member
      const teamWinRate = {};
      wonOpps.forEach(opp => {
        const owner = opp.leadSupporter || opp.accountOwner || 'Unassigned';
        teamWinRate[owner] = (teamWinRate[owner] || 0) + 1;
      });

      const sortedWinners = Object.entries(teamWinRate)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      if (sortedWinners.length > 0) {
        response += `**Top Performers (Wins):**\n`;
        sortedWinners.forEach(([name, wins]) => {
          response += `• ${name}: ${wins} wins\n`;
        });
      }

      return response;
    }

    // Customer Questions
    if (q.includes('customer') || q.includes('account') || q.includes('client')) {
      response = `👥 **Customer Portfolio Analysis**\n\n`;
      response += `• **Total Customers:** ${customers.length}\n`;
      response += `• **Active Customers:** ${customers.filter(c => c.status === 'Active').length}\n`;
      response += `• **Total Contract Value:** ₹${customers.reduce((sum, c) => sum + (c.contractValue || 0), 0).toLocaleString()}\n`;
      response += `• **Average Customer Value:** ₹${(customers.reduce((sum, c) => sum + (c.contractValue || 0), 0) / customers.length).toLocaleString()}\n\n`;

      const topCustomers = customers
        .sort((a, b) => (b.contractValue || 0) - (a.contractValue || 0))
        .slice(0, 5);

      response += `**Top Customers by Value:**\n`;
      topCustomers.forEach((customer, idx) => {
        response += `${idx + 1}. **${customer.companyName}**\n`;
        response += `   • Value: ₹${customer.contractValue.toLocaleString()}\n`;
        response += `   • Health Score: ${customer.healthScore}/100\n`;
        response += `   • Projects: ${customer.projectCount}\n\n`;
      });

      return response;
    }

    // Default Summary Response
    response = `📊 **CRM Overview**\n\n`;
    response += `**Pipeline Metrics:**\n`;
    response += `• Total Pipeline Value: ₹${crmContext.summary.totalPipelineValue.toLocaleString()}\n`;
    response += `• Active Opportunities: ${crmContext.summary.totalOpportunities}\n`;
    response += `• Average Deal Size: ₹${crmContext.summary.avgDealSize.toLocaleString()}\n\n`;

    response += `**Lead Metrics:**\n`;
    response += `• Total Leads: ${crmContext.summary.totalLeads}\n`;
    response += `• Qualified Leads: ${crmContext.summary.qualifiedLeads}\n`;
    response += `• Conversion Rate: ${crmContext.summary.leadConversionRate}\n\n`;

    response += `**Customer Base:**\n`;
    response += `• Total Customers: ${customers.length}\n`;
    response += `• Active Customers: ${customers.filter(c => c.status === 'Active').length}\n\n`;

    response += `💡 **Try asking:**\n`;
    response += `• "What's our total pipeline value?"\n`;
    response += `• "Show me team performance"\n`;
    response += `• "Which service line generates the most revenue?"\n`;
    response += `• "What's our win rate?"\n`;

    return response;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentQuery = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    // Simulate slight delay for better UX
    setTimeout(() => {
      try {
        const crmContext = prepareCRMContext();
        const analysisResult = analyzeQuery(currentQuery, crmContext);

        const assistantMessage = {
          role: 'assistant',
          content: analysisResult,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, assistantMessage]);
      } catch (error) {
        console.error('Copilot error:', error);
        const errorMessage = {
          role: 'assistant',
          content: 'I apologize, but I encountered an error analyzing your request. Please try rephrasing your question or contact support.',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    }, 500);
  };

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col">
      <div className="mb-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-pierian-10 rounded-lg">
            <Bot className="text-pierian" size={24} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">Sales Copilot</h2>
            <p className="text-sm text-gray-500">AI-powered insights for your sales team</p>
          </div>
        </div>

        <div className="mt-4">
          <p className="text-xs font-medium text-gray-500 mb-2">Quick Questions:</p>
          <div className="flex flex-wrap gap-2">
            {quickQuestions.map((question, idx) => (
              <button
                key={idx}
                onClick={() => setInputMessage(question)}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded-full transition-colors"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Card className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((message, idx) => (
            <div
              key={idx}
              className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                message.role === 'user' ? 'bg-pierian' : 'bg-gray-200'
              }`}>
                {message.role === 'user' ? (
                  <User size={16} className="text-white" />
                ) : (
                  <Bot size={16} className="text-gray-600" />
                )}
              </div>

              <div className={`flex-1 ${message.role === 'user' ? 'flex justify-end' : ''}`}>
                <div className={`max-w-3xl rounded-lg p-4 ${
                  message.role === 'user'
                    ? 'bg-pierian text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  <div className="text-sm w-full" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowWrap: 'anywhere'}}>{message.content}</div>
                  <div className={`text-xs mt-2 ${
                    message.role === 'user' ? 'text-slate-tint' : 'text-gray-500'
                  }`}>
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Bot size={16} className="text-gray-600" />
              </div>
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <div className="animate-pulse">Analyzing data</div>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ask about sales performance, pipeline, team metrics..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00BBA2] text-sm"
              disabled={isLoading}
            />
            <Button onClick={handleSendMessage} disabled={isLoading || !inputMessage.trim()}>
              <Send size={16} />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            💡 Tip: Ask about team performance, conversion rates, pipeline analysis, or service line revenue
          </p>
        </div>
      </Card>
    </div>
  );
};
   ========================================
   END OF COMMENTED CODE
   ======================================== */

// ========================================
// COPILOT VIEW - AI-POWERED SALES ASSISTANT
// ========================================

interface CopilotMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  actionProposal?: ActionProposal | null;
}

interface ActionProposal {
  type: string;
  description: string;
  input: any;
  id?: string;
}

interface CopilotViewProps {
  leads: any[];
  opportunities: any[];
  customers: any[];
  users: any[];
  currentUser: any;
  setOpportunities: (opps: any[]) => void;
  setTasks: (tasks: any[]) => void;
  tasks: any[];
}

// Suggested prompts organized by category
const PROMPT_CATEGORIES = {
  pipeline: {
    label: 'Pipeline',
    icon: Target,
    color: '#00BBA2',
    prompts: [
      "What's our total pipeline value?",
      "Show opportunities closing this month",
      "Which deals are stalled or inactive?",
      "Breakdown opportunities by sales stage"
    ]
  },
  team: {
    label: 'Team',
    icon: Users,
    color: '#6A7CA5',
    prompts: [
      "Who are the top performers?",
      "Show deals by account owner",
      "What's our team win rate?",
      "Activity summary this week"
    ]
  },
  insights: {
    label: 'Insights',
    icon: TrendingUp,
    color: '#D9A441',
    prompts: [
      "What trends do you see in our data?",
      "Which industries perform best?",
      "Analyze our sales velocity",
      "Forecast for next quarter"
    ]
  },
  actions: {
    label: 'Actions',
    icon: Zap,
    color: '#9CAF88',
    prompts: [
      "What needs attention this week?",
      "Which opportunities to prioritize?",
      "Identify at-risk deals",
      "Create follow-up tasks for stale opportunities"
    ]
  }
};

// Query history item type
interface QueryHistoryItem {
  id: string;
  query: string;
  timestamp: string;
}

const CopilotView = ({ leads, opportunities, customers, users, currentUser, setOpportunities, setTasks, tasks }: CopilotViewProps) => {
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<ActionProposal | null>(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);
  const [followUpSuggestions, setFollowUpSuggestions] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [queryHistory, setQueryHistory] = useState<QueryHistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(true);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // Voice input state
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = React.useRef<SpeechRecognition | null>(null);

  // Voice output (TTS) state
  const [speakingMessageIdx, setSpeakingMessageIdx] = useState<number | null>(null);
  const [ttsSupported, setTtsSupported] = useState(false);

  // Load query history from localStorage on mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('copilot_query_history');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        // Filter to only keep last 7 days
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const filteredHistory = parsed.filter((item: QueryHistoryItem) =>
          new Date(item.timestamp) >= sevenDaysAgo
        );
        setQueryHistory(filteredHistory);
      } catch (e) {
        console.error('Failed to parse query history:', e);
      }
    }
  }, []);

  // Detect browser support for Speech Recognition and TTS
  useEffect(() => {
    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognitionAPI) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognitionRef.current = recognition;
    }
    if ('speechSynthesis' in window) {
      setTtsSupported(true);
    }
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (_e) { /* ignore */ }
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Save query to history
  const saveQueryToHistory = (query: string) => {
    const newItem: QueryHistoryItem = {
      id: `query_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      query: query,
      timestamp: new Date().toISOString()
    };

    const updatedHistory = [newItem, ...queryHistory].slice(0, 100); // Keep max 100 items
    setQueryHistory(updatedHistory);
    localStorage.setItem('copilot_query_history', JSON.stringify(updatedHistory));
  };

  // Group history by date
  const groupedHistory = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: { label: string; items: QueryHistoryItem[] }[] = [
      { label: 'Today', items: [] },
      { label: 'Yesterday', items: [] },
      { label: 'Last 7 Days', items: [] }
    ];

    queryHistory.forEach(item => {
      const itemDate = new Date(item.timestamp);
      itemDate.setHours(0, 0, 0, 0);

      if (itemDate.getTime() === today.getTime()) {
        groups[0].items.push(item);
      } else if (itemDate.getTime() === yesterday.getTime()) {
        groups[1].items.push(item);
      } else {
        groups[2].items.push(item);
      }
    });

    return groups.filter(g => g.items.length > 0);
  }, [queryHistory]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Memoize the CRM context to avoid recalculating on every render
  // Only recalculate when opportunities, leads, customers, or currentUser change
  const prepareCRMContext = React.useMemo(() => {
    const today = new Date();
    const todayTime = today.getTime();

    // Optimized date calculation functions (reuse today timestamp)
    const daysSince = (dateStr: string | null | undefined): number | null => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      const time = date.getTime();
      if (isNaN(time)) return null;
      return Math.floor((todayTime - time) / 86400000); // 86400000 = 1000 * 60 * 60 * 24
    };

    const daysUntil = (dateStr: string | null | undefined): number | null => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      const time = date.getTime();
      if (isNaN(time)) return null;
      return Math.floor((time - todayTime) / 86400000);
    };

    // Filter opportunities by user geography access
    const userAccessibleOpportunities = opportunities.filter(opp => {
      return currentUser?.isAdmin ||
        (currentUser?.geographies?.length > 0 &&
         opp.geography &&
         currentUser.geographies.includes(opp.geography));
    });

    console.log('🔍 CopilotView Geography Filter:', {
      currentUserName: currentUser?.name,
      currentUserGeographies: currentUser?.geographies,
      currentUserIsAdmin: currentUser?.isAdmin,
      totalOpportunities: opportunities.length,
      accessibleOpportunities: userAccessibleOpportunities.length
    });

    // ── Single-pass: compute summary metrics + per-owner/geo/SL aggregations ──
    const opportunitiesByStage: { [key: string]: number } = {};
    const opportunitiesByStatus: { [key: string]: number } = {};
    const revenueByStage: { [key: string]: number } = {};
    const ownerAgg: { [key: string]: { count: number; acv: number } } = {};
    const geoAgg: { [key: string]: { count: number; acv: number } } = {};
    const slAgg: { [key: string]: { count: number; acv: number } } = {};
    let activeOpportunitiesCount = 0;
    let totalPipelineValue = 0;
    let staleCount = 0;
    let overdueCount = 0;

    // Collect deals needing attention during the same pass
    const attentionCandidates: any[] = [];

    userAccessibleOpportunities.forEach(opp => {
      const stage = opp.salesStage || 'Unknown';
      const status = getEffectiveSalesStatus(opp);
      const acv = opp.annualContractValue || 0;

      opportunitiesByStage[stage] = (opportunitiesByStage[stage] || 0) + 1;
      opportunitiesByStatus[status] = (opportunitiesByStatus[status] || 0) + 1;
      revenueByStage[stage] = (revenueByStage[stage] || 0) + acv;

      const isActive = opp.salesStage !== 'Closed-Won' &&
                       opp.salesStage !== 'Closed-Lost' &&
                       status !== 'Inactive';

      if (isActive) {
        activeOpportunitiesCount++;
        totalPipelineValue += acv;

        // Owner aggregation
        const owner = opp.leadSupporter || opp.accountOwner || 'Unassigned';
        if (!ownerAgg[owner]) ownerAgg[owner] = { count: 0, acv: 0 };
        ownerAgg[owner].count++;
        ownerAgg[owner].acv += acv;

        // Geography aggregation
        const geo = opp.geography || 'Unknown';
        if (!geoAgg[geo]) geoAgg[geo] = { count: 0, acv: 0 };
        geoAgg[geo].count++;
        geoAgg[geo].acv += acv;

        // Service line aggregation
        const sl = opp.serviceLine || 'Unknown';
        if (!slAgg[sl]) slAgg[sl] = { count: 0, acv: 0 };
        slAgg[sl].count++;
        slAgg[sl].acv += acv;

        const daysSinceActivity = daysSince(opp.lastEditedDate);
        const dtc = daysUntil(opp.expectedCloseDate);

        if (daysSinceActivity !== null && daysSinceActivity > 30) staleCount++;
        if (dtc !== null && dtc < 0) overdueCount++;

        // Flag for attention: overdue, stale, or closing within 14 days
        const needsAttention = (dtc !== null && dtc < 0) ||
                               (daysSinceActivity !== null && daysSinceActivity > 30) ||
                               (dtc !== null && dtc <= 14);
        if (needsAttention) {
          attentionCandidates.push({
            name: opp.opportunityName,
            account: opp.associatedAccount,
            stage,
            value: acv,
            owner,
            daysToClose: dtc,
            daysSinceActivity,
            status
          });
        }
      }
    });

    // Format aggregation strings: "Owner:count/$XM, ..."
    const fmtAcv = (v: number) => v >= 1000000 ? `₹${(v / 1000000).toFixed(1)}M` : `₹${(v / 1000).toFixed(0)}K`;
    const byOwner = Object.entries(ownerAgg)
      .sort((a, b) => b[1].acv - a[1].acv)
      .map(([k, v]) => `${k}:${v.count}/${fmtAcv(v.acv)}`).join(', ');
    const byGeography = Object.entries(geoAgg)
      .sort((a, b) => b[1].acv - a[1].acv)
      .map(([k, v]) => `${k}:${v.count}/${fmtAcv(v.acv)}`).join(', ');
    const byServiceLine = Object.entries(slAgg)
      .sort((a, b) => b[1].acv - a[1].acv)
      .map(([k, v]) => `${k}:${v.count}/${fmtAcv(v.acv)}`).join(', ');

    // Top 5 attention deals (sorted: overdue first, then by value)
    const attentionDeals = attentionCandidates
      .sort((a, b) => {
        const aOverdue = (a.daysToClose !== null && a.daysToClose < 0) ? 1 : 0;
        const bOverdue = (b.daysToClose !== null && b.daysToClose < 0) ? 1 : 0;
        if (aOverdue !== bOverdue) return bOverdue - aOverdue;
        return (b.value || 0) - (a.value || 0);
      })
      .slice(0, 5);

    // Lead aggregations (counts only — filtered by user geography access)
    const accessibleLeads = leads.filter(l =>
      currentUser?.isAdmin ||
      (currentUser?.geographies?.length > 0 && l.geography && currentUser.geographies.includes(l.geography))
    );
    const leadByStatus: { [key: string]: number } = {};
    const leadBySource: { [key: string]: number } = {};
    accessibleLeads.forEach(l => {
      const st = l.leadStatus || 'Unknown';
      leadByStatus[st] = (leadByStatus[st] || 0) + 1;
      const src = l.leadSource || 'Unknown';
      leadBySource[src] = (leadBySource[src] || 0) + 1;
    });

    // Customer aggregations (counts only — filtered by user geography access)
    const accessibleCustomers = customers.filter(c =>
      currentUser?.isAdmin ||
      (currentUser?.geographies?.length > 0 && c.geography && currentUser.geographies.includes(c.geography))
    );
    let activeCustomerCount = 0;
    let totalHealthScore = 0;
    let healthCount = 0;
    accessibleCustomers.forEach(c => {
      if (c.status === 'Active') activeCustomerCount++;
      if (c.healthScore != null) { totalHealthScore += c.healthScore; healthCount++; }
    });
    const avgHealthScore = healthCount > 0 ? Math.round(totalHealthScore / healthCount) : 0;

    // Task aggregations + top 3 overdue (filtered by user geography access)
    const accessibleTasks = tasks.filter((t: any) =>
      currentUser?.isAdmin ||
      (currentUser?.geographies?.length > 0 && t.geography && currentUser.geographies.includes(t.geography))
    );
    const openTasks = accessibleTasks.filter((t: any) => t.status !== 'Completed' && t.status !== 'Done');
    const overdueTasks = openTasks.filter((t: any) => t.dueDate && new Date(t.dueDate) < today);
    const topOverdueTasks = overdueTasks.slice(0, 3).map((t: any) => ({
      task: (t.taskDescription || t.task || '').substring(0, 80),
      owner: t.assignedTo || t.owner || 'Unassigned',
      dueDate: t.dueDate
    }));

    return {
      summary: {
        totalOpportunities: userAccessibleOpportunities.length,
        activeOpportunities: activeOpportunitiesCount,
        totalPipelineValue,
        opportunitiesByStage,
        opportunitiesByStatus,
        revenueByStage,
        staleOpportunitiesCount: staleCount,
        overdueOpportunitiesCount: overdueCount,
        todayDate: today.toISOString().split('T')[0],
        // Compact aggregation strings for the AI system prompt
        byOwner,
        byGeography,
        byServiceLine
      },
      // Only top 5 attention deals (not full record arrays) — keeps tokens low
      attentionDeals,
      // Leads: counts only (geo-filtered)
      leads: {
        total: accessibleLeads.length,
        byStatus: leadByStatus,
        bySource: leadBySource
      },
      // Customers: counts only (geo-filtered)
      customers: {
        total: accessibleCustomers.length,
        active: activeCustomerCount,
        avgHealthScore
      },
      // Tasks: counts + top 3 overdue (geo-filtered)
      tasks: {
        total: accessibleTasks.length,
        open: openTasks.length,
        overdue: overdueTasks.length,
        topOverdue: topOverdueTasks
      },
      currentUser: currentUser ? {
        name: currentUser.name,
        role: currentUser.role,
        email: currentUser.email,
        isAdmin: currentUser.isAdmin,
        geographies: currentUser.geographies
      } : null
    };
  }, [opportunities, leads, customers, tasks, currentUser]); // Recalculate only when these change

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputMessage.trim();
    if (!textToSend || isLoading) return;

    // Save query to history
    saveQueryToHistory(textToSend);

    const userMessage: CopilotMessage = { role: 'user', content: textToSend, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setFollowUpSuggestions([]);
    setPendingAction(null);

    try {
      // Use the memoized context (already prepared)
      const crmContext = prepareCRMContext;

      // Debug: Log the context being sent to AI
      console.log('🤖 Copilot Context Debug:', {
        attentionDeals: crmContext.attentionDeals?.length,
        summaryTotalOpps: crmContext.summary?.totalOpportunities,
        leads: crmContext.leads?.total,
        customers: crmContext.customers?.total,
        tasks: crmContext.tasks?.total,
        currentUserInContext: crmContext.currentUser?.name
      });
      const response = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: textToSend, context: crmContext, sessionId })
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const result = await response.json();

      if (result.success) {
        // Generate content for action proposals when response is empty
        let responseContent = result.data.response;
        const actionProposal = result.data.actionProposal;

        if ((!responseContent || responseContent.trim() === '') && actionProposal) {
          // Generate a descriptive message for the action proposal
          const actionDescriptions: Record<string, string> = {
            'update_opportunity': 'I\'d like to update an opportunity for you.',
            'create_task': 'I\'d like to create a task for you.',
            'add_comment': 'I\'d like to add a comment to an opportunity.',
            'schedule_followup': 'I\'d like to schedule a follow-up for you.'
          };
          const actionDesc = actionDescriptions[actionProposal.type] || 'I\'d like to take an action for you.';
          const details = actionProposal.description || '';
          responseContent = `${actionDesc}\n\n📋 **Proposed Action**: ${details}\n\nPlease review the action below and approve or reject it.`;
        }

        const assistantMessage: CopilotMessage = {
          role: 'assistant',
          content: responseContent || 'I processed your request.',
          timestamp: new Date().toISOString(),
          actionProposal: actionProposal || null
        };
        setMessages(prev => [...prev, assistantMessage]);
        if (result.data.followUpSuggestions?.length > 0) setFollowUpSuggestions(result.data.followUpSuggestions);
        if (actionProposal) setPendingAction(actionProposal);
      } else {
        throw new Error(result.message || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Copilot API error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: `I apologize, but I encountered an error: ${error.message}. Please check that the API is configured correctly and try again.`, timestamp: new Date().toISOString() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearConversation = async () => {
    try {
      await fetch(`${API_BASE_URL}/ai/conversation`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId }) });
    } catch (error) { console.error('Failed to clear conversation on server:', error); }
    setMessages([]);
    setFollowUpSuggestions([]);
    setPendingAction(null);
  };

  // --- Voice Input Functions ---
  const startListening = () => {
    const recognition = recognitionRef.current;
    if (!recognition || isListening) return;
    setIsListening(true);
    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((result: any) => result[0].transcript)
        .join('');
      setInputMessage(transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone permission in your browser settings.');
      } else if (event.error === 'network') {
        alert('Network error during speech recognition. Please check your connection.');
      }
    };
    try { recognition.start(); } catch (_e) { setIsListening(false); }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const toggleListening = () => {
    if (isListening) stopListening();
    else startListening();
  };

  // --- Voice Output (TTS) Functions ---
  const speakMessage = (text: string, messageIdx: number) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const cleanText = text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/#{1,6}\s/g, '')
      .replace(/[-*]\s/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\n{2,}/g, '. ')
      .replace(/\n/g, '. ')
      .trim();
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.lang = 'en-US';
    utterance.onstart = () => setSpeakingMessageIdx(messageIdx);
    utterance.onend = () => setSpeakingMessageIdx(null);
    utterance.onerror = () => setSpeakingMessageIdx(null);
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setSpeakingMessageIdx(null);
    }
  };

  return (
    <div className="h-[calc(100vh-180px)] flex gap-4">
      {/* History Sidebar */}
      {showHistory && (
        <div className="w-64 flex-shrink-0 bg-white rounded-lg border border-gray-200 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Query History</span>
            </div>
            <button
              onClick={() => setShowHistory(false)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Hide history"
            >
              <ChevronLeft size={16} className="text-gray-400" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {groupedHistory.length === 0 ? (
              <div className="text-center py-8">
                <Clock size={24} className="text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-400">No recent queries</p>
                <p className="text-xs text-gray-400">Your questions will appear here</p>
              </div>
            ) : (
              groupedHistory.map((group, groupIdx) => (
                <div key={groupIdx} className="mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase px-2 mb-2">{group.label}</p>
                  <div className="space-y-1">
                    {group.items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleSendMessage(item.query)}
                        className="w-full text-left px-2 py-2 rounded-md hover:bg-gray-100 transition-colors group"
                        title={item.query}
                      >
                        <p className="text-sm text-gray-700 truncate">{item.query}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          {queryHistory.length > 0 && (
            <div className="p-2 border-t border-gray-200">
              <button
                onClick={() => {
                  if (confirm('Clear all query history?')) {
                    setQueryHistory([]);
                    localStorage.removeItem('copilot_query_history');
                  }
                }}
                className="w-full text-xs text-gray-500 hover:text-red-500 py-1"
              >
                Clear History
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              {!showHistory && (
                <button
                  onClick={() => setShowHistory(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200"
                  title="Show history"
                >
                  <Clock size={20} className="text-gray-500" />
                </button>
              )}
              <div className="p-2.5 rounded-xl" style={{background:'rgba(0,187,162,0.12)',border:'1px solid rgba(0,187,162,0.25)'}}><Bot style={{color:'#00BBA2'}} size={22} /></div>
              <div>
                <h2 className="text-sm font-bold text-gray-900">Sales Copilot</h2>
                <p className="text-xs text-gray-400 font-medium tracking-wide uppercase">AI-powered insights for your sales team</p>
              </div>
            </div>
            {messages.length > 0 && (
              <Button variant="secondary" onClick={handleClearConversation} className="text-sm">
                <RefreshCw size={14} className="mr-1" />Clear Chat
              </Button>
            )}
          </div>

          <div className="mt-4">
            <p className="text-sm font-medium text-gray-600 mb-2">Quick prompts:</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {Object.entries(PROMPT_CATEGORIES).map(([key, category]) => {
                const IconComponent = category.icon;
                const isSelected = selectedCategory === key;
                return (
                  <div key={key} className="space-y-1">
                    <button
                      onClick={() => setSelectedCategory(isSelected ? null : key)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border transition-all text-xs"
                      style={isSelected
                        ? {borderColor:'rgba(0,187,162,0.4)',background:'rgba(0,187,162,0.08)',color:'#00BBA2'}
                        : {borderColor:'#e2e8f0',background:'#fff',color:'#475569'}}
                    >
                      <IconComponent size={14} style={{ color: isSelected ? '#00BBA2' : category.color }} />
                      <span className="font-medium">{category.label}</span>
                      <ChevronDown size={12} className={`ml-auto transition-transform text-gray-400 ${isSelected ? 'rotate-180' : ''}`} />
                    </button>
                    {isSelected && (
                      <div className="space-y-0.5 pl-1 max-h-32 overflow-y-auto">
                        {category.prompts.map((prompt, idx) => (
                          <button key={idx} onClick={() => { setSelectedCategory(null); handleSendMessage(prompt); }} className="w-full text-left px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" style={{borderLeft:'2px solid rgba(0,187,162,0.3)',paddingLeft:'0.75rem'}}>{prompt}</button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <Card className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="p-5 rounded-2xl mb-4" style={{background:'rgba(0,187,162,0.1)',border:'1px solid rgba(0,187,162,0.2)'}}>
                <Bot size={32} style={{color:'#00BBA2'}} />
              </div>
              <p className="font-semibold text-gray-700 mb-1">How can I help you today?</p>
              <p className="text-gray-400 text-sm max-w-md">Ask me anything about your sales data — pipeline insights, team performance, forecasts, or let me help you take action on opportunities.</p>
            </div>
          )}

          {messages.map((message, idx) => (
            <div key={idx} className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0`} style={message.role === 'user' ? {background:'#00BBA2'} : {background:'var(--dm-surface-2)'}}>
                {message.role === 'user' ? <User size={16} className="text-white" /> : <Bot size={16} style={{color:'#00BBA2'}} />}
              </div>
              <div className={`flex-1 ${message.role === 'user' ? 'flex justify-end' : ''}`}>
                <div className={`max-w-3xl rounded-xl p-4`} style={message.role === 'user' ? {background:'#00BBA2',color:'#fff'} : {background:'var(--dm-surface-3)',border:'1px solid var(--dm-border)',color:'var(--dm-text)'}}>
                  <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs" style={message.role === 'user' ? {color:'rgba(255,255,255,0.65)'} : {color:'#94a3b8'}}>{new Date(message.timestamp).toLocaleTimeString()}</span>
                    {message.role === 'assistant' && ttsSupported && message.content && (
                      <button
                        onClick={() => speakingMessageIdx === idx ? stopSpeaking() : speakMessage(message.content, idx)}
                        className="p-1 rounded transition-colors"
                        style={speakingMessageIdx === idx ? {color:'#00BBA2'} : {color:'#94a3b8'}}
                        title={speakingMessageIdx === idx ? 'Stop reading' : 'Read aloud'}
                      >
                        {speakingMessageIdx === idx ? <VolumeX size={14} /> : <Volume2 size={14} />}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'var(--dm-surface-2)'}}><Bot size={16} style={{color:'#00BBA2'}} /></div>
              <div className="rounded-xl p-4" style={{background:'var(--dm-surface-3)',border:'1px solid var(--dm-border)'}}>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Analyzing your data</span>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full animate-bounce" style={{background:'#00BBA2',animationDelay:'0ms'}}></div>
                    <div className="w-2 h-2 rounded-full animate-bounce" style={{background:'#00BBA2',animationDelay:'150ms'}}></div>
                    <div className="w-2 h-2 rounded-full animate-bounce" style={{background:'#00BBA2',animationDelay:'300ms'}}></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {followUpSuggestions.length > 0 && !isLoading && (
            <div className="flex flex-wrap gap-2 mt-2">
              {followUpSuggestions.map((suggestion, idx) => (
                <button key={idx} onClick={() => handleSendMessage(suggestion)} className="px-3 py-1.5 text-xs rounded-full transition-colors" style={{background:'rgba(0,187,162,0.08)',border:'1px solid rgba(0,187,162,0.25)',color:'#009E8A'}}>{suggestion}</button>
              ))}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="border-t border-gray-200 p-4">
          {isListening && (
            <div className="flex items-center gap-2 px-3 py-1.5 mb-2 rounded-lg" style={{background:'rgba(0,187,162,0.08)',border:'1px solid rgba(0,187,162,0.3)'}}>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{background:'#00BBA2'}} />
              <span className="text-xs font-medium" style={{color:'#00BBA2'}}>Listening... speak now</span>
              <button onClick={stopListening} className="ml-auto text-xs text-gray-500 hover:text-gray-800">Cancel</button>
            </div>
          )}
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendMessage()}
              placeholder={isListening ? "Listening..." : "Ask about pipeline, performance, trends, or actions..."}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none bg-gray-50 placeholder-gray-400"
              style={isListening ? {borderColor:'#00BBA2',background:'rgba(0,187,162,0.04)'} : {}}
              disabled={isLoading}
            />
            {speechSupported && (
              <button
                onClick={toggleListening}
                disabled={isLoading}
                className="p-2.5 rounded-xl transition-all"
                style={isListening ? {background:'#00BBA2',color:'#fff'} : {background:'var(--dm-surface-2)',color:'#64748b'}}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                {isListening ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
            )}
            <button
              onClick={() => handleSendMessage()}
              disabled={isLoading || !inputMessage.trim()}
              className="p-2.5 rounded-xl text-white transition-all disabled:opacity-40"
              style={{background:'#00BBA2'}}
            >
              <Send size={16} />
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">Powered by Claude AI · Ask about pipeline, team performance, forecasts, or request actions.</p>
        </div>
      </Card>
      </div> {/* End of Main Content */}
    </div>
  );
};

// ========================================
// HELP VIEW
// ========================================
// HELP MODAL COMPONENT
// ========================================
const HelpModal = ({ onClose }) => {
  const [selectedTopic, setSelectedTopic] = useState('leads');
  const [searchTerm, setSearchTerm] = useState('');

  const topics = [
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'opportunities', label: 'Opportunities', icon: Target },
    { id: 'dashboard', label: 'Dashboard & Readout', icon: BarChart3 },
    { id: 'calendar', label: 'Calendar & Tasks', icon: CalendarDays },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'masterdata', label: 'Master Data', icon: FileText }
  ];

  const leadStatuses = [
    {
      status: 'OPEN',
      color: 'bg-blue-50',
      description: 'New lead captured and awaiting qualification. The lead has been entered into the system but has not yet been assessed.',
      activities: [
        'Capture lead details from source (LinkedIn, referral, event, etc.)',
        'Record contact person, company name, and initial requirements',
        'Assign lead owner based on geography or service line',
        'Set estimated revenue and priority level',
        'Begin initial research on the company'
      ]
    },
    {
      status: 'QUALIFIED',
      color: 'bg-green-50',
      description: 'Lead meets Ideal Customer Profile (ICP) criteria and is ready for conversion to an opportunity. Budget, timeline, and decision-maker access have been confirmed.',
      activities: [
        'Validate ICP criteria: industry, company size, budget, timeline',
        'Confirm decision-maker engagement and access',
        'Document qualification notes and key findings',
        'Assess fit with service offerings',
        'Prepare for conversion to opportunity'
      ]
    },
    {
      status: 'DISQUALIFIED',
      color: 'bg-amber-50',
      description: 'Lead has been assessed and does not meet the qualification criteria. Removed from active pipeline but retained for future reference.',
      activities: [
        'Document reason for disqualification (budget, timing, fit, etc.)',
        'Record any relevant notes for future reference',
        'Maintain contact for potential re-engagement',
        'Update CRM with disqualification details'
      ]
    },
    {
      status: 'CONVERTED',
      color: 'bg-teal-50',
      description: 'Lead has been qualified and converted into an opportunity. The lead record is linked to the new opportunity for tracking the full sales journey.',
      activities: [
        'Click "Qualify" on an Open lead to create an opportunity',
        'Opportunity starts at Qualified stage (20% probability)',
        'Transfer all lead details to opportunity record',
        'Assign account owner and begin opportunity management',
        'Link lead record to opportunity for traceability'
      ]
    }
  ];

  const opportunityStages = [
    {
      stage: 'QUALIFIED',
      probability: '20%',
      color: 'bg-[rgba(106,124,165,0.1)]',
      description: 'Lead qualified and converted to opportunity. Initial assessment completed; ready for discovery.',
      activities: [
        'Review lead qualification notes and ICP criteria',
        'Confirm decision-maker engagement and access',
        'Plan discovery approach and prepare questions',
        'Schedule initial discovery meeting',
        'Document qualification rationale'
      ]
    },
    {
      stage: 'DISCOVERY',
      probability: '30%',
      color: 'bg-slate-tint',
      description: 'First meaningful conversation held; gather context and needs.',
      activities: [
        'Confirm budget availability and timeline',
        'Conduct discovery call to understand business needs',
        'Identify pain points, challenges, and priorities',
        'Build rapport with key contacts and document findings',
        'Validate ICP criteria and record qualification notes'
      ]
    },
    {
      stage: 'SOLUTIONING',
      probability: '50%',
      color: 'bg-ivory',
      description: 'Deep dive on client\u2019s processes and requirements.',
      activities: [
        'Conduct workshops and scoping sessions',
        'Analyze current processes and systems',
        'Design tailored solution approach',
        'Prepare solution document and draft commercials',
        'Get client feedback and refine approach'
      ]
    },
    {
      stage: 'DISCUSSION',
      probability: '60%',
      color: 'bg-amber-tint',
      description: 'Solution presented to client; active follow-up on status and outcome.',
      activities: [
        'Present solution approach in client meeting',
        'Follow up on proposal status and feedback',
        'Address concerns, objections, and questions',
        'Refine approach based on client feedback',
        'Coordinate with internal teams for revisions'
      ]
    },
    {
      stage: 'PROPOSAL',
      probability: '70%',
      color: 'bg-clay-tint',
      description: 'Formal proposal or RFP submitted to the client.',
      activities: [
        'Prepare and submit detailed proposal document',
        'Include scope, timeline, and commercials',
        'Respond to clarifications and questions',
        'Schedule and deliver proposal presentations',
        'Address initial feedback from client'
      ]
    },
    {
      stage: 'NEGOTIATION',
      probability: '80%',
      color: 'bg-clay',
      description: 'Down-selected; discussions on scope, terms, and commercials.',
      activities: [
        'Participate in down-select meetings',
        'Negotiate scope, deliverables, and terms',
        'Discuss commercial pricing and contract structure',
        'Address legal and compliance requirements',
        'Coordinate with legal teams on both sides'
      ]
    },
    {
      stage: 'Closed-Won',
      probability: '90%',
      color: 'bg-sage-tint',
      description: 'Deal awarded; contract preparation in progress.',
      activities: [
        'Confirm award and document win details',
        'Initiate contract drafting with legal teams',
        'Prepare implementation and kick-off plan',
        'Transition to delivery team',
        'Schedule project kick-off meeting'
      ]
    },
    {
      stage: 'CONTRACT',
      probability: '100%',
      color: 'bg-emerald-50',
      description: 'Contract signed and fully executed. Deal is complete and revenue recognized.',
      activities: [
        'Finalize contract with all amendments and sign-offs',
        'Get executive sign-offs from both parties',
        'Complete handover to delivery and customer success',
        'Update CRM with final contract values (ACV/TCV)',
        'Confirm project start date and milestones'
      ]
    },
    {
      stage: 'Closed-Lost',
      probability: '0%',
      color: 'bg-pierian-20',
      description: 'Opportunity closed without success.',
      activities: [
        'Record reason for loss (pricing, competition, timing, scope mismatch)',
        'Conduct lessons learned session',
        'Maintain relationship for future opportunities',
        'Update CRM with feedback and insights',
        'Thank stakeholders for their time and consideration'
      ]
    }
  ];

  const renderLeadsHelp = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-2">Lead Management</h2>
        <p className="text-gray-600">
          Leads represent potential business opportunities that have been captured from various sources.
          Each lead progresses through a lifecycle from initial capture to qualification and conversion into an opportunity.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">Lead Lifecycle Stages</h3>
        {leadStatuses.map((item, index) => (
          <Card key={index} className={`p-4 ${item.color} border-l-4 ${
            item.status === 'QUALIFIED' ? 'border-l-green-600' :
            item.status === 'CONVERTED' ? 'border-l-red-600' :
            item.status === 'DISQUALIFIED' ? 'border-l-amber-500' :
            'border-l-blue-500'
          }`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-gray-700">{index + 1}</span>
                <div>
                  <h4 className="font-semibold text-gray-900">{item.status}</h4>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-3 w-full" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowWrap: 'anywhere'}}>{item.description}</p>
            <div className="space-y-1">
              <p className="text-xs font-semibold text-gray-700">Key Activities:</p>
              {item.activities.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <CheckCircle size={14} className="text-gray-600 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-600">{activity}</span>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-4 bg-pierian-10 border-l-4 border-l-pierian">
        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Sparkles size={18} className="text-pierian" />
          Best Practices
        </h4>
        <ul className="text-sm text-gray-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Capture leads promptly — enter all details as soon as a new prospect is identified</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Qualify leads within 48 hours of capture to maintain pipeline velocity</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Use the source field accurately to track which channels generate the best leads</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Add estimated revenue early to help with pipeline forecasting</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Flag leads for review when management attention is needed</span>
          </li>
        </ul>
      </Card>
    </div>
  );

  const renderOpportunitiesHelp = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-2">Opportunity Management</h2>
        <p className="text-gray-600">
          Pierag Sales Connect follows a structured 9-stage opportunity pipeline. Each stage represents a significant milestone
          in the sales process, from initial qualification through to contract execution and deal closure.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">Sales Pipeline Stages</h3>
        {opportunityStages.map((item, index) => (
          <Card key={index} className={`p-4 ${item.color} border-l-4 ${
            item.stage === 'WON' ? 'border-l-green-600' :
            item.stage === 'LOST' ? 'border-l-red-600' :
            'border-l-pierian'
          }`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-gray-700">{index + 1}</span>
                <div>
                  <h4 className="font-semibold text-gray-900">{item.stage}</h4>
                  <p className="text-xs text-gray-600">Win Probability: {item.probability}</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-3 w-full" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowWrap: 'anywhere'}}>{item.description}</p>
            <div className="space-y-1">
              <p className="text-xs font-semibold text-gray-700">Key Activities:</p>
              {item.activities.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <CheckCircle size={14} className="text-gray-600 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-600">{activity}</span>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-4 bg-pierian-10 border-l-4 border-l-pierian">
        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Sparkles size={18} className="text-pierian" />
          Best Practices
        </h4>
        <ul className="text-sm text-gray-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Update opportunity stages promptly after each customer interaction</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Add detailed notes and action items in the engagement log</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Keep probability estimates realistic based on actual progress</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Set clear next steps and follow-up dates</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-pierian">•</span>
            <span>Review stuck opportunities regularly and take corrective action</span>
          </li>
        </ul>
      </Card>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">Sales Status (Health Indicators)</h3>
        <p className="text-sm text-gray-600 mb-2">
          Each opportunity is automatically assigned a sales status based on time since last activity. The status can also be manually overridden.
        </p>
        <div className="space-y-2">
          <Card className="p-3 bg-red-50 border-l-4 border-l-red-500">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-teal-500"></div>
              <h4 className="font-semibold text-sm text-gray-900">Hot</h4>
            </div>
            <p className="text-xs text-gray-700">Active within 30 days and at Proposal stage. These are high-priority deals near closure.</p>
          </Card>
          <Card className="p-3 bg-amber-50 border-l-4 border-l-amber-500">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <h4 className="font-semibold text-sm text-gray-900">Warm</h4>
            </div>
            <p className="text-xs text-gray-700">Active within 30 days at earlier stages (Qualified, Discovery). Engagement is healthy; keep momentum going.</p>
          </Card>
          <Card className="p-3 bg-[rgba(0,187,162,0.06)] border-l-4 border-l-[#00BBA2]">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#6A7CA5' }}></div>
              <h4 className="font-semibold text-sm text-gray-900">Cold</h4>
            </div>
            <p className="text-xs text-gray-700">No activity for 30\u201390 days. Requires immediate re-engagement or risk moving to Inactive.</p>
          </Card>
          <Card className="p-3 bg-gray-50 border-l-4 border-l-gray-400">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <h4 className="font-semibold text-sm text-gray-900">Inactive</h4>
            </div>
            <p className="text-xs text-gray-700">No activity for more than 90 days. Excluded from active pipeline metrics. Consider closing or re-qualifying.</p>
          </Card>
        </div>
        <Card className="p-3 bg-gray-50 border border-gray-200">
          <p className="text-xs text-gray-600"><span className="font-semibold">Manual Override:</span> Managers can override the auto-calculated status via the opportunity form. Overrides persist until manually cleared.</p>
        </Card>
      </div>
    </div>
  );

  const renderDashboardHelp = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-2">Dashboard & Readout</h2>
        <p className="text-gray-600">
          The Dashboard provides a real-time view of your sales pipeline, performance metrics, and activity feed.
          The Readout generates executive-ready summaries for any date range.
        </p>
      </div>

      <div className="space-y-4">
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Dashboard Sub-tabs</h3>
          <div className="space-y-2">
            <div className="flex items-start gap-3 p-2 bg-gray-50 rounded">
              <span className="font-semibold text-pierian min-w-[90px] text-sm">Pipeline</span>
              <p className="text-xs text-gray-700">Funnel view with open leads, active opportunities by stage, pipeline value chart, and top deals. Click any stage to filter.</p>
            </div>
            <div className="flex items-start gap-3 p-2 bg-gray-50 rounded">
              <span className="font-semibold text-pierian min-w-[90px] text-sm">Activity</span>
              <p className="text-xs text-gray-700">Chronological feed of stage movements, new deals, and won/lost outcomes. Filterable by movement type.</p>
            </div>
            <div className="flex items-start gap-3 p-2 bg-gray-50 rounded">
              <span className="font-semibold text-pierian min-w-[90px] text-sm">Performance</span>
              <p className="text-xs text-gray-700">KPI summary, weekly performance trends, pipeline by geography/service line, and win/loss analysis.</p>
            </div>
            <div className="flex items-start gap-3 p-2 bg-gray-50 rounded">
              <span className="font-semibold text-pierian min-w-[90px] text-sm">Reports</span>
              <p className="text-xs text-gray-700">Detailed tabular reports by sales rep, geography, and service line with export to Excel.</p>
            </div>
            <div className="flex items-start gap-3 p-2 bg-gray-50 rounded">
              <span className="font-semibold text-pierian min-w-[90px] text-sm">Readout</span>
              <p className="text-xs text-gray-700">Executive summary for a selected date range with KPIs, trends, pipeline health, forecasts, and data quality scores. Exportable as PDF or Excel.</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Readout Date Range</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p>The Readout tab has quick-select buttons and custom date pickers:</p>
            <div className="space-y-1.5 ml-2">
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-pierian mt-0.5" />
                <span className="text-xs"><strong>Week</strong> \u2014 Last 7 days</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-pierian mt-0.5" />
                <span className="text-xs"><strong>Fortnight</strong> \u2014 Last 14 days</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-pierian mt-0.5" />
                <span className="text-xs"><strong>This Month</strong> \u2014 1st of current month to today</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-pierian mt-0.5" />
                <span className="text-xs"><strong>Last Month</strong> \u2014 Full previous calendar month</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-pierian mt-0.5" />
                <span className="text-xs"><strong>2 Months</strong> \u2014 Last 60 days</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-pierian mt-0.5" />
                <span className="text-xs"><strong>Custom</strong> \u2014 Set any From/To dates manually</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">The prior-period comparison always uses the same span length immediately before your selected range.</p>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Global Filters</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p>Filter chips at the top of the Dashboard apply across all sub-tabs:</p>
            <div className="space-y-1.5 ml-2">
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-pierian mt-0.5" />
                <span className="text-xs"><strong>My Dashboard</strong> \u2014 Show only your own leads and opportunities (off by default)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-pierian mt-0.5" />
                <span className="text-xs"><strong>Geo / Owner / Service Line / BU / Status / Entity</strong> \u2014 Multi-select filters</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={14} className="text-pierian mt-0.5" />
                <span className="text-xs"><strong>Date</strong> \u2014 Filter by time period (hidden on Readout tab which has its own date controls)</span>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Pipeline Metrics</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <CheckCircle size={14} className="text-pierian mt-0.5" />
              <span className="text-xs"><strong>Active Pipeline</strong> \u2014 ACV sum of opportunities excluding Won, Lost, Contract, Assessment, Lead stages, and Inactive status</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={14} className="text-pierian mt-0.5" />
              <span className="text-xs"><strong>Weighted Pipeline</strong> \u2014 ACV weighted by stage probability (e.g., Proposal at 70% = ACV \u00d7 0.7)</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={14} className="text-pierian mt-0.5" />
              <span className="text-xs"><strong>Total Pipeline Value</strong> \u2014 Includes both open leads pipeline and active opportunity ACV</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderCalendarHelp = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-2">Calendar & Task Management</h2>
        <p className="text-gray-600">
          The Calendar view helps you manage tasks, follow-ups, and activities across all opportunities.
        </p>
      </div>

      <div className="space-y-4">
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Creating Tasks</h3>
          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex items-start gap-3">
              <span className="font-semibold text-pierian min-w-[24px]">1.</span>
              <div>
                <p className="font-medium">Click "Create Task" button</p>
                <p className="text-gray-600">Opens the task creation form</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="font-semibold text-pierian min-w-[24px]">2.</span>
              <div>
                <p className="font-medium">Fill in task details</p>
                <p className="text-gray-600">Task name, owner, due date, priority, and related entity</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="font-semibold text-pierian min-w-[24px]">3.</span>
              <div>
                <p className="font-medium">Set priority level</p>
                <p className="text-gray-600">High, Medium, or Low based on urgency</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="font-semibold text-pierian min-w-[24px]">4.</span>
              <div>
                <p className="font-medium">Link to opportunity</p>
                <p className="text-gray-600">Associate task with specific opportunity for context</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Task Status</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-2 bg-amber-tint rounded">
              <Clock size={16} className="text-amber" />
              <div className="flex-1">
                <p className="font-medium text-sm">Pending</p>
                <p className="text-xs text-gray-600">Task created but not yet started</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-pierian-10 rounded">
              <Activity size={16} className="text-pierian" />
              <div className="flex-1">
                <p className="font-medium text-sm">In Progress</p>
                <p className="text-xs text-gray-600">Task is currently being worked on</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-sage-tint rounded">
              <CheckCircle size={16} className="text-sage" />
              <div className="flex-1">
                <p className="font-medium text-sm">Completed</p>
                <p className="text-xs text-gray-600">Task finished successfully</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Task Features</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-pierian mt-0.5" />
              <div>
                <p className="font-medium">Postpone Tasks</p>
                <p className="text-gray-600 text-xs">Move due date forward when needed</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-pierian mt-0.5" />
              <div>
                <p className="font-medium">Add Comments</p>
                <p className="text-gray-600 text-xs">Document progress and updates</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-pierian mt-0.5" />
              <div>
                <p className="font-medium">Filter & Sort</p>
                <p className="text-gray-600 text-xs">View by status, priority, or date</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-pierian mt-0.5" />
              <div>
                <p className="font-medium">Calendar View</p>
                <p className="text-gray-600 text-xs">Visualize tasks on monthly calendar</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderSettingsHelp = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-2">Settings Configuration</h2>
        <p className="text-gray-600">
          Admin users can configure system settings, manage users, and maintain master data.
        </p>
      </div>

      <div className="space-y-4">
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <User size={20} className="text-pierian" />
            User Management
          </h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p className="font-medium">Add New Users:</p>
            <ul className="space-y-1 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-pierian">•</span>
                <span>Click "Add User" button in Settings → Users tab</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pierian">•</span>
                <span>Enter user details: name, email, role, geography, business unit</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pierian">•</span>
                <span>Assign appropriate role: Sales Rep, Manager, or Admin</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pierian">•</span>
                <span>Set user status to Active or Inactive</span>
              </li>
            </ul>
            <p className="font-medium mt-3">Edit Users:</p>
            <ul className="space-y-1 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-pierian">•</span>
                <span>Click edit icon next to user name</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pierian">•</span>
                <span>Modify user information and save</span>
              </li>
            </ul>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText size={20} className="text-pierian" />
            Data Management
          </h3>
          <div className="space-y-3 text-sm text-gray-700">
            <div>
              <p className="font-medium">Export Data:</p>
              <ul className="space-y-1 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Navigate to Settings → Data Management tab</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Click export buttons for Opportunities, Leads, or Customers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Data downloads as Excel file</span>
                </li>
              </ul>
            </div>
            <div>
              <p className="font-medium">Import Data:</p>
              <ul className="space-y-1 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Download sample template for correct format</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Prepare your Excel file with proper column headers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Click import button and select your file</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Review import summary and confirm</span>
                </li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-amber-tint border-l-4 border-l-amber">
          <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <AlertCircle size={18} className="text-amber" />
            Important Notes
          </h4>
          <ul className="text-sm text-gray-700 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-amber">⚠</span>
              <span>Only Admin users can access Settings and manage data</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber">⚠</span>
              <span>Export data regularly for backup purposes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber">⚠</span>
              <span>Clear All Data action is permanent and cannot be undone</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );

  const renderMasterDataHelp = () => {
    const handleDownloadTemplate = () => {
      // Generate the Excel template
      const XLSX = window.XLSX;
      if (!XLSX) {
        alert('Excel library not loaded. Please refresh the page.');
        return;
      }

      // Define all master data with sample values
      const masters = {
        'Sales Stages': [
          { 'ID': 1, 'Stage Name': 'Qualified', 'Order': 1, 'Status': 'Active' },
          { 'ID': 2, 'Stage Name': 'Discovery', 'Order': 2, 'Status': 'Active' },
          { 'ID': 3, 'Stage Name': 'Proposal', 'Order': 3, 'Status': 'Active' },
          { 'ID': 4, 'Stage Name': 'Contract', 'Order': 4, 'Status': 'Active' },
          { 'ID': 5, 'Stage Name': 'Closed-Lost', 'Order': 5, 'Status': 'Active' },
          { 'ID': 6, 'Stage Name': 'Closed-Won', 'Order': 6, 'Status': 'Active' }
        ],
        'Industries': [
          { 'ID': 1, 'Industry Name': 'Technology', 'Status': 'Active' },
          { 'ID': 2, 'Industry Name': 'Healthcare', 'Status': 'Active' },
          { 'ID': 3, 'Industry Name': 'Finance', 'Status': 'Active' },
          { 'ID': 4, 'Industry Name': 'Manufacturing', 'Status': 'Active' },
          { 'ID': 5, 'Industry Name': 'Retail', 'Status': 'Active' },
          { 'ID': 6, 'Industry Name': 'Education', 'Status': 'Active' },
          { 'ID': 7, 'Industry Name': 'Telecommunications', 'Status': 'Active' },
          { 'ID': 8, 'Industry Name': 'Real Estate', 'Status': 'Active' }
        ],
        'Service Lines': [
          { 'ID': 1, 'Service Line Name': 'Accounting Advisory', 'Status': 'Active' },
          { 'ID': 2, 'Service Line Name': 'Assurance', 'Status': 'Active' },
          { 'ID': 3, 'Service Line Name': 'Business Risk Advisory', 'Status': 'Active' },
          { 'ID': 4, 'Service Line Name': 'CFO Advisory', 'Status': 'Active' },
          { 'ID': 5, 'Service Line Name': 'Corporate', 'Status': 'Active' },
          { 'ID': 6, 'Service Line Name': 'Deals Advisory', 'Status': 'Active' },
          { 'ID': 7, 'Service Line Name': 'ESG & Sustainability', 'Status': 'Active' },
          { 'ID': 8, 'Service Line Name': 'Technology Risk Advisory', 'Status': 'Active' }
        ],
        'Geographies': [
          { 'ID': 1, 'Geography Name': 'United States', 'Region': 'North America', 'Status': 'Active' },
          { 'ID': 2, 'Geography Name': 'Canada', 'Region': 'North America', 'Status': 'Active' },
          { 'ID': 3, 'Geography Name': 'United Kingdom', 'Region': 'Europe', 'Status': 'Active' },
          { 'ID': 4, 'Geography Name': 'Germany', 'Region': 'Europe', 'Status': 'Active' },
          { 'ID': 5, 'Geography Name': 'France', 'Region': 'Europe', 'Status': 'Active' },
          { 'ID': 6, 'Geography Name': 'India', 'Region': 'Asia Pacific', 'Status': 'Active' },
          { 'ID': 7, 'Geography Name': 'Singapore', 'Region': 'Asia Pacific', 'Status': 'Active' },
          { 'ID': 8, 'Geography Name': 'Australia', 'Region': 'Asia Pacific', 'Status': 'Active' },
          { 'ID': 9, 'Geography Name': 'Brazil', 'Region': 'Latin America', 'Status': 'Active' },
          { 'ID': 10, 'Geography Name': 'Mexico', 'Region': 'Latin America', 'Status': 'Active' }
        ],
        'Service Offerings': [
          { 'ID': 1, 'Service Offering Name': '24/7 Support', 'Status': 'Active' },
          { 'ID': 2, 'Service Offering Name': 'Business Process Outsourcing', 'Status': 'Active' },
          { 'ID': 3, 'Service Offering Name': 'IT Infrastructure Management', 'Status': 'Active' },
          { 'ID': 4, 'Service Offering Name': 'Application Development', 'Status': 'Active' },
          { 'ID': 5, 'Service Offering Name': 'Data Analytics', 'Status': 'Active' },
          { 'ID': 6, 'Service Offering Name': 'Cloud Migration', 'Status': 'Active' },
          { 'ID': 7, 'Service Offering Name': 'Security Services', 'Status': 'Active' },
          { 'ID': 8, 'Service Offering Name': 'Quality Assurance', 'Status': 'Active' }
        ],
        'Entities': [
          { 'ID': 1, 'Entity Name': 'Pierian Inc', 'Status': 'Active' },
          { 'ID': 2, 'Entity Name': 'Pierian India Private Limited', 'Status': 'Active' },
          { 'ID': 3, 'Entity Name': 'Pierian UK Limited', 'Status': 'Active' },
          { 'ID': 4, 'Entity Name': 'Pierian Singapore Pte Ltd', 'Status': 'Active' }
        ],
        'Lead Sources': [
          { 'ID': 1, 'Source Name': 'Leadership Network', 'Status': 'Active' },
          { 'ID': 2, 'Source Name': 'Network Partner (External)', 'Status': 'Active' },
          { 'ID': 3, 'Source Name': 'Internal SL Partner', 'Status': 'Active' },
          { 'ID': 4, 'Source Name': 'Employee Referral', 'Status': 'Active' },
          { 'ID': 5, 'Source Name': 'Inbound Leads', 'Status': 'Active' },
          { 'ID': 6, 'Source Name': 'Existing Clients', 'Status': 'Active' },
          { 'ID': 7, 'Source Name': 'Events', 'Status': 'Active' },
          { 'ID': 8, 'Source Name': 'Cold Outreach', 'Status': 'Active' },
          { 'ID': 9, 'Source Name': 'Sales team', 'Status': 'Active' }
        ],
        'Users': [
          { 'User ID': 'USER-001', 'Name': 'Sarah Johnson', 'Email': 'sarah.johnson@pierian.com', 'Role': 'Sales Head', 'Department': 'Sales', 'Manager Name': '', 'Phone': '+1-555-0101', 'Status': 'Active', 'Join Date': '2020-01-15', 'Team': 'North America Sales', 'Is Admin': 'Yes' },
          { 'User ID': 'USER-002', 'Name': 'Michael Chen', 'Email': 'michael.chen@pierian.com', 'Role': 'Senior Sales Manager', 'Department': 'Sales', 'Manager Name': 'Sarah Johnson', 'Phone': '+1-555-0102', 'Status': 'Active', 'Join Date': '2020-03-20', 'Team': 'North America Sales', 'Is Admin': 'No' },
          { 'User ID': 'USER-003', 'Name': 'Emily Rodriguez', 'Email': 'emily.rodriguez@pierian.com', 'Role': 'Sales Executive', 'Department': 'Sales', 'Manager Name': 'Michael Chen', 'Phone': '+1-555-0103', 'Status': 'Active', 'Join Date': '2021-06-10', 'Team': 'North America Sales', 'Is Admin': 'No' },
          { 'User ID': 'USER-004', 'Name': 'Priya Sharma', 'Email': 'priya.sharma@pierian.com', 'Role': 'Regional Sales Manager', 'Department': 'Sales', 'Manager Name': 'Sarah Johnson', 'Phone': '+91-555-0201', 'Status': 'Active', 'Join Date': '2020-05-01', 'Team': 'Asia Pacific Sales', 'Is Admin': 'No' }
        ]
      };

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Add instructions sheet
      const instructions = [
        ['Pierag Sales Connect - Master Data Template'],
        [''],
        ['This Excel workbook contains all master data required to set up Pierag Sales Connect CRM.'],
        [''],
        ['Instructions:'],
        ['1. Review each sheet and update the data according to your organization\'s requirements'],
        ['2. You can add new rows for additional entries'],
        ['3. Do not change the column headers'],
        ['4. ID column: Use sequential numbers for new entries'],
        ['5. Status column: Use "Active" or "Inactive"'],
        ['6. For Geographies sheet, Region must be one of: North America, Europe, Asia Pacific, Latin America, Middle East, Africa'],
        ['7. For Users sheet, Is Admin must be "Yes" or "No"'],
        [''],
        ['Sheets included:'],
        ['- Sales Stages: Pipeline stages for opportunities (10 entries)'],
        ['- Industries: Industry verticals for leads and opportunities (8 entries)'],
        ['- Service Lines: Service offerings categories (8 entries)'],
        ['- Geographies: Countries/regions for operations (10 entries)'],
        ['- Service Offerings: Specific service offerings (8 entries)'],
        ['- Entities: Legal entities (4 entries)'],
        ['- Lead Sources: Sources for lead capture (9 entries)'],
        ['- Users: Team members and sales representatives (4 sample entries)'],
        [''],
        ['Date Created: ' + new Date().toISOString().split('T')[0]],
        [''],
        ['Note: This template contains sample data. Please customize it for your organization.']
      ];

      const instructionsSheet = XLSX.utils.aoa_to_sheet(instructions);
      instructionsSheet['!cols'] = [{ wch: 100 }];
      XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');

      // Add each master data sheet
      Object.keys(masters).forEach(sheetName => {
        const worksheet = XLSX.utils.json_to_sheet(masters[sheetName]);
        const cols = [];
        const data = masters[sheetName];
        if (data.length > 0) {
          Object.keys(data[0]).forEach(key => {
            const maxLength = Math.max(key.length, ...data.map(row => String(row[key]).length));
            cols.push({ wch: Math.min(maxLength + 2, 50) });
          });
          worksheet['!cols'] = cols;
        }
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      // Download file
      XLSX.writeFile(workbook, 'Pierian_Sales_Connect_Master_Data_Template.xlsx');
    };

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-sm font-bold text-gray-900 mb-2">Master Data Management</h2>
          <p className="text-gray-600">
            Configure and maintain reference data used throughout the CRM system. Use the Master Data Template to collect and set up your organization's data for go-live.
          </p>
        </div>

        {/* Download Template Section */}
        <Card className="p-4 bg-gradient-to-r from-[rgba(0,187,162,0.06)] to-[rgba(106,124,165,0.06)] border border-[rgba(0,187,162,0.25)]">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-pierian rounded-lg">
              <Upload size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Master Data Collection Template</h3>
              <p className="text-sm text-gray-700 mb-4">
                Download the comprehensive Excel template containing all master data required for system setup.
                The template includes 9 pre-configured sheets with sample data to help you prepare for go-live.
              </p>
              <Button
                onClick={handleDownloadTemplate}
                variant="primary"
                className="flex items-center gap-2"
              >
                <Upload size={16} />
                Download Excel Template
              </Button>
              <p className="text-xs text-gray-600 mt-2">
                Template includes: Sales Stages, Industries, Service Lines, Geographies, Service Offerings, Entities, Lead Sources, and Users
              </p>
            </div>
          </div>
        </Card>

        {/* Template Contents */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Template Contents</h3>
          <p className="text-sm text-gray-600 mb-4">
            The Master Data Template contains 10 sheets with sample data for your reference:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">Sales Stages</p>
                  <p className="text-xs text-gray-600 mt-1">10 pipeline stages with ordering</p>
                </div>
                <span className="text-xs bg-pierian-10 text-scarlet px-2 py-1 rounded">10 entries</span>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">Industries</p>
                  <p className="text-xs text-gray-600 mt-1">Industry verticals & sectors</p>
                </div>
                <span className="text-xs bg-pierian-10 text-scarlet px-2 py-1 rounded">8 entries</span>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">Service Lines</p>
                  <p className="text-xs text-gray-600 mt-1">Service offering categories</p>
                </div>
                <span className="text-xs bg-pierian-10 text-scarlet px-2 py-1 rounded">8 entries</span>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">Geographies</p>
                  <p className="text-xs text-gray-600 mt-1">Countries with regional mapping</p>
                </div>
                <span className="text-xs bg-pierian-10 text-scarlet px-2 py-1 rounded">10 entries</span>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">Service Offerings</p>
                  <p className="text-xs text-gray-600 mt-1">Specific service products</p>
                </div>
                <span className="text-xs bg-pierian-10 text-scarlet px-2 py-1 rounded">8 entries</span>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">Entities</p>
                  <p className="text-xs text-gray-600 mt-1">Legal entities & subsidiaries</p>
                </div>
                <span className="text-xs bg-pierian-10 text-scarlet px-2 py-1 rounded">4 entries</span>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">Lead Sources</p>
                  <p className="text-xs text-gray-600 mt-1">Lead acquisition channels</p>
                </div>
                <span className="text-xs bg-pierian-10 text-scarlet px-2 py-1 rounded">9 entries</span>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">Users</p>
                  <p className="text-xs text-gray-600 mt-1">Team members & sales reps</p>
                </div>
                <span className="text-xs bg-pierian-10 text-scarlet px-2 py-1 rounded">4 samples</span>
              </div>
            </div>
            <div className="p-3 bg-pierian-10 rounded border border-pierian-30">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">Instructions</p>
                  <p className="text-xs text-gray-600 mt-1">Setup guide & guidelines</p>
                </div>
                <span className="text-xs bg-sage-tint text-sage-shade px-2 py-1 rounded">Guide</span>
              </div>
            </div>
          </div>
        </Card>

        {/* How to Use Template */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">How to Use the Template</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-pierian text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">1</div>
              <div>
                <p className="font-medium text-sm">Download the Template</p>
                <p className="text-xs text-gray-600 mt-1">Click the "Download Excel Template" button above to get the pre-configured workbook</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-pierian text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">2</div>
              <div>
                <p className="font-medium text-sm">Review Sample Data</p>
                <p className="text-xs text-gray-600 mt-1">Each sheet contains sample entries to help you understand the required format and structure</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-pierian text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">3</div>
              <div>
                <p className="font-medium text-sm">Customize for Your Organization</p>
                <p className="text-xs text-gray-600 mt-1">Replace sample data with your organization's actual master data. Add or remove rows as needed, but keep column headers unchanged</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-pierian text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">4</div>
              <div>
                <p className="font-medium text-sm">Maintain Data Quality</p>
                <p className="text-xs text-gray-600 mt-1">Ensure all required fields are filled, use consistent naming conventions, and validate region mappings for geographies</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-pierian text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">5</div>
              <div>
                <p className="font-medium text-sm">Upload to System</p>
                <p className="text-xs text-gray-600 mt-1">Once completed, use the Settings page to manually enter the data or coordinate with your administrator for bulk import</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Managing Master Data */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Managing Master Data in the System</h3>
          <div className="space-y-3 text-sm text-gray-700">
            <div>
              <p className="font-medium">Adding New Entries:</p>
              <ul className="space-y-1 ml-4 mt-1">
                <li className="flex items-start gap-2">
                  <span className="text-pierian">1.</span>
                  <span>Navigate to Settings page (gear icon in top navigation)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">2.</span>
                  <span>Select the appropriate tab (Sales Stages, Industries, Service Lines, etc.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">3.</span>
                  <span>Click "Add New" button in the top-right corner</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">4.</span>
                  <span>Enter the name/details in the modal form</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">5.</span>
                  <span>For Geographies, select the appropriate Region from dropdown</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">6.</span>
                  <span>Set Active status checkbox (checked = Active)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">7.</span>
                  <span>Click Create to save the entry</span>
                </li>
              </ul>
            </div>
            <div>
              <p className="font-medium mt-3">Editing Entries:</p>
              <ul className="space-y-1 ml-4 mt-1">
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Click the edit icon (pencil) next to any entry in the table</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Modify the name, region (for geographies), or status as needed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span>Click Update to save changes</span>
                </li>
              </ul>
            </div>
            <div>
              <p className="font-medium mt-3">Deactivating vs. Deleting:</p>
              <ul className="space-y-1 ml-4 mt-1">
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span><strong>Deactivate:</strong> Uncheck Active status - entry won't appear in new records but preserves historical data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span><strong>Delete:</strong> Click trash icon - permanently removes entry (use with caution)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pierian">•</span>
                  <span><strong>Recommendation:</strong> Always deactivate instead of delete to maintain data integrity</span>
                </li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Template Specifications */}
        <Card className="p-4 bg-amber-tint border-l-4 border-l-amber">
          <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <AlertCircle size={18} className="text-amber" />
            Important Guidelines
          </h4>
          <ul className="text-sm text-gray-700 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-amber">•</span>
              <span><strong>Do not modify column headers</strong> - The import process relies on specific header names</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber">•</span>
              <span><strong>ID column:</strong> Use sequential numbers (1, 2, 3...) for new entries</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber">•</span>
              <span><strong>Status column:</strong> Must be exactly "Active" or "Inactive" (case-sensitive)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber">•</span>
              <span><strong>Geographies Region:</strong> Must be one of: North America, Europe, Asia Pacific, Latin America, Middle East, Africa</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber">•</span>
              <span><strong>Users Is Admin:</strong> Must be "Yes" or "No"</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber">•</span>
              <span><strong>Date format:</strong> Use YYYY-MM-DD format (e.g., 2025-01-15)</span>
            </li>
          </ul>
        </Card>

        {/* Best Practices */}
        <Card className="p-4 bg-pierian-10 border-l-4 border-l-pierian">
          <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Sparkles size={18} className="text-pierian" />
            Best Practices
          </h4>
          <ul className="text-sm text-gray-700 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-pierian">•</span>
              <span><strong>Setup before go-live:</strong> Complete all master data setup before importing leads, opportunities, or customers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-pierian">•</span>
              <span><strong>Consistent naming:</strong> Use clear, consistent naming conventions across all master data types</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-pierian">•</span>
              <span><strong>Start minimal:</strong> Begin with essential entries and add more as needed rather than overwhelming the system</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-pierian">•</span>
              <span><strong>Regular reviews:</strong> Schedule quarterly reviews to clean up unused or duplicate entries</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-pierian">•</span>
              <span><strong>Document decisions:</strong> Keep notes on why certain master data entries were added or modified</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-pierian">•</span>
              <span><strong>User hierarchy:</strong> Properly set up manager relationships in Users sheet for reporting and permissions</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-pierian">•</span>
              <span><strong>Backup regularly:</strong> Export master data periodically as backup and for audit purposes</span>
            </li>
          </ul>
        </Card>
      </div>
    );
  };

  const renderContent = () => {
    switch (selectedTopic) {
      case 'leads':
        return renderLeadsHelp();
      case 'opportunities':
        return renderOpportunitiesHelp();
      case 'dashboard':
        return renderDashboardHelp();
      case 'calendar':
        return renderCalendarHelp();
      case 'settings':
        return renderSettingsHelp();
      case 'masterdata':
        return renderMasterDataHelp();
      default:
        return renderLeadsHelp();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <HelpCircle className="text-pierian" size={28} />
            <div>
              <h2 className="text-sm font-bold text-gray-900">Help & Documentation</h2>
              <p className="text-sm text-gray-600">Learn how to use Pierag Sales Connect</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b bg-gray-50">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search help topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian"
            />
          </div>
        </div>

        {/* Content Area with Sidebar */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Topics */}
          <div className="w-64 border-r bg-gray-50 overflow-y-auto">
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Topics</h3>
              <div className="space-y-1">
                {topics.map((topic) => {
                  const Icon = topic.icon;
                  return (
                    <button
                      key={topic.id}
                      onClick={() => setSelectedTopic(topic.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        selectedTopic === topic.id
                          ? 'bg-pierian-10 text-scarlet'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon size={18} />
                      <span className="text-sm font-medium">{topic.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// ========================================
// LOGIN PAGE COMPONENT
// ========================================
const LoginPage = ({ onLogin, apiBaseUrl }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSSOLogin = async () => {
    try {
      setLoading(true);

      // Step 1: Clear CRM's stale psc.sid cookie via backend logout
      // This is a no-op if there's no session, but ensures any stale CRM cookie is removed
      try {
        await fetch(`${apiBaseUrl}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        console.log('🔐 Pre-login: CRM cookies cleared');
      } catch (logoutErr) {
        // Non-critical — proceed even if backend is unreachable
        console.warn('⚠️ Pre-login cleanup failed (proceeding):', logoutErr);
      }

      // Step 2: Route through SSO logout first to clear the pierian.sid cookie
      // (set by SSO service at domain .pierianservices.com — only SSO can clear it).
      // SSO logout clears the cookie and redirects to our specified URL,
      // which we set to the SSO login URL — so the user gets a fresh login flow.
      const redirectUrl = window.location.origin + window.location.pathname;
      const ssoLoginUrl = `${SSO_SERVICE_URL}/sso/login?redirect=${encodeURIComponent(redirectUrl)}`;
      const ssoLogoutThenLoginUrl = `${SSO_SERVICE_URL}/sso/logout?redirect=${encodeURIComponent(ssoLoginUrl)}`;

      console.log('🔐 Pre-login: routing through SSO logout to clear stale SSO cookies');
      console.log('🔐 Redirect chain: SSO logout → SSO login → Azure AD → CRM');

      window.location.href = ssoLogoutThenLoginUrl;
    } catch (err) {
      console.error('❌ SSO redirect error:', err);
      setError('Unable to redirect to login. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#0d0e1a', fontFamily: "'Poppins', sans-serif" }}>

      {/* ── Left panel — brand visual ── */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center p-12 relative overflow-hidden">
        {/* Subtle radial glow */}
        <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%,-50%)', width: 480, height: 480, background: 'radial-gradient(circle, rgba(0,187,162,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        {/* Teal accent line top-left */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: 'linear-gradient(to bottom, transparent, #00BBA2, transparent)' }} />

        <div className="relative z-10 text-center max-w-sm">
          <img src={vaultLogoNew} alt="Vault" style={{ height: 100, marginBottom: '2rem', margin: '0 auto 2rem' }} />
          <p style={{ fontSize: '1rem', color: '#00BBA2', fontWeight: 600, marginBottom: '1rem', letterSpacing: '0.01em', textAlign: 'center' }}>Sales &amp; Customer Management Platform</p>
          <p style={{ fontSize: '0.9rem', color: '#D4D4D4', lineHeight: 1.7, textAlign: 'center' }}>
            Built for high-performance sales teams.
          </p>

          {/* Feature bullets */}
          <div style={{ marginTop: '2.5rem', display: 'flex', flexDirection: 'column', gap: '0.875rem', textAlign: 'left' }}>
            {['Pipeline & Opportunity Management', 'AI-Powered Sales Copilot', 'Real-time Analytics & Forecasting', 'Task & Calendar Management'].map((f) => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00BBA2', flexShrink: 0 }} />
                <span style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.55)' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Separator ── */}
      <div className="hidden lg:block" style={{ width: 1, background: 'linear-gradient(to bottom, transparent 5%, rgba(0,187,162,0.35) 30%, rgba(0,187,162,0.35) 70%, transparent 95%)', flexShrink: 0 }} />

      {/* ── Right panel — login card ── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div style={{ width: '100%', maxWidth: 420 }}>

          {/* Mobile-only logo */}
          <div className="flex justify-center mb-8 lg:hidden">
            <img src={vaultLogoNew} alt="Vault" style={{ height: 72 }} />
          </div>

          {/* Card */}
          <div style={{ background: 'transparent', border: '1px solid rgba(0,187,162,0.35)', borderRadius: '1.25rem', padding: '2.5rem' }}>

            <div style={{ marginBottom: '2rem' }}>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#D4D4D4', marginBottom: '0.375rem', textAlign: 'center' }}>
                Welcome back
              </h1>
              <p style={{ fontSize: '0.875rem', color: '#D4D4D4', textAlign: 'center' }}>
                Sign in to your Vault account
              </p>
            </div>

            {/* Error */}
            {error && (
              <div style={{ marginBottom: '1.25rem', padding: '0.75rem 1rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.625rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#fca5a5' }}>
                <AlertCircle size={15} />
                <span style={{ fontSize: '0.8125rem' }}>{error}</span>
              </div>
            )}

            {/* Divider label */}
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.75rem', color: '#D4D4D4', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center' }}>
                Continue with
              </p>

              {/* Microsoft SSO button */}
              <button
                onClick={handleSSOLogin}
                disabled={loading}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  padding: '0.8rem 1.25rem',
                  background: loading ? 'rgba(255,255,255,0.06)' : '#ffffff',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '0.625rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease',
                  opacity: loading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => { if (!loading) (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'; }}
                onMouseLeave={(e) => { if (!loading) (e.currentTarget as HTMLButtonElement).style.background = '#ffffff'; }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>Redirecting...</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 11H0V0H11V11Z" fill="#F25022"/>
                      <path d="M23 11H12V0H23V11Z" fill="#7FBA00"/>
                      <path d="M11 23H0V12H11V23Z" fill="#00A4EF"/>
                      <path d="M23 23H12V12H23V23Z" fill="#FFB900"/>
                    </svg>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827' }}>Sign in with Microsoft</span>
                  </>
                )}
              </button>
            </div>

            {/* Info */}
            <div style={{ marginTop: '1.75rem', paddingTop: '1.25rem', textAlign: 'center' }}>
              <p style={{ fontSize: '0.75rem', color: '#D4D4D4', whiteSpace: 'nowrap' }}>
                Secured by Azure Active Directory · Single Sign-On
              </p>
            </div>
          </div>

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
            <p style={{ fontSize: '0.6875rem', color: '#D4D4D4' }}>
              &copy; 2026 &nbsp;·&nbsp; All rights reserved
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <span style={{ fontSize: '0.6875rem', color: '#D4D4D4' }}>Powered by</span>
              <img src={pieLogoFooter} alt="Pierian" style={{ height: 18 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Field wrappers for FullScreenOpportunityModal — defined at module scope so their
// component identity is stable across the modal's re-renders. Previously these were
// declared inline inside the modal, so every setFormData keystroke re-created them and
// React remounted the wrapped <input>, dropping focus after a single character.
const DisplayField = ({ label, value, className = '' }: { label: string; value: any; className?: string }) => (
  <div className={className}>
    <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
    <div className="text-sm font-medium text-gray-800">{value || '-'}</div>
  </div>
);

const EditField = ({ label, children, className = '' }: { label: string; children: any; className?: string }) => {
  const isRequired = label.endsWith(' *');
  const displayLabel = isRequired ? label.slice(0, -2) : label;
  return (
    <div className={className}>
      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
        {displayLabel}{isRequired && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
};

// Full-Screen Opportunity Detail Modal - Display mode with Edit button, color-coded sections
const FullScreenOpportunityModal = ({
  opp,
  onClose,
  opportunities,
  setOpportunities,
  users,
  geographies,
  serviceLines,
  industries,
  salesStages,
  serviceOfferings,
  entities,
  tags,
  handleMoveToNextStage,
  handleCreateTask,
  currentUser
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    ...opp,
    annualContractValue: opp.annualContractValue || 0,
    totalContractValue: opp.totalContractValue || 0,
    opportunityCategory: opp.opportunityCategory || 'New Business',
    opportunityClassification: opp.opportunityClassification || 'NC, NB',
    tags: opp.tags || []
  });
  const [newComment, setNewComment] = useState('');
  const [newEngagementEntry, setNewEngagementEntry] = useState('');
  const [stageRegressed, setStageRegressed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Closed-Lost modal state
  const [showClosedLostModal, setShowClosedLostModal] = useState(false);
  const [closedLostReason, setClosedLostReason] = useState('');
  const [closedLostComments, setClosedLostComments] = useState('');
  const [closedLostSaving, setClosedLostSaving] = useState(false);

  // Mark Inactive modal state
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [inactiveReason, setInactiveReason] = useState('');
  const [inactiveComments, setInactiveComments] = useState('');
  const [inactiveSaving, setInactiveSaving] = useState(false);

  // Tags editing state
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>(
    opp.tags ? opp.tags.map(t => t.name) : []
  );
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const safeTags = tags || [];

  // Refs for text inputs to prevent cursor jumping (uncontrolled input pattern)
  const opportunityNameRef = React.useRef<HTMLInputElement>(null);
  const accountRef = React.useRef<HTMLInputElement>(null);
  const primaryContactRef = React.useRef<HTMLInputElement>(null);
  const phoneNumberRef = React.useRef<HTMLInputElement>(null);
  const executiveSponsorRef = React.useRef<HTMLInputElement>(null);
  const natureOfWorkRef = React.useRef<HTMLInputElement>(null);
  const tagInputRef = React.useRef<HTMLInputElement>(null);

  // Helper to get current tag input value from ref
  const getTagInputValue = () => tagInputRef.current?.value || '';

  // Sync formData and refs when the opportunity is updated in the parent state (e.g., after save)
  React.useEffect(() => {
    const updatedOpp = opportunities.find(o => o.opportunityId === opp.opportunityId);
    if (updatedOpp && !isEditing) {
      setFormData({
        ...updatedOpp,
        annualContractValue: updatedOpp.annualContractValue || 0,
        totalContractValue: updatedOpp.totalContractValue || 0,
        opportunityCategory: updatedOpp.opportunityCategory || 'New Business',
        opportunityClassification: updatedOpp.opportunityClassification || 'NC, NB',
        tags: updatedOpp.tags || []
      });
      // Also update ref values
      if (opportunityNameRef.current) opportunityNameRef.current.value = updatedOpp.opportunityName || '';
      if (accountRef.current) accountRef.current.value = updatedOpp.associatedAccount || '';
      if (primaryContactRef.current) primaryContactRef.current.value = updatedOpp.primaryContact || '';
      if (phoneNumberRef.current) phoneNumberRef.current.value = updatedOpp.phoneNumber || '';
      if (executiveSponsorRef.current) executiveSponsorRef.current.value = updatedOpp.executiveSponsor || '';
      if (natureOfWorkRef.current) natureOfWorkRef.current.value = updatedOpp.natureOfWork || '';
      // Also sync selectedTagNames with the updated opportunity's tags
      setSelectedTagNames(updatedOpp.tags ? updatedOpp.tags.map(t => t.name) : []);
    }
  }, [opportunities, opp.opportunityId, isEditing]);

  // Get allowed stages for progression - uses master data from salesStages prop
  const getAllowedStagesInline = (currentStage) => {
    // Use salesStages from parent component (passed as prop)
    if (salesStages && salesStages.length > 0) {
      return [...salesStages]
        .filter(s => s.isActive !== false)
        .sort((a, b) => (a.stageOrder || 0) - (b.stageOrder || 0))
        .map(s => s.name);
    }
    // Fallback to valid stages if master data not loaded
    return ['Discovery', 'Proposal', 'Contract', 'Closed-Won', 'Closed-Lost'];
  };

  // Check if stage is regression
  const isStageRegressionInline = (oldStage: string, newStage: string): boolean => {
    const stageOrder = getAllowedStagesInline(oldStage);
    const oldIndex = stageOrder.indexOf(oldStage);
    const newIndex = stageOrder.indexOf(newStage);
    return newIndex < oldIndex && newStage !== 'Closed-Lost' && newStage !== 'Contract';
  };

  const handleSave = async () => {
    // Read values from refs (uncontrolled inputs) for text fields
    const oppName = opportunityNameRef.current?.value?.trim() || '';
    const account = accountRef.current?.value?.trim() || '';
    const primaryContact = primaryContactRef.current?.value?.trim() || '';
    const phoneNumber = phoneNumberRef.current?.value?.trim() || '';
    const executiveSponsor = executiveSponsorRef.current?.value?.trim() || '';
    const natureOfWork = natureOfWorkRef.current?.value?.trim() || '';

    if (!oppName || !account) {
      alert('Please fill in required fields (Name and Account)');
      return;
    }

    setIsSaving(true);

    try {
      const now = new Date();
      const isoTimestamp = now.toISOString();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = String(now.getDate()).padStart(2, '0');
      const month = monthNames[now.getMonth()];
      const year = String(now.getFullYear()).slice(-2);
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const displayTimestamp = `${day}${month}${year} ${hours}:${minutes}`;

      let updatedEngagementHistory = formData.engagementHistory || '';
      let updatedComments = formData.comments || '';

      if (newEngagementEntry.trim()) {
        updatedEngagementHistory = prependEngagementEntry(updatedEngagementHistory, newEngagementEntry, currentUser?.name);
      }

      if (newComment.trim()) {
        const commentEntry = `[${displayTimestamp}] ${newComment}`;
        updatedComments = commentEntry + '\n' + updatedComments;
      }

      const oppToSave = {
        ...formData,
        // Override with values from refs (uncontrolled inputs)
        opportunityName: oppName,
        associatedAccount: account,
        primaryContact: primaryContact,
        phoneNumber: phoneNumber,
        executiveSponsor: executiveSponsor,
        natureOfWork: natureOfWork,
        engagementHistory: updatedEngagementHistory,
        comments: updatedComments,
        latestComment: newComment.trim() || formData.latestComment,
        lastEditedDate: now.toISOString().split('T')[0]
      };

      // Sanitize numeric fields to ensure valid numbers
      const safeNum = (v: any): number => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
      const safeDate = (v: any): string | null => {
        if (!v) return null;
        const s = String(v);
        if (s === 'Invalid Date' || s === 'undefined' || s === 'null') return null;
        return s;
      };
      const safeProbOverride = (v: any): number | null => {
        if (v === null || v === undefined || v === '') return null;
        const n = parseInt(String(v), 10);
        return isNaN(n) ? null : n;
      };

      const payload = {
        opportunity_name: oppToSave.opportunityName,
        associated_account: oppToSave.associatedAccount,
        sales_stage: oppToSave.salesStage,
        probability: String(oppToSave.probability || '50%'),
        probability_override: safeProbOverride(oppToSave.probabilityOverride),
        estimated_revenue: safeNum(oppToSave.estimatedRevenue),
        annual_contract_value: safeNum(oppToSave.annualContractValue),
        total_contract_value: safeNum(oppToSave.totalContractValue),
        service_line: oppToSave.serviceLine || null,
        service_offering: oppToSave.serviceOffering || null,
        entity: oppToSave.entity || null,
        geography: oppToSave.geography || null,
        industry: oppToSave.industryVertical || oppToSave.industry || null,
        opportunity_type: oppToSave.opportunityType || null,
        opportunity_category: oppToSave.opportunityCategory || 'New Business',
        opportunity_classification: oppToSave.opportunityClassification || null,
        nature_of_work: oppToSave.natureOfWork || null,
        priority: oppToSave.priorityLevel || null,
        status: oppToSave.salesStatus || 'Warm',
        sales_status_override: oppToSave.salesStatusOverride || null,
        account_owner: oppToSave.accountOwner || null,
        lead_supporter: oppToSave.leadSupporter || null,
        solution_architect: oppToSave.solutionArchitect || null,
        executive_sponsor: oppToSave.executiveSponsor || null,
        primary_contact: oppToSave.primaryContact || null,
        primary_contact_email: oppToSave.primaryContactEmail || null,
        phone_number: oppToSave.phoneNumber || null,
        initiation_date: safeDate(oppToSave.initiationDate),
        expected_close_date: safeDate(oppToSave.estimatedCloseDate),
        solution_scope: oppToSave.solutionScope || null,
        engagement_history: oppToSave.engagementHistory || null,
        comments: oppToSave.comments || null,
        latest_comment: oppToSave.latestComment || null,
        actual_close_date: safeDate(oppToSave.actualCloseDate),
        win_loss_reason: oppToSave.winLossReason || null,
        last_edited_date: safeDate(oppToSave.lastEditedDate) || now.toISOString().split('T')[0],
        marked_for_review: oppToSave.markedForReview || false,
        lead_history: oppToSave.leadHistory || null,
        source_lead_id: oppToSave.sourceLeadId || null,
        city: oppToSave.city || null,
        lead_generator: oppToSave.leadGenerator || null,
        // Include tags in the main save payload
        tag_names: selectedTagNames
      };

      console.log('🏷️ Saving opportunity with tags:', selectedTagNames);

      // Remove tag_names from payload - we'll save tags separately
      const { tag_names, ...payloadWithoutTags } = payload;

      const response = await fetch(`${API_BASE_URL}/opportunities/${opp.opportunityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payloadWithoutTags)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || responseData.message || 'Failed to save opportunity');
      }

      console.log('✅ Opportunity save response:', responseData);

      // Save tags separately using the dedicated tag endpoint (same as OpportunityFormModal)
      // This endpoint properly creates new tags if they don't exist
      let savedTagObjects = [];
      try {
        const tagSaveResponse = await fetch(`${API_BASE_URL}/master/opportunity-tags-by-name/${opp.opportunityId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tag_names: selectedTagNames })
        });
        const tagSaveResult = await tagSaveResponse.json();
        if (tagSaveResult.success && tagSaveResult.data) {
          savedTagObjects = tagSaveResult.data;
          console.log('✅ Tags saved successfully:', savedTagObjects);
        } else if (!tagSaveResponse.ok) {
          console.error('⚠️ Tag save failed:', tagSaveResult);
          alert(`Opportunity saved, but tags failed to save: ${tagSaveResult.message || 'Unknown error'}`);
        }
      } catch (tagError) {
        console.error('⚠️ Tag save error:', tagError);
        alert(`Opportunity saved, but tags failed to save: ${tagError.message}`);
      }

      // Use saved tag objects if available, otherwise build from selectedTagNames
      const selectedTagObjects = savedTagObjects.length > 0 ? savedTagObjects : selectedTagNames.map(name => {
        const existingTag = safeTags.find(t => t.name.toLowerCase() === name.toLowerCase());
        return existingTag || { id: -1, name, color: '#6A7CA5' };
      });

      const oppWithTags = { ...oppToSave, tags: selectedTagObjects };
      setOpportunities(prev => prev.map(o => o.opportunityId === opp.opportunityId ? oppWithTags : o));
      // Update formData with the saved tags so view mode shows them correctly
      setFormData(prev => ({ ...prev, tags: selectedTagObjects }));
      setNewComment('');
      setNewEngagementEntry('');
      setIsEditing(false);
      alert('Opportunity updated successfully!');
    } catch (error) {
      console.error('Error saving opportunity:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Get the current opportunity from parent state (may have been updated by previous save)
    const currentOpp = opportunities.find(o => o.opportunityId === opp.opportunityId) || opp;
    setFormData({
      ...currentOpp,
      annualContractValue: currentOpp.annualContractValue || 0,
      totalContractValue: currentOpp.totalContractValue || 0,
      opportunityCategory: currentOpp.opportunityCategory || 'New Business',
      opportunityClassification: currentOpp.opportunityClassification || 'NC, NB',
      tags: currentOpp.tags || []
    });
    // Reset tag selection to current saved state
    setSelectedTagNames(currentOpp.tags ? currentOpp.tags.map(t => t.name) : []);
    setNewComment('');
    setNewEngagementEntry('');
    setIsEditing(false);
  };

  // DisplayField / EditField are defined at module scope (above this component)
  // so they keep a stable identity across re-renders — defining them inline here
  // caused wrapped <input> children to remount on every keystroke (focus loss).

  const inputClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00BBA2] focus:border-[#00BBA2]";
  const selectClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00BBA2] focus:border-[#00BBA2]";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{formData.opportunityId}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  formData.salesStage === 'Closed-Won' ? 'bg-green-100 text-green-700' :
                  formData.salesStage === 'Closed-Lost' ? 'bg-red-100 text-red-700' :
                  formData.salesStage === 'Contract' ? 'bg-emerald-100 text-emerald-700' :
                  'bg-[rgba(0,187,162,0.12)] text-[#006B5E]'
                }`}>{formData.salesStage}</span>
              </div>
              <h2 className="text-sm font-bold text-gray-900 mt-1">{formData.opportunityName}</h2>
              <p className="text-sm text-gray-600">{formData.associatedAccount}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} className="px-4 py-2 text-sm font-medium rounded-lg bg-[#00BBA2] text-white hover:bg-[#009E8A] flex items-center gap-2 shadow-sm">
                <Edit3 size={16} /> Edit
              </button>
            ) : (
              <>
                <button onClick={handleCancel} className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 text-sm font-medium rounded-lg text-white hover:opacity-90 disabled:opacity-50 shadow-sm" style={{backgroundColor: '#00BBA2'}}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Quick Actions Bar at Top */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mr-1">
                <Zap size={13} /> Quick Actions:
              </span>
              {(() => {
                // Calculate next stage
                const stageOrder = ['Qualified', 'Discovery', 'Proposal', 'Contract', 'Closed-Won', 'Closed-Lost'];
                const currentStage = formData.salesStage || 'Qualified';
                const currentIndex = stageOrder.indexOf(currentStage);
                let nextIndex = currentIndex + 1;
                if (nextIndex >= stageOrder.length) nextIndex = stageOrder.length - 1;
                const nextStage = stageOrder[nextIndex];
                const isAtEnd = currentStage === 'Closed-Won' || currentStage === 'Closed-Lost';

                return (
                  <button
                    onClick={() => { handleMoveToNextStage(opp.opportunityId, opp); onClose(); }}
                    disabled={isAtEnd}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1.5 transition-colors whitespace-nowrap ${isAtEnd ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'}`}
                  >
                    <ArrowRight size={13} /> Next Stage {!isAtEnd && `(${nextStage})`}
                  </button>
                );
              })()}
              <button
                onClick={async () => {
                  const newReviewStatus = !formData.markedForReview;
                  const now = new Date();
                  const updatedEngagementHistory = prependEngagementEntry(
                    formData.engagementHistory,
                    newReviewStatus ? 'Marked for Review' : 'Unmarked for Review',
                    currentUser?.name,
                    now
                  );

                  const updatedOpp = {
                    ...opp,
                    markedForReview: newReviewStatus,
                    engagementHistory: updatedEngagementHistory,
                    lastEditedDate: now.toISOString().split('T')[0]
                  };
                  // Update local state immediately for responsive UI
                  setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? updatedOpp : o));
                  setFormData(prev => ({
                    ...prev,
                    markedForReview: newReviewStatus,
                    engagementHistory: updatedEngagementHistory,
                    lastEditedDate: now.toISOString().split('T')[0]
                  }));
                  // Save to database using PUT endpoint
                  try {
                    console.log('🚀 Saving review status from modal via PUT:', { id: opp.opportunityId, marked_for_review: newReviewStatus });
                    const response = await fetch(`${API_BASE_URL}/opportunities/${opp.opportunityId}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        opportunity_name: opp.opportunityName,
                        associated_account: opp.associatedAccount,
                        sales_stage: opp.salesStage,
                        probability: String(opp.probability || '50%'),
                        probability_override: opp.probabilityOverride,
                        estimated_revenue: opp.estimatedRevenue,
                        annual_contract_value: opp.annualContractValue,
                        total_contract_value: opp.totalContractValue,
                        service_line: opp.serviceLine,
                        service_offering: opp.serviceOffering,
                        entity: opp.entity,
                        geography: opp.geography,
                        industry: opp.industryVertical || opp.industry,
                        opportunity_type: opp.opportunityType,
                        opportunity_category: opp.opportunityCategory || 'New Business',
                        opportunity_classification: opp.opportunityClassification || null,
                        nature_of_work: opp.natureOfWork || null,
                        priority: opp.priorityLevel,
                        status: opp.salesStatus || 'Warm',
                        sales_status_override: opp.salesStatusOverride || null,
                        account_owner: opp.accountOwner,
                        lead_supporter: opp.leadSupporter,
                        solution_architect: opp.solutionArchitect || null,
                        executive_sponsor: opp.executiveSponsor,
                        primary_contact: opp.primaryContact,
                        primary_contact_email: opp.primaryContactEmail,
                        phone_number: opp.phoneNumber || null,
                        initiation_date: opp.initiationDate,
                        expected_close_date: opp.estimatedCloseDate || null,
                        solution_scope: opp.solutionScope || null,
                        engagement_history: updatedEngagementHistory,
                        comments: opp.comments || null,
                        latest_comment: opp.latestComment || null,
                        last_edited_date: now.toISOString().split('T')[0],
                        marked_for_review: newReviewStatus ? 1 : 0
                      })
                    });
                    const responseData = await response.json();
                    console.log('📥 PUT Response:', responseData);
                    if (!response.ok || !responseData.success) {
                      console.error('Failed to save review status:', response.status, responseData);
                      alert(`Failed to save: ${responseData.message || response.statusText}`);
                      // Revert local state on failure
                      setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? opp : o));
                      setFormData(prev => ({ ...prev, markedForReview: !newReviewStatus, engagementHistory: opp.engagementHistory }));
                    } else {
                      console.log('✅ Review status saved. DB marked_for_review:', responseData.data?.marked_for_review);
                    }
                  } catch (error) {
                    console.error('Error saving review status:', error);
                    alert(`Network error: ${error.message}`);
                    // Revert local state on failure
                    setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? opp : o));
                    setFormData(prev => ({ ...prev, markedForReview: !newReviewStatus, engagementHistory: opp.engagementHistory }));
                  }
                }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                  formData.markedForReview
                    ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Flag size={13} className={formData.markedForReview ? 'fill-amber-500' : ''} />
                {formData.markedForReview ? 'Unmark Review' : 'Mark for Review'}
              </button>
              {/* Mark Inactive */}
              {getEffectiveSalesStatus(formData) !== 'Inactive' && (
                <button
                  onClick={() => { setInactiveReason(''); setInactiveComments(''); setShowInactiveModal(true); }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1.5 transition-colors whitespace-nowrap bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                >
                  <PauseCircle size={13} /> Mark Inactive
                </button>
              )}
              {/* Closed-Lost */}
              {formData.salesStage !== 'Closed-Lost' && formData.salesStage !== 'Closed-Won' && (
                <button
                  onClick={() => { setClosedLostReason(''); setClosedLostComments(''); setShowClosedLostModal(true); }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1.5 transition-colors whitespace-nowrap bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                >
                  <XCircle size={13} /> Closed-Lost
                </button>
              )}
              {/* Divider */}
              <div className="w-px h-5 bg-gray-200 mx-0.5"></div>
              {/* Delete Opportunity — Admin only */}
              {currentUser?.isAdmin && (
                <button
                  onClick={async () => {
                    if (window.confirm(`Are you sure you want to delete opportunity "${opp.opportunityName}"? This action can be undone by an administrator.`)) {
                      try {
                        const response = await fetch(`${API_BASE_URL}/opportunities/${opp.opportunityId}`, {
                          method: 'DELETE'
                        });
                        if (!response.ok) {
                          throw new Error('Failed to delete opportunity');
                        }
                        setOpportunities(opportunities.filter(o => o.opportunityId !== opp.opportunityId));
                        onClose();
                        alert('Opportunity deleted successfully!');
                      } catch (error) {
                        console.error('Error deleting opportunity:', error);
                        alert('Failed to delete opportunity. Please try again.');
                      }
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1.5 transition-colors whitespace-nowrap bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                >
                  <Trash2 size={13} /> Delete
                </button>
              )}
            </div>
            <div className="flex-1 flex items-center gap-2">
              <input
                type="text"
                value={newEngagementEntry}
                onChange={(e) => setNewEngagementEntry(e.target.value)}
                placeholder="Add engagement update (e.g., '15-Jan-2026 Meeting scheduled with client...')"
                className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-[#00BBA2] focus:border-[#00BBA2]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newEngagementEntry.trim()) {
                    const updatedHistory = prependEngagementEntry(formData.engagementHistory, newEngagementEntry, currentUser?.name);
                    setFormData({...formData, engagementHistory: updatedHistory});
                    setNewEngagementEntry('');
                    setIsEditing(true);
                  }
                }}
              />
              <button
                onClick={() => {
                  if (newEngagementEntry.trim()) {
                    const updatedHistory = prependEngagementEntry(formData.engagementHistory, newEngagementEntry, currentUser?.name);
                    setFormData({...formData, engagementHistory: updatedHistory});
                    setNewEngagementEntry('');
                    setIsEditing(true);
                  }
                }}
                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-[#00BBA2] text-white hover:bg-cyan-700 flex items-center gap-1.5 transition-colors whitespace-nowrap"
              >
                <Plus size={13} /> Add
              </button>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-4">

            {/* LEFT COLUMN (50%): Basic Info + Financial & Stage */}
            <div className="space-y-4">
              {/* Combined: Basic Information + Ownership + Service Details */}
              <div className="bg-white border border-gray-100 rounded-lg p-4">
                <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Building2 size={16} /> Basic Information, Ownership & Service Details
                </div>
                {!isEditing ? (
                  <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                    <DisplayField label="Opportunity Name" value={formData.opportunityName} />
                    <DisplayField label="Account" value={formData.associatedAccount} />
                    <DisplayField label="Geography" value={formData.geography || 'Domestic'} />
                    <DisplayField label="Industry" value={formData.industryVertical || formData.industry} />
                    <DisplayField label="Category" value={formData.opportunityCategory} />
                    <DisplayField label="Classification" value={formData.opportunityClassification} />
                    <DisplayField label="Nature of Work" value={formData.natureOfWork} />
                    <DisplayField label="Primary Contact" value={formData.primaryContact} />
                    <DisplayField label="Phone Number" value={formData.phoneNumber} />
                    <DisplayField label="Account Owner" value={formData.accountOwner} />
                    <DisplayField label="Lead Supporter" value={formData.leadSupporter} />
                    <DisplayField label="Solution Architect" value={formData.solutionArchitect} />
                    <DisplayField label="Executive Sponsor" value={formData.executiveSponsor} />
                    <DisplayField label="Service Line" value={formData.serviceLine} />
                    <DisplayField label="Service Offering" value={formData.serviceOffering} />
                    <DisplayField label="Entity" value={formData.entity} />
                    <DisplayField label="City" value={formData.city} />
                    <DisplayField label="Lead Generator" value={formData.leadGenerator} />
                    <div className="col-span-3">
                      <DisplayField label="Tags" value={
                        formData.tags && formData.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {formData.tags.map(tag => (
                              <span
                                key={tag.id}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        ) : '-'
                      } />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                    <EditField label="Opportunity Name *">
                      <input type="text" ref={opportunityNameRef} defaultValue={formData.opportunityName || ''} className={inputClass} />
                    </EditField>
                    <EditField label="Account *">
                      <input type="text" ref={accountRef} defaultValue={formData.associatedAccount || ''} className={inputClass} />
                    </EditField>
                    <EditField label="Geography *">
                      <select value={formData.geography || ''} onChange={(e) => setFormData({...formData, geography: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {geographies?.filter(g => g.isActive).map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Industry *">
                      <select value={formData.industryVertical || formData.industry || ''} onChange={(e) => setFormData({...formData, industryVertical: e.target.value, industry: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {industries?.filter(i => i.isActive).map(i => <option key={i.id} value={i.name}>{i.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Category *">
                      <select value={formData.opportunityCategory || 'New Business'} onChange={(e) => setFormData({...formData, opportunityCategory: e.target.value, opportunityClassification: e.target.value === 'Renewals' ? 'EC, EB' : (formData.opportunityClassification || 'NC, NB')})} className={selectClass}>
                        <option value="New Business">New Business</option>
                        <option value="Renewals">Renewals</option>
                      </select>
                    </EditField>
                    <EditField label="Classification">
                      <select value={formData.opportunityClassification || 'NC, NB'} onChange={(e) => setFormData({...formData, opportunityClassification: e.target.value})} disabled={formData.opportunityCategory === 'Renewals'} className={`${selectClass} disabled:bg-gray-100`}>
                        <option value="EC, EB">EC, EB</option>
                        <option value="EC, NB">EC, NB</option>
                        <option value="NC, NB">NC, NB</option>
                      </select>
                    </EditField>
                    <EditField label="Nature of Work">
                      <input type="text" ref={natureOfWorkRef} defaultValue={formData.natureOfWork || ''} className={inputClass} placeholder="Enter nature of work" />
                    </EditField>
                    <EditField label="Primary Contact">
                      <input type="text" ref={primaryContactRef} defaultValue={formData.primaryContact || ''} className={inputClass} />
                    </EditField>
                    <EditField label="Phone Number">
                      <input type="tel" ref={phoneNumberRef} defaultValue={formData.phoneNumber || ''} className={inputClass} placeholder="+1-555-0101" />
                    </EditField>
                    <EditField label="Account Owner *">
                      <select value={formData.accountOwner || ''} onChange={(e) => setFormData({...formData, accountOwner: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {users?.filter(u => u.status === 'Active').map(u => <option key={u.userId} value={u.name}>{u.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Lead Supporter">
                      <input type="text" value={formData.leadSupporter || ''} onChange={(e) => setFormData({...formData, leadSupporter: e.target.value})} className={inputClass} placeholder="Enter lead supporter name" />
                    </EditField>
                    <EditField label="Solution Architect">
                      <select value={formData.solutionArchitect || ''} onChange={(e) => setFormData({...formData, solutionArchitect: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {users?.filter(u => u.status === 'Active').map(u => <option key={u.userId} value={u.name}>{u.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Executive Sponsor">
                      <input type="text" ref={executiveSponsorRef} defaultValue={formData.executiveSponsor || ''} className={inputClass} />
                    </EditField>
                    <EditField label="Service Line *">
                      <select value={formData.serviceLine || ''} onChange={(e) => setFormData({...formData, serviceLine: e.target.value, serviceOffering: ''})} className={selectClass}>
                        <option value="">Select</option>
                        {serviceLines?.filter(s => s.isActive).map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Service Offering">
                      <select value={formData.serviceOffering || ''} onChange={(e) => setFormData({...formData, serviceOffering: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {serviceOfferings?.filter(s => s.isActive && (!formData.serviceLine || s.serviceLine === formData.serviceLine)).map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="Entity *">
                      <select value={formData.entity || ''} onChange={(e) => setFormData({...formData, entity: e.target.value})} className={selectClass}>
                        <option value="">Select</option>
                        {entities?.filter(e => e.isActive).map(ent => <option key={ent.id} value={ent.name}>{ent.name}</option>)}
                      </select>
                    </EditField>
                    <EditField label="City">
                      <input type="text" value={formData.city || ''} onChange={(e) => setFormData({...formData, city: e.target.value})} className={inputClass} placeholder="Enter city" />
                    </EditField>
                    <EditField label="Lead Generator">
                      <input type="text" value={formData.leadGenerator || ''} onChange={(e) => setFormData({...formData, leadGenerator: e.target.value})} className={inputClass} placeholder="Enter lead generator" />
                    </EditField>
                    <EditField label="Tags">
                      <div className="relative">
                        <div className={`${inputClass} min-h-[38px] flex flex-wrap gap-1 items-center p-1`}>
                          {selectedTagNames.map((tagName, idx) => {
                            const existingTag = safeTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
                            const tagColor = existingTag?.color || '#6A7CA5';
                            return (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                style={{ backgroundColor: `${tagColor}20`, color: tagColor }}
                              >
                                {tagName}
                                <button
                                  type="button"
                                  onClick={() => setSelectedTagNames(selectedTagNames.filter((_, i) => i !== idx))}
                                  className="ml-1 hover:opacity-70"
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            );
                          })}
                          <input
                            type="text"
                            ref={tagInputRef}
                            defaultValue=""
                            onFocus={() => setShowTagDropdown(true)}
                            onBlur={(e) => {
                              // Delay hiding dropdown to allow click events on dropdown items
                              setTimeout(() => setShowTagDropdown(false), 200);
                            }}
                            onKeyDown={(e) => {
                              const inputValue = tagInputRef.current?.value || '';
                              if (e.key === 'Enter' && inputValue.trim()) {
                                e.preventDefault();
                                const newTag = inputValue.trim();
                                if (!selectedTagNames.some(t => t.toLowerCase() === newTag.toLowerCase())) {
                                  setSelectedTagNames([...selectedTagNames, newTag]);
                                }
                                if (tagInputRef.current) tagInputRef.current.value = '';
                                setShowTagDropdown(false);
                              } else if (e.key === 'Backspace' && !inputValue && selectedTagNames.length > 0) {
                                setSelectedTagNames(selectedTagNames.slice(0, -1));
                              } else if (e.key === 'Escape') {
                                setShowTagDropdown(false);
                              }
                            }}
                            placeholder={selectedTagNames.length === 0 ? "Type to add tags..." : ""}
                            className="flex-1 min-w-[100px] outline-none text-sm bg-transparent"
                          />
                        </div>
                        {showTagDropdown && (
                          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                            {safeTags.filter(t => t.isActive &&
                              !selectedTagNames.some(st => st.toLowerCase() === t.name.toLowerCase())
                            ).map(tag => (
                              <div
                                key={tag.id}
                                onMouseDown={(e) => {
                                  e.preventDefault(); // Prevent blur
                                  setSelectedTagNames([...selectedTagNames, tag.name]);
                                  if (tagInputRef.current) tagInputRef.current.value = '';
                                  setShowTagDropdown(false);
                                }}
                                className="px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2"
                              >
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                                <span className="flex-1 text-sm">{tag.name}</span>
                              </div>
                            ))}
                            <div className="px-3 py-2 text-xs text-gray-500 border-t">
                              Type and press Enter to add a custom tag
                            </div>
                          </div>
                        )}
                      </div>
                    </EditField>
                  </div>
                )}
              </div>

              {/* Financial & Sales Stage */}
              <div className="bg-white border border-gray-100 rounded-lg p-4">
                <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                  <IndianRupee size={16} /> Financial & Stage
                </div>
                {!isEditing ? (
                  <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                    <DisplayField label="ACV" value={formData.annualContractValue ? `₹${Number(formData.annualContractValue).toLocaleString()}` : '-'} />
                    <DisplayField label="TCV" value={formData.totalContractValue ? `₹${Number(formData.totalContractValue).toLocaleString()}` : '-'} />
                    <DisplayField label="Stage" value={formData.salesStage} />
                    <DisplayField label={formData.probabilityOverride !== null && formData.probabilityOverride !== undefined ? "Probability *" : "Probability"} value={formData.probabilityOverride !== null && formData.probabilityOverride !== undefined ? `${formData.probabilityOverride}%` : formData.probability} />
                    <DisplayField label="Est. Close Date" value={formData.estimatedCloseDate ? (() => {
                      const d = new Date(formData.estimatedCloseDate);
                      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      return `${d.getDate().toString().padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear().toString().slice(-2)}`;
                    })() : '-'} />
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                    <EditField label="ACV (₹)">
                      <input type="number" value={formData.annualContractValue} onChange={(e) => setFormData({...formData, annualContractValue: e.target.value})} className={inputClass} />
                    </EditField>
                    <EditField label="TCV (₹)">
                      <input type="number" value={formData.totalContractValue} onChange={(e) => setFormData({...formData, totalContractValue: e.target.value})} className={inputClass} />
                    </EditField>
                    <EditField label="Stage">
                      <select
                        value={formData.salesStage || ''}
                        onChange={(e) => {
                          const newStage = e.target.value;
                          const stageProbInfo = calculateProbabilityByStage(newStage);
                          const isRegression = isStageRegressionInline(opp.salesStage, newStage);
                          setStageRegressed(isRegression);
                          if (formData.probabilityOverride === null || formData.probabilityOverride === undefined) {
                            setFormData({...formData, salesStage: newStage, probability: `${stageProbInfo.percent}%`, probabilityPercent: `${stageProbInfo.percent}%`, probabilityToClose: stageProbInfo.category, forecastMultiplier: stageProbInfo.percent / 100});
                          } else {
                            setFormData({...formData, salesStage: newStage});
                          }
                        }}
                        className={`${selectClass} ${stageRegressed ? 'border-amber-400 bg-amber-100' : ''}`}
                      >
                        {getAllowedStagesInline(opp.salesStage).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </EditField>
                    <EditField label={formData.probabilityOverride !== null && formData.probabilityOverride !== undefined ? "Probability *" : "Probability"}>
                      <select
                        value={formData.probabilityOverride !== null && formData.probabilityOverride !== undefined ? `${formData.probabilityOverride}%` : (formData.probability || `${calculateProbabilityByStage(formData.salesStage).percent}%`)}
                        onChange={(e) => {
                          const newProbStr = e.target.value;
                          const newProb = parseInt(newProbStr.replace('%', ''), 10);
                          const stageProbInfo = calculateProbabilityByStage(formData.salesStage);
                          const isOverriding = newProb !== stageProbInfo.percent;
                          const oldProb = formData.probabilityOverride !== null && formData.probabilityOverride !== undefined
                            ? formData.probabilityOverride
                            : parseInt((formData.probability || `${stageProbInfo.percent}%`).replace('%', ''), 10);

                          // Add engagement history entry if probability changed
                          let updatedHistory = formData.engagementHistory || '';
                          if (newProb !== oldProb) {
                            updatedHistory = prependEngagementEntry(
                              updatedHistory,
                              `Probability ${isOverriding ? 'manually overridden' : 'reset'} from ${oldProb}% to ${newProb}%`,
                              currentUser?.name
                            );
                          }

                          setFormData({...formData, probability: newProbStr, probabilityPercent: newProbStr, probabilityOverride: isOverriding ? newProb : null, forecastMultiplier: newProb / 100, engagementHistory: updatedHistory});
                        }}
                        className={selectClass}
                      >
                        <option value="10%">10%</option>
                        <option value="20%">20%</option>
                        <option value="30%">30%</option>
                        <option value="40%">40%</option>
                        <option value="50%">50%</option>
                        <option value="60%">60%</option>
                        <option value="75%">75%</option>
                        <option value="90%">90%</option>
                        <option value="100%">100%</option>
                      </select>
                    </EditField>
                    <EditField label="Est. Close Date">
                      <input type="date" value={formData.estimatedCloseDate || ''} onChange={(e) => setFormData({...formData, estimatedCloseDate: e.target.value})} className={inputClass} />
                    </EditField>
                  </div>
                )}
              </div>

              {/* Solution Scope */}
              <div className="bg-white border border-gray-100 rounded-lg p-4">
                <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-2">
                  <Target size={16} /> Solution Scope
                </div>
                {!isEditing ? (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">{formData.solutionScope || 'No scope defined'}</div>
                ) : (
                  <textarea
                    value={formData.solutionScope || ''}
                    onChange={(e) => setFormData({...formData, solutionScope: e.target.value})}
                    rows={3}
                    placeholder="Describe the solution scope..."
                    className="w-full px-3 py-2 text-sm border border-teal-200 rounded-lg resize-none focus:ring-2 focus:ring-[#00BBA2]"
                  />
                )}
              </div>
            </div>

            {/* RIGHT COLUMN (50%): Engagement History + Lead History + Comments */}
            <div className="space-y-4">
              <div className="bg-white border border-gray-100 rounded-lg p-4">
                <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                  <FileText size={16} /> Engagement History
                </div>
                <div className="bg-white border border-cyan-100 rounded-lg p-3 overflow-y-auto text-sm" style={{maxHeight: 'calc(100vh - 450px)'}}>
                  <TimelineDisplay entries={formData.engagementHistory} emptyMessage="No engagement history yet" />
                </div>
              </div>

              {/* Lead History - shown only for opportunities converted from leads */}
              {formData.leadHistory && formData.leadHistory.trim() && (
                <div className="bg-white border border-gray-100 rounded-lg p-4">
                  <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-3 flex items-center gap-2">
                    <FileText size={16} /> Lead History {formData.sourceLeadId && <span className="text-xs font-normal normal-case text-amber-500">({formData.sourceLeadId})</span>}
                  </div>
                  <div className="bg-white border border-amber-100 rounded-lg p-3 max-h-40 overflow-y-auto text-sm">
                    <TimelineDisplay entries={formData.leadHistory} emptyMessage="No lead history" />
                  </div>
                </div>
              )}

              {/* Comments - Below Engagement History */}
              <div className="bg-white border border-gray-100 rounded-lg p-4">
                <div className="text-xs font-semibold text-[#00BBA2] uppercase tracking-widest mb-2 flex items-center gap-2">
                  <MessageSquare size={16} /> Comments
                </div>
                <div className="bg-white border border-rose-100 rounded-lg p-3 max-h-32 overflow-y-auto text-sm mb-2">
                  <TimelineDisplay entries={formData.comments} emptyMessage="No comments yet" />
                </div>
                {isEditing && (
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={2}
                    placeholder="Add a comment..."
                    className="w-full px-3 py-2 text-sm border border-rose-200 rounded-lg resize-none focus:ring-2 focus:ring-[#00BBA2]"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with timestamps */}
        <div className="px-6 py-2 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <div className="text-xs text-gray-500">
              Created: {formData.initiationDate ? new Date(formData.initiationDate).toLocaleDateString() : '-'} |
              Last Updated: {formData.lastEditedDate ? new Date(formData.lastEditedDate).toLocaleDateString() : '-'}
            </div>
          </div>
        </div>
      </div>

      {/* Mark Inactive Modal */}
      {showInactiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[60] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowInactiveModal(false); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Mark as Inactive</h3>
            <p className="text-sm text-gray-500 mb-4">"{opp.opportunityName}" will be marked as Inactive.</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <select value={inactiveReason} onChange={(e) => setInactiveReason(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian mb-4">
              <option value="">Select a reason...</option>
              <option value="No Response from Client">No Response from Client</option>
              <option value="Client Delayed Decision">Client Delayed Decision</option>
              <option value="Budget Freeze">Budget Freeze</option>
              <option value="Internal Reprioritization">Internal Reprioritization</option>
              <option value="Awaiting Client Feedback">Awaiting Client Feedback</option>
              <option value="Resource Constraints">Resource Constraints</option>
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">Comments <span className="text-gray-400">(optional)</span></label>
            <textarea value={inactiveComments} onChange={(e) => setInactiveComments(e.target.value)}
              placeholder="Additional details..." rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian mb-4 resize-none" />

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowInactiveModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button disabled={inactiveSaving || !inactiveReason} onClick={async () => {
                if (!window.confirm(`Are you sure you want to mark "${opp.opportunityName}" as Inactive?`)) return;
                setInactiveSaving(true);
                const now = new Date();
                const fullReason = inactiveComments ? `${inactiveReason} — ${inactiveComments}` : inactiveReason;
                const updatedHistory = prependEngagementEntry(formData.engagementHistory, `Marked as Inactive — ${fullReason}`, currentUser?.name, now);
                const updatedOpp = { ...opp, salesStatusOverride: 'Inactive', salesStatus: 'Inactive', engagementHistory: updatedHistory, lastEditedDate: now.toISOString().split('T')[0] };
                setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? updatedOpp : o));
                setFormData(prev => ({ ...prev, salesStatusOverride: 'Inactive', salesStatus: 'Inactive', engagementHistory: updatedHistory, lastEditedDate: now.toISOString().split('T')[0] }));
                try {
                  const response = await fetch(`${API_BASE_URL}/opportunities/${opp.opportunityId}`, {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      opportunity_name: opp.opportunityName, associated_account: opp.associatedAccount, sales_stage: opp.salesStage,
                      probability: String(opp.probability || '50%'), probability_override: opp.probabilityOverride,
                      estimated_revenue: opp.estimatedRevenue, annual_contract_value: opp.annualContractValue, total_contract_value: opp.totalContractValue,
                      service_line: opp.serviceLine, service_offering: opp.serviceOffering, entity: opp.entity,
                      geography: opp.geography, industry: opp.industryVertical || opp.industry, opportunity_type: opp.opportunityType,
                      opportunity_category: opp.opportunityCategory || 'New Business', opportunity_classification: opp.opportunityClassification || null, nature_of_work: opp.natureOfWork || null,
                      priority: opp.priorityLevel, status: 'Inactive', sales_status_override: 'Inactive',
                      account_owner: opp.accountOwner, lead_supporter: opp.leadSupporter, solution_architect: opp.solutionArchitect || null, executive_sponsor: opp.executiveSponsor,
                      primary_contact: opp.primaryContact, primary_contact_email: opp.primaryContactEmail, phone_number: opp.phoneNumber || null,
                      initiation_date: opp.initiationDate, expected_close_date: opp.estimatedCloseDate || null,
                      solution_scope: opp.solutionScope || null, engagement_history: updatedHistory,
                      win_loss_reason: fullReason,
                      comments: opp.comments || null, latest_comment: opp.latestComment || null,
                      last_edited_date: now.toISOString().split('T')[0], marked_for_review: opp.markedForReview ? 1 : 0
                    })
                  });
                  const responseData = await response.json();
                  if (!response.ok || !responseData.success) {
                    alert(`Failed to save: ${responseData.message || response.statusText}`);
                    setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? opp : o));
                    setFormData(prev => ({ ...prev, salesStatusOverride: opp.salesStatusOverride, salesStatus: opp.salesStatus, engagementHistory: opp.engagementHistory }));
                  } else { setShowInactiveModal(false); }
                } catch (error) {
                  alert(`Network error: ${error.message}`);
                  setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? opp : o));
                  setFormData(prev => ({ ...prev, salesStatusOverride: opp.salesStatusOverride, salesStatus: opp.salesStatus, engagementHistory: opp.engagementHistory }));
                }
                setInactiveSaving(false);
              }}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50">
                {inactiveSaving ? 'Saving...' : 'Mark Inactive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Closed-Lost Modal */}
      {showClosedLostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[60] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowClosedLostModal(false); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Move to Closed-Lost</h3>
            <p className="text-sm text-gray-500 mb-4">"{opp.opportunityName}" will be moved to Closed-Lost.</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <select value={closedLostReason} onChange={(e) => setClosedLostReason(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian mb-4">
              <option value="">Select a reason...</option>
              <option value="Lost to Competitor">Lost to Competitor</option>
              <option value="Budget Constraints">Budget Constraints</option>
              <option value="No Decision / Project Shelved">No Decision / Project Shelved</option>
              <option value="Client Went with Internal Solution">Client Went with Internal Solution</option>
              <option value="Scope / Requirements Mismatch">Scope / Requirements Mismatch</option>
              <option value="Pricing Not Competitive">Pricing Not Competitive</option>
              <option value="Non Responsive">Non Responsive</option>
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">Comments <span className="text-gray-400">(optional)</span></label>
            <textarea value={closedLostComments} onChange={(e) => setClosedLostComments(e.target.value)}
              placeholder="Additional details about why this opportunity was lost..." rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian mb-4 resize-none" />

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowClosedLostModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button disabled={closedLostSaving || !closedLostReason} onClick={async () => {
                if (!window.confirm(`Are you sure you want to move "${opp.opportunityName}" to Closed-Lost? This action marks the opportunity as lost.`)) return;
                setClosedLostSaving(true);
                const now = new Date();
                const fullReason = closedLostComments ? `${closedLostReason} — ${closedLostComments}` : closedLostReason;
                const updatedHistory = prependEngagementEntry(
                  formData.engagementHistory,
                  `Stage Changed: ${opp.salesStage} → Closed-Lost — ${fullReason}`,
                  currentUser?.name,
                  now
                );
                const updatedOpp = { ...opp, salesStage: 'Closed-Lost', engagementHistory: updatedHistory, winLossReason: fullReason, actualCloseDate: now.toISOString().split('T')[0], lastEditedDate: now.toISOString().split('T')[0] };
                setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? updatedOpp : o));
                setFormData(prev => ({ ...prev, salesStage: 'Closed-Lost', engagementHistory: updatedHistory, winLossReason: fullReason, actualCloseDate: now.toISOString().split('T')[0], lastEditedDate: now.toISOString().split('T')[0] }));
                try {
                  const response = await fetch(`${API_BASE_URL}/opportunities/${opp.opportunityId}`, {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      opportunity_name: opp.opportunityName, associated_account: opp.associatedAccount, sales_stage: 'Closed-Lost',
                      probability: '0%', probability_override: opp.probabilityOverride,
                      estimated_revenue: opp.estimatedRevenue, annual_contract_value: opp.annualContractValue, total_contract_value: opp.totalContractValue,
                      service_line: opp.serviceLine, service_offering: opp.serviceOffering, entity: opp.entity,
                      geography: opp.geography, industry: opp.industryVertical || opp.industry, opportunity_type: opp.opportunityType,
                      opportunity_category: opp.opportunityCategory || 'New Business', opportunity_classification: opp.opportunityClassification || null, nature_of_work: opp.natureOfWork || null,
                      priority: opp.priorityLevel, status: opp.salesStatus || 'Warm', sales_status_override: opp.salesStatusOverride || null,
                      account_owner: opp.accountOwner, lead_supporter: opp.leadSupporter, solution_architect: opp.solutionArchitect || null, executive_sponsor: opp.executiveSponsor,
                      primary_contact: opp.primaryContact, primary_contact_email: opp.primaryContactEmail, phone_number: opp.phoneNumber || null,
                      initiation_date: opp.initiationDate, expected_close_date: opp.estimatedCloseDate || null,
                      actual_close_date: now.toISOString().split('T')[0],
                      solution_scope: opp.solutionScope || null, engagement_history: updatedHistory,
                      win_loss_reason: fullReason,
                      comments: opp.comments || null, latest_comment: opp.latestComment || null,
                      last_edited_date: now.toISOString().split('T')[0], marked_for_review: opp.markedForReview ? 1 : 0
                    })
                  });
                  const responseData = await response.json();
                  if (!response.ok || !responseData.success) {
                    alert(`Failed to save: ${responseData.message || response.statusText}`);
                    setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? opp : o));
                    setFormData(prev => ({ ...prev, salesStage: opp.salesStage, engagementHistory: opp.engagementHistory, winLossReason: opp.winLossReason }));
                  } else { setShowClosedLostModal(false); }
                } catch (error) {
                  alert(`Network error: ${error.message}`);
                  setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? opp : o));
                  setFormData(prev => ({ ...prev, salesStage: opp.salesStage, engagementHistory: opp.engagementHistory, winLossReason: opp.winLossReason }));
                }
                setClosedLostSaving(false);
              }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                {closedLostSaving ? 'Saving...' : 'Move to Closed-Lost'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

  // Opportunities View with Expandable Rows
  const OpportunitiesView = ({
    currentUser,
    onNewOpportunity,
    salesStages,
    fetchOpportunityById,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    currentPage,
    setCurrentPage,
    showMyOppsOnly,
    setShowMyOppsOnly,
    selectedOwners,
    setSelectedOwners,
    selectedGeographies,
    setSelectedGeographies,
    selectedServiceLines,
    setSelectedServiceLines,
    selectedSalesStages,
    setSelectedSalesStages,
    selectedLeadRatings,
    setSelectedLeadRatings,
    leads,
    showReviewOnly,
    setShowReviewOnly,
    searchOpp,
    setSearchOpp,
    activeFilterType,
    setActiveFilterType,
    expandedFilterOptions,
    setExpandedFilterOptions,
    summaryStageFilter,
    setSummaryStageFilter,
    opportunities,
    setOpportunities,
    tasks,
    setTasks,
    setSelectedOpportunity,
    setShowOpportunityModal,
    users,
    geographies,
    serviceLines,
    industries,
    serviceOfferings,
    entities,
    tags,
    selectedTags,
    setSelectedTags,
    customers,
    setCustomers
  }) => {

    const { theme: oppTheme } = useTheme();
    const isDark = oppTheme.mode === 'dark';

    // Defensive: Ensure arrays are never undefined
    const safeOpportunities = opportunities || [];
    const safeTags = tags || [];
    const safeSelectedTags = selectedTags || [];
    const safeEntities = entities || [];
    const safeUsers = users || [];

    const [oppActions, setOppActions] = useState({});
    const [oppNotes, setOppNotes] = useState({});
    const [oppActiveTab, setOppActiveTab] = useState({});
    const [fullScreenOpp, setFullScreenOpp] = useState(null); // For full-screen opportunity detail modal

    // Date filter state
    const [selectedDateFilter, setSelectedDateFilter] = useState<string | null>(null);
    const [customDateFrom, setCustomDateFrom] = useState<string>('');
    const [customDateTo, setCustomDateTo] = useState<string>('');

    // Entity filter state
    const [selectedEntityGroups, setSelectedEntityGroups] = useState<string[]>([]);
    const [selectedEntities, setSelectedEntities] = useState<string[]>([]);

    // Industry, City, Lead Generator filter state
    const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
    const [selectedCities, setSelectedCities] = useState<string[]>([]);
    const [selectedLeadGenerators, setSelectedLeadGenerators] = useState<string[]>([]);
    const [selectedLeadSupporters, setSelectedLeadSupporters] = useState<string[]>([]);

    const itemsPerPage = 50;

    // Sorting handler
    const handleSort = (field) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
      setCurrentPage(1);
    };

    // Intelligent stage matching function
    const matchStageToClosest = (inputStage) => {
      if (!inputStage || !salesStages || salesStages.length === 0) {
        return 'Qualified';
      }

      const input = inputStage.toLowerCase().trim();
      const availableStages = salesStages.map(s => s.name);

      // Direct match (case-insensitive)
      const directMatch = availableStages.find(stage => stage.toLowerCase() === input);
      if (directMatch) return directMatch;

      // Define comprehensive stage mapping patterns (using valid stage names)
      const stagePatterns = {
        'Qualified': ['qualified', 'qualification', 'lead', 'new', 'prospecting', 'prospect', 'initial', 'contact', 'outreach'],
        'Discovery': ['discovery', 'needs', 'analysis', 'assessment', 'requirement', 'discovery & needs', 'discovery completed'],
        'Proposal': ['proposal', 'submitted', 'under review', 'review', 'evaluation', 'pending', 'proposal sent', 'solution', 'design', 'solutioning', 'rfp', 'quote', 'discussion', 'negotiation', 'negotiate', 'pricing', 'terms'],
        'Closed-Won': ['won', 'closed won', 'closed-won', 'win', 'success', 'signed', 'awarded', 'accepted', 'end game', 'endgame', 'closing'],
        'Closed-Lost': ['lost', 'closed lost', 'closed-lost', 'lose', 'rejected', 'declined', 'unsuccessful', 'failed'],
        'Contract': ['contracting', 'contracting complete', 'contracted', 'contract', 'payment', 'complete']
      };

      // Find best match based on keywords
      let bestMatch = null;
      let maxMatches = 0;

      for (const [stage, keywords] of Object.entries(stagePatterns)) {
        if (availableStages.includes(stage)) {
          let matchCount = 0;
          for (const keyword of keywords) {
            if (input.includes(keyword.toLowerCase())) {
              matchCount++;
            }
          }
          if (matchCount > maxMatches) {
            maxMatches = matchCount;
            bestMatch = stage;
          }
        }
      }

      if (bestMatch && maxMatches > 0) {
        return bestMatch;
      }

      // If no match found, try partial matching with available stages
      const partialMatch = availableStages.find(stage => {
        const stageLower = stage.toLowerCase();
        return input.includes(stageLower) || stageLower.includes(input);
      });

      if (partialMatch) return partialMatch;

      // If no match found, try to determine stage based on order keywords
      if (input.includes('1') || input.includes('first') || input.includes('initial')) {
        return availableStages[0] || 'Qualified';
      }
      if (input.includes('final') || input.includes('last') || input.includes('close')) {
        // Check if it sounds like won or lost
        if (input.includes('won') || input.includes('win') || input.includes('success')) {
          return 'Closed-Won';
        }
        if (input.includes('lost') || input.includes('lose') || input.includes('fail')) {
          return 'Closed-Lost';
        }
      }

      // Default to first stage (Qualified) or fallback
      return availableStages[0] || 'Qualified';
    };

    // Function to intelligently create task based on opportunity stage
    const handleCreateTask = (opp) => {
      const taskMapping = {
        'Qualified': {
          task: `Review qualification and plan discovery for ${opp.associatedAccount}`,
          type: 'Task',
          priority: 'High'
        },
        'Discovery': {
          task: `Conduct discovery call with ${opp.associatedAccount}`,
          type: 'Meeting',
          priority: 'High'
        },
        'Proposal': {
          task: `Follow up on submitted proposal - ${opp.associatedAccount}`,
          type: 'Proposal',
          priority: 'High'
        },
        'Contract': {
          task: `Finalise contract terms with ${opp.associatedAccount}`,
          type: 'Meeting',
          priority: 'High'
        },
        'Closed-Won': {
          task: `Complete contracting for ${opp.opportunityName}`,
          type: 'Task',
          priority: 'High'
        },
        'Closed-Lost': {
          task: `Request feedback from ${opp.associatedAccount}`,
          type: 'Call',
          priority: 'Low'
        }
      };

      const taskInfo = taskMapping[opp.salesStage] || {
        task: `Follow up with ${opp.associatedAccount}`,
        type: 'Task',
        priority: 'Medium'
      };

      // Calculate due date - always 3 days from now
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 3);

      // Determine owner (use bid manager or account owner)
      const owner = opp.leadSupporter || opp.accountOwner || 'Unassigned';

      // Check if there's an existing task for this opportunity
      const existingTaskIndex = tasks.findIndex(t =>
        t.relatedType === 'Opportunity' && t.relatedId === opp.opportunityId
      );

      if (existingTaskIndex !== -1) {
        // Replace existing task
        const updatedTask = {
          ...tasks[existingTaskIndex],
          task: taskInfo.task,
          owner: owner,
          dueDate: dueDate.toISOString().split('T')[0],
          priority: taskInfo.priority,
          status: 'Pending',
          type: taskInfo.type,
          relatedTo: opp.associatedAccount
        };

        const updatedTasks = [...tasks];
        updatedTasks[existingTaskIndex] = updatedTask;
        setTasks(updatedTasks);

        alert(`Task updated successfully!\n\nTask: ${taskInfo.task}\nOwner: ${owner}\nDue: ${dueDate.toLocaleDateString()}\nPriority: ${taskInfo.priority}\n\nYou can view this task in the Calendar tab.`);
      } else {
        // Create new task
        const newTask = {
          id: tasks.length + 1,
          task: taskInfo.task,
          owner: owner,
          dueDate: dueDate.toISOString().split('T')[0],
          priority: taskInfo.priority,
          status: 'Pending',
          type: taskInfo.type,
          relatedTo: opp.associatedAccount,
          relatedId: opp.opportunityId,
          relatedType: 'Opportunity'
        };

        setTasks([...tasks, newTask]);

        alert(`Task created successfully!\n\nTask: ${taskInfo.task}\nOwner: ${owner}\nDue: ${dueDate.toLocaleDateString()}\nPriority: ${taskInfo.priority}\n\nYou can view this task in the Calendar tab.`);
      }
    };

    // Toggle filter selection (multi-select like Dashboard)
    const toggleOppFilterSelection = (filterType, value) => {
      switch (filterType) {
        case 'geography':
          setSelectedGeographies(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
          break;
        case 'salesperson':
          setSelectedOwners(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
          break;
        case 'serviceline':
          setSelectedServiceLines(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
          break;
        case 'leadrating':
          setSelectedLeadRatings(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
          break;
        case 'tags':
          if (setSelectedTags) setSelectedTags(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
          break;
        case 'industry':
          setSelectedIndustries(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
          break;
        case 'city':
          setSelectedCities(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
          break;
        case 'leadgenerator':
          setSelectedLeadGenerators(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
          break;
        case 'leadsupporter':
          setSelectedLeadSupporters(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
          break;
      }
    };

    // Helper to get current financial year label (April to March)
    const getCurrentFYLabel = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth(); // 0-11
      // FY starts in April (month 3)
      if (month >= 3) {
        // April onwards - FY is current year to next year
        return `FY${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
      } else {
        // Jan-Mar - FY is previous year to current year
        return `FY${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
      }
    };

    // Helper to format date as YYYY-MM-DD for input
    const formatDateForInput = (date: Date): string => {
      return date.toISOString().split('T')[0];
    };

    // Date filter helper function - calculates date ranges
    const calculateDateRange = (filterType: string | null): { from: Date; to: Date } => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (filterType) {
        case 'fy': {
          // Financial Year: April 1 to March 31
          const year = now.getFullYear();
          const month = now.getMonth();
          let fyStartYear: number;
          if (month >= 3) {
            // April onwards
            fyStartYear = year;
          } else {
            // Jan-Mar
            fyStartYear = year - 1;
          }
          const startOfFY = new Date(fyStartYear, 3, 1); // April 1
          return { from: startOfFY, to: today };
        }
        case 'quarter': {
          // Current quarter (calendar quarter)
          const currentQuarter = Math.floor(now.getMonth() / 3);
          const startOfQuarter = new Date(now.getFullYear(), currentQuarter * 3, 1);
          return { from: startOfQuarter, to: today };
        }
        case 'last30': {
          // Last 30 days
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return { from: thirtyDaysAgo, to: today };
        }
        case 'last7': {
          // Last 7 days
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return { from: sevenDaysAgo, to: today };
        }
        default:
          return { from: today, to: today };
      }
    };

    // Apply preset and update date inputs
    const applyDatePreset = (preset: string) => {
      const { from, to } = calculateDateRange(preset);
      setCustomDateFrom(formatDateForInput(from));
      setCustomDateTo(formatDateForInput(to));
      setSelectedDateFilter(preset);
    };

    // Check if opportunity matches date filter (based on initiationDate)
    const matchesDateFilter = (opp: any): boolean => {
      if (!customDateFrom && !customDateTo) return true;

      const from = customDateFrom ? new Date(customDateFrom) : null;
      const to = customDateTo ? new Date(customDateTo) : null;

      const oppDate = opp.initiationDate ? new Date(opp.initiationDate) : null;
      if (!oppDate) return false;

      if (from && oppDate < from) return false;
      if (to) {
        const endOfDay = new Date(to);
        endOfDay.setHours(23, 59, 59, 999);
        if (oppDate > endOfDay) return false;
      }
      return true;
    };

    // Check if any filters are active
    const hasOppActiveFilters = showMyOppsOnly || selectedOwners.length > 0 || selectedGeographies.length > 0 ||
      selectedServiceLines.length > 0 || selectedLeadRatings.length > 0 ||
      selectedEntityGroups.length > 0 || selectedEntities.length > 0 || safeSelectedTags.length > 0 ||
      customDateFrom !== '' || customDateTo !== '' || showReviewOnly || selectedIndustries.length > 0 ||
      selectedCities.length > 0 || selectedLeadGenerators.length > 0 || selectedLeadSupporters.length > 0;

    // Clear all filters
    const clearOppAllFilters = () => {
      setShowMyOppsOnly(false);
      setSelectedOwners([]);
      setSelectedGeographies([]);
      setSelectedServiceLines([]);
      setSelectedLeadRatings([]);
      setSelectedEntityGroups([]);
      setSelectedEntities([]);
      setSelectedIndustries([]);
      setSelectedCities([]);
      setSelectedLeadGenerators([]);
      setSelectedLeadSupporters([]);
      if (setSelectedTags) setSelectedTags([]);
      setSelectedDateFilter(null);
      setCustomDateFrom('');
      setCustomDateTo('');
      setActiveFilterType(null);
      setShowReviewOnly(false);
    };

    // Access control — filter opportunities by geography and/or service line for non-admin users
    const userAccessibleOpportunities = safeOpportunities.filter(opp => {
      if (currentUser?.isAdmin) return true;
      const hasGeo = currentUser?.geographies?.length > 0;
      const hasSL = currentUser?.serviceLines?.length > 0;
      // A user with no service lines assigned sees nothing
      if (!hasGeo && !hasSL) return false;
      const geoOk = !hasGeo ||
        (opp.geography && currentUser.geographies.includes(opp.geography));
      const slOk = !hasSL ||
        (opp.serviceLine && currentUser.serviceLines.includes(opp.serviceLine));
      const matchesAccess = geoOk && slOk;
      // Flagged-for-review records are accessible to the account owner or solution architect regardless
      const isFlaggedAndOwned = opp.markedForReview === true &&
        currentUser?.name &&
        (opp.accountOwner === currentUser.name || opp.solutionArchitect === currentUser.name);
      return matchesAccess || isFlaggedAndOwned;
    });

    // Debug logging for geography filtering
    console.log('🔍 Geography Filter Debug:', {
      currentUserName: currentUser?.name,
      currentUserGeographies: currentUser?.geographies,
      currentUserIsAdmin: currentUser?.isAdmin,
      totalOpportunities: safeOpportunities.length,
      accessibleOpportunities: userAccessibleOpportunities.length,
      sampleOpportunityGeographies: safeOpportunities.slice(0, 5).map(o => o.geography)
    });



    // Get unique values for filters from user-accessible opportunities only
    const uniqueOwners = (() => {
      const seen = new Map();
      userAccessibleOpportunities.forEach(o => {
        const owner = o.accountOwner?.trim();
        if (owner) {
          const normalized = owner.toLowerCase();
          if (!seen.has(normalized)) {
            seen.set(normalized, owner);
          }
        }
      });
      return [...seen.values()];
    })();
    const uniqueGeographies = (() => {
      const seen = new Map();
      let hasNull = false;
      userAccessibleOpportunities.forEach(o => {
        const geo = o.geography?.trim();
        if (geo) {
          const normalized = geo.toLowerCase();
          if (!seen.has(normalized)) {
            seen.set(normalized, geo);
          }
        } else {
          hasNull = true;
        }
      });
      const result = [...seen.values()];
      if (hasNull && !seen.has('domestic')) result.push('Domestic');
      return result;
    })();
    const uniqueServiceLines = (() => {
      const seen = new Map();
      userAccessibleOpportunities.forEach(o => {
        const sl = o.serviceLine?.trim();
        if (sl) {
          const normalized = sl.toLowerCase();
          if (!seen.has(normalized)) {
            seen.set(normalized, sl);
          }
        }
      });
      return [...seen.values()];
    })();
    const uniqueSalesStages = (() => {
      const seen = new Map();
      userAccessibleOpportunities.forEach(o => {
        const stage = o.salesStage?.trim();
        if (stage) {
          const normalized = stage.toLowerCase();
          if (!seen.has(normalized)) {
            seen.set(normalized, stage);
          }
        }
      });
      return [...seen.values()];
    })();
    // Get effective sales status for each opportunity (handles null lastEditedDate)
    const uniqueSalesStatuses = [...new Set(userAccessibleOpportunities.map(o => getEffectiveSalesStatus(o)).filter(Boolean))];
    const uniqueIndustries = Array.from(new Set(userAccessibleOpportunities.map(o => o.industry).filter(Boolean))).sort();
    const uniqueOppCities = Array.from(new Set(userAccessibleOpportunities.map(o => o.city).filter(Boolean))).sort();
    const uniqueOppLeadGenerators = Array.from(new Set(userAccessibleOpportunities.map(o => o.leadGenerator).filter(Boolean))).sort();
    const uniqueOppLeadSupporters = Array.from(new Set(userAccessibleOpportunities.map(o => o.leadSupporter).filter(Boolean))).sort();

    // Apply filters (multi-select arrays like Dashboard)
    // Start with user-accessible opportunities (already filtered by geography access)
    const filteredOpportunities = userAccessibleOpportunities.filter(opp => {
      // Exclude leads - only show qualified opportunities (Solution Phase and beyond)
      // Filter out Assessment stage and any stage containing "Lead"
      const isQualifiedOpportunity = opp.salesStage !== 'Assessment' &&
                                     !opp.salesStage?.toLowerCase().includes('lead');

      const searchLower = searchOpp.toLowerCase();
      const matchesSearch = !searchOpp ||
                          opp.opportunityName?.toLowerCase().includes(searchLower) ||
                          opp.associatedAccount?.toLowerCase().includes(searchLower) ||
                          (opp.primaryContact || '').toLowerCase().includes(searchLower) ||
                          (opp.primaryContactEmail || '').toLowerCase().includes(searchLower) ||
                          (opp.customerName || '').toLowerCase().includes(searchLower);

      // Get effective sales status for filtering (handles null lastEditedDate)
      const computedSalesStatus = getEffectiveSalesStatus(opp);

      // My Opps filter
      const matchesMyOpps = !showMyOppsOnly || !currentUser?.name || opp.accountOwner === currentUser.name;
      // Multi-select filter matching (empty array = show all)
      const matchesOwner = selectedOwners.length === 0 || selectedOwners.includes(opp.accountOwner);
      const matchesGeography = selectedGeographies.length === 0 || selectedGeographies.includes(opp.geography || 'Domestic');
      const matchesServiceLine = selectedServiceLines.length === 0 || selectedServiceLines.some(sl => sl.trim().toLowerCase() === (opp.serviceLine || '').trim().toLowerCase());
      const matchesSalesStage = selectedSalesStages.length === 0 || selectedSalesStages.includes(opp.salesStage);
      const matchesLeadRating = selectedLeadRatings.length === 0 || selectedLeadRatings.includes(opp.leadRating || '');

      // Entity filter matching - check entity group and individual entity
      const matchesEntityGroup = selectedEntityGroups.length === 0 || (() => {
        if (!opp.entity) {
          // Opportunities without entity match "Ungrouped"
          return selectedEntityGroups.includes('Ungrouped');
        }
        const entityData = entities.find(e => e.name === opp.entity);
        if (!entityData) {
          // Entity not found matches "Ungrouped"
          return selectedEntityGroups.includes('Ungrouped');
        }
        // Check if entity has no group (matches "Ungrouped") or matches selected group
        if (!entityData.entityGroup || entityData.entityGroup === '') {
          return selectedEntityGroups.includes('Ungrouped');
        }
        return selectedEntityGroups.includes(entityData.entityGroup);
      })();
      const matchesEntity = selectedEntities.length === 0 || (() => {
        // Handle "Blank" selection - matches opportunities with no entity
        if (selectedEntities.includes('Blank') && (!opp.entity || opp.entity.trim() === '')) {
          return true;
        }
        // Match specific entities (excluding "Blank" from the check)
        const actualEntities = selectedEntities.filter(e => e !== 'Blank');
        if (actualEntities.length > 0 && opp.entity && actualEntities.includes(opp.entity)) {
          return true;
        }
        // If only "Blank" is selected and entity is not blank, don't match
        if (selectedEntities.length === 1 && selectedEntities.includes('Blank')) {
          return false;
        }
        return actualEntities.length === 0;
      })();

      // Apply summary stage filter
      // 'everything' = open pipeline (excludes Closed-Won and Closed-Lost)
      const PIPELINE_EXCLUDED_STAGES = ['Closed-Won', 'Closed-Lost'];
      const matchesSummaryStage = summaryStageFilter === 'all' ||
        (summaryStageFilter === 'everything' && !PIPELINE_EXCLUDED_STAGES.includes(opp.salesStage)) ||
        opp.salesStage === summaryStageFilter;

      // Exclude Won, Lost, and Inactive unless explicitly selected via stage filter or sales stage pills
      const isClosedStage = opp.salesStage === 'Closed-Won' || opp.salesStage === 'Closed-Lost';
      const isInactive = getEffectiveSalesStatus(opp) === 'Inactive';
      // 'all' and 'everything' both exclude closed stages; only explicit stage pill selection shows them
      const showClosedStages = (summaryStageFilter !== 'all' && summaryStageFilter !== 'everything') && (
        summaryStageFilter === 'Closed-Won' ||
        summaryStageFilter === 'Closed-Lost' ||
        selectedSalesStages.includes('Closed-Won') ||
        selectedSalesStages.includes('Closed-Lost')
      );
      const showInactive = false;

      const matchesClosedFilter = !isClosedStage || showClosedStages;
      const matchesInactiveFilter = !isInactive || showInactive;

      // Apply date filter
      const matchesDate = matchesDateFilter(opp);

      // Apply review filter
      const matchesReviewFilter = !showReviewOnly || opp.markedForReview === true;

      // Apply tag filter (show opportunities that have at least one selected tag)
      const matchesTags = safeSelectedTags.length === 0 || (opp.tags && opp.tags.some(tag => safeSelectedTags.includes(tag.id)));

      const matchesIndustry = selectedIndustries.length === 0 || selectedIndustries.includes(opp.industry);
      const matchesCity = selectedCities.length === 0 || selectedCities.includes(opp.city);
      const matchesLeadGenerator = selectedLeadGenerators.length === 0 || selectedLeadGenerators.includes(opp.leadGenerator);
      const matchesLeadSupporter = selectedLeadSupporters.length === 0 || selectedLeadSupporters.includes(opp.leadSupporter);

      return isQualifiedOpportunity && matchesMyOpps && matchesSearch && matchesOwner && matchesGeography &&
             matchesServiceLine && matchesSalesStage && matchesLeadRating && matchesEntityGroup && matchesEntity &&
             matchesSummaryStage && matchesClosedFilter && matchesInactiveFilter && matchesDate && matchesReviewFilter && matchesTags && matchesIndustry && matchesCity && matchesLeadGenerator && matchesLeadSupporter;
    });

    // Valid pipeline stages — only opportunities with these stages appear in the UI
    const validPipelineStages = ['Qualified', 'Discovery', 'Proposal', 'Contract', 'Closed-Won', 'Closed-Lost', 'Deferred', 'Did not pursue further'];

    // Cross-filter helper: returns opportunities matching ALL filters EXCEPT the specified one.
    // Mirrors filteredOpportunities logic exactly so pill counts predict actual table results.
    const getFilteredOppsExcluding = (excludeFilter: string) => {
      return userAccessibleOpportunities.filter(opp => {
        // Must be a qualified opportunity (not Assessment or lead stages)
        if (opp.salesStage === 'Assessment' || opp.salesStage?.toLowerCase().includes('lead')) return false;

        // Must be a valid pipeline stage (matches summary card logic)
        if (!validPipelineStages.includes(opp.salesStage)) return false;

        // Search filter always applies
        if (searchOpp) {
          const matchesSearch = opp.opportunityName?.toLowerCase().includes(searchOpp.toLowerCase()) ||
                              opp.associatedAccount?.toLowerCase().includes(searchOpp.toLowerCase());
          if (!matchesSearch) return false;
        }

        // Exclude closed stages unless explicitly selected via stage pills (mirrors filteredOpportunities)
        const isClosedStage = opp.salesStage === 'Closed-Won' || opp.salesStage === 'Closed-Lost';
        const showClosedStages = (summaryStageFilter !== 'all' && summaryStageFilter !== 'everything') && (
          summaryStageFilter === 'Closed-Won' ||
          summaryStageFilter === 'Closed-Lost' ||
          selectedSalesStages.includes('Closed-Won') ||
          selectedSalesStages.includes('Closed-Lost')
        );
        if (isClosedStage && !showClosedStages) return false;

        const computedSalesStatus = getEffectiveSalesStatus(opp);
        const isInactive = computedSalesStatus === 'Inactive';
        if (isInactive) return false;

        // My Opps filter always applies in cross-filter
        if (showMyOppsOnly && currentUser?.name && opp.accountOwner !== currentUser.name) return false;
        // Apply all dimension filters EXCEPT the excluded one (uses same matching as filteredOpportunities)
        if (excludeFilter !== 'geography' && selectedGeographies.length > 0 && !selectedGeographies.includes(opp.geography || 'Domestic')) return false;
        if (excludeFilter !== 'salesperson' && selectedOwners.length > 0 && !selectedOwners.includes(opp.accountOwner)) return false;
        if (excludeFilter !== 'serviceline' && selectedServiceLines.length > 0 && !selectedServiceLines.some(sl => sl.trim().toLowerCase() === (opp.serviceLine || '').trim().toLowerCase())) return false;
        if (excludeFilter !== 'stage' && selectedSalesStages.length > 0 && !selectedSalesStages.includes(opp.salesStage)) return false;
        if (excludeFilter !== 'leadrating' && selectedLeadRatings.length > 0 && !selectedLeadRatings.includes(opp.leadRating || '')) return false;

        // Entity group filter
        if (excludeFilter !== 'entity' && selectedEntityGroups.length > 0) {
          if (!opp.entity) {
            if (!selectedEntityGroups.includes('Ungrouped')) return false;
          } else {
            const entityData = entities.find(e => e.name === opp.entity);
            if (!entityData) {
              if (!selectedEntityGroups.includes('Ungrouped')) return false;
            } else if (!entityData.entityGroup || entityData.entityGroup === '') {
              if (!selectedEntityGroups.includes('Ungrouped')) return false;
            } else {
              if (!selectedEntityGroups.includes(entityData.entityGroup)) return false;
            }
          }
        }
        // Entity filter
        if (excludeFilter !== 'entity' && selectedEntities.length > 0) {
          const hasBlank = selectedEntities.includes('Blank');
          const actualEntities = selectedEntities.filter(e => e !== 'Blank');
          const isBlankEntity = !opp.entity || opp.entity.trim() === '';
          if (hasBlank && isBlankEntity) { /* matches */ }
          else if (actualEntities.length > 0 && opp.entity && actualEntities.includes(opp.entity)) { /* matches */ }
          else return false;
        }

        if (excludeFilter !== 'date' && !matchesDateFilter(opp)) return false;
        if (excludeFilter !== 'review' && showReviewOnly && !opp.markedForReview) return false;
        if (excludeFilter !== 'tags' && safeSelectedTags.length > 0 && !(opp.tags && opp.tags.some(tag => safeSelectedTags.includes(tag.id)))) return false;
        if (excludeFilter !== 'industry' && selectedIndustries.length > 0 && !selectedIndustries.includes(opp.industry)) return false;
        if (excludeFilter !== 'city' && selectedCities.length > 0 && !selectedCities.includes(opp.city)) return false;
        if (excludeFilter !== 'leadgenerator' && selectedLeadGenerators.length > 0 && !selectedLeadGenerators.includes(opp.leadGenerator)) return false;
        if (excludeFilter !== 'leadsupporter' && selectedLeadSupporters.length > 0 && !selectedLeadSupporters.includes(opp.leadSupporter)) return false;

        return true;
      });
    };

    // Sort filtered opportunities
    const sortedOpps = [...filteredOpportunities].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'initiationDate' || sortField === 'estimatedCloseDate') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (sortField === 'annualContractValue' || sortField === 'totalContractValue') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (sortField === 'salesStatus') {
        // Sort by computed effective sales status (Hot > Warm > Cold)
        const statusOrder = { 'Hot': 1, 'Warm': 2, 'Cold': 3 };
        aVal = statusOrder[getEffectiveSalesStatus(a)] || 4;
        bVal = statusOrder[getEffectiveSalesStatus(b)] || 4;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Pagination
    const totalPages = Math.ceil(sortedOpps.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedOpps = sortedOpps.slice(startIndex, endIndex);

    // Open full-screen opportunity detail modal
    const openFullScreenOpp = async (opp) => {
      // Fetch fresh data from API before opening
      const freshOpp = await fetchOpportunityById(opp.opportunityId);
      setFullScreenOpp(freshOpp || opp);
    };
    
    const getActionsForStage = (stage) => {
      // Only "Update Engagement" option for all stages
      return [
        { value: 'update_engagement', label: 'Update Engagement' }
      ];
    };
    
    const handleOppActionSubmit = async (oppId, oppData) => {
      const action = oppActions[oppId];
      const note = oppNotes[oppId];

      if (!note || note.trim() === '') {
        alert('Please enter a note');
        return;
      }

      // Use ISO timestamp for storage (parseable)
      const now = new Date();
      const isoTimestamp = now.toISOString();

      // Format as DDMonYY HH:MM:SS (e.g., "03Nov25 15:30:45")
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = String(now.getDate()).padStart(2, '0');
      const month = monthNames[now.getMonth()];
      const year = String(now.getFullYear()).slice(-2);
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const displayTimestamp = `${day}${month}${year} ${hours}:${minutes}:${seconds}`;

      let entryBody;
      if (action) {
        const actionLabel = getActionsForStage(oppData.salesStage).find(a => a.value === action)?.label || action;
        entryBody = `${actionLabel}: ${note}`;
      } else {
        entryBody = `Note: ${note}`;
      }

      // Build comment history with newest first
      const commentEntry = `[${displayTimestamp}] ${note}`;
      const existingComments = oppData.comments || '';
      const newComments = existingComments
        ? `${commentEntry}\n${existingComments}`
        : commentEntry;

      // Update the opportunity's engagement history (attributed to current user)
      const updatedOpp = {
        ...oppData,
        engagementHistory: prependEngagementEntry(oppData.engagementHistory, entryBody, currentUser?.name, now),
        comments: newComments,
        latestComment: note,
        lastEditedDate: now.toISOString().split('T')[0]
      };

      // Save to database
      try {
        // Using global API_BASE_URL defined at top of file
        const logNotePayload = {
            opportunity_name: updatedOpp.opportunityName,
            associated_account: updatedOpp.associatedAccount,
            sales_stage: updatedOpp.salesStage,
            probability: String(updatedOpp.probabilityPercent || '0% - 35%'),
            estimated_revenue: updatedOpp.estimatedRevenue,
            annual_contract_value: updatedOpp.annualContractValue,
            total_contract_value: updatedOpp.totalContractValue,
            service_line: updatedOpp.serviceLine,
            service_offering: updatedOpp.serviceOffering,
            entity: updatedOpp.entity,
            geography: updatedOpp.geography,
            industry: updatedOpp.industry,
            opportunity_type: updatedOpp.opportunityType,
            opportunity_category: updatedOpp.opportunityCategory || 'New Business',
            opportunity_classification: updatedOpp.opportunityClassification || null,
            nature_of_work: updatedOpp.natureOfWork || null,
            priority: updatedOpp.priorityLevel,
            status: updatedOpp.salesStatus || 'Warm',
            sales_status_override: updatedOpp.salesStatusOverride || null,
            account_owner: updatedOpp.accountOwner,
            lead_supporter: updatedOpp.leadSupporter,
            initiation_date: updatedOpp.initiationDate,
            expected_close_date: updatedOpp.estimatedCloseDate || null,
            actual_close_date: updatedOpp.actualCloseDate || null,
            win_loss_reason: updatedOpp.winLossReason || null,
            engagement_history: updatedOpp.engagementHistory || null,
            comments: updatedOpp.comments || null,
            latest_comment: updatedOpp.latestComment || null,
            last_edited_date: updatedOpp.lastEditedDate,
            lead_history: updatedOpp.leadHistory || null,
            source_lead_id: updatedOpp.sourceLeadId || null,
            city: updatedOpp.city || null,
            lead_generator: updatedOpp.leadGenerator || null
        };

        // Debug: Log the exact payload being sent
        console.log('📤 LogNote API Payload:', JSON.stringify(logNotePayload, null, 2));
        console.log('📊 Probability field type:', typeof logNotePayload.probability, 'value:', logNotePayload.probability);

        const response = await fetch(`${API_BASE_URL}/opportunities/${oppId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(logNotePayload)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to save note');
        }

        setOpportunities(opportunities.map(o => o.opportunityId === oppId ? updatedOpp : o));

        // Clear the action fields
        setOppActions(prev => ({...prev, [oppId]: ''}));
        setOppNotes(prev => ({...prev, [oppId]: ''}));

        alert('Note logged successfully!');
      } catch (error) {
        console.error('Error saving note:', error);
        alert(`Error saving note: ${error.message}`);
      }
    };

    const handleMoveToNextStage = async (oppId, oppData) => {
      // Define the stage progression order (excluding Closed-Lost which is a separate terminal path)
      const stageOrder = [
        'Qualified',
        'Discovery',
        'Proposal',
        'Contract',
        'Closed-Won'
      ];

      const currentStage = oppData.salesStage;
      const currentIndex = stageOrder.indexOf(currentStage);

      // Can't move forward if already at terminal stages
      if (currentStage === 'Closed-Won' || currentStage === 'Closed-Lost') {
        alert('Opportunity is already at a terminal stage. Cannot move to next stage.');
        return;
      }

      if (currentIndex === -1) {
        alert('Current stage not found in progression order.');
        return;
      }

      // Move to next stage
      let nextIndex = currentIndex + 1;
      if (nextIndex >= stageOrder.length) {
        nextIndex = stageOrder.length - 1; // Point to Closed-Won
      }

      const nextStage = stageOrder[nextIndex];

      if (window.confirm(`Move opportunity "${oppData.opportunityName}" from "${currentStage}" to "${nextStage}"?`)) {
        const now = new Date();
        const estimatedCloseDateFormatted = oppData.estimatedCloseDate ?
          new Date(oppData.estimatedCloseDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : '';
        const estimatedCloseDateText = estimatedCloseDateFormatted ? ` (Est. Close: ${estimatedCloseDateFormatted})` : '';
        const entryBody = `Stage Changed: Moved from "${currentStage}" to "${nextStage}"${estimatedCloseDateText}`;

        // Recalculate probability based on the new stage
        const newStageProbInfo = calculateProbabilityByStage(nextStage);
        const updatedOpp = {
          ...oppData,
          salesStage: nextStage,
          probability: `${newStageProbInfo.percent}%`,
          probabilityPercent: `${newStageProbInfo.percent}%`,
          probabilityToClose: newStageProbInfo.category,
          forecastMultiplier: newStageProbInfo.percent / 100,
          engagementHistory: prependEngagementEntry(oppData.engagementHistory, entryBody, currentUser?.name, now),
          lastEditedDate: now.toISOString().split('T')[0]
        };

        setOpportunities(opportunities.map(o => o.opportunityId === oppId ? updatedOpp : o));

        // Save stage change to database
        try {
          const stageChangePayload = {
            opportunity_name: updatedOpp.opportunityName,
            associated_account: updatedOpp.associatedAccount,
            sales_stage: updatedOpp.salesStage,
            probability: String(newStageProbInfo.percent),
            estimated_revenue: updatedOpp.estimatedRevenue,
            annual_contract_value: updatedOpp.annualContractValue,
            total_contract_value: updatedOpp.totalContractValue,
            service_line: updatedOpp.serviceLine,
            service_offering: updatedOpp.serviceOffering,
            entity: updatedOpp.entity,
            geography: updatedOpp.geography,
            industry: updatedOpp.industry,
            opportunity_type: updatedOpp.opportunityType,
            opportunity_category: updatedOpp.opportunityCategory || 'New Business',
            opportunity_classification: updatedOpp.opportunityClassification || null,
            nature_of_work: updatedOpp.natureOfWork || null,
            priority: updatedOpp.priorityLevel,
            status: updatedOpp.salesStatus || 'Warm',
            sales_status_override: updatedOpp.salesStatusOverride || null,
            account_owner: updatedOpp.accountOwner,
            lead_supporter: updatedOpp.leadSupporter,
            initiation_date: updatedOpp.initiationDate,
            expected_close_date: updatedOpp.estimatedCloseDate || null,
            actual_close_date: updatedOpp.actualCloseDate || null,
            win_loss_reason: updatedOpp.winLossReason || null,
            engagement_history: updatedOpp.engagementHistory || null,
            comments: updatedOpp.comments || null,
            latest_comment: updatedOpp.latestComment || null,
            last_edited_date: updatedOpp.lastEditedDate,
            lead_history: updatedOpp.leadHistory || null,
            source_lead_id: updatedOpp.sourceLeadId || null,
            city: updatedOpp.city || null,
            lead_generator: updatedOpp.leadGenerator || null
          };

          const response = await fetch(`${API_BASE_URL}/opportunities/${oppId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(stageChangePayload)
          });

          if (!response.ok) {
            console.error('Failed to save stage change to database');
          }
        } catch (error) {
          console.error('Error saving stage change:', error);
        }

        // Create/update task for the new stage
        const taskMapping = {
          'Qualified': {
            task: `Review qualification and plan discovery for ${oppData.associatedAccount}`,
            type: 'Task',
            priority: 'High'
          },
          'Discovery': {
            task: `Conduct discovery call with ${oppData.associatedAccount}`,
            type: 'Meeting',
            priority: 'High'
          },
          'Solutioning': {
            task: `Deep dive and design solution for ${oppData.opportunityName}`,
            type: 'Task',
            priority: 'High'
          },
          'Discussion': {
            task: `Present solution and discuss with ${oppData.associatedAccount}`,
            type: 'Meeting',
            priority: 'High'
          },
          'Proposal': {
            task: `Follow up on submitted proposal - ${oppData.associatedAccount}`,
            type: 'Proposal',
            priority: 'High'
          },
          'Negotiation': {
            task: `Finalize contract terms with ${oppData.associatedAccount}`,
            type: 'Meeting',
            priority: 'High'
          },
          'Closed-Won': {
            task: `Complete contracting for ${oppData.opportunityName}`,
            type: 'Task',
            priority: 'High'
          },
          'Contract': {
            task: `Initiate onboarding for ${oppData.associatedAccount}`,
            type: 'Task',
            priority: 'Medium'
          },
          'Closed-Lost': {
            task: `Request feedback from ${oppData.associatedAccount}`,
            type: 'Call',
            priority: 'Low'
          }
        };

        const taskInfo = taskMapping[nextStage] || {
          task: `Follow up with ${oppData.associatedAccount}`,
          type: 'Task',
          priority: 'Medium'
        };

        // Calculate due date - always 3 days from now
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 3);

        // Determine owner (use bid manager or account owner)
        const owner = oppData.leadSupporter || oppData.accountOwner || 'Unassigned';

        // Check if there's an existing task for this opportunity
        const existingTaskIndex = tasks.findIndex(t =>
          t.relatedType === 'Opportunity' && t.relatedId === oppId
        );

        if (existingTaskIndex !== -1) {
          // Replace existing task
          const updatedTask = {
            ...tasks[existingTaskIndex],
            task: taskInfo.task,
            owner: owner,
            dueDate: dueDate.toISOString().split('T')[0],
            priority: taskInfo.priority,
            status: 'Pending',
            type: taskInfo.type,
            relatedTo: oppData.associatedAccount
          };

          const updatedTasks = [...tasks];
          updatedTasks[existingTaskIndex] = updatedTask;
          setTasks(updatedTasks);
        } else {
          // Create new task
          const newTask = {
            id: tasks.length + 1,
            task: taskInfo.task,
            owner: owner,
            dueDate: dueDate.toISOString().split('T')[0],
            priority: taskInfo.priority,
            status: 'Pending',
            type: taskInfo.type,
            relatedTo: oppData.associatedAccount,
            relatedId: oppId,
            relatedType: 'Opportunity'
          };

          setTasks([...tasks, newTask]);
        }

        // Auto-create customer entry when opportunity is Closed-Won
        if (nextStage === 'Closed-Won') {
          const accountName = oppData.associatedAccount || oppData.opportunityName;
          // Check if customer already exists (by company name)
          const existingCustomer = customers?.find(c =>
            c.companyName?.toLowerCase() === accountName?.toLowerCase()
          );

          if (!existingCustomer) {
            const newCustomerId = `CUST-${Date.now()}`;
            const today = new Date().toISOString().split('T')[0];
            const renewalDate = new Date();
            renewalDate.setFullYear(renewalDate.getFullYear() + 1);

            const newCustomer = {
              customerId: newCustomerId,
              companyName: accountName,
              primaryContact: oppData.primaryContact || '',
              email: oppData.primaryContactEmail || '',
              phone: oppData.phoneNumber || '',
              serviceLine: oppData.serviceLine || '',
              geography: oppData.geography || '',
              contractValue: oppData.annualContractValue || oppData.estimatedRevenue || 0,
              startDate: today,
              renewalDate: renewalDate.toISOString().split('T')[0],
              status: 'Active',
              healthScore: 100,
              projectCount: 1,
              accountManager: oppData.accountOwner || oppData.leadSupporter || ''
            };

            // Add to local state
            setCustomers(prev => [...prev, newCustomer]);

            // Save to database
            try {
              const customerPayload = {
                customer_id: newCustomerId,
                company_name: newCustomer.companyName,
                primary_contact: newCustomer.primaryContact,
                email: newCustomer.email,
                phone: newCustomer.phone,
                service_line: newCustomer.serviceLine,
                geography: newCustomer.geography,
                contract_value: newCustomer.contractValue,
                start_date: newCustomer.startDate,
                renewal_date: newCustomer.renewalDate,
                status: newCustomer.status,
                health_score: newCustomer.healthScore,
                project_count: newCustomer.projectCount,
                account_manager: newCustomer.accountManager
              };

              const custResponse = await fetch(`${API_BASE_URL}/customers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(customerPayload)
              });

              if (custResponse.ok) {
                console.log(`✅ Customer "${accountName}" created from Closed-Won opportunity`);
              } else {
                console.error('Failed to save new customer to database');
              }
            } catch (custError) {
              console.error('Error creating customer from Closed-Won opportunity:', custError);
            }

            alert(`Opportunity moved to ${nextStage} successfully!\n\nTask "${taskInfo.task}" has been created/updated in Calendar tab.\nDue: ${dueDate.toLocaleDateString()}\n\n🎉 New customer "${accountName}" has been added to the Customers tab.`);
          } else {
            alert(`Opportunity moved to ${nextStage} successfully!\n\nTask "${taskInfo.task}" has been created/updated in Calendar tab.\nDue: ${dueDate.toLocaleDateString()}\n\nCustomer "${accountName}" already exists in the Customers tab.`);
          }
        } else {
          alert(`Opportunity moved to ${nextStage} successfully!\n\nTask "${taskInfo.task}" has been created/updated in Calendar tab.\nDue: ${dueDate.toLocaleDateString()}`);
        }
      }
    };


    return (
      <div className="flex flex-col">
        {/* Full Screen Opportunity Detail Modal */}
        {fullScreenOpp && (
          <FullScreenOpportunityModal
            opp={fullScreenOpp}
            onClose={() => setFullScreenOpp(null)}
            opportunities={safeOpportunities}
            setOpportunities={setOpportunities}
            users={safeUsers}
            geographies={geographies}
                        serviceLines={serviceLines}
            industries={industries}
            salesStages={salesStages}
            serviceOfferings={serviceOfferings}
            entities={safeEntities}
            tags={safeTags}
            handleMoveToNextStage={handleMoveToNextStage}
            handleCreateTask={handleCreateTask}
            currentUser={currentUser}
          />
        )}

        {/* Filters Section */}
        <div className="pb-1">
          {/* Filters - Compact Single Row */}
          <div className="rounded-md shadow-sm px-2 py-1.5" style={{ backgroundColor: 'var(--dm-surface)', border: '1px solid var(--dm-border)' }}>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Filter size={14} className="text-gray-400 flex-shrink-0" />
              {/* Search Bar - inline */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={12} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchOpp}
                  onChange={(e) => setSearchOpp(e.target.value)}
                  className="w-32 pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-pierian focus:border-pierian"
                />
              </div>
              {/* Geography Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'geography' ? null : 'geography')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedGeographies.length > 0 ? '#00BBA2' : activeFilterType === 'geography' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: selectedGeographies.length > 0 ? 'white' : activeFilterType === 'geography' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'geography' && selectedGeographies.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Geo{selectedGeographies.length > 0 && ` (${selectedGeographies.length})`}
              </button>

              {/* Salesperson Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'salesperson' ? null : 'salesperson')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedOwners.length > 0 ? '#00BBA2' : activeFilterType === 'salesperson' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: selectedOwners.length > 0 ? 'white' : activeFilterType === 'salesperson' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'salesperson' && selectedOwners.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Lead Owner{selectedOwners.length > 0 && ` (${selectedOwners.length})`}
              </button>

              {/* Service Lines Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'serviceline' ? null : 'serviceline')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedServiceLines.length > 0 ? '#00BBA2' : activeFilterType === 'serviceline' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: selectedServiceLines.length > 0 ? 'white' : activeFilterType === 'serviceline' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'serviceline' && selectedServiceLines.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Service Line{selectedServiceLines.length > 0 && ` (${selectedServiceLines.length})`}
              </button>

              {/* Industry Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'industry' ? null : 'industry')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedIndustries.length > 0 ? '#00BBA2' : activeFilterType === 'industry' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: selectedIndustries.length > 0 ? 'white' : activeFilterType === 'industry' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'industry' && selectedIndustries.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Industry{selectedIndustries.length > 0 && ` (${selectedIndustries.length})`}
              </button>

              {/* City Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'city' ? null : 'city')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedCities.length > 0 ? '#00BBA2' : activeFilterType === 'city' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: selectedCities.length > 0 ? 'white' : activeFilterType === 'city' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'city' && selectedCities.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                City{selectedCities.length > 0 && ` (${selectedCities.length})`}
              </button>

              {/* Lead Generator Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'leadgenerator' ? null : 'leadgenerator')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedLeadGenerators.length > 0 ? '#00BBA2' : activeFilterType === 'leadgenerator' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: selectedLeadGenerators.length > 0 ? 'white' : activeFilterType === 'leadgenerator' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'leadgenerator' && selectedLeadGenerators.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Lead Generator{selectedLeadGenerators.length > 0 && ` (${selectedLeadGenerators.length})`}
              </button>

              {/* Lead Supporter Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'leadsupporter' ? null : 'leadsupporter')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedLeadSupporters.length > 0 ? '#00BBA2' : activeFilterType === 'leadsupporter' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: selectedLeadSupporters.length > 0 ? 'white' : activeFilterType === 'leadsupporter' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'leadsupporter' && selectedLeadSupporters.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Lead Supporter{selectedLeadSupporters.length > 0 && ` (${selectedLeadSupporters.length})`}
              </button>

              {/* Lead Rating Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'leadrating' ? null : 'leadrating')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedLeadRatings.length > 0 ? '#00BBA2' : activeFilterType === 'leadrating' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: selectedLeadRatings.length > 0 ? 'white' : activeFilterType === 'leadrating' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'leadrating' && selectedLeadRatings.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Lead Rating{selectedLeadRatings.length > 0 && ` (${selectedLeadRatings.length})`}
              </button>

              {/* Entity Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'entity' ? null : 'entity')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedEntities.length > 0 ? '#00BBA2' : activeFilterType === 'entity' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: selectedEntities.length > 0 ? 'white' : activeFilterType === 'entity' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'entity' && selectedEntities.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Entity{selectedEntities.length > 0 && ` (${selectedEntities.length})`}
              </button>

              {/* Tags Filter */}
              {safeTags.length > 0 && (
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'tags' ? null : 'tags')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors flex items-center gap-0.5"
                style={{
                  backgroundColor: safeSelectedTags.length > 0 ? '#00BBA2' : activeFilterType === 'tags' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: safeSelectedTags.length > 0 ? 'white' : activeFilterType === 'tags' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'tags' && safeSelectedTags.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                <Tag size={12} />
                Tags{safeSelectedTags.length > 0 && ` (${safeSelectedTags.length})`}
              </button>
              )}

              {/* Date Filter */}
              <button
                onClick={() => setActiveFilterType(activeFilterType === 'daterange' ? null : 'daterange')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors flex items-center gap-0.5"
                style={{
                  backgroundColor: (customDateFrom || customDateTo) ? '#00BBA2' : activeFilterType === 'daterange' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                  color: (customDateFrom || customDateTo) ? 'white' : activeFilterType === 'daterange' ? '#009E8A' : '#374151',
                  border: activeFilterType === 'daterange' && !(customDateFrom || customDateTo) ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                <Calendar size={12} />
                {(customDateFrom || customDateTo) ? (selectedDateFilter === 'fy' ? 'FY' : selectedDateFilter === 'quarter' ? 'Q' : selectedDateFilter === 'last30' ? '30d' : selectedDateFilter === 'last7' ? '7d' : 'Date') : 'Date'}
              </button>

              {/* Review Items Filter */}
              <button
                onClick={() => setShowReviewOnly(!showReviewOnly)}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1"
                style={{
                  backgroundColor: showReviewOnly ? '#f59e0b' : '#f3f4f6',
                  color: showReviewOnly ? 'white' : 'var(--dm-btn-text)',
                  border: showReviewOnly ? '1px solid #d97706' : '1px solid transparent'
                }}
              >
                <Flag size={12} className={showReviewOnly ? 'fill-white' : ''} />
                Review Items
              </button>

              {/* Clear Filters */}
              {hasOppActiveFilters && (
                <button
                  onClick={clearOppAllFilters}
                  className="px-2.5 py-1 rounded text-xs font-medium ml-1"
                  style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}
                >
                  Clear
                </button>
              )}
              {/* Qualify-from-lead hint */}
              <span className="ml-auto flex-shrink-0 inline-flex items-center gap-1 text-[10px] text-gray-400" title="Opportunities can only be created by qualifying a lead from the Leads tab">
                <ArrowRight size={11} className="text-pierian" />
                Create via <strong className="text-pierian">Leads</strong>
              </span>
            </div>

              {/* Filter Options - Show when a filter type is selected (multi-select toggle) */}
              {activeFilterType === 'geography' && (() => {
                const crossFiltered = getFilteredOppsExcluding('geography');
                const geoWithCounts = uniqueGeographies
                  .map(geo => ({ value: geo, count: crossFiltered.filter(o => o.geography === geo).length }))
                  .filter(g => g.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedFilterOptions['geography'];
                const visibleItems = isExpanded ? geoWithCounts : geoWithCounts.slice(0, 8);
                const hiddenCount = geoWithCounts.length - 8;

                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {visibleItems.map(({ value: geo, count }) => {
                      const isSelected = selectedGeographies.includes(geo);
                      return (
                        <button
                          key={geo}
                          onClick={() => toggleOppFilterSelection('geography', geo)}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: isSelected ? '#00BBA2' : 'white',
                            color: isSelected ? 'white' : 'var(--dm-btn-text)',
                            border: isSelected ? 'none' : '1px solid #d1d5db'
                          }}
                        >
                          {geo} ({count})
                        </button>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <button
                        onClick={() => setExpandedFilterOptions(prev => ({ ...prev, geography: !prev.geography }))}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                      >
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeFilterType === 'salesperson' && (() => {
                const crossFiltered = getFilteredOppsExcluding('salesperson');
                const ownerWithCounts = uniqueOwners
                  .map(owner => ({ value: owner, count: crossFiltered.filter(o => o.accountOwner === owner).length }))
                  .filter(o => o.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedFilterOptions['salesperson'];
                const visibleItems = isExpanded ? ownerWithCounts : ownerWithCounts.slice(0, 8);
                const hiddenCount = ownerWithCounts.length - 8;

                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {visibleItems.map(({ value: owner, count }) => {
                      const isSelected = selectedOwners.includes(owner);
                      return (
                        <button
                          key={owner}
                          onClick={() => toggleOppFilterSelection('salesperson', owner)}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: isSelected ? '#00BBA2' : 'white',
                            color: isSelected ? 'white' : 'var(--dm-btn-text)',
                            border: isSelected ? 'none' : '1px solid #d1d5db'
                          }}
                        >
                          {owner} ({count})
                        </button>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <button
                        onClick={() => setExpandedFilterOptions(prev => ({ ...prev, salesperson: !prev.salesperson }))}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                      >
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeFilterType === 'serviceline' && (() => {
                const crossFiltered = getFilteredOppsExcluding('serviceline');
                const slWithCounts = uniqueServiceLines
                  .map(sl => ({ value: sl, count: crossFiltered.filter(o => o.serviceLine?.trim().toLowerCase() === sl.trim().toLowerCase()).length }))
                  .filter(s => s.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedFilterOptions['serviceline'];
                const visibleItems = isExpanded ? slWithCounts : slWithCounts.slice(0, 8);
                const hiddenCount = slWithCounts.length - 8;

                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {visibleItems.map(({ value: sl, count }) => {
                      const isSelected = selectedServiceLines.includes(sl);
                      return (
                        <button
                          key={sl}
                          onClick={() => toggleOppFilterSelection('serviceline', sl)}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: isSelected ? '#00BBA2' : 'white',
                            color: isSelected ? 'white' : 'var(--dm-btn-text)',
                            border: isSelected ? 'none' : '1px solid #d1d5db'
                          }}
                        >
                          {sl} ({count})
                        </button>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <button
                        onClick={() => setExpandedFilterOptions(prev => ({ ...prev, serviceline: !prev.serviceline }))}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                      >
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeFilterType === 'industry' && (() => {
                const crossFiltered = getFilteredOppsExcluding('industry');
                const indWithCounts = uniqueIndustries
                  .map(ind => ({ value: ind, count: crossFiltered.filter(o => o.industry === ind).length }))
                  .filter(i => i.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedFilterOptions['industry'];
                const visibleItems = isExpanded ? indWithCounts : indWithCounts.slice(0, 8);
                const hiddenCount = indWithCounts.length - 8;
                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {visibleItems.map(({ value: ind, count }) => {
                      const isSelected = selectedIndustries.includes(ind);
                      return (
                        <button key={ind} onClick={() => toggleOppFilterSelection('industry', ind)}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: isSelected ? '#00BBA2' : 'white', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                          {ind} ({count})
                        </button>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <button onClick={() => setExpandedFilterOptions(prev => ({ ...prev, industry: !prev.industry }))}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}>
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeFilterType === 'city' && (() => {
                const crossFiltered = getFilteredOppsExcluding('city');
                const cityWithCounts = uniqueOppCities
                  .map(c => ({ value: c, count: crossFiltered.filter(o => o.city === c).length }))
                  .filter(c => c.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedFilterOptions['city'];
                const visibleItems = isExpanded ? cityWithCounts : cityWithCounts.slice(0, 8);
                const hiddenCount = cityWithCounts.length - 8;
                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {visibleItems.map(({ value: c, count }) => {
                      const isSelected = selectedCities.includes(c);
                      return (
                        <button key={c} onClick={() => toggleOppFilterSelection('city', c)}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: isSelected ? '#00BBA2' : 'white', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                          {c} ({count})
                        </button>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <button onClick={() => setExpandedFilterOptions(prev => ({ ...prev, city: !prev.city }))}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}>
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeFilterType === 'leadgenerator' && (() => {
                const crossFiltered = getFilteredOppsExcluding('leadgenerator');
                const lgWithCounts = uniqueOppLeadGenerators
                  .map(lg => ({ value: lg, count: crossFiltered.filter(o => o.leadGenerator === lg).length }))
                  .filter(lg => lg.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedFilterOptions['leadgenerator'];
                const visibleItems = isExpanded ? lgWithCounts : lgWithCounts.slice(0, 8);
                const hiddenCount = lgWithCounts.length - 8;
                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {visibleItems.map(({ value: lg, count }) => {
                      const isSelected = selectedLeadGenerators.includes(lg);
                      return (
                        <button key={lg} onClick={() => toggleOppFilterSelection('leadgenerator', lg)}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: isSelected ? '#00BBA2' : 'white', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                          {lg} ({count})
                        </button>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <button onClick={() => setExpandedFilterOptions(prev => ({ ...prev, leadgenerator: !prev.leadgenerator }))}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}>
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeFilterType === 'leadsupporter' && (() => {
                const crossFiltered = getFilteredOppsExcluding('leadsupporter');
                const lsWithCounts = uniqueOppLeadSupporters
                  .map(ls => ({ value: ls, count: crossFiltered.filter(o => o.leadSupporter === ls).length }))
                  .filter(ls => ls.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedFilterOptions['leadsupporter'];
                const visibleItems = isExpanded ? lsWithCounts : lsWithCounts.slice(0, 8);
                const hiddenCount = lsWithCounts.length - 8;
                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {visibleItems.map(({ value: ls, count }) => {
                      const isSelected = selectedLeadSupporters.includes(ls);
                      return (
                        <button key={ls} onClick={() => toggleOppFilterSelection('leadsupporter', ls)}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: isSelected ? '#00BBA2' : 'white', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                          {ls} ({count})
                        </button>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <button onClick={() => setExpandedFilterOptions(prev => ({ ...prev, leadsupporter: !prev.leadsupporter }))}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}>
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeFilterType === 'leadrating' && (
                <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                  {(['Hot', 'Warm', 'Cold'] as const).map((rating) => {
                    const isSelected = selectedLeadRatings.includes(rating);
                    return (
                      <button
                        key={rating}
                        onClick={() => toggleOppFilterSelection('leadrating', rating)}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: isSelected ? '#00BBA2' : 'white',
                          color: isSelected ? 'white' : 'var(--dm-btn-text)',
                          border: isSelected ? 'none' : '1px solid #d1d5db'
                        }}
                      >
                        {rating}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Date Range Filter Options */}
              {activeFilterType === 'daterange' && (
                <div className="flex flex-wrap gap-1 mt-1 pt-1 border-t border-gray-100 items-center animate-fadeIn">
                  {[
                    { value: 'fy', label: getCurrentFYLabel() },
                    { value: 'quarter', label: 'Quarter' },
                    { value: 'last30', label: '30d' },
                    { value: 'last7', label: '7d' }
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => applyDatePreset(value)}
                      className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                      style={{
                        backgroundColor: selectedDateFilter === value ? '#4b5563' : 'var(--dm-btn-bg)',
                        color: selectedDateFilter === value ? 'white' : 'var(--dm-btn-text)',
                        border: selectedDateFilter === value ? 'none' : '1px solid #d1d5db'
                      }}
                    >
                      {label}
                    </button>
                  ))}
                  <span className="text-gray-300 mx-0.5">|</span>
                  <div className="flex items-center gap-1">
                    <input
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => {
                        setCustomDateFrom(e.target.value);
                        setSelectedDateFilter('custom');
                      }}
                      className="px-1 py-0.5 text-[10px] border border-gray-300 rounded focus:ring-1 focus:ring-pierian"
                      style={{ width: '110px' }}
                    />
                    <span className="text-[10px] text-gray-400">-</span>
                    <input
                      type="date"
                      value={customDateTo}
                      onChange={(e) => {
                        setCustomDateTo(e.target.value);
                        setSelectedDateFilter('custom');
                      }}
                      className="px-1 py-0.5 text-[10px] border border-gray-300 rounded focus:ring-1 focus:ring-pierian"
                      style={{ width: '110px' }}
                    />
                    {(customDateFrom || customDateTo) && (
                      <button
                        onClick={() => {
                          setCustomDateFrom('');
                          setCustomDateTo('');
                          setSelectedDateFilter(null);
                        }}
                        className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Entity Filter Options */}
              {activeFilterType === 'entity' && (() => {
                const crossFiltered = getFilteredOppsExcluding('entity');
                const entityWithCounts = entities
                  .filter(e => e.isActive)
                  .map(entity => ({
                    value: entity.name,
                    count: crossFiltered.filter(o => o.entity === entity.name).length
                  }))
                  .filter(e => e.count > 0)
                  .sort((a, b) => b.count - a.count);
                const blankEntityCount = crossFiltered.filter(o => !o.entity || o.entity.trim() === '').length;
                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {blankEntityCount > 0 && (() => {
                      const isSelected = selectedEntities.includes('Blank');
                      return (
                        <button key="blank-entity"
                          onClick={() => setSelectedEntities(prev => prev.includes('Blank') ? prev.filter(e => e !== 'Blank') : [...prev, 'Blank'])}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: isSelected ? '#6b7280' : 'var(--dm-btn-bg)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid var(--dm-border-2)', fontStyle: 'italic' }}>
                          Blank
                        </button>
                      );
                    })()}
                    {entityWithCounts.map(({ value: entityName, count }) => {
                      const isSelected = selectedEntities.includes(entityName);
                      return (
                        <button key={entityName}
                          onClick={() => setSelectedEntities(prev => prev.includes(entityName) ? prev.filter(e => e !== entityName) : [...prev, entityName])}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: isSelected ? '#00BBA2' : 'var(--dm-btn-bg)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid var(--dm-border-2)' }}>
                          {entityName}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Tags Filter Options */}
              {activeFilterType === 'tags' && safeTags.length > 0 && (() => {
                // Get all available tags and count opportunities that have each tag
                const crossFiltered = getFilteredOppsExcluding('tags');
                const tagWithCounts = safeTags
                  .filter(tag => tag && tag.isActive)
                  .map(tag => ({
                    ...tag,
                    count: crossFiltered.filter(o =>
                      o.tags && o.tags.some(t => t.id === tag.id)
                    ).length
                  }))
                  .filter(t => t.count > 0)
                  .sort((a, b) => b.count - a.count);

                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {tagWithCounts.length === 0 ? (
                      <span className="text-xs text-gray-400 italic">No tags assigned to open opportunities</span>
                    ) : (
                      tagWithCounts.map(tag => {
                        const isSelected = safeSelectedTags.includes(tag.id);
                        return (
                          <button
                            key={tag.id}
                            onClick={() => toggleOppFilterSelection('tags', tag.id)}
                            className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1"
                            style={{
                              backgroundColor: isSelected ? tag.color : 'white',
                              color: isSelected ? 'white' : 'var(--dm-btn-text)',
                              border: isSelected ? 'none' : `1px solid ${tag.color}`
                            }}
                          >
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: isSelected ? 'white' : tag.color }}
                            />
                            {tag.name}
                          </button>
                        );
                      })
                    )}
                  </div>
                );
              })()}

              {/* Active Filters Display - Compact */}
              {hasOppActiveFilters && (
                <div className="mt-1 pt-1 border-t border-gray-100">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-[10px] font-medium text-gray-400">Filtering:</span>
                    {selectedGeographies.map(geo => (
                      <span key={`geo-${geo}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                        {geo}
                        <button onClick={() => toggleOppFilterSelection('geography', geo)} className="hover:text-pierian">×</button>
                      </span>
                    ))}
                    {selectedOwners.map(owner => (
                      <span key={`owner-${owner}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                        {owner}
                        <button onClick={() => toggleOppFilterSelection('salesperson', owner)} className="hover:text-pierian">×</button>
                      </span>
                    ))}
                    {selectedServiceLines.map(sl => (
                      <span key={`sl-${sl}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                        {sl}
                        <button onClick={() => toggleOppFilterSelection('serviceline', sl)} className="hover:text-pierian">×</button>
                      </span>
                    ))}
                    {selectedLeadRatings.map(rating => (
                      <span key={`lr-${rating}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                        {rating}
                        <button onClick={() => toggleOppFilterSelection('leadrating', rating)} className="hover:text-pierian">×</button>
                      </span>
                    ))}
                    {selectedEntityGroups.map(group => (
                      <span key={`eg-${group}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                            style={{
                              backgroundColor: 'rgba(0,187,162,0.12)',
                              color: '#007A6A'
                            }}>
                        {group}
                        <button onClick={() => setSelectedEntityGroups(prev => prev.filter(g => g !== group))} className="hover:opacity-70">×</button>
                      </span>
                    ))}
                    {selectedEntities.map(entity => (
                      <span key={`ent-${entity}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                            style={{
                              backgroundColor: entity === 'Blank' ? '#e5e7eb' : '#e0f2fe',
                              color: entity === 'Blank' ? '#6b7280' : '#0369a1',
                              fontStyle: entity === 'Blank' ? 'italic' : 'normal'
                            }}>
                        {entity}
                        <button onClick={() => setSelectedEntities(prev => prev.filter(e => e !== entity))} className="hover:opacity-70">×</button>
                      </span>
                    ))}
                    {selectedIndustries.map(ind => (
                      <span key={`ind-${ind}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                        {ind}
                        <button onClick={() => toggleOppFilterSelection('industry', ind)} className="hover:text-pierian">×</button>
                      </span>
                    ))}
                    {selectedCities.map(c => (
                      <span key={`city-${c}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                        {c}
                        <button onClick={() => toggleOppFilterSelection('city', c)} className="hover:text-pierian">×</button>
                      </span>
                    ))}
                    {selectedLeadGenerators.map(lg => (
                      <span key={`lg-${lg}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                        {lg}
                        <button onClick={() => toggleOppFilterSelection('leadgenerator', lg)} className="hover:text-pierian">×</button>
                      </span>
                    ))}
                    {selectedLeadSupporters.map(ls => (
                      <span key={`ls-${ls}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                        {ls}
                        <button onClick={() => toggleOppFilterSelection('leadsupporter', ls)} className="hover:text-pierian">×</button>
                      </span>
                    ))}
                    {safeSelectedTags.map(tagId => {
                      const tag = safeTags.find(t => t.id === tagId);
                      return tag ? (
                        <span key={`tag-${tagId}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                              style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                          {tag.name}
                          <button onClick={() => toggleOppFilterSelection('tags', tagId)} className="hover:opacity-70">×</button>
                        </span>
                      ) : null;
                    })}
                    {(customDateFrom || customDateTo) && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                        {selectedDateFilter === 'fy' ? 'FY' : selectedDateFilter === 'quarter' ? 'Q' : selectedDateFilter === 'last30' ? '30d' : selectedDateFilter === 'last7' ? '7d' : 'Custom'}
                        <button onClick={() => { setSelectedDateFilter(null); setCustomDateFrom(''); setCustomDateTo(''); }} className="hover:text-pierian">×</button>
                      </span>
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Summary Section */}
          <Card className="p-1.5 mt-1">
          {(() => {
            // Calculate opportunities WITHOUT summary stage filter for accurate counts
            // Start with user-accessible opportunities only
            const summaryOpportunities = userAccessibleOpportunities.filter(opp => {
              // Exclude leads - only show qualified opportunities (same logic as filteredOpportunities)
              const isQualifiedOpportunity = opp.salesStage !== 'Assessment' &&
                                             !opp.salesStage?.toLowerCase().includes('lead');

              const matchesSearch = opp.opportunityName?.toLowerCase().includes(searchOpp.toLowerCase()) ||
                                  opp.associatedAccount?.toLowerCase().includes(searchOpp.toLowerCase());
              const matchesOwner = selectedOwners.length === 0 || selectedOwners.includes(opp.accountOwner);
              const matchesGeography = selectedGeographies.length === 0 || selectedGeographies.includes(opp.geography || 'Domestic');
              const matchesServiceLine = selectedServiceLines.length === 0 || selectedServiceLines.some(sl => sl.trim().toLowerCase() === (opp.serviceLine || '').trim().toLowerCase());
              const matchesSalesStage = selectedSalesStages.length === 0 || selectedSalesStages.includes(opp.salesStage);

              const matchesSalesStatus = selectedLeadRatings.length === 0 || selectedLeadRatings.includes(opp.leadRating || '');

              // Entity filter matching
              const matchesEntityGroup = selectedEntityGroups.length === 0 || (() => {
                if (!opp.entity) {
                  return selectedEntityGroups.includes('Ungrouped');
                }
                const entityData = safeEntities.find(e => e.name === opp.entity);
                if (!entityData) {
                  return selectedEntityGroups.includes('Ungrouped');
                }
                if (!entityData.entityGroup || entityData.entityGroup === '') {
                  return selectedEntityGroups.includes('Ungrouped');
                }
                return selectedEntityGroups.includes(entityData.entityGroup);
              })();
              const matchesEntity = selectedEntities.length === 0 || (() => {
                if (selectedEntities.includes('Blank') && (!opp.entity || opp.entity.trim() === '')) {
                  return true;
                }
                const actualEntities = selectedEntities.filter(e => e !== 'Blank');
                if (actualEntities.length > 0 && opp.entity && actualEntities.includes(opp.entity)) {
                  return true;
                }
                if (selectedEntities.length === 1 && selectedEntities.includes('Blank')) {
                  return false;
                }
                return actualEntities.length === 0;
              })();

              // Apply date filter
              const matchesDate = matchesDateFilter(opp);

              // Apply tags filter
              const matchesTags = safeSelectedTags.length === 0 ||
                (opp.tags && opp.tags.some(t => safeSelectedTags.includes(t.id)));

              // Apply review filter
              const matchesReviewFilter = !showReviewOnly || opp.markedForReview === true;

              const matchesIndustry = selectedIndustries.length === 0 || selectedIndustries.includes(opp.industry);
              const matchesCity = selectedCities.length === 0 || selectedCities.includes(opp.city);
              const matchesLeadGenerator = selectedLeadGenerators.length === 0 || selectedLeadGenerators.includes(opp.leadGenerator);
              const matchesLeadSupporter = selectedLeadSupporters.length === 0 || selectedLeadSupporters.includes(opp.leadSupporter);

              const matchesMyOpps = !showMyOppsOnly || !currentUser?.name || opp.accountOwner === currentUser.name;
              return isQualifiedOpportunity && matchesSearch && matchesOwner && matchesGeography &&
                     matchesServiceLine && matchesSalesStage && matchesSalesStatus && matchesEntityGroup && matchesEntity && matchesDate && matchesTags && matchesReviewFilter && matchesIndustry && matchesCity && matchesLeadGenerator && matchesLeadSupporter && matchesMyOpps;
            });

            // Define all opportunity stages in the pipeline with Pierian brand colors
            const pipelineStages = [
              { stage: 'Qualified', displayName: 'Qualified', bgColor: isDark ? 'rgba(106,124,165,0.42)' : 'rgba(106,124,165,0.18)' },
              { stage: 'Discovery', displayName: 'Discovery', bgColor: isDark ? 'rgba(0,187,162,0.25)' : 'rgba(0,187,162,0.08)' },
              { stage: 'Proposal', displayName: 'Proposal', bgColor: isDark ? 'rgba(200,100,90,0.38)' : '#E9C1BE' },
              { stage: 'Contract', displayName: 'Contract', bgColor: isDark ? 'rgba(16,185,129,0.28)' : '#d1fae5' },
              { stage: 'Closed-Won', displayName: 'Closed-Won', bgColor: isDark ? 'rgba(134,188,134,0.32)' : '#C7D8C1' },
              { stage: 'Closed-Lost', displayName: 'Closed-Lost', bgColor: isDark ? 'rgba(209,86,86,0.38)' : 'rgba(209,86,86,0.12)' },
              { stage: 'Deferred', displayName: 'Deferred', bgColor: isDark ? 'rgba(156,163,175,0.35)' : 'rgba(156,163,175,0.18)' },
              { stage: 'Did not pursue further', displayName: 'Did Not Pursue', bgColor: isDark ? 'rgba(107,114,128,0.35)' : 'rgba(107,114,128,0.15)' }
            ];

            // Get the list of valid stage names
            const validStages = pipelineStages.map(s => s.stage);

            // Only count opportunities that have one of the 10 defined stages
            const validSummaryOpportunities = summaryOpportunities.filter(opp =>
              validStages.includes(opp.salesStage)
            );

            // Calculate overall totals (include ALL stages in the summary count for accurate totals)
            const overallTotal = validSummaryOpportunities.length;
            // Open Opp. total: exclude only Closed-Won and Closed-Lost
            const PIPELINE_CLOSED = ['Closed-Won', 'Closed-Lost'];
            const pipelineTotal = validSummaryOpportunities.filter(opp => !PIPELINE_CLOSED.includes(opp.salesStage)).length;
            const overallValue = validSummaryOpportunities.filter(opp => !PIPELINE_CLOSED.includes(opp.salesStage)).reduce((sum, opp) => sum + (opp.annualContractValue || 0), 0);

            // Active count comes directly from filteredOpportunities (same source as the table)
            // so the pill count always matches exactly what is shown when Active is clicked.
            // filteredOpportunities already excludes Closed-Won/Closed-Lost (matchesClosedFilter)
            // and Inactive (showInactive is now only true when Inactive status pill is selected).
            const activeOpps = filteredOpportunities.filter(opp =>
              opp.salesStage !== 'Closed-Won' &&
              opp.salesStage !== 'Closed-Lost' &&
              getEffectiveSalesStatus(opp) !== 'Inactive'
            );
            const activeTotal = activeOpps.length;
            const activeValue = activeOpps.reduce((sum, opp) => sum + (opp.annualContractValue || 0), 0);

            // Calculate stage totals (excluding inactive opportunities from both counts and values)
            const stageCounts = {};
            validSummaryOpportunities.forEach(opp => {
              const stage = opp.salesStage;
              const isInactive = getEffectiveSalesStatus(opp) === 'Inactive';
              if (!stageCounts[stage]) {
                stageCounts[stage] = { count: 0, value: 0, inactiveCount: 0 };
              }
              if (isInactive) {
                stageCounts[stage].inactiveCount++;
              } else {
                stageCounts[stage].count++;
                stageCounts[stage].value += (opp.annualContractValue || 0);
              }
            });

            // Prepare stages with counts (show all stages even if 0)
            // Count excludes inactive; inactiveCount tracked separately for display
            const totalActiveCount = Object.values(stageCounts).reduce((sum: number, s: any) => sum + s.count, 0);
            const stagesWithData = pipelineStages.map(({ stage, displayName, bgColor }) => {
              const stageData = stageCounts[stage] || { count: 0, value: 0, inactiveCount: 0 };
              const percentage = totalActiveCount > 0 ? (stageData.count / totalActiveCount * 100).toFixed(0) : 0;
              return {
                stage,
                displayName,
                bgColor,
                count: stageData.count,
                inactiveCount: stageData.inactiveCount,
                value: stageData.value,
                percentage
              };
            });

            // Verification: Sum of active stage counts + inactive counts should equal overallTotal
            const verificationActiveCount = stagesWithData.reduce((sum, stage) => sum + stage.count, 0);
            const verificationInactiveCount = stagesWithData.reduce((sum, stage) => sum + stage.inactiveCount, 0);
            const verificationTotalCount = verificationActiveCount + verificationInactiveCount;
            const verificationValue = stagesWithData.reduce((sum, stage) => sum + stage.value, 0);
            // Calculate expected value excluding inactive opportunities
            const expectedValue = validSummaryOpportunities
              .filter(opp => getEffectiveSalesStatus(opp) !== 'Inactive')
              .reduce((sum, opp) => sum + (opp.annualContractValue || 0), 0);

            // Log verification (for debugging)
            if (verificationTotalCount !== overallTotal) {
              console.warn('Opportunities Count mismatch:', {
                overallTotal,
                verificationTotalCount,
                activeCount: verificationActiveCount,
                inactiveCount: verificationInactiveCount,
                difference: overallTotal - verificationTotalCount
              });
            }
            if (Math.abs(verificationValue - expectedValue) > 0.01) {
              console.warn('Opportunities Value mismatch:', {
                expectedValue: expectedValue.toFixed(2),
                verificationValue: verificationValue.toFixed(2),
                difference: (expectedValue - verificationValue).toFixed(2)
              });
            }

            return (
              <div className="w-full overflow-x-auto">
                {/* Pipeline Stage Cards - Horizontal Scroll */}
                <div className="flex gap-1.5 items-start min-w-max pb-0.5">
                  {/* All Open Opps Box */}
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => setSummaryStageFilter('all')}
                      className={`border rounded-lg px-2 py-0.5 text-center transition-all shadow-sm ${
                        summaryStageFilter === 'all'
                          ? 'ring-2'
                          : 'hover:bg-gray-100'
                      }`}
                      style={{
                        backgroundColor: summaryStageFilter === 'all' ? '#00BBA2' : 'var(--dm-btn-bg)',
                        borderColor: summaryStageFilter === 'all' ? '#009E8A' : '#e5e7eb',
                        ringColor: summaryStageFilter === 'all' ? '#009E8A' : undefined
                      }}
                    >
                      <p className="text-[10px] font-semibold" style={{ color: summaryStageFilter === 'all' ? '#ffffff' : 'var(--dm-btn-text)' }}>All Open Opps</p>
                      <span className="text-sm font-bold block leading-tight" style={{ color: summaryStageFilter === 'all' ? '#ffffff' : 'var(--dm-text)' }}>{activeTotal}</span>
                    </button>
                    <span className="text-[10px] font-bold mt-0.5" style={{ color: '#059669' }}>₹{(activeValue / 1000000).toFixed(2)}M</span>
                  </div>

                  {/* Stage Cards - Horizontal scrollable */}
                  {stagesWithData.map(({ stage, displayName, bgColor, count, inactiveCount, value, percentage }) => {
                    const isSelected = summaryStageFilter === stage;

                    return (
                      <div key={stage} className="flex flex-col items-center">
                        <button
                          onClick={() => setSummaryStageFilter(stage)}
                          className={`rounded-lg px-2 py-0.5 text-center hover:shadow-md transition-all min-w-[60px] ${
                            isSelected ? 'shadow-lg ring-2' : ''
                          }`}
                          style={{
                            backgroundColor: isSelected ? '#00BBA2' : bgColor,
                            borderColor: isSelected ? '#009E8A' : 'transparent',
                            ringColor: isSelected ? '#009E8A' : undefined
                          }}
                        >
                          <span className="text-[10px] font-semibold block leading-tight" style={{ color: isSelected ? '#ffffff' : 'var(--dm-btn-text)' }}>{displayName}</span>
                          <span className="text-sm font-bold block leading-tight" style={{ color: isSelected ? '#ffffff' : 'var(--dm-text)' }}>{count}</span>
                        </button>
                        <div className="flex gap-1.5 mt-0.5 text-[10px]">
                          <span className="font-semibold" style={{ color: '#6b7280' }}>{percentage}%</span>
                          <span className="font-bold" style={{ color: '#059669' }}>₹{value >= 1000000 ? (value / 1000000).toFixed(1) + 'M' : (value / 1000).toFixed(0) + 'K'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          </Card>

        </div>

        {/* Table Container */}
        <Card className="mt-1 overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 240px)' }}>
          <table className="w-full text-sm" style={{tableLayout: 'fixed', minWidth: '1000px'}}>
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200" style={{ backgroundColor: '#131A25' }}>
                <th className="w-56 px-2 py-2.5 text-left">
                  <button
                    onClick={() => handleSort('opportunityName')}
                    className="flex items-center gap-1 text-xs font-semibold text-white hover:text-gray-300"
                  >
                    Opportunity Name
                    {sortField === 'opportunityName' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-20 px-2 py-2.5 text-left">
                  <button
                    onClick={() => handleSort('salesStage')}
                    className="flex items-center gap-1 text-xs font-semibold text-white hover:text-gray-300"
                  >
                    Stage
                    {sortField === 'salesStage' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-14 px-2 py-2.5 text-right">
                  <button
                    onClick={() => handleSort('annualContractValue')}
                    className="flex items-center justify-end gap-1 text-xs font-semibold text-white hover:text-gray-300 w-full"
                  >
                    ACV
                    {sortField === 'annualContractValue' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-14 px-2 py-2.5 text-right">
                  <button
                    onClick={() => handleSort('totalContractValue')}
                    className="flex items-center justify-end gap-1 text-xs font-semibold text-white hover:text-gray-300 w-full"
                  >
                    TCV
                    {sortField === 'totalContractValue' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-12 px-2 py-2.5 text-center">
                  <button
                    onClick={() => handleSort('initiationDate')}
                    className="flex items-center justify-center gap-1 text-xs font-semibold text-white hover:text-gray-300 w-full"
                    title="Shows Age for active opportunities, Close Date for Won/Lost"
                  >
                    Age
                    {sortField === 'initiationDate' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-16 px-2 py-2.5 text-center">
                  <button
                    onClick={() => handleSort('initiationDate')}
                    className="flex items-center justify-center gap-1 text-xs font-semibold text-white hover:text-gray-300 w-full"
                  >
                    Created
                    {sortField === 'initiationDate' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-16 px-2 py-2.5 text-center">
                  <button
                    onClick={() => handleSort('updatedAt')}
                    className="flex items-center justify-center gap-1 text-xs font-semibold text-white hover:text-gray-300 w-full"
                  >
                    Modified
                    {sortField === 'updatedAt' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-12 px-2 py-2.5 text-center">
                  <button
                    onClick={() => handleSort('probabilityPercent')}
                    className="flex items-center justify-center gap-1 text-xs font-semibold text-white hover:text-gray-300 w-full"
                    title="Probability %"
                  >
                    Prob
                    {sortField === 'probabilityPercent' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-20 px-2 py-2.5 text-left">
                  <button
                    onClick={() => handleSort('accountOwner')}
                    className="flex items-center gap-1 text-xs font-semibold text-white hover:text-gray-300"
                  >
                    Lead Owner
                    {sortField === 'accountOwner' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-24 px-2 py-2.5 text-left">
                  <button
                    onClick={() => handleSort('leadGenerator')}
                    className="flex items-center gap-1 text-xs font-semibold text-white hover:text-gray-300"
                  >
                    Lead Generator
                    {sortField === 'leadGenerator' && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                </th>
                <th className="w-24 px-2 py-2.5 text-center text-xs font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
                {paginatedOpps.map((opp) => (
                  <React.Fragment key={opp.opportunityId}>
                    <tr
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => openFullScreenOpp(opp)}
                    >
                      <td className="px-2 py-2" style={{ minWidth: '320px', maxWidth: '400px' }}>
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-gray-900 text-sm" title={opp.opportunityName}>{opp.opportunityName}</span>
                            {opp.tags && opp.tags.length > 0 && opp.tags.slice(0, 2).map(tag => (
                              <span
                                key={tag.id}
                                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                                style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                                title={tag.name}
                              >
                                {tag.name}
                              </span>
                            ))}
                            {opp.tags && opp.tags.length > 2 && (
                              <span className="text-[10px] text-gray-500" title={opp.tags.slice(2).map(t => t.name).join(', ')}>
                                +{opp.tags.length - 2}
                              </span>
                            )}
                          </div>
                          {opp.serviceLine && <div className="text-xs text-gray-500 leading-tight">{opp.serviceLine}</div>}
                          {(() => {
                            // Extract latest engagement from engagement_history - find the entry with the most recent date
                            const getLatestEngagement = () => {
                              if (!opp.engagementHistory) return null;

                              const entries = opp.engagementHistory.split('\n').filter(e => {
                                const t = e.trim();
                                if (!t) return false;
                                // Exclude "Marked for Review" / "Unmarked for Review" entries
                                if (/\b(Un)?[Mm]arked for Review\s*$/i.test(t)) return false;
                                return true;
                              });
                              if (entries.length === 0) return null;

                              // Parse date from entry text - supports multiple formats
                              const parseEntryDate = (text: string) => {
                                // Remove any [timestamp] prefix (handles all locale formats including 24-hour time)
                                const cleaned = text.replace(/^\[[^\]]*\]\s*/, '').trim();

                                // Try format: "14-Jan-2025" or "14 Jan 2025" or "01-Dec-05" (2-digit year)
                                const match1 = cleaned.match(/^(\d{1,2})[-\s]+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\w*[-\s]+(\d{2,4})/i);
                                if (match1) {
                                  const months = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11 };
                                  const day = parseInt(match1[1]);
                                  const month = months[match1[2].toLowerCase().substring(0, 3)];
                                  const year = parseInt(match1[3]) < 100 ? 2000 + parseInt(match1[3]) : parseInt(match1[3]);
                                  return new Date(year, month, day);
                                }

                                // Try format: "2025-01-14"
                                const match2 = cleaned.match(/^(\d{4})-(\d{2})-(\d{2})/);
                                if (match2) {
                                  return new Date(parseInt(match2[1]), parseInt(match2[2]) - 1, parseInt(match2[3]));
                                }

                                // Try "... on DD/MM/YYYY" at end (legacy conversion entries)
                                const match3 = cleaned.match(/on\s+(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/);
                                if (match3) {
                                  return new Date(parseInt(match3[3]), parseInt(match3[2]) - 1, parseInt(match3[1]));
                                }

                                return null;
                              };

                              // Find entry with most recent date
                              let latestEntry = null;
                              let latestDate = null;

                              for (const entry of entries) {
                                const trimmed = entry.trim();
                                const entryDate = parseEntryDate(trimmed);
                                if (entryDate && (!latestDate || entryDate > latestDate)) {
                                  latestDate = entryDate;
                                  latestEntry = trimmed.replace(/^\[[^\]]*\]\s*/, '').trim();
                                }
                              }

                              // Fallback: first entry if no dates found
                              if (!latestEntry && entries.length > 0) {
                                latestEntry = entries[0].trim().replace(/^\[[^\]]*\]\s*/, '').trim();
                              }

                              return latestEntry;
                            };

                            // Extract date and rest of text separately
                            const extractDateAndText = (text: string) => {
                              // Try DD-Mon-YYYY or DD-Mon-YY at start
                              const dateMatch = text.match(/^(\d{1,2}[-\s]+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\w*[-\s]+\d{2,4})\s*(.*)/i);
                              if (dateMatch) {
                                return { date: dateMatch[1], rest: dateMatch[3] || '' };
                              }
                              // Try ISO format at start
                              const isoMatch = text.match(/^(\d{4}-\d{2}-\d{2})\s*(.*)/);
                              if (isoMatch) {
                                return { date: isoMatch[1], rest: isoMatch[2] || '' };
                              }
                              // Try "... on DD/MM/YYYY" at end (legacy conversion entries)
                              const endDateMatch = text.match(/^(.*?)\s+on\s+(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/);
                              if (endDateMatch) {
                                const _ms = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                                const d = parseInt(endDateMatch[2]);
                                const m = parseInt(endDateMatch[3]) - 1;
                                const y = endDateMatch[4];
                                const formattedDate = `${d}-${_ms[m]}-${y}`;
                                return { date: formattedDate, rest: endDateMatch[1] || '' };
                              }
                              return { date: null, rest: text };
                            };

                            // Format a date string to "dd MMM" (e.g., "26 Feb")
                            const formatDatePill = (dateStr: string) => {
                              const months: Record<string, number> = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,sept:8,oct:9,nov:10,dec:11 };
                              const m1 = dateStr.match(/^(\d{1,2})[-\s]+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)/i);
                              if (m1) {
                                const d = String(parseInt(m1[1])).padStart(2, '0');
                                const mon = m1[2].substring(0, 3);
                                return `${d} ${mon.charAt(0).toUpperCase()}${mon.slice(1).toLowerCase()}`;
                              }
                              const m2 = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
                              if (m2) {
                                const ms = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                                return `${m2[3]} ${ms[parseInt(m2[2]) - 1]}`;
                              }
                              return dateStr;
                            };

                            const latestEngagement = getLatestEngagement();
                            const latestComment = opp.latestComment;
                            const displayText = latestEngagement || latestComment || '';
                            const { date, rest } = latestEngagement ? extractDateAndText(latestEngagement) : { date: null, rest: displayText };
                            const datePill = date ? formatDatePill(date) : null;

                            return displayText ? (
                              <div
                                className="text-xs text-gray-600 leading-tight"
                                title={displayText}
                                style={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {datePill && <span className="font-semibold px-1 rounded bg-[rgba(0,187,162,0.1)] text-[#006B5E]">{datePill}</span>}{datePill && ' '}
                                {rest}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 italic">No engagement history</span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="w-20 px-2 py-1.5">
                        <span
                          className="px-1 py-0.5 rounded text-xs font-medium whitespace-nowrap"
                          style={{
                            backgroundColor:
                              opp.salesStage === 'Qualified' ? 'rgba(106,124,165,0.18)' :
                              opp.salesStage === 'Discovery' ? 'rgba(0,187,162,0.08)' :
                              opp.salesStage === 'Proposal' ? '#E9C1BE' :
                              opp.salesStage === 'Closed-Won' ? '#C7D8C1' :
                              opp.salesStage === 'Closed-Lost' ? 'rgba(209,86,86,0.12)' :
                              opp.salesStage === 'Contract' ? '#d1fae5' : '#f3f4f6',
                            color:
                              opp.salesStage === 'Closed-Won' ? '#6F8F6A' :
                              opp.salesStage === 'Closed-Lost' ? '#dc2626' :
                              opp.salesStage === 'Contract' ? '#065f46' : '#374151'
                          }}
                        >
                          {opp.salesStage}
                        </span>
                      </td>
                      <td className="w-14 px-2 py-1.5 text-right font-semibold text-sage-shade text-xs" title={`₹${(opp.annualContractValue || 0).toLocaleString()}`}>
                        ₹{(opp.annualContractValue || 0) >= 1000000 ? ((opp.annualContractValue || 0) / 1000000).toFixed(1) + 'M' : ((opp.annualContractValue || 0) / 1000).toFixed(0) + 'K'}
                      </td>
                      <td className="w-14 px-2 py-1.5 text-right text-gray-600 text-xs" title={`₹${(opp.totalContractValue || 0).toLocaleString()}`}>
                        ₹{(opp.totalContractValue || 0) >= 1000000 ? ((opp.totalContractValue || 0) / 1000000).toFixed(1) + 'M' : ((opp.totalContractValue || 0) / 1000).toFixed(0) + 'K'}
                      </td>
                      <td className="w-12 px-2 py-1.5 text-center text-gray-700 text-xs">
                        {opp.salesStage === 'Closed-Won' || opp.salesStage === 'Closed-Lost' ? (
                          <span title={opp.estimatedCloseDate ? new Date(opp.estimatedCloseDate).toLocaleDateString() : ''}>
                            {opp.estimatedCloseDate ? (() => { const d = new Date(opp.estimatedCloseDate); const dd = String(d.getDate()).padStart(2,'0'); const mon = d.toLocaleString('en-US',{month:'short'}); const yr = d.getFullYear() !== new Date().getFullYear() ? '-' + String(d.getFullYear()).slice(-2) : ''; return `${dd}-${mon}${yr}`; })() : ''}
                          </span>
                        ) : (
                          (() => {
                            const ageDays = opp.initiationDate ? Math.floor((new Date() - new Date(opp.initiationDate)) / (1000 * 60 * 60 * 24)) : 0;
                            return (
                              <span
                                title={opp.initiationDate ? `Created: ${new Date(opp.initiationDate).toLocaleDateString()}` : ''}
                                className={ageDays > 120 ? 'text-pierian font-semibold' : ''}
                              >
                                {opp.initiationDate ? `${ageDays}d` : ''}
                              </span>
                            );
                          })()
                        )}
                      </td>
                      <td className="w-16 px-2 py-1.5 text-center text-gray-700 text-xs" title={opp.initiationDate ? new Date(opp.initiationDate).toLocaleDateString() : ''}>
                        {opp.initiationDate ? (() => { const d = new Date(opp.initiationDate); const dd = String(d.getDate()).padStart(2,'0'); const mon = d.toLocaleString('en-US',{month:'short'}); const yr = d.getFullYear() !== new Date().getFullYear() ? '-' + String(d.getFullYear()).slice(-2) : ''; return `${dd}-${mon}${yr}`; })() : ''}
                      </td>
                      <td className="w-16 px-2 py-1.5 text-center text-gray-700 text-xs" title={opp.updatedAt ? new Date(opp.updatedAt).toLocaleDateString() : ''}>
                        {opp.updatedAt ? (() => { const d = new Date(opp.updatedAt); const dd = String(d.getDate()).padStart(2,'0'); const mon = d.toLocaleString('en-US',{month:'short'}); const yr = d.getFullYear() !== new Date().getFullYear() ? '-' + String(d.getFullYear()).slice(-2) : ''; return `${dd}-${mon}${yr}`; })() : ''}
                      </td>
                      <td className="w-12 px-2 py-1.5 text-center text-xs">
                        <span className={`px-1 py-0.5 rounded text-xs font-medium ${
                          opp.probabilityToClose === 'High' ? 'bg-sage-tint text-sage-shade' :
                          opp.probabilityToClose === 'Medium' ? 'bg-amber-tint text-amber-shade' :
                          'bg-red-100 text-red-600'
                        }`} title={`${sanitizeProbabilityPercent(opp.probabilityPercent)}%`}>
                          {sanitizeProbabilityPercent(opp.probabilityPercent)}%
                        </span>
                      </td>
                      <td className="w-20 px-2 py-1.5 text-gray-700 text-xs truncate" title={opp.accountOwner}>{opp.accountOwner}</td>
                      <td className="w-24 px-2 py-1.5 text-gray-700 text-xs truncate" title={opp.leadGenerator || ''}>{opp.leadGenerator || '—'}</td>
                      <td className="w-24 px-2 py-1.5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const newReviewStatus = !opp.markedForReview;
                              const now = new Date();
                              const updatedEngagementHistory = prependEngagementEntry(
                                opp.engagementHistory,
                                newReviewStatus ? 'Marked for Review' : 'Unmarked for Review',
                                currentUser?.name,
                                now
                              );

                              const updatedOpp = {
                                ...opp,
                                markedForReview: newReviewStatus,
                                engagementHistory: updatedEngagementHistory,
                                lastEditedDate: now.toISOString().split('T')[0]
                              };
                              // Update local state immediately
                              setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? updatedOpp : o));
                              // Save to database using PUT endpoint
                              try {
                                console.log('🚀 Saving review status via PUT:', { id: opp.opportunityId, marked_for_review: newReviewStatus });
                                const response = await fetch(`${API_BASE_URL}/opportunities/${opp.opportunityId}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    opportunity_name: opp.opportunityName,
                                    associated_account: opp.associatedAccount,
                                    sales_stage: opp.salesStage,
                                    probability: String(opp.probability || '50%'),
                                    probability_override: opp.probabilityOverride,
                                    estimated_revenue: opp.estimatedRevenue,
                                    annual_contract_value: opp.annualContractValue,
                                    total_contract_value: opp.totalContractValue,
                                    service_line: opp.serviceLine,
                                    service_offering: opp.serviceOffering,
                                    entity: opp.entity,
                                    geography: opp.geography,
                                    industry: opp.industryVertical || opp.industry,
                                    opportunity_type: opp.opportunityType,
                                    opportunity_category: opp.opportunityCategory || 'New Business',
                                    opportunity_classification: opp.opportunityClassification || null,
                                    nature_of_work: opp.natureOfWork || null,
                                    priority: opp.priorityLevel,
                                    status: opp.salesStatus || 'Warm',
                                    sales_status_override: opp.salesStatusOverride || null,
                                    account_owner: opp.accountOwner,
                                    lead_supporter: opp.leadSupporter,
                                    solution_architect: opp.solutionArchitect || null,
                                    executive_sponsor: opp.executiveSponsor,
                                    primary_contact: opp.primaryContact,
                                    primary_contact_email: opp.primaryContactEmail,
                                    phone_number: opp.phoneNumber || null,
                                    initiation_date: opp.initiationDate,
                                    expected_close_date: opp.estimatedCloseDate || null,
                                    solution_scope: opp.solutionScope || null,
                                    engagement_history: updatedEngagementHistory,
                                    comments: opp.comments || null,
                                    latest_comment: opp.latestComment || null,
                                    last_edited_date: now.toISOString().split('T')[0],
                                    marked_for_review: newReviewStatus ? 1 : 0
                                  })
                                });
                                const responseData = await response.json();
                                console.log('📥 PUT Response:', responseData);
                                if (!response.ok || !responseData.success) {
                                  console.error('Failed to save review status:', response.status, responseData);
                                  alert(`Failed to save: ${responseData.message || response.statusText}`);
                                  // Revert local state on failure
                                  setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? opp : o));
                                } else {
                                  console.log('✅ Review status saved. DB marked_for_review:', responseData.data?.marked_for_review);
                                }
                              } catch (error) {
                                console.error('Error saving review status:', error);
                                alert(`Network error: ${error.message}`);
                                // Revert local state on failure
                                setOpportunities(opportunities.map(o => o.opportunityId === opp.opportunityId ? opp : o));
                              }
                            }}
                            className={`px-2 py-1 rounded text-white ${opp.markedForReview ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gray-300 hover:bg-gray-400'}`}
                            title={opp.markedForReview ? 'Unmark for Review' : 'Mark for Review'}
                          >
                            <Flag size={12} className={opp.markedForReview ? 'fill-white' : ''} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openFullScreenOpp(opp);
                            }}
                            className="px-2 py-1 bg-slate text-white rounded hover:bg-slate-shade"
                            title="Edit Opportunity"
                          >
                            <Edit3 size={12} />
                          </button>
                          {opp.salesStage !== 'Closed-Won' && opp.salesStage !== 'Closed-Lost' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveToNextStage(opp.opportunityId, opp);
                              }}
                              className="px-2 py-1 bg-sage text-white rounded hover:bg-sage-shade"
                              title={`Move to next stage`}
                            >
                              <ChevronsRight size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
          </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              Showing {startIndex + 1}–{Math.min(endIndex, sortedOpps.length)} of {sortedOpps.length}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-100"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let page;
                if (totalPages <= 7) page = i + 1;
                else if (currentPage <= 4) page = i + 1;
                else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
                else page = currentPage - 3 + i;
                return (
                  <button key={page} onClick={() => setCurrentPage(page)}
                    className={`px-2 py-1 text-xs border rounded ${currentPage === page ? 'text-white border-transparent' : 'border-gray-300 hover:bg-gray-100'}`}
                    style={currentPage === page ? { backgroundColor: '#00BBA2' } : {}}>{page}</button>
                );
              })}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  };
// ========================================
// MAIN CRM APPLICATION
// ========================================

// Module-level references to fetch functions so inner components can access them
// (Vite/SWC minifier can break closure references for nested component functions)
let _fetchLeads: (() => Promise<void>) | null = null;
let _fetchOpportunities: (() => Promise<void>) | null = null;

export default function CRMApplication() {
  console.log('✅ CRMApplication component loaded and rendering');

  const { theme, toggleTheme } = useTheme();
  const isDark = theme.mode === 'dark';

  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  // Sticky banner shown only when apiService confirms (via whoami) that the
  // backend session is truly gone. Never flips isAuthenticated, so the user's
  // unsaved work stays in the DOM until they click Re-login.
  const [sessionExpired, setSessionExpired] = useState(false);

  const [activeTab, setActiveTab] = useState('dashboard');
  const dashboardTabRef = React.useRef<HTMLButtonElement>(null);
  const [leads, setLeads] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showICPWizard, setShowICPWizard] = useState(false);
  const [prefilledLead, setPrefilledLead] = useState<any>(null);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [showMyProfileModal, setShowMyProfileModal] = useState(false);
  const [dismissedNotifIds, setDismissedNotifIds] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('crm_dismissed_notifs') || '[]')); }
    catch { return new Set(); }
  });

  const dismissNotif = (id: string) => {
    setDismissedNotifIds(prev => {
      const next = new Set(prev);
      next.add(id);
      localStorage.setItem('crm_dismissed_notifs', JSON.stringify([...next]));
      return next;
    });
  };

  const clearAllNotifs = (keys: string[]) => {
    setDismissedNotifIds(prev => {
      const next = new Set([...prev, ...keys]);
      localStorage.setItem('crm_dismissed_notifs', JSON.stringify([...next]));
      return next;
    });
  };
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [convertFromLead, setConvertFromLead] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFormServiceLines, setUserFormServiceLines] = useState<string[]>([]);
  const [showLeadDetails, setShowLeadDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedLeads, setExpandedLeads] = useState({});
  const [expandedOpps, setExpandedOpps] = useState({});
  const [expandedCustomers, setExpandedCustomers] = useState({});

  // AI Copilot visibility
  const [copilotVisible, setCopilotVisible] = useState(true);

  // Leads Filter State
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const [leadStatusFilter, setLeadStatusFilter] = useState('Open');
  const [leadSourceFilter, setLeadSourceFilter] = useState('all');
  const [leadViewMode, setLeadViewMode] = useState<'view' | 'edit' | null>(null);
  const [leadSelectedOwners, setLeadSelectedOwners] = useState<string[]>([]);

  // Lead view local state (lifted from LeadsView to prevent remounting on re-render)
  const [leadConverting, setLeadConverting] = useState<string | null>(null);
  const [leadCurrentPage, setLeadCurrentPage] = useState(1);

  // Lost Lead Modal state (CRM-level so both LeadsView and LeadFormModal can access)
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostLeadTarget, setLostLeadTarget] = useState<any>(null);
  const [lostReason, setLostReason] = useState('');
  const [lostComments, setLostComments] = useState('');
  const [lostSaving, setLostSaving] = useState(false);

  const openLostModal = (lead: any) => {
    setLostLeadTarget(lead);
    setLostReason('');
    setLostComments('');
    setShowLostModal(true);
  };

  // Disqualify Lead Modal state
  const [showDisqualifyModal, setShowDisqualifyModal] = useState(false);
  const [disqualifyLeadTarget, setDisqualifyLeadTarget] = useState<any>(null);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [disqualifyComments, setDisqualifyComments] = useState('');
  const [disqualifySaving, setDisqualifySaving] = useState(false);

  const openDisqualifyModal = (lead: any) => {
    setDisqualifyLeadTarget(lead);
    setDisqualifyReason('');
    setDisqualifyComments('');
    setShowDisqualifyModal(true);
  };

  // Top-level lead actions (used by both LeadsView table and LeadFormModal)
  const handleDeleteLeadAction = async (lead: any) => {
    if (!confirm(`Delete lead "${lead.companyName}"?\n\nThis will permanently remove the lead from all reports and dashboards.`)) return;
    try {
      const result = await leadsApi.delete(lead.leadId);
      if (result.success) { if (_fetchLeads) await _fetchLeads(); }
      else { alert(`Error: ${result.error || result.message || 'Failed to delete lead'}`); }
    } catch (error) { console.error('Error deleting lead:', error); alert('Error deleting lead. Please try again.'); }
  };

  const handleDisqualifyLeadAction = async () => {
    if (!disqualifyReason) { alert('Please select a reason.'); return; }
    setDisqualifySaving(true);
    const lead = disqualifyLeadTarget;
    const fullReason = disqualifyComments ? `${disqualifyReason} — ${disqualifyComments}` : disqualifyReason;
    const now = new Date();
    const updatedHistory = prependEngagementEntry(lead.engagementHistory, `Lead unqualified — ${fullReason}`, currentUser?.name, now);
    try {
      const payload = {
        lead_id: lead.leadId, company_name: lead.companyName, contact_person: lead.contactPerson,
        designation: lead.designation || null, email: lead.email, phone: lead.phone || null,
        city: lead.city || null, geography: lead.geography,
        industry: lead.industry, service_line: lead.serviceLine, service_offering: lead.serviceOffering || null,
        entity: lead.entity || null,
        pierian_geography: lead.pierianGeography || lead.geography,
        lead_source: lead.leadSource, source_comments: lead.sourceComments || null,
        lead_owner: lead.leadOwner, sales_representative: lead.leadOwner, lead_supporter: lead.leadSupporter || null,
        lead_status: 'Unqualified',
        estimated_revenue: parseFloat(lead.estimatedRevenue) || 0, budget: parseFloat(lead.budget) || 0,
        pain_points: lead.painPoints || null, comments: lead.comments || null,
        priority: lead.priority || 'Medium',
        date_captured: lead.dateCaptured || null, last_contacted: lead.lastContacted || null,
        converted_opportunity_id: lead.convertedOpportunityId || null, converted_at: lead.convertedAt || null,
        marked_for_review: lead.markedForReview || false,
        loss_reason: fullReason,
        engagement_history: updatedHistory
      };
      const result = await leadsApi.update(lead.leadId, payload);
      if (result.success) {
        setShowDisqualifyModal(false);
        setDisqualifyLeadTarget(null);
        if (_fetchLeads) await _fetchLeads();
      } else { alert(`Error: ${result.error || result.message || 'Failed to update lead'}`); }
    } catch (error) { console.error('Error updating lead:', error); alert('Error updating lead. Please try again.'); }
    setDisqualifySaving(false);
  };

  const handleMarkLost = async () => {
    if (!lostReason) { alert('Please select a reason.'); return; }
    setLostSaving(true);
    const lead = lostLeadTarget;
    const fullReason = lostComments ? `${lostReason} — ${lostComments}` : lostReason;
    const now = new Date();
    const updatedHistory = prependEngagementEntry(lead.engagementHistory, `Lead marked as Lost — ${fullReason}`, currentUser?.name, now);
    try {
      const payload = {
        lead_id: lead.leadId, company_name: lead.companyName, contact_person: lead.contactPerson,
        designation: lead.designation || null, email: lead.email, phone: lead.phone || null,
        city: lead.city || null, geography: lead.geography,
        industry: lead.industry, service_line: lead.serviceLine, service_offering: lead.serviceOffering || null,
        entity: lead.entity || null,
        pierian_geography: lead.pierianGeography || lead.geography,
        lead_source: lead.leadSource, source_comments: lead.sourceComments || null,
        lead_owner: lead.leadOwner, sales_representative: lead.leadOwner, lead_supporter: lead.leadSupporter || null,
        lead_status: 'Lost',
        estimated_revenue: parseFloat(lead.estimatedRevenue) || 0, budget: parseFloat(lead.budget) || 0,
        pain_points: lead.painPoints || null, comments: lead.comments || null,
        priority: lead.priority || 'Medium',
        date_captured: lead.dateCaptured || null, last_contacted: lead.lastContacted || null,
        converted_opportunity_id: lead.convertedOpportunityId || null, converted_at: lead.convertedAt || null,
        marked_for_review: lead.markedForReview || false,
        loss_reason: fullReason,
        engagement_history: updatedHistory
      };
      const result = await leadsApi.update(lead.leadId, payload);
      if (result.success) {
        setShowLostModal(false);
        setLostLeadTarget(null);
        if (_fetchLeads) await _fetchLeads();
      } else { alert(`Error: ${result.error || result.message || 'Failed to update lead'}`); }
    } catch (error) { console.error('Error marking lead as lost:', error); alert('Error updating lead. Please try again.'); }
    finally { setLostSaving(false); }
  };
  const [leadSelectedGeographies, setLeadSelectedGeographies] = useState<string[]>([]);
  const [leadSelectedSources, setLeadSelectedSources] = useState<string[]>([]);
  const [leadActiveFilterType, setLeadActiveFilterType] = useState<string | null>(null);
  const [leadExpandedFilterOptions, setLeadExpandedFilterOptions] = useState<{[key: string]: boolean}>({});
  const [leadShowReviewOnly, setLeadShowReviewOnly] = useState(false);
  const [leadSelectedTags, setLeadSelectedTags] = useState<number[]>([]);
  const [leadSelectedEntityGroups, setLeadSelectedEntityGroups] = useState<string[]>([]);
  const [leadSelectedEntities, setLeadSelectedEntities] = useState<string[]>([]);
  const [leadSelectedServiceLines, setLeadSelectedServiceLines] = useState<string[]>([]);
  const [leadSelectedIndustries, setLeadSelectedIndustries] = useState<string[]>([]);
  const [leadSelectedLeadGenerators, setLeadSelectedLeadGenerators] = useState<string[]>([]);
  const [leadSelectedLeadSupporters, setLeadSelectedLeadSupporters] = useState<string[]>([]);
  const [leadSelectedCities, setLeadSelectedCities] = useState<string[]>([]);
  const [leadSelectedLeadRatings, setLeadSelectedLeadRatings] = useState<string[]>([]);

  // "My Leads" / "My Opps" toggle filters - default ON so users see their own items first
  const [showMyLeadsOnly, setShowMyLeadsOnly] = useState(false);
  const [showMyOppsOnly, setShowMyOppsOnly] = useState(false);

  // Leads Sorting State - kept at CRMApplication level to prevent reset on re-render
  const [leadSortField, setLeadSortField] = useState<string | null>('age');
  const [leadSortDirection, setLeadSortDirection] = useState<'asc' | 'desc'>('asc');

  // Opportunities Sorting and Pagination State - kept at CRMApplication level to prevent reset on re-render
  const [oppSortField, setOppSortField] = useState('initiationDate');
  const [oppSortDirection, setOppSortDirection] = useState<'asc' | 'desc'>('desc');
  const [oppCurrentPage, setOppCurrentPage] = useState(1);

  // Opportunities Filter State - kept at CRMApplication level to prevent reset on re-render
  const [oppSelectedOwners, setOppSelectedOwners] = useState<string[]>([]);
  const [oppSelectedGeographies, setOppSelectedGeographies] = useState<string[]>([]);
  const [oppSelectedServiceLines, setOppSelectedServiceLines] = useState<string[]>([]);
  const [oppSelectedSalesStages, setOppSelectedSalesStages] = useState<string[]>([]);
  const [oppSelectedLeadRatings, setOppSelectedLeadRatings] = useState<string[]>([]);
  const [oppSelectedTags, setOppSelectedTags] = useState<number[]>([]);
  const [oppShowReviewOnly, setOppShowReviewOnly] = useState(false);
  const [oppSearchTerm, setOppSearchTerm] = useState('');
  const [oppActiveFilterType, setOppActiveFilterType] = useState<string | null>(null);
  const [oppExpandedFilterOptions, setOppExpandedFilterOptions] = useState<{[key: string]: boolean}>({});
  const [oppSummaryStageFilter, setOppSummaryStageFilter] = useState('all');

  // Settings Tab State - kept at CRMApplication level to prevent reset on re-render
  const [settingsTab, setSettingsTab] = useState('users');

  // Import State - kept at CRMApplication level to prevent reset on re-render
  const [importing, setImporting] = useState(false);
  const [importType, setImportType] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const isUnloadingRef = React.useRef(false);

  // Dashboard States
  const [dashboardSubTab, setDashboardSubTab] = useState('overview');
  const [activityGroupBy, setActivityGroupBy] = useState('salesperson');

  // Apply font size class to HTML element
  useEffect(() => {
    const htmlElement = document.documentElement;
    // Remove all font size classes
    htmlElement.classList.remove('font-small', 'font-medium', 'font-large');
    // Add the selected font size class
    htmlElement.classList.add(`font-${fontSize}`);
  }, [fontSize]);

  // Sync header logo width with Dashboard tab width
  useEffect(() => {
    const syncLogoWidth = () => {
      if (dashboardTabRef.current) {
        const w = dashboardTabRef.current.offsetWidth;
        document.documentElement.style.setProperty('--dashboard-tab-width', `${w}px`);
      }
    };
    syncLogoWidth();
    window.addEventListener('resize', syncLogoWidth);
    return () => window.removeEventListener('resize', syncLogoWidth);
  }, []);

  // Check SSO authentication on mount and periodically
  useEffect(() => {
    // DEV BYPASS: Skip SSO auth in development when VITE_DEV_BYPASS_AUTH is set
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
      console.log('🔧 DEV MODE: Bypassing SSO authentication');
      setCurrentUser({
        id: 'dev-user',
        name: 'Dev User',
        email: 'dev@pierian.com',
        isAdmin: true,
        geographies: [],
        role: 'Admin',
      });
      setIsAuthenticated(true);
      setAuthChecking(false);
      return;
    }

    /**
     * Attempt a single auth check against /api/auth/me.
     * Returns { authenticated, user } on success, or { authenticated: false } on failure.
     */
    const attemptAuthCheck = async (authUrl: string): Promise<{ authenticated: boolean; user?: any; message?: string }> => {
      const response = await fetch(authUrl, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        return { authenticated: false, message: `HTTP ${response.status}` };
      }

      const data = await response.json();
      if (data.authenticated && data.user) {
        return { authenticated: true, user: data.user };
      }
      return { authenticated: false, message: data.message };
    };

    /**
     * Parse geography and service_lines fields on a user object into arrays.
     */
    const parseGeographies = (user: any) => {
      if (user.geography && typeof user.geography === 'string') {
        try {
          const parsed = JSON.parse(user.geography);
          user.geographies = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          user.geographies = [user.geography];
        }
      } else {
        user.geographies = user.geographies || [];
      }
      if (user.service_lines && typeof user.service_lines === 'string') {
        try {
          const parsed = JSON.parse(user.service_lines);
          user.serviceLines = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          user.serviceLines = [user.service_lines];
        }
      } else {
        user.serviceLines = user.serviceLines || [];
      }
      return user;
    };

    const checkSSOAuth = async () => {
      try {
        console.log('🔐 Checking SSO authentication...');

        // Check if there's a sessionId in the URL (from SSO redirect)
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('sessionId');

        // Use /auth/whoami instead of /auth/me as a WAF-cache-busting fallback —
        // a prior WAF caching rule leaked identity via cached /auth/me responses.
        // Server accepts both paths; clients use /whoami to bypass any stale cache.
        let authUrl = `${API_BASE_URL}/auth/whoami`;
        if (sessionId) {
          authUrl += `?sessionId=${sessionId}`;
          console.log('🔐 Found sessionId in URL, verifying session...');
        }

        // If we have a sessionId from SSO redirect, the SSO service may not have
        // finished writing the session to Redis yet (race condition).
        // Retry up to 3 times with 800ms delay to give Redis time to propagate.
        const maxRetries = sessionId ? 3 : 1;
        let lastResult: { authenticated: boolean; user?: any; message?: string } = { authenticated: false };

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            lastResult = await attemptAuthCheck(authUrl);
            if (lastResult.authenticated) {
              break; // Success — stop retrying
            }
            if (attempt < maxRetries) {
              console.log(`🔐 Auth attempt ${attempt}/${maxRetries} not yet authenticated, retrying in 800ms...`);
              await new Promise(resolve => setTimeout(resolve, 800));
            }
          } catch (fetchErr) {
            console.warn(`🔐 Auth attempt ${attempt}/${maxRetries} network error:`, fetchErr);
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 800));
            }
          }
        }

        if (lastResult.authenticated && lastResult.user) {
          const user = parseGeographies({ ...lastResult.user });
          console.log('✅ User authenticated via SSO:', user.email);

          // Clean up sessionId from URL after successful authentication
          if (sessionId) {
            console.log('🔐 Cleaning up sessionId from URL...');
            window.history.replaceState({}, document.title, window.location.pathname);
          }

          // Batch state updates to prevent flicker
          Promise.resolve().then(() => {
            setCurrentUser(user);
            setIsAuthenticated(true);
            setAuthChecking(false);
          });
        } else {
          console.log('❌ Not authenticated:', lastResult.message);

          // Proactively clear stale CRM cookies (psc.sid) when session is expired/invalid.
          // This ensures that by the time the user sees the login page and clicks
          // "Sign in", the stale CRM cookie is already removed from the browser.
          // Note: "No session" means no cookie was sent, so no cleanup needed.
          if (lastResult.message && lastResult.message !== 'No session') {
            try {
              await fetch(`${API_BASE_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
              });
              console.log('🔐 Proactively cleared stale CRM cookies');
            } catch (cleanupErr) {
              // Non-critical — Layer 2 (SSO logout routing) handles the SSO cookie
              console.warn('⚠️ Cookie cleanup failed (non-critical):', cleanupErr);
            }
          }

          setCurrentUser(null);
          setIsAuthenticated(false);
          setAuthChecking(false);
        }
      } catch (error) {
        console.error('❌ Error checking SSO auth:', error);
        setCurrentUser(null);
        setIsAuthenticated(false);
        setAuthChecking(false);
      }
    };

    // Check auth once on mount. We deliberately do NOT poll on an interval —
    // a periodic re-check would evict the user mid-task whenever the session
    // briefly looked invalid. Sliding TTL on the backend + 401 handling in
    // apiService.ts cover the legitimate "session really expired" case.
    checkSSOAuth();
  }, []);

  // Listen for the global "session-expired" event raised by apiService when a
  // 401 is confirmed by whoami. We surface a banner instead of redirecting so
  // the user never gets bounced out of in-progress work without consenting.
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener('psc:session-expired', handler as EventListener);
    return () => window.removeEventListener('psc:session-expired', handler as EventListener);
  }, []);

  // Handle login
  const handleLogin = (user) => {
    // Parse geography to array - handles JSON arrays, plain strings, or null
    if (user.geography && typeof user.geography === 'string') {
      try {
        const parsed = JSON.parse(user.geography);
        user.geographies = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        user.geographies = [user.geography];
      }
    } else {
      user.geographies = user.geographies || [];
    }
    // Parse service_lines to array
    if (user.service_lines && typeof user.service_lines === 'string') {
      try {
        const parsed = JSON.parse(user.service_lines);
        user.serviceLines = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        user.serviceLines = [user.service_lines];
      }
    } else {
      user.serviceLines = user.serviceLines || [];
    }
    setCurrentUser(user);
    setIsAuthenticated(true);
  };

  // Handle logout — clears server session + cookie, then redirects to SSO logout
  const handleLogout = async () => {
    try {
      // 1. Clear frontend auth state immediately (prevents stale UI)
      setCurrentUser(null);
      setIsAuthenticated(false);
      setAuthChecking(true);

      // 2. Call server-side logout to destroy Redis session & clear httpOnly cookie
      //    This prevents the stale cookie from auto-authenticating on next login
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        console.log('🔐 Server-side session cleared');
      } catch (logoutErr) {
        // Server may be unreachable — proceed with SSO redirect regardless
        console.warn('⚠️ Server logout call failed (proceeding with SSO redirect):', logoutErr);
      }

      // 3. Redirect to SSO logout which clears the SSO-side session
      const redirectUrl = window.location.origin + window.location.pathname;
      const ssoLogoutUrl = `${SSO_SERVICE_URL}/sso/logout?redirect=${encodeURIComponent(redirectUrl)}`;
      console.log('🔐 Redirecting to SSO logout:', ssoLogoutUrl);

      window.location.href = ssoLogoutUrl;
    } catch (err) {
      console.error('❌ SSO logout error:', err);
      setCurrentUser(null);
      setIsAuthenticated(false);
      setAuthChecking(false);
    }
  };

  // Reference Data States
  const [salesStages, setSalesStages] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [serviceLines, setServiceLines] = useState([]);
  const [geographies, setGeographies] = useState([]);
  const [serviceOfferings, setServiceOfferings] = useState([]);
  const [entities, setEntities] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [tags, setTags] = useState([]);

  // Effect to handle beforeunload - warn user and track unload state during import
  // This is at CRMApplication level to survive child component re-renders
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (importing) {
        isUnloadingRef.current = true;
        // Show browser's native "are you sure?" dialog
        e.preventDefault();
        e.returnValue = 'Import is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [importing]);

  // Fetch opportunities from database on component mount
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchOpportunities = async () => {
      try {
        // Using global API_BASE_URL defined at top of file
        const response = await fetch(`${API_BASE_URL}/opportunities`);

        if (!response.ok) {
          throw new Error('Failed to fetch opportunities');
        }

        const result = await response.json();

        if (result.success && result.data && result.data.length > 0) {
          // Map backend field names (snake_case) to frontend field names (camelCase)
          const mappedOpportunities = result.data.map(opp => ({
            opportunityId: opp.opportunity_id,
            opportunityName: opp.opportunity_name,
            associatedAccount: opp.associated_account,
            salesStage: opp.sales_stage,
            probability: opp.probability || '10%',  // Keep original DB string value
            probabilityPercent: String(opp.probability || '10%').replace(/%%/g, '%'),
            probabilityOverride: opp.probability_override != null ? opp.probability_override : null,
            estimatedRevenue: opp.estimated_revenue,
            annualContractValue: opp.annual_contract_value,
            totalContractValue: opp.total_contract_value,
            serviceLine: opp.service_line,
            serviceOffering: opp.service_offering,
            entity: opp.entity,
            geography: opp.geography,
            industry: opp.industry,
            industryVertical: opp.industry || '',
            opportunityType: opp.opportunity_type,
            opportunityCategory: opp.opportunity_category || 'New Business',
            opportunityClassification: opp.opportunity_classification || null,
            natureOfWork: opp.nature_of_work || '',
            priorityLevel: opp.priority,
            salesStatus: opp.status,
            salesStatusOverride: opp.sales_status_override || null,
            accountOwner: opp.account_owner || '',
            leadSupporter: opp.lead_supporter || '',
            solutionArchitect: opp.solution_architect || '',
            executiveSponsor: opp.executive_sponsor || '',
            primaryContact: opp.primary_contact || '',
            primaryContactEmail: opp.primary_contact_email || opp.contact_email || '',
            phoneNumber: opp.phone_number || '',
            solutionScope: opp.solution_scope || '',
            initiationDate: opp.initiation_date,
            estimatedCloseDate: opp.expected_close_date,
            engagementHistory: opp.engagement_history || '',
            comments: opp.comments || '',
            lastEditedDate: opp.last_edited_date,
            leadHistory: opp.lead_history || '',
            sourceLeadId: opp.source_lead_id || '',
            leadRating: opp.lead_rating || '',
            city: opp.city || '',
            leadGenerator: opp.lead_generator || '',
            createdAt: opp.created_at,
            updatedAt: opp.updated_at,
            // Fetch from DB or calculate additional fields based on numeric probability
            forecastMultiplier: opp.forecast_multiplier || ((typeof opp.probability === 'number' ? opp.probability : parseInt(String(opp.probability).replace('%', ''), 10) || 10) / 100),
            probabilityValue: opp.probability_value || (opp.annual_contract_value * ((typeof opp.probability === 'number' ? opp.probability : parseInt(String(opp.probability).replace('%', ''), 10) || 10) / 100)),
            budget: opp.annual_contract_value,
            aging: opp.aging || Math.floor((new Date() - new Date(opp.initiation_date)) / (1000 * 60 * 60 * 24)),
            markedForReview: opp.marked_for_review === true || opp.marked_for_review === 1,
            tags: opp.tags || []
          }));

          setOpportunities(mappedOpportunities);
          console.log(`✅ Loaded ${mappedOpportunities.length} opportunities from database`);
        } else {
          setOpportunities([]);
        }
      } catch (error) {
        console.error('Error fetching opportunities:', error);
        setOpportunities([]);
      }
    };
    // Expose to module-level so nested components can reliably access it
    _fetchOpportunities = fetchOpportunities;

  const fetchMasterData = async () => {
    console.log('🔄 Fetching master data from database...');
    try {
      // Using global API_BASE_URL defined at top of file

      // Fetch all master data in parallel
      const [geo, ind, sl, ss, ent, so, ls, tagsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/master/geographies`).then(r => r.json()),
        fetch(`${API_BASE_URL}/master/industries`).then(r => r.json()),
        fetch(`${API_BASE_URL}/master/service-lines`).then(r => r.json()),
        fetch(`${API_BASE_URL}/master/sales-stages`).then(r => r.json()),
        fetch(`${API_BASE_URL}/master/entities`).then(r => r.json()),
        fetch(`${API_BASE_URL}/master/service-offerings`).then(r => r.json()),
        fetch(`${API_BASE_URL}/master/lead-sources`).then(r => r.json()),
        fetch(`${API_BASE_URL}/master/tags`).then(r => r.json()).catch(() => ({ success: false, data: [] }))
      ]);
      
      if (geo.success && geo.data) {
        setGeographies(geo.data.map(g => ({id: g.id, name: g.name.trim(), region: g.region, isActive: Boolean(g.is_active)})));
        console.log('✅ Loaded geographies:', geo.data.length);
      }
      if (ind.success && ind.data) {
        setIndustries(ind.data.map(i => ({id: i.id, name: i.name.trim(), isActive: Boolean(i.is_active)})));
        console.log('✅ Loaded industries:', ind.data.length);
      }
      // Load service lines first (needed for service offerings mapping)
      let serviceLinesList = [];
      if (sl.success && sl.data) {
        serviceLinesList = sl.data.map(s => ({id: s.id, name: s.name.trim(), isActive: Boolean(s.is_active)}));
        setServiceLines(serviceLinesList);
        console.log('✅ Loaded service lines:', sl.data.length);
      }

      if (ss.success && ss.data) {
        setSalesStages(ss.data.map(s => ({id: s.id, name: s.name.trim(), stageOrder: s.stage_order, isActive: Boolean(s.is_active)})));
        console.log('✅ Loaded sales stages:', ss.data.length);
      }
      if (ent.success && ent.data) {
        setEntities(ent.data.map(e => ({id: e.id, name: e.name.trim(), isActive: Boolean(e.is_active), entityGroup: e.entity_group || null})));
        console.log('✅ Loaded entities:', ent.data.length);
      }
      if (so.success && so.data) {
        // Map service_lines_id to service line name for each service offering
        setServiceOfferings(so.data.map(s => {
          const serviceLine = serviceLinesList.find(sl => sl.id === s.service_lines_id);
          return {
            id: s.id,
            name: s.name.trim(),
            serviceLine: serviceLine ? serviceLine.name : '',
            serviceLineId: s.service_lines_id,
            isActive: Boolean(s.is_active)
          };
        }));
        console.log('✅ Loaded service offerings:', so.data.length);
      }
      if (ls.success && ls.data) {
        setLeadSources(ls.data.map(l => ({id: l.id, name: l.name.trim(), isActive: Boolean(l.is_active)})));
        console.log('✅ Loaded lead sources:', ls.data.length);
      }
      if (tagsRes.success && tagsRes.data) {
        setTags(tagsRes.data.map(t => ({id: t.id, name: t.name.trim(), color: t.color, isActive: Boolean(t.is_active)})));
        console.log('✅ Loaded tags:', tagsRes.data.length);
      }

      console.log('✅✅✅ ALL MASTER DATA LOADED FROM DATABASE! ✅✅✅');
    } catch (error) {
      console.error('❌ Error fetching master data:', error);
    }
  };

  const fetchTasks = async () => {
    console.log('🔄 Fetching tasks from database...');
    try {
      // Using global API_BASE_URL defined at top of file
      const response = await fetch(`${API_BASE_URL}/tasks`);
      const result = await response.json();

      if (result.success && result.data) {
        // Map database fields to frontend format
        const mappedTasks = result.data.map(task => ({
          id: task.id,
          task: task.task,
          owner: task.owner,
          dueDate: task.due_date,
          priority: task.priority,
          status: task.status,
          type: task.type,
          relatedTo: task.related_to,
          relatedId: task.related_id,
          relatedType: task.related_type
        }));

        setTasks(mappedTasks);
        console.log(`✅ Loaded ${mappedTasks.length} tasks from database`);
      }
    } catch (error) {
      console.error('❌ Error fetching tasks:', error);
    }
  };

  const fetchLeads = async () => {
    console.log('🔄 Fetching leads from database...');
    try {
      // Using global API_BASE_URL defined at top of file
      const response = await fetch(`${API_BASE_URL}/leads`);
      const result = await response.json();

      if (result.success && result.data) {
        // Map database fields to frontend format
        const mappedLeads = result.data.map(lead => ({
          leadId: lead.lead_id,
          companyName: lead.company_name,
          contactPerson: lead.contact_person,
          designation: lead.designation || '',
          email: lead.email,
          phone: lead.phone,
          contactNumber: lead.phone || '',
          city: lead.city || '',
          leadSource: lead.lead_source,
          sourceComments: lead.source_comments || '',
          industry: lead.industry,
          industryVertical: lead.industry || '',
          serviceLine: lead.service_line,
          serviceOffering: lead.service_offering || '',
          entity: lead.entity || '',
          leadStatus: lead.lead_status,
          geography: lead.geography,
          region: lead.geography || '',
          pierianGeography: lead.pierian_geography || '',
          estimatedRevenue: lead.estimated_revenue,
          budget: lead.budget,
          leadOwner: lead.lead_owner || lead.sales_representative || '',
          salesRepresentative: lead.sales_representative || '',
          painPoints: lead.pain_points || '',
          comments: lead.comments || '',
          engagementHistory: lead.engagement_history || '',
          priority: lead.priority,
          priorityLevel: lead.priority || '',
          companySize: lead.company_size || '',
          companyRevenue: lead.revenue || '',
          timeline: lead.timeline || '',
          dateCaptured: lead.date_captured ? String(lead.date_captured).split('T')[0] : null,
          lastContacted: lead.last_contacted ? String(lead.last_contacted).split('T')[0] : null,
          createdDate: lead.created_at,
          updatedAt: lead.updated_at,
          convertedOpportunityId: lead.converted_opportunity_id || null,
          convertedAt: lead.converted_at || null,
          markedForReview: Boolean(lead.marked_for_review),
          lossReason: lead.loss_reason || null,
          tags: lead.tags || [],
          // Import fields (from Excel import to leads table)
          opportunityType: lead.opportunity_type || '',
          associatedAccount: lead.associated_account || '',
          leadSupporter: lead.lead_supporter || '',
          leadRating: lead.lead_rating || '',
          businessType: lead.business_type || '',
          leadGenerator: lead.lead_generator || '',
          executiveSponsor: lead.executive_sponsor || '',
          solutionArchitect: lead.solution_architect || '',
          solutionScope: lead.solution_scope || '',
          expectedCloseDate: lead.expected_close_date ? String(lead.expected_close_date).split('T')[0] : null,
          actualCloseDate: lead.actual_close_date ? String(lead.actual_close_date).split('T')[0] : null,
          probability: lead.probability || '',
          probabilityOverride: lead.probability_override,
          annualContractValue: lead.annual_contract_value || 0,
          totalContractValue: lead.total_contract_value || 0,
          salesStage: lead.sales_stage || '',
          status: lead.status || '',
          salesStatusOverride: lead.sales_status_override || '',
          opportunityCategory: lead.opportunity_category || '',
          opportunityClassification: lead.opportunity_classification || '',
          winLossReason: lead.win_loss_reason || '',
          latestComment: lead.latest_comment || '',
          lastEditedDate: lead.last_edited_date ? String(lead.last_edited_date).split('T')[0] : null,
          leadHistory: lead.lead_history || '',
          sourceLeadId: lead.source_lead_id || ''
        }));

        setLeads(mappedLeads);
        console.log(`✅ Loaded ${mappedLeads.length} leads from database`);
      } else {
        setLeads([]);
      }
    } catch (error) {
      console.error('❌ Error fetching leads:', error);
      setLeads([]);
    }
  };
  // Expose to module-level so nested components can reliably access it
  _fetchLeads = fetchLeads;

  const fetchCustomers = async () => {
    console.log('🔄 Fetching customers from database...');
    try {
      // Using global API_BASE_URL defined at top of file
      const response = await fetch(`${API_BASE_URL}/customers`);
      const result = await response.json();

      if (result.success && result.data) {
        // Map database fields to frontend format
        const mappedCustomers = result.data.map(customer => ({
          customerId: customer.customer_id,
          companyName: customer.company_name,
          primaryContact: customer.primary_contact || '',
          contactDesignation: customer.contact_designation || '',
          email: customer.email || '',
          phone: customer.phone || '',
          secondaryContact: customer.secondary_contact || '',
          secondaryContactEmail: customer.secondary_contact_email || '',
          secondaryContactPhone: customer.secondary_contact_phone || '',
          secondaryContactDesignation: customer.secondary_contact_designation || '',
          serviceLine: customer.service_line || '',
          geography: customer.geography || '',
          contractValue: customer.contract_value || 0,
          startDate: customer.start_date ? new Date(customer.start_date).toISOString().split('T')[0] : '',
          renewalDate: customer.renewal_date ? new Date(customer.renewal_date).toISOString().split('T')[0] : '',
          status: customer.status || 'Active',
          healthScore: customer.health_score || 0,
          projectCount: customer.project_count || 0,
          accountManager: customer.account_manager || '',
          createdAt: customer.created_at
        }));

        setCustomers(mappedCustomers);
        console.log(`✅ Loaded ${mappedCustomers.length} customers from database`);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error('❌ Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const fetchUsers = async () => {
    console.log('🔄 Fetching users from database...');
    try {
      // Using global API_BASE_URL defined at top of file
      const response = await fetch(`${API_BASE_URL}/master/users`);
      const result = await response.json();

      if (result.success && result.data) {
        // Map database fields to frontend format
        const mappedUsers = result.data.map(user => {
          // Parse geography to array - handles JSON arrays, plain strings, or null
          let geographies = [];
          if (user.geography && typeof user.geography === 'string') {
            try {
              const parsed = JSON.parse(user.geography);
              geographies = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              geographies = [user.geography];
            }
          }
          // Parse service_lines to array
          let userServiceLines: string[] = [];
          if (user.service_lines && typeof user.service_lines === 'string') {
            try {
              const parsed = JSON.parse(user.service_lines);
              userServiceLines = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              userServiceLines = [user.service_lines];
            }
          }

          return {
            id: user.id,
            userId: user.user_id,
            name: user.name,
            email: user.email,
            role: user.role || 'Sales Executive',
            department: user.department || 'Sales',
            phone: user.phone,
            team: (() => {
              if (!user.team) return ['North America Sales'];
              try {
                const parsed = JSON.parse(user.team);
                return Array.isArray(parsed) ? parsed : [parsed];
              } catch {
                return [user.team];
              }
            })(),
            geographies: geographies,
            serviceLines: userServiceLines,
            managerId: user.manager_id,
            managerName: user.manager_name,
            joinDate: user.join_date ? new Date(user.join_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            status: user.status || 'Active',
            isActive: Boolean(user.is_active),
            isAdmin: user.is_admin === true || user.is_admin === 1
          };
        });

        setUsers(mappedUsers);

        // Note: Do NOT set currentUser here - it should only be set by:
        // 1. Login flow (handleLogin function)
        // 2. Session restoration from localStorage on page load

        console.log(`✅ Loaded ${mappedUsers.length} users from database`);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      setUsers([]);
    }
  };

  fetchOpportunities();
  fetchMasterData();
  fetchTasks();
  fetchLeads(); // Now has fallback to mock data if API fails
  fetchCustomers();
  fetchUsers();
}, [isAuthenticated]); // Re-run after auth so SSO redirect users don't see empty data

  // Fetch single opportunity by ID for editing (defined outside useEffect for accessibility)
  const fetchOpportunityById = async (opportunityId) => {
    try {
      // Using global API_BASE_URL defined at top of file
      const response = await fetch(`${API_BASE_URL}/opportunities/${opportunityId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch opportunity');
      }

      const result = await response.json();

      if (result.success && result.data) {
        const opp = result.data;
        // Map backend field names (snake_case) to frontend field names (camelCase)
        return {
          opportunityId: opp.opportunity_id,
          opportunityName: opp.opportunity_name,
          associatedAccount: opp.associated_account,
          salesStage: opp.sales_stage,
          probability: opp.probability || '0% - 35%',
          probabilityPercent: String(opp.probability || '0% - 35%').replace(/%%/g, '%'),
          probabilityOverride: opp.probability_override != null ? opp.probability_override : null,
          estimatedRevenue: opp.estimated_revenue || 0,
          annualContractValue: opp.annual_contract_value || 0,
          totalContractValue: opp.total_contract_value || 0,
          serviceLine: opp.service_line || '',
          serviceOffering: opp.service_offering || '',
          entity: opp.entity || '',
          geography: opp.geography || '',
          industry: opp.industry || '',
          industryVertical: opp.industry || '',
          opportunityType: opp.opportunity_type || 'New Logo',
          opportunityCategory: opp.opportunity_category || 'New Business',
          opportunityClassification: opp.opportunity_classification || null,
          priorityLevel: opp.priority || 'Medium',
          salesStatus: opp.status || 'Warm',
          natureOfWork: opp.nature_of_work || '',
          salesStatusOverride: opp.sales_status_override || null,
          accountOwner: opp.account_owner || '',
          leadSupporter: opp.lead_supporter || '',
          solutionArchitect: opp.solution_architect || '',
          executiveSponsor: opp.executive_sponsor || '',
          primaryContact: opp.primary_contact || '',
          primaryContactEmail: opp.primary_contact_email || '',
          phoneNumber: opp.phone_number || '',
          solutionScope: opp.solution_scope || '',
          initiationDate: opp.initiation_date ? opp.initiation_date.split('T')[0] : '',
          estimatedCloseDate: opp.expected_close_date ? opp.expected_close_date.split('T')[0] : '',
          actualCloseDate: opp.actual_close_date ? opp.actual_close_date.split('T')[0] : null,
          winLossReason: opp.win_loss_reason || null,
          engagementHistory: opp.engagement_history || '',
          comments: opp.comments || '',
          latestComment: opp.latest_comment || '',
          lastEditedDate: opp.last_edited_date || null,  // Keep null for Excel imports
          leadHistory: opp.lead_history || '',
          sourceLeadId: opp.source_lead_id || '',
          city: opp.city || '',
          leadGenerator: opp.lead_generator || '',
          forecastMultiplier: ((typeof opp.probability === 'number' ? opp.probability : parseInt(String(opp.probability).replace('%', ''), 10) || 10) / 100),
          probabilityValue: opp.probability_value || 0,
          budget: opp.annual_contract_value || 0,
          aging: opp.aging || 0,
          tags: opp.tags || [],
          markedForReview: opp.marked_for_review === true || opp.marked_for_review === 1
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching opportunity by ID:', error);
      return null;
    }
  };

  console.log('CRM App State:', {
    showLeadModal,
    showOpportunityModal,
    showCustomerModal,
    opportunitiesCount: opportunities.length
  });
  console.log('CRM App State:', {
    showLeadModal,
    showOpportunityModal,
    showCustomerModal,
    opportunitiesCount: opportunities.length
  });
  
  // ======================================================================
  // LEADS VIEW — Table, filters, convert-to-opportunity
  // ======================================================================
  const leadsViewContent = (() => {
    const converting = leadConverting;
    const setConverting = setLeadConverting;
    const currentPage = leadCurrentPage;
    const setCurrentPage = setLeadCurrentPage;
    const pageSize = 20;

    // Access control — filter leads by geography and/or service line for non-admin users
    const userAccessibleLeads = leads.filter(lead => {
      if (currentUser?.isAdmin) return true;
      const hasGeo = currentUser?.geographies?.length > 0;
      const hasSL = currentUser?.serviceLines?.length > 0;
      // A user with no service lines assigned sees nothing
      if (!hasGeo && !hasSL) return false;
      const geoOk = !hasGeo ||
        (lead.geography && currentUser.geographies.includes(lead.geography));
      const slOk = !hasSL ||
        (lead.serviceLine && currentUser.serviceLines.includes(lead.serviceLine));
      const matchesAccess = geoOk && slOk;
      // Flagged-for-review leads are accessible to the owner or solution architect regardless
      const isFlaggedAndOwned = lead.markedForReview === true &&
        currentUser?.name &&
        (lead.leadOwner === currentUser.name || lead.solutionArchitect === currentUser.name);
      return matchesAccess || isFlaggedAndOwned;
    });

    // Unique values for multi-select filters (computed from accessible leads only)
    const uniqueLeadOwners = Array.from(new Set(userAccessibleLeads.map(l => l.leadOwner || l.salesRepresentative).filter(Boolean))).sort();
    const uniqueLeadGeographies = (() => {
      const geos = Array.from(new Set(userAccessibleLeads.map(l => l.geography).filter(Boolean))).sort();
      if (userAccessibleLeads.some(l => !l.geography) && !geos.includes('Domestic')) geos.push('Domestic');
      return geos;
    })();
    const uniqueLeadSources = Array.from(new Set(userAccessibleLeads.map(l => l.leadSource).filter(Boolean))).sort();
    const uniqueLeadServiceLines = Array.from(new Set(userAccessibleLeads.map(l => l.serviceLine).filter(Boolean))).sort();
    const uniqueLeadIndustries = Array.from(new Set(userAccessibleLeads.map(l => l.industry).filter(Boolean))).sort();
    const uniqueLeadLeadGenerators = Array.from(new Set(userAccessibleLeads.map(l => l.leadGenerator).filter(Boolean))).sort();
    const uniqueLeadLeadSupporters = Array.from(new Set(userAccessibleLeads.map(l => l.leadSupporter).filter(Boolean))).sort();
    const uniqueLeadCities = Array.from(new Set(userAccessibleLeads.map(l => l.city).filter(Boolean))).sort();

    // Toggle multi-select filter
    const toggleLeadFilter = (type: string, value: string | number) => {
      switch (type) {
        case 'owner': setLeadSelectedOwners(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'geography': setLeadSelectedGeographies(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'source': setLeadSelectedSources(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'tags': setLeadSelectedTags(prev => prev.includes(value as number) ? prev.filter(v => v !== value) : [...prev, value as number]); break;
        case 'entityGroup': setLeadSelectedEntityGroups(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'entity': setLeadSelectedEntities(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'serviceLine': setLeadSelectedServiceLines(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'industry': setLeadSelectedIndustries(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'leadGenerator': setLeadSelectedLeadGenerators(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'leadSupporter': setLeadSelectedLeadSupporters(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'city': setLeadSelectedCities(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
        case 'leadrating': setLeadSelectedLeadRatings(prev => prev.includes(value as string) ? prev.filter(v => v !== value) : [...prev, value as string]); break;
      }
    };

    const hasLeadActiveFilters = showMyLeadsOnly || leadSelectedOwners.length > 0 || leadSelectedGeographies.length > 0 || leadSelectedSources.length > 0 || leadShowReviewOnly || leadSelectedTags.length > 0 || leadSelectedEntityGroups.length > 0 || leadSelectedEntities.length > 0 || leadSelectedServiceLines.length > 0 || leadSelectedIndustries.length > 0 || leadSelectedLeadGenerators.length > 0 || leadSelectedLeadSupporters.length > 0 || leadSelectedCities.length > 0 || leadSelectedLeadRatings.length > 0;
    const clearLeadAllFilters = () => { setShowMyLeadsOnly(false); setLeadSelectedOwners([]); setLeadSelectedGeographies([]); setLeadSelectedSources([]); setLeadSelectedTags([]); setLeadSelectedEntityGroups([]); setLeadSelectedEntities([]); setLeadSelectedServiceLines([]); setLeadSelectedIndustries([]); setLeadSelectedLeadGenerators([]); setLeadSelectedLeadSupporters([]); setLeadSelectedCities([]); setLeadSelectedLeadRatings([]); setLeadShowReviewOnly(false); setLeadActiveFilterType(null); };

    // Filtered leads — when search is active, bypass status tab to find leads across all statuses
    const hasLeadActiveSearch = leadSearchTerm.trim().length > 0;
    const filteredLeads = userAccessibleLeads.filter(lead => {
      const matchesSearch = leadSearchTerm === '' ||
        (lead.companyName || '').toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
        (lead.contactPerson || '').toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
        (lead.email || '').toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
        (lead.leadId || '').toLowerCase().includes(leadSearchTerm.toLowerCase());
      const matchesStatus = hasLeadActiveSearch ||
        (leadStatusFilter === 'all'
          ? (lead.leadStatus === 'Open' || !lead.leadStatus || lead.leadStatus === 'Contacted' || lead.leadStatus === 'Unqualified')
          : (leadStatusFilter === 'Open' ? (lead.leadStatus === 'Open' || !lead.leadStatus) : lead.leadStatus === leadStatusFilter));
      const matchesMyLeads = !showMyLeadsOnly || !currentUser?.name || (lead.leadOwner || lead.salesRepresentative) === currentUser.name;
      const matchesOwner = leadSelectedOwners.length === 0 || leadSelectedOwners.includes(lead.leadOwner || lead.salesRepresentative);
      const matchesGeo = leadSelectedGeographies.length === 0 || leadSelectedGeographies.includes(lead.geography || 'Domestic');
      const matchesSource = leadSelectedSources.length === 0 || leadSelectedSources.includes(lead.leadSource);
      const matchesReviewFilter = !leadShowReviewOnly || lead.markedForReview === true;
      const matchesTags = leadSelectedTags.length === 0 || (lead.tags && lead.tags.some((t: any) => leadSelectedTags.includes(t.id)));
      const matchesEntityGroup = leadSelectedEntityGroups.length === 0 || (() => {
        if (!lead.entity) return leadSelectedEntityGroups.includes('Ungrouped');
        const entityData = entities.find((e: any) => e.name === lead.entity);
        if (!entityData) return leadSelectedEntityGroups.includes('Ungrouped');
        if (!entityData.entityGroup || entityData.entityGroup === '') return leadSelectedEntityGroups.includes('Ungrouped');
        return leadSelectedEntityGroups.includes(entityData.entityGroup);
      })();
      const matchesEntity = leadSelectedEntities.length === 0 || (() => {
        if (leadSelectedEntities.includes('Blank') && (!lead.entity || lead.entity.trim() === '')) return true;
        return leadSelectedEntities.filter(e => e !== 'Blank').includes(lead.entity);
      })();
      const matchesServiceLine = leadSelectedServiceLines.length === 0 || leadSelectedServiceLines.includes(lead.serviceLine);
      const matchesIndustry = leadSelectedIndustries.length === 0 || leadSelectedIndustries.includes(lead.industry);
      const matchesLeadGenerator = leadSelectedLeadGenerators.length === 0 || leadSelectedLeadGenerators.includes(lead.leadGenerator);
      const matchesLeadSupporter = leadSelectedLeadSupporters.length === 0 || leadSelectedLeadSupporters.includes(lead.leadSupporter);
      const matchesCity = leadSelectedCities.length === 0 || leadSelectedCities.includes(lead.city);
      const matchesLeadRating = leadSelectedLeadRatings.length === 0 || leadSelectedLeadRatings.includes(lead.leadRating || '');
      return matchesMyLeads && matchesSearch && matchesStatus && matchesOwner && matchesGeo && matchesSource && matchesReviewFilter && matchesTags && matchesEntityGroup && matchesEntity && matchesServiceLine && matchesIndustry && matchesLeadGenerator && matchesLeadSupporter && matchesCity && matchesLeadRating;
    });

    // Cross-filter helper for leads: returns leads matching ALL filters EXCEPT the specified one.
    // Mirrors filteredLeads logic exactly so pill counts predict actual results.
    const getFilteredLeadsExcluding = (excludeFilter: string) => {
      return userAccessibleLeads.filter(lead => {
        // Search always applies
        if (leadSearchTerm) {
          const matchesSearch =
            (lead.companyName || '').toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
            (lead.contactPerson || '').toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
            (lead.email || '').toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
            (lead.leadId || '').toLowerCase().includes(leadSearchTerm.toLowerCase());
          if (!matchesSearch) return false;
        }
        // Status filter always applies (status cards are separate from filter pills)
        if (leadStatusFilter !== 'all') {
          const matchesStatus = leadStatusFilter === 'Open'
            ? (lead.leadStatus === 'Open' || !lead.leadStatus)
            : lead.leadStatus === leadStatusFilter;
          if (!matchesStatus) return false;
        }
        // My Leads filter always applies in cross-filter
        if (showMyLeadsOnly && currentUser?.name && (lead.leadOwner || lead.salesRepresentative) !== currentUser.name) return false;
        // Apply all dimension filters except the excluded one
        if (excludeFilter !== 'owner' && leadSelectedOwners.length > 0 && !leadSelectedOwners.includes(lead.leadOwner || lead.salesRepresentative)) return false;
        if (excludeFilter !== 'geography' && leadSelectedGeographies.length > 0 && !leadSelectedGeographies.includes(lead.geography || 'Domestic')) return false;
        if (excludeFilter !== 'source' && leadSelectedSources.length > 0 && !leadSelectedSources.includes(lead.leadSource)) return false;
        if (excludeFilter !== 'review' && leadShowReviewOnly && !lead.markedForReview) return false;
        if (excludeFilter !== 'tags' && leadSelectedTags.length > 0 && !(lead.tags && lead.tags.some((t: any) => leadSelectedTags.includes(t.id)))) return false;
        // Entity group filter
        if (excludeFilter !== 'entity' && leadSelectedEntityGroups.length > 0) {
          if (!lead.entity) { if (!leadSelectedEntityGroups.includes('Ungrouped')) return false; }
          else {
            const entityData = entities.find((e: any) => e.name === lead.entity);
            if (!entityData) { if (!leadSelectedEntityGroups.includes('Ungrouped')) return false; }
            else if (!entityData.entityGroup || entityData.entityGroup === '') { if (!leadSelectedEntityGroups.includes('Ungrouped')) return false; }
            else { if (!leadSelectedEntityGroups.includes(entityData.entityGroup)) return false; }
          }
        }
        // Entity filter
        if (excludeFilter !== 'entity' && leadSelectedEntities.length > 0) {
          if (!lead.entity || lead.entity.trim() === '') { if (!leadSelectedEntities.includes('Blank')) return false; }
          else { if (!leadSelectedEntities.filter(e => e !== 'Blank').includes(lead.entity)) return false; }
        }
        if (excludeFilter !== 'serviceLine' && leadSelectedServiceLines.length > 0 && !leadSelectedServiceLines.includes(lead.serviceLine)) return false;
        if (excludeFilter !== 'industry' && leadSelectedIndustries.length > 0 && !leadSelectedIndustries.includes(lead.industry)) return false;
        if (excludeFilter !== 'leadGenerator' && leadSelectedLeadGenerators.length > 0 && !leadSelectedLeadGenerators.includes(lead.leadGenerator)) return false;
        if (excludeFilter !== 'leadSupporter' && leadSelectedLeadSupporters.length > 0 && !leadSelectedLeadSupporters.includes(lead.leadSupporter)) return false;
        if (excludeFilter !== 'city' && leadSelectedCities.length > 0 && !leadSelectedCities.includes(lead.city)) return false;
        if (excludeFilter !== 'leadrating' && leadSelectedLeadRatings.length > 0 && !leadSelectedLeadRatings.includes(lead.leadRating || '')) return false;
        return true;
      });
    };

    // Summary counts & revenue totals — use leads filtered by all criteria EXCEPT status
    // so that owner/geo/source/search/review/tag/entity filters affect the summary card counts
    const baseFilteredLeads = leads.filter(lead => {
      const matchesSearch = leadSearchTerm === '' ||
        (lead.companyName || '').toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
        (lead.contactPerson || '').toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
        (lead.email || '').toLowerCase().includes(leadSearchTerm.toLowerCase()) ||
        (lead.leadId || '').toLowerCase().includes(leadSearchTerm.toLowerCase());
      const matchesOwner = leadSelectedOwners.length === 0 || leadSelectedOwners.includes(lead.leadOwner || lead.salesRepresentative);
      const matchesGeo = leadSelectedGeographies.length === 0 || leadSelectedGeographies.includes(lead.geography || 'Domestic');
      const matchesSource = leadSelectedSources.length === 0 || leadSelectedSources.includes(lead.leadSource);
      const matchesReviewFilter = !leadShowReviewOnly || lead.markedForReview === true;
      const matchesTags = leadSelectedTags.length === 0 || (lead.tags && lead.tags.some((t: any) => leadSelectedTags.includes(t.id)));
      const matchesEntityGroup = leadSelectedEntityGroups.length === 0 || (() => {
        if (!lead.entity) return leadSelectedEntityGroups.includes('Ungrouped');
        const entityData = entities.find((e: any) => e.name === lead.entity);
        if (!entityData) return leadSelectedEntityGroups.includes('Ungrouped');
        if (!entityData.entityGroup || entityData.entityGroup === '') return leadSelectedEntityGroups.includes('Ungrouped');
        return leadSelectedEntityGroups.includes(entityData.entityGroup);
      })();
      const matchesEntity = leadSelectedEntities.length === 0 || (() => {
        if (leadSelectedEntities.includes('Blank') && (!lead.entity || lead.entity.trim() === '')) return true;
        return leadSelectedEntities.filter(e => e !== 'Blank').includes(lead.entity);
      })();
      const matchesServiceLine = leadSelectedServiceLines.length === 0 || leadSelectedServiceLines.includes(lead.serviceLine);
      const matchesIndustry = leadSelectedIndustries.length === 0 || leadSelectedIndustries.includes(lead.industry);
      const matchesLeadGenerator = leadSelectedLeadGenerators.length === 0 || leadSelectedLeadGenerators.includes(lead.leadGenerator);
      const matchesLeadSupporter = leadSelectedLeadSupporters.length === 0 || leadSelectedLeadSupporters.includes(lead.leadSupporter);
      const matchesCity = leadSelectedCities.length === 0 || leadSelectedCities.includes(lead.city);
      const matchesMyLeads = !showMyLeadsOnly || !currentUser?.name || (lead.leadOwner || lead.salesRepresentative) === currentUser.name;
      const matchesLeadRating = leadSelectedLeadRatings.length === 0 || leadSelectedLeadRatings.includes(lead.leadRating || '');
      return matchesSearch && matchesOwner && matchesGeo && matchesSource && matchesReviewFilter && matchesTags && matchesEntityGroup && matchesEntity && matchesServiceLine && matchesIndustry && matchesLeadGenerator && matchesLeadSupporter && matchesCity && matchesMyLeads && matchesLeadRating;
    });
    const openLeads = baseFilteredLeads.filter(l => l.leadStatus === 'Open' || (!l.leadStatus));
    const contactedLeads = baseFilteredLeads.filter(l => l.leadStatus === 'Contacted');
    const qualifiedLeads = baseFilteredLeads.filter(l => l.leadStatus === 'Qualified');
    const disqualifiedLeads = baseFilteredLeads.filter(l => l.leadStatus === 'Unqualified');
    const convertedLeads = baseFilteredLeads.filter(l => l.leadStatus === 'Converted');
    const lostLeads = baseFilteredLeads.filter(l => l.leadStatus === 'Lost');

    const openCount = openLeads.length;
    const contactedCount = contactedLeads.length;
    const qualifiedCount = qualifiedLeads.length;
    const disqualifiedCount = disqualifiedLeads.length;
    const convertedCount = convertedLeads.length;
    const lostCount = lostLeads.length;

    const sumRevenue = (arr: any[]) => arr.reduce((sum, l) => sum + (parseFloat(l.estimatedRevenue) || 0), 0);
    const openRevenue = sumRevenue(openLeads);
    const contactedRevenue = sumRevenue(contactedLeads);
    const qualifiedRevenue = sumRevenue(qualifiedLeads);
    const disqualifiedRevenue = sumRevenue(disqualifiedLeads);
    const convertedRevenue = sumRevenue(convertedLeads);
    const lostRevenue = sumRevenue(lostLeads);

    // Sorting handler for leads
    const handleLeadSort = (field: string) => {
      if (leadSortField === field) {
        setLeadSortDirection(leadSortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setLeadSortField(field);
        setLeadSortDirection('asc');
      }
      setCurrentPage(1);
    };

    // Sort filtered leads
    const sortedLeads = leadSortField ? [...filteredLeads].sort((a, b) => {
      const dir = leadSortDirection === 'asc' ? 1 : -1;
      if (leadSortField === 'age') {
        const ageA = a.dateCaptured ? Math.floor((Date.now() - new Date(a.dateCaptured).getTime()) / (1000 * 60 * 60 * 24)) : 0;
        const ageB = b.dateCaptured ? Math.floor((Date.now() - new Date(b.dateCaptured).getTime()) / (1000 * 60 * 60 * 24)) : 0;
        return dir * (ageA - ageB);
      }
      if (leadSortField === 'dateCaptured') {
        const dateA = a.dateCaptured ? new Date(a.dateCaptured).getTime() : 0;
        const dateB = b.dateCaptured ? new Date(b.dateCaptured).getTime() : 0;
        return dir * (dateA - dateB);
      }
      if (leadSortField === 'estimatedRevenue') {
        return dir * ((parseFloat(a.estimatedRevenue) || 0) - (parseFloat(b.estimatedRevenue) || 0));
      }
      // Generic string sort for all other fields
      const strA = (a[leadSortField] || '').toString().toLowerCase();
      const strB = (b[leadSortField] || '').toString().toLowerCase();
      return dir * strA.localeCompare(strB);
    }) : filteredLeads;

    // Pagination
    const totalPages = Math.ceil(sortedLeads.length / pageSize);
    const paginatedLeads = sortedLeads.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    // Convert lead to opportunity — opens create-opportunity screen.
    // Lead status is flipped to Converted only after the opportunity row is actually inserted.
    // If the user cancels the opportunity form, the lead remains unchanged.
    const handleConvertLead = async (lead: any) => {
      if (!confirm(`Convert "${lead.companyName}" to an opportunity?\n\nThis opens the opportunity creation screen. The lead is marked Converted only after you save the opportunity.`)) return;
      setSelectedOpportunity(null); // null = create mode
      setConvertFromLead(lead);     // pass lead data to pre-fill the form
      setShowOpportunityModal(true);
    };

    // Mark lead as Not Qualified (soft-delete)
    const handleDeleteLead = async (lead: any) => {
      if (!confirm(`Mark lead "${lead.companyName}" as Not Qualified?\n\nThe lead will be moved to the Not Qualified list.`)) return;
      try {
        const result = await leadsApi.delete(lead.leadId);
        if (result.success) { if (_fetchLeads) await _fetchLeads(); }
        else { alert(`Error: ${result.error || result.message || 'Failed to update lead'}`); }
      } catch (error) { console.error('Error updating lead:', error); alert('Error updating lead. Please try again.'); }
    };

    // Mark lead as Not Qualified via status update
    const handleMarkNotQualified = async (lead: any) => {
      if (!confirm(`Disqualify lead "${lead.companyName}"?`)) return;
      try {
        const payload = {
          lead_id: lead.leadId, company_name: lead.companyName, contact_person: lead.contactPerson,
          designation: lead.designation || null, email: lead.email, phone: lead.phone || null,
          city: lead.city || null, geography: lead.geography,
          industry: lead.industry, service_line: lead.serviceLine, service_offering: lead.serviceOffering || null,
          entity: lead.entity || null,
          pierian_geography: lead.pierianGeography || lead.geography,
          lead_source: lead.leadSource, source_comments: lead.sourceComments || null,
          lead_owner: lead.leadOwner, sales_representative: lead.leadOwner, lead_supporter: lead.leadSupporter || null,
          lead_status: 'Unqualified',
          estimated_revenue: parseFloat(lead.estimatedRevenue) || 0, budget: parseFloat(lead.budget) || 0,
          pain_points: lead.painPoints || null, comments: lead.comments || null,
          priority: lead.priority || 'Medium',
          date_captured: lead.dateCaptured || null, last_contacted: lead.lastContacted || null,
          converted_opportunity_id: lead.convertedOpportunityId || null, converted_at: lead.convertedAt || null,
          marked_for_review: lead.markedForReview || false
        };
        const result = await leadsApi.update(lead.leadId, payload);
        if (result.success) { if (_fetchLeads) await _fetchLeads(); }
        else { alert(`Error: ${result.error || result.message || 'Failed to update lead'}`); }
      } catch (error) { console.error('Error updating lead:', error); alert('Error updating lead. Please try again.'); }
    };

    // Qualify lead — opens the opportunity creation screen at the Qualified stage.
    // Lead status is flipped to Converted only after the opportunity row is actually inserted.
    // If the user cancels the opportunity form, the lead remains unchanged.
    const handleMarkQualified = async (lead: any) => {
      if (!confirm(`Qualify "${lead.companyName}" and create an opportunity?\n\nThis opens the opportunity creation screen. The lead is marked Converted only after you save the opportunity.`)) return;
      setSelectedOpportunity(null); // null = create mode
      setConvertFromLead(lead);     // pass lead data to pre-fill the form
      setShowOpportunityModal(true);
    };

    // Toggle marked for review on a lead
    const handleToggleReview = async (lead: any) => {
      try {
        const newVal = !lead.markedForReview;
        const payload = {
          lead_id: lead.leadId, company_name: lead.companyName, contact_person: lead.contactPerson,
          designation: lead.designation || null, email: lead.email, phone: lead.phone || null,
          city: lead.city || null, geography: lead.geography,
          industry: lead.industry, service_line: lead.serviceLine, service_offering: lead.serviceOffering || null,
          entity: lead.entity || null,
          pierian_geography: lead.pierianGeography || lead.geography,
          lead_source: lead.leadSource, source_comments: lead.sourceComments || null,
          lead_owner: lead.leadOwner, sales_representative: lead.leadOwner, lead_supporter: lead.leadSupporter || null,
          lead_status: lead.leadStatus,
          estimated_revenue: parseFloat(lead.estimatedRevenue) || 0, budget: parseFloat(lead.budget) || 0,
          pain_points: lead.painPoints || null, comments: lead.comments || null,
          priority: lead.priority || 'Medium',
          date_captured: lead.dateCaptured || null, last_contacted: lead.lastContacted || null,
          converted_opportunity_id: lead.convertedOpportunityId || null, converted_at: lead.convertedAt || null,
          marked_for_review: newVal
        };
        const result = await leadsApi.update(lead.leadId, payload);
        if (result.success) { if (_fetchLeads) await _fetchLeads(); }
        else { alert(`Error: ${result.error || result.message || 'Failed to update lead'}`); }
      } catch (error) { console.error('Error toggling review:', error); alert('Error toggling review. Please try again.'); }
    };

    // Open lead in view mode
    const openLeadView = (lead: any) => { setSelectedLead(lead); setLeadViewMode('view'); setShowLeadModal(true); };

    // Status badge
    const getStatusBadge = (status: string) => {
      const s = status === 'Qualified' ? { bg: '#C7D8C1', color: '#3d5a3d' } : status === 'Converted' ? { bg: 'rgba(156,175,136,0.25)', color: '#3d5a3d' } : status === 'Unqualified' ? { bg: '#fed7aa', color: '#9a3412' } : status === 'Lost' ? { bg: '#e5e7eb', color: '#6b7280' } : { bg: 'rgba(106,124,165,0.18)', color: '#4B5B7A' };
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: s.bg, color: s.color }}>{status || 'Open'}</span>;
    };

    // Filter button helper (identical to opportunities pattern)
    const LeadFilterBtn = ({ filterKey, label, selected }: { filterKey: string; label: string; selected: string[] }) => (
      <button
        onClick={() => setLeadActiveFilterType(leadActiveFilterType === filterKey ? null : filterKey)}
        className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
        style={{
          backgroundColor: selected.length > 0 ? '#00BBA2' : leadActiveFilterType === filterKey ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
          color: selected.length > 0 ? 'white' : leadActiveFilterType === filterKey ? '#009E8A' : '#374151',
          border: leadActiveFilterType === filterKey && selected.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
        }}
      >
        {label}{selected.length > 0 && ` (${selected.length})`}
      </button>
    );

    // Filter options dropdown helper (identical to opportunities pattern)
    const LeadFilterOptions = ({ filterKey, items, selected, toggle }: { filterKey: string; items: { value: string; count: number }[]; selected: string[]; toggle: (v: string) => void }) => {
      if (leadActiveFilterType !== filterKey) return null;
      const isExpanded = leadExpandedFilterOptions[filterKey];
      const visible = isExpanded ? items : items.slice(0, 8);
      const hiddenCount = items.length - 8;
      return (
        <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
          {visible.map(({ value, count }) => {
            const isSelected = selected.includes(value);
            return (
              <button key={value} onClick={() => toggle(value)} className="px-2 py-0.5 rounded text-xs font-medium"
                style={{ backgroundColor: isSelected ? '#00BBA2' : 'var(--dm-surface)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                {value} ({count})
              </button>
            );
          })}
          {hiddenCount > 0 && (
            <button onClick={() => setLeadExpandedFilterOptions(prev => ({ ...prev, [filterKey]: !prev[filterKey] }))}
              className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5" style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}>
              {isExpanded ? 'Less' : `+${hiddenCount}`}
              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-1">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <h2 className="text-sm font-bold text-gray-900">Leads</h2>
            <p className="text-xs text-gray-400">Manage and convert leads to opportunities</p>
          </div>
          <Button size="sm" onClick={() => { setSelectedLead(null); setLeadViewMode(null); setPrefilledLead(null); setShowICPWizard(true); }}>
            <Plus size={14} className="mr-1" /> New Lead
          </Button>
        </div>

        {/* Filters Section - Compact Single Row (Identical to Opportunities) */}
        <div className="rounded-md shadow-sm px-2 py-1.5" style={{ backgroundColor: 'var(--dm-surface)', border: '1px solid var(--dm-border)' }}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter size={14} className="text-gray-400 flex-shrink-0" />
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={12} />
              <input type="text" placeholder="Search..." value={leadSearchTerm}
                onChange={(e) => { setLeadSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-32 pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-pierian focus:border-pierian" />
            </div>
            {/* Owner Filter */}
            <LeadFilterBtn filterKey="owner" label="Lead Owner" selected={leadSelectedOwners} />
            {/* Geography Filter */}
            <LeadFilterBtn filterKey="geography" label="Geo" selected={leadSelectedGeographies} />
            {/* Source Filter */}
            <LeadFilterBtn filterKey="source" label="Source" selected={leadSelectedSources} />
            {/* Service Line Filter */}
            <LeadFilterBtn filterKey="serviceLine" label="Service Line" selected={leadSelectedServiceLines} />
            {/* Industry Filter */}
            <LeadFilterBtn filterKey="industry" label="Industry" selected={leadSelectedIndustries} />
            {/* Lead Generator Filter */}
            <LeadFilterBtn filterKey="leadGenerator" label="Lead Generator" selected={leadSelectedLeadGenerators} />
            <LeadFilterBtn filterKey="leadSupporter" label="Lead Supporter" selected={leadSelectedLeadSupporters} />
            {/* City Filter */}
            <LeadFilterBtn filterKey="city" label="City" selected={leadSelectedCities} />
            {/* Lead Rating Filter */}
            <button
              onClick={() => setLeadActiveFilterType(leadActiveFilterType === 'leadrating' ? null : 'leadrating')}
              className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
              style={{
                backgroundColor: leadSelectedLeadRatings.length > 0 ? '#00BBA2' : leadActiveFilterType === 'leadrating' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                color: leadSelectedLeadRatings.length > 0 ? 'white' : leadActiveFilterType === 'leadrating' ? '#009E8A' : '#374151',
                border: leadActiveFilterType === 'leadrating' && leadSelectedLeadRatings.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
              }}
            >
              Lead Rating{leadSelectedLeadRatings.length > 0 && ` (${leadSelectedLeadRatings.length})`}
            </button>
            {/* Entity Filter */}
            <button
              onClick={() => setLeadActiveFilterType(leadActiveFilterType === 'entity' ? null : 'entity')}
              className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
              style={{
                backgroundColor: (leadSelectedEntityGroups.length + leadSelectedEntities.length) > 0 ? '#00BBA2' : leadActiveFilterType === 'entity' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                color: (leadSelectedEntityGroups.length + leadSelectedEntities.length) > 0 ? 'white' : leadActiveFilterType === 'entity' ? '#009E8A' : '#374151',
                border: leadActiveFilterType === 'entity' && (leadSelectedEntityGroups.length + leadSelectedEntities.length) === 0 ? '1px solid #00BBA2' : '1px solid transparent'
              }}
            >
              Entity{(leadSelectedEntityGroups.length + leadSelectedEntities.length) > 0 && ` (${leadSelectedEntityGroups.length + leadSelectedEntities.length})`}
            </button>
            {/* Tags Filter */}
            <LeadFilterBtn filterKey="tags" label="Tags" selected={leadSelectedTags.map(String)} />
            {/* Review Items Toggle */}
            <button
              onClick={() => setLeadShowReviewOnly(!leadShowReviewOnly)}
              className="px-2.5 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1"
              style={{
                backgroundColor: leadShowReviewOnly ? '#f59e0b' : '#f3f4f6',
                color: leadShowReviewOnly ? 'white' : 'var(--dm-btn-text)',
                border: leadShowReviewOnly ? '1px solid #d97706' : '1px solid transparent'
              }}
            >
              <Flag size={12} className={leadShowReviewOnly ? 'fill-white' : ''} />
              Review Items
            </button>
            {/* Clear Filters */}
            {hasLeadActiveFilters && (
              <button onClick={clearLeadAllFilters} className="px-2.5 py-1 rounded text-xs font-medium ml-1" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                Clear
              </button>
            )}
            <span className="text-xs text-gray-400 ml-auto">{filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Filter Options Dropdowns — use cross-filter so pills reflect other active filters */}
          <LeadFilterOptions filterKey="owner"
            items={uniqueLeadOwners.map(o => ({ value: o, count: getFilteredLeadsExcluding('owner').filter(l => (l.leadOwner || l.salesRepresentative) === o).length })).filter(x => x.count > 0).sort((a, b) => b.count - a.count)}
            selected={leadSelectedOwners} toggle={(v) => toggleLeadFilter('owner', v)} />
          <LeadFilterOptions filterKey="geography"
            items={uniqueLeadGeographies.map(g => ({ value: g, count: getFilteredLeadsExcluding('geography').filter(l => l.geography === g).length })).filter(x => x.count > 0).sort((a, b) => b.count - a.count)}
            selected={leadSelectedGeographies} toggle={(v) => toggleLeadFilter('geography', v)} />
          <LeadFilterOptions filterKey="source"
            items={uniqueLeadSources.map(s => ({ value: s, count: getFilteredLeadsExcluding('source').filter(l => l.leadSource === s).length })).filter(x => x.count > 0).sort((a, b) => b.count - a.count)}
            selected={leadSelectedSources} toggle={(v) => toggleLeadFilter('source', v)} />
          <LeadFilterOptions filterKey="serviceLine"
            items={uniqueLeadServiceLines.map(sl => ({ value: sl, count: getFilteredLeadsExcluding('serviceLine').filter(l => l.serviceLine === sl).length })).filter(x => x.count > 0).sort((a, b) => b.count - a.count)}
            selected={leadSelectedServiceLines} toggle={(v) => toggleLeadFilter('serviceLine', v)} />
          <LeadFilterOptions filterKey="industry"
            items={uniqueLeadIndustries.map(ind => ({ value: ind, count: getFilteredLeadsExcluding('industry').filter(l => l.industry === ind).length })).filter(x => x.count > 0).sort((a, b) => b.count - a.count)}
            selected={leadSelectedIndustries} toggle={(v) => toggleLeadFilter('industry', v)} />
          <LeadFilterOptions filterKey="leadGenerator"
            items={uniqueLeadLeadGenerators.map(lg => ({ value: lg, count: getFilteredLeadsExcluding('leadGenerator').filter(l => l.leadGenerator === lg).length })).filter(x => x.count > 0).sort((a, b) => b.count - a.count)}
            selected={leadSelectedLeadGenerators} toggle={(v) => toggleLeadFilter('leadGenerator', v)} />
          <LeadFilterOptions filterKey="leadSupporter"
            items={uniqueLeadLeadSupporters.map(ls => ({ value: ls, count: getFilteredLeadsExcluding('leadSupporter').filter(l => l.leadSupporter === ls).length })).filter(x => x.count > 0).sort((a, b) => b.count - a.count)}
            selected={leadSelectedLeadSupporters} toggle={(v) => toggleLeadFilter('leadSupporter', v)} />
          <LeadFilterOptions filterKey="city"
            items={uniqueLeadCities.map(c => ({ value: c, count: getFilteredLeadsExcluding('city').filter(l => l.city === c).length })).filter(x => x.count > 0).sort((a, b) => b.count - a.count)}
            selected={leadSelectedCities} toggle={(v) => toggleLeadFilter('city', v)} />

          {/* Lead Rating Filter Options */}
          {leadActiveFilterType === 'leadrating' && (
            <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
              {['Hot', 'Warm', 'Cold'].map((value) => {
                const isSelected = leadSelectedLeadRatings.includes(value);
                return (
                  <button key={value} onClick={() => toggleLeadFilter('leadrating', value)}
                    className="px-2 py-0.5 rounded text-xs font-medium"
                    style={{ backgroundColor: isSelected ? '#00BBA2' : 'white', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                    {value}
                  </button>
                );
              })}
            </div>
          )}

          {/* Tags Filter Options — cross-filtered */}
          {leadActiveFilterType === 'tags' && (() => {
            const crossFilteredForTags = getFilteredLeadsExcluding('tags');
            return (
            <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
              {(tags || []).filter((t: any) => t.isActive).map((tag: any) => {
                const isSelected = leadSelectedTags.includes(tag.id);
                const count = crossFilteredForTags.filter(l => l.tags && l.tags.some((lt: any) => lt.id === tag.id)).length;
                if (count === 0 && !isSelected) return null;
                return (
                  <button key={tag.id} onClick={() => toggleLeadFilter('tags', tag.id)} className="px-2 py-0.5 rounded text-xs font-medium"
                    style={{ backgroundColor: isSelected ? (tag.color || '#00BBA2') : 'white', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                    {tag.name} ({count})
                  </button>
                );
              })}
            </div>
            );
          })()}

          {/* Entity Filter Options */}
          {leadActiveFilterType === 'entity' && (() => {
            const crossFiltered = getFilteredLeadsExcluding('entity');
            const entityWithCounts = entities
              .filter((e: any) => e.isActive)
              .map((entity: any) => ({ value: entity.name, count: crossFiltered.filter(l => l.entity === entity.name).length }))
              .filter((e: any) => e.count > 0)
              .sort((a: any, b: any) => b.count - a.count);
            const blankEntityCount = crossFiltered.filter(l => !l.entity || l.entity.trim() === '').length;
            return (
              <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                {blankEntityCount > 0 && (() => {
                  const isSelected = leadSelectedEntities.includes('Blank');
                  return (
                    <button key="blank-entity" onClick={() => setLeadSelectedEntities(prev => prev.includes('Blank') ? prev.filter(e => e !== 'Blank') : [...prev, 'Blank'])}
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: isSelected ? '#6b7280' : '#f3f4f6', color: isSelected ? 'white' : '#6b7280', border: isSelected ? 'none' : '1px solid #9ca3af', fontStyle: 'italic' }}>
                      Blank
                    </button>
                  );
                })()}
                {entityWithCounts.map(({ value: entityName, count }: any) => {
                  const isSelected = leadSelectedEntities.includes(entityName);
                  return (
                    <button key={entityName} onClick={() => setLeadSelectedEntities(prev => prev.includes(entityName) ? prev.filter(e => e !== entityName) : [...prev, entityName])}
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: isSelected ? '#00BBA2' : 'var(--dm-surface)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                      {entityName} ({count})
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* Active Filters Display */}
          {hasLeadActiveFilters && (
            <div className="mt-1 pt-1 border-t border-gray-100">
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-[10px] font-medium text-gray-400">Filtering:</span>
                {leadSelectedOwners.map(owner => (
                  <span key={`lo-${owner}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {owner}
                    <button onClick={() => toggleLeadFilter('owner', owner)} className="hover:text-pierian">x</button>
                  </span>
                ))}
                {leadSelectedGeographies.map(geo => (
                  <span key={`lg-${geo}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {geo}
                    <button onClick={() => toggleLeadFilter('geography', geo)} className="hover:text-pierian">x</button>
                  </span>
                ))}
                {leadSelectedSources.map(src => (
                  <span key={`ls-${src}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {src}
                    <button onClick={() => toggleLeadFilter('source', src)} className="hover:text-pierian">x</button>
                  </span>
                ))}
                {leadSelectedTags.map(tagId => {
                  const tag = (tags || []).find((t: any) => t.id === tagId);
                  return tag ? (
                    <span key={`lt-${tagId}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: tag.color ? `${tag.color}20` : 'rgba(0,187,162,0.08)', color: tag.color || '#009E8A' }}>
                      {tag.name}
                      <button onClick={() => toggleLeadFilter('tags', tagId)} className="hover:opacity-70">x</button>
                    </span>
                  ) : null;
                })}
                {leadSelectedEntityGroups.map(group => (
                  <span key={`leg-${group}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.12)', color: '#007A6A' }}>
                    {group}
                    <button onClick={() => setLeadSelectedEntityGroups(prev => prev.filter(g => g !== group))} className="hover:opacity-70">x</button>
                  </span>
                ))}
                {leadSelectedEntities.map(entity => (
                  <span key={`le-${entity}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {entity}
                    <button onClick={() => setLeadSelectedEntities(prev => prev.filter(e => e !== entity))} className="hover:opacity-70">x</button>
                  </span>
                ))}
                {leadSelectedServiceLines.map(sl => (
                  <span key={`lsl-${sl}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {sl}
                    <button onClick={() => toggleLeadFilter('serviceLine', sl)} className="hover:opacity-70">x</button>
                  </span>
                ))}
                {leadSelectedIndustries.map(ind => (
                  <span key={`li-${ind}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {ind}
                    <button onClick={() => toggleLeadFilter('industry', ind)} className="hover:opacity-70">x</button>
                  </span>
                ))}
                {leadSelectedLeadGenerators.map(lg => (
                  <span key={`llg-${lg}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {lg}
                    <button onClick={() => toggleLeadFilter('leadGenerator', lg)} className="hover:opacity-70">x</button>
                  </span>
                ))}
                {leadSelectedLeadSupporters.map(ls => (
                  <span key={`lls-${ls}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {ls}
                    <button onClick={() => toggleLeadFilter('leadSupporter', ls)} className="hover:opacity-70">x</button>
                  </span>
                ))}
                {leadSelectedCities.map(city => (
                  <span key={`lc-${city}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {city}
                    <button onClick={() => toggleLeadFilter('city', city)} className="hover:opacity-70">x</button>
                  </span>
                ))}
                {leadSelectedLeadRatings.map(rating => (
                  <span key={`llr-${rating}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                    style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {rating}
                    <button onClick={() => toggleLeadFilter('leadrating', rating)} className="hover:opacity-70">x</button>
                  </span>
                ))}
                {leadShowReviewOnly && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: '#fef3cd', color: '#92400e' }}>
                    Review Items
                    <button onClick={() => setLeadShowReviewOnly(false)} className="hover:opacity-70">x</button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Dashboard Filter Cards */}
        <div className="flex gap-1.5 items-start">
          {[
            { key: 'all', label: 'All Leads', desc: 'Open + Contacted + Unqualified', count: openLeads.length + contactedLeads.length + disqualifiedLeads.length, revenue: sumRevenue([...openLeads, ...contactedLeads, ...disqualifiedLeads]), bg: isDark ? 'rgba(255,255,255,0.12)' : '#f3f4f6' },
            { key: 'Open', label: 'Open', desc: 'Awaiting qualification', count: openCount, revenue: openRevenue, bg: isDark ? 'rgba(106,124,165,0.42)' : 'rgba(106,124,165,0.18)' },
            { key: 'Contacted', label: 'Contacted', desc: 'In contact with prospect', count: contactedCount, revenue: contactedRevenue, bg: isDark ? 'rgba(59,130,246,0.35)' : 'rgba(59,130,246,0.12)' },
            { key: 'Unqualified', label: 'Unqualified', desc: 'Removed from pipeline', count: disqualifiedCount, revenue: disqualifiedRevenue, bg: isDark ? 'rgba(210,160,40,0.35)' : '#EBCB7E' },
            { key: 'Converted', label: 'Converted', desc: 'Moved to opportunities', count: convertedCount, revenue: convertedRevenue, bg: isDark ? 'rgba(209,86,86,0.38)' : 'rgba(209,86,86,0.12)' },
            { key: 'Lost', label: 'Lost', desc: 'Did not convert', count: lostCount, revenue: lostRevenue, bg: isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb' },
          ].map(card => {
            const isActive = leadStatusFilter === card.key;
            return (
              <div key={card.key} className="flex flex-col items-center group/card">
                <button
                  onClick={() => { setLeadStatusFilter(card.key); setCurrentPage(1); }}
                  className={`rounded-lg px-2.5 py-1 text-center hover:shadow-md transition-all min-w-[70px] ${isActive ? 'shadow-lg ring-2' : ''}`}
                  style={{
                    backgroundColor: isActive ? '#00BBA2' : card.bg,
                    borderColor: isActive ? '#009E8A' : 'transparent',
                    ringColor: isActive ? '#009E8A' : undefined
                  }}
                  title={card.desc}
                >
                  <span className="text-[10px] font-semibold block leading-none" style={{ color: isActive ? '#ffffff' : 'var(--dm-btn-text)' }}>{card.label}</span>
                  <span className="text-base font-bold block leading-tight" style={{ color: isActive ? '#ffffff' : 'var(--dm-text)' }}>{card.count}</span>
                </button>
                <span className="text-[9px] font-bold" style={{ color: '#059669' }}>₹{card.revenue >= 1000000 ? (card.revenue / 1000000).toFixed(2) + 'M' : (card.revenue / 1000).toFixed(0) + 'K'}</span>
                <span className="text-[7px] text-gray-400 italic leading-none opacity-0 group-hover/card:opacity-100 transition-opacity duration-200">{card.desc}</span>
              </div>
            );
          })}
        </div>

        {/* Leads Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-gray-200" style={{ backgroundColor: '#131A25' }}>
                  {[
                    { label: 'Company',    field: 'companyName',       align: 'left',   width: '15%' },
                    { label: 'Contact',    field: 'contactName',       align: 'left',   width: '11%' },
                    { label: 'Status',     field: 'leadStatus',        align: 'center', width: '7%'  },
                    { label: 'Lead Rating', field: 'leadRating',        align: 'center', width: '6%'  },
                    { label: 'Source',     field: 'leadSource',        align: 'left',   width: '8%'  },
                    { label: 'Geo',        field: 'geography',         align: 'left',   width: '7%'  },
                    { label: 'Industry',   field: 'industry',          align: 'left',   width: '8%'  },
                    { label: 'Revenue',    field: 'estimatedRevenue',  align: 'right',  width: '6%'  },
                    { label: 'Age',        field: 'age',               align: 'center', width: '5%'  },
                    { label: 'Created',    field: 'dateCaptured',      align: 'center', width: '7%'  },
                    { label: 'Modified',   field: 'updatedAt',         align: 'center', width: '7%'  },
                    { label: 'Lead Owner',      field: 'leadOwner',      align: 'left',   width: '9%'  },
                    { label: 'Lead Generator',  field: 'leadGenerator',  align: 'left',   width: '9%'  },
                  ].map(col => (
                    <th key={col.field} className={`py-2.5 px-2`} style={{ width: col.width, textAlign: col.align as any }}>
                      <button
                        onClick={() => handleLeadSort(col.field)}
                        className={`inline-flex items-center gap-0.5 text-xs font-semibold text-white hover:text-gray-300 ${col.align === 'right' ? 'flex-row-reverse w-full justify-start' : col.align === 'center' ? 'justify-center w-full' : ''}`}
                      >
                        {col.label}
                        <span className="text-[10px] opacity-60">
                          {leadSortField === col.field ? (leadSortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </button>
                    </th>
                  ))}
                  <th className="text-center py-2.5 px-1 text-xs font-semibold text-white" style={{ width: '13%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="text-center py-8 text-gray-400">
                      {leads.length === 0 ? 'No leads yet. Click "+ New Lead" to create one.' : 'No leads match your filters.'}
                    </td>
                  </tr>
                ) : paginatedLeads.map((lead, idx) => (
                  <tr key={lead.leadId || idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => openLeadView(lead)}>
                    <td className="py-2 px-2" style={{ width: '18%' }}>
                      <div className="font-semibold text-gray-900 text-sm">{lead.companyName}</div>
                      {lead.serviceLine && <div className="text-xs text-gray-500 leading-tight">{lead.serviceLine}</div>}
                      {(() => {
                        // Extract latest entry from engagement history (same logic as opportunities)
                        const src = lead.engagementHistory || lead.comments || '';
                        if (!src.trim()) return null;
                        const entries = src.split('\n').filter((e: string) => {
                          const t = e.trim();
                          if (!t) return false;
                          // Exclude "Marked for Review" / "Unmarked for Review" entries
                          if (/\b(Un)?[Mm]arked for Review\s*$/i.test(t)) return false;
                          return true;
                        });
                        if (entries.length === 0) return null;

                        // Parse date from entry text
                        const parseEntryDate = (text: string) => {
                          // Remove any [timestamp] prefix (handles all locale formats including 24-hour time)
                          const cleaned = text.replace(/^\[[^\]]*\]\s*/, '').trim();
                          const m1 = cleaned.match(/^(\d{1,2})[-\s]+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\w*[-\s]+(\d{2,4})/i);
                          if (m1) {
                            const months: Record<string, number> = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,sept:8,oct:9,nov:10,dec:11 };
                            return new Date(parseInt(m1[3]) < 100 ? 2000 + parseInt(m1[3]) : parseInt(m1[3]), months[m1[2].toLowerCase().substring(0,3)], parseInt(m1[1]));
                          }
                          const m2 = cleaned.match(/^(\d{4})-(\d{2})-(\d{2})/);
                          if (m2) return new Date(parseInt(m2[1]), parseInt(m2[2]) - 1, parseInt(m2[3]));
                          // Try "... on DD/MM/YYYY" at end (legacy conversion entries)
                          const m3 = cleaned.match(/on\s+(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/);
                          if (m3) return new Date(parseInt(m3[3]), parseInt(m3[2]) - 1, parseInt(m3[1]));
                          return null;
                        };

                        // Find entry with most recent date
                        let latestEntry: string | null = null;
                        let latestDate: Date | null = null;
                        for (const entry of entries) {
                          const trimmed = entry.trim();
                          const entryDate = parseEntryDate(trimmed);
                          if (entryDate && (!latestDate || entryDate > latestDate)) {
                            latestDate = entryDate;
                            latestEntry = trimmed.replace(/^\[[^\]]*\]\s*/, '').trim();
                          }
                        }
                        // Fallback: first entry if no dates found
                        if (!latestEntry) {
                          latestEntry = entries[0].trim()
                            .replace(/^\[[^\]]*\]\s*/, '').trim();
                        }

                        // Extract date portion and remaining text
                        const dMatch = latestEntry.match(/^(\d{1,2}[-\s]+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\w*[-\s]+\d{2,4})\s*(.*)/i)
                                    || latestEntry.match(/^(\d{4}-\d{2}-\d{2})\s*(.*)/);
                        let dateBadge: string | null = null;
                        let rest = latestEntry;
                        if (dMatch) {
                          // Format date as dd MMM pillbox (e.g., "26 Feb")
                          const parsed = parseEntryDate(latestEntry);
                          if (parsed) {
                            dateBadge = `${String(parsed.getDate()).padStart(2,'0')} ${parsed.toLocaleString('en-US',{month:'short'})}`;
                          }
                          rest = (dMatch[3] || dMatch[2] || '').trim();
                        } else if (latestDate) {
                          dateBadge = `${String(latestDate.getDate()).padStart(2,'0')} ${latestDate.toLocaleString('en-US',{month:'short'})}`;
                          // Strip trailing "on DD/MM/YYYY" from rest text
                          rest = latestEntry.replace(/\s+on\s+\d{1,2}\/\d{1,2}\/\d{4}\s*$/, '').trim();
                        }
                        const displayText = rest.length > 30 ? rest.substring(0, 30) + '...' : rest;

                        return (displayText || dateBadge) ? (
                          <div className="text-xs text-gray-600 leading-tight mt-0.5" title={src} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {dateBadge && <span className="font-semibold px-1 rounded bg-[rgba(0,187,162,0.1)] text-[#006B5E]">{dateBadge}</span>}{dateBadge && ' '}
                            {displayText}
                          </div>
                        ) : null;
                      })()}
                    </td>
                    <td className="py-2 px-2">
                      {lead.contactPerson && <div className="text-xs font-medium text-gray-800 leading-tight">{lead.contactPerson}</div>}
                      {lead.designation && <div className="text-xs text-gray-500 leading-tight">{lead.designation}</div>}
                      {lead.email && <div className="text-xs text-[#00BBA2] leading-tight truncate" title={lead.email}>{lead.email}</div>}
                      {lead.phone && <div className="text-xs text-gray-500 leading-tight">{lead.phone}</div>}
                    </td>
                    <td className="py-2 px-1 text-center">{getStatusBadge(lead.leadStatus)}</td>
                    <td className="py-2 px-1 text-center">
                      {lead.leadRating ? (
                        <span className={`px-1 py-0.5 rounded text-xs font-medium ${lead.leadRating === 'Hot' ? 'bg-red-100 text-red-700' : lead.leadRating === 'Warm' ? 'bg-amber-100 text-amber-700' : lead.leadRating === 'Cold' ? 'bg-slate-100 text-slate-600' : 'bg-gray-100 text-gray-600'}`}>
                          {lead.leadRating}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-600">{lead.leadSource}</td>
                    <td className="py-2 px-2 text-xs text-gray-600">{lead.geography || 'Domestic'}</td>
                    <td className="py-2 px-2 text-xs text-gray-600">{lead.industry}</td>
                    <td className="py-2 px-2 text-xs text-right text-gray-900 font-medium">
                      {(() => { const v = parseFloat(lead.estimatedRevenue || 0); return v > 0 ? `₹${v.toLocaleString()}` : '—'; })()}
                    </td>
                    <td className="py-2 px-1 text-xs text-center text-gray-600">
                      {lead.dateCaptured ? `${Math.floor((Date.now() - new Date(lead.dateCaptured).getTime()) / (1000 * 60 * 60 * 24))}d` : '-'}
                    </td>
                    <td className="py-2 px-1 text-xs text-center text-gray-600">
                      {lead.dateCaptured ? (() => { const d = new Date(lead.dateCaptured); const dd = String(d.getDate()).padStart(2,'0'); const mon = d.toLocaleString('en-US',{month:'short'}); const yr = d.getFullYear() !== new Date().getFullYear() ? String(d.getFullYear()).slice(-2) : ''; return `${dd}-${mon}${yr ? '-' + yr : ''}`; })() : '-'}
                    </td>
                    <td className="py-2 px-1 text-xs text-center text-gray-600">
                      {lead.updatedAt ? (() => { const d = new Date(lead.updatedAt); const dd = String(d.getDate()).padStart(2,'0'); const mon = d.toLocaleString('en-US',{month:'short'}); const yr = d.getFullYear() !== new Date().getFullYear() ? String(d.getFullYear()).slice(-2) : ''; return `${dd}-${mon}${yr ? '-' + yr : ''}`; })() : '-'}
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-700">{lead.leadOwner || lead.salesRepresentative}</td>
                    <td className="py-2 px-2 text-xs text-gray-700">{lead.leadGenerator || '—'}</td>
                    <td className="py-2 px-1" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        {/* Flag - always visible */}
                        <button onClick={() => handleToggleReview(lead)}
                          className={`px-2 py-1 rounded text-white ${lead.markedForReview ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gray-300 hover:bg-gray-400'}`}
                          title={lead.markedForReview ? 'Remove from review' : 'Flag for review'}>
                          <Flag size={12} className={lead.markedForReview ? 'fill-white' : ''} />
                        </button>
                        {/* Edit - always visible */}
                        <button onClick={() => { setSelectedLead(lead); setLeadViewMode('edit'); setShowLeadModal(true); }}
                          className="px-2 py-1 bg-slate text-white rounded hover:bg-slate-shade" title="Edit Lead">
                          <Edit2 size={12} />
                        </button>
                        {/* Qualify - only for open/new leads */}
                        {lead.leadStatus !== 'Converted' && lead.leadStatus !== 'Unqualified' && lead.leadStatus !== 'Lost' && (
                          <button onClick={() => handleMarkQualified(lead)} disabled={converting === lead.leadId}
                            className="px-2 py-1 bg-sage text-white rounded hover:bg-sage-shade disabled:opacity-50" title="Qualify & Create Opportunity">
                            {converting === lead.leadId ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                          </button>
                        )}
                        {/* View Opportunity - only for converted leads */}
                        {lead.leadStatus === 'Converted' && (
                          <button onClick={() => setActiveTab('opportunities')} className="px-2 py-1 bg-slate text-white rounded hover:bg-slate-shade" title={`View Opportunity ${lead.convertedOpportunityId}`}>
                            <ExternalLink size={12} />
                          </button>
                        )}
                        {/* Disqualify, Mark as Lost, Delete — moved into the Lead modal */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <span className="text-xs text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredLeads.length)} of {filteredLeads.length}
              </span>
              <div className="flex gap-1">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
                  className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-100">Previous</button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let page;
                  if (totalPages <= 7) page = i + 1;
                  else if (currentPage <= 4) page = i + 1;
                  else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
                  else page = currentPage - 3 + i;
                  return (
                    <button key={page} onClick={() => setCurrentPage(page)}
                      className={`px-2 py-1 text-xs border rounded ${currentPage === page ? 'text-white border-transparent' : 'border-gray-300 hover:bg-gray-100'}`}
                      style={currentPage === page ? { backgroundColor: '#00BBA2' } : {}}>{page}</button>
                  );
                })}
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
                  className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-100">Next</button>
              </div>
            </div>
          )}
        </Card>

      </div>
    );
  })();

  // Mock data initialization removed - all data now comes from database
  // See useEffect at line 4031 for database fetching logic

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown-container')) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showProfileDropdown]);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotificationDropdown && !event.target.closest('.notification-dropdown-container')) {
        setShowNotificationDropdown(false);
      }
    };

    if (showNotificationDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showNotificationDropdown]);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'opportunities', label: 'Opportunities', icon: Target },
    { id: 'customers', label: 'Customers', icon: Building },
    { id: 'calendar', label: 'Task Manager', icon: CalendarDays },
    { id: 'copilot', label: 'AI Copilot', icon: Bot }
  ];

  // Dashboard View
  const DashboardView = () => {
    // ========== UNIFIED GLOBAL FILTERS (Multi-Select) ==========
    // Filter category selector - which category to show options for
    const [activeFilterCategory, setActiveFilterCategory] = useState(null);

    // "My Dashboard" toggle - shows only current user's leads & opportunities
    const [showMyDashboard, setShowMyDashboard] = useState(false);

    // Multi-select filter arrays
    const [selectedSalespersons, setSelectedSalespersons] = useState([]);
    const [selectedGeographies, setSelectedGeographies] = useState([]);
    const [selectedServiceLines, setSelectedServiceLines] = useState([]);
    const [selectedServiceOfferings, setSelectedServiceOfferings] = useState([]);
    const [selectedSalesStatuses, setSelectedSalesStatuses] = useState([]);
    const [selectedEntityGroups, setSelectedEntityGroups] = useState([]); // Entity group filter (PSPL/Non-PSPL)
    const [selectedEntities, setSelectedEntities] = useState([]); // Individual entity filter
    const [selectedTags, setSelectedTags] = useState<number[]>([]); // Tags filter
    const [selectedDateRange, setSelectedDateRange] = useState<string | null>(null); // Date range filter
    const [customDateFrom, setCustomDateFrom] = useState<string>(''); // Custom date range - from
    const [customDateTo, setCustomDateTo] = useState<string>(''); // Custom date range - to
    const [showActiveOppsList, setShowActiveOppsList] = useState(true); // Expandable active opportunities list - default to expanded
    const [activeOppsListFilter, setActiveOppsListFilter] = useState<'all' | 'hotWarm' | 'cold'>('all'); // Filter for active opportunities list
    const [leadsListFilter, setLeadsListFilter] = useState<'all' | 'open'>('all'); // Filter for leads list in dashboard funnel
    // ========== READOUT STATE ==========
    const [readoutFromDate, setReadoutFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; });
    const [readoutToDate, setReadoutToDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [readoutExporting, setReadoutExporting] = useState(false); // PDF export in progress
    const readoutContentRef = React.useRef<HTMLDivElement>(null);

    // ========== REPORTS TAB STATE ==========
    const [activeReport, setActiveReport] = useState<string | null>(null);
    const [reportSortField, setReportSortField] = useState('');
    const [reportSortDir, setReportSortDir] = useState<'asc' | 'desc'>('asc');
    const [reportSearch, setReportSearch] = useState('');
    const [reportPage, setReportPage] = useState(1);
    const [reportPageSize, setReportPageSize] = useState(25);

    // ========== OVERVIEW TAB LOCAL FILTERS ==========
    const [ovServiceLine, setOvServiceLine] = useState<string>('');
    const [ovLeadSource, setOvLeadSource] = useState<string>('');
    const [ovMonth, setOvMonth] = useState<string>('');
    const [ovLastUpdated, setOvLastUpdated] = useState<Date>(() => new Date());
    React.useEffect(() => { setOvLastUpdated(new Date()); }, [leads.length, opportunities.length]);

    // Filter categories configuration
    const filterCategories = [
      { id: 'salesperson', label: 'Salesperson', icon: Users },
      { id: 'geography', label: 'Geography', icon: Globe },
      { id: 'serviceLine', label: 'Service Line', icon: Layers },
      { id: 'serviceOffering', label: 'Service Offering', icon: Package },
      { id: 'salesStatus', label: 'Sales Status', icon: Activity },
      { id: 'entityGroup', label: 'Entity Group', icon: Building },
      { id: 'tags', label: 'Tags', icon: Tag },
    ];

    // State for expanded filter categories (to show all options)
    const [expandedCategories, setExpandedCategories] = useState({});

    // Toggle expanded state for a category
    const toggleCategoryExpanded = (categoryId) => {
      setExpandedCategories(prev => ({
        ...prev,
        [categoryId]: !prev[categoryId]
      }));
    };

    // Number of items to show before "Others" button
    const ITEMS_TO_SHOW = 5;

    // Get filter options for each category
    const getFilterOptions = (categoryId) => {
      switch (categoryId) {
        case 'salesperson':
          return Array.from(new Set(users.map(u => u.name))).sort();
        case 'geography':
          // Filter geographies to only show those mapped to the current user
          // Admin users see all geographies, non-admin users see only their mapped geographies
          const allActiveGeographies = geographies.filter(g => g.isActive).map(g => g.name);
          return currentUser?.isAdmin
            ? allActiveGeographies.sort()
            : allActiveGeographies.filter(geo => currentUser?.geographies?.includes(geo)).sort();
        case 'serviceLine':
          return serviceLines.filter(sl => sl.isActive).map(sl => sl.name).sort();
        case 'serviceOffering':
          return serviceOfferings.filter(so => so.isActive).map(so => so.name).sort();
        case 'salesStatus':
          return ['Hot', 'Warm', 'Cold', 'Inactive'];
        case 'tags':
          return (tags || []).filter(t => t.isActive).map(t => ({ id: t.id, name: t.name, color: t.color }));
        default:
          return [];
      }
    };

    // Get selected values for each category
    const getSelectedValues = (categoryId) => {
      switch (categoryId) {
        case 'salesperson':
          return selectedSalespersons;
        case 'geography':
          return selectedGeographies;
        case 'serviceLine':
          return selectedServiceLines;
        case 'serviceOffering':
          return selectedServiceOfferings;
        case 'salesStatus':
          return selectedSalesStatuses;
        case 'entityGroup':
          return selectedEntityGroups;
        case 'entity':
          return selectedEntities;
        case 'tags':
          return selectedTags;
        default:
          return [];
      }
    };

    // Toggle selection for a filter value
    const toggleFilterSelection = (categoryId, value) => {
      switch (categoryId) {
        case 'salesperson':
          setSelectedSalespersons(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
          );
          break;
        case 'geography':
          setSelectedGeographies(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
          );
          break;
        case 'serviceLine':
          setSelectedServiceLines(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
          );
          break;
        case 'serviceOffering':
          setSelectedServiceOfferings(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
          );
          break;
        case 'salesStatus':
          setSelectedSalesStatuses(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
          );
          break;
        case 'entityGroup':
          setSelectedEntityGroups(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
          );
          break;
        case 'entity':
          setSelectedEntities(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
          );
          break;
        case 'tags':
          // value is tag ID (number)
          setSelectedTags(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
          );
          break;
      }
    };

    // Clear all filters for a category
    const clearCategoryFilters = (categoryId) => {
      switch (categoryId) {
        case 'salesperson':
          setSelectedSalespersons([]);
          break;
        case 'geography':
          setSelectedGeographies([]);
          break;
        case 'serviceLine':
          setSelectedServiceLines([]);
          break;
        case 'serviceOffering':
          setSelectedServiceOfferings([]);
          break;
        case 'salesStatus':
          setSelectedSalesStatuses([]);
          break;
        case 'entityGroup':
          setSelectedEntityGroups([]);
          setSelectedEntities([]); // Also clear individual entities when clearing entity group
          break;
        case 'entity':
          setSelectedEntities([]);
          break;
      }
    };

    // Clear all filters
    const clearAllFilters = () => {
      setShowMyDashboard(false);
      setSelectedSalespersons([]);
      setSelectedGeographies([]);
      setSelectedServiceLines([]);
      setSelectedServiceOfferings([]);
      setSelectedSalesStatuses([]);
      setSelectedEntityGroups([]);
      setSelectedEntities([]);
      setSelectedDateRange(null);
      setCustomDateFrom('');
      setCustomDateTo('');
      setActiveFilterCategory(null);
    };

    // Check if any filters are active
    const hasActiveFilters = showMyDashboard ||
      selectedSalespersons.length > 0 ||
      selectedGeographies.length > 0 ||
      selectedServiceLines.length > 0 ||
      selectedServiceOfferings.length > 0 ||
      selectedSalesStatuses.length > 0 ||
      selectedEntityGroups.length > 0 ||
      selectedEntities.length > 0 ||
      selectedDateRange !== null;

    // Get total active filter count
    const totalActiveFilterCount = selectedSalespersons.length +
      selectedGeographies.length +
      selectedServiceLines.length +
      selectedServiceOfferings.length +
      selectedSalesStatuses.length +
      selectedEntityGroups.length +
      selectedEntities.length +
      (selectedDateRange ? 1 : 0);

    // Generate date range options based on available opportunity data
    const getDateRangeOptions = () => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const currentQuarter = Math.floor(currentMonth / 3);

      // Find the earliest year in the data
      const years = opportunities
        .map(o => {
          const date = o.initiationDate || o.createdAt;
          return date ? new Date(date).getFullYear() : null;
        })
        .filter(y => y !== null && y > 2000 && y <= currentYear);

      const minYear = years.length > 0 ? Math.min(...years) : currentYear - 2;

      const options = [
        { id: 'last1month', label: 'Last 1 Month', getDateRange: () => {
          const start = new Date(now);
          start.setMonth(start.getMonth() - 1);
          return { start, end: now };
        }},
        { id: 'lastQuarter', label: 'Last Quarter', getDateRange: () => {
          const quarterStart = new Date(currentYear, currentQuarter * 3, 1);
          const prevQuarterStart = new Date(quarterStart);
          prevQuarterStart.setMonth(prevQuarterStart.getMonth() - 3);
          const prevQuarterEnd = new Date(quarterStart);
          prevQuarterEnd.setDate(prevQuarterEnd.getDate() - 1);
          return { start: prevQuarterStart, end: prevQuarterEnd };
        }},
        { id: 'ytd', label: 'YTD (Year to Date)', getDateRange: () => {
          const start = new Date(currentYear, 0, 1);
          return { start, end: now };
        }},
      ];

      // Add previous years based on available data
      for (let year = currentYear - 1; year >= minYear && year >= currentYear - 5; year--) {
        options.push({
          id: `year${year}`,
          label: `${year}`,
          getDateRange: () => ({
            start: new Date(year, 0, 1),
            end: new Date(year, 11, 31, 23, 59, 59)
          })
        });
      }

      return options;
    };

    // Get the date range for filtering based on selected option
    const getSelectedDateRange = () => {
      // Handle custom date range
      if (selectedDateRange === 'custom') {
        if (customDateFrom && customDateTo) {
          return {
            start: new Date(customDateFrom),
            end: new Date(customDateTo + 'T23:59:59') // Include full end day
          };
        }
        return null;
      }
      if (!selectedDateRange) return null;
      const options = getDateRangeOptions();
      const selected = options.find(o => o.id === selectedDateRange);
      return selected ? selected.getDateRange() : null;
    };

    // Get label for currently selected date range
    const getSelectedDateRangeLabel = () => {
      if (selectedDateRange === 'custom' && customDateFrom && customDateTo) {
        const fromDate = new Date(customDateFrom);
        const toDate = new Date(customDateTo);
        const formatDate = (d: Date) => `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
        return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
      }
      if (!selectedDateRange) return null;
      const options = getDateRangeOptions();
      const selected = options.find(o => o.id === selectedDateRange);
      return selected?.label || selectedDateRange;
    };

    // State for activity search and pagination
    const [activitySearchTerm, setActivitySearchTerm] = useState('');
    const [activityPage, setActivityPage] = useState(1);
    const activitiesPerPage = 20; // Show 20 activities per page
    const [activityMovementFilter, setActivityMovementFilter] = useState<string | null>(null); // Clickable movement filter: 'newLeads' | 'convertedLeads' | 'newOpps' | 'stageChanges' | 'updated' | 'won' | 'lost' | null

    // Reset page to 1 when search term or filters change
    useEffect(() => {
      setActivityPage(1);
    }, [activitySearchTerm, selectedSalespersons, selectedGeographies, selectedServiceLines, selectedServiceOfferings, selectedSalesStatuses, selectedDateRange, customDateFrom, customDateTo]);

    // Get filtered opportunities excluding specific category(s) (for cascading filter counts)
    // This allows each filter to show counts based on other selected filters
    // excludeCategory can be a string or array of strings
    const getFilteredOpportunitiesExcluding = (excludeCategory) => {
      // Normalize to array for consistent checking
      const excludeCategories = Array.isArray(excludeCategory) ? excludeCategory : [excludeCategory];

      // Start with only active opportunities (excluding Won, Lost, and Inactive), filtered by user's geography access
      let filtered = opportunities.filter(o => {
        // User Geography Access Control
        // Admin users see all records
        // Non-admin users with no geography mapped see NO records
        // Non-admin users with geography mapped see only their geography records
        const matchesUserGeography =
          currentUser?.isAdmin || // Admin sees all records
          (currentUser?.geographies?.length > 0 && // User has geographies mapped
           o.geography && // Opportunity has geography
           currentUser.geographies.includes(o.geography)); // Geography matches
        return matchesUserGeography &&
          o.salesStage !== 'Closed-Won' &&
          o.salesStage !== 'Closed-Lost' &&
          getEffectiveSalesStatus(o) !== 'Inactive';
      });

      // Apply "My Dashboard" filter (always applies in cross-filter)
      if (showMyDashboard && currentUser?.name) {
        const myName = currentUser.name.trim();
        filtered = filtered.filter(o => o.accountOwner?.trim() === myName || o.leadSupporter?.trim() === myName);
      }

      // Apply salesperson filter (unless excluded)
      if (!excludeCategories.includes('salesperson') && selectedSalespersons.length > 0) {
        filtered = filtered.filter(o =>
          selectedSalespersons.includes(o.leadSupporter) ||
          selectedSalespersons.includes(o.accountOwner)
        );
      }

      // Apply geography filter (unless excluded) - case-insensitive matching
      if (!excludeCategories.includes('geography') && selectedGeographies.length > 0) {
        const selectedLower = selectedGeographies.map(g => g.trim().toLowerCase());
        filtered = filtered.filter(o => o.geography && selectedLower.includes(o.geography.trim().toLowerCase()));
      }

      // Apply service line filter (unless excluded) - case-insensitive matching
      if (!excludeCategories.includes('serviceLine') && selectedServiceLines.length > 0) {
        const selectedLower = selectedServiceLines.map(s => s.trim().toLowerCase());
        filtered = filtered.filter(o => o.serviceLine && selectedLower.includes(o.serviceLine.trim().toLowerCase()));
      }

      // Apply service offering filter (unless excluded)
      if (!excludeCategories.includes('serviceOffering') && selectedServiceOfferings.length > 0) {
        filtered = filtered.filter(o => selectedServiceOfferings.includes(o.serviceOffering));
      }

      // Apply sales status filter (unless excluded) - case-insensitive matching
      // Use getEffectiveSalesStatus which handles null lastEditedDate (Excel imports)
      if (!excludeCategories.includes('salesStatus') && selectedSalesStatuses.length > 0) {
        const selectedLower = selectedSalesStatuses.map(s => s.trim().toLowerCase());
        filtered = filtered.filter(o => {
          const effectiveStatus = getEffectiveSalesStatus(o);
          return selectedLower.includes(effectiveStatus.trim().toLowerCase());
        });
      }

      // Apply entity group filter (unless excluded) - lookup entity's group from entities master data
      if (!excludeCategories.includes('entityGroup') && selectedEntityGroups.length > 0) {
        filtered = filtered.filter(o => {
          if (!o.entity) {
            // Opportunities without entity match "Ungrouped"
            return selectedEntityGroups.includes('Ungrouped');
          }
          const entityData = entities.find(e => e.name === o.entity);
          if (!entityData) {
            // Entity not found matches "Ungrouped"
            return selectedEntityGroups.includes('Ungrouped');
          }
          // Check if entity has no group (matches "Ungrouped") or matches selected group
          if (!entityData.entityGroup || entityData.entityGroup === '') {
            return selectedEntityGroups.includes('Ungrouped');
          }
          return selectedEntityGroups.includes(entityData.entityGroup);
        });
      }

      // Apply individual entity filter (unless excluded)
      // Handle "Blank" selection for opportunities with no entity
      if (!excludeCategories.includes('entity') && selectedEntities.length > 0) {
        filtered = filtered.filter(o => {
          // Handle "Blank" selection - matches opportunities with no entity
          if (selectedEntities.includes('Blank') && (!o.entity || o.entity.trim() === '')) {
            return true;
          }
          // Match specific entities (excluding "Blank" from the check)
          const actualEntities = selectedEntities.filter(e => e !== 'Blank');
          if (actualEntities.length > 0 && o.entity && actualEntities.includes(o.entity)) {
            return true;
          }
          // If only "Blank" is selected and entity is not blank, don't match
          if (selectedEntities.length === 1 && selectedEntities.includes('Blank')) {
            return !o.entity || o.entity.trim() === '';
          }
          return actualEntities.length === 0;
        });
      }

      // Apply date range filter (unless excluded)
      if (!excludeCategories.includes('dateRange') && selectedDateRange) {
        const dateRange = getSelectedDateRange();
        if (dateRange) {
          filtered = filtered.filter(o => {
            const oppDate = o.initiationDate || o.createdAt;
            if (!oppDate) return false;
            const date = new Date(oppDate);
            return date >= dateRange.start && date <= dateRange.end;
          });
        }
      }

      return filtered;
    };

    // Mirror of dashboardLeads but with one filter dimension excluded (for cross-filter counts)
    const getDashboardLeadsExcluding = (excludeCategory) => {
      const excludeCategories = Array.isArray(excludeCategory) ? excludeCategory : [excludeCategory];
      let filtered = leads.filter(l =>
        currentUser?.isAdmin ||
        (currentUser?.geographies?.length > 0 && l.geography && currentUser.geographies.includes(l.geography))
      );
      if (showMyDashboard && currentUser?.name) {
        const myName = currentUser.name.trim();
        filtered = filtered.filter(l => (l.leadOwner || l.salesRepresentative)?.trim() === myName);
      }
      if (!excludeCategories.includes('geography') && selectedGeographies.length > 0) {
        const selLower = selectedGeographies.map(g => g.trim().toLowerCase());
        filtered = filtered.filter(l => l.geography && selLower.includes(l.geography.trim().toLowerCase()));
      }
      if (!excludeCategories.includes('salesperson') && selectedSalespersons.length > 0) {
        filtered = filtered.filter(l =>
          selectedSalespersons.includes(l.leadOwner) || selectedSalespersons.includes(l.salesRepresentative)
        );
      }
      if (!excludeCategories.includes('serviceLine') && selectedServiceLines.length > 0) {
        const selLower = selectedServiceLines.map(s => s.trim().toLowerCase());
        filtered = filtered.filter(l => l.serviceLine && selLower.includes(l.serviceLine.trim().toLowerCase()));
      }
      if (!excludeCategories.includes('salesStatus') && selectedSalesStatuses.length > 0) {
        const selLower = selectedSalesStatuses.map(s => s.trim().toLowerCase());
        filtered = filtered.filter(l => selLower.includes((l.leadRating || '').trim().toLowerCase()));
      }
      if (!excludeCategories.includes('entityGroup') && selectedEntityGroups.length > 0) {
        filtered = filtered.filter(l => {
          if (!l.entity) return selectedEntityGroups.includes('Ungrouped');
          const entityData = entities.find(e => e.name === l.entity);
          if (!entityData) return selectedEntityGroups.includes('Ungrouped');
          if (!entityData.entityGroup || entityData.entityGroup === '') return selectedEntityGroups.includes('Ungrouped');
          return selectedEntityGroups.includes(entityData.entityGroup);
        });
      }
      if (!excludeCategories.includes('entity') && selectedEntities.length > 0) {
        filtered = filtered.filter(l => {
          if (selectedEntities.includes('Blank') && (!l.entity || l.entity.trim() === '')) return true;
          const actualEntities = selectedEntities.filter(e => e !== 'Blank');
          if (actualEntities.length > 0 && l.entity && actualEntities.includes(l.entity)) return true;
          if (selectedEntities.length === 1 && selectedEntities.includes('Blank')) return !l.entity || l.entity.trim() === '';
          return actualEntities.length === 0;
        });
      }
      if (!excludeCategories.includes('dateRange') && selectedDateRange) {
        const dateRange = getSelectedDateRange();
        if (dateRange) {
          filtered = filtered.filter(l => {
            const d = l.dateCaptured || l.lastContacted;
            if (!d) return false;
            const date = new Date(d);
            return date >= dateRange.start && date <= dateRange.end;
          });
        }
      }
      return filtered;
    };

    // Filter opportunities based on unified multi-select filters
    const getFilteredOpportunities = () => {
      // Start with all opportunities, filtered by user's geography access
      // User Geography Access Control
      // Admin users see all records
      // Non-admin users with no geography mapped see NO records
      // Non-admin users with geography mapped see only their geography records
      let filtered = opportunities.filter(opp => {
        if ((opp.opportunityName || '').toLowerCase().includes('test')) return false;
        if ((opp.associatedAccount || '').toLowerCase().includes('test')) return false;
        const matchesUserGeography =
          currentUser?.isAdmin || // Admin sees all records
          (currentUser?.geographies?.length > 0 && // User has geographies mapped
           opp.geography && // Opportunity has geography
           currentUser.geographies.includes(opp.geography)); // Geography matches
        return matchesUserGeography;
      });

      // Apply "My Dashboard" filter
      if (showMyDashboard && currentUser?.name) {
        const myName = currentUser.name.trim();
        filtered = filtered.filter(o => o.accountOwner?.trim() === myName || o.leadSupporter?.trim() === myName);
      }

      // Apply salesperson filter (using leadSupporter or accountOwner)
      if (selectedSalespersons.length > 0) {
        filtered = filtered.filter(o =>
          selectedSalespersons.includes(o.leadSupporter) ||
          selectedSalespersons.includes(o.accountOwner)
        );
      }

      // Apply geography filter - case-insensitive matching
      if (selectedGeographies.length > 0) {
        const selectedLower = selectedGeographies.map(g => g.trim().toLowerCase());
        filtered = filtered.filter(o => o.geography && selectedLower.includes(o.geography.trim().toLowerCase()));
      }

      // Apply service line filter - case-insensitive matching
      if (selectedServiceLines.length > 0) {
        const selectedLower = selectedServiceLines.map(s => s.trim().toLowerCase());
        filtered = filtered.filter(o => o.serviceLine && selectedLower.includes(o.serviceLine.trim().toLowerCase()));
      }

      // Apply service offering filter
      if (selectedServiceOfferings.length > 0) {
        filtered = filtered.filter(o => selectedServiceOfferings.includes(o.serviceOffering));
      }

      // Apply sales status filter - case-insensitive matching
      // Use getEffectiveSalesStatus which handles null lastEditedDate (Excel imports)
      if (selectedSalesStatuses.length > 0) {
        const selectedLower = selectedSalesStatuses.map(s => s.trim().toLowerCase());
        filtered = filtered.filter(o => {
          const effectiveStatus = getEffectiveSalesStatus(o);
          return selectedLower.includes(effectiveStatus.trim().toLowerCase());
        });
      }

      // Apply date range filter
      // Filter based on initiationDate (or createdAt as fallback)
      if (selectedDateRange) {
        const dateRange = getSelectedDateRange();
        if (dateRange) {
          filtered = filtered.filter(o => {
            const oppDate = o.initiationDate || o.createdAt;
            if (!oppDate) return false;
            const date = new Date(oppDate);
            return date >= dateRange.start && date <= dateRange.end;
          });
        }
      }

      // Apply entity group filter - lookup entity's group from entities master data
      if (selectedEntityGroups.length > 0) {
        filtered = filtered.filter(o => {
          if (!o.entity) {
            // Opportunities without entity match "Ungrouped"
            return selectedEntityGroups.includes('Ungrouped');
          }
          const entityData = entities.find(e => e.name === o.entity);
          if (!entityData) {
            // Entity not found matches "Ungrouped"
            return selectedEntityGroups.includes('Ungrouped');
          }
          // Check if entity has no group (matches "Ungrouped") or matches selected group
          if (!entityData.entityGroup || entityData.entityGroup === '') {
            return selectedEntityGroups.includes('Ungrouped');
          }
          return selectedEntityGroups.includes(entityData.entityGroup);
        });
      }

      // Apply individual entity filter
      // Handle "Blank" selection for opportunities with no entity
      if (selectedEntities.length > 0) {
        filtered = filtered.filter(o => {
          // Handle "Blank" selection - matches opportunities with no entity
          if (selectedEntities.includes('Blank') && (!o.entity || o.entity.trim() === '')) {
            return true;
          }
          // Match specific entities (excluding "Blank" from the check)
          const actualEntities = selectedEntities.filter(e => e !== 'Blank');
          if (actualEntities.length > 0 && o.entity && actualEntities.includes(o.entity)) {
            return true;
          }
          // If only "Blank" is selected and entity is not blank, don't match
          if (selectedEntities.length === 1 && selectedEntities.includes('Blank')) {
            return !o.entity || o.entity.trim() === '';
          }
          return actualEntities.length === 0;
        });
      }

      // Apply tags filter
      // Match opportunities that have at least one of the selected tags
      if (selectedTags && selectedTags.length > 0) {
        filtered = filtered.filter(o => {
          if (!o.tags || o.tags.length === 0) return false;
          return o.tags.some(tag => selectedTags.includes(tag.id));
        });
      }

      return filtered;
    };

    const filteredOpportunities = getFilteredOpportunities();

    // ========== DASHBOARD LEADS (filtered by user geography + global filters) ==========
    // Apply same access control + dashboard filters to leads as we do for opportunities
    const dashboardLeads = (() => {
      // Step 1: User geography access control (exclude test records)
      let filtered = leads.filter(l => {
        return currentUser?.isAdmin ||
          (currentUser?.geographies?.length > 0 &&
           l.geography &&
           currentUser.geographies.includes(l.geography));
      });
      // Step 2: Apply "My Dashboard" filter
      if (showMyDashboard && currentUser?.name) {
        const myName = currentUser.name.trim();
        filtered = filtered.filter(l => (l.leadOwner || l.salesRepresentative)?.trim() === myName);
      }
      // Step 3: Apply global dashboard filters
      if (selectedGeographies.length > 0) {
        const selLower = selectedGeographies.map(g => g.trim().toLowerCase());
        filtered = filtered.filter(l => l.geography && selLower.includes(l.geography.trim().toLowerCase()));
      }
      if (selectedSalespersons.length > 0) {
        filtered = filtered.filter(l =>
          selectedSalespersons.includes(l.leadOwner) ||
          selectedSalespersons.includes(l.salesRepresentative)
        );
      }
      if (selectedServiceLines.length > 0) {
        const selLower = selectedServiceLines.map(s => s.trim().toLowerCase());
        filtered = filtered.filter(l => l.serviceLine && selLower.includes(l.serviceLine.trim().toLowerCase()));
      }
      if (selectedSalesStatuses.length > 0) {
        const selLower = selectedSalesStatuses.map(s => s.trim().toLowerCase());
        filtered = filtered.filter(l => selLower.includes((l.leadRating || '').trim().toLowerCase()));
      }
      if (selectedEntityGroups.length > 0) {
        filtered = filtered.filter(l => {
          if (!l.entity) return selectedEntityGroups.includes('Ungrouped');
          const entityData = entities.find(e => e.name === l.entity);
          if (!entityData) return selectedEntityGroups.includes('Ungrouped');
          if (!entityData.entityGroup || entityData.entityGroup === '') return selectedEntityGroups.includes('Ungrouped');
          return selectedEntityGroups.includes(entityData.entityGroup);
        });
      }
      if (selectedEntities.length > 0) {
        filtered = filtered.filter(l => {
          if (selectedEntities.includes('Blank') && (!l.entity || l.entity.trim() === '')) return true;
          const actualEntities = selectedEntities.filter(e => e !== 'Blank');
          if (actualEntities.length > 0 && l.entity && actualEntities.includes(l.entity)) return true;
          if (selectedEntities.length === 1 && selectedEntities.includes('Blank')) return !l.entity || l.entity.trim() === '';
          return actualEntities.length === 0;
        });
      }
      if (selectedDateRange) {
        const dateRange = getSelectedDateRange();
        if (dateRange) {
          filtered = filtered.filter(l => {
            const leadDate = l.dateCaptured || l.lastContacted;
            if (!leadDate) return false;
            const date = new Date(leadDate);
            return date >= dateRange.start && date <= dateRange.end;
          });
        }
      }
      return filtered;
    })();

    // ========== LEADS METRICS FOR DASHBOARD ==========
    // Categorize leads by their actual status to match Leads tab exactly
    const openLeads = dashboardLeads.filter(l => l.leadStatus === 'Open' || (!l.leadStatus));
    const convertedLeads = dashboardLeads.filter(l => l.leadStatus === 'Converted');
    const openLeadsPipelineValue = openLeads.reduce((sum, l) => sum + (l.estimatedRevenue || 0), 0);
    const convertedLeadsPipelineValue = convertedLeads.reduce((sum, l) => sum + (l.estimatedRevenue || 0), 0);

    // Active opportunities (excluding Won, Lost, Contract, Assessment, Lead stages, and Inactive status) for dashboard metrics
    const activeOpportunities = filteredOpportunities.filter(opp => {
      const stage = (opp.salesStage || '').toLowerCase();
      return !stage.includes('won') &&
        !stage.includes('lost') &&
        stage !== 'contract' &&
        stage !== 'assessment' &&
        !stage.includes('lead') &&
        getEffectiveSalesStatus(opp) !== 'Inactive';
    });

    // Chart-filtered opportunities based on activeOppsListFilter (for dashboard charts)
    const chartFilteredOpportunities = activeOpportunities.filter(opp => {
      if (activeOppsListFilter === 'all') return true;
      const status = getEffectiveSalesStatus(opp);
      if (activeOppsListFilter === 'hotWarm') return status === 'Hot' || status === 'Warm';
      if (activeOppsListFilter === 'cold') return status === 'Cold';
      return true;
    });

    // Calculate pipeline data from filtered opportunities (excluding Won and Lost for active pipeline)
    // Using same colors as stage cards in Opportunities tab
    const pipelineStagesForDashboard = [
      { name: 'Qualified', color: 'rgba(106,124,165,0.18)' },         // slate-tint
      { name: 'Discovery', color: 'rgba(0,187,162,0.08)' },           // teal-tint
      { name: 'Proposal', color: '#E9C1BE' },                         // clay-tint
      { name: 'Contract', color: '#d1fae5' }                          // emerald-tint
    ];

    // Build unified pipeline data: Open Leads → opportunity stages (Qualified onward)
    // Includes leadAmount and oppAmount for stacked chart visualization
    const pipelineData = (() => {
      const data = [];

      // 1. Open Leads (status=Open or null, matching Leads tab)
      const openLeadAmt = openLeads.reduce((sum, l) => sum + (l.estimatedRevenue || 0), 0);
      data.push({
        stage: 'Open Leads',
        value: openLeads.length,
        amount: openLeadAmt,
        leadAmount: openLeadAmt,
        oppAmount: 0,
        fill: '#D8A39D',  // Clay Pink
        type: 'lead'
      });

      // 2. Opportunity stages (Qualified onward)
      pipelineStagesForDashboard.forEach(({ name: stage, color }) => {
        const stageOpps = chartFilteredOpportunities.filter(opp => opp.salesStage === stage);
        const stageOppAmt = stageOpps.reduce((sum, opp) => sum + (opp.annualContractValue || 0), 0);
        data.push({
          stage,
          value: stageOpps.length,
          amount: stageOppAmt,
          leadAmount: 0,
          oppAmount: stageOppAmt,
          fill: color,
          type: 'opportunity'
        });
      });

      return data;
    })();

    // Calculate weekly leads & opportunities progress (last 8 weeks)
    // Tracks: new leads captured, leads converted, cumulative open leads, active opportunities
    const performanceData = (() => {
      const now = new Date();
      const last8Weeks = [];

      // Generate last 8 weeks
      for (let i = 7; i >= 0; i--) {
        // Calculate the start of each week (Monday)
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - (i * 7) - now.getDay() + 1);
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const isCurrentWeek = i === 0;

        // Format week label
        const day = weekStart.getDate();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[weekStart.getMonth()];
        const weekLabel = `${day} ${month}`;

        // --- Opportunity metrics ---
        let weekOppsCount: number;
        let weekRevenue: number;

        if (isCurrentWeek) {
          weekOppsCount = chartFilteredOpportunities.length;
          weekRevenue = chartFilteredOpportunities.reduce((sum, opp) => sum + (opp.annualContractValue || 0), 0);
        } else {
          const weekOpps = chartFilteredOpportunities.filter(opp => {
            if (opp.initiationDate) {
              const initDate = new Date(opp.initiationDate);
              if (initDate > weekEnd) return false;
            }
            return true;
          });
          weekOppsCount = weekOpps.length;
          weekRevenue = weekOpps.reduce((sum, opp) => sum + (opp.annualContractValue || 0), 0);
        }

        // --- Lead metrics (using geography + filter-scoped dashboardLeads) ---
        // New leads captured this week
        const newLeadsThisWeek = dashboardLeads.filter(l => {
          if (!l.dateCaptured) return false;
          const captured = new Date(l.dateCaptured);
          return captured >= weekStart && captured <= weekEnd;
        }).length;

        // Leads converted to opportunities this week
        const convertedThisWeek = dashboardLeads.filter(l => {
          if (!l.convertedAt) return false;
          const converted = new Date(l.convertedAt);
          return converted >= weekStart && converted <= weekEnd;
        }).length;

        // Cumulative open leads as of week end (captured by week end AND not yet converted by week end)
        const openLeadsAsOfWeekEnd = dashboardLeads.filter(l => {
          if (!l.dateCaptured) return false;
          const captured = new Date(l.dateCaptured);
          if (captured > weekEnd) return false; // Not yet captured
          if (l.convertedAt) {
            const converted = new Date(l.convertedAt);
            if (converted <= weekEnd) return false; // Already converted by week end
          }
          if (l.leadStatus === 'Converted' && !l.convertedAt) return false; // Converted but no date — exclude
          return true;
        }).length;

        last8Weeks.push({
          month: weekLabel,
          opportunities: weekOppsCount,
          revenue: weekRevenue,
          newLeads: newLeadsThisWeek,
          converted: convertedThisWeek,
          openLeads: openLeadsAsOfWeekEnd
        });
      }

      return last8Weeks;
    })();

    // Calculate service line distribution from filtered opportunities using all service lines from master table
    // Pierian brand chart colors
    const colors = ['#00BBA2', '#6A7CA5', '#9CAF88', '#D9A441', '#D8A39D', '#481F89', '#6B4C47', '#009E8A', '#4F0E32', '#8FA0C1'];

    // Initialize revenue for all active service lines from master table (case-insensitive lookup with deduplication)
    const serviceLineRevenue = {};
    const serviceLineNameMap = {}; // lowercase -> display name mapping

    serviceLines.filter(sl => sl.isActive).forEach(sl => {
      const trimmedName = sl.name.trim();
      const lowerKey = trimmedName.toLowerCase();
      // Only add if not already present (deduplicate by lowercase key)
      if (!serviceLineNameMap[lowerKey]) {
        serviceLineRevenue[trimmedName] = 0;
        serviceLineNameMap[lowerKey] = trimmedName;
      }
    });

    // Calculate revenue from chart-filtered opportunities only (excluding Won and Lost)
    chartFilteredOpportunities.forEach(opp => {
      const sl = opp.serviceLine?.trim(); // Trim whitespace
      if (sl) {
        const slLower = sl.toLowerCase();
        // Find matching service line (case-insensitive)
        const matchedName = serviceLineNameMap[slLower];
        if (matchedName) {
          serviceLineRevenue[matchedName] += (opp.annualContractValue || 0);
        } else {
          // Service line not in master table - add it dynamically
          if (!serviceLineNameMap[slLower]) {
            serviceLineRevenue[sl] = 0;
            serviceLineNameMap[slLower] = sl;
          }
          serviceLineRevenue[serviceLineNameMap[slLower]] += (opp.annualContractValue || 0);
        }
      }
    });

    const totalRevenue = Object.values(serviceLineRevenue).reduce((sum, val) => sum + val, 0);

    const serviceLineData = Object.entries(serviceLineRevenue)
      .map(([name, value], idx) => ({
        name,
        value: totalRevenue > 0 ? Math.round((value / totalRevenue) * 100) : 0,
        amount: value,
        color: colors[idx % colors.length]
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.amount - a.amount);

    // Get active filter summary for display
    const getActiveFilterSummary = () => {
      const parts = [];

      if (selectedSalespersons.length > 0) {
        parts.push(`Salesperson: ${selectedSalespersons.length === 1 ? selectedSalespersons[0] : `${selectedSalespersons.length} selected`}`);
      }
      if (selectedGeographies.length > 0) {
        parts.push(`Geography: ${selectedGeographies.length === 1 ? selectedGeographies[0] : `${selectedGeographies.length} selected`}`);
      }
      if (selectedServiceLines.length > 0) {
        parts.push(`Service Line: ${selectedServiceLines.length === 1 ? selectedServiceLines[0] : `${selectedServiceLines.length} selected`}`);
      }
      if (selectedServiceOfferings.length > 0) {
        parts.push(`Service Offering: ${selectedServiceOfferings.length === 1 ? selectedServiceOfferings[0] : `${selectedServiceOfferings.length} selected`}`);
      }
      if (selectedSalesStatuses.length > 0) {
        parts.push(`Sales Status: ${selectedSalesStatuses.length === 1 ? selectedSalesStatuses[0] : `${selectedSalesStatuses.length} selected`}`);
      }

      return parts.length > 0 ? parts.join(' | ') : 'All Data';
    };
    
    // Calculate recent activities from actual opportunities
    const recentActivities = (() => {
      const activities = [];

      // Add opportunity activities (recent opportunities)
      filteredOpportunities
        .filter(opp => opp.initiationDate)
        .sort((a, b) => new Date(b.initiationDate) - new Date(a.initiationDate))
        .slice(0, 10)
        .forEach(opp => {
          const daysAgo = Math.floor((new Date() - new Date(opp.initiationDate)) / (1000 * 60 * 60 * 24));

          // Determine activity type based on stage
          let action = 'Opportunity created';
          let icon = Target;
          let color = 'blue';

          if (opp.salesStage === 'Closed-Won') {
            action = 'Deal closed';
            icon = CheckCircle;
            color = 'green';
          } else if (['Proposal', 'Contract'].includes(opp.salesStage)) {
            action = 'Opportunity updated';
            icon = Target;
            color = 'green';
          }

          const revenue = opp.annualContractValue || opp.estimatedRevenue || 0;
          const revenueText = revenue > 0 ? ` - ₹${(revenue / 1000).toFixed(0)}K` : '';

          activities.push({
            id: opp.opportunityId,
            type: 'opportunity',
            action: action,
            details: `${opp.opportunityName}${opp.salesStage ? ` - ${opp.salesStage}` : ''}${revenueText}`,
            time: daysAgo === 0 ? 'Today' : `${daysAgo}d ago`,
            daysAgo: daysAgo,
            user: opp.leadSupporter || opp.accountOwner || 'CRM Admin',
            icon: icon,
            color: color
          });
        });

      return activities.slice(0, 10);
    })();

    // Calculate team performance from actual opportunities
    // Top Sales uses ONLY leadSupporter (not accountOwner) to count deals per salesperson
    const teamPerformance = (() => {
      const performanceByPerson = {};

      filteredOpportunities.forEach(opp => {
        // Use ONLY leadSupporter for Top Sales - this is the assigned salesperson for the deal
        const person = opp.leadSupporter && opp.leadSupporter.trim();

        // Skip if no leadSupporter assigned
        if (!person) return;

        if (!performanceByPerson[person]) {
          performanceByPerson[person] = {
            name: person,
            opportunities: 0,
            revenue: 0,
            won: 0,
            total: 0
          };
        }

        performanceByPerson[person].opportunities++;
        performanceByPerson[person].revenue += (opp.annualContractValue || 0);
        performanceByPerson[person].total++;

        // Check for Closed-Won or Contract stage
        if (opp.salesStage && (opp.salesStage === 'Closed-Won' || opp.salesStage === 'Contract')) {
          performanceByPerson[person].won++;
        }
      });

      // Debug logging
      console.log('📊 Top Sales Debug:');
      console.log('Total filtered opportunities:', filteredOpportunities.length);
      Object.entries(performanceByPerson).forEach(([name, data]) => {
        console.log(`  ${name}: ${data.opportunities} deals, ₹${data.revenue} revenue, ${data.won} won`);
      });

      // Calculate win rate and return top performers
      return Object.values(performanceByPerson)
        .map(person => ({
          name: person.name,
          opportunities: person.opportunities,
          revenue: person.revenue,
          winRate: person.total > 0 ? Math.round((person.won / person.total) * 100) : 0
        }))
        .filter(person => person.name && person.name.trim() !== '')
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
    })();
    
    const upcomingTasks = [
      { id: 1, task: 'Follow up with TechCorp', due: 'Today 2:00 PM', priority: 'High' },
      { id: 2, task: 'Send proposal to Global Finance', due: 'Today 4:00 PM', priority: 'High' },
      { id: 3, task: 'Demo preparation', due: 'Tomorrow 10:00 AM', priority: 'Medium' },
      { id: 4, task: 'Contract review', due: 'Tomorrow 3:00 PM', priority: 'Low' }
    ];

    // Helper function to convert date to "x days ago" format - v3
    const getTimeAgo = (dateString) => {
      if (!dateString) return '-';

      let date;
      try {
        date = new Date(dateString);
      } catch {
        return '-';
      }

      const now = new Date();

      // Check if date is valid
      if (!date || isNaN(date.getTime())) {
        return '-';
      }

      const year = date.getFullYear();
      // Check if year is reasonable (2020-2030)
      if (year < 2020 || year > 2030) {
        return '-';
      }

      const diffTime = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      // Future dates are invalid for activity entries - show dash
      if (diffDays < 0) return '-';
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return '1 day ago';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 14) return '1 week ago';
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 60) return '1 month ago';
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Activity aggregation logic - Shows 3 latest engagement history entries per opportunity
    const getAllActivities = () => {
      const activities = [];

      // Helper function to parse date from engagement history entry
      // IMPORTANT: Only parse the ENTRY date (at the start), not dates in the content (like Est. Close dates)
      const parseEntryDate = (entry: string): Date | null => {
        const monthMap: { [key: string]: number } = {
          'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
          'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
        };

        // Helper to validate date is in the past (entry dates should not be in the future)
        const isValidEntryDate = (d: Date): boolean => {
          if (isNaN(d.getTime())) return false;
          const now = new Date();
          const minYear = 2020;
          // Entry date should be in the past or today, not future
          return d.getFullYear() >= minYear && d <= now;
        };

        // PRIORITY 1: Try M/D/YYYY format from timestamp in brackets at START (e.g., [1/6/2026, 3:45:12 PM])
        const bracketMatch = entry.match(/^\[(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (bracketMatch) {
          const month = parseInt(bracketMatch[1]) - 1;
          const day = parseInt(bracketMatch[2]);
          const year = parseInt(bracketMatch[3]);
          const date = new Date(year, month, day);
          if (isValidEntryDate(date)) return date;
        }

        // PRIORITY 2: Try DD-MMM-YYYY format at START (e.g., "6-Jan-2026 Marked for Review")
        const ddMmmStartMatch = entry.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
        if (ddMmmStartMatch) {
          const day = parseInt(ddMmmStartMatch[1]);
          const monthStr = ddMmmStartMatch[2].toLowerCase();
          const month = monthMap[monthStr];
          const year = parseInt(ddMmmStartMatch[3]);
          if (!isNaN(day) && month !== undefined && !isNaN(year)) {
            const date = new Date(year, month, day);
            if (isValidEntryDate(date)) return date;
          }
        }

        // PRIORITY 3: Try DD-MMM-YYYY after bracket timestamp (e.g., "[timestamp] 6-Jan-2026 Stage Changed")
        const afterBracketMatch = entry.match(/^\[.*?\]\s*(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
        if (afterBracketMatch) {
          const day = parseInt(afterBracketMatch[1]);
          const monthStr = afterBracketMatch[2].toLowerCase();
          const month = monthMap[monthStr];
          const year = parseInt(afterBracketMatch[3]);
          if (!isNaN(day) && month !== undefined && !isNaN(year)) {
            const date = new Date(year, month, day);
            if (isValidEntryDate(date)) return date;
          }
        }

        // PRIORITY 4: Try ISO format YYYY-MM-DD at start or after "on " (but NOT after "Est. Close" or "Close:")
        // Only match if it's clearly the entry date, not an estimated close date
        const isoStartMatch = entry.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoStartMatch) {
          const date = new Date(isoStartMatch[0]);
          if (isValidEntryDate(date)) return date;
        }

        // Check for "on YYYY-MM-DD" but NOT if preceded by "Close" or "Est"
        const onDateMatch = entry.match(/(?<!Close[:\s]|Est[.\s])on\s+(\d{4})-(\d{2})-(\d{2})/i);
        if (onDateMatch) {
          const date = new Date(`${onDateMatch[1]}-${onDateMatch[2]}-${onDateMatch[3]}`);
          if (isValidEntryDate(date)) return date;
        }

        return null;
      };

      // Process each opportunity - using filteredOpportunities to respect global filters
      (filteredOpportunities || []).forEach(opp => {
        // Parse engagement history and get the 3 latest entries
        if (opp.engagementHistory) {
          const history = opp.engagementHistory.split('\n').filter(h => h.trim());

          // Parse all entries with their dates for sorting
          const parsedEntries = history.map((entry, idx) => {
            let restOfEntry = entry.trim();

            // Check if entry has [timestamp] brackets format
            const timestampMatch = entry.match(/^\[(.*?)\]/);
            if (timestampMatch) {
              restOfEntry = entry.substring(timestampMatch[0].length).trim();
            }

            // Check if entry starts with DD-MMM-YYYY format directly (e.g., "6-Jan-2026 Marked for Review")
            const directDateMatch = entry.match(/^(\d{1,2}-[A-Za-z]{3}-\d{4})\s+(.*)/);
            if (directDateMatch) {
              restOfEntry = directDateMatch[2].trim();
            }

            // Parse the entry date
            const entryDate = parseEntryDate(entry);
            if (!entryDate) return null; // Skip entries without valid date

            // Determine action type and description
            let actionType = 'Engagement';
            let action = '';
            let description = restOfEntry;

            if (restOfEntry.includes('Stage Changed:') || restOfEntry.includes('Stage Regressed:')) {
              actionType = 'Stage Update';
              if (restOfEntry.includes('Stage Regressed:')) {
                action = 'Stage Regressed';
                const stageMatch = restOfEntry.match(/Stage Regressed:\s*(.*)/);
                description = stageMatch ? stageMatch[1] : restOfEntry;
              } else {
                action = 'Stage Changed';
                const stageMatch = restOfEntry.match(/Stage Changed:\s*(.*)/);
                description = stageMatch ? stageMatch[1] : restOfEntry;
              }
            } else if (restOfEntry.includes('Probability Overridden')) {
              actionType = 'Probability Update';
              action = 'Probability Override';
              description = restOfEntry;
            } else if (restOfEntry.includes('Sales Status Overridden')) {
              actionType = 'Status Update';
              action = 'Status Override';
              description = restOfEntry;
            } else if (restOfEntry.includes('Marked for Review')) {
              actionType = 'Review';
              action = 'Marked for Review';
              description = 'Opportunity flagged for review';
            } else if (restOfEntry.includes('Unmarked for Review')) {
              actionType = 'Review';
              action = 'Unmarked for Review';
              description = 'Review flag removed';
            } else {
              // User-entered engagement entry - parse as "Category: Description" if possible
              const categoryMatch = restOfEntry.match(/^([^:]+):\s*(.*)/);
              if (categoryMatch) {
                action = categoryMatch[1].trim();
                description = categoryMatch[2].trim();
              } else {
                action = 'Note';
                description = restOfEntry;
              }
            }

            return {
              idx,
              entryDate,
              action,
              actionType,
              description,
              rawEntry: entry
            };
          }).filter(e => e !== null);

          // Sort by date (newest first)
          parsedEntries.sort((a, b) => b.entryDate.getTime() - a.entryDate.getTime());

          // Separate review entries from non-review entries
          const reviewEntries = parsedEntries.filter(e => e.actionType === 'Review');
          const nonReviewEntries = parsedEntries.filter(e => e.actionType !== 'Review');

          // Take only the latest review entry (if any) and 2 non-review entries
          const selectedEntries = [];

          // Add the latest review entry first (if exists)
          if (reviewEntries.length > 0) {
            selectedEntries.push(reviewEntries[0]);
          }

          // Add up to 2 non-review entries
          const nonReviewToAdd = nonReviewEntries.slice(0, 2);
          selectedEntries.push(...nonReviewToAdd);

          // Sort the selected entries by date (newest first)
          selectedEntries.sort((a, b) => b.entryDate.getTime() - a.entryDate.getTime());

          // Add selected entries to activities
          selectedEntries.forEach((entry, rank) => {
            activities.push({
              id: `opp-engagement-${opp.opportunityId}-${entry.idx}`,
              date: entry.entryDate.toISOString(),
              dateObj: entry.entryDate,
              action: entry.action,
              actionType: entry.actionType,
              description: entry.description,
              type: 'Opportunity',
              relatedTo: opp.opportunityName,
              relatedId: opp.opportunityId,
              salesperson: opp.leadSupporter || opp.accountOwner || 'Unknown',
              customer: opp.customerName || opp.associatedAccount || 'Unknown',
              geography: opp.geography || 'Unknown',
              serviceLine: opp.serviceLine || 'Unknown',
                entity: opp.entity || 'Unknown',
              serviceOffering: opp.serviceOffering || 'Unknown',
              revenue: opp.annualContractValue || opp.estimatedRevenue,
              closeDate: opp.estimatedCloseDate,
              stage: opp.salesStage,
              priority: opp.priority,
              status: opp.status,
              comment: null,
              entryRank: rank + 1 // 1 = most recent, 2 = second, 3 = third
            });
          });
        }
      });

      // Include explicit opportunity creation activities (based on initiationDate)
      (filteredOpportunities || []).forEach(opp => {
        const initDate = opp.initiationDate ? new Date(opp.initiationDate) : null;
        if (initDate && !isNaN(initDate.getTime())) {
          activities.push({
            id: `opp-created-${opp.opportunityId}`,
            date: initDate.toISOString(),
            dateObj: initDate,
            action: 'Opportunity Created',
            actionType: 'Opp Created',
            description: `New opportunity created in ${opp.salesStage || 'Unknown'} stage`,
            type: 'Opportunity',
            relatedTo: opp.opportunityName || opp.associatedAccount || opp.opportunityId,
            relatedId: opp.opportunityId,
            salesperson: opp.leadSupporter || opp.accountOwner || 'Unknown',
            customer: opp.customerName || opp.associatedAccount || 'Unknown',
            geography: opp.geography || 'Unknown',
            serviceLine: opp.serviceLine || 'Unknown',
            entity: opp.entity || 'Unknown',
            serviceOffering: opp.serviceOffering || 'Unknown',
            revenue: opp.annualContractValue || opp.estimatedRevenue,
            closeDate: opp.estimatedCloseDate,
            stage: opp.salesStage,
            priority: opp.priority,
            status: opp.status,
            comment: null,
            entryRank: 1
          });
        }
      });

      // Include lead activities (creation + conversion to opportunity)
      (dashboardLeads || []).forEach(lead => {
        // Lead creation activity
        const capturedDate = lead.dateCaptured || lead.createdDate || lead.created_at;
        if (capturedDate) {
          const dateObj = new Date(capturedDate);
          if (!isNaN(dateObj.getTime())) {
            activities.push({
              id: `lead-created-${lead.leadId}`,
              date: dateObj.toISOString(),
              dateObj: dateObj,
              action: 'Lead Created',
              actionType: 'Lead Created',
              description: `New lead captured from ${lead.leadSource || 'Unknown source'}`,
              type: 'Lead',
              relatedTo: lead.companyName || lead.contactPerson || lead.leadId,
              relatedId: lead.leadId,
              salesperson: lead.leadOwner || lead.salesRepresentative || 'Unknown',
              customer: lead.companyName || 'Unknown',
              geography: lead.geography || 'Unknown',
              serviceLine: lead.serviceLine || 'Unknown',
              entity: '',
              serviceOffering: '',
              revenue: lead.estimatedRevenue || 0,
              closeDate: null,
              stage: lead.leadStatus || 'New',
              priority: lead.priorityLevel,
              status: lead.leadStatus,
              comment: null,
              entryRank: 1
            });
          }
        }

        // Lead converted to opportunity
        if (lead.leadStatus === 'Converted' && lead.convertedAt) {
          const convertDate = new Date(lead.convertedAt);
          if (!isNaN(convertDate.getTime())) {
            activities.push({
              id: `lead-converted-${lead.leadId}`,
              date: convertDate.toISOString(),
              dateObj: convertDate,
              action: 'Converted to Opportunity',
              actionType: 'Lead Converted',
              description: `Lead converted to opportunity`,
              type: 'Lead',
              relatedTo: lead.companyName || lead.contactPerson || lead.leadId,
              relatedId: lead.leadId,
              salesperson: lead.leadOwner || lead.salesRepresentative || 'Unknown',
              customer: lead.companyName || 'Unknown',
              geography: lead.geography || 'Unknown',
              serviceLine: lead.serviceLine || 'Unknown',
              entity: '',
              serviceOffering: '',
              revenue: lead.estimatedRevenue || 0,
              closeDate: null,
              stage: 'Converted',
              priority: lead.priorityLevel,
              status: 'Converted',
              comment: null,
              entryRank: 1
            });
          }
        }

        // Parse lead engagement history for status change activities
        if (lead.engagementHistory) {
          const history = lead.engagementHistory.split('\n').filter(h => h.trim());
          const parsedLeadEntries = history.map((entry, idx) => {
            let restOfEntry = entry.trim();

            // Strip bracket timestamps
            const timestampMatch = entry.match(/^\[(.*?)\]/);
            if (timestampMatch) {
              restOfEntry = entry.substring(timestampMatch[0].length).trim();
            }
            // Strip DD-MMM-YYYY date prefix
            const directDateMatch = entry.match(/^(\d{1,2}-[A-Za-z]{3}-\d{4})\s+(.*)/);
            if (directDateMatch) {
              restOfEntry = directDateMatch[2].trim();
            }

            const entryDate = parseEntryDate(entry);
            if (!entryDate) return null;

            let actionType = '';
            let action = '';
            let description = restOfEntry;

            if (restOfEntry.toLowerCase().includes('lead unqualified')) {
              actionType = 'Lead Unqualified';
              action = 'Lead Unqualified';
              const reasonMatch = restOfEntry.match(/Lead unqualified\s*—\s*(.*)/i);
              description = reasonMatch ? reasonMatch[1] : 'Lead unqualified';
            } else if (restOfEntry.toLowerCase().includes('lead marked as lost')) {
              actionType = 'Lead Lost';
              action = 'Lead Marked as Lost';
              const reasonMatch = restOfEntry.match(/Lead marked as Lost\s*—\s*(.*)/i);
              description = reasonMatch ? reasonMatch[1] : 'Lead marked as lost';
            } else if (restOfEntry.toLowerCase().includes('lead deleted')) {
              actionType = 'Lead Deleted';
              action = 'Lead Deleted';
              description = 'Lead deleted';
            } else if (restOfEntry.toLowerCase().includes('lead converted to opportunity')) {
              // Already handled by dedicated lead conversion section above — skip to avoid duplicate
              return null;
            } else if (restOfEntry.toLowerCase().includes('status changed to')) {
              actionType = 'Lead Status Change';
              action = 'Status Changed';
              const statusMatch = restOfEntry.match(/Status changed to\s+(.*)/i);
              description = statusMatch ? `Status changed to ${statusMatch[1]}` : restOfEntry;
            } else if (restOfEntry.toLowerCase().includes('lead created')) {
              // Already handled by dedicated lead creation section above — skip
              return null;
            } else {
              // Generic lead engagement entry
              return null;
            }

            return { idx, entryDate, action, actionType, description, rawEntry: entry };
          }).filter(e => e !== null);

          // Take only the 2 most recent lead status change entries
          parsedLeadEntries.sort((a, b) => b.entryDate.getTime() - a.entryDate.getTime());
          const selectedLeadEntries = parsedLeadEntries.slice(0, 2);

          selectedLeadEntries.forEach((entry, rank) => {
            activities.push({
              id: `lead-engagement-${lead.leadId}-${entry.idx}`,
              date: entry.entryDate.toISOString(),
              dateObj: entry.entryDate,
              action: entry.action,
              actionType: entry.actionType,
              description: entry.description,
              type: 'Lead',
              relatedTo: lead.companyName || lead.contactPerson || lead.leadId,
              relatedId: lead.leadId,
              salesperson: lead.leadOwner || lead.salesRepresentative || 'Unknown',
              customer: lead.companyName || 'Unknown',
              geography: lead.geography || 'Unknown',
              serviceLine: lead.serviceLine || 'Unknown',
              entity: '',
              serviceOffering: '',
              revenue: lead.estimatedRevenue || 0,
              closeDate: null,
              stage: lead.leadStatus || 'Unknown',
              priority: lead.priorityLevel,
              status: lead.leadStatus,
              comment: null,
              entryRank: rank + 1
            });
          });
        }
      });

      // Sort all activities by date (newest first)
      return activities.sort((a, b) => {
        const dateA = a.dateObj || new Date(a.date);
        const dateB = b.dateObj || new Date(b.date);
        return dateB.getTime() - dateA.getTime();
      });
    };

    // Get flat list of activities with search + movement filter applied (uses global filters via getAllActivities)
    const getFilteredActivities = () => {
      let activities = getAllActivities();

      // Apply movement filter (from clickable summary cards)
      if (activityMovementFilter) {
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        activities = activities.filter(a => {
          const actDate = a.dateObj || new Date(a.date);
          const isThisWeek = actDate >= oneWeekAgo && actDate <= now;
          if (!isThisWeek) return false;
          switch (activityMovementFilter) {
            case 'newLeads': return a.actionType === 'Lead Created';
            case 'convertedLeads': return a.actionType === 'Lead Converted';
            case 'newOpps': return a.actionType === 'Opp Created';
            case 'stageChanges': return a.actionType === 'Stage Update';
            case 'updated': return a.type === 'Opportunity'; // All opp activities this week
            case 'won': return a.stage === 'Closed-Won';
            case 'lost': return a.stage === 'Closed-Lost';
            default: return true;
          }
        });
      }

      // Apply search filter only (global filters are already applied via filteredOpportunities)
      if (activitySearchTerm) {
        const searchLower = activitySearchTerm.toLowerCase();
        activities = activities.filter(a =>
          (a.relatedTo && a.relatedTo.toLowerCase().includes(searchLower)) ||
          (a.description && a.description.toLowerCase().includes(searchLower)) ||
          (a.salesperson && a.salesperson.toLowerCase().includes(searchLower)) ||
          (a.geography && a.geography.toLowerCase().includes(searchLower)) ||
          (a.serviceLine && a.serviceLine.toLowerCase().includes(searchLower)) ||
          (a.stage && a.stage.toLowerCase().includes(searchLower)) ||
          (a.customer && a.customer.toLowerCase().includes(searchLower))
        );
      }

      return activities; // Return flat list sorted by date (already sorted in getAllActivities)
    };

    // Get paginated activities
    const getPaginatedActivities = () => {
      const allActivities = getFilteredActivities();
      const startIndex = (activityPage - 1) * activitiesPerPage;
      const endIndex = startIndex + activitiesPerPage;
      return {
        activities: allActivities.slice(startIndex, endIndex),
        totalCount: allActivities.length,
        totalPages: Math.ceil(allActivities.length / activitiesPerPage)
      };
    };

    // ========== OVERVIEW SECTION COMPUTATIONS ==========

    // Build month option labels (last 12 months)
    const ovMonthOptions = (() => {
      const opts: string[] = [];
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        opts.push(d.toLocaleString('en-US', { month: 'short' }) + " '" + String(d.getFullYear()).slice(-2));
      }
      return opts;
    })();

    // Build filter option lists from base data
    const ovServiceLineOptions = ([...new Set([
      ...dashboardLeads.map(l => l.serviceLine),
      ...filteredOpportunities.map(o => o.serviceLine)
    ])] as string[]).filter(Boolean).sort();
    const ovLeadSourceOptions = ([...new Set(dashboardLeads.map(l => l.leadSource))] as string[]).filter(Boolean).sort();

    // Apply overview-tab local filters on top of global dashboard filters
    const ovLeads = dashboardLeads.filter(l => {
      if (ovServiceLine && l.serviceLine !== ovServiceLine) return false;
      if (ovLeadSource && l.leadSource !== ovLeadSource) return false;
      if (ovMonth) {
        if (!l.dateCaptured) return false;
        const d = new Date(l.dateCaptured);
        const lbl = d.toLocaleString('en-US', { month: 'short' }) + " '" + String(d.getFullYear()).slice(-2);
        if (lbl !== ovMonth) return false;
      }
      return true;
    });
    const ovOpps = filteredOpportunities.filter(o => {
      if (ovServiceLine && o.serviceLine !== ovServiceLine) return false;
      if (ovMonth) {
        if (!o.initiationDate) return false;
        const d = new Date(o.initiationDate);
        const lbl = d.toLocaleString('en-US', { month: 'short' }) + " '" + String(d.getFullYear()).slice(-2);
        if (lbl !== ovMonth) return false;
      }
      return true;
    });

    const overviewWonOpps = ovOpps.filter(o => o.salesStage === 'Closed-Won');
    const overviewLostOpps = ovOpps.filter(o => o.salesStage === 'Closed-Lost');
    const overviewPipelineOpps = ovOpps.filter(
      o => !['Closed-Won', 'Closed-Lost', 'Deferred', 'Did not pursue further', 'Assessment'].includes(o.salesStage) &&
           !o.salesStage?.toLowerCase().includes('lead')
    );
    const overviewWonTCV = overviewWonOpps.reduce((s, o) => s + (o.totalContractValue || 0), 0);
    const overviewLostTCV = overviewLostOpps.reduce((s, o) => s + (o.totalContractValue || 0), 0);
    const overviewPipelineTCV = overviewPipelineOpps.reduce((s, o) => s + (o.totalContractValue || 0), 0);
    const overviewAvgClientTCV = ovOpps.length > 0
      ? ovOpps.reduce((s, o) => s + (o.totalContractValue || 0), 0) / ovOpps.length
      : 0;
    const overviewClosedOpps = ovOpps.filter(o => o.salesStage === 'Closed-Won' || o.salesStage === 'Closed-Lost');
    const overviewWinRate = overviewClosedOpps.length > 0 ? Math.round((overviewWonOpps.length / overviewClosedOpps.length) * 100) : 0;

    // Lead Conversion Ratio and Opportunities Won Ratio
    const ovConvertedLeads = ovLeads.filter(l => l.leadStatus === 'Converted');
    const ovLeadConversionRate = ovLeads.length > 0 ? Math.round((ovConvertedLeads.length / ovLeads.length) * 100) : 0;
    const ovOppWonRatio = ovOpps.length > 0 ? Math.round((overviewWonOpps.length / ovOpps.length) * 100) : 0;
    const ovLastUpdatedStr = ovLastUpdated.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });

    const openOvLeads = ovLeads.filter((l: any) => l.leadStatus === 'Open' || !l.leadStatus);
    const activeOvOpps = ovOpps.filter(o => o.salesStage !== 'Closed-Won' && o.salesStage !== 'Closed-Lost' && getEffectiveSalesStatus(o) !== 'Inactive');
    const overviewLeadRatingData = [
      { name: 'Hot',  value: openOvLeads.filter((l: any) => l.leadRating === 'Hot').length,  color: '#EF4444' },
      { name: 'Warm', value: openOvLeads.filter((l: any) => l.leadRating === 'Warm').length, color: '#F59E0B' },
      { name: 'Cold', value: openOvLeads.filter((l: any) => l.leadRating === 'Cold').length, color: '#3B82F6' },
    ].filter(d => d.value > 0);

    const overviewWonVsTotalData = [
      { name: 'Won', value: overviewWonOpps.length, color: '#059669' },
      { name: 'Others', value: Math.max(0, ovOpps.length - overviewWonOpps.length), color: isDark ? '#374151' : '#E5E7EB' },
    ].filter(d => d.value > 0);

    const overviewOppStageColorMap: Record<string, string> = {
      'Qualified': '#6A7CA5', 'Discovery': '#00BBA2', 'Proposal': '#D9845C',
      'Contract': '#86BC86', 'Closed-Won': '#059669', 'Closed-Lost': '#EF4444',
      'Assessment': '#D9A441', 'Deferred': '#9CA3AF', 'Did not pursue further': '#6B7280', 'Lead': '#EBCB7E'
    };
    const overviewOppStatusData = Object.entries(overviewOppStageColorMap)
      .map(([stage, color]) => ({
        name: stage,
        shortName: stage === 'Did not pursue further' ? 'Did Not Pursue' : stage.replace('Closed-', ''),
        value: ovOpps.filter(o => o.salesStage === stage).length,
        color
      }))
      .filter(d => d.value > 0);

    const overviewServiceLineSet = new Set([
      ...ovLeads.map(l => l.serviceLine),
      ...ovOpps.map(o => o.serviceLine)
    ]);
    const overviewServiceLines = ([...overviewServiceLineSet] as string[]).filter(Boolean).sort();
    const overviewServiceLineData = overviewServiceLines.map(sl => ({
      name: sl.length > 16 ? sl.substring(0, 14) + '…' : sl,
      fullName: sl,
      leads:         ovLeads.filter(l => l.serviceLine === sl).length,
      opportunities: ovOpps.filter(o => o.serviceLine === sl).length,
      won:           ovOpps.filter(o => o.serviceLine === sl && o.salesStage === 'Closed-Won').length,
    }));

    const fmtTCV = (v: number) => v >= 1e6 ? `₹${(v / 1e6).toFixed(1)}M` : `₹${(v / 1e3).toFixed(0)}K`;

    const overviewMonthlyData = (() => {
      const result = [];
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const yr = d.getFullYear();
        const mo = d.getMonth();
        const label = d.toLocaleString('en-US', { month: 'short' }) + " '" + String(yr).slice(-2);
        const leadsCount = ovLeads.filter(l => {
          if (!l.dateCaptured) return false;
          const ld = new Date(l.dateCaptured);
          return ld.getFullYear() === yr && ld.getMonth() === mo;
        }).length;
        const oppsCount = ovOpps.filter(o => {
          if (!o.initiationDate) return false;
          const od = new Date(o.initiationDate);
          return od.getFullYear() === yr && od.getMonth() === mo;
        }).length;
        result.push({ month: label, leads: leadsCount, opportunities: oppsCount });
      }
      return result;
    })();

    return (
      <div className="space-y-0">
        {/* ========== DASHBOARD SUB-TABS - Compact Design ========== */}
        <div className="sticky top-0 z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" style={{ backgroundColor: 'var(--dm-surface)', borderBottom: '1px solid var(--dm-border)' }}>
          <nav className="flex space-x-0.5">
            {[
              { id: 'overview',     label: 'Overview',    lightColor: '#007A6A', darkColor: '#00D4B8', bgColor: isDark ? 'rgba(0,187,162,0.25)' : 'rgba(0,187,162,0.08)' },
              { id: 'main',         label: 'Pipeline',    lightColor: '#4A5E8C', darkColor: '#8BA8D8', bgColor: isDark ? 'rgba(106,124,165,0.3)' : 'rgba(106,124,165,0.18)' },
              { id: 'activity',     label: 'Activity',    lightColor: '#4A6E38', darkColor: '#A8C896', bgColor: isDark ? 'rgba(134,188,134,0.28)' : '#C7D8C1' },
              { id: 'performance',  label: 'Performance', lightColor: '#9A6A10', darkColor: '#F0C060', bgColor: isDark ? 'rgba(210,160,40,0.3)' : '#EBCB7E' },
              { id: 'reports',      label: 'Reports',     lightColor: '#5B2D9E', darkColor: '#C4AAEE', bgColor: isDark ? 'rgba(148,132,191,0.4)' : '#9484BF' },
              { id: 'weeklyreadout',label: 'Readout',     lightColor: '#007A6A', darkColor: '#00D4B8', bgColor: isDark ? 'rgba(0,187,162,0.25)' : '#fbe8ec' }
            ].map((tab) => {
              const activeColor = isDark ? tab.darkColor : tab.lightColor;
              const inactiveColor = isDark ? '#D4D4D4' : '#374151';
              return (
              <button
                key={tab.id}
                onClick={() => setDashboardSubTab(tab.id)}
                className="px-2.5 py-1 text-xs font-semibold transition-colors rounded-t-md"
                style={{
                  backgroundColor: dashboardSubTab === tab.id ? tab.bgColor : 'transparent',
                  color: dashboardSubTab === tab.id ? activeColor : inactiveColor,
                  borderBottom: dashboardSubTab === tab.id ? `2px solid ${activeColor}` : '2px solid transparent'
                }}
              >
                {tab.label}
              </button>
              );
            })}
          </nav>
        </div>

        {/* ========== FILTER BAR - Compact Single Row ========== */}
        <div className="sticky top-[26px] z-20 bg-porcelain -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 py-0.5">
          <div className="rounded-md shadow-sm px-2 py-1.5" style={{ backgroundColor: 'var(--dm-surface)', border: '1px solid var(--dm-border)' }}>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Filter size={14} className="text-gray-400 flex-shrink-0" />
              {/* My Dashboard Toggle */}
              <button
                onClick={() => setShowMyDashboard(!showMyDashboard)}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1"
                style={{
                  backgroundColor: showMyDashboard ? '#00BBA2' : 'var(--dm-btn-bg)',
                  color: showMyDashboard ? 'white' : 'var(--dm-btn-text)',
                  border: showMyDashboard ? '1px solid #dc2626' : '1px solid transparent'
                }}
              >
                <User size={12} />
                My Dashboard
              </button>
              {/* Geography Filter */}
              <button
                onClick={() => setActiveFilterCategory(activeFilterCategory === 'geography' ? null : 'geography')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedGeographies.length > 0 ? '#00BBA2' : activeFilterCategory === 'geography' ? 'rgba(0,187,162,0.08)' : 'var(--dm-btn-bg)',
                  color: selectedGeographies.length > 0 ? 'white' : activeFilterCategory === 'geography' ? '#009E8A' : 'var(--dm-btn-text)',
                  border: activeFilterCategory === 'geography' && selectedGeographies.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Geo{selectedGeographies.length > 0 && ` (${selectedGeographies.length})`}
              </button>

              {/* Salesperson Filter */}
              <button
                onClick={() => setActiveFilterCategory(activeFilterCategory === 'salesperson' ? null : 'salesperson')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedSalespersons.length > 0 ? '#00BBA2' : activeFilterCategory === 'salesperson' ? 'rgba(0,187,162,0.08)' : 'var(--dm-btn-bg)',
                  color: selectedSalespersons.length > 0 ? 'white' : activeFilterCategory === 'salesperson' ? '#009E8A' : 'var(--dm-btn-text)',
                  border: activeFilterCategory === 'salesperson' && selectedSalespersons.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Owner{selectedSalespersons.length > 0 && ` (${selectedSalespersons.length})`}
              </button>

              {/* Service Lines Filter */}
              <button
                onClick={() => setActiveFilterCategory(activeFilterCategory === 'serviceLine' ? null : 'serviceLine')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedServiceLines.length > 0 ? '#00BBA2' : activeFilterCategory === 'serviceLine' ? 'rgba(0,187,162,0.08)' : 'var(--dm-btn-bg)',
                  color: selectedServiceLines.length > 0 ? 'white' : activeFilterCategory === 'serviceLine' ? '#009E8A' : 'var(--dm-btn-text)',
                  border: activeFilterCategory === 'serviceLine' && selectedServiceLines.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Service Line{selectedServiceLines.length > 0 && ` (${selectedServiceLines.length})`}
              </button>

              {/* Sales Status Filter */}
              <button
                onClick={() => setActiveFilterCategory(activeFilterCategory === 'salesStatus' ? null : 'salesStatus')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedSalesStatuses.length > 0 ? '#00BBA2' : activeFilterCategory === 'salesStatus' ? 'rgba(0,187,162,0.08)' : 'var(--dm-btn-bg)',
                  color: selectedSalesStatuses.length > 0 ? 'white' : activeFilterCategory === 'salesStatus' ? '#009E8A' : 'var(--dm-btn-text)',
                  border: activeFilterCategory === 'salesStatus' && selectedSalesStatuses.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Status{selectedSalesStatuses.length > 0 && ` (${selectedSalesStatuses.length})`}
              </button>

              {/* Entity Group Filter */}
              <button
                onClick={() => setActiveFilterCategory(activeFilterCategory === 'entityGroup' ? null : 'entityGroup')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: (selectedEntityGroups.length + selectedEntities.length) > 0 ? '#00BBA2' : activeFilterCategory === 'entityGroup' ? 'rgba(0,187,162,0.08)' : 'var(--dm-btn-bg)',
                  color: (selectedEntityGroups.length + selectedEntities.length) > 0 ? 'white' : activeFilterCategory === 'entityGroup' ? '#009E8A' : 'var(--dm-btn-text)',
                  border: activeFilterCategory === 'entityGroup' && (selectedEntityGroups.length + selectedEntities.length) === 0 ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                Entity{(selectedEntityGroups.length + selectedEntities.length) > 0 && ` (${selectedEntityGroups.length + selectedEntities.length})`}
              </button>

              {/* Date Range Filter — hidden on Readout tab (has its own date controls) */}
              {dashboardSubTab !== 'weeklyreadout' && (
              <button
                onClick={() => setActiveFilterCategory(activeFilterCategory === 'dateRange' ? null : 'dateRange')}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors flex items-center gap-0.5"
                style={{
                  backgroundColor: selectedDateRange ? '#00BBA2' : activeFilterCategory === 'dateRange' ? 'rgba(0,187,162,0.08)' : 'var(--dm-btn-bg)',
                  color: selectedDateRange ? 'white' : activeFilterCategory === 'dateRange' ? '#009E8A' : 'var(--dm-btn-text)',
                  border: activeFilterCategory === 'dateRange' && !selectedDateRange ? '1px solid #00BBA2' : '1px solid transparent'
                }}
              >
                <Calendar size={12} />
                {selectedDateRange ? getSelectedDateRangeLabel() : 'Date'}
              </button>
              )}

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="px-2.5 py-1 rounded text-xs font-medium ml-1"
                  style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}
                >
                  Clear
                </button>
              )}
            </div>

              {/* Filter Options - Show when a filter category is selected */}
              {activeFilterCategory === 'geography' && (() => {
                const eligibleOpps = getFilteredOpportunitiesExcluding('geography');
                const geoWithCounts = getFilterOptions('geography')
                  .map(geo => ({
                    value: geo,
                    count: eligibleOpps.filter(o => o.geography?.trim().toLowerCase() === geo.trim().toLowerCase()).length
                  }))
                  .filter(g => g.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedCategories['geography'];
                const visibleItems = isExpanded ? geoWithCounts : geoWithCounts.slice(0, 8);
                const hiddenCount = geoWithCounts.length - 8;

                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn items-center">
                    <span className="text-[10px] font-semibold mr-0.5" style={{ color: 'var(--dm-text-2)' }}>Opps</span>
                    {visibleItems.map(({ value: geo, count }) => (
                      <button
                        key={geo}
                        onClick={() => toggleFilterSelection('geography', geo)}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: selectedGeographies.includes(geo) ? '#00BBA2' : 'var(--dm-btn-bg)',
                          color: selectedGeographies.includes(geo) ? 'white' : 'var(--dm-btn-text)',
                          border: selectedGeographies.includes(geo) ? 'none' : '1px solid #d1d5db'
                        }}
                      >
                        {geo} ({count})
                      </button>
                    ))}
                    {hiddenCount > 0 && (
                      <button
                        onClick={() => toggleCategoryExpanded('geography')}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                      >
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeFilterCategory === 'salesperson' && (() => {
                const eligibleOpps = getFilteredOpportunitiesExcluding('salesperson')
                  .filter(o => o.salesStage !== 'Closed-Won' && o.salesStage !== 'Closed-Lost');
                const spWithCounts = getFilterOptions('salesperson')
                  .map(sp => ({
                    value: sp,
                    count: eligibleOpps.filter(o => o.accountOwner === sp).length
                  }))
                  .filter(s => s.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedCategories['salesperson'];
                const visibleItems = isExpanded ? spWithCounts : spWithCounts.slice(0, 8);
                const hiddenCount = spWithCounts.length - 8;

                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn items-center">
                    <span className="text-[10px] font-semibold mr-0.5" style={{ color: 'var(--dm-text-2)' }}>Opps</span>
                    {visibleItems.map(({ value: sp, count }) => (
                      <button
                        key={sp}
                        onClick={() => toggleFilterSelection('salesperson', sp)}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: selectedSalespersons.includes(sp) ? '#00BBA2' : 'var(--dm-btn-bg)',
                          color: selectedSalespersons.includes(sp) ? 'white' : 'var(--dm-btn-text)',
                          border: selectedSalespersons.includes(sp) ? 'none' : '1px solid #d1d5db'
                        }}
                      >
                        {sp} ({count})
                      </button>
                    ))}
                    {hiddenCount > 0 && (
                      <button
                        onClick={() => toggleCategoryExpanded('salesperson')}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                      >
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeFilterCategory === 'serviceLine' && (() => {
                const eligibleOpps = getFilteredOpportunitiesExcluding('serviceLine')
                  .filter(o => o.salesStage !== 'Closed-Won' && o.salesStage !== 'Closed-Lost');
                const slWithCounts = getFilterOptions('serviceLine')
                  .map(sl => ({
                    value: sl,
                    count: eligibleOpps.filter(o => o.serviceLine?.trim().toLowerCase() === sl.trim().toLowerCase()).length
                  }))
                  .filter(s => s.count > 0)
                  .sort((a, b) => b.count - a.count);
                const isExpanded = expandedCategories['serviceLine'];
                const visibleItems = isExpanded ? slWithCounts : slWithCounts.slice(0, 8);
                const hiddenCount = slWithCounts.length - 8;

                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn items-center">
                    <span className="text-[10px] font-semibold mr-0.5" style={{ color: 'var(--dm-text-2)' }}>Opps</span>
                    {visibleItems.map(({ value: sl, count }) => (
                      <button
                        key={sl}
                        onClick={() => toggleFilterSelection('serviceLine', sl)}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: selectedServiceLines.includes(sl) ? '#00BBA2' : 'var(--dm-btn-bg)',
                          color: selectedServiceLines.includes(sl) ? 'white' : 'var(--dm-btn-text)',
                          border: selectedServiceLines.includes(sl) ? 'none' : '1px solid #d1d5db'
                        }}
                      >
                        {sl} ({count})
                      </button>
                    ))}
                    {hiddenCount > 0 && (
                      <button
                        onClick={() => toggleCategoryExpanded('serviceLine')}
                        className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}
                      >
                        {isExpanded ? 'Less' : `+${hiddenCount}`}
                        {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Sales Status Filter Options */}
              {activeFilterCategory === 'salesStatus' && (() => {
                const eligibleOpps = getFilteredOpportunitiesExcluding('salesStatus')
                  .filter(o => o.salesStage !== 'Closed-Won' && o.salesStage !== 'Closed-Lost');
                const statusWithCounts = getFilterOptions('salesStatus')
                  .map(status => ({
                    value: status,
                    count: eligibleOpps.filter(o => {
                      const effectiveStatus = getEffectiveSalesStatus(o);
                      return effectiveStatus.trim().toLowerCase() === status.trim().toLowerCase();
                    }).length
                  }));

                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn items-center">
                    <span className="text-[10px] font-semibold mr-0.5" style={{ color: 'var(--dm-text-2)' }}>Opps</span>
                    {statusWithCounts.map(({ value: status, count }) => {
                      const isSelected = selectedSalesStatuses.includes(status);
                      return (
                        <button
                          key={status}
                          onClick={() => toggleFilterSelection('salesStatus', status)}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: isSelected ? '#4b5563' : 'var(--dm-btn-bg)',
                            color: isSelected ? 'white' : 'var(--dm-btn-text)',
                            border: isSelected ? 'none' : '1px solid #d1d5db'
                          }}
                        >
                          {status} ({count})
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Entity Group Filter Options */}
              {activeFilterCategory === 'entityGroup' && (() => {
                const eligibleOpps = getFilteredOpportunitiesExcluding(['entityGroup', 'entity']);
                const entityWithCounts = entities
                  .filter(e => e.isActive)
                  .map(entity => ({
                    value: entity.name,
                    count: eligibleOpps.filter(o => o.entity === entity.name).length
                  }))
                  .filter(e => e.count > 0)
                  .sort((a, b) => b.count - a.count);
                const blankEntityCount = eligibleOpps.filter(o => !o.entity || o.entity.trim() === '').length;
                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                    {blankEntityCount > 0 && (() => {
                      const isSelected = selectedEntities.includes('Blank');
                      return (
                        <button key="blank-entity"
                          onClick={() => toggleFilterSelection('entity', 'Blank')}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: isSelected ? '#6b7280' : 'var(--dm-btn-bg)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid var(--dm-border-2)', fontStyle: 'italic' }}
                          title="Records with no entity assigned">
                          Blank ({blankEntityCount})
                        </button>
                      );
                    })()}
                    {entityWithCounts.map(({ value: entityName, count }) => {
                      const isSelected = selectedEntities.includes(entityName);
                      return (
                        <button key={entityName}
                          onClick={() => toggleFilterSelection('entity', entityName)}
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: isSelected ? '#00BBA2' : 'var(--dm-btn-bg)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid var(--dm-border-2)' }}>
                          {entityName} ({count})
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Date Range Filter Options — hidden on Readout tab */}
              {activeFilterCategory === 'dateRange' && dashboardSubTab !== 'weeklyreadout' && (() => {
                const dateOptions = getDateRangeOptions();
                const baseOpps = getFilteredOpportunitiesExcluding('dateRange');
                const optionsWithCounts = dateOptions.map(option => {
                  const range = option.getDateRange();
                  const oppCount = baseOpps.filter(o => {
                    const oppDate = o.initiationDate || o.createdAt;
                    if (!oppDate) return false;
                    const date = new Date(oppDate);
                    return date >= range.start && date <= range.end;
                  }).length;
                  return { ...option, count: oppCount };
                }).filter(o => o.count > 0);

                return (
                  <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 items-center animate-fadeIn">
                    {optionsWithCounts.map(option => (
                      <button
                        key={option.id}
                        onClick={() => {
                          setSelectedDateRange(selectedDateRange === option.id ? null : option.id);
                          setCustomDateFrom('');
                          setCustomDateTo('');
                          setActiveFilterCategory(null);
                        }}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: selectedDateRange === option.id ? '#00BBA2' : 'var(--dm-btn-bg)',
                          color: selectedDateRange === option.id ? 'white' : 'var(--dm-btn-text)',
                          border: selectedDateRange === option.id ? 'none' : '1px solid #d1d5db'
                        }}
                      >
                        {option.label} ({option.count})
                      </button>
                    ))}
                    <span className="text-gray-300 mx-0.5">|</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        value={customDateFrom}
                        onChange={(e) => setCustomDateFrom(e.target.value)}
                        className="px-1 py-0.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-pierian-red"
                        style={{ width: '120px' }}
                      />
                      <span className="text-xs text-gray-400">-</span>
                      <input
                        type="date"
                        value={customDateTo}
                        onChange={(e) => setCustomDateTo(e.target.value)}
                        className="px-1 py-0.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-pierian-red"
                        style={{ width: '120px' }}
                      />
                      <button
                        onClick={() => {
                          if (customDateFrom && customDateTo) {
                            setSelectedDateRange('custom');
                            setActiveFilterCategory(null);
                          }
                        }}
                        disabled={!customDateFrom || !customDateTo}
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: (customDateFrom && customDateTo) ? '#00BBA2' : 'var(--dm-btn-bg)',
                          color: (customDateFrom && customDateTo) ? 'white' : '#9ca3af',
                          cursor: (customDateFrom && customDateTo) ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Go
                      </button>
                    </div>
                  </div>
                );
              })()}
          </div>

          {/* Active Filters Display - Compact */}
          {hasActiveFilters && (
            <div className="flex items-center gap-1 flex-wrap mt-0.5 px-2 py-0.5 bg-gray-50 rounded">
              <span className="text-[10px] font-medium text-gray-400">Filtering:</span>
              {selectedGeographies.map(value => (
                <span key={`geo-${value}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: 'rgba(0,187,162,0.12)', color: '#006B5E' }}>
                  {value}
                  <button onClick={() => toggleFilterSelection('geography', value)} className="hover:text-[#006B5E]">×</button>
                </span>
              ))}
              {selectedSalespersons.map(value => (
                <span key={`sp-${value}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: '#C7D8C1', color: '#166534' }}>
                  {value}
                  <button onClick={() => toggleFilterSelection('salesperson', value)} className="hover:text-green-800">×</button>
                </span>
              ))}
              {selectedServiceLines.map(value => (
                <span key={`sl-${value}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: '#EBCB7E', color: '#92400e' }}>
                  {value}
                  <button onClick={() => toggleFilterSelection('serviceLine', value)} className="hover:text-amber-800">×</button>
                </span>
              ))}
              {selectedSalesStatuses.map(value => (
                <span key={`ss-${value}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: 'rgba(0,187,162,0.12)', color: '#006B5E' }}>
                  {value}
                  <button onClick={() => toggleFilterSelection('salesStatus', value)} className="hover:text-blue-800">×</button>
                </span>
              ))}
              {selectedEntityGroups.map(value => (
                <span key={`eg-${value}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                      style={{
                        backgroundColor: 'rgba(0,187,162,0.12)',
                        color: '#007A6A'
                      }}>
                  {value}
                  <button onClick={() => toggleFilterSelection('entityGroup', value)} className="hover:text-blue-800">×</button>
                </span>
              ))}
              {selectedEntities.map(value => (
                <span key={`ent-${value}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                      style={{
                        backgroundColor: value === 'Blank' ? '#e5e7eb' : '#e0f2fe',
                        color: value === 'Blank' ? '#6b7280' : '#0369a1',
                        fontStyle: value === 'Blank' ? 'italic' : 'normal'
                      }}>
                  {value}
                  <button onClick={() => toggleFilterSelection('entity', value)} className="hover:text-[#006B5E]">×</button>
                </span>
              ))}
              {selectedDateRange && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: '#fce7f3', color: '#9d174d' }}>
                  {getSelectedDateRangeLabel()}
                  <button onClick={() => { setSelectedDateRange(null); setCustomDateFrom(''); setCustomDateTo(''); }} className="hover:text-pink-800">×</button>
                </span>
              )}
              <button
                onClick={clearAllFilters}
                className="text-[10px] font-medium px-1.5 py-0 rounded hover:bg-gray-100"
                style={{ color: '#009E8A' }}
              >
                Clear
              </button>
            </div>
          )}

        </div>

        {/* Overview Tab */}
        {dashboardSubTab === 'overview' && (
          <div className="px-1 pt-3 pb-4 space-y-2">

            {/* Overview Filter Bar */}
            <div className="flex flex-wrap items-center gap-2 pb-2" style={{ borderBottom: `1px solid ${isDark ? '#374151' : '#E5E7EB'}` }}>
              {/* Service Line filter */}
              <select
                value={ovServiceLine}
                onChange={e => setOvServiceLine(e.target.value)}
                className="text-xs px-2 py-1 rounded border"
                style={{ backgroundColor: isDark ? '#1F2937' : '#fff', color: isDark ? '#D1D5DB' : '#374151', borderColor: isDark ? '#374151' : '#D1D5DB', minWidth: 140 }}
              >
                <option value="">All Service Lines</option>
                {ovServiceLineOptions.map(sl => <option key={sl} value={sl}>{sl}</option>)}
              </select>

              {/* Lead Source filter */}
              <select
                value={ovLeadSource}
                onChange={e => setOvLeadSource(e.target.value)}
                className="text-xs px-2 py-1 rounded border"
                style={{ backgroundColor: isDark ? '#1F2937' : '#fff', color: isDark ? '#D1D5DB' : '#374151', borderColor: isDark ? '#374151' : '#D1D5DB', minWidth: 130 }}
              >
                <option value="">All Lead Sources</option>
                {ovLeadSourceOptions.map(src => <option key={src} value={src}>{src}</option>)}
              </select>

              {/* Month filter */}
              <select
                value={ovMonth}
                onChange={e => setOvMonth(e.target.value)}
                className="text-xs px-2 py-1 rounded border"
                style={{ backgroundColor: isDark ? '#1F2937' : '#fff', color: isDark ? '#D1D5DB' : '#374151', borderColor: isDark ? '#374151' : '#D1D5DB', minWidth: 100 }}
              >
                <option value="">All Months</option>
                {ovMonthOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>

              {/* Clear filters */}
              {(ovServiceLine || ovLeadSource || ovMonth) && (
                <button
                  onClick={() => { setOvServiceLine(''); setOvLeadSource(''); setOvMonth(''); }}
                  className="text-xs px-2 py-1 rounded"
                  style={{ color: '#EF4444', border: '1px solid #EF4444' }}
                >
                  Clear
                </button>
              )}

              <div className="flex-1" />

              {/* Lead Conversion Rate chip */}
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: isDark ? 'rgba(217,164,65,0.15)' : '#FEF3C7', color: '#92400E' }}>
                <span style={{ color: isDark ? '#9CA3AF' : '#6B7280', fontWeight: 400 }}>Lead Conversion Rate</span>
                <span className="text-sm font-extrabold" style={{ color: '#D9A441' }}>{ovLeadConversionRate}%</span>
              </div>

              {/* Opportunities Won Ratio chip */}
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: isDark ? 'rgba(5,150,105,0.15)' : '#D1FAE5', color: '#065F46' }}>
                <span style={{ color: isDark ? '#9CA3AF' : '#6B7280', fontWeight: 400 }}>Opportunity Win Rate</span>
                <span className="text-sm font-extrabold" style={{ color: '#059669' }}>{ovOppWonRatio}%</span>
              </div>

              {/* Last Updated */}
              <div className="text-[10px] flex items-center gap-1" style={{ color: isDark ? '#6B7280' : '#9CA3AF' }}>
                <span>Updated:</span>
                <span className="font-medium" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>{ovLastUpdatedStr}</span>
              </div>
            </div>

            {/* All KPI Pills — single row */}
            <div className="flex gap-2">
              {[
                { label: 'Leads',            value: String(openOvLeads.length),           bg: '#FEF3C7' },
                { label: 'Opps.',             value: String(activeOvOpps.length),          bg: '#CCFBF1' },
                { label: 'Won Opp.',         value: String(overviewWonOpps.length),       bg: '#D1FAE5' },
                { label: 'Lost Opp.',        value: String(overviewLostOpps.length),      bg: '#FEE2E2' },
                { label: 'Pipeline Opp.',    value: String(overviewPipelineOpps.length),  bg: '#E8EDF5' },
                { label: 'Win Rate',               value: `${overviewWinRate}%`,                bg: '#EDE9FE' },
                { label: 'Avg.Clientsize',         value: fmtTCV(overviewAvgClientTCV),         bg: '#E8EDF5' },
                { label: 'Won TCV',                value: fmtTCV(overviewWonTCV),               bg: '#D1FAE5' },
                { label: 'Lost TCV',               value: fmtTCV(overviewLostTCV),              bg: '#FEE2E2' },
                { label: 'Pipeline TCV',           value: fmtTCV(overviewPipelineTCV),          bg: '#CCFBF1' },
              ].map(card => (
                <div key={card.label} className="flex-1 rounded-2xl px-2 py-2.5 text-center min-w-0" style={{ backgroundColor: card.bg }}>
                  <p className="text-[9px] font-semibold uppercase tracking-wider leading-none mb-1 truncate" style={{ color: '#4B5563' }}>{card.label}</p>
                  <p className="text-lg font-extrabold leading-none" style={{ color: '#111827' }}>{card.value}</p>
                </div>
              ))}
            </div>

            {/* Row 3 — Service Line (left, wider) + Lead Rating (right, narrower) */}
            <div className="ov-sl-row">

              {/* Service Line Wise Lead vs Opportunity */}
              <Card className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Service Line Wise Lead vs Opportunity</p>
                {overviewServiceLineData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart data={overviewServiceLineData} margin={{ top: 10, right: 16, left: 0, bottom: 90 }} barCategoryGap="25%">
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} vertical={false} />
                        <XAxis
                          dataKey="fullName"
                          tick={(props: any) => {
                            const { x, y, payload } = props;
                            const words: string[] = (payload.value || '').split(' ');
                            const mid = Math.ceil(words.length / 2);
                            const line1 = words.slice(0, mid).join(' ');
                            const line2 = words.slice(mid).join(' ');
                            return (
                              <g transform={`translate(${x},${y})`}>
                                <text transform="rotate(-40)" textAnchor="end" fontSize={11} fontWeight={500} fill={isDark ? '#9CA3AF' : '#555'}>
                                  <tspan x={0} dy={4}>{line1}</tspan>
                                  {line2 && <tspan x={0} dy={13}>{line2}</tspan>}
                                </text>
                              </g>
                            );
                          }}
                          axisLine={false} tickLine={false} interval={0} height={90}
                        />
                        <YAxis tick={{ fontSize: 12, fill: isDark ? '#9CA3AF' : '#6B7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{ fontSize: '12px', borderRadius: '8px', backgroundColor: isDark ? '#1F2937' : '#fff', border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}` }}
                          formatter={(value: number, name: string) => [value, name === 'leads' ? 'Total Leads' : name === 'opportunities' ? 'Total Opportunities' : 'Won Opportunities']}
                        />
                        <Bar dataKey="leads" name="leads" fill="#D9A441" radius={[3, 3, 0, 0]} maxBarSize={22}>
                          <LabelList dataKey="leads" position="top" formatter={(v: number) => v > 0 ? v : ''} style={{ fontSize: '10px', fontWeight: 700, fill: isDark ? '#D1D5DB' : '#374151' }} />
                        </Bar>
                        <Bar dataKey="opportunities" name="opportunities" fill="#6A7CA5" radius={[3, 3, 0, 0]} maxBarSize={22}>
                          <LabelList dataKey="opportunities" position="top" formatter={(v: number) => v > 0 ? v : ''} style={{ fontSize: '10px', fontWeight: 700, fill: isDark ? '#D1D5DB' : '#374151' }} />
                        </Bar>
                        <Bar dataKey="won" name="won" fill="#059669" radius={[3, 3, 0, 0]} maxBarSize={22}>
                          <LabelList dataKey="won" position="top" formatter={(v: number) => v > 0 ? v : ''} style={{ fontSize: '10px', fontWeight: 700, fill: isDark ? '#D1D5DB' : '#374151' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-5 justify-center mt-1">
                      {[{ label: 'Leads', color: '#D9A441' }, { label: 'Opportunities', color: '#6A7CA5' }, { label: 'Won', color: '#059669' }].map(l => (
                        <div key={l.label} className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: l.color }} />
                          <span className="text-xs" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>{l.label}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-64 flex items-center justify-center text-sm text-gray-400">No data</div>
                )}
              </Card>

              {/* Lead Rating */}
              <Card className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Open Lead Rating</p>
                {overviewLeadRatingData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={overviewLeadRatingData} layout="vertical" margin={{ top: 8, right: 48, left: 8, bottom: 8 }} barSize={22}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 12, fill: isDark ? '#9CA3AF' : '#6B7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 13, fill: isDark ? '#D1D5DB' : '#374151', fontWeight: 600 }} width={60} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '8px', backgroundColor: isDark ? '#1F2937' : '#fff', border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}` }} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {overviewLeadRatingData.map((entry, idx) => (
                            <Cell key={idx} fill={entry.color} />
                          ))}
                          <LabelList dataKey="value" position="right" offset={8} formatter={(v: number) => v > 0 ? v : ''} style={{ fontSize: '13px', fontWeight: 700, fill: isDark ? '#D1D5DB' : '#374151' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="pt-2 border-t mt-1" style={{ borderColor: isDark ? '#374151' : '#E5E7EB' }}>
                      <span className="text-xs font-semibold" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Total: {overviewLeadRatingData.reduce((s, d) => s + d.value, 0)} open leads</span>
                    </div>
                  </>
                ) : (
                  <div className="h-64 flex items-center justify-center text-sm text-gray-400">No data</div>
                )}
              </Card>

            </div>

            {/* Row 4 — Opportunity Status (left) + Month on Month (right) */}
            <div className="ov-chart-row-2">

              {/* Opportunity Status */}
              <Card className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Opportunity Status</p>
                {overviewOppStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={overviewOppStatusData} layout="vertical" margin={{ top: 4, right: 56, left: 8, bottom: 4 }} barSize={20}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 12, fill: isDark ? '#9CA3AF' : '#6B7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="shortName" tick={{ fontSize: 12, fill: isDark ? '#9CA3AF' : '#4B5563', fontWeight: 600 }} width={80} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '8px', backgroundColor: isDark ? '#1F2937' : '#fff', border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}` }} formatter={(value: number, _name: string, props: { payload?: { name?: string } }) => [value, props?.payload?.name || '']} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {overviewOppStatusData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} />
                        ))}
                        <LabelList dataKey="value" position="right" offset={8} formatter={(v: number) => v > 0 ? v : ''} style={{ fontSize: '12px', fill: isDark ? '#D1D5DB' : '#1F2937', fontWeight: 700 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-sm text-gray-400">No data</div>
                )}
              </Card>

              {/* Month on Month Lead vs Opportunity */}
              <Card className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Month on Month Lead vs Opportunity</p>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={overviewMonthlyData} margin={{ top: 10, right: 24, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: isDark ? '#9CA3AF' : '#6B7280' }} axisLine={false} tickLine={false} interval={0} angle={-30} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 12, fill: isDark ? '#9CA3AF' : '#6B7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '8px', backgroundColor: isDark ? '#1F2937' : '#fff', border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}` }} />
                    <Line type="monotone" dataKey="leads" name="Leads" stroke="#D9A441" strokeWidth={2.5} dot={{ r: 4, fill: '#D9A441' }} activeDot={{ r: 6 }}
                      label={(props: any) => { const { x, y, value } = props; return value > 0 ? <text key={`l-${x}`} x={x} y={y - 8} textAnchor="middle" fontSize={11} fontWeight={700} fill="#D9A441">{value}</text> : <text key={`l-${x}`}/>; }} />
                    <Line type="monotone" dataKey="opportunities" name="Opportunities" stroke="#6A7CA5" strokeWidth={2.5} dot={{ r: 4, fill: '#6A7CA5' }} activeDot={{ r: 6 }}
                      label={(props: any) => { const { x, y, value } = props; return value > 0 ? <text key={`o-${x}`} x={x} y={y + 18} textAnchor="middle" fontSize={11} fontWeight={700} fill="#6A7CA5">{value}</text> : <text key={`o-${x}`}/>; }} />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-5 mt-2 justify-center">
                  {[{ label: 'Leads', color: '#D9A441' }, { label: 'Opportunities', color: '#6A7CA5' }].map(l => (
                    <div key={l.label} className="flex items-center gap-1.5">
                      <div className="w-5 h-0.5 rounded" style={{ backgroundColor: l.color }} />
                      <span className="text-xs" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>{l.label}</span>
                    </div>
                  ))}
                </div>
              </Card>

            </div>

            {/* Row 5 — Lead Details Table (hidden) */}
            {false && <Card className="p-2.5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}>Lead Details</p>
                <button
                  onClick={() => {
                    const XLSX = (window as any).XLSX;
                    if (!XLSX) { alert('Excel library not loaded.'); return; }
                    const fmtDate = (val: any) => { if (!val) return ''; const d = new Date(val); return `${String(d.getDate()).padStart(2,'0')}-${d.toLocaleString('en-US',{month:'short'})}-${d.getFullYear()}`; };
                    const rows = [...ovLeads]
                      .filter((l: any) => l.leadStatus === 'Converted' || l.convertedOpportunityId)
                      .sort((a, b) => new Date(b.dateCaptured || 0).getTime() - new Date(a.dateCaptured || 0).getTime())
                      .map((lead: any) => {
                        const linkedOpp = lead.convertedOpportunityId
                          ? (filteredOpportunities as any[]).find((o: any) => o.opportunityId === lead.convertedOpportunityId)
                          : (filteredOpportunities as any[]).filter((o: any) => o.associatedAccount && lead.companyName && o.associatedAccount.trim().toLowerCase() === lead.companyName.trim().toLowerCase()).sort((a: any, b: any) => new Date(b.initiationDate || 0).getTime() - new Date(a.initiationDate || 0).getTime())[0] || null;
                        const rawAmt = parseFloat(linkedOpp?.totalContractValue || linkedOpp?.annualContractValue || linkedOpp?.estimatedRevenue || lead.estimatedRevenue || 0);
                        return {
                          'Lead ID': lead.leadId || '',
                          'Lead Date': fmtDate(lead.dateCaptured),
                          'Opp ID': linkedOpp?.opportunityId || lead.convertedOpportunityId || '',
                          'Opp. Date': fmtDate(linkedOpp?.initiationDate || lead.convertedAt),
                          'Lead Owner': lead.leadOwner || '',
                          'Company Name': lead.companyName || '',
                          'Nature of Work': linkedOpp?.natureOfWork || '',
                          'Amount': rawAmt > 0 ? rawAmt : '',
                          'Contact Person': lead.contactPerson || '',
                          'Designation': lead.designation || '',
                        };
                      });
                    const ws = XLSX.utils.json_to_sheet(rows);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Lead Details');
                    XLSX.writeFile(wb, `Lead_Details_${new Date().toISOString().split('T')[0]}.xlsx`);
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors"
                  style={{ backgroundColor: '#00BBA2', color: '#fff' }}
                  title="Export to Excel"
                >
                  <Download size={11} />
                  Export
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${isDark ? '#374151' : '#E5E7EB'}` }}>
                      {['Lead ID', 'Lead Date', 'Opp ID', 'Opp. Date', 'Lead Owner', 'Company Name', 'Nature of Work', 'Amount', 'Contact Person', 'Designation'].map(h => (
                        <th key={h} className="py-1.5 px-2 text-left font-semibold" style={{ color: isDark ? '#9CA3AF' : '#6B7280', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...ovLeads]
                      .filter((l: any) => l.leadStatus === 'Converted' || l.convertedOpportunityId)
                      .sort((a, b) => new Date(b.dateCaptured || 0).getTime() - new Date(a.dateCaptured || 0).getTime())
                      .slice(0, 10)
                      .map((lead, idx) => {
                        const fmtDate = (val: any) => {
                          if (!val) return '—';
                          const d = new Date(val);
                          return `${String(d.getDate()).padStart(2,'0')}-${d.toLocaleString('en-US',{month:'short'})}-${d.getFullYear()}`;
                        };
                        // Lookup linked opportunity: first try by convertedOpportunityId, then fallback to company name match
                        const linkedOpp = (() => {
                          if (lead.convertedOpportunityId) {
                            return filteredOpportunities.find((o: any) => o.opportunityId === lead.convertedOpportunityId) || null;
                          }
                          const matches = (filteredOpportunities as any[]).filter((o: any) =>
                            o.associatedAccount && lead.companyName &&
                            o.associatedAccount.trim().toLowerCase() === lead.companyName.trim().toLowerCase()
                          );
                          if (!matches.length) return null;
                          return matches.sort((a: any, b: any) =>
                            new Date(b.initiationDate || 0).getTime() - new Date(a.initiationDate || 0).getTime()
                          )[0];
                        })();
                        const oppId = linkedOpp?.opportunityId || lead.convertedOpportunityId || null;
                        const oppDate = linkedOpp?.initiationDate || lead.convertedAt || null;
                        const rawAmt = parseFloat(linkedOpp?.totalContractValue || linkedOpp?.annualContractValue || linkedOpp?.estimatedRevenue || lead.estimatedRevenue || 0);
                        const amtFmt = rawAmt >= 1e6 ? `₹${(rawAmt / 1e6).toFixed(1)}M` : rawAmt >= 1e3 ? `₹${(rawAmt / 1e3).toFixed(0)}K` : rawAmt > 0 ? `₹${rawAmt}` : '—';
                        const tdCell = (val: any, opts?: { mono?: boolean; bold?: boolean }) => (
                          <td className={`py-1.5 px-2${opts?.mono ? ' font-mono' : ''}${opts?.bold ? ' font-medium' : ''}`} style={{ color: isDark ? (opts?.bold ? '#F3F4F6' : '#D1D5DB') : (opts?.bold ? '#111827' : '#374151'), whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{val || '—'}</td>
                        );
                        return (
                          <tr key={lead.leadId} style={{ borderBottom: `1px solid ${isDark ? '#1F2937' : '#F3F4F6'}`, backgroundColor: idx % 2 === 0 ? 'transparent' : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') }}>
                            {tdCell(lead.leadId, { mono: true })}
                            {tdCell(fmtDate(lead.dateCaptured))}
                            {tdCell(oppId, { mono: true })}
                            {tdCell(fmtDate(oppDate))}
                            {tdCell(lead.leadOwner)}
                            {tdCell(lead.companyName, { bold: true })}
                            {tdCell(linkedOpp?.natureOfWork)}
                            <td className="py-1.5 px-2 font-semibold" style={{ color: isDark ? '#F3F4F6' : '#111827', whiteSpace: 'nowrap' }}>{amtFmt}</td>
                            {tdCell(lead.contactPerson)}
                            {tdCell(lead.designation)}
                          </tr>
                        );
                      })}
                    {ovLeads.length === 0 && (
                      <tr><td colSpan={10} className="py-6 text-center text-gray-400">No converted leads to display</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>}

          </div>
        )}

        {/* Main Tab Content */}
        {dashboardSubTab === 'main' && (
          <div className="space-y-2">
            {/* Row 1 - Summary Cards: Open Leads | Active Opportunities */}
            <div className="grid grid-cols-2 gap-3">

          {/* ========== COLUMN 1: OPEN LEADS ========== */}
          <Card className="p-2.5 flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-2 mb-1.5">
              <div className="p-1 rounded-lg" style={{ backgroundColor: '#EBCB7E' }}>
                <Users size={14} style={{ color: '#D9A441' }} />
              </div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Open Leads</p>
            </div>
            {/* Stats row — 3 equal columns */}
            <div className="grid grid-cols-2 gap-0">
              <button
                className={`text-left p-1.5 rounded-lg transition-colors ${leadsListFilter === 'all' ? 'bg-gray-100 ring-1 ring-gray-300' : 'hover:bg-gray-50'}`}
                onClick={() => setLeadsListFilter('all')}
              >
                <p className="text-xl font-bold text-gray-900">{openLeads.length}</p>
                <p className="text-[10px] text-amber font-medium">Open</p>
              </button>
              <div className="border-l border-gray-200 pl-3 flex flex-col justify-center">
                <p className="text-[10px] text-gray-500 font-medium">Converted</p>
                <p className="text-lg font-bold text-green-600">{convertedLeads.length}</p>
              </div>
            </div>
            {/* Item list */}
            <div className="mt-1.5 pt-1.5 border-t border-gray-100 flex-1 flex flex-col">
              <div className="space-y-0.5 flex-1">
                {[...openLeads]
                  .sort((a, b) => (b.estimatedRevenue || 0) - (a.estimatedRevenue || 0))
                  .slice(0, 5)
                  .map((lead) => {
                    const priorityColors: { [key: string]: string } = { 'High': 'bg-teal-500', 'Medium': 'bg-amber-500', 'Low': 'bg-green-500' };
                    const dotColor = priorityColors[lead.priorityLevel] || 'bg-gray-400';
                    return (
                      <div key={lead.leadId} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-gray-50 cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); setLeadStatusFilter('Open'); setActiveTab('leads'); }}>
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`}></div>
                        <span className="text-xs text-gray-800 truncate flex-1">{lead.companyName}</span>
                        <span className="text-xs font-semibold text-gray-600 flex-shrink-0 w-14 text-right">
                          {(lead.estimatedRevenue || 0) >= 1000000 ? '₹' + ((lead.estimatedRevenue || 0) / 1000000).toFixed(1) + 'M' : '₹' + ((lead.estimatedRevenue || 0) / 1000).toFixed(0) + 'K'}
                        </span>
                      </div>
                    );
                  })}
              </div>
              {openLeads.length > 0 && (
                <button onClick={() => {
                    setLeadStatusFilter('Open');
                    if (selectedGeographies.length > 0) setLeadSelectedGeographies(selectedGeographies);
                    if (selectedSalespersons.length > 0) setLeadSelectedOwners(selectedSalespersons);
                    if (selectedEntities.length > 0) setLeadSelectedEntities(selectedEntities);
                    if (selectedEntityGroups.length > 0) setLeadSelectedEntityGroups(selectedEntityGroups);
                    setActiveTab('leads');
                  }}
                  className="w-full text-center text-xs text-pierian hover:text-capital font-medium py-1 mt-0.5">
                  {openLeads.length > 5 ? `View all ${openLeads.length} →` : 'Go to Leads →'}
                </button>
              )}
            </div>
          </Card>

          {/* ========== COLUMN 2: ACTIVE OPPORTUNITIES ========== */}
          <Card className="p-2.5 flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-2 mb-1.5">
              <div className="p-1 rounded-lg" style={{ backgroundColor: 'rgba(0,187,162,0.08)' }}>
                <Target size={14} style={{ color: '#00BBA2' }} />
              </div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Active Opportunities</p>
            </div>
            {/* Stats row — 3 equal columns */}
            <div className="grid grid-cols-3 gap-0">
              <button
                className={`text-left p-1.5 rounded-lg transition-colors ${activeOppsListFilter === 'all' ? 'bg-gray-100 ring-1 ring-gray-300' : 'hover:bg-gray-50'}`}
                onClick={() => setActiveOppsListFilter('all')}
              >
                <p className="text-xl font-bold text-gray-900">{activeOpportunities.length}</p>
                <p className="text-[10px] text-sage font-medium">Active</p>
              </button>
              <div className="border-l border-gray-200 pl-3 flex flex-col justify-center">
                <p className="text-[10px] text-gray-500 font-medium">Pipeline</p>
                <p className="text-lg font-bold text-gray-900">
                  ₹{(activeOpportunities.reduce((sum, opp) => sum + (opp.totalContractValue || 0), 0) / 1000000).toFixed(1)}M
                </p>
              </div>
              {/* Hot/Warm/Cold */}
              {(() => {
                const hotWarmOpps = activeOpportunities.filter(opp => { const status = getEffectiveSalesStatus(opp); return status === 'Hot' || status === 'Warm'; });
                const coldOpps = activeOpportunities.filter(opp => getEffectiveSalesStatus(opp) === 'Cold');
                return (
                  <div className="border-l border-gray-200 pl-3 flex flex-col justify-center gap-1">
                    <button className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${activeOppsListFilter === 'hotWarm' ? 'bg-amber-50 ring-1 ring-amber-200' : 'hover:bg-gray-50'}`}
                      onClick={() => setActiveOppsListFilter(activeOppsListFilter === 'hotWarm' ? 'all' : 'hotWarm')}>
                      <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                      <span className="text-[10px] text-gray-500">Hot+Warm</span>
                      <span className="text-xs font-bold text-gray-800">{hotWarmOpps.length}</span>
                    </button>
                    <button className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${activeOppsListFilter === 'cold' ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'}`}
                      onClick={() => setActiveOppsListFilter(activeOppsListFilter === 'cold' ? 'all' : 'cold')}>
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                      <span className="text-[10px] text-gray-500">Cold</span>
                      <span className="text-xs font-bold text-gray-800">{coldOpps.length}</span>
                    </button>
                  </div>
                );
              })()}
            </div>
            {/* Item list */}
            <div className="mt-1.5 pt-1.5 border-t border-gray-100 flex-1 flex flex-col">
              <div className="space-y-0.5 flex-1">
                {[...activeOpportunities]
                  .filter(opp => {
                    if (activeOppsListFilter === 'all') return true;
                    const status = getEffectiveSalesStatus(opp);
                    if (activeOppsListFilter === 'hotWarm') return status === 'Hot' || status === 'Warm';
                    if (activeOppsListFilter === 'cold') return status === 'Cold';
                    return true;
                  })
                  .sort((a, b) => (b.annualContractValue || 0) - (a.annualContractValue || 0))
                  .slice(0, 5)
                  .map((opp) => {
                    const effectiveStatus = getEffectiveSalesStatus(opp);
                    const statusColors: Record<string, string> = { 'Hot': 'bg-red-500', 'Warm': 'bg-amber-500', 'Cold': 'bg-slate-400' };
                    const dotColor = statusColors[effectiveStatus] || 'bg-gray-400';
                    const stageColors: { [key: string]: { bg: string; text: string } } = {
                      'Discovery': { bg: 'rgba(0,187,162,0.08)', text: '#374151' },
                      'Qualified': { bg: 'rgba(106,124,165,0.18)', text: '#374151' },
                      'Proposal': { bg: '#E9C1BE', text: '#374151' },
                      'Contract': { bg: '#d1fae5', text: '#065f46' },
                    };
                    const stageStyle = stageColors[opp.salesStage] || { bg: '#f3f4f6', text: '#374151' };
                    return (
                      <div key={opp.opportunityId} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-gray-50 cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); setActiveTab('opportunities'); setSelectedOpportunity(opp); setShowOpportunityDetail(true); }}>
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`}></div>
                        <span className="text-xs text-gray-800 truncate flex-1">{opp.opportunityName}</span>
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{ backgroundColor: stageStyle.bg, color: stageStyle.text }}>{opp.salesStage}</span>
                        <span className="text-xs font-semibold text-gray-600 flex-shrink-0 w-14 text-right">
                          ₹{(opp.annualContractValue || 0) >= 1000000 ? ((opp.annualContractValue || 0) / 1000000).toFixed(1) + 'M' : ((opp.annualContractValue || 0) / 1000).toFixed(0) + 'K'}
                        </span>
                      </div>
                    );
                  })}
              </div>
              {activeOpportunities.length > 0 && (
                <button onClick={() => {
                    if (activeOppsListFilter === 'hotWarm') { setOppSelectedLeadRatings(['Hot', 'Warm']); }
                    else if (activeOppsListFilter === 'cold') { setOppSelectedLeadRatings(['Cold']); }
                    else { setOppSelectedLeadRatings([]); }
                    if (selectedGeographies.length > 0) setOppSelectedGeographies(selectedGeographies);
                    if (selectedSalespersons.length > 0) setOppSelectedOwners(selectedSalespersons);
                    if (selectedServiceLines.length > 0) setOppSelectedServiceLines(selectedServiceLines);
                    setActiveTab('opportunities');
                  }}
                  className="w-full text-center text-xs text-pierian hover:text-capital font-medium py-1 mt-0.5">
                  {activeOpportunities.length > 5 ? `View all ${activeOpportunities.length} →` : 'Go to Opportunities →'}
                </button>
              )}
            </div>
          </Card>
        </div>

            {/* Row 2 - Sales Pipeline + Weekly Chart side by side */}
          <div className="grid grid-cols-2 gap-3">
          {/* Unified Sales Pipeline — Stacked Horizontal Bar Chart */}
          <Card className="p-3">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-gray-900">Sales Pipeline</h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: '#4F7A3A' }}></div>
                  <span className="text-[10px] text-gray-500 font-medium">Leads</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: '#4B5B7A' }}></div>
                  <span className="text-[10px] text-gray-500 font-medium">Opps</span>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={pipelineData} layout="vertical" margin={{ top: 5, right: 80, left: 5, bottom: 5 }} barSize={22}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 9, fill: '#4b5563' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => v >= 1000000 ? `₹${(v / 1000000).toFixed(0)}M` : `₹${(v / 1000).toFixed(0)}K`}
                />
                <YAxis
                  type="category"
                  dataKey="stage"
                  tick={{ fontSize: 10, fontWeight: 600, fill: '#1a1a1a' }}
                  width={90}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{ fontSize: '12px', borderRadius: '8px' }}
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  formatter={(value: number, name: string) => {
                    const formatted = value >= 1000000 ? `₹${(value / 1000000).toFixed(1)}M` : value > 0 ? `₹${(value / 1000).toFixed(0)}K` : '₹0';
                    return [formatted, name];
                  }}
                  labelFormatter={(label) => {
                    const item = pipelineData.find(d => d.stage === label);
                    return `${label} — ${item?.value || 0} deals`;
                  }}
                />
                <Bar dataKey="leadAmount" name="Lead Value" stackId="pipeline" fill="#4F7A3A" radius={[0, 0, 0, 0]} />
                <Bar dataKey="oppAmount" name="Opportunity Value" stackId="pipeline" fill="#4B5B7A" radius={[0, 4, 4, 0]}>
                  <LabelList
                    dataKey="amount"
                    position="right"
                    offset={6}
                    formatter={(v: number) => v > 0 ? (v >= 1000000 ? `₹${(v / 1000000).toFixed(1)}M` : `₹${(v / 1000).toFixed(0)}K`) : ''}
                    style={{ fontSize: '9px', fill: '#1f2937', fontWeight: 700 }}
                  />
                  <LabelList
                    dataKey="value"
                    position="right"
                    offset={45}
                    formatter={(v: number) => v > 0 ? `${v}` : ''}
                    style={{ fontSize: '10px', fill: '#009E8A', fontWeight: 700 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Summary footer */}
            {(() => {
              const totalDeals = pipelineData.reduce((sum, d) => sum + d.value, 0);
              const totalAmount = pipelineData.reduce((sum, d) => sum + d.amount, 0);
              const totalLeadAmt = pipelineData.reduce((sum, d) => sum + (d.leadAmount || 0), 0);
              const totalOppAmt = pipelineData.reduce((sum, d) => sum + (d.oppAmount || 0), 0);
              // Separate lead count vs opportunity count for clarity
              const totalLeadCount = pipelineData.filter(d => d.type === 'lead').reduce((sum, d) => sum + d.value, 0);
              const totalOppCount = totalDeals - totalLeadCount;
              return (
                <div className="flex items-center justify-between mt-1 pt-1.5 border-t border-gray-100 px-1">
                  <div className="flex items-center gap-3">
                    {totalLeadCount > 0 && (
                      <span className="text-[10px] font-semibold" style={{ color: '#4F7A3A' }}>
                        {totalLeadCount} leads
                      </span>
                    )}
                    <span className="text-[10px] font-semibold" style={{ color: '#4B5B7A' }}>
                      {totalOppCount} opps
                    </span>
                    <span className="text-[10px] font-bold" style={{ color: '#009E8A' }}>
                      {totalAmount >= 1000000 ? `₹${(totalAmount / 1000000).toFixed(1)}M` : `₹${(totalAmount / 1000).toFixed(0)}K`} total
                    </span>
                  </div>
                </div>
              );
            })()}
          </Card>

          {/* Weekly Leads & Opportunities Progress */}
          <Card className="p-3">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-gray-900">Weekly Leads & Opportunities</h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: '#9CAF88' }}></div>
                  <span className="text-[9px] text-gray-500 font-medium">New Leads</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: '#00BBA2' }}></div>
                  <span className="text-[9px] text-gray-500 font-medium">Converted</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-0.5 rounded" style={{ backgroundColor: '#D9A441' }}></div>
                  <span className="text-[9px] text-gray-500 font-medium">Open Leads</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-0.5 rounded" style={{ backgroundColor: '#6A7CA5' }}></div>
                  <span className="text-[9px] text-gray-500 font-medium">Opportunities</span>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={performanceData} margin={{ top: 15, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorNewLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9CAF88" stopOpacity={0.6}/>
                    <stop offset="95%" stopColor="#9CAF88" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="colorConverted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00BBA2" stopOpacity={0.6}/>
                    <stop offset="95%" stopColor="#00BBA2" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="month" tick={{fontSize: 9}} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{fontSize: 9}} axisLine={false} tickLine={false} label={{ value: 'Weekly', angle: -90, position: 'insideLeft', style: { fontSize: '8px', fill: '#9CA3AF' } }} />
                <YAxis yAxisId="right" orientation="right" tick={{fontSize: 9}} axisLine={false} tickLine={false} label={{ value: 'Cumulative', angle: 90, position: 'insideRight', style: { fontSize: '8px', fill: '#9CA3AF' } }} />
                <Tooltip
                  contentStyle={{fontSize: '11px', borderRadius: '8px'}}
                  formatter={(value, name) => {
                    const labels = { newLeads: 'New Leads', converted: 'Converted', openLeads: 'Open Leads', opportunities: 'Active Opps' };
                    return [value, labels[name] || name];
                  }}
                />
                {/* Bars: New Leads and Converted (weekly counts) */}
                <Bar yAxisId="left" dataKey="newLeads" fill="url(#colorNewLeads)" radius={[3, 3, 0, 0]} name="newLeads" barSize={18}>
                  <LabelList dataKey="newLeads" position="top" offset={5} formatter={(v) => v > 0 ? v : ''} style={{fontSize: '8px', fill: '#6F8F6A', fontWeight: '700'}} />
                </Bar>
                <Bar yAxisId="left" dataKey="converted" fill="url(#colorConverted)" radius={[3, 3, 0, 0]} name="converted" barSize={18}>
                  <LabelList dataKey="converted" position="top" offset={5} formatter={(v) => v > 0 ? v : ''} style={{fontSize: '8px', fill: '#00BBA2', fontWeight: '700'}} />
                </Bar>
                {/* Lines: Cumulative Open Leads and Active Opportunities */}
                <Line yAxisId="right" type="monotone" dataKey="openLeads" stroke="#D9A441" strokeWidth={2} dot={{ fill: '#D9A441', r: 3, stroke: '#fff', strokeWidth: 1.5 }} name="openLeads"
                  label={(props: any) => { const { x, y, value } = props; return value > 0 ? <text key={`ol-${x}`} x={x} y={y - 7} textAnchor="middle" fontSize={9} fontWeight={700} fill="#D9A441">{value}</text> : <text key={`ol-${x}`}/>; }} />
                <Line yAxisId="right" type="monotone" dataKey="opportunities" stroke="#6A7CA5" strokeWidth={2} dot={{ fill: '#6A7CA5', r: 3, stroke: '#fff', strokeWidth: 1.5 }} name="opportunities"
                  label={(props: any) => { const { x, y, value } = props; return value > 0 ? <text key={`op-${x}`} x={x} y={y + 16} textAnchor="middle" fontSize={9} fontWeight={700} fill="#6A7CA5">{value}</text> : <text key={`op-${x}`}/>; }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
          </div>
          </div>
        )}

        {/* Sales Activity Tab - UPDATED VERSION */}
        {dashboardSubTab === 'activity' && (
          <div className="space-y-3">
            {/* Weekly Summary Section */}
            {(() => {
              const now = new Date();
              const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

              // Lead metrics for last 7 days
              const weeklyNewLeads = dashboardLeads.filter(l => {
                const d = l.dateCaptured ? new Date(l.dateCaptured) : (l.createdDate ? new Date(l.createdDate) : null);
                return d && d >= oneWeekAgo && d <= now;
              });
              const weeklyConvertedLeads = dashboardLeads.filter(l => {
                if (l.leadStatus !== 'Converted' || !l.convertedAt) return false;
                const d = new Date(l.convertedAt);
                return d >= oneWeekAgo && d <= now;
              });

              // Calculate weekly movement
              const weeklyNewOpps = filteredOpportunities.filter(opp => {
                if (!opp.initiationDate) return false;
                const initDate = new Date(opp.initiationDate);
                return initDate >= oneWeekAgo && initDate <= now;
              });

              const weeklyModifiedOpps = filteredOpportunities.filter(opp => {
                if (!opp.lastEditedDate) return false;
                const editDate = new Date(opp.lastEditedDate);
                return editDate >= oneWeekAgo && editDate <= now;
              });

              const weeklyWon = getFilteredOpportunitiesExcluding('dateRange').filter(opp => {
                if (opp.salesStage !== 'Closed-Won') return false;
                if ((opp.opportunityName || '').toLowerCase().includes('test')) return false;
                const closeDate = new Date(opp.actualCloseDate || opp.lastEditedDate);
                return closeDate >= oneWeekAgo && closeDate <= now;
              });

              const weeklyLost = getFilteredOpportunitiesExcluding('dateRange').filter(opp => {
                if (opp.salesStage !== 'Closed-Lost') return false;
                if ((opp.opportunityName || '').toLowerCase().includes('test')) return false;
                const closeDate = new Date(opp.actualCloseDate || opp.lastEditedDate);
                return closeDate >= oneWeekAgo && closeDate <= now;
              });

              // Count stage changes from engagement history in last week
              const parseEngagementDate = (entry: string): Date | null => {
                const ddMmmMatch = entry.match(/(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
                if (ddMmmMatch) {
                  const monthMap: { [key: string]: number } = {
                    'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                    'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
                  };
                  const day = parseInt(ddMmmMatch[1]);
                  const month = monthMap[ddMmmMatch[2]];
                  const year = parseInt(ddMmmMatch[3]);
                  if (!isNaN(day) && month !== undefined && !isNaN(year)) {
                    return new Date(year, month, day);
                  }
                }
                const isoMatch = entry.match(/(\d{4})-(\d{2})-(\d{2})/);
                if (isoMatch) return new Date(isoMatch[0]);
                const bracketMatch = entry.match(/\[(\d{1,2})\/(\d{1,2})\/(\d{4})/);
                if (bracketMatch) return new Date(parseInt(bracketMatch[3]), parseInt(bracketMatch[1]) - 1, parseInt(bracketMatch[2]));
                return null;
              };

              let stageChanges = 0;
              filteredOpportunities.forEach(opp => {
                if (opp.engagementHistory) {
                  opp.engagementHistory.split('\n').forEach(entry => {
                    if (entry.includes('Stage Changed') || entry.includes('Stage Regressed')) {
                      const entryDate = parseEngagementDate(entry);
                      if (entryDate && entryDate >= oneWeekAgo && entryDate <= now) stageChanges++;
                    }
                  });
                }
              });

              // Pipeline summary — must match activeOpportunities definition (excl Won, Lost, Contract, Assessment, Lead stages, Inactive)
              const activeOppsForSummary = filteredOpportunities.filter(o => {
                const stage = (o.salesStage || '').toLowerCase();
                return !stage.includes('won') &&
                  !stage.includes('lost') &&
                  stage !== 'contract' &&
                  stage !== 'assessment' &&
                  !stage.includes('lead') &&
                  getEffectiveSalesStatus(o) !== 'Inactive';
              });
              const totalActive = activeOppsForSummary.length;
              const totalPipeline = activeOppsForSummary.reduce((sum, o) => sum + (o.annualContractValue || 0), 0);
              const avgDealSize = totalActive > 0 ? totalPipeline / totalActive : 0;
              const hotOppsCount = activeOppsForSummary.filter(o => getEffectiveSalesStatus(o) === 'Hot').length;
              const proposalStageCount = activeOppsForSummary.filter(o =>
                ['Proposal', 'Contract'].includes(o.salesStage)
              ).length;
              const weeklyWonValue = weeklyWon.reduce((sum, o) => sum + (o.totalContractValue || o.annualContractValue || 0), 0);

              // Helper: clickable movement card item
              const MovementItem = ({ filterKey, count, label, color, isActive }: { filterKey: string; count: number; label: string; color: string; isActive: boolean }) => (
                <button
                  onClick={() => { setActivityMovementFilter(isActive ? null : filterKey); setActivityPage(1); }}
                  className={`text-center p-2 rounded-lg shadow-sm transition-all cursor-pointer ${isActive ? 'ring-2 ring-offset-1 ring-' + color + ' bg-gray-50' : 'bg-white hover:bg-gray-50'}`}
                  style={isActive ? { ringColor: color, boxShadow: `0 0 0 2px ${color}` } : {}}
                >
                  <p className="text-2xl font-bold" style={{ color }}>{count}</p>
                  <p className="text-[10px] text-gray-600 leading-tight">{label}</p>
                </button>
              );

              return (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Last 7 Days Movement — Leads & Opportunities */}
                  <Card className="p-4 bg-gradient-to-br from-[rgba(0,187,162,0.06)] to-white border-[rgba(0,187,162,0.2)]">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <TrendingUp size={18} className="text-pierian" />
                        <h4 className="text-sm font-semibold text-gray-900">Last 7 Days Movement</h4>
                      </div>
                      {activityMovementFilter && (
                        <button onClick={() => setActivityMovementFilter(null)} className="text-[10px] text-pierian hover:underline">Clear filter</button>
                      )}
                    </div>
                    {/* Leads row */}
                    <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Leads</p>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <MovementItem filterKey="newLeads" count={weeklyNewLeads.length} label="New Leads" color="#D9A441" isActive={activityMovementFilter === 'newLeads'} />
                      <MovementItem filterKey="convertedLeads" count={weeklyConvertedLeads.length} label="Converted" color="#9CAF88" isActive={activityMovementFilter === 'convertedLeads'} />
                    </div>
                    {/* Opportunities row */}
                    <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Opportunities</p>
                    <div className="grid grid-cols-3 gap-2">
                      <MovementItem filterKey="newOpps" count={weeklyNewOpps.length} label="New Opps" color="#00BBA2" isActive={activityMovementFilter === 'newOpps'} />
                      <MovementItem filterKey="stageChanges" count={stageChanges} label="Stage Changes" color="#6A7CA5" isActive={activityMovementFilter === 'stageChanges'} />
                      <MovementItem filterKey="updated" count={weeklyModifiedOpps.length} label="Updated" color="#D9A441" isActive={activityMovementFilter === 'updated'} />
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-4">
                        <button onClick={() => { setActivityMovementFilter(activityMovementFilter === 'won' ? null : 'won'); setActivityPage(1); }} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${activityMovementFilter === 'won' ? 'bg-green-100' : 'hover:bg-gray-50'}`}>
                          <CheckCircle size={14} className="text-sage" />
                          <span className="text-xs font-medium text-sage">{weeklyWon.length} Won</span>
                          {weeklyWonValue > 0 && <span className="text-xs text-gray-500">(₹{(weeklyWonValue / 1000).toFixed(0)}K)</span>}
                        </button>
                        <button onClick={() => { setActivityMovementFilter(activityMovementFilter === 'lost' ? null : 'lost'); setActivityPage(1); }} className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${activityMovementFilter === 'lost' ? 'bg-red-100' : 'hover:bg-gray-50'}`}>
                          <XCircle size={14} className="text-pierian" />
                          <span className="text-xs font-medium text-pierian">{weeklyLost.length} Lost</span>
                        </button>
                      </div>
                    </div>
                  </Card>

                  {/* Pipeline Summary */}
                  <Card className="p-4 bg-gradient-to-br from-slate-50 to-white">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <BarChart3 size={18} className="text-slate-blue" />
                        <h4 className="text-sm font-semibold text-gray-900">Pipeline Summary</h4>
                      </div>
                      <span className="text-[9px] text-sage font-medium">Excluding Won, Lost & Inactive</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-2 bg-white rounded-lg shadow-sm">
                        <p className="text-2xl font-bold text-gray-900">{totalActive}</p>
                        <p className="text-xs text-gray-600">Active Opps</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg shadow-sm">
                        <p className="text-2xl font-bold text-slate-blue">
                          ₹{totalPipeline >= 1000000 ? (totalPipeline / 1000000).toFixed(1) + 'M' : (totalPipeline / 1000).toFixed(0) + 'K'}
                        </p>
                        <p className="text-xs text-gray-600">Total Pipeline</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg shadow-sm">
                        <p className="text-2xl font-bold text-amber">
                          ₹{avgDealSize >= 1000000 ? (avgDealSize / 1000000).toFixed(1) + 'M' : (avgDealSize / 1000).toFixed(0) + 'K'}
                        </p>
                        <p className="text-xs text-gray-600">Avg Deal Size</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Flame size={14} className="text-pierian" />
                          <span className="text-xs font-medium">{hotOppsCount} Hot</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText size={14} className="text-amber" />
                          <span className="text-xs font-medium">{proposalStageCount} In Proposal+</span>
                        </div>
                      </div>
                      <span className="text-[9px] text-gray-400">Includes: Hot, Warm, Cold</span>
                    </div>
                  </Card>
                </div>
              );
            })()}

            {/* Header with Search */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-gray-900">Sales Activity Feed</h3>
                {activityMovementFilter && (
                  <span className="text-xs bg-pierian text-white px-2 py-0.5 rounded-full">
                    Filtered: {activityMovementFilter === 'newLeads' ? 'New Leads' : activityMovementFilter === 'convertedLeads' ? 'Converted Leads' : activityMovementFilter === 'newOpps' ? 'New Opportunities' : activityMovementFilter === 'stageChanges' ? 'Stage Changes' : activityMovementFilter === 'updated' ? 'Updated Opps' : activityMovementFilter === 'won' ? 'Won' : activityMovementFilter === 'lost' ? 'Lost' : activityMovementFilter}
                    <button onClick={() => setActivityMovementFilter(null)} className="ml-1 hover:text-gray-200">×</button>
                  </span>
                )}
                {(hasActiveFilters || !activityMovementFilter) && (
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500">
                      ({getAllActivities().length} activities from {filteredOpportunities.length} opps + {dashboardLeads.length} leads)
                    </span>
                  </div>
                )}
              </div>
              {/* Search Input */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search activities..."
                  value={activitySearchTerm}
                  onChange={(e) => setActivitySearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian w-64"
                />
                {activitySearchTerm && (
                  <button
                    onClick={() => setActivitySearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Flat Activity List with Pagination */}
            {(() => {
              const { activities, totalCount, totalPages } = getPaginatedActivities();
              return (
                <div className="space-y-4">
                  {/* Activity Count Header */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      Showing {activities.length > 0 ? ((activityPage - 1) * activitiesPerPage) + 1 : 0} - {Math.min(activityPage * activitiesPerPage, totalCount)} of {totalCount} activities
                      {hasActiveFilters && <span className="text-gray-400 ml-2">(filtered)</span>}
                    </span>
                  </div>

                  {/* Activities Card */}
                  <Card className="p-4">
                    <div className="space-y-2">
                      {activities.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Target className="mx-auto mb-2 text-gray-300" size={32} />
                          <p>No activities found</p>
                          {activitySearchTerm && <p className="text-sm">Try adjusting your search</p>}
                          {hasActiveFilters && <p className="text-sm">Try clearing some filters</p>}
                        </div>
                      ) : (
                        activities.map((activity) => (
                          <div key={activity.id} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                            <div className={`p-1.5 rounded-lg flex-shrink-0 ${activity.type === 'Lead' ? (activity.actionType === 'Lead Converted' ? 'bg-green-50' : activity.actionType === 'Lead Unqualified' || activity.actionType === 'Lead Lost' || activity.actionType === 'Lead Deleted' ? 'bg-teal-50' : activity.actionType === 'Lead Status Change' ? 'bg-blue-50' : 'bg-amber-50') : 'bg-sage-tint'}`}>
                              {activity.type === 'Lead' ? (
                                activity.actionType === 'Lead Converted' ? <CheckCircle className="text-sage" size={14} /> :
                                activity.actionType === 'Lead Unqualified' ? <XCircle className="text-orange-500" size={14} /> :
                                activity.actionType === 'Lead Lost' ? <Ban className="text-red-500" size={14} /> :
                                activity.actionType === 'Lead Deleted' ? <Trash2 className="text-red-400" size={14} /> :
                                activity.actionType === 'Lead Status Change' ? <RefreshCw className="text-blue-500" size={14} /> :
                                <Users className="text-amber" size={14} />
                              ) : (
                                <Target className="text-sage" size={14} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              {/* Line 1: Full descriptive sentence */}
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm text-gray-900">
                                  {activity.type === 'Lead' && (
                                    <span className={`text-[9px] font-semibold px-1 py-0.5 rounded mr-1 ${activity.actionType === 'Lead Converted' ? 'bg-green-100 text-green-800' : activity.actionType === 'Lead Unqualified' || activity.actionType === 'Lead Lost' || activity.actionType === 'Lead Deleted' ? 'bg-red-100 text-red-800' : activity.actionType === 'Lead Status Change' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>LEAD</span>
                                  )}
                                  <span className="font-semibold">{activity.relatedTo}</span>
                                  {' '}
                                  {activity.actionType === 'Lead Created' && (
                                    <>— <span className="font-medium" style={{ color: '#D9A441' }}>new lead captured</span> from <span className="font-medium">{activity.description?.replace('New lead captured from ', '') || 'Unknown'}</span></>
                                  )}
                                  {activity.actionType === 'Lead Converted' && (
                                    <>— <span className="font-medium text-sage">converted to opportunity</span></>
                                  )}
                                  {activity.actionType === 'Lead Unqualified' && (
                                    <>— <span className="font-medium text-orange-600">unqualified</span>{activity.description && activity.description !== 'Lead unqualified' && <> — <span className="text-gray-600">{activity.description}</span></>}</>
                                  )}
                                  {activity.actionType === 'Lead Lost' && (
                                    <>— <span className="font-medium text-red-600">marked as lost</span>{activity.description && activity.description !== 'Lead marked as lost' && <> — <span className="text-gray-600">{activity.description}</span></>}</>
                                  )}
                                  {activity.actionType === 'Lead Deleted' && (
                                    <>— <span className="font-medium text-red-500">deleted</span></>
                                  )}
                                  {activity.actionType === 'Lead Status Change' && (
                                    <>— <span className="font-medium text-[#00BBA2]">{activity.description}</span></>
                                  )}
                                  {activity.actionType === 'Stage Update' && activity.action !== 'Stage Changed' && activity.action !== 'Stage Regressed' && (
                                    <>moved to <span className={`font-medium ${activity.stage === 'Closed-Won' ? 'text-sage' : activity.stage === 'Closed-Lost' ? 'text-pierian' : 'text-pierian'}`}>{activity.stage}</span> stage</>
                                  )}
                                  {activity.actionType === 'Stage Update' && (activity.action === 'Stage Changed' || activity.action === 'Stage Regressed') && (
                                    <>- <span className={`text-gray-600 ${activity.action === 'Stage Regressed' ? 'text-amber' : ''}`}>
                                      {activity.action === 'Stage Regressed' && '⭐ '}
                                      {activity.description}
                                    </span></>
                                  )}
                                  {(activity.actionType === 'Created' || activity.actionType === 'Opp Created') && (
                                    <>was <span className="font-medium text-pierian">created</span> in <span className="font-medium">{activity.stage}</span> stage</>
                                  )}
                                  {activity.actionType === 'Status Update' && (
                                    <>status changed to <span className={`font-medium ${activity.stage === 'Closed-Won' ? 'text-sage' : activity.stage === 'Closed-Lost' ? 'text-pierian' : 'text-amber'}`}>{activity.status || activity.stage}</span></>
                                  )}
                                  {activity.actionType === 'Probability Update' && (
                                    <>- <span className="text-slate-blue">{activity.description}</span></>
                                  )}
                                  {activity.actionType === 'Review' && (
                                    <>- <span className={`font-medium ${activity.action === 'Marked for Review' ? 'text-amber' : 'text-gray-600'}`}>
                                      {activity.action === 'Marked for Review' && '🚩 '}
                                      {activity.action}
                                    </span></>
                                  )}
                                  {activity.actionType === 'Engagement' && (
                                    <>- <span className="text-gray-600">{activity.action}: {activity.description}</span></>
                                  )}
                                  {!['Stage Update', 'Created', 'Opp Created', 'Status Update', 'Engagement', 'Probability Update', 'Review', 'Lead Created', 'Lead Converted', 'Lead Unqualified', 'Lead Lost', 'Lead Deleted', 'Lead Status Change'].includes(activity.actionType) && (
                                    <>- {activity.description}</>
                                  )}
                                  {activity.revenue !== undefined && activity.revenue > 0 ? (
                                    <span className="text-sage font-semibold"> (₹{activity.revenue >= 1000000 ? (activity.revenue / 1000000).toFixed(1) + 'M' : (activity.revenue / 1000).toFixed(0) + 'K'})</span>
                                  ) : (
                                    <span className="font-bold" style={{ color: '#00BBA2' }}> (₹0K)</span>
                                  )}
                                </p>
                                <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{getTimeAgo(activity.date)}</span>
                              </div>
                              {/* Line 2: Metadata */}
                              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                {activity.customer && activity.customer !== 'Unknown' && (
                                  <>
                                    <span>Customer: <span className="font-medium text-gray-700">{activity.customer}</span></span>
                                    <span className="text-gray-300">•</span>
                                  </>
                                )}
                                <span>Owner: <span className="font-medium text-gray-700">{activity.salesperson}</span></span>
                                {activity.geography && activity.geography !== 'Unknown' && (
                                  <>
                                    <span className="text-gray-300">•</span>
                                    <span>{activity.geography}</span>
                                  </>
                                )}
                                {activity.serviceLine && activity.serviceLine !== 'Unknown' && (
                                  <>
                                    <span className="text-gray-300">•</span>
                                    <span>{activity.serviceLine}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            {/* View Link — Opportunity or Lead */}
                            {activity.type === 'Lead' ? (
                              <button
                                onClick={() => setActiveTab('leads')}
                                className="p-1.5 text-amber hover:bg-amber-50 rounded flex-shrink-0"
                                title="Go to Leads"
                              >
                                <ExternalLink size={16} />
                              </button>
                            ) : (
                              <button
                                onClick={async () => {
                                  const opp = opportunities.find(o => o.opportunityId === activity.relatedId);
                                  if (opp) {
                                    const freshOpp = await fetchOpportunityById(opp.opportunityId);
                                    setSelectedOpportunity(freshOpp || opp);
                                    setShowOpportunityModal(true);
                                  }
                                }}
                                className="p-1.5 text-pierian hover:bg-pierian-10 rounded flex-shrink-0"
                                title="View Opportunity"
                              >
                                <ExternalLink size={16} />
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </Card>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        Page {activityPage} of {totalPages}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setActivityPage(1)}
                          disabled={activityPage === 1}
                          className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          First
                        </button>
                        <button
                          onClick={() => setActivityPage(p => Math.max(1, p - 1))}
                          disabled={activityPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1 text-sm bg-pierian text-white rounded">
                          {activityPage}
                        </span>
                        <button
                          onClick={() => setActivityPage(p => Math.min(totalPages, p + 1))}
                          disabled={activityPage === totalPages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => setActivityPage(totalPages)}
                          disabled={activityPage === totalPages}
                          className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Performance Tab */}
        {dashboardSubTab === 'performance' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Performance Metrics</h3>
              {hasActiveFilters && (
                <span className="text-xs text-gray-500">
                  Based on {filteredOpportunities.length} of {opportunities.length} opportunities
                </span>
              )}
            </div>

            {/* Pipeline Conversion Metrics */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-pierian-10 rounded-lg">
                    <Users className="text-pierian" size={24} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">Total Pipeline Value</p>
                      <span className="text-[8px] text-sage font-medium">Excl. Won, Lost, Inactive</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      ₹{(() => { const total = pipelineData.reduce((sum, stage) => sum + stage.amount, 0); return total >= 1000000 ? (total / 1000000).toFixed(1) + 'M' : (total / 1000).toFixed(0) + 'K'; })()}
                    </p>
                    <p className="text-xs text-sage font-medium mt-1">
                      {(() => {
                        const oppOnlyCount = pipelineData.filter(d => d.type === 'opportunity').reduce((sum, d) => sum + d.value, 0);
                        const leadCount = pipelineData.reduce((sum, d) => sum + d.value, 0) - oppOnlyCount;
                        return leadCount > 0
                          ? `${oppOnlyCount} opps + ${leadCount} leads`
                          : `${oppOnlyCount} opportunities`;
                      })()}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-sage-tint rounded-lg">
                    <Target className="text-sage" size={24} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">Avg Deal Size</p>
                      <span className="text-[8px] text-sage font-medium">Excl. Won, Lost, Inactive</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      ₹{(() => {
                        const oppOnlyAmt = pipelineData.filter(d => d.type !== 'lead').reduce((sum, d) => sum + (d.oppAmount || 0), 0);
                        const oppOnlyCount = pipelineData.filter(d => d.type === 'opportunity').reduce((sum, d) => sum + d.value, 0);
                        return oppOnlyCount > 0 ? (oppOnlyAmt / oppOnlyCount).toLocaleString(undefined, {maximumFractionDigits: 0}) : '0';
                      })()}
                    </p>
                    <p className="text-xs text-gray-600 font-medium mt-1">Per opportunity</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amethyst-tint rounded-lg">
                    <CheckCircle className="text-amethyst" size={24} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">Win Rate</p>
                      <span className="text-[8px] text-gray-400 font-medium">Won / (Won + Lost)</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {(() => {
                        const closedWonCount = filteredOpportunities.filter(o => o.salesStage === 'Closed-Won').length;
                        const closedLostCount = filteredOpportunities.filter(o => o.salesStage === 'Closed-Lost').length;
                        const closedTotal = closedWonCount + closedLostCount;
                        return closedTotal > 0 ? ((closedWonCount / closedTotal) * 100).toFixed(1) : '0.0';
                      })()}%
                    </p>
                    <p className="text-xs text-gray-600 font-medium mt-1">
                      {filteredOpportunities.filter(o => o.salesStage === 'Closed-Won').length} won, {filteredOpportunities.filter(o => o.salesStage === 'Closed-Lost').length} lost
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Charts Grid - 2 Column Layout */}
            <div className="grid grid-cols-2 gap-4">
              {/* Weekly Performance Trends */}
              <Card className="p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Weekly Performance Trends</h4>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{fontSize: 12}} />
                    <YAxis yAxisId="left" tick={{fontSize: 12}} />
                    <YAxis yAxisId="right" orientation="right" tick={{fontSize: 12}} tickFormatter={(value) => value >= 1000000 ? `₹${(value / 1000000).toFixed(1)}M` : `₹${(value / 1000).toFixed(0)}K`} />
                    <Tooltip
                      contentStyle={{fontSize: '12px', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px'}}
                      formatter={(value: number, name: string) => {
                        if (name === 'Revenue (₹)') {
                          return [value >= 1000000 ? `₹${(value / 1000000).toFixed(1)}M` : `₹${(value / 1000).toFixed(0)}K`, name];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line yAxisId="left" type="monotone" dataKey="opportunities" stroke="#00BBA2" strokeWidth={2} name="Opportunities" dot={{ r: 3 }}
                      label={(props: any) => { const { x, y, value } = props; return value > 0 ? <text key={`wpt-op-${x}`} x={x} y={y - 7} textAnchor="middle" fontSize={9} fontWeight={700} fill="#00BBA2">{value}</text> : <text key={`wpt-op-${x}`}/>; }} />
                    <Line yAxisId="left" type="monotone" dataKey="newLeads" stroke="#6A7CA5" strokeWidth={2} name="New Leads" dot={{ r: 3 }}
                      label={(props: any) => { const { x, y, value } = props; return value > 0 ? <text key={`wpt-nl-${x}`} x={x} y={y + 16} textAnchor="middle" fontSize={9} fontWeight={700} fill="#6A7CA5">{value}</text> : <text key={`wpt-nl-${x}`}/>; }} />
                    <Line yAxisId="left" type="monotone" dataKey="converted" stroke="#9CAF88" strokeWidth={2} name="Leads Converted" dot={{ r: 3 }} strokeDasharray="5 5"
                      label={(props: any) => { const { x, y, value } = props; return value > 0 ? <text key={`wpt-cv-${x}`} x={x} y={y - 7} textAnchor="middle" fontSize={9} fontWeight={700} fill="#9CAF88">{value}</text> : <text key={`wpt-cv-${x}`}/>; }} />
                    <Line yAxisId="left" type="monotone" dataKey="openLeads" stroke="#D9A441" strokeWidth={2} name="Open Leads" dot={{ r: 3 }}
                      label={(props: any) => { const { x, y, value } = props; return value > 0 ? <text key={`wpt-ol-${x}`} x={x} y={y + 16} textAnchor="middle" fontSize={9} fontWeight={700} fill="#D9A441">{value}</text> : <text key={`wpt-ol-${x}`}/>; }} />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#481F89" strokeWidth={2} name="Revenue (₹)" dot={{ r: 3 }}
                      label={(props: any) => { const { x, y, value } = props; if (!value || value === 0) return <text key={`wpt-rv-${x}`}/>; const fmt = value >= 1000000 ? `₹${(value/1000000).toFixed(1)}M` : `₹${(value/1000).toFixed(0)}K`; return <text key={`wpt-rv-${x}`} x={x} y={y - 7} textAnchor="middle" fontSize={9} fontWeight={700} fill="#481F89">{fmt}</text>; }} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              {/* Active Sales Pipeline Funnel */}
              <Card className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-900">Active Sales Pipeline Funnel</h4>
                  <span className="text-[9px] text-sage font-medium">Excluding Won, Lost & Inactive</span>
                </div>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={pipelineData} layout="vertical" margin={{ right: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" tick={{fontSize: 11}} />
                    <YAxis dataKey="stage" type="category" tick={{fontSize: 11}} width={110} />
                    <Tooltip
                      contentStyle={{fontSize: '12px', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px'}}
                      formatter={(value, name) => {
                        if (name === 'Deals') return [`${value} deals`, 'Count'];
                        if (name === 'Value (₹)') return [`₹${value.toLocaleString()}`, 'Amount'];
                        return value;
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} name="Deals">
                      {pipelineData.map((entry, index) => (
                        <Cell key={`cell-deals-${index}`} fill={entry.fill} />
                      ))}
                      <LabelList dataKey="value" position="right" style={{fontSize: 11, fill: '#131A25'}} />
                    </Bar>
                    <Bar dataKey="amount" radius={[0, 4, 4, 0]} name="Value (₹)">
                      {pipelineData.map((entry, index) => (
                        <Cell key={`cell-amount-${index}`} fill={entry.fill} opacity={0.6} />
                      ))}
                      <LabelList
                        dataKey="amount"
                        position="right"
                        style={{fontSize: 11, fill: '#1F2937'}}
                        formatter={(value) => `₹${value >= 1000000 ? (value / 1000000).toFixed(1) + 'M' : (value / 1000).toFixed(0) + 'K'}`}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>

            {/* Weekly Lead Conversion Chart */}
            {dashboardLeads.length > 0 && (
              <Card className="p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Weekly Lead Conversion</h4>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={(() => {
                    // Compute 8-week rolling window
                    const now = new Date();
                    const weeks: { label: string; created: number; converted: number }[] = [];
                    for (let i = 7; i >= 0; i--) {
                      const weekStart = new Date(now);
                      weekStart.setDate(now.getDate() - (i * 7) - now.getDay());
                      weekStart.setHours(0, 0, 0, 0);
                      const weekEnd = new Date(weekStart);
                      weekEnd.setDate(weekStart.getDate() + 6);
                      weekEnd.setHours(23, 59, 59, 999);
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      const label = `${weekStart.getDate()}-${monthNames[weekStart.getMonth()]}`;
                      const created = dashboardLeads.filter(l => {
                        const d = l.dateCaptured ? new Date(l.dateCaptured) : (l.createdDate ? new Date(l.createdDate) : null);
                        return d && d >= weekStart && d <= weekEnd;
                      }).length;
                      const converted = dashboardLeads.filter(l => {
                        const d = l.convertedAt ? new Date(l.convertedAt) : null;
                        return d && d >= weekStart && d <= weekEnd;
                      }).length;
                      weeks.push({ label, created, converted });
                    }
                    return weeks;
                  })()} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ fontSize: '12px', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="created" fill="#6A7CA5" name="Leads Created" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="created" position="top" formatter={(v: number) => v > 0 ? v : ''} style={{ fontSize: '11px', fontWeight: 700, fill: '#6A7CA5' }} />
                    </Bar>
                    <Bar dataKey="converted" fill="#00BBA2" name="Leads Converted" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="converted" position="top" formatter={(v: number) => v > 0 ? v : ''} style={{ fontSize: '11px', fontWeight: 700, fill: '#00BBA2' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

          </div>
        )}

        {/* Reports Tab */}
        {dashboardSubTab === 'reports' && (() => {
          // Sort handler for report tables
          const handleReportSort = (field: string) => {
            if (reportSortField === field) {
              setReportSortDir(reportSortDir === 'asc' ? 'desc' : 'asc');
            } else {
              setReportSortField(field);
              setReportSortDir('asc');
            }
          };

          // Generic sort function
          const sortData = (data, field, direction) => {
            if (!field) return data;
            return [...data].sort((a, b) => {
              let aVal = a[field];
              let bVal = b[field];
              if (typeof aVal === 'number' && typeof bVal === 'number') {
                return direction === 'asc' ? aVal - bVal : bVal - aVal;
              }
              aVal = String(aVal || '').toLowerCase();
              bVal = String(bVal || '').toLowerCase();
              if (aVal < bVal) return direction === 'asc' ? -1 : 1;
              if (aVal > bVal) return direction === 'asc' ? 1 : -1;
              return 0;
            });
          };

          // Filter data by search term
          const filterBySearch = (data, search) => {
            if (!search) return data;
            const term = search.toLowerCase();
            return data.filter(row =>
              Object.values(row).some(val =>
                String(val).toLowerCase().includes(term)
              )
            );
          };

          // Report definitions with data generators
          // Only leadreport and oppreport are shown; others are hidden (remove the .filter to restore all)
          const reportTypes = ([
            {
              id: 'pipeline',
              name: 'Pipeline Report',
              description: 'Active pipeline analysis with leads & opportunities (Excluding Won, Lost & Inactive)',
              icon: BarChart2,
              color: '#6A7CA5',
              bgColor: 'rgba(106,124,165,0.18)',
              getData: () => {
                const safeNumber = (val: any): number => {
                  if (val === null || val === undefined || val === '') return 0;
                  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                  return isNaN(num) || !isFinite(num) ? 0 : num;
                };

                // Lead rows
                const leadRows = dashboardLeads.filter(l => l.leadStatus !== 'Converted').map(lead => ({
                  'Type': 'Lead',
                  'Name': lead.companyName || '',
                  'Stage / Status': lead.leadStatus || '',
                  'Source': lead.leadSource || '',
                  'Est. Revenue': safeNumber(lead.estimatedRevenue),
                  'Geography': lead.geography || '',
                  'Service Line': lead.serviceLine || '',
                  'Owner': lead.leadOwner || lead.salesRepresentative || '',
                  'Priority': lead.priorityLevel || ''
                }));

                // Opportunity rows (excluding Won, Lost, Inactive)
                const oppRows = filteredOpportunities.filter(o =>
                  o.salesStage !== 'Closed-Won' &&
                  o.salesStage !== 'Closed-Lost' &&
                  getEffectiveSalesStatus(o) !== 'Inactive'
                ).map(opp => ({
                  'Type': 'Opportunity',
                  'Name': opp.opportunityName || '',
                  'Stage / Status': opp.salesStage || '',
                  'Source': '',
                  'Est. Revenue': safeNumber(opp.totalContractValue),
                  'Geography': opp.geography || '',
                  'Service Line': opp.serviceLine || '',
                  'Owner': opp.accountOwner || '',
                  'Priority': opp.priorityLevel || ''
                }));

                return [...leadRows, ...oppRows];
              },
              columns: ['Type', 'Name', 'Stage / Status', 'Source', 'Est. Revenue', 'Geography', 'Service Line', 'Owner'],
              valueColumns: ['Est. Revenue']
            },
            {
              id: 'crm',
              name: 'CRM Report',
              description: 'Combined leads & opportunities report with full contact and pipeline details',
              icon: FileText,
              color: '#4A6FBF',
              bgColor: 'rgba(74,111,191,0.14)',
              getData: () => {
                const safeNumber = (val: any): number => {
                  if (val === null || val === undefined || val === '') return 0;
                  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                  return isNaN(num) || !isFinite(num) ? 0 : num;
                };
                const formatDate = (dateStr: string | null | undefined): string => {
                  if (!dateStr) return '';
                  const date = new Date(dateStr);
                  if (isNaN(date.getTime())) return '';
                  const day = String(date.getDate()).padStart(2, '0');
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const year = date.getFullYear();
                  return `${day}-${month}-${year}`;
                };

                // Build lookup: normalised company name → array of matching opps
                const oppsByCompany: Record<string, any[]> = {};
                filteredOpportunities.forEach(opp => {
                  const key = (opp.associatedAccount || '').toLowerCase().trim();
                  if (key) {
                    if (!oppsByCompany[key]) oppsByCompany[key] = [];
                    oppsByCompany[key].push(opp);
                  }
                });

                // Build a combined row from an optional lead and optional opp
                const buildRow = (lead: any, opp: any) => ({
                  'Lead Creation Date': lead ? formatDate(lead.dateCaptured || lead.createdDate) : '',
                  'Lead ID': lead ? (lead.leadId || '') : '',
                  'Opp Creation Date': opp ? formatDate(opp.initiationDate || opp.createdAt) : '',
                  'Opp ID': opp ? (opp.opportunityId || '') : '',
                  'Status / Stages': opp ? (opp.salesStage || '') : (lead ? (lead.leadStatus || '') : ''),
                  'Lead Owner': opp ? (opp.accountOwner || '') : (lead ? (lead.leadOwner || lead.salesRepresentative || '') : ''),
                  'Service Line': opp ? (opp.serviceLine || '') : (lead ? (lead.serviceLine || '') : ''),
                  'Company Name': opp ? (opp.associatedAccount || '') : (lead ? (lead.companyName || '') : ''),
                  'Industry': opp ? (opp.industryVertical || '') : (lead ? (lead.industry || '') : ''),
                  'Amount': (() => {
                    if (opp && opp.totalContractValue != null && opp.totalContractValue !== 0) return safeNumber(opp.totalContractValue);
                    if (lead && lead.estimatedRevenue != null && lead.estimatedRevenue !== '') return safeNumber(lead.estimatedRevenue);
                    return null;
                  })(),
                  'Lead Source': lead ? (lead.leadSource || '') : '',
                  'Lead Rating': lead ? (lead.leadRating || '') : (opp ? getEffectiveSalesStatus(opp) : ''),
                  'City': lead ? (lead.city || '') : (opp ? (opp.city || '') : ''),
                  'Contact Person': opp ? (opp.primaryContact || '') : (lead ? (lead.contactPerson || '') : ''),
                  'Designation': lead ? (lead.designation || '') : '',
                  'Mobile': opp ? (opp.phoneNumber || '') : (lead ? (lead.phone || '') : ''),
                  'Email': opp ? (opp.primaryContactEmail || opp.email || '') : (lead ? (lead.email || '') : ''),
                  'Geography': opp ? (opp.geography || '') : (lead ? (lead.geography || '') : ''),
                  'Lead Generator': opp ? (opp.leadGenerator || '') : (lead ? (lead.leadGenerator || '') : ''),
                  'Lead Supporter': opp ? (opp.leadSupporter || '') : (lead ? (lead.leadSupporter || '') : ''),
                  'Business Type': opp ? (opp.opportunityType || opp.opportunityCategory || '') : (lead ? (lead.businessType || '') : ''),
                });

                const matchedOppIds = new Set<string>();
                const rows: any[] = [];

                // For each lead, find matching opps by company name
                dashboardLeads.forEach(lead => {
                  const key = (lead.companyName || '').toLowerCase().trim();
                  const matchingOpps = key ? (oppsByCompany[key] || []) : [];
                  if (matchingOpps.length > 0) {
                    matchingOpps.forEach(opp => {
                      matchedOppIds.add(opp.opportunityId);
                      rows.push(buildRow(lead, opp));
                    });
                  } else {
                    // Lead has no matching opp — show lead-only row
                    rows.push(buildRow(lead, null));
                  }
                });

                // Opps with no matching lead — show opp-only rows
                filteredOpportunities
                  .filter(opp => !matchedOppIds.has(opp.opportunityId))
                  .forEach(opp => rows.push(buildRow(null, opp)));

                return rows;
              },
              columns: ['Lead Creation Date', 'Lead ID', 'Opp Creation Date', 'Opp ID', 'Status / Stages', 'Lead Owner', 'Service Line', 'Company Name', 'Industry', 'Amount', 'Lead Source', 'Lead Rating', 'City', 'Contact Person', 'Designation', 'Mobile', 'Email', 'Geography', 'Lead Generator', 'Lead Supporter', 'Business Type'],
              valueColumns: ['Amount']
            },
            {
              id: 'forecast',
              name: 'Revenue Forecast',
              description: 'Projected revenue by quarter with leads & opportunities (Excluding Won, Lost & Inactive)',
              icon: TrendingUp,
              color: '#9CAF88',
              bgColor: '#C7D8C1',
              getData: () => {
                const safeNumber = (val: any): number => {
                  if (val === null || val === undefined || val === '') return 0;
                  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                  return isNaN(num) || !isFinite(num) ? 0 : num;
                };

                const formatDateShort = (dateStr: string): string => {
                  if (!dateStr) return '';
                  const date = new Date(dateStr);
                  if (isNaN(date.getTime())) return '';
                  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const day = String(date.getDate()).padStart(2, '0');
                  const month = months[date.getMonth()];
                  const year = String(date.getFullYear()).slice(-2);
                  return `${day}-${month}-${year}`;
                };

                // Lead rows (open leads with estimated revenue)
                const leadRows = dashboardLeads.filter(l => l.leadStatus !== 'Converted' && safeNumber(l.estimatedRevenue) > 0).map(lead => {
                  const estRev = safeNumber(lead.estimatedRevenue);
                  const prob = lead.leadStatus === 'Qualified' ? 30 : 15;
                  const now = new Date();
                  const timelineMonths = lead.timeline || 6;
                  const estClose = new Date(now.getFullYear(), now.getMonth() + timelineMonths, now.getDate());
                  return {
                    'Type': 'Lead',
                    'Name': lead.companyName || '',
                    'Close Date': formatDateShort(estClose.toISOString()),
                    'Quarter': `Q${Math.ceil((estClose.getMonth() + 1) / 3)} ${estClose.getFullYear()}`,
                    'Value': estRev,
                    'Probability': prob,
                    'Weighted Value': Math.round(estRev * (prob / 100)),
                    'Stage': lead.leadStatus || '',
                    'Owner': lead.leadOwner || lead.salesRepresentative || ''
                  };
                });

                // Opportunity rows
                const oppRows = filteredOpportunities.filter(o =>
                  o.estimatedCloseDate &&
                  o.salesStage !== 'Closed-Won' &&
                  o.salesStage !== 'Closed-Lost' &&
                  getEffectiveSalesStatus(o) !== 'Inactive'
                ).map(opp => {
                  const closeDate = new Date(opp.estimatedCloseDate);
                  const probability = safeNumber(opp.probability) || 50;
                  const tcv = safeNumber(opp.totalContractValue);
                  return {
                    'Type': 'Opportunity',
                    'Name': opp.opportunityName || '',
                    'Close Date': formatDateShort(opp.estimatedCloseDate),
                    'Quarter': `Q${Math.ceil((closeDate.getMonth() + 1) / 3)} ${closeDate.getFullYear()}`,
                    'Value': tcv,
                    'Probability': probability,
                    'Weighted Value': Math.round(tcv * (probability / 100)),
                    'Stage': opp.salesStage || '',
                    'Owner': opp.accountOwner || ''
                  };
                });

                return [...leadRows, ...oppRows];
              },
              columns: ['Type', 'Name', 'Close Date', 'Quarter', 'Value', 'Probability', 'Weighted Value', 'Owner'],
              valueColumns: ['Value', 'Weighted Value']
            },
            {
              id: 'performance',
              name: 'Sales Performance',
              description: 'Individual and team performance metrics with leads & win/loss analysis',
              icon: Users,
              color: '#D9A441',
              bgColor: '#EBCB7E',
              getData: () => {
                const safeNumber = (val: any): number => {
                  if (val === null || val === undefined || val === '') return 0;
                  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                  return isNaN(num) || !isFinite(num) ? 0 : num;
                };

                const ownerStats: Record<string, any> = {};
                // Aggregate leads per owner
                dashboardLeads.forEach(lead => {
                  const owner = lead.leadOwner || lead.salesRepresentative || 'Unassigned';
                  if (!ownerStats[owner]) {
                    ownerStats[owner] = { owner, totalLeads: 0, openLeads: 0, convertedLeads: 0, leadValue: 0, totalOpps: 0, totalValue: 0, wonCount: 0, wonValue: 0, lostCount: 0, activeCount: 0, activeValue: 0 };
                  }
                  ownerStats[owner].totalLeads++;
                  ownerStats[owner].leadValue += safeNumber(lead.estimatedRevenue);
                  if (lead.leadStatus === 'Converted') { ownerStats[owner].convertedLeads++; }
                  else if (lead.leadStatus === 'Open' || !lead.leadStatus) { ownerStats[owner].openLeads++; }
                });
                // Aggregate opportunities per owner
                filteredOpportunities.forEach(opp => {
                  const owner = opp.accountOwner || 'Unassigned';
                  if (!ownerStats[owner]) {
                    ownerStats[owner] = { owner, totalLeads: 0, openLeads: 0, convertedLeads: 0, leadValue: 0, totalOpps: 0, totalValue: 0, wonCount: 0, wonValue: 0, lostCount: 0, activeCount: 0, activeValue: 0 };
                  }
                  ownerStats[owner].totalOpps++;
                  ownerStats[owner].totalValue += safeNumber(opp.totalContractValue);
                  if (opp.salesStage === 'Closed-Won') { ownerStats[owner].wonCount++; ownerStats[owner].wonValue += safeNumber(opp.totalContractValue); }
                  else if (opp.salesStage === 'Closed-Lost') { ownerStats[owner].lostCount++; }
                  else { ownerStats[owner].activeCount++; ownerStats[owner].activeValue += safeNumber(opp.totalContractValue); }
                });
                return Object.values(ownerStats).map((s: any) => ({
                  'Sales Rep': s.owner,
                  'Leads': s.totalLeads,
                  'Open Leads': s.openLeads,
                  'Converted': s.convertedLeads,
                  'Lead Value': s.leadValue,
                  'Total Opps': s.totalOpps,
                  'Pipeline Value': s.totalValue,
                  'Won': s.wonCount,
                  'Won Value': s.wonValue,
                  'Lost': s.lostCount,
                  'Active': s.activeCount,
                  'Win Rate': s.totalOpps > 0 ? `${((s.wonCount / s.totalOpps) * 100).toFixed(1)}%` : '0%'
                }));
              },
              columns: ['Sales Rep', 'Leads', 'Open Leads', 'Converted', 'Lead Value', 'Total Opps', 'Pipeline Value', 'Won', 'Won Value', 'Lost', 'Win Rate'],
              valueColumns: ['Lead Value', 'Pipeline Value', 'Won Value']
            },
            {
              id: 'geographic',
              name: 'Geographic Analysis',
              description: 'Pipeline breakdown by region with leads & opportunities',
              icon: MapPin,
              color: '#D8A39D',
              bgColor: '#E9C1BE',
              getData: () => {
                const safeNumber = (val: any): number => {
                  if (val === null || val === undefined || val === '') return 0;
                  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                  return isNaN(num) || !isFinite(num) ? 0 : num;
                };

                const geoStats: Record<string, any> = {};
                // Aggregate leads per geography
                dashboardLeads.forEach(lead => {
                  const geo = lead.geography || 'Unknown';
                  if (!geoStats[geo]) { geoStats[geo] = { geography: geo, totalLeads: 0, openLeads: 0, convertedLeads: 0, leadValue: 0, totalOpps: 0, totalValue: 0, wonCount: 0, wonValue: 0 }; }
                  geoStats[geo].totalLeads++;
                  geoStats[geo].leadValue += safeNumber(lead.estimatedRevenue);
                  if (lead.leadStatus === 'Converted') { geoStats[geo].convertedLeads++; }
                  else { geoStats[geo].openLeads++; }
                });
                // Aggregate opportunities per geography
                filteredOpportunities.forEach(opp => {
                  const geo = opp.geography || 'Unknown';
                  if (!geoStats[geo]) { geoStats[geo] = { geography: geo, totalLeads: 0, openLeads: 0, convertedLeads: 0, leadValue: 0, totalOpps: 0, totalValue: 0, wonCount: 0, wonValue: 0 }; }
                  geoStats[geo].totalOpps++;
                  geoStats[geo].totalValue += safeNumber(opp.totalContractValue);
                  if (opp.salesStage === 'Closed-Won') { geoStats[geo].wonCount++; geoStats[geo].wonValue += safeNumber(opp.totalContractValue); }
                });
                return Object.values(geoStats).map((g: any) => ({
                  'Geography': g.geography,
                  'Leads': g.totalLeads,
                  'Open Leads': g.openLeads,
                  'Converted': g.convertedLeads,
                  'Lead Value': g.leadValue,
                  'Total Opps': g.totalOpps,
                  'Pipeline Value': g.totalValue,
                  'Won': g.wonCount,
                  'Won Value': g.wonValue,
                  'Win Rate': g.totalOpps > 0 ? `${((g.wonCount / g.totalOpps) * 100).toFixed(1)}%` : '0%'
                }));
              },
              columns: ['Geography', 'Leads', 'Open Leads', 'Converted', 'Lead Value', 'Total Opps', 'Pipeline Value', 'Won', 'Won Value', 'Win Rate'],
              valueColumns: ['Lead Value', 'Pipeline Value', 'Won Value']
            },
            {
              id: 'serviceline',
              name: 'Service Line Analysis',
              description: 'Pipeline breakdown by service line with leads & opportunities',
              icon: Briefcase,
              color: '#481F89',
              bgColor: '#9484BF',
              getData: () => {
                const safeNumber = (val: any): number => {
                  if (val === null || val === undefined || val === '') return 0;
                  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                  return isNaN(num) || !isFinite(num) ? 0 : num;
                };

                const slStats: Record<string, any> = {};
                // Aggregate leads per service line
                dashboardLeads.forEach(lead => {
                  const sl = lead.serviceLine || 'Unknown';
                  if (!slStats[sl]) { slStats[sl] = { serviceLine: sl, totalLeads: 0, openLeads: 0, convertedLeads: 0, leadValue: 0, totalOpps: 0, totalValue: 0, wonCount: 0, wonValue: 0 }; }
                  slStats[sl].totalLeads++;
                  slStats[sl].leadValue += safeNumber(lead.estimatedRevenue);
                  if (lead.leadStatus === 'Converted') { slStats[sl].convertedLeads++; }
                  else { slStats[sl].openLeads++; }
                });
                // Aggregate opportunities per service line
                filteredOpportunities.forEach(opp => {
                  const sl = opp.serviceLine || 'Unknown';
                  if (!slStats[sl]) { slStats[sl] = { serviceLine: sl, totalLeads: 0, openLeads: 0, convertedLeads: 0, leadValue: 0, totalOpps: 0, totalValue: 0, wonCount: 0, wonValue: 0 }; }
                  slStats[sl].totalOpps++;
                  slStats[sl].totalValue += safeNumber(opp.totalContractValue);
                  if (opp.salesStage === 'Closed-Won') { slStats[sl].wonCount++; slStats[sl].wonValue += safeNumber(opp.totalContractValue); }
                });
                return Object.values(slStats).map((s: any) => ({
                  'Service Line': s.serviceLine,
                  'Leads': s.totalLeads,
                  'Open Leads': s.openLeads,
                  'Converted': s.convertedLeads,
                  'Lead Value': s.leadValue,
                  'Total Opps': s.totalOpps,
                  'Pipeline Value': s.totalValue,
                  'Won': s.wonCount,
                  'Won Value': s.wonValue,
                  'Win Rate': s.totalOpps > 0 ? `${((s.wonCount / s.totalOpps) * 100).toFixed(1)}%` : '0%'
                }));
              },
              columns: ['Service Line', 'Leads', 'Open Leads', 'Converted', 'Lead Value', 'Total Opps', 'Pipeline Value', 'Won', 'Won Value', 'Win Rate'],
              valueColumns: ['Lead Value', 'Pipeline Value', 'Won Value']
            },
            {
              id: 'aging',
              name: 'Pipeline Aging',
              description: 'Leads & opportunities by age with duration analysis',
              icon: Clock,
              color: '#00BBA2',
              bgColor: 'rgba(0,187,162,0.08)',
              getData: () => {
                const safeNumber = (val: any): number => {
                  if (val === null || val === undefined || val === '') return 0;
                  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                  return isNaN(num) || !isFinite(num) ? 0 : num;
                };

                const today = new Date();

                // Lead rows (open leads only)
                const leadRows = dashboardLeads.filter(l => l.leadStatus !== 'Converted').map(lead => {
                  const captureDate = lead.dateCaptured ? new Date(lead.dateCaptured) : (lead.createdDate ? new Date(lead.createdDate) : null);
                  const daysOpen = captureDate ? Math.floor((today.getTime() - captureDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
                  let ageBucket = '0-30 days';
                  if (daysOpen > 90) ageBucket = '90+ days';
                  else if (daysOpen > 60) ageBucket = '61-90 days';
                  else if (daysOpen > 30) ageBucket = '31-60 days';
                  return {
                    'Type': 'Lead',
                    'Name': lead.companyName || '',
                    'Stage': lead.leadStatus || '',
                    'Days Open': daysOpen,
                    'Days to Close': 'N/A',
                    'Age Bucket': ageBucket,
                    'Value': safeNumber(lead.estimatedRevenue),
                    'Owner': lead.leadOwner || lead.salesRepresentative || '',
                    'Status': daysOpen > 60 ? 'AGING' : 'Active'
                  };
                });

                // Opportunity rows (excluding Won & Lost)
                const oppRows = filteredOpportunities.filter(o => o.salesStage !== 'Closed-Won' && o.salesStage !== 'Closed-Lost').map(opp => {
                  const initDate = opp.initiationDate ? new Date(opp.initiationDate) : null;
                  const closeDate = opp.estimatedCloseDate ? new Date(opp.estimatedCloseDate) : null;
                  const daysInPipeline = initDate ? Math.floor((today.getTime() - initDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
                  const daysToClose = closeDate ? Math.floor((closeDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : null;
                  let ageBucket = '0-30 days';
                  if (daysInPipeline > 90) ageBucket = '90+ days';
                  else if (daysInPipeline > 60) ageBucket = '61-90 days';
                  else if (daysInPipeline > 30) ageBucket = '31-60 days';
                  return {
                    'Type': 'Opportunity',
                    'Name': opp.opportunityName || '',
                    'Stage': opp.salesStage || '',
                    'Days Open': daysInPipeline,
                    'Days to Close': daysToClose !== null ? daysToClose : 'N/A',
                    'Age Bucket': ageBucket,
                    'Value': safeNumber(opp.totalContractValue),
                    'Owner': opp.accountOwner || '',
                    'Status': daysToClose !== null && daysToClose < 0 ? 'OVERDUE' : 'On Track'
                  };
                });

                return [...leadRows, ...oppRows];
              },
              columns: ['Type', 'Name', 'Stage', 'Days Open', 'Days to Close', 'Age Bucket', 'Value', 'Status'],
              valueColumns: ['Value']
            },
            {
              id: 'winloss',
              name: 'Win/Loss Analysis',
              description: 'Analysis of won/lost opportunities and lead conversions',
              icon: CheckCircle,
              color: '#6F8F6A',
              bgColor: '#C7D8C1',
              getData: () => {
                const safeNumber = (val: any): number => {
                  if (val === null || val === undefined || val === '') return 0;
                  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                  return isNaN(num) || !isFinite(num) ? 0 : num;
                };

                // Converted leads
                const leadRows = dashboardLeads.filter(l => l.leadStatus === 'Converted').map(lead => {
                  let daysToConvert: number | string = 'N/A';
                  if (lead.dateCaptured && lead.convertedAt) {
                    const captured = new Date(lead.dateCaptured);
                    const converted = new Date(lead.convertedAt);
                    daysToConvert = Math.floor((converted.getTime() - captured.getTime()) / (1000 * 60 * 60 * 24));
                  }
                  return {
                    'Type': 'Lead',
                    'Name': lead.companyName || '',
                    'Outcome': 'Converted',
                    'Value': safeNumber(lead.estimatedRevenue),
                    'Geography': lead.geography || '',
                    'Service Line': lead.serviceLine || '',
                    'Owner': lead.leadOwner || lead.salesRepresentative || '',
                    'Cycle (Days)': daysToConvert
                  };
                });

                // Won/Lost opportunities
                const oppRows = filteredOpportunities.filter(o => o.salesStage === 'Closed-Won' || o.salesStage === 'Closed-Lost').map(opp => {
                  let salesCycle: number | string = 'N/A';
                  if (opp.initiationDate && opp.estimatedCloseDate) {
                    const init = new Date(opp.initiationDate);
                    const close = new Date(opp.estimatedCloseDate);
                    salesCycle = Math.floor((close.getTime() - init.getTime()) / (1000 * 60 * 60 * 24));
                  }
                  return {
                    'Type': 'Opportunity',
                    'Name': opp.opportunityName || '',
                    'Outcome': opp.salesStage,
                    'Value': safeNumber(opp.totalContractValue),
                    'Geography': opp.geography || '',
                    'Service Line': opp.serviceLine || '',
                    'Owner': opp.accountOwner || '',
                    'Cycle (Days)': salesCycle
                  };
                });

                return [...leadRows, ...oppRows];
              },
              columns: ['Type', 'Name', 'Outcome', 'Value', 'Geography', 'Service Line', 'Owner', 'Cycle (Days)'],
              valueColumns: ['Value']
            },
            {
              id: 'complete',
              name: 'Complete Data Export',
              description: 'Export all CRM data including leads, opportunities, and customers',
              icon: FileSpreadsheet,
              color: '#00BBA2',
              bgColor: 'rgba(0,187,162,0.12)',
              getData: () => {
                // Format date as dd-mm-yyyy
                const formatDate = (dateStr: string | null | undefined): string => {
                  if (!dateStr) return '';
                  const date = new Date(dateStr);
                  if (isNaN(date.getTime())) return '';
                  const day = String(date.getDate()).padStart(2, '0');
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const year = date.getFullYear();
                  return `${day}-${month}-${year}`;
                };
                return opportunities.filter(opp =>
                  !(opp.opportunityName || '').toLowerCase().includes('test') &&
                  !(opp.associatedAccount || '').toLowerCase().includes('test')
                ).map(opp => ({
                  'SN': opp.sn || '',
                  'Opportunity ID': opp.opportunityId || '',
                  'Opportunity Name': opp.opportunityName || '',
                  'Opportunity Type': opp.opportunityType || '',
                  'Geography': opp.geography || '',
                  'Account Owner': opp.accountOwner || '',
                  'Associated Account': opp.associatedAccount || '',
                  'Primary Contact': opp.primaryContact || '',
                  'Primary Contact Email': opp.primaryContactEmail || '',
                  'Phone Number': opp.phoneNumber || '',
                  'Email': opp.email || '',
                  'Influencers': opp.influencers || '',
                  'Decision Making Team': opp.decisionMakingTeam || '',
                  'Industry Vertical': opp.industryVertical || '',
                  'Industry Sub-Vertical': opp.industrySubVertical || '',
                  'Service Line': opp.serviceLine || '',
                  'Service Offering': opp.serviceOffering || '',
                  'Entity': opp.entity || '',
                  'Annual Contract Value': opp.annualContractValue || 0,
                  'Total Contract Value': opp.totalContractValue || 0,
                  'Priority Level': opp.priorityLevel || '',
                  'Initiation Date': formatDate(opp.initiationDate),
                  'Aging (Days)': opp.aging || 0,
                  'Estimated Close Date': formatDate(opp.estimatedCloseDate),
                  'Actual Close Date': formatDate(opp.actualCloseDate),
                  'Sales Status': opp.salesStatus || '',
                  'Sales Stage': opp.salesStage || '',
                  'Solution Scope': opp.solutionScope || '',
                  'Engagement History': opp.engagementHistory || '',
                  'Lead Supporter': opp.leadSupporter || '',
                  'Executive Sponsor': opp.executiveSponsor || '',
                  'Probability To Close': opp.probabilityToClose || '',
                  'Probability Percent': opp.probabilityPercent || 0,
                  'Forecast Multiplier': opp.forecastMultiplier || 0,
                  'Actual Value': opp.actualValue || 0,
                  'Probability Value': opp.probabilityValue || 0,
                  'Budget': opp.budget || 0,
                  'Estimated Revenue': opp.estimatedRevenue || 0,
                  'Actual Revenue': opp.actualRevenue || 0,
                  'Timeline (Months)': opp.timeline || 0,
                  'Pain Points': opp.painPoints || '',
                  'Competitors': opp.competitors || '',
                  'Notes': opp.notes || '',
                  'Comments': opp.comments || '',
                  'Assessment Score': opp.assessmentScore || 0,
                  'Last Edited Date': formatDate(opp.lastEditedDate)
                }));
              },
              columns: ['Opportunity ID', 'Opportunity Name', 'Associated Account', 'Sales Stage', 'Annual Contract Value', 'Total Contract Value', 'Geography', 'Account Owner', 'Engagement History', 'Comments'],
              valueColumns: ['Annual Contract Value', 'Total Contract Value', 'Actual Value', 'Probability Value', 'Budget', 'Estimated Revenue']
            },
            {
              id: 'leadreport',
              name: 'Lead Report',
              description: 'All leads with contact, classification and ownership details',
              icon: User,
              color: '#D9845C',
              bgColor: 'rgba(217,132,92,0.14)',
              getData: () => {
                const formatDate = (dateStr: string | null | undefined): string => {
                  if (!dateStr) return '';
                  const date = new Date(dateStr);
                  if (isNaN(date.getTime())) return '';
                  return `${String(date.getDate()).padStart(2,'0')}-${String(date.getMonth()+1).padStart(2,'0')}-${date.getFullYear()}`;
                };
                return dashboardLeads.map(lead => ({
                  'Lead Creation Date': formatDate(lead.dateCaptured || lead.createdDate),
                  'Lead ID': lead.leadId || '',
                  'Lead Status': lead.leadStatus || '',
                  'Lead Owner': lead.leadOwner || lead.salesRepresentative || '',
                  'Service Line': lead.serviceLine || '',
                  'Company Name': lead.companyName || '',
                  'Industry': lead.industry || '',
                  'Lead Source': lead.leadSource || '',
                  'Lead Rating': lead.leadRating || '',
                  'City': lead.city || '',
                  'Contact Person': lead.contactPerson || '',
                  'Designation': lead.designation || '',
                  'Mobile': lead.phone || '',
                  'Email': lead.email || '',
                  'Geography': lead.geography || '',
                  'Lead Generator': lead.leadGenerator || '',
                  'Lead Supporter': lead.leadSupporter || '',
                  'Business Type': lead.businessType || '',
                }));
              },
              columns: ['Lead Creation Date', 'Lead ID', 'Lead Status', 'Lead Owner', 'Service Line', 'Company Name', 'Industry', 'Lead Source', 'Lead Rating', 'City', 'Contact Person', 'Designation', 'Mobile', 'Email', 'Geography', 'Lead Generator', 'Lead Supporter', 'Business Type'],
              valueColumns: []
            },
            {
              id: 'oppreport',
              name: 'Opp Report',
              description: 'All opportunities with linked lead, contact, and pipeline details',
              icon: Target,
              color: '#6A7CA5',
              bgColor: 'rgba(106,124,165,0.14)',
              getData: () => {
                const formatDate = (dateStr: string | null | undefined): string => {
                  if (!dateStr) return '';
                  const date = new Date(dateStr);
                  if (isNaN(date.getTime())) return '';
                  return `${String(date.getDate()).padStart(2,'0')}-${String(date.getMonth()+1).padStart(2,'0')}-${date.getFullYear()}`;
                };
                const safeNumber = (val: any): number => {
                  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                  return isNaN(num) || !isFinite(num) ? 0 : num;
                };
                // Build lead lookup by company name for linked Lead ID
                const leadByCompany: Record<string, any> = {};
                dashboardLeads.forEach(l => {
                  const key = (l.companyName || '').toLowerCase().trim();
                  if (key && !leadByCompany[key]) leadByCompany[key] = l;
                });
                return filteredOpportunities.map(opp => {
                  const linkedLead = opp.associatedAccount
                    ? leadByCompany[(opp.associatedAccount || '').toLowerCase().trim()] || null
                    : null;
                  const amt = safeNumber(opp.totalContractValue || opp.annualContractValue || 0);
                  return {
                    'Lead ID': linkedLead ? (linkedLead.leadId || '') : '',
                    'Opp Creation Date': formatDate(opp.initiationDate || opp.createdAt),
                    'Opp ID': opp.opportunityId || '',
                    'Opp Status': opp.salesStage || '',
                    'Lead Owner': opp.accountOwner || '',
                    'Service Line': opp.serviceLine || '',
                    'Company Name': opp.associatedAccount || '',
                    'Industry': opp.industryVertical || (linkedLead ? linkedLead.industry : '') || '',
                    'Amount': amt > 0 ? amt : '',
                    'Lead Source': linkedLead ? (linkedLead.leadSource || '') : '',
                    'City': opp.city || (linkedLead ? linkedLead.city : '') || '',
                    'Contact Person': opp.primaryContact || (linkedLead ? linkedLead.contactPerson : '') || '',
                    'Designation': linkedLead ? (linkedLead.designation || '') : '',
                    'Mobile': opp.phoneNumber || (linkedLead ? linkedLead.phone : '') || '',
                    'Email': opp.primaryContactEmail || opp.email || (linkedLead ? linkedLead.email : '') || '',
                    'Geography': opp.geography || '',
                    'Lead Generator': opp.leadGenerator || (linkedLead ? linkedLead.leadGenerator : '') || '',
                    'Lead Supporter': opp.leadSupporter || (linkedLead ? linkedLead.leadSupporter : '') || '',
                    'Business Type': opp.opportunityType || opp.opportunityCategory || (linkedLead ? linkedLead.businessType : '') || '',
                  };
                });
              },
              columns: ['Lead ID', 'Opp Creation Date', 'Opp ID', 'Opp Status', 'Lead Owner', 'Service Line', 'Company Name', 'Industry', 'Amount', 'Lead Source', 'City', 'Contact Person', 'Designation', 'Mobile', 'Email', 'Geography', 'Lead Generator', 'Lead Supporter', 'Business Type'],
              valueColumns: ['Amount']
            }
          ] as any[]).filter(r => r.id === 'leadreport' || r.id === 'oppreport');

          // Get current report data
          const currentReport = reportTypes.find(r => r.id === activeReport);
          const reportData = currentReport ? currentReport.getData() : [];
          const filteredReportData = filterBySearch(reportData, reportSearch);
          const sortedReportData = sortData(filteredReportData, reportSortField, reportSortDir);

          // Calculate totals for value columns
          const calculateTotals = (data, valueColumns) => {
            const totals = {};
            valueColumns.forEach(col => {
              totals[col] = data.reduce((sum, row) => sum + (typeof row[col] === 'number' ? row[col] : 0), 0);
            });
            return totals;
          };

          // Export current view to Excel
          const exportToExcel = (data, filename) => {
            const ws = window.XLSX.utils.json_to_sheet(data);
            const wb = window.XLSX.utils.book_new();
            window.XLSX.utils.book_append_sheet(wb, ws, 'Report');
            window.XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
          };

          // Format currency
          const formatCurrency = (value) => {
            if (typeof value !== 'number') return value;
            return '₹' + (value >= 1000000 ? (value / 1000000).toFixed(1) + 'M' : (value / 1000).toFixed(0) + 'K');
          };

          // Set default report if none selected
          const effectiveReport = activeReport || 'leadreport';
          const selectedReport = reportTypes.find(r => r.id === effectiveReport);
          const selectedReportData = selectedReport ? selectedReport.getData() : [];
          const filteredSelectedData = filterBySearch(selectedReportData, reportSearch);
          const sortedSelectedData = sortData(filteredSelectedData, reportSortField, reportSortDir);

          return (
          <div className="flex gap-4 items-start">
            {/* Left Sidebar - Vertical Report Menu */}
            <div className="w-64 flex-shrink-0">
              <Card className="overflow-hidden">
                <div className="p-3 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-sm">Reports</h3>
                </div>
                <div>
                  {reportTypes.map(report => {
                    const ReportIcon = report.icon;
                    const isActive = effectiveReport === report.id;
                    return (
                      <button
                        key={report.id}
                        onClick={() => { setActiveReport(report.id); setReportSortField(''); setReportSearch(''); setReportPage(1); }}
                        className={`w-full px-3 py-2.5 flex items-center gap-3 text-left transition-colors border-l-3 ${
                          isActive
                            ? 'bg-gray-50 border-l-pierian'
                            : 'border-l-transparent hover:bg-gray-50'
                        }`}
                        style={{ borderLeftWidth: '3px', borderLeftColor: isActive ? report.color : 'transparent' }}
                      >
                        <div
                          className="p-1.5 rounded"
                          style={{ backgroundColor: isActive ? report.bgColor : '#f3f4f6' }}
                        >
                          <ReportIcon
                            size={16}
                            style={{ color: isActive ? report.color : '#6b7280' }}
                          />
                        </div>
                        <span className={`text-sm ${isActive ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                          {report.name}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Summary Stats at bottom */}
                <div className="border-t border-gray-200 p-3">
                  <div className="text-center">
                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(0,187,162,0.08)' }}>
                      <p className="text-sm font-bold" style={{ color: '#00BBA2' }}>
                        {opportunities.filter(o =>
                          o.salesStage !== 'Closed-Won' &&
                          o.salesStage !== 'Closed-Lost' &&
                          getEffectiveSalesStatus(o) !== 'Inactive'
                        ).length}
                      </p>
                      <p className="text-xs text-gray-500">Active Opps</p>
                    </div>
                    <p className="text-[8px] text-gray-400 mt-1">Excl. Won, Lost, Inactive</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Main Content - Report Data */}
            <div className="flex-1 flex flex-col min-w-0">
              {selectedReport && (
                <>
                  {/* Report Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: selectedReport.bgColor }}>
                        {(() => { const Icon = selectedReport.icon; return <Icon size={20} style={{ color: selectedReport.color }} />; })()}
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">{selectedReport.name}</h3>
                        <p className="text-xs text-gray-500">{selectedReport.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                        <input
                          type="text"
                          placeholder="Search..."
                          value={reportSearch}
                          onChange={(e) => setReportSearch(e.target.value)}
                          className="w-48 pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian"
                        />
                      </div>
                      {/* Export Button */}
                      <button
                        onClick={() => exportToExcel(sortedSelectedData, selectedReport.name.replace(/\s+/g, '_'))}
                        className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1"
                        style={{ backgroundColor: '#00BBA2', color: 'white' }}
                      >
                        <Download size={14} /> Export Excel
                      </button>
                    </div>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <Card className="p-2.5">
                      <div className="text-center">
                        <p className="text-lg font-bold" style={{ color: selectedReport.color }}>{sortedSelectedData.length}</p>
                        <p className="text-xs text-gray-500">Records</p>
                      </div>
                    </Card>
                    {selectedReport.valueColumns.slice(0, 3).map(col => {
                      const totals = calculateTotals(sortedSelectedData, selectedReport.valueColumns);
                      return (
                        <Card key={col} className="p-2.5">
                          <div className="text-center">
                            <p className="text-lg font-bold" style={{ color: selectedReport.color }}>
                              {formatCurrency(totals[col])}
                            </p>
                            <p className="text-xs text-gray-500">{col}</p>
                          </div>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Data Table */}
                  {(() => {
                    const totalRecords = sortedSelectedData.length;
                    const totalPages = Math.ceil(totalRecords / reportPageSize);
                    const startIndex = (reportPage - 1) * reportPageSize;
                    const endIndex = Math.min(startIndex + reportPageSize, totalRecords);
                    const paginatedData = sortedSelectedData.slice(startIndex, endIndex);

                    return (
                      <Card className="flex-1 overflow-hidden flex flex-col">
                        <div className="flex-1 overflow-auto">
                          <table className="w-full text-sm table-auto">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                {selectedReport.columns.map(col => (
                                  <th
                                    key={col}
                                    onClick={() => handleReportSort(col)}
                                    className="px-3 py-2.5 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 text-xs whitespace-nowrap"
                                  >
                                    <div className="flex items-center gap-1">
                                      {col}
                                      {reportSortField === col && (
                                        <span className="text-pierian">{reportSortDir === 'asc' ? '↑' : '↓'}</span>
                                      )}
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {paginatedData.map((row, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  {selectedReport.columns.map((col, colIdx) => (
                                    <td key={col} className={`px-3 py-2 text-gray-600 text-xs ${colIdx === 0 ? '' : 'whitespace-nowrap'}`}>
                                      {selectedReport.valueColumns.includes(col) && typeof row[col] === 'number'
                                        ? formatCurrency(row[col])
                                        : row[col]}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                            {/* Totals Row */}
                            <tfoot className="bg-gray-100 font-semibold sticky bottom-0">
                              <tr>
                                {selectedReport.columns.map((col, idx) => {
                                  const totals = calculateTotals(sortedSelectedData, selectedReport.valueColumns);
                                  return (
                                    <td key={col} className="px-4 py-2 text-gray-900 text-xs">
                                      {idx === 0 ? `Total (${totalRecords})` :
                                       selectedReport.valueColumns.includes(col) ? formatCurrency(totals[col]) : ''}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {/* Pagination Controls */}
                        <div className="p-3 bg-gray-50 border-t flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">Rows per page:</span>
                            <select
                              value={reportPageSize}
                              onChange={(e) => { setReportPageSize(Number(e.target.value)); setReportPage(1); }}
                              className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-pierian"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">
                              {startIndex + 1}-{endIndex} of {totalRecords}
                            </span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setReportPage(1)}
                                disabled={reportPage === 1}
                                className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="First page"
                              >
                                <ChevronsLeft size={16} className="text-gray-600" />
                              </button>
                              <button
                                onClick={() => setReportPage(p => Math.max(1, p - 1))}
                                disabled={reportPage === 1}
                                className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Previous page"
                              >
                                <ChevronLeft size={16} className="text-gray-600" />
                              </button>
                              <span className="px-2 py-1 text-xs text-gray-700">
                                Page {reportPage} of {totalPages}
                              </span>
                              <button
                                onClick={() => setReportPage(p => Math.min(totalPages, p + 1))}
                                disabled={reportPage >= totalPages}
                                className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Next page"
                              >
                                <ChevronRight size={16} className="text-gray-600" />
                              </button>
                              <button
                                onClick={() => setReportPage(totalPages)}
                                disabled={reportPage >= totalPages}
                                className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Last page"
                              >
                                <ChevronsRight size={16} className="text-gray-600" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })()}

                  {/* Filter Notice */}
                  {hasActiveFilters && (
                    <div className="mt-2 p-2 rounded-lg text-xs" style={{ backgroundColor: 'rgba(0,187,162,0.08)', border: '1px solid rgba(0,187,162,0.25)' }}>
                      <div className="flex items-center gap-2">
                        <Filter size={14} className="text-pierian" />
                        <span className="text-gray-700">
                          <strong>Note:</strong> Reports filtered ({filteredOpportunities.length} of {opportunities.length} opportunities)
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          );
        })()}

        {/* ========== READOUT TAB ========== */}
        {dashboardSubTab === 'weeklyreadout' && (() => {
          // ---- Period computation ----
          const now = new Date();
          const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

          // ---- Period computation from date range ----
          const periodStart = new Date(readoutFromDate + 'T00:00:00');
          const periodEnd = new Date(readoutToDate + 'T23:59:59.999');
          const periodSpanDays = Math.max(1, Math.round((periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)));
          const prevEnd = new Date(periodStart);
          prevEnd.setDate(periodStart.getDate() - 1);
          prevEnd.setHours(23, 59, 59, 999);
          const prevStart = new Date(prevEnd);
          prevStart.setDate(prevEnd.getDate() - periodSpanDays + 1);
          prevStart.setHours(0, 0, 0, 0);
          const periodRangeLabel = `${periodStart.getDate()} ${monthNames[periodStart.getMonth()]} – ${periodEnd.getDate()} ${monthNames[periodEnd.getMonth()]} ${periodEnd.getFullYear()}`;
          const periodBounds = { start: periodStart, end: periodEnd, prevStart, prevEnd, label: periodRangeLabel, periodName: periodRangeLabel };

          const fmtCurr = (v: number) => v >= 1000000 ? `₹${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${Math.round(v)}`;
          const fmtDateRange = (s: Date, e: Date) => `${s.getDate()} ${monthNames[s.getMonth()]} – ${e.getDate()} ${monthNames[e.getMonth()]} ${e.getFullYear()}`;
          const periodLabel = 'Selected Period';
          const prevPeriodLabel = 'Prior Period';

          // ---- Helper to check date in range ----
          const inRange = (d: Date | null, start: Date, end: Date) => d ? d >= start && d <= end : false;
          const toDate = (v: string | null | undefined) => v ? new Date(v) : null;

          // ---- Metrics for selected period ----
          const weekNewLeads = dashboardLeads.filter(l => inRange(toDate(l.dateCaptured), periodBounds.start, periodBounds.end));
          const weekConvertedLeads = dashboardLeads.filter(l => l.convertedAt && inRange(toDate(l.convertedAt), periodBounds.start, periodBounds.end));
          const weekNewOpps = filteredOpportunities.filter(o => {
            if (!o.initiationDate || !inRange(toDate(o.initiationDate), periodBounds.start, periodBounds.end)) return false;
            const stage = (o.salesStage || '').toLowerCase();
            return stage !== 'assessment' && !stage.includes('lead');
          });
          const weekWonOpps = filteredOpportunities.filter(o => o.salesStage === 'Closed-Won' && o.actualCloseDate && inRange(toDate(o.actualCloseDate), periodBounds.start, periodBounds.end));
          const weekLostOpps = filteredOpportunities.filter(o => o.salesStage === 'Closed-Lost' && o.actualCloseDate && inRange(toDate(o.actualCloseDate), periodBounds.start, periodBounds.end));
          const weekNewOppsValue = weekNewOpps.reduce((s, o) => s + (o.annualContractValue || 0), 0);
          const weekWonValue = weekWonOpps.reduce((s, o) => s + (o.totalContractValue || o.annualContractValue || 0), 0);

          // Previous period for comparison
          const prevNewLeads = dashboardLeads.filter(l => inRange(toDate(l.dateCaptured), periodBounds.prevStart, periodBounds.prevEnd));
          const prevConvertedLeads = dashboardLeads.filter(l => l.convertedAt && inRange(toDate(l.convertedAt), periodBounds.prevStart, periodBounds.prevEnd));
          const prevNewOpps = filteredOpportunities.filter(o => {
            if (!o.initiationDate || !inRange(toDate(o.initiationDate), periodBounds.prevStart, periodBounds.prevEnd)) return false;
            const stage = (o.salesStage || '').toLowerCase();
            return stage !== 'assessment' && !stage.includes('lead');
          });

          // Biggest new deal
          const biggestNewDeal = weekNewOpps.length > 0 ? weekNewOpps.reduce((mx, o) => (o.annualContractValue || 0) > (mx.annualContractValue || 0) ? o : mx, weekNewOpps[0]) : null;

          // ---- Stage movements this week (parse engagement history) ----
          const stageMovements: { opp: any; from: string; to: string }[] = [];
          filteredOpportunities.forEach(opp => {
            if (!opp.engagementHistory) return;
            opp.engagementHistory.split('\n').forEach((entry: string) => {
              if (!entry.includes('Stage Changed') && !entry.includes('Stage Regressed')) return;
              // Parse date from entry
              let entryDate: Date | null = null;
              const ddMmmMatch = entry.match(/(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
              if (ddMmmMatch) {
                const mMap: Record<string, number> = { 'Jan':0,'Feb':1,'Mar':2,'Apr':3,'May':4,'Jun':5,'Jul':6,'Aug':7,'Sep':8,'Oct':9,'Nov':10,'Dec':11 };
                entryDate = new Date(parseInt(ddMmmMatch[3]), mMap[ddMmmMatch[2]] ?? 0, parseInt(ddMmmMatch[1]));
              }
              if (!entryDate) {
                const isoMatch = entry.match(/(\d{4})-(\d{2})-(\d{2})/);
                if (isoMatch) entryDate = new Date(isoMatch[0]);
              }
              if (entryDate && inRange(entryDate, periodBounds.start, periodBounds.end)) {
                const match = entry.match(/from\s+["']?([^"'→\]]+?)["']?\s*(?:→|to)\s*["']?([^"'\]\n]+)/i);
                if (match) stageMovements.push({ opp, from: match[1].trim(), to: match[2].trim() });
              }
            });
          });

          // Group movements
          const movementGroups: Record<string, number> = {};
          stageMovements.forEach(m => { const k = `${m.from} → ${m.to}`; movementGroups[k] = (movementGroups[k] || 0) + 1; });

          // ---- Active opps & health distribution ----
          const activeOpps = filteredOpportunities.filter(o => {
            const s = (o.salesStage || '').toLowerCase();
            return !s.includes('won') && !s.includes('lost') && s !== 'contract' && s !== 'assessment' && !s.includes('lead') && getEffectiveSalesStatus(o) !== 'Inactive';
          });
          const statusDist = [
            { name: 'Hot', value: activeOpps.filter(o => getEffectiveSalesStatus(o) === 'Hot').length, color: '#00BBA2' },
            { name: 'Warm', value: activeOpps.filter(o => getEffectiveSalesStatus(o) === 'Warm').length, color: '#D9A441' },
            { name: 'Cold', value: activeOpps.filter(o => getEffectiveSalesStatus(o) === 'Cold').length, color: '#6A7CA5' },
            { name: 'Inactive', value: activeOpps.filter(o => getEffectiveSalesStatus(o) === 'Inactive').length, color: '#9b9b9b' }
          ].filter(d => d.value > 0);

          // ---- 6-period trend data (each period = same span as selected date range) ----
          // Build oldest → newest (left-to-right on chart)
          const trendData: { week: string; newLeads: number; conversions: number; newOpps: number; isTarget: boolean }[] = [];
          const trendPeriodLabel = 'Periods';
          const trendStepDays = periodSpanDays;
          const trendStepMs = trendStepDays * 86400000;
          const trendOriginMs = periodBounds.start.getTime() - 5 * trendStepMs;
          for (let i = 0; i < 6; i++) {
            const tStart = new Date(trendOriginMs + i * trendStepMs);
            tStart.setHours(0, 0, 0, 0);
            const tEnd = new Date(tStart.getTime() + trendStepMs - 1);
            tEnd.setHours(23, 59, 59, 999);
            trendData.push({
              week: `${tStart.getDate()} ${monthNames[tStart.getMonth()]}`,
              newLeads: dashboardLeads.filter(l => inRange(toDate(l.dateCaptured), tStart, tEnd)).length,
              conversions: dashboardLeads.filter(l => l.convertedAt && inRange(toDate(l.convertedAt), tStart, tEnd)).length,
              newOpps: filteredOpportunities.filter(o => {
                if (!o.initiationDate || !inRange(toDate(o.initiationDate), tStart, tEnd)) return false;
                const st = (o.salesStage || '').toLowerCase();
                return st !== 'assessment' && !st.includes('lead');
              }).length,
              isTarget: i === 5
            });
          }

          // ---- Closure forecast: next 6 periods from period end ----
          const forecastData = [];
          const forecastOriginMs = periodBounds.end.getTime() + 86400000; // day after period end
          for (let i = 0; i < 6; i++) {
            const fStart = new Date(forecastOriginMs + i * trendStepMs);
            fStart.setHours(0, 0, 0, 0);
            const fEnd = new Date(fStart.getTime() + trendStepMs - 1);
            fEnd.setHours(23, 59, 59, 999);
            const wkOpps = filteredOpportunities.filter(o => {
              if (!o.estimatedCloseDate || o.salesStage === 'Closed-Won' || o.salesStage === 'Closed-Lost' || o.salesStage === 'Contract') return false;
              const d = new Date(o.estimatedCloseDate);
              return d >= fStart && d <= fEnd;
            });
            const expected = wkOpps.reduce((s, o) => s + (o.totalContractValue || o.annualContractValue || 0), 0);
            const weighted = wkOpps.reduce((s, o) => {
              const prob = o.probabilityOverride ? parseInt(String(o.probabilityOverride)) : calculateProbabilityByStage(o.salesStage).percent;
              return s + ((o.totalContractValue || o.annualContractValue || 0) * (prob / 100));
            }, 0);
            forecastData.push({
              week: `${fStart.getDate()} ${monthNames[fStart.getMonth()]}`,
              deals: wkOpps.length,
              expected,
              weighted,
              opps: wkOpps
            });
          }

          // Top 5 forecast deals by weighted value
          const allForecastOpps = forecastData.flatMap(f => f.opps.map(o => {
            const prob = o.probabilityOverride ? parseInt(String(o.probabilityOverride)) : calculateProbabilityByStage(o.salesStage).percent;
            return { ...o, prob, weightedVal: (o.totalContractValue || o.annualContractValue || 0) * (prob / 100) };
          }));
          const topForecastDeals = [...allForecastOpps].sort((a, b) => b.weightedVal - a.weightedVal).slice(0, 5);

          // ---- Data Quality ----
          const leadFields = [
            { name: 'Company Name', key: 'companyName' },
            { name: 'Contact Person', key: 'contactPerson' },
            { name: 'Email', key: 'email' },
            { name: 'Lead Source', key: 'leadSource' },
            { name: 'Geography', key: 'geography', altKey: 'region' },
            { name: 'Service Line', key: 'serviceLine' },
            { name: 'Est. Revenue', key: 'estimatedRevenue' },
            { name: 'Lead Owner', key: 'leadOwner', altKey: 'salesRepresentative' }
          ];
          const oppFields = [
            { name: 'Account Owner', key: 'accountOwner' },
            { name: 'Close Date', key: 'estimatedCloseDate' },
            { name: 'ACV', key: 'annualContractValue', numeric: true },
            { name: 'TCV', key: 'totalContractValue', numeric: true },
            { name: 'Geography', key: 'geography' },
            { name: 'Service Line', key: 'serviceLine' },
            { name: 'Probability', key: 'probability', altKey: 'probabilityOverride' },
            { name: 'Engagement Hist.', key: 'engagementHistory' }
          ];
          const calcFieldPct = (items: any[], field: { key: string; altKey?: string; numeric?: boolean }) => {
            if (items.length === 0) return 100;
            const filled = items.filter(i => {
              const v = i[field.key];
              // For numeric fields (ACV/TCV): treat 0, "0", null, undefined as missing
              if (field.numeric) return v !== null && v !== undefined && Number(v) > 0;
              if (v !== null && v !== undefined && v !== '' && v !== 0) return true;
              if (field.altKey) { const av = i[field.altKey]; return av !== null && av !== undefined && av !== '' && av !== 0; }
              return false;
            }).length;
            return Math.round((filled / items.length) * 100);
          };
          const leadQuality = leadFields.map(f => ({ ...f, percent: calcFieldPct(dashboardLeads, f) }));
          const oppQuality = oppFields.map(f => ({ ...f, percent: calcFieldPct(filteredOpportunities, f) }));
          const leadAvg = leadQuality.length > 0 ? Math.round(leadQuality.reduce((s, f) => s + f.percent, 0) / leadQuality.length) : 100;
          const oppAvg = oppQuality.length > 0 ? Math.round(oppQuality.reduce((s, f) => s + f.percent, 0) / oppQuality.length) : 100;
          const overallQuality = Math.round(oppAvg * 0.6 + leadAvg * 0.4);
          const qualityColor = overallQuality >= 80 ? '#9CAF88' : overallQuality >= 60 ? '#D9A441' : '#00BBA2';

          // ---- Key Movers & Shakers ----
          const moverMap: Record<string, { name: string; newLeads: number; converted: number; newOpps: number; stageAdvances: number; won: number; wonValue: number; score: number }> = {};
          const addMover = (name: string) => {
            if (!name || name === 'Unassigned') return;
            if (!moverMap[name]) moverMap[name] = { name, newLeads: 0, converted: 0, newOpps: 0, stageAdvances: 0, won: 0, wonValue: 0, score: 0 };
          };
          weekNewLeads.forEach(l => { const n = l.leadOwner || l.salesRepresentative; addMover(n); if (moverMap[n]) moverMap[n].newLeads++; });
          weekConvertedLeads.forEach(l => { const n = l.leadOwner || l.salesRepresentative; addMover(n); if (moverMap[n]) moverMap[n].converted++; });
          weekNewOpps.forEach(o => { addMover(o.accountOwner); if (moverMap[o.accountOwner]) moverMap[o.accountOwner].newOpps++; });
          stageMovements.forEach(m => { addMover(m.opp.accountOwner); if (moverMap[m.opp.accountOwner]) moverMap[m.opp.accountOwner].stageAdvances++; });
          weekWonOpps.forEach(o => { addMover(o.accountOwner); if (moverMap[o.accountOwner]) { moverMap[o.accountOwner].won++; moverMap[o.accountOwner].wonValue += (o.totalContractValue || o.annualContractValue || 0); } });
          Object.values(moverMap).forEach(m => { m.score = m.won * 5 + m.stageAdvances * 3 + m.newOpps * 2 + m.converted * 2 + m.newLeads; });
          const topMovers = Object.values(moverMap).sort((a, b) => b.score - a.score).slice(0, 6);
          const moverAccentColors = ['#D9A441', '#6A7CA5', '#D8A39D'];

          // ---- Recommended Actions ----
          const recommendations: { priority: 'HIGH' | 'MEDIUM' | 'LOW'; text: string }[] = [];
          // Overdue close dates
          const overdueOpps = activeOpps.filter(o => o.estimatedCloseDate && new Date(o.estimatedCloseDate) < now);
          if (overdueOpps.length > 0) recommendations.push({ priority: 'HIGH', text: `${overdueOpps.length} opportunity(ies) are past their estimated close date. Review and update close dates or push for immediate closure.` });
          // Stale deals (30+ days no activity)
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          const staleOpps = activeOpps.filter(o => o.lastEditedDate && new Date(o.lastEditedDate) < thirtyDaysAgo);
          if (staleOpps.length > 0) recommendations.push({ priority: 'HIGH', text: `${staleOpps.length} deal(s) have had no activity in 30+ days. Schedule follow-ups to re-engage or consider deprioritizing.` });
          // Imminent low-probability
          const twoWeeksOut = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
          const imminentLowProb = activeOpps.filter(o => {
            if (!o.estimatedCloseDate) return false;
            const cd = new Date(o.estimatedCloseDate);
            if (cd > twoWeeksOut || cd < now) return false;
            const prob = o.probabilityOverride ? parseInt(String(o.probabilityOverride)) : calculateProbabilityByStage(o.salesStage).percent;
            return prob < 60;
          });
          if (imminentLowProb.length > 0) recommendations.push({ priority: 'HIGH', text: `${imminentLowProb.length} deal(s) closing within 2 weeks have probability below 60%. Intensive engagement required to improve win likelihood.` });
          // High-value early pipeline
          const highValEarly = activeOpps.filter(o => (o.annualContractValue || 0) >= 500000 && ['Qualified', 'Discovery'].includes(o.salesStage));
          if (highValEarly.length > 0) recommendations.push({ priority: 'MEDIUM', text: `${highValEarly.length} high-value deal(s) (₹500K+) are in early pipeline stages. Assign senior resources to accelerate qualification.` });
          // Cold opportunities
          const coldCount = activeOpps.filter(o => getEffectiveSalesStatus(o) === 'Cold').length;
          if (coldCount > 3) recommendations.push({ priority: 'MEDIUM', text: `${coldCount} opportunities are classified as Cold. Review each for viability — either re-engage or mark as lost to clean the pipeline.` });
          // Data quality
          if (overallQuality < 70) recommendations.push({ priority: 'MEDIUM', text: `Data quality score is ${overallQuality}/100. Improve missing fields in leads and opportunities to enable better forecasting and reporting.` });
          // Conversion rate
          if (weekNewLeads.length >= 3 && weekConvertedLeads.length / weekNewLeads.length < 0.2) {
            recommendations.push({ priority: 'LOW', text: `Lead conversion rate this period is ${Math.round((weekConvertedLeads.length / weekNewLeads.length) * 100)}%. Review lead qualification criteria and follow-up cadence to improve conversion.` });
          }

          const priorityColors = { HIGH: '#00BBA2', MEDIUM: '#D9A441', LOW: '#6A7CA5' };

          // ---- Pipeline by Service Line ----
          const serviceLineMap: Record<string, { acv: number; count: number }> = {};
          activeOpps.forEach(o => {
            const sl = o.serviceLine || 'Unspecified';
            if (!serviceLineMap[sl]) serviceLineMap[sl] = { acv: 0, count: 0 };
            serviceLineMap[sl].acv += (o.annualContractValue || 0);
            serviceLineMap[sl].count++;
          });
          const serviceLineData = Object.entries(serviceLineMap)
            .map(([name, v]) => ({ name, acv: v.acv, count: v.count }))
            .sort((a, b) => b.acv - a.acv);
          const slColors = ['#009E8A', '#6A7CA5', '#9CAF88', '#D9A441', '#481F89', '#D8A39D', '#4B5563'];

          // ---- Pipeline by Geography ----
          const geoMap: Record<string, { acv: number; count: number }> = {};
          activeOpps.forEach(o => {
            const geo = o.geography || 'Unspecified';
            if (!geoMap[geo]) geoMap[geo] = { acv: 0, count: 0 };
            geoMap[geo].acv += (o.annualContractValue || 0);
            geoMap[geo].count++;
          });
          const geoData = Object.entries(geoMap)
            .map(([name, v]) => ({ name, acv: v.acv, count: v.count }))
            .sort((a, b) => b.acv - a.acv);
          const geoColors = ['#481F89', '#D9A441', '#009E8A', '#6A7CA5', '#9CAF88', '#D8A39D', '#4B5563'];

          // ---- Lead Source Effectiveness ----
          const leadSourceMap: Record<string, { total: number; converted: number }> = {};
          dashboardLeads.forEach(l => {
            const src = l.leadSource || 'Unknown';
            if (!leadSourceMap[src]) leadSourceMap[src] = { total: 0, converted: 0 };
            leadSourceMap[src].total++;
            if (l.convertedAt) leadSourceMap[src].converted++;
          });
          const leadSourceData = Object.entries(leadSourceMap)
            .map(([source, v]) => ({ source, total: v.total, converted: v.converted, rate: v.total > 0 ? Math.round((v.converted / v.total) * 100) : 0 }))
            .sort((a, b) => b.total - a.total);

          // ---- Lost Deal Analysis ----
          const lostReasonMap: Record<string, { count: number; value: number }> = {};
          weekLostOpps.forEach(o => {
            const reason = o.lossReason || o.reasonForLoss || 'Not specified';
            if (!lostReasonMap[reason]) lostReasonMap[reason] = { count: 0, value: 0 };
            lostReasonMap[reason].count++;
            lostReasonMap[reason].value += (o.totalContractValue || o.annualContractValue || 0);
          });
          const lostReasonData = Object.entries(lostReasonMap)
            .map(([reason, v]) => ({ reason, count: v.count, value: v.value }))
            .sort((a, b) => b.value - a.value);
          const weekLostValue = weekLostOpps.reduce((s, o) => s + (o.totalContractValue || o.annualContractValue || 0), 0);
          const avgLostDealSize = weekLostOpps.length > 0 ? weekLostValue / weekLostOpps.length : 0;

          // ---- Avg Deal Size ----
          const avgDealSize = activeOpps.length > 0 ? activeOpps.reduce((s, o) => s + (o.annualContractValue || 0), 0) / activeOpps.length : 0;

          // ---- PDF Export Handler ----
          const handleExportPDF = async () => {
            if (!readoutContentRef.current || readoutExporting) return;
            setReadoutExporting(true);
            try {
              const el = readoutContentRef.current;
              const canvas = await html2canvas(el, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
                windowWidth: el.scrollWidth,
                windowHeight: el.scrollHeight
              });

              const imgData = canvas.toDataURL('image/png');
              const pdf = new jsPDF('p', 'mm', 'a4');
              const pdfW = pdf.internal.pageSize.getWidth();
              const pdfH = pdf.internal.pageSize.getHeight();
              const margin = 10;
              const usableW = pdfW - margin * 2;

              // Header
              pdf.setFillColor(206, 31, 61); // Pierian red
              pdf.rect(0, 0, pdfW, 18, 'F');
              pdf.setTextColor(255, 255, 255);
              pdf.setFontSize(14);
              pdf.setFont('helvetica', 'bold');
              pdf.text('Pierag Sales Connect — Readout', margin, 12);
              pdf.setFontSize(9);
              pdf.setFont('helvetica', 'normal');
              pdf.text(periodBounds.label, pdfW - margin, 12, { align: 'right' });

              const headerH = 22; // mm below header
              const contentStartY = headerH;

              // Scale image to fit page width
              const imgW = usableW;
              const imgH = (canvas.height / canvas.width) * imgW;

              // Paginate if content is taller than one page
              const pageContentH = pdfH - contentStartY - 8; // leave 8mm bottom margin
              let remainingH = imgH;
              let srcY = 0;
              let pageNum = 0;

              while (remainingH > 0) {
                if (pageNum > 0) {
                  pdf.addPage();
                  // Repeat header on subsequent pages
                  pdf.setFillColor(206, 31, 61);
                  pdf.rect(0, 0, pdfW, 18, 'F');
                  pdf.setTextColor(255, 255, 255);
                  pdf.setFontSize(14);
                  pdf.setFont('helvetica', 'bold');
                  pdf.text('Pierag Sales Connect — Readout', margin, 12);
                  pdf.setFontSize(9);
                  pdf.setFont('helvetica', 'normal');
                  pdf.text(`${periodBounds.label}  (page ${pageNum + 1})`, pdfW - margin, 12, { align: 'right' });
                }

                const sliceH = Math.min(remainingH, pageContentH);
                // Crop the source image for this page slice
                const sliceCanvas = document.createElement('canvas');
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = Math.round((sliceH / imgH) * canvas.height);
                const ctx = sliceCanvas.getContext('2d');
                if (ctx) {
                  ctx.drawImage(canvas, 0, srcY, canvas.width, sliceCanvas.height, 0, 0, canvas.width, sliceCanvas.height);
                  const sliceData = sliceCanvas.toDataURL('image/png');
                  pdf.addImage(sliceData, 'PNG', margin, contentStartY, imgW, sliceH);
                }

                srcY += sliceCanvas.height;
                remainingH -= sliceH;
                pageNum++;
              }

              // Footer on last page
              pdf.setTextColor(150, 150, 150);
              pdf.setFontSize(7);
              pdf.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, margin, pdfH - 4);

              pdf.save(`Readout-${readoutFromDate}-to-${readoutToDate}.pdf`);
            } catch (err) {
              console.error('PDF export failed:', err);
              alert('Failed to export PDF. Please try again.');
            } finally {
              setReadoutExporting(false);
            }
          };

          // ---- Excel Export Handler ----
          const handleExportExcel = () => {
            const XLSX = (window as any).XLSX;
            if (!XLSX) { alert('Excel library not loaded.'); return; }
            const wb = XLSX.utils.book_new();

            // Sheet 1: Summary
            const summaryRows = [
              { Metric: 'Period', Value: periodBounds.label },
              { Metric: 'New Leads', Value: weekNewLeads.length },
              { Metric: 'Converted Leads', Value: weekConvertedLeads.length },
              { Metric: 'Conversion Rate', Value: `${weekNewLeads.length > 0 ? Math.round((weekConvertedLeads.length / weekNewLeads.length) * 100) : 0}%` },
              { Metric: 'New Opportunities', Value: weekNewOpps.length },
              { Metric: 'New Pipeline Value', Value: weekNewOppsValue },
              { Metric: 'Won Deals', Value: weekWonOpps.length },
              { Metric: 'Won Value', Value: weekWonValue },
              { Metric: 'Lost Deals', Value: weekLostOpps.length },
              { Metric: 'Lost Value', Value: weekLostValue },
              { Metric: 'Active Pipeline (ACV)', Value: activeOpps.reduce((s, o) => s + (o.annualContractValue || 0), 0) },
              { Metric: 'Active Pipeline (Weighted)', Value: activeOpps.reduce((s, o) => { const prob = o.probabilityOverride ? parseInt(String(o.probabilityOverride)) : calculateProbabilityByStage(o.salesStage).percent; return s + ((o.annualContractValue || 0) * (prob / 100)); }, 0) },
              { Metric: 'Avg Deal Size (ACV)', Value: Math.round(avgDealSize) },
              { Metric: 'Data Quality Score', Value: overallQuality },
            ];
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'Summary');

            // Sheet 2: New Leads
            const leadRows = weekNewLeads.map((l: any) => ({
              Company: l.companyName || '', Contact: l.contactPerson || '', Email: l.email || '',
              Source: l.leadSource || '', Geography: l.geography || l.region || '',
              'Service Line': l.serviceLine || '', Owner: l.leadOwner || l.salesRepresentative || '',
              'Est. Revenue': l.estimatedRevenue || '', 'Date Captured': l.dateCaptured || ''
            }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(leadRows.length ? leadRows : [{ Note: 'No new leads this period' }]), 'New Leads');

            // Sheet 3: New Opportunities
            const newOppRows = weekNewOpps.map((o: any) => ({
              Opportunity: o.opportunityName || '', Account: o.accountOwner || '', Stage: o.salesStage || '',
              ACV: o.annualContractValue || 0, TCV: o.totalContractValue || 0,
              'Service Line': o.serviceLine || '', Geography: o.geography || '',
              'Close Date': o.estimatedCloseDate || '', 'Initiation Date': o.initiationDate || ''
            }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(newOppRows.length ? newOppRows : [{ Note: 'No new opportunities this period' }]), 'New Opportunities');

            // Sheet 4: Won Deals
            const wonRows = weekWonOpps.map((o: any) => ({
              Opportunity: o.opportunityName || '', Account: o.accountOwner || '',
              ACV: o.annualContractValue || 0, TCV: o.totalContractValue || 0,
              'Service Line': o.serviceLine || '', Geography: o.geography || '',
              'Close Date': o.actualCloseDate || ''
            }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(wonRows.length ? wonRows : [{ Note: 'No won deals this period' }]), 'Won Deals');

            // Sheet 5: Lost Deals
            const lostRows = weekLostOpps.map((o: any) => ({
              Opportunity: o.opportunityName || '', Account: o.accountOwner || '',
              ACV: o.annualContractValue || 0, TCV: o.totalContractValue || 0,
              Reason: o.lossReason || o.reasonForLoss || '', 'Service Line': o.serviceLine || '',
              Geography: o.geography || '', 'Close Date': o.actualCloseDate || ''
            }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lostRows.length ? lostRows : [{ Note: 'No lost deals this period' }]), 'Lost Deals');

            // Sheet 6: Stage Movements
            const moveRows = stageMovements.map(m => ({
              Opportunity: m.opp.opportunityName || '', Owner: m.opp.accountOwner || '',
              From: m.from, To: m.to
            }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(moveRows.length ? moveRows : [{ Note: 'No stage movements this period' }]), 'Stage Movements');

            // Sheet 7: Active Pipeline
            const pipeRows = activeOpps.map((o: any) => {
              const prob = o.probabilityOverride ? parseInt(String(o.probabilityOverride)) : calculateProbabilityByStage(o.salesStage).percent;
              return {
                Opportunity: o.opportunityName || '', Account: o.accountOwner || '', Stage: o.salesStage || '',
                Status: getEffectiveSalesStatus(o), ACV: o.annualContractValue || 0, TCV: o.totalContractValue || 0,
                'Probability %': prob, 'Weighted Value': Math.round((o.totalContractValue || o.annualContractValue || 0) * (prob / 100)),
                'Service Line': o.serviceLine || '', Geography: o.geography || '',
                'Close Date': o.estimatedCloseDate || ''
              };
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pipeRows.length ? pipeRows : [{ Note: 'No active pipeline' }]), 'Active Pipeline');

            // Sheet 8: Movers & Shakers
            const moverRows = topMovers.map(m => ({
              Name: m.name, Score: m.score, 'New Leads': m.newLeads, Converted: m.converted,
              'New Opps': m.newOpps, 'Stage Advances': m.stageAdvances, Won: m.won, 'Won Value': m.wonValue
            }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(moverRows.length ? moverRows : [{ Note: 'No activity this period' }]), 'Movers & Shakers');

            XLSX.writeFile(wb, `Readout-${readoutFromDate}-to-${readoutToDate}.xlsx`);
          };

          return (
            <div className="space-y-4">

              {/* ---- Section 1: Period Selector + Export ---- */}
              {(() => {
                const today = new Date();
                const fmt = (d: Date) => d.toISOString().split('T')[0];
                const quickPeriods = [
                  { id: 'week', label: 'Week', from: (() => { const d = new Date(today); d.setDate(d.getDate() - 7); return fmt(d); })(), to: fmt(today) },
                  { id: 'fortnight', label: 'Fortnight', from: (() => { const d = new Date(today); d.setDate(d.getDate() - 14); return fmt(d); })(), to: fmt(today) },
                  { id: 'thisMonth', label: 'This Month', from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(today) },
                  { id: 'lastMonth', label: 'Last Month', from: fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)), to: fmt(new Date(today.getFullYear(), today.getMonth(), 0)) },
                  { id: '2months', label: '2 Months', from: (() => { const d = new Date(today); d.setMonth(d.getMonth() - 2); return fmt(d); })(), to: fmt(today) }
                ];
                const activeQuick = quickPeriods.find(p => p.from === readoutFromDate && p.to === readoutToDate)?.id || null;
                return (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex rounded-lg overflow-hidden border border-gray-200">
                        {quickPeriods.map(p => (
                          <button key={p.id} onClick={() => { setReadoutFromDate(p.from); setReadoutToDate(p.to); }}
                            className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${activeQuick === p.id ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                            style={activeQuick === p.id ? { backgroundColor: '#009E8A' } : {}}>
                            {p.label}
                          </button>
                        ))}
                      </div>
                      <span className="text-gray-300 mx-0.5">|</span>
                      <label className="text-xs text-gray-500 font-medium">From</label>
                      <input type="date" value={readoutFromDate} onChange={e => setReadoutFromDate(e.target.value)}
                        className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pierian" />
                      <label className="text-xs text-gray-500 font-medium">To</label>
                      <input type="date" value={readoutToDate} onChange={e => setReadoutToDate(e.target.value)}
                        className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pierian" />
                      <span className="text-[10px] text-gray-400 ml-1">({periodSpanDays + 1}d)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-400">{filteredOpportunities.length} opps, {dashboardLeads.length} leads</p>
                      <button onClick={handleExportExcel}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
                        style={{ backgroundColor: '#16a34a' }} title="Export to Excel">
                        <FileSpreadsheet size={13} />
                        Export Excel
                      </button>
                      <button onClick={handleExportPDF} disabled={readoutExporting}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                        style={{ backgroundColor: '#009E8A' }} title="Export to PDF">
                        {readoutExporting ? <RefreshCw size={13} className="animate-spin" /> : <Download size={13} />}
                        {readoutExporting ? 'Exporting...' : 'Export PDF'}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* ---- PDF Export Content Area ---- */}
              <div ref={readoutContentRef} className="space-y-4 bg-white">

              {/* ---- Section 2: Executive Summary (Expanded) ---- */}
              {(() => {
                // Pre-compute summary metrics
                const leadDelta = weekNewLeads.length - prevNewLeads.length;
                const leadTrend = leadDelta > 0 ? `up ${leadDelta}` : leadDelta < 0 ? `down ${Math.abs(leadDelta)}` : 'flat';
                const oppDelta = weekNewOpps.length - prevNewOpps.length;
                const convDelta = weekConvertedLeads.length - prevConvertedLeads.length;
                const convRate = weekNewLeads.length > 0 ? Math.round((weekConvertedLeads.length / weekNewLeads.length) * 100) : 0;
                const prevConvRate = prevNewLeads.length > 0 ? Math.round((prevConvertedLeads.length / prevNewLeads.length) * 100) : 0;
                const weekWinLossTotal = weekWonOpps.length + weekLostOpps.length;
                const weekWinRate = weekWinLossTotal > 0 ? Math.round((weekWonOpps.length / weekWinLossTotal) * 100) : null;
                const weekLostValue = weekLostOpps.reduce((s, o) => s + (o.totalContractValue || o.annualContractValue || 0), 0);
                const totalActivePipelineValue = activeOpps.reduce((s, o) => s + (o.annualContractValue || 0), 0);
                const totalWeightedPipeline = activeOpps.reduce((s, o) => {
                  const prob = o.probabilityOverride ? parseInt(String(o.probabilityOverride)) : calculateProbabilityByStage(o.salesStage).percent;
                  return s + ((o.annualContractValue || 0) * (prob / 100));
                }, 0);
                const hotCount = statusDist.find(d => d.name === 'Hot')?.value || 0;
                const warmCount = statusDist.find(d => d.name === 'Warm')?.value || 0;
                const coldCountExec = statusDist.find(d => d.name === 'Cold')?.value || 0;
                const inactiveCount = statusDist.find(d => d.name === 'Inactive')?.value || 0;
                const forecastTotalWeighted = forecastData.reduce((s, f) => s + f.weighted, 0);
                const forecastTotalDeals = forecastData.reduce((s, f) => s + f.deals, 0);
                const stageAdvanceCount = stageMovements.filter(m => {
                  const stages = ['Assessment', 'Lead', 'Qualified', 'Discovery', 'Proposal', 'Contract', 'Closed-Won'];
                  const fi = stages.findIndex(s => s.toLowerCase() === m.from.toLowerCase());
                  const ti = stages.findIndex(s => s.toLowerCase() === m.to.toLowerCase());
                  return fi >= 0 && ti >= 0 && ti > fi;
                }).length;
                const stageRegressCount = stageMovements.length - stageAdvanceCount;
                const topMover = topMovers.length > 0 ? topMovers[0] : null;

                return (
                  <Card className="p-4 border-l-4" style={{ borderLeftColor: '#009E8A' }}>
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 rounded-lg bg-[rgba(0,187,162,0.08)] flex-shrink-0 mt-0.5">
                        <FileText size={16} className="text-pierian" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">Executive Summary</h3>

                        {/* Narrative paragraph */}
                        <p className="text-sm text-gray-700 leading-relaxed mb-3">
                          {(() => {
                            const parts: string[] = [];
                            parts.push(`The team captured ${weekNewLeads.length} new lead${weekNewLeads.length !== 1 ? 's' : ''} this period (${leadTrend} from prior period) and converted ${weekConvertedLeads.length} into qualified opportunities${weekNewLeads.length > 0 ? ` (${convRate}% conversion rate)` : ''}.`);
                            if (weekNewOpps.length > 0) parts.push(`${weekNewOpps.length} new ${weekNewOpps.length !== 1 ? 'opportunities were' : 'opportunity was'} added to the pipeline, representing ${fmtCurr(weekNewOppsValue)} in potential ACV.`);
                            if (biggestNewDeal) parts.push(`The largest new deal is "${biggestNewDeal.opportunityName}" (${biggestNewDeal.accountOwner || 'Unassigned'}) at ${fmtCurr(biggestNewDeal.annualContractValue || 0)}.`);
                            if (weekWonOpps.length > 0) parts.push(`${weekWonOpps.length} deal${weekWonOpps.length !== 1 ? 's were' : ' was'} closed-won, totaling ${fmtCurr(weekWonValue)}.`);
                            if (weekLostOpps.length > 0) parts.push(`${weekLostOpps.length} ${weekLostOpps.length !== 1 ? 'opportunities were' : 'opportunity was'} closed-lost${weekLostValue > 0 ? ` (${fmtCurr(weekLostValue)} lost)` : ''}.`);
                            if (stageMovements.length > 0) parts.push(`${stageMovements.length} stage ${stageMovements.length !== 1 ? 'movements' : 'movement'} recorded${stageAdvanceCount > 0 ? ` — ${stageAdvanceCount} forward` : ''}${stageRegressCount > 0 ? `, ${stageRegressCount} backward` : ''}.`);
                            parts.push(`Pipeline generation is trending ${oppDelta > 0 ? 'upward' : oppDelta < 0 ? 'downward' : 'flat'} period-over-period.`);
                            return parts.join(' ');
                          })()}
                        </p>

                        {/* KPI Metrics Row */}
                        <div className="grid grid-cols-5 gap-2 mb-3">
                          <div className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="text-[10px] text-gray-500 mb-0.5">Active Pipeline</p>
                            <p className="text-sm font-bold text-gray-900">{fmtCurr(totalActivePipelineValue)}</p>
                            <p className="text-[9px] text-gray-400">{activeOpps.length} deals</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="text-[10px] text-gray-500 mb-0.5">Weighted Pipeline</p>
                            <p className="text-sm font-bold" style={{ color: '#009E8A' }}>{fmtCurr(totalWeightedPipeline)}</p>
                            <p className="text-[9px] text-gray-400">{Math.round((totalWeightedPipeline / (totalActivePipelineValue || 1)) * 100)}% of total</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="text-[10px] text-gray-500 mb-0.5">Avg Deal Size</p>
                            <p className="text-sm font-bold" style={{ color: '#D9A441' }}>{fmtCurr(avgDealSize)}</p>
                            <p className="text-[9px] text-gray-400">ACV per deal</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="text-[10px] text-gray-500 mb-0.5">6-Period Forecast</p>
                            <p className="text-sm font-bold" style={{ color: '#481F89' }}>{fmtCurr(forecastTotalWeighted)}</p>
                            <p className="text-[9px] text-gray-400">{forecastTotalDeals} deal{forecastTotalDeals !== 1 ? 's' : ''} expected</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="text-[10px] text-gray-500 mb-0.5">Conversion Rate</p>
                            <p className="text-sm font-bold" style={{ color: convRate >= prevConvRate ? '#9CAF88' : '#00BBA2' }}>{convRate}%</p>
                            <p className="text-[9px] text-gray-400">{convRate >= prevConvRate ? '+' : ''}{convRate - prevConvRate}pp vs prior</p>
                          </div>
                        </div>

                        {/* Pipeline Health + Win Rate + Highlights Row */}
                        <div className="flex items-stretch gap-2">
                          {/* Pipeline Health Mini */}
                          <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5">
                            <span className="text-[10px] text-gray-500 mr-1">Health:</span>
                            {hotCount > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#fbe8ec', color: '#00BBA2' }}>{hotCount} Hot</span>}
                            {warmCount > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#fdf4e3', color: '#D9A441' }}>{warmCount} Warm</span>}
                            {coldCountExec > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#edf0f5', color: '#6A7CA5' }}>{coldCountExec} Cold</span>}
                            {inactiveCount > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">{inactiveCount} Inactive</span>}
                          </div>

                          {/* Win Rate */}
                          {weekWinRate !== null && (
                            <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5">
                              <span className="text-[10px] text-gray-500">Win Rate:</span>
                              <span className="text-[10px] font-bold" style={{ color: weekWinRate >= 50 ? '#9CAF88' : '#00BBA2' }}>{weekWinRate}%</span>
                              <span className="text-[9px] text-gray-400">({weekWonOpps.length}W / {weekLostOpps.length}L)</span>
                            </div>
                          )}

                          {/* Top Mover */}
                          {topMover && (
                            <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5">
                              <Flame size={11} style={{ color: '#D9A441' }} />
                              <span className="text-[10px] text-gray-500">Top Mover:</span>
                              <span className="text-[10px] font-bold text-gray-800">{topMover.name}</span>
                              <span className="text-[9px] text-gray-400">(score {topMover.score})</span>
                            </div>
                          )}

                          {/* Data Quality Badge */}
                          <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5 ml-auto">
                            <span className="text-[10px] text-gray-500">Data Quality:</span>
                            <span className="text-[10px] font-bold" style={{ color: qualityColor }}>{overallQuality}/100</span>
                          </div>
                        </div>

                        {/* Risk / Opportunity Callouts */}
                        {(overdueOpps.length > 0 || staleOpps.length > 0 || coldCountExec > 3 || weekWonOpps.length > 0) && (
                          <div className="flex flex-wrap gap-1.5 mt-2.5">
                            {overdueOpps.length > 0 && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium">
                                <AlertCircle size={10} /> {overdueOpps.length} overdue close date{overdueOpps.length !== 1 ? 's' : ''}
                              </span>
                            )}
                            {staleOpps.length > 0 && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">
                                <Clock size={10} /> {staleOpps.length} stale deal{staleOpps.length !== 1 ? 's' : ''} (30+ days)
                              </span>
                            )}
                            {coldCountExec > 3 && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                                <Target size={10} /> {coldCountExec} cold opportunities need review
                              </span>
                            )}
                            {weekWonOpps.length > 0 && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">
                                <CheckCircle size={10} /> {fmtCurr(weekWonValue)} closed-won this period
                              </span>
                            )}
                            {weekNewOpps.length > 0 && weekNewOppsValue >= 500000 && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium">
                                <Zap size={10} /> {fmtCurr(weekNewOppsValue)} new pipeline added
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })()}

              {/* ---- Section 3: 6-Week Trends ---- */}
              <Card className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Lead & Opportunity Trends (6 {trendPeriodLabel})</h3>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm" style={{ backgroundColor: '#6A7CA5' }}></div><span className="text-[9px] text-gray-500">New Leads</span></div>
                    <div className="flex items-center gap-1"><div className="w-3 h-2 rounded-sm" style={{ backgroundColor: '#00BBA2' }}></div><span className="text-[9px] text-gray-500">New Opps</span></div>
                    <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded" style={{ backgroundColor: '#9CAF88' }}></div><span className="text-[9px] text-gray-500">Conversions</span></div>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={trendData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="week" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '8px' }} />
                    <Bar dataKey="newLeads" fill="#6A7CA5" radius={[3, 3, 0, 0]} name="New Leads" barSize={20}>
                      <LabelList dataKey="newLeads" position="top" offset={4} formatter={(v: number) => v > 0 ? v : ''} style={{ fontSize: '9px', fill: '#6A7CA5', fontWeight: 700 }} />
                    </Bar>
                    <Bar dataKey="newOpps" fill="#00BBA2" radius={[3, 3, 0, 0]} name="New Opps" barSize={20}>
                      <LabelList dataKey="newOpps" position="top" offset={4} formatter={(v: number) => v > 0 ? v : ''} style={{ fontSize: '9px', fill: '#00BBA2', fontWeight: 700 }} />
                    </Bar>
                    <Line type="monotone" dataKey="conversions" stroke="#9CAF88" strokeWidth={2} strokeDasharray="5 3" dot={{ fill: '#9CAF88', r: 4, stroke: '#fff', strokeWidth: 1.5 }} name="Conversions" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>

              {/* ---- Section 4: Opportunity Progress (2 col) ---- */}
              <div className="grid grid-cols-2 gap-3">
                {/* Stage Movements */}
                <Card className="p-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Stage Movements — {periodBounds.periodName}</h3>
                  {Object.keys(movementGroups).length === 0 ? (
                    <p className="text-xs text-gray-400 py-4 text-center">No stage movements recorded this period</p>
                  ) : (
                    <div className="space-y-1.5">
                      {Object.entries(movementGroups).sort((a, b) => b[1] - a[1]).map(([transition, count]) => (
                        <div key={transition} className="flex items-center gap-2 py-1 px-2 rounded bg-gray-50">
                          <ArrowRight size={12} className="text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-700 flex-1">{transition}</span>
                          <span className="text-xs font-bold text-gray-900">{count}</span>
                        </div>
                      ))}
                      <p className="text-[10px] text-gray-400 mt-1">{stageMovements.length} total movement{stageMovements.length !== 1 ? 's' : ''} across {Object.keys(movementGroups).length} transition type{Object.keys(movementGroups).length !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                </Card>

                {/* Pipeline Health */}
                <Card className="p-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Pipeline Health</h3>
                  {statusDist.length === 0 ? (
                    <p className="text-xs text-gray-400 py-4 text-center">No active opportunities</p>
                  ) : (
                    <div className="flex items-center gap-4">
                      <ResponsiveContainer width="50%" height={160}>
                        <PieChart>
                          <Pie data={statusDist} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="value"
                            label={({ value }: any) => value > 0 ? value : ''} labelLine={false}>
                            {statusDist.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                          </Pie>
                          <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '8px' }} formatter={(value: number, name: string) => [value, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-2 flex-1">
                        {statusDist.map(d => (
                          <div key={d.name} className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }}></div>
                            <span className="text-xs text-gray-600 flex-1">{d.name}</span>
                            <span className="text-sm font-bold text-gray-900">{d.value}</span>
                          </div>
                        ))}
                        <div className="pt-1.5 border-t border-gray-100">
                          <span className="text-xs font-semibold text-gray-700">Total Active: {activeOpps.length}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              </div>

              {/* ---- Pipeline by Service Line + Geography ---- */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="p-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Pipeline by Service Line</h3>
                  {serviceLineData.length === 0 ? (
                    <p className="text-xs text-gray-400 py-4 text-center">No active pipeline data</p>
                  ) : (
                    <div className="space-y-1.5">
                      {serviceLineData.slice(0, 8).map((sl, idx) => {
                        const maxAcv = serviceLineData[0]?.acv || 1;
                        return (
                          <div key={sl.name} className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-600 w-28 truncate">{sl.name}</span>
                            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full flex items-center justify-end pr-1"
                                style={{ width: `${Math.max(8, (sl.acv / maxAcv) * 100)}%`, backgroundColor: slColors[idx % slColors.length] }}>
                                <span className="text-[8px] font-bold text-white">{fmtCurr(sl.acv)}</span>
                              </div>
                            </div>
                            <span className="text-[9px] text-gray-400 w-8 text-right">{sl.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>

                <Card className="p-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Pipeline by Geography</h3>
                  {geoData.length === 0 ? (
                    <p className="text-xs text-gray-400 py-4 text-center">No active pipeline data</p>
                  ) : (
                    <div className="space-y-1.5">
                      {geoData.slice(0, 8).map((g, idx) => {
                        const maxAcv = geoData[0]?.acv || 1;
                        return (
                          <div key={g.name} className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-600 w-28 truncate">{g.name}</span>
                            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full flex items-center justify-end pr-1"
                                style={{ width: `${Math.max(8, (g.acv / maxAcv) * 100)}%`, backgroundColor: geoColors[idx % geoColors.length] }}>
                                <span className="text-[8px] font-bold text-white">{fmtCurr(g.acv)}</span>
                              </div>
                            </div>
                            <span className="text-[9px] text-gray-400 w-8 text-right">{g.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </div>

              {/* ---- Lead Source Effectiveness ---- */}
              <Card className="p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Lead Source Effectiveness</h3>
                {leadSourceData.length === 0 ? (
                  <p className="text-xs text-gray-400 py-4 text-center">No lead source data available</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Lead Source</th>
                          <th className="text-right py-1.5 px-2 text-gray-500 font-medium">Total Leads</th>
                          <th className="text-right py-1.5 px-2 text-gray-500 font-medium">Converted</th>
                          <th className="text-right py-1.5 px-2 text-gray-500 font-medium">Conv. Rate</th>
                          <th className="text-left py-1.5 px-2 text-gray-500 font-medium w-32">Performance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leadSourceData.slice(0, 10).map(ls => (
                          <tr key={ls.source} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-1.5 px-2 text-gray-800 font-medium">{ls.source}</td>
                            <td className="py-1.5 px-2 text-right text-gray-700">{ls.total}</td>
                            <td className="py-1.5 px-2 text-right text-gray-700">{ls.converted}</td>
                            <td className="py-1.5 px-2 text-right font-bold" style={{ color: ls.rate >= 30 ? '#9CAF88' : ls.rate >= 15 ? '#D9A441' : '#00BBA2' }}>{ls.rate}%</td>
                            <td className="py-1.5 px-2">
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${ls.rate}%`, backgroundColor: ls.rate >= 30 ? '#9CAF88' : ls.rate >= 15 ? '#D9A441' : '#00BBA2' }} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* ---- Lost Deal Analysis (conditional) ---- */}
              {weekLostOpps.length > 0 && (
                <Card className="p-3 border-l-4" style={{ borderLeftColor: '#00BBA2' }}>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Lost Deal Analysis — {periodBounds.periodName}</h3>
                  {/* Summary cards */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="bg-teal-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-gray-500 mb-0.5">Deals Lost</p>
                      <p className="text-sm font-bold text-red-700">{weekLostOpps.length}</p>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-gray-500 mb-0.5">Value Lost</p>
                      <p className="text-sm font-bold text-red-700">{fmtCurr(weekLostValue)}</p>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-gray-500 mb-0.5">Avg Deal Size</p>
                      <p className="text-sm font-bold text-red-700">{fmtCurr(avgLostDealSize)}</p>
                    </div>
                  </div>
                  {/* Loss reason breakdown */}
                  {lostReasonData.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-600 mb-1.5">Loss Reasons</p>
                      <div className="space-y-1">
                        {lostReasonData.map(lr => (
                          <div key={lr.reason} className="flex items-center gap-2 py-1 px-2 rounded bg-gray-50">
                            <span className="text-xs text-gray-700 flex-1">{lr.reason}</span>
                            <span className="text-xs font-bold text-gray-900">{lr.count} deal{lr.count !== 1 ? 's' : ''}</span>
                            <span className="text-xs text-gray-500">{fmtCurr(lr.value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Individual deals table */}
                  <div className="overflow-x-auto">
                    <p className="text-xs font-semibold text-gray-600 mb-1.5">Lost Deals Detail</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-1 px-1 text-gray-500 font-medium">Opportunity</th>
                          <th className="text-left py-1 px-1 text-gray-500 font-medium">Owner</th>
                          <th className="text-left py-1 px-1 text-gray-500 font-medium">Service Line</th>
                          <th className="text-right py-1 px-1 text-gray-500 font-medium">ACV</th>
                          <th className="text-left py-1 px-1 text-gray-500 font-medium">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weekLostOpps.map((o: any, idx: number) => (
                          <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-1 px-1 text-gray-800 truncate max-w-[180px]">{o.opportunityName}</td>
                            <td className="py-1 px-1 text-gray-600">{o.accountOwner || '-'}</td>
                            <td className="py-1 px-1 text-gray-600">{o.serviceLine || '-'}</td>
                            <td className="py-1 px-1 text-right font-semibold text-red-700">{fmtCurr(o.annualContractValue || 0)}</td>
                            <td className="py-1 px-1 text-gray-600">{o.lossReason || o.reasonForLoss || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* ---- Closure Forecast ---- */}
              <Card className="p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Pipeline Closure Forecast — Next 6 {trendPeriodLabel}</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={forecastData} margin={{ top: 15, right: 40, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="week" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => fmtCurr(v)} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '8px' }} formatter={(value: number, name: string) => [name.includes('Deal') ? value : fmtCurr(value), name]} />
                    <Bar yAxisId="left" dataKey="expected" fill="#D8A39D" fillOpacity={0.5} radius={[3, 3, 0, 0]} name="Expected Value" barSize={28} />
                    <Bar yAxisId="left" dataKey="weighted" fill="'#009E8A'" radius={[3, 3, 0, 0]} name="Weighted Value" barSize={28}>
                      <LabelList dataKey="weighted" position="top" offset={4} formatter={(v: number) => v > 0 ? fmtCurr(v) : ''} style={{ fontSize: '9px', fill: '#009E8A', fontWeight: 700 }} />
                    </Bar>
                    <Line yAxisId="right" type="monotone" dataKey="deals" stroke="#481F89" strokeWidth={2} dot={{ fill: '#481F89', r: 4, stroke: '#fff', strokeWidth: 1.5 }} name="Deal Count" />
                  </ComposedChart>
                </ResponsiveContainer>
                {/* Top 5 deals table */}
                {topForecastDeals.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-600 mb-1.5">Top Deals by Weighted Value</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-1 px-1 text-gray-500 font-medium">Opportunity</th>
                            <th className="text-left py-1 px-1 text-gray-500 font-medium">Close Date</th>
                            <th className="text-left py-1 px-1 text-gray-500 font-medium">Stage</th>
                            <th className="text-right py-1 px-1 text-gray-500 font-medium">Value</th>
                            <th className="text-right py-1 px-1 text-gray-500 font-medium">Prob.</th>
                            <th className="text-right py-1 px-1 text-gray-500 font-medium">Weighted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topForecastDeals.map((d, idx) => (
                            <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="py-1 px-1 text-gray-800 truncate max-w-[200px]">{d.opportunityName}</td>
                              <td className="py-1 px-1 text-gray-600">{d.estimatedCloseDate ? new Date(d.estimatedCloseDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}</td>
                              <td className="py-1 px-1 text-gray-600">{d.salesStage}</td>
                              <td className="py-1 px-1 text-right font-semibold text-gray-800">{fmtCurr(d.totalContractValue || d.annualContractValue || 0)}</td>
                              <td className="py-1 px-1 text-right text-gray-600">{d.prob}%</td>
                              <td className="py-1 px-1 text-right font-bold" style={{ color: '#009E8A' }}>{fmtCurr(d.weightedVal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Card>

              {/* ---- Section 6: Data Quality Scorecard (2 col) ---- */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="p-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Data Quality Score</h3>
                  <div className="flex items-center gap-6">
                    {/* Circular score */}
                    <div className="flex-shrink-0">
                      <svg width="96" height="96" className="transform -rotate-90">
                        <circle cx="48" cy="48" r="38" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                        <circle cx="48" cy="48" r="38" stroke={qualityColor} strokeWidth="8" fill="none"
                          strokeDasharray={`${overallQuality * 2.39} 239`} strokeLinecap="round" />
                      </svg>
                      <div className="relative -mt-[72px] flex items-center justify-center h-[48px]">
                        <span className="text-2xl font-bold" style={{ color: qualityColor }}>{overallQuality}</span>
                      </div>
                      <p className="text-[10px] text-gray-400 text-center mt-1">out of 100</p>
                    </div>
                    {/* Breakdown */}
                    <div className="flex-1 space-y-1">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Leads (avg {leadAvg}%)</p>
                      {leadQuality.map(f => (
                        <div key={f.key} className="flex items-center gap-1.5">
                          <span className="text-[10px] text-gray-500 w-24 truncate">{f.name}</span>
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${f.percent}%`, backgroundColor: f.percent >= 90 ? '#9CAF88' : f.percent >= 70 ? '#D9A441' : '#00BBA2' }} />
                          </div>
                          <span className="text-[9px] font-semibold w-7 text-right">{f.percent}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>

                <Card className="p-3">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Opportunity Data Quality</h3>
                  <div className="space-y-1 mb-3">
                    <p className="text-xs font-semibold text-gray-600 mb-1">Opportunities (avg {oppAvg}%)</p>
                    {oppQuality.map(f => (
                      <div key={f.key} className="flex items-center gap-1.5">
                        <span className="text-[10px] text-gray-500 w-28 truncate">{f.name}</span>
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${f.percent}%`, backgroundColor: f.percent >= 90 ? '#9CAF88' : f.percent >= 70 ? '#D9A441' : '#00BBA2' }} />
                        </div>
                        <span className="text-[9px] font-semibold w-7 text-right">{f.percent}%</span>
                      </div>
                    ))}
                  </div>
                  {/* Recommendations for low fields */}
                  {[...leadQuality, ...oppQuality].filter(f => f.percent < 70).length > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-[10px] font-semibold text-amber-700 mb-1">Improvement Areas:</p>
                      {[...leadQuality, ...oppQuality].filter(f => f.percent < 70).slice(0, 4).map(f => (
                        <p key={f.key} className="text-[10px] text-gray-500">• {f.name} is at {f.percent}% — needs attention</p>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* ---- Section 7: Key Movers & Shakers ---- */}
              <Card className="p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Movers & Shakers</h3>
                {topMovers.length === 0 ? (
                  <p className="text-xs text-gray-400 py-4 text-center">No pipeline movements recorded for this period</p>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {topMovers.map((m, idx) => (
                      <div key={m.name} className={`p-3 rounded-lg bg-gray-50 ${idx < 3 ? 'border-t-2' : ''}`}
                        style={idx < 3 ? { borderTopColor: moverAccentColors[idx] } : {}}>
                        <div className="flex items-center gap-2 mb-2">
                          {idx === 0 && <Flame size={14} style={{ color: '#D9A441' }} />}
                          <span className="text-xs font-bold text-gray-900">{m.name}</span>
                          <span className="text-[10px] text-gray-400 ml-auto">Score: {m.score}</span>
                        </div>
                        <div className="space-y-0.5 text-[10px] text-gray-600">
                          {m.won > 0 && <p>Won {m.won} deal{m.won > 1 ? 's' : ''} ({fmtCurr(m.wonValue)})</p>}
                          {m.stageAdvances > 0 && <p>Advanced {m.stageAdvances} deal{m.stageAdvances > 1 ? 's' : ''}</p>}
                          {m.newOpps > 0 && <p>Created {m.newOpps} new opp{m.newOpps > 1 ? 's' : ''}</p>}
                          {m.converted > 0 && <p>Converted {m.converted} lead{m.converted > 1 ? 's' : ''}</p>}
                          {m.newLeads > 0 && <p>Captured {m.newLeads} new lead{m.newLeads > 1 ? 's' : ''}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* ---- Section 8: Recommended Actions ---- */}
              <Card className="p-3">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Recommended Actions</h3>
                {recommendations.length === 0 ? (
                  <p className="text-xs text-gray-500 py-3 text-center">No critical actions identified. Pipeline is in good shape!</p>
                ) : (
                  <div className="space-y-2">
                    {recommendations.slice(0, 10).map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 py-1.5 px-2 rounded bg-gray-50">
                        <span className="text-xs font-bold text-white px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: priorityColors[rec.priority] }}>
                          {rec.priority}
                        </span>
                        <span className="text-xs text-gray-700 leading-relaxed">{rec.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              </div>{/* end readoutContentRef */}
            </div>
          );
        })()}

      </div>
    );
  };

  // Lead Creation Wizard
  const LeadWizard = ({ serviceLines, serviceOfferings, entities, leadSources, leadOwners, industries, geographies, leads, setLeads, setShowWizard }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 3;

    const [wizardData, setWizardData] = useState({
      companyName: '',
      industryVertical: '',
      industrySubVertical: '',
      city: '',
      geography: '',
      companySize: '1-50 employees',
      companyRevenue: '',
      contactPerson: '',
      designation: '',
      email: '',
      contactNumber: '',
      serviceLine: '',
      serviceOffering: '',
      entity: '',
      pierianGeography: '',
      salesRep: '',
      painPoints: '',
      budget: '',
      estimatedRevenue: '',
      timeline: '',
      leadSource: '',
      priorityLevel: 'Medium',
      leadOwner: '',
      comments: ''
    });

    const companySizes = ['1-50 employees', '51-200 employees', '201-500 employees', '500-1000 employees', '1000+ employees'];
    const priorities = ['Low', 'Medium', 'High', 'Critical'];
    const timelines = ['1-3 months', '3-6 months', '6-12 months', '12+ months'];

    // Email validation function
    const isValidEmail = (email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    const handleNext = () => {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    };

    const handlePrevious = () => {
      if (currentStep > 1) {
        setCurrentStep(currentStep - 1);
      }
    };

    const handleFinish = () => {
      const newLead = {
        leadId: 'NEW',
        ...wizardData,
        leadOwner: wizardData.salesRep, // Use Sales Rep from page 2 as Lead Owner
        leadStatus: 'New',
        dateCaptured: new Date().toISOString().split('T')[0],
        engagementHistory: `Lead created via wizard on ${new Date().toLocaleDateString()}`
      };

      setLeads([...leads, newLead]);
      setShowWizard(false);
      alert('Lead created successfully!');
    };

    const updateWizardData = (field, value) => {
      setWizardData(prev => ({ ...prev, [field]: value }));
    };

    const isStepValid = () => {
      switch(currentStep) {
        case 1:
          // Client Information: company + contact details with email validation
          return wizardData.companyName &&
                 wizardData.industryVertical &&
                 wizardData.geography &&
                 wizardData.contactPerson &&
                 wizardData.email &&
                 isValidEmail(wizardData.email) &&
                 wizardData.contactNumber;
        case 2:
          // Pierian Information
          return wizardData.serviceLine &&
                 wizardData.serviceOffering &&
                 wizardData.entity &&
                 wizardData.pierianGeography &&
                 wizardData.salesRep;
        case 3:
          // Pain points and other info
          return wizardData.painPoints &&
                 wizardData.budget &&
                 wizardData.leadSource;
        default:
          return false;
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-sm font-semibold">Lead Creation Wizard</h3>
                <p className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</p>
              </div>
              <button
                onClick={() => setShowWizard(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex items-center mb-2">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        currentStep >= step ? 'bg-pierian text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {step}
                      </div>
                      <span className="text-xs text-gray-600 mt-1 text-center">
                        {step === 1 ? 'Client Info' : step === 2 ? 'Info' : 'Other Details'}
                      </span>
                    </div>
                    {step < 3 && (
                      <div className={`h-1 w-full mx-2 mt-[-20px] ${
                        currentStep > step ? 'bg-pierian' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Step 1: Client Information (Company + Contact) */}
            {currentStep === 1 && (
              <div className="space-y-3">
                <h4 className="text-md font-semibold text-gray-800 mb-3">Client Information</h4>

                {/* Company Details */}
                <div className="p-3 bg-gray-50 rounded-md">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">Company Details</h5>

                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                      <input
                        type="text"
                        value={wizardData.companyName}
                        onChange={(e) => updateWizardData('companyName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Enter company name"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Industry *</label>
                        <select
                          value={wizardData.industryVertical}
                          onChange={(e) => updateWizardData('industryVertical', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">Select industry</option>
                          {industries?.filter(i => i.isActive).map(ind => (
                            <option key={ind.id} value={ind.name}>{ind.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Industry</label>
                        <input
                          type="text"
                          value={wizardData.industrySubVertical}
                          onChange={(e) => updateWizardData('industrySubVertical', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="e.g., Cloud Computing"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          value={wizardData.city}
                          onChange={(e) => updateWizardData('city', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Enter city"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Geography *</label>
                        <select
                          value={wizardData.geography}
                          onChange={(e) => updateWizardData('geography', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">Select geography</option>
                          {geographies?.filter(g => g.isActive).map(geo => (
                            <option key={geo.id} value={geo.name}>{geo.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Size</label>
                        <select
                          value={wizardData.companySize}
                          onChange={(e) => updateWizardData('companySize', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          {companySizes.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Revenue (₹)</label>
                        <input
                          type="number"
                          value={wizardData.companyRevenue}
                          onChange={(e) => updateWizardData('companyRevenue', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Annual revenue"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="p-3 bg-gray-50 rounded-md">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">Contact Information</h5>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person Name *</label>
                      <input
                        type="text"
                        value={wizardData.contactPerson}
                        onChange={(e) => updateWizardData('contactPerson', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Enter contact name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                      <input
                        type="text"
                        value={wizardData.designation}
                        onChange={(e) => updateWizardData('designation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="e.g., CTO, VP Operations"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input
                        type="email"
                        value={wizardData.email}
                        onChange={(e) => updateWizardData('email', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md ${
                          wizardData.email && !isValidEmail(wizardData.email)
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-300'
                        }`}
                        placeholder="email@company.com"
                      />
                      {wizardData.email && !isValidEmail(wizardData.email) && (
                        <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
                      <input
                        type="tel"
                        value={wizardData.contactNumber}
                        onChange={(e) => updateWizardData('contactNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="+1-234-567-8900"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Pierian Information */}
            {currentStep === 2 && (
              <div className="space-y-3">
                <h4 className="text-md font-semibold text-gray-800 mb-3">Information</h4>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Line *</label>
                    <select
                      value={wizardData.serviceLine}
                      onChange={(e) => updateWizardData('serviceLine', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select service line</option>
                      {serviceLines?.filter(sl => sl.isActive).map(sl => (
                        <option key={sl.id} value={sl.name}>{sl.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Offering *</label>
                    <select
                      value={wizardData.serviceOffering}
                      onChange={(e) => updateWizardData('serviceOffering', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select service offering</option>
                      {serviceOfferings?.filter(so => so.isActive).map(so => (
                        <option key={so.id} value={so.name}>{so.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entity</label>
                    <select
                      value={wizardData.entity}
                      onChange={(e) => updateWizardData('entity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select entity</option>
                      {entities?.filter(e => e.isActive).map(entity => (
                        <option key={entity.id} value={entity.name}>{entity.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Geography *</label>
                    <select
                      value={wizardData.pierianGeography}
                      onChange={(e) => updateWizardData('pierianGeography', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select geography</option>
                      {geographies?.filter(g => g.isActive).map(geo => (
                        <option key={geo.id} value={geo.name}>{geo.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sales Representative *</label>
                    <select
                      value={wizardData.salesRep}
                      onChange={(e) => updateWizardData('salesRep', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select salesperson</option>
                      {leadOwners?.filter(owner => owner.status === 'Active').map(owner => (
                        <option key={owner.userId} value={owner.name}>{owner.name} ({owner.role})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Pain Points and Other Information */}
            {currentStep === 3 && (
              <div className="space-y-3">
                <h4 className="text-md font-semibold text-gray-800 mb-3">Pain Points & Other Information</h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pain Points / Requirements *</label>
                  <textarea
                    value={wizardData.painPoints}
                    onChange={(e) => updateWizardData('painPoints', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows="3"
                    placeholder="Describe the customer's pain points and requirements"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Budget (₹) *</label>
                    <input
                      type="number"
                      value={wizardData.budget}
                      onChange={(e) => updateWizardData('budget', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Expected budget"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Revenue (₹)</label>
                    <input
                      type="number"
                      value={wizardData.estimatedRevenue}
                      onChange={(e) => updateWizardData('estimatedRevenue', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Potential revenue"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timeline</label>
                    <select
                      value={wizardData.timeline}
                      onChange={(e) => updateWizardData('timeline', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select timeline</option>
                      {timelines.map(tl => (
                        <option key={tl} value={tl}>{tl}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source *</label>
                    <select
                      value={wizardData.leadSource}
                      onChange={(e) => updateWizardData('leadSource', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select lead source</option>
                      {leadSources?.filter(ls => ls.isActive).map(source => (
                        <option key={source.id} value={source.name}>{source.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority Level</label>
                    <select
                      value={wizardData.priorityLevel}
                      onChange={(e) => updateWizardData('priorityLevel', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {priorities.map(priority => (
                        <option key={priority} value={priority}>{priority}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Comments</label>
                  <textarea
                    value={wizardData.comments}
                    onChange={(e) => updateWizardData('comments', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows="3"
                    placeholder="Any additional notes or comments"
                  />
                </div>

                {/* Summary */}
                <div className="mt-6 p-4 bg-gray-50 rounded-md">
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Summary</h5>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="font-medium">Company:</span> {wizardData.companyName}</div>
                    <div><span className="font-medium">Contact:</span> {wizardData.contactPerson}</div>
                    <div><span className="font-medium">Email:</span> {wizardData.email}</div>
                    <div><span className="font-medium">Service Line:</span> {wizardData.serviceLine}</div>
                    <div><span className="font-medium">Service Offering:</span> {wizardData.serviceOffering}</div>
                    <div><span className="font-medium">Entity:</span> {wizardData.entity}</div>
                    <div><span className="font-medium">Budget:</span> {(() => { const n = parseFloat(wizardData.budget); return Number.isFinite(n) ? `₹${n.toLocaleString()}` : '-'; })()}</div>
                    <div><span className="font-medium">Source:</span> {wizardData.leadSource}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`px-4 py-2 text-sm rounded-md ${
                currentStep === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>

            <div className="flex gap-2">
              <button
                onClick={() => setShowWizard(false)}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>

              {currentStep < totalSteps ? (
                <button
                  onClick={handleNext}
                  disabled={!isStepValid()}
                  className={`px-4 py-2 text-sm rounded-md ${
                    isStepValid()
                      ? 'bg-pierian text-white hover:bg-scarlet'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleFinish}
                  disabled={!isStepValid()}
                  className={`px-4 py-2 text-sm rounded-md ${
                    isStepValid()
                      ? 'bg-sage text-white hover:bg-sage-shade'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Create Lead
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ========================================
  // CUSTOMERS VIEW COMPONENT
  // ========================================
  const CustomersView = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState<string | null>(null);

    // Multi-select filter state (matching opportunities pattern)
    const [custActiveFilterType, setCustActiveFilterType] = useState<string | null>(null);
    const [custSelectedGeographies, setCustSelectedGeographies] = useState<string[]>([]);
    const [custSelectedServiceLines, setCustSelectedServiceLines] = useState<string[]>([]);
    const [custSelectedStatuses, setCustSelectedStatuses] = useState<string[]>([]);
    const [custSelectedManagers, setCustSelectedManagers] = useState<string[]>([]);
    const [custExpandedFilterOptions, setCustExpandedFilterOptions] = useState<Record<string, boolean>>({});
    const [custStatusFilter, setCustStatusFilter] = useState('all');

    // Toggle multi-select filter
    const toggleCustFilter = (type: string, value: string) => {
      switch (type) {
        case 'geography': setCustSelectedGeographies(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]); break;
        case 'serviceline': setCustSelectedServiceLines(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]); break;
        case 'status': setCustSelectedStatuses(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]); break;
        case 'manager': setCustSelectedManagers(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]); break;
      }
    };

    const hasCustActiveFilters = custSelectedGeographies.length > 0 || custSelectedServiceLines.length > 0 || custSelectedStatuses.length > 0 || custSelectedManagers.length > 0;
    const clearCustAllFilters = () => { setCustSelectedGeographies([]); setCustSelectedServiceLines([]); setCustSelectedStatuses([]); setCustSelectedManagers([]); setCustActiveFilterType(null); };

    // User Geography Access Control — non-admin users only see customers matching their assigned geographies
    const userAccessibleCustomers = customers.filter(customer => {
      if (currentUser?.isAdmin) return true;
      const hasGeo = currentUser?.geographies?.length > 0;
      const hasSL = currentUser?.serviceLines?.length > 0;
      if (!hasGeo && !hasSL) return true;
      const geoOk = !hasGeo ||
        (customer.geography && currentUser.geographies.includes(customer.geography));
      const slOk = !hasSL ||
        (customer.serviceLine && currentUser.serviceLines.includes(customer.serviceLine));
      return geoOk && slOk;
    });

    // Unique values for filters (computed from accessible customers only)
    const uniqueCustGeographies = Array.from(new Set(userAccessibleCustomers.map(c => c.geography).filter(Boolean))).sort();
    const uniqueCustServiceLines = Array.from(new Set(userAccessibleCustomers.map(c => c.serviceLine).filter(Boolean))).sort();
    const uniqueCustManagers = Array.from(new Set(userAccessibleCustomers.map(c => c.accountManager).filter(Boolean))).sort();

    // Filter customers based on search and multi-select filters
    const filteredCustomers = userAccessibleCustomers.filter(customer => {
      const matchesSearch = !searchTerm ||
        customer.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.primaryContact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.customerId?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCardStatus = custStatusFilter === 'all' || customer.status === custStatusFilter;
      const matchesStatus = custSelectedStatuses.length === 0 || custSelectedStatuses.includes(customer.status);
      const matchesGeography = custSelectedGeographies.length === 0 || custSelectedGeographies.includes(customer.geography);
      const matchesServiceLine = custSelectedServiceLines.length === 0 || custSelectedServiceLines.includes(customer.serviceLine);
      const matchesManager = custSelectedManagers.length === 0 || custSelectedManagers.includes(customer.accountManager);

      return matchesSearch && matchesCardStatus && matchesStatus && matchesGeography && matchesServiceLine && matchesManager;
    });

    const handleEditCustomer = (customer) => {
      setSelectedCustomer(customer);
      setShowCustomerModal(true);
    };

    const handleDeleteCustomer = async (customerId) => {
      if (!currentUser?.isAdmin) { alert('Only admins can delete customers.'); return; }
      if (!confirm('Are you sure you want to delete this customer?')) return;

      try {
        const response = await fetch(`${API_BASE_URL}/customers/${customerId}`, {
          method: 'DELETE'
        });
        const result = await response.json();

        if (result.success) {
          setCustomers(customers.filter(c => c.customerId !== customerId));
          alert('Customer deleted successfully');
        } else {
          alert('Failed to delete customer: ' + result.message);
        }
      } catch (error) {
        console.error('Error deleting customer:', error);
        alert('Error deleting customer');
      }
    };

    const getHealthColor = (score) => {
      if (score >= 80) return 'bg-green-100 text-green-800';
      if (score >= 50) return 'bg-yellow-100 text-yellow-800';
      return 'bg-red-100 text-red-800';
    };

    const getStatusColor = (status) => {
      switch (status) {
        case 'Active': return 'bg-green-100 text-green-800';
        case 'Inactive': return 'bg-gray-100 text-gray-800';
        case 'Churned': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Summary stats & status-based counts with contract values (from accessible customers only)
    const activeList = userAccessibleCustomers.filter(c => c.status === 'Active');
    const inactiveList = userAccessibleCustomers.filter(c => c.status === 'Inactive');
    const churnedList = userAccessibleCustomers.filter(c => c.status === 'Churned');
    const totalCustomers = customers.length;

    const sumCV = (arr: any[]) => arr.reduce((sum, c) => sum + (parseFloat(c.contractValue) || 0), 0);
    const activeCount = activeList.length;
    const activeValue = sumCV(activeList);
    const inactiveCount = inactiveList.length;
    const inactiveValue = sumCV(inactiveList);
    const churnedCount = churnedList.length;
    const churnedValue = sumCV(churnedList);
    const totalContractValue = sumCV(customers);

    return (
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-gray-900">Customers</h2>
            <p className="text-sm text-gray-500">Manage your customer accounts and relationships</p>
          </div>
          <Button onClick={() => { setSelectedCustomer(null); setShowCustomerModal(true); }}>
            <Plus size={16} className="mr-1" /> New Customer
          </Button>
        </div>

        {/* Filters - Multi-select chips matching Opportunities pattern */}
        <div className="rounded-md shadow-sm px-2 py-1.5" style={{ backgroundColor: 'var(--dm-surface)', border: '1px solid var(--dm-border)' }}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter size={14} className="text-gray-400 flex-shrink-0" />
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={12} />
              <input type="text" placeholder="Search..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-32 pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-pierian focus:border-pierian" />
            </div>
            {/* Geography Filter */}
            <button onClick={() => setCustActiveFilterType(custActiveFilterType === 'geography' ? null : 'geography')}
              className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
              style={{
                backgroundColor: custSelectedGeographies.length > 0 ? '#00BBA2' : custActiveFilterType === 'geography' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                color: custSelectedGeographies.length > 0 ? 'white' : custActiveFilterType === 'geography' ? '#009E8A' : '#374151',
                border: custActiveFilterType === 'geography' && custSelectedGeographies.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
              }}>
              Geo{custSelectedGeographies.length > 0 && ` (${custSelectedGeographies.length})`}
            </button>
            {/* Service Line Filter */}
            <button onClick={() => setCustActiveFilterType(custActiveFilterType === 'serviceline' ? null : 'serviceline')}
              className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
              style={{
                backgroundColor: custSelectedServiceLines.length > 0 ? '#00BBA2' : custActiveFilterType === 'serviceline' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                color: custSelectedServiceLines.length > 0 ? 'white' : custActiveFilterType === 'serviceline' ? '#009E8A' : '#374151',
                border: custActiveFilterType === 'serviceline' && custSelectedServiceLines.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
              }}>
              Service Line{custSelectedServiceLines.length > 0 && ` (${custSelectedServiceLines.length})`}
            </button>
            {/* Status Filter */}
            <button onClick={() => setCustActiveFilterType(custActiveFilterType === 'status' ? null : 'status')}
              className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
              style={{
                backgroundColor: custSelectedStatuses.length > 0 ? '#00BBA2' : custActiveFilterType === 'status' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                color: custSelectedStatuses.length > 0 ? 'white' : custActiveFilterType === 'status' ? '#009E8A' : '#374151',
                border: custActiveFilterType === 'status' && custSelectedStatuses.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
              }}>
              Status{custSelectedStatuses.length > 0 && ` (${custSelectedStatuses.length})`}
            </button>
            {/* Account Manager Filter */}
            <button onClick={() => setCustActiveFilterType(custActiveFilterType === 'manager' ? null : 'manager')}
              className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
              style={{
                backgroundColor: custSelectedManagers.length > 0 ? '#00BBA2' : custActiveFilterType === 'manager' ? 'rgba(0,187,162,0.08)' : '#f3f4f6',
                color: custSelectedManagers.length > 0 ? 'white' : custActiveFilterType === 'manager' ? '#009E8A' : '#374151',
                border: custActiveFilterType === 'manager' && custSelectedManagers.length === 0 ? '1px solid #00BBA2' : '1px solid transparent'
              }}>
              Manager{custSelectedManagers.length > 0 && ` (${custSelectedManagers.length})`}
            </button>
            {/* Clear Filters */}
            {hasCustActiveFilters && (
              <button onClick={clearCustAllFilters} className="px-2.5 py-1 rounded text-xs font-medium ml-1" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                Clear
              </button>
            )}
            <span className="text-xs text-gray-400 ml-auto">{filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Filter Options Dropdowns */}
          {custActiveFilterType === 'geography' && (() => {
            const geoWithCounts = uniqueCustGeographies
              .map(geo => ({ value: geo, count: customers.filter(c => c.geography === geo).length }))
              .filter(g => g.count > 0).sort((a, b) => b.count - a.count);
            const isExpanded = custExpandedFilterOptions['geography'];
            const visible = isExpanded ? geoWithCounts : geoWithCounts.slice(0, 8);
            const hiddenCount = geoWithCounts.length - 8;
            return (
              <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                {visible.map(({ value, count }) => {
                  const isSelected = custSelectedGeographies.includes(value);
                  return (
                    <button key={value} onClick={() => toggleCustFilter('geography', value)} className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: isSelected ? '#00BBA2' : 'var(--dm-surface)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                      {value} ({count})
                    </button>
                  );
                })}
                {hiddenCount > 0 && (
                  <button onClick={() => setCustExpandedFilterOptions(prev => ({ ...prev, geography: !prev.geography }))}
                    className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5" style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}>
                    {isExpanded ? 'Less' : `+${hiddenCount}`}
                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                )}
              </div>
            );
          })()}

          {custActiveFilterType === 'serviceline' && (() => {
            const slWithCounts = uniqueCustServiceLines
              .map(sl => ({ value: sl, count: customers.filter(c => c.serviceLine === sl).length }))
              .filter(s => s.count > 0).sort((a, b) => b.count - a.count);
            const isExpanded = custExpandedFilterOptions['serviceline'];
            const visible = isExpanded ? slWithCounts : slWithCounts.slice(0, 8);
            const hiddenCount = slWithCounts.length - 8;
            return (
              <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                {visible.map(({ value, count }) => {
                  const isSelected = custSelectedServiceLines.includes(value);
                  return (
                    <button key={value} onClick={() => toggleCustFilter('serviceline', value)} className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: isSelected ? '#00BBA2' : 'var(--dm-surface)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                      {value} ({count})
                    </button>
                  );
                })}
                {hiddenCount > 0 && (
                  <button onClick={() => setCustExpandedFilterOptions(prev => ({ ...prev, serviceline: !prev.serviceline }))}
                    className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5" style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}>
                    {isExpanded ? 'Less' : `+${hiddenCount}`}
                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                )}
              </div>
            );
          })()}

          {custActiveFilterType === 'status' && (() => {
            const allStatuses = ['Active', 'Inactive', 'Churned'];
            return (
              <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                {allStatuses.map(status => {
                  const isSelected = custSelectedStatuses.includes(status);
                  const count = userAccessibleCustomers.filter(c => c.status === status).length;
                  return (
                    <button key={status} onClick={() => toggleCustFilter('status', status)} className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: isSelected ? '#00BBA2' : 'var(--dm-surface)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                      {status} ({count})
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {custActiveFilterType === 'manager' && (() => {
            const mgrWithCounts = uniqueCustManagers
              .map(mgr => ({ value: mgr, count: customers.filter(c => c.accountManager === mgr).length }))
              .filter(m => m.count > 0).sort((a, b) => b.count - a.count);
            const isExpanded = custExpandedFilterOptions['manager'];
            const visible = isExpanded ? mgrWithCounts : mgrWithCounts.slice(0, 8);
            const hiddenCount = mgrWithCounts.length - 8;
            return (
              <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100 animate-fadeIn">
                {visible.map(({ value, count }) => {
                  const isSelected = custSelectedManagers.includes(value);
                  return (
                    <button key={value} onClick={() => toggleCustFilter('manager', value)} className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: isSelected ? '#00BBA2' : 'var(--dm-surface)', color: isSelected ? 'white' : 'var(--dm-btn-text)', border: isSelected ? 'none' : '1px solid #d1d5db' }}>
                      {value} ({count})
                    </button>
                  );
                })}
                {hiddenCount > 0 && (
                  <button onClick={() => setCustExpandedFilterOptions(prev => ({ ...prev, manager: !prev.manager }))}
                    className="px-2 py-0.5 rounded text-xs font-medium flex items-center gap-0.5" style={{ backgroundColor: 'var(--dm-btn-bg)', color: 'var(--dm-text-2)' }}>
                    {isExpanded ? 'Less' : `+${hiddenCount}`}
                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                )}
              </div>
            );
          })()}

          {/* Active Filters Display */}
          {hasCustActiveFilters && (
            <div className="mt-1 pt-1 border-t border-gray-100">
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-[10px] font-medium text-gray-400">Filtering:</span>
                {custSelectedGeographies.map(geo => (
                  <span key={`cg-${geo}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {geo}
                    <button onClick={() => toggleCustFilter('geography', geo)} className="hover:text-pierian">x</button>
                  </span>
                ))}
                {custSelectedServiceLines.map(sl => (
                  <span key={`cs-${sl}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {sl}
                    <button onClick={() => toggleCustFilter('serviceline', sl)} className="hover:text-pierian">x</button>
                  </span>
                ))}
                {custSelectedStatuses.map(st => (
                  <span key={`ct-${st}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {st}
                    <button onClick={() => toggleCustFilter('status', st)} className="hover:text-pierian">x</button>
                  </span>
                ))}
                {custSelectedManagers.map(mgr => (
                  <span key={`cm-${mgr}`} className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[10px] font-medium" style={{ backgroundColor: 'rgba(0,187,162,0.18)', color: '#00BBA2', border: '1px solid rgba(0,187,162,0.5)', fontWeight: 600 }}>
                    {mgr}
                    <button onClick={() => toggleCustFilter('manager', mgr)} className="hover:text-pierian">x</button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Summary Cards - Pipeline Style */}
        <div className="flex gap-2 items-start">
          {[
            { key: 'Active', label: 'Active', desc: 'Currently engaged', count: activeCount, revenue: activeValue, bg: isDark ? 'rgba(134,188,134,0.32)' : '#C7D8C1' },
            { key: 'Inactive', label: 'Inactive', desc: 'No active engagement', count: inactiveCount, revenue: inactiveValue, bg: isDark ? 'rgba(100,130,180,0.32)' : '#d1d9e6' },
            { key: 'Churned', label: 'Churned', desc: 'Lost accounts', count: churnedCount, revenue: churnedValue, bg: isDark ? 'rgba(209,86,86,0.38)' : 'rgba(209,86,86,0.12)' },
            { key: 'all', label: 'All', desc: 'All customers', count: totalCustomers, revenue: totalContractValue, bg: isDark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' },
          ].map(card => {
            const isActive = custStatusFilter === card.key;
            return (
              <div key={card.key} className="flex flex-col items-center">
                <button
                  onClick={() => setCustStatusFilter(card.key)}
                  className={`rounded-lg px-3 py-1.5 text-center hover:shadow-md transition-all min-w-[80px] ${isActive ? 'shadow-lg ring-2' : ''}`}
                  style={{
                    backgroundColor: isActive ? '#00BBA2' : card.bg,
                    borderColor: isActive ? '#009E8A' : 'transparent',
                    ringColor: isActive ? '#009E8A' : undefined
                  }}
                  title={card.desc}
                >
                  <span className="text-[10px] font-semibold block leading-tight" style={{ color: isActive ? '#ffffff' : 'var(--dm-btn-text)' }}>{card.label}</span>
                  <span className="text-lg font-bold block leading-tight" style={{ color: isActive ? '#ffffff' : 'var(--dm-text)' }}>{card.count}</span>
                </button>
                <span className="text-[10px] font-bold mt-0.5" style={{ color: '#059669' }}>₹{card.revenue >= 1000000 ? (card.revenue / 1000000).toFixed(2) + 'M' : (card.revenue / 1000).toFixed(0) + 'K'}</span>
                <span className="text-[8px] text-gray-400 italic leading-tight">{card.desc}</span>
              </div>
            );
          })}
        </div>

        {/* Customers List */}
        <Card className="overflow-hidden">
          {filteredCustomers.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <Building size={36} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm font-medium">No customers found</p>
              <p className="text-xs mt-1">Try adjusting your filters or create a new customer</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200" style={{ backgroundColor: '#131A25' }}>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-white">Customer</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-white">Service Line</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-white">Geography</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-white">Contract Value</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-white">Renewal Date</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-white">Health</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-white">Status</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <React.Fragment key={customer.customerId}>
                      <tr
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => setExpandedCustomer(expandedCustomer === customer.customerId ? null : customer.customerId)}
                      >
                        <td className="py-1.5 px-3">
                          <div className="font-medium text-gray-900 text-xs">{customer.companyName}</div>
                          <div className="text-[10px] text-gray-400">{customer.customerId}</div>
                        </td>
                        <td className="py-1.5 px-3 text-xs text-gray-600">{customer.serviceLine || '-'}</td>
                        <td className="py-1.5 px-3 text-xs text-gray-600">{customer.geography || '-'}</td>
                        <td className="py-1.5 px-3 text-xs text-right text-gray-900 font-medium">{formatCurrency(customer.contractValue)}</td>
                        <td className="py-1.5 px-3 text-xs text-gray-600">{formatDate(customer.renewalDate)}</td>
                        <td className="py-1.5 px-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getHealthColor(customer.healthScore)}`}>
                            {customer.healthScore || 0}%
                          </span>
                        </td>
                        <td className="py-1.5 px-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(customer.status)}`}>
                            {customer.status}
                          </span>
                        </td>
                        <td className="py-1.5 px-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleEditCustomer(customer)}
                              className="px-2 py-1 bg-slate text-white rounded hover:bg-slate-shade"
                              title="Edit"
                            >
                              <Edit size={12} />
                            </button>
                            {currentUser?.isAdmin && (
                              <button
                                onClick={() => handleDeleteCustomer(customer.customerId)}
                                className="px-2 py-1 bg-pierian text-white rounded hover:bg-scarlet"
                                title="Delete"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Details Row */}
                      {expandedCustomer === customer.customerId && (
                        <tr className="bg-gray-50">
                          <td colSpan={8} className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {/* Primary Contact Details */}
                              <div className="space-y-2">
                                <h4 className="font-medium text-gray-900 flex items-center">
                                  <User size={16} className="mr-2" />
                                  Primary Contact
                                </h4>
                                <div className="text-sm space-y-1 pl-6">
                                  <p><span className="text-gray-500">Name:</span> {customer.primaryContact || '-'}</p>
                                  <p><span className="text-gray-500">Title:</span> {customer.contactDesignation || '-'}</p>
                                  <p className="flex items-center">
                                    <Mail size={14} className="mr-1 text-gray-400" />
                                    {customer.email || '-'}
                                  </p>
                                  <p className="flex items-center">
                                    <Phone size={14} className="mr-1 text-gray-400" />
                                    {customer.phone || '-'}
                                  </p>
                                </div>
                              </div>

                              {/* Secondary Contact Details */}
                              <div className="space-y-2">
                                <h4 className="font-medium text-gray-900 flex items-center">
                                  <Users size={16} className="mr-2" />
                                  Secondary Contact
                                </h4>
                                <div className="text-sm space-y-1 pl-6">
                                  {customer.secondaryContact ? (
                                    <>
                                      <p><span className="text-gray-500">Name:</span> {customer.secondaryContact}</p>
                                      <p><span className="text-gray-500">Title:</span> {customer.secondaryContactDesignation || '-'}</p>
                                      <p className="flex items-center">
                                        <Mail size={14} className="mr-1 text-gray-400" />
                                        {customer.secondaryContactEmail || '-'}
                                      </p>
                                      <p className="flex items-center">
                                        <Phone size={14} className="mr-1 text-gray-400" />
                                        {customer.secondaryContactPhone || '-'}
                                      </p>
                                    </>
                                  ) : (
                                    <p className="text-gray-400 italic">No secondary contact</p>
                                  )}
                                </div>
                              </div>

                              {/* Contract Details */}
                              <div className="space-y-2">
                                <h4 className="font-medium text-gray-900 flex items-center">
                                  <FileText size={16} className="mr-2" />
                                  Contract Details
                                </h4>
                                <div className="text-sm space-y-1 pl-6">
                                  <p><span className="text-gray-500">Service Line:</span> {customer.serviceLine || '-'}</p>
                                  <p><span className="text-gray-500">Account Manager:</span> {customer.accountManager || '-'}</p>
                                  <p><span className="text-gray-500">Start Date:</span> {formatDate(customer.startDate)}</p>
                                  <p><span className="text-gray-500">Projects:</span> {customer.projectCount || 0}</p>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    );
  };

  // Proposals View - Shows opportunities with proposals prepared or in negotiation
  const ProposalsView = () => {
    const [filterOwner, setFilterOwner] = useState('all');
    const [filterGeography, setFilterGeography] = useState('all');
    const [filterServiceLine, setFilterServiceLine] = useState('all');
    const [filterSalesStage, setFilterSalesStage] = useState('all');
    const [searchProposal, setSearchProposal] = useState('');
    const [expandedProps, setExpandedProps] = useState({});
    const [propActions, setPropActions] = useState({});
    const [propNotes, setPropNotes] = useState({});

    // Summary stage filter state
    const [summaryStageFilter, setSummaryStageFilter] = useState('all');

    // Sorting and Pagination State
    const [sortField, setSortField] = useState('estimatedCloseDate');
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // Sorting handler
    const handleSort = (field) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
      setCurrentPage(1);
    };

    // Filter to show only proposals (all proposal-related stages)
    const proposalStages = ['Proposal', 'Follow-up', 'Contract', 'Closed-Lost', 'Closed-Won'];

    // Get proposals from opportunities, filtered by user's geography access
    const proposals = opportunities.filter(opp => {
      // User Geography Access Control — non-admin users only see their assigned geographies
      const matchesUserGeography = currentUser?.isAdmin ||
        (currentUser?.geographies?.length > 0 &&
         opp.geography &&
         currentUser.geographies.includes(opp.geography));
      return matchesUserGeography && proposalStages.includes(opp.salesStage);
    });

    // Get unique values for filters from proposals
    const uniqueOwners = [...new Set(proposals.map(p => p.accountOwner).filter(Boolean))];
    const uniqueGeographies = [...new Set(proposals.map(p => p.geography).filter(Boolean))];
    const uniqueServiceLines = [...new Set(proposals.map(p => p.serviceLine).filter(Boolean))];
    const uniqueSalesStages = [...new Set(proposals.map(p => p.salesStage).filter(Boolean))];

    // Apply filters
    const filteredProposals = proposals.filter(prop => {
      const matchesSearch = prop.opportunityName?.toLowerCase().includes(searchProposal.toLowerCase()) ||
                          prop.associatedAccount?.toLowerCase().includes(searchProposal.toLowerCase());
      const matchesOwner = filterOwner === 'all' || prop.accountOwner === filterOwner;
      const matchesGeography = filterGeography === 'all' || prop.geography === filterGeography;
      const matchesServiceLine = filterServiceLine === 'all' || prop.serviceLine === filterServiceLine;
      const matchesSalesStage = filterSalesStage === 'all' || prop.salesStage === filterSalesStage;

      // Apply summary stage filter
      const matchesSummaryStage = summaryStageFilter === 'all' || prop.salesStage === summaryStageFilter;

      return matchesSearch && matchesOwner && matchesGeography &&
             matchesServiceLine && matchesSalesStage && matchesSummaryStage;
    });

    // Sort proposals
    const sortedProposals = [...filteredProposals].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'estimatedCloseDate' || sortField === 'initiationDate') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (sortField === 'annualContractValue' || sortField === 'totalContractValue') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Pagination
    const totalPages = Math.ceil(sortedProposals.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedProposals = sortedProposals.slice(startIndex, endIndex);

    const togglePropExpansion = (propId) => {
      setExpandedProps(prev => ({
        ...prev,
        [propId]: !prev[propId]
      }));
    };

    const onEditProposal = (proposal) => {
      setSelectedOpportunity(proposal);
      setShowOpportunityModal(true);
    };

    const onDeleteProposal = async (proposal) => {
      if (window.confirm(`Are you sure you want to mark proposal "${proposal.opportunityName}" as Closed-Lost?`)) {
        const now = new Date();
        const updatedOpp = {
          ...proposal,
          salesStage: 'Closed-Lost',
          actualCloseDate: now.toISOString().split('T')[0],
          lastEditedDate: now.toISOString().split('T')[0]
        };
        setOpportunities(opportunities.map(o =>
          o.opportunityId === proposal.opportunityId ? updatedOpp : o
        ));

        // Save to database
        try {
          const payload = {
            opportunity_name: updatedOpp.opportunityName,
            associated_account: updatedOpp.associatedAccount,
            sales_stage: updatedOpp.salesStage,
            probability: String(updatedOpp.probabilityPercent || '0% - 35%'),
            estimated_revenue: updatedOpp.estimatedRevenue,
            annual_contract_value: updatedOpp.annualContractValue,
            total_contract_value: updatedOpp.totalContractValue,
            service_line: updatedOpp.serviceLine,
            service_offering: updatedOpp.serviceOffering,
            entity: updatedOpp.entity,
            geography: updatedOpp.geography,
            industry: updatedOpp.industry,
            opportunity_type: updatedOpp.opportunityType,
            opportunity_category: updatedOpp.opportunityCategory || 'New Business',
            opportunity_classification: updatedOpp.opportunityClassification || null,
            nature_of_work: updatedOpp.natureOfWork || null,
            priority: updatedOpp.priorityLevel,
            status: updatedOpp.salesStatus || 'Warm',
            sales_status_override: updatedOpp.salesStatusOverride || null,
            account_owner: updatedOpp.accountOwner,
            lead_supporter: updatedOpp.leadSupporter,
            initiation_date: updatedOpp.initiationDate,
            expected_close_date: updatedOpp.estimatedCloseDate || null,
            actual_close_date: updatedOpp.actualCloseDate || null,
            win_loss_reason: updatedOpp.winLossReason || null,
            engagement_history: updatedOpp.engagementHistory || null,
            comments: updatedOpp.comments || null,
            latest_comment: updatedOpp.latestComment || null,
            last_edited_date: updatedOpp.lastEditedDate,
            lead_history: updatedOpp.leadHistory || null,
            source_lead_id: updatedOpp.sourceLeadId || null
          };

          await fetch(`${API_BASE_URL}/opportunities/${proposal.opportunityId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        } catch (error) {
          console.error('Error saving to database:', error);
        }

        alert('Proposal marked as Closed-Lost!');
      }
    };

    const onMarkAsWon = async (proposal) => {
      if (window.confirm(`Mark proposal "${proposal.opportunityName}" as Closed-Won and create a customer?`)) {
        // Mark opportunity as Closed-Won
        const now = new Date();
        const updatedOpp = {
          ...proposal,
          salesStage: 'Closed-Won',
          actualCloseDate: now.toISOString().split('T')[0],
          actualRevenue: proposal.annualContractValue,
          lastEditedDate: now.toISOString().split('T')[0]
        };
        setOpportunities(opportunities.map(o =>
          o.opportunityId === proposal.opportunityId ? updatedOpp : o
        ));

        // Save to database
        try {
          const payload = {
            opportunity_name: updatedOpp.opportunityName,
            associated_account: updatedOpp.associatedAccount,
            sales_stage: updatedOpp.salesStage,
            probability: String(updatedOpp.probabilityPercent || '0% - 35%'),
            estimated_revenue: updatedOpp.estimatedRevenue,
            annual_contract_value: updatedOpp.annualContractValue,
            total_contract_value: updatedOpp.totalContractValue,
            service_line: updatedOpp.serviceLine,
            service_offering: updatedOpp.serviceOffering,
            entity: updatedOpp.entity,
            geography: updatedOpp.geography,
            industry: updatedOpp.industry,
            opportunity_type: updatedOpp.opportunityType,
            opportunity_category: updatedOpp.opportunityCategory || 'New Business',
            opportunity_classification: updatedOpp.opportunityClassification || null,
            nature_of_work: updatedOpp.natureOfWork || null,
            priority: updatedOpp.priorityLevel,
            status: updatedOpp.salesStatus || 'Warm',
            sales_status_override: updatedOpp.salesStatusOverride || null,
            account_owner: updatedOpp.accountOwner,
            lead_supporter: updatedOpp.leadSupporter,
            initiation_date: updatedOpp.initiationDate,
            expected_close_date: updatedOpp.estimatedCloseDate || null,
            actual_close_date: updatedOpp.actualCloseDate || null,
            win_loss_reason: updatedOpp.winLossReason || null,
            engagement_history: updatedOpp.engagementHistory || null,
            comments: updatedOpp.comments || null,
            latest_comment: updatedOpp.latestComment || null,
            last_edited_date: updatedOpp.lastEditedDate,
            lead_history: updatedOpp.leadHistory || null,
            source_lead_id: updatedOpp.sourceLeadId || null
          };

          await fetch(`${API_BASE_URL}/opportunities/${proposal.opportunityId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        } catch (error) {
          console.error('Error saving to database:', error);
        }

        // Create new customer from opportunity data
        const newCustomerId = `CUST-${String(customers.length + 1).padStart(3, '0')}`;
        const startDate = now.toISOString().split('T')[0];
        const renewalDateObj = new Date(now);
        renewalDateObj.setFullYear(renewalDateObj.getFullYear() + 1);
        const renewalDate = renewalDateObj.toISOString().split('T')[0];

        const newCustomer = {
          customerId: newCustomerId,
          companyName: proposal.associatedAccount || proposal.accountName || 'Unknown Company',
          primaryContact: proposal.primaryContact || '',
          email: proposal.primaryContactEmail || proposal.email || '',
          phone: proposal.phone || '',
          serviceLine: proposal.serviceLine || '',
          geography: proposal.geography || '',
          contractValue: proposal.annualContractValue || 0,
          startDate: startDate,
          renewalDate: renewalDate,
          status: 'Active',
          healthScore: 100,
          projectCount: 1,
          accountManager: proposal.accountOwner || '',
          originatingOpportunityId: proposal.opportunityId,
          industryVertical: proposal.industryVertical || '',
          lastActivityDate: startDate,
          lastEditedDate: startDate
        };

        // Set the new customer as selected to open the modal with pre-filled data
        setSelectedCustomer(newCustomer);
        setShowCustomerModal(true);
      }
    };

    const getActionsForStage = (stage) => {
      const actionsByStage = {
        'Proposal': [
          { value: 'submit_proposal', label: 'Submit Proposal' },
          { value: 'review_proposal', label: 'Review Proposal' },
          { value: 'schedule_presentation', label: 'Schedule Presentation' },
          { value: 'update_proposal', label: 'Update Proposal' }
        ],
        'Follow-up': [
          { value: 'follow_up_call', label: 'Follow-up Call' },
          { value: 'follow_up_email', label: 'Follow-up Email' },
          { value: 'schedule_meeting', label: 'Schedule Meeting' },
          { value: 'answer_questions', label: 'Answer Questions' },
          { value: 'clarify_queries', label: 'Clarify Queries' }
        ],
        'Contract': [
          { value: 'negotiate_terms', label: 'Negotiate Terms' },
          { value: 'finalize_pricing', label: 'Finalize Pricing' },
          { value: 'legal_review', label: 'Legal Review' },
          { value: 'contract_archive', label: 'Archive Contract' },
          { value: 'delivery_handoff', label: 'Final Delivery Handoff' }
        ],
        'Closed-Won': [
          { value: 'handover', label: 'Handover to Delivery' },
          { value: 'onboarding', label: 'Start Onboarding' },
          { value: 'schedule_kickoff', label: 'Schedule Kickoff' }
        ],
        'Closed-Lost': [
          { value: 'request_feedback', label: 'Request Loss Feedback' },
          { value: 'competitor_analysis', label: 'Competitor Analysis' },
          { value: 'add_nurture', label: 'Add to Nurture' }
        ]
      };

      return actionsByStage[stage] || [
        { value: 'update_status', label: 'Update Status' },
        { value: 'add_note', label: 'Add Note' },
        { value: 'follow_up', label: 'Follow Up' }
      ];
    };

    const handlePropActionSubmit = async (propId, propData) => {
      const action = propActions[propId];
      const note = propNotes[propId];

      if (!note || note.trim() === '') {
        alert('Please enter a note');
        return;
      }

      const now = new Date();
      const isoTimestamp = now.toISOString();

      let historyEntry;
      if (action) {
        const actionLabel = getActionsForStage(propData.salesStage).find(a => a.value === action)?.label || action;
        historyEntry = `[${isoTimestamp}] ${actionLabel}: ${note}`;
      } else {
        historyEntry = `[${isoTimestamp}] Note: ${note}`;
      }

      const updatedProp = {
        ...propData,
        engagementHistory: `${propData.engagementHistory || ''}\n${historyEntry}`,
        comments: note,
        lastEditedDate: now.toISOString().split('T')[0]
      };

      setOpportunities(opportunities.map(o => o.opportunityId === propId ? updatedProp : o));

      // Save to database
      try {
        const payload = {
          opportunity_name: updatedProp.opportunityName,
          associated_account: updatedProp.associatedAccount,
          sales_stage: updatedProp.salesStage,
          probability: String(updatedProp.probabilityPercent || '0% - 35%'),
          estimated_revenue: updatedProp.estimatedRevenue,
          annual_contract_value: updatedProp.annualContractValue,
          total_contract_value: updatedProp.totalContractValue,
          service_line: updatedProp.serviceLine,
          service_offering: updatedProp.serviceOffering,
          entity: updatedProp.entity,
          geography: updatedProp.geography,
          industry: updatedProp.industry,
          opportunity_type: updatedProp.opportunityType,
          opportunity_category: updatedProp.opportunityCategory || 'New Business',
          opportunity_classification: updatedProp.opportunityClassification || null,
          nature_of_work: updatedProp.natureOfWork || null,
          priority: updatedProp.priorityLevel,
          status: updatedProp.salesStatus || 'Warm',
          sales_status_override: updatedProp.salesStatusOverride || null,
          account_owner: updatedProp.accountOwner,
          lead_supporter: updatedProp.leadSupporter,
          initiation_date: updatedProp.initiationDate,
          expected_close_date: updatedProp.estimatedCloseDate || null,
          actual_close_date: updatedProp.actualCloseDate || null,
          win_loss_reason: updatedProp.winLossReason || null,
          engagement_history: updatedProp.engagementHistory || null,
          comments: updatedProp.comments || null,
          latest_comment: note || null,
          last_edited_date: updatedProp.lastEditedDate,
          lead_history: updatedProp.leadHistory || null,
          source_lead_id: updatedProp.sourceLeadId || null
        };

        await fetch(`${API_BASE_URL}/opportunities/${propId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (error) {
        console.error('Error saving to database:', error);
      }

      setPropActions(prev => ({...prev, [propId]: ''}));
      setPropNotes(prev => ({...prev, [propId]: ''}));

      alert('Note logged successfully!');
    };

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-sm font-bold">Proposals Management</h2>
            <p className="text-sm text-gray-500">Track proposals and negotiations</p>
          </div>
        </div>

        {/* Filters Section */}
        <Card className="p-3">
          <div className="grid grid-cols-6 gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Search proposals..."
                value={searchProposal}
                onChange={(e) => setSearchProposal(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded-md"
              />
            </div>

            <select
              value={filterOwner}
              onChange={(e) => setFilterOwner(e.target.value)}
              className="px-2 py-1.5 text-xs border border-gray-300 rounded-md"
            >
              <option value="all">All Owners</option>
              {uniqueOwners.map(owner => (
                <option key={owner} value={owner}>{owner}</option>
              ))}
            </select>

            <select
              value={filterGeography}
              onChange={(e) => setFilterGeography(e.target.value)}
              className="px-2 py-1.5 text-xs border border-gray-300 rounded-md"
            >
              <option value="all">All Geographies</option>
              {uniqueGeographies.map(geo => (
                <option key={geo} value={geo}>{geo}</option>
              ))}
            </select>

            <select
              value={filterServiceLine}
              onChange={(e) => setFilterServiceLine(e.target.value)}
              className="px-2 py-1.5 text-xs border border-gray-300 rounded-md"
            >
              <option value="all">All Service Lines</option>
              {uniqueServiceLines.map(sl => (
                <option key={sl} value={sl}>{sl}</option>
              ))}
            </select>

            <select
              value={filterSalesStage}
              onChange={(e) => setFilterSalesStage(e.target.value)}
              className="px-2 py-1.5 text-xs border border-gray-300 rounded-md"
            >
              <option value="all">All Stages</option>
              {uniqueSalesStages.map(stage => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>
          </div>

          <div className="mt-2 flex items-center justify-end">
            <button
              onClick={() => {
                setFilterOwner('all');
                setFilterGeography('all');
                setFilterServiceLine('all');
                setFilterSalesStage('all');
                setSearchProposal('');
                setSummaryStageFilter('all');
              }}
              className="text-xs text-pierian hover:underline"
            >
              Clear All Filters
            </button>
          </div>
        </Card>

        {/* Summary Section */}
        <Card className="p-3">
          {(() => {
            // Calculate proposals WITHOUT summary stage filter for accurate counts
            const summaryProposals = proposals.filter(prop => {
              const matchesSearch = prop.opportunityName?.toLowerCase().includes(searchProposal.toLowerCase()) ||
                                  prop.associatedAccount?.toLowerCase().includes(searchProposal.toLowerCase());
              const matchesOwner = filterOwner === 'all' || prop.accountOwner === filterOwner;
              const matchesGeography = filterGeography === 'all' || prop.geography === filterGeography;
              const matchesServiceLine = filterServiceLine === 'all' || prop.serviceLine === filterServiceLine;
              const matchesSalesStage = filterSalesStage === 'all' || prop.salesStage === filterSalesStage;

              return matchesSearch && matchesOwner && matchesGeography &&
                     matchesServiceLine && matchesSalesStage;
            });

            // Calculate overall totals
            const overallTotal = summaryProposals.length;
            const overallValue = summaryProposals.reduce((sum, prop) => sum + (prop.annualContractValue || 0), 0);

            // Define proposal stages
            const proposalStages = [
              { stage: 'Proposal', displayName: 'Proposal', color: 'bg-amber-tint' },
              { stage: 'Follow-up', displayName: 'Follow-up', color: 'bg-pierian-10' },
              { stage: 'Contract', displayName: 'Contract', color: 'bg-emerald-100' },
              { stage: 'Closed-Won', displayName: 'Closed-Won', color: 'bg-sage-tint' },
              { stage: 'Closed-Lost', displayName: 'Closed-Lost', color: 'bg-pierian-20' }
            ];

            // Calculate stage totals
            const stageCounts: { [key: string]: { count: number; value: number } } = {};
            summaryProposals.forEach(prop => {
              const stage = prop.salesStage || 'Unknown';
              if (!stageCounts[stage]) {
                stageCounts[stage] = { count: 0, value: 0 };
              }
              stageCounts[stage].count++;
              stageCounts[stage].value += (prop.annualContractValue || 0);
            });

            // Prepare stages with counts (show all stages even if 0)
            const stagesWithData = proposalStages.map(({ stage, displayName, color }) => {
              const stageData = stageCounts[stage] || { count: 0, value: 0 };
              const percentage = overallTotal > 0 ? (stageData.count / overallTotal * 100).toFixed(0) : 0;
              return {
                stage,
                displayName,
                color,
                count: stageData.count,
                value: stageData.value,
                percentage
              };
            });

            return (
              <div className="flex gap-3 items-center">
                {/* ALL Box - Clickable */}
                <button
                  onClick={() => setSummaryStageFilter('all')}
                  className={`border rounded-lg p-3 min-w-[120px] text-left transition-all ${
                    summaryStageFilter === 'all'
                      ? 'bg-pierian text-white border-scarlet shadow-lg'
                      : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                  }`}
                >
                  <p className={`text-xs font-semibold mb-1 ${
                    summaryStageFilter === 'all' ? 'text-slate-tint' : 'text-gray-600'
                  }`}>Open</p>
                  <div className="flex justify-between items-center">
                    <span className={`text-2xl font-bold ${
                      summaryStageFilter === 'all' ? 'text-white' : 'text-gray-900'
                    }`}>{overallTotal}</span>
                    <span className={`text-sm font-semibold ${
                      summaryStageFilter === 'all' ? 'text-white' : 'text-sage'
                    }`}>₹{overallValue >= 1000000 ? (overallValue / 1000000).toFixed(1) + 'M' : (overallValue / 1000).toFixed(0) + 'K'}</span>
                  </div>
                </button>

                {/* Stage Breakup - Single Row - Clickable */}
                <div className="flex gap-2 flex-1">
                  {stagesWithData.map(({ stage, displayName, color, count, value, percentage }) => {
                    const isSelected = summaryStageFilter === stage;

                    return (
                      <React.Fragment key={stage}>
                        {stage === 'Closed-Won' && (
                          <div className="w-px bg-gray-300 self-stretch mx-1"></div>
                        )}
                        <button
                          onClick={() => setSummaryStageFilter(stage)}
                          className={`${isSelected ? 'ring-2 ring-blue-600 ' + color : color} rounded-lg p-2.5 flex-1 min-w-[100px] text-left hover:shadow-md transition-all ${
                            isSelected ? 'shadow-lg' : ''
                          }`}
                        >
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium text-gray-700">{displayName}</span>
                            <span className="text-lg font-bold text-gray-900">{count}</span>
                          </div>
                          <div className="flex justify-between items-center text-xs text-gray-600">
                            <span className="font-medium">{percentage}%</span>
                            <span className="font-semibold text-sage">₹{value >= 1000000 ? (value / 1000000).toFixed(1) + 'M' : (value / 1000).toFixed(0) + 'K'}</span>
                          </div>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </Card>

        {/* Proposals Table */}
        <Card>
          <div className="w-full overflow-auto">
            <table className="w-full text-sm" style={{tableLayout: 'fixed'}}>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-10 px-2 py-3 text-left"></th>
                  <th className="w-48 px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('opportunityName')}
                      className="flex items-center gap-1 font-semibold text-gray-700 hover:text-gray-900"
                    >
                      Proposal Name
                      {sortField === 'opportunityName' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="w-48 px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('associatedAccount')}
                      className="flex items-center gap-1 font-semibold text-gray-700 hover:text-gray-900"
                    >
                      Company Name
                      {sortField === 'associatedAccount' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="w-28 px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('salesStage')}
                      className="flex items-center gap-1 font-semibold text-gray-700 hover:text-gray-900"
                    >
                      Stage
                      {sortField === 'salesStage' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="w-28 px-4 py-3 text-right">
                    <button
                      onClick={() => handleSort('annualContractValue')}
                      className="flex items-center justify-end gap-1 font-semibold text-gray-700 hover:text-gray-900 w-full"
                    >
                      Amount (ACV)
                      {sortField === 'annualContractValue' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="w-28 px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('initiationDate')}
                      className="flex items-center gap-1 font-semibold text-gray-700 hover:text-gray-900"
                      title="Shows Age for active opportunities, Close Date for Won/Lost"
                    >
                      Age/Close
                      {sortField === 'initiationDate' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="w-28 px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('initiationDate')}
                      className="flex items-center gap-1 font-semibold text-gray-700 hover:text-gray-900"
                    >
                      Create Date
                      {sortField === 'initiationDate' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="w-24 px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('probabilityPercent')}
                      className="flex items-center gap-1 font-semibold text-gray-700 hover:text-gray-900"
                    >
                      Probability
                      {sortField === 'probabilityPercent' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="w-32 px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('accountOwner')}
                      className="flex items-center gap-1 font-semibold text-gray-700 hover:text-gray-900"
                    >
                      Owner
                      {sortField === 'accountOwner' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </th>
                  <th className="w-32 px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedProposals.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-sm text-gray-500">
                      No proposals found. Opportunities with "Proposal" or "Negotiation" stage will appear here.
                    </td>
                  </tr>
                ) : (
                  paginatedProposals.map((prop) => (
                    <React.Fragment key={prop.opportunityId}>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-2 py-2">
                          <button
                            onClick={() => togglePropExpansion(prop.opportunityId)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            {expandedProps[prop.opportunityId] ? (
                              <ChevronUp size={16} className="text-gray-600" />
                            ) : (
                              <ChevronDown size={16} className="text-gray-600" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium text-gray-900">{prop.opportunityName}</span>
                            <div className="flex items-center gap-2">
                              {(() => {
                                const lastEditedDate = prop.lastEditedDate ? new Date(prop.lastEditedDate) : new Date(prop.initiationDate);
                                const today = new Date();
                                const daysSinceUpdate = Math.floor((today - lastEditedDate) / (1000 * 60 * 60 * 24));

                                return (
                                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                                    daysSinceUpdate > 15 ? 'bg-red-50 text-red-600' :
                                    daysSinceUpdate > 7 ? 'bg-amber-tint text-amber-shade' :
                                    'bg-sage-tint text-sage-shade'
                                  }`}>
                                    {daysSinceUpdate === 0 ? 'Today' :
                                     daysSinceUpdate === 1 ? '1 day ago' :
                                     `${daysSinceUpdate} days ago`}
                                  </span>
                                );
                              })()}
                              <span className="text-xs text-gray-600 italic truncate">
                                {prop.comments || 'No recent updates'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <span className="text-gray-900">{prop.associatedAccount || prop.accountName || 'No Account'}</span>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            prop.salesStage === 'Closed-Won' ? 'bg-sage-tint text-sage-shade' :
                            prop.salesStage === 'Closed-Lost' ? 'bg-red-100 text-red-600' :
                            prop.salesStage === 'Contract' ? 'bg-emerald-100 text-emerald-700' :
                            prop.salesStage === 'Contract' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-pierian-10 text-scarlet'
                          }`}>
                            {prop.salesStage}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-semibold text-sage-shade">
                          ${(prop.annualContractValue || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-gray-700">
                          {prop.salesStage === 'Closed-Won' || prop.salesStage === 'Closed-Lost' ? (
                            <span title={prop.estimatedCloseDate ? new Date(prop.estimatedCloseDate).toLocaleDateString() : ''}>
                              {prop.estimatedCloseDate ? new Date(prop.estimatedCloseDate).toLocaleDateString() : ''}
                            </span>
                          ) : (
                            (() => {
                              const ageDays = prop.initiationDate ? Math.floor((new Date() - new Date(prop.initiationDate)) / (1000 * 60 * 60 * 24)) : 0;
                              return (
                                <span
                                  title={prop.initiationDate ? `Created: ${new Date(prop.initiationDate).toLocaleDateString()}` : ''}
                                  className={ageDays > 120 ? 'text-pierian font-semibold' : ''}
                                >
                                  {prop.initiationDate ? `${ageDays} Days` : ''}
                                </span>
                              );
                            })()
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-700">
                          {prop.initiationDate ? new Date(prop.initiationDate).toLocaleDateString() : ''}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            prop.probabilityToClose === 'High' ? 'bg-sage-tint text-sage-shade' :
                            prop.probabilityToClose === 'Medium' ? 'bg-amber-tint text-amber-shade' :
                            'bg-red-100 text-red-600'
                          }`}>
                            {sanitizeProbabilityPercent(prop.probabilityPercent)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-700">{prop.accountOwner}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => onEditProposal(prop)}
                              className="p-1 text-pierian hover:bg-pierian-10 rounded"
                              title="Edit Proposal"
                            >
                              <Edit size={16} />
                            </button>
                            {prop.salesStage !== 'Closed-Won' && prop.salesStage !== 'Closed-Lost' && (
                              <>
                                <button
                                  onClick={() => onMarkAsWon(prop)}
                                  className="p-1 text-sage hover:bg-sage-tint rounded"
                                  title="Mark as Won"
                                >
                                  <CheckCircle size={16} />
                                </button>
                                <button
                                  onClick={() => onDeleteProposal(prop)}
                                  className="p-1 text-pierian hover:bg-teal-50 rounded"
                                  title="Mark as Lost"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row */}
                      {expandedProps[prop.opportunityId] && (
                        <tr className="bg-gray-50">
                          <td colSpan={10} className="px-3 py-4" style={{maxWidth: '0', overflow: 'hidden'}}>
                            <div className="grid grid-cols-[2fr_1fr] gap-6 w-full" style={{minWidth: 0}}>
                              {/* Left Column - Details */}
                              <div className="space-y-4 min-w-0 overflow-hidden">
                                {/* Quick Actions */}
                                <div className="bg-white p-3 rounded-lg border border-gray-200">
                                  <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1 text-xs">
                                    <Activity size={14} className="text-pierian" /> Quick Actions
                                  </p>
                                  <div className="space-y-2">
                                    <div className="flex flex-col gap-2">
                                      <input
                                        type="text"
                                        value={propNotes[prop.opportunityId] || ''}
                                        onChange={(e) => setPropNotes(prev => ({...prev, [prop.opportunityId]: e.target.value}))}
                                        placeholder="Enter notes..."
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                      />
                                      <div className="flex gap-2">
                                        <select
                                          value={propActions[prop.opportunityId] || ''}
                                          onChange={(e) => setPropActions(prev => ({...prev, [prop.opportunityId]: e.target.value}))}
                                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                        >
                                          <option value="">Select Action (Optional)</option>
                                          {getActionsForStage(prop.salesStage).map(action => (
                                            <option key={action.value} value={action.value}>
                                              {action.label}
                                            </option>
                                          ))}
                                        </select>
                                        <button
                                          onClick={() => handlePropActionSubmit(prop.opportunityId, prop)}
                                          className="px-4 py-1 bg-pierian text-white rounded hover:bg-scarlet text-xs whitespace-nowrap"
                                        >
                                          Log
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 text-xs" style={{minWidth: 0}}>
                                  <div className="bg-white p-3 rounded-lg border border-gray-200 min-w-0 overflow-hidden">
                                    <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                      <Building size={14} className="text-pierian" /> Business Details
                                    </p>
                                    <div className="space-y-1">
                                      <p><span className="text-gray-500">Industry:</span> <span className="font-medium">{prop.industryVertical}</span></p>
                                      <p><span className="text-gray-500">Type:</span> <span className="font-medium">{prop.opportunityType}</span></p>
                                      <p><span className="text-gray-500">Category:</span> <span className="font-medium">{prop.opportunityCategory || '-'}</span></p>
                                      {(prop.opportunityClassification || prop.opportunityCategory === 'New Business') && (
                                        <p><span className="text-gray-500">Classification:</span> <span className="font-medium">{prop.opportunityClassification || '-'}</span></p>
                                      )}
                                      <p><span className="text-gray-500">Geography:</span> <span className="font-medium">{prop.geography}</span></p>
                                    </div>
                                  </div>
                                  <div className="bg-white p-3 rounded-lg border border-gray-200 min-w-0 overflow-hidden">
                                    <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                      <IndianRupee size={14} className="text-sage" /> Financial
                                    </p>
                                    <div className="space-y-1">
                                      <p><span className="text-gray-500">ACV:</span> <span className="font-medium">₹{(prop.annualContractValue || 0).toLocaleString()}</span></p>
                                      <p><span className="text-gray-500">TCV:</span> <span className="font-medium">₹{(prop.totalContractValue || 0).toLocaleString()}</span></p>
                                      <p><span className="text-gray-500">Probability:</span> <span className="font-medium">{prop.probabilityToClose}</span></p>
                                      <p><span className="text-gray-500">Forecast:</span> <span className="font-medium">₹{(prop.probabilityValue || 0).toLocaleString()}</span></p>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-white p-3 rounded-lg border border-gray-200 text-xs min-w-0 overflow-hidden">
                                  <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                    <Users size={14} className="text-amethyst" /> Team & Contacts
                                  </p>
                                  <div className="space-y-1">
                                    <p><span className="text-gray-500">Account Owner:</span> <span className="font-medium">{prop.accountOwner}</span></p>
                                    <p><span className="text-gray-500">Lead Supporter:</span> <span className="font-medium">{prop.leadSupporter || '-'}</span></p>
                                    <p><span className="text-gray-500">Executive Sponsor:</span> <span className="font-medium">{prop.executiveSponsor || '-'}</span></p>
                                    <p><span className="text-gray-500">Primary Contact:</span> <span className="font-medium">{prop.primaryContact || '-'}</span></p>
                                  </div>
                                </div>
                                {prop.solutionScope && (
                                  <div className="bg-white p-3 rounded-lg border border-gray-200 text-xs w-full" style={{maxWidth: '100%', overflow: 'auto'}}>
                                    <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                      <FileText size={14} className="text-orange-600" /> Solution Scope
                                    </p>
                                    <p className="text-gray-600 leading-relaxed w-full" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowWrap: 'anywhere'}}>{prop.solutionScope}</p>
                                  </div>
                                )}
                              </div>

                              {/* Right Column - Engagement History Timeline */}
                              <div className="min-w-0 overflow-hidden">
                                <div className="bg-white p-2 rounded-lg border border-gray-200">
                                  <p className="font-semibold text-gray-700 mb-1.5 flex items-center gap-1 text-xs">
                                    <Clock size={12} className="text-pierian" />
                                    Engagement History
                                  </p>
                                  <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                                    {(() => {
                                      // Generate timeline events from opportunity data
                                      const timelineEvents = [];

                                      // Add stage changes as timeline events
                                      if (prop.initiationDate) {
                                        timelineEvents.push({
                                          date: prop.initiationDate,
                                          action: 'Opportunity Created',
                                          description: `Opportunity initiated in ${prop.salesStage}`,
                                          user: prop.accountOwner,
                                          icon: Plus,
                                          color: 'blue'
                                        });
                                      }

                                      // Add proposal submission event
                                      if (prop.salesStage.includes('Proposal')) {
                                        const estimatedCloseDateFormatted = prop.estimatedCloseDate ?
                                          new Date(prop.estimatedCloseDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          }) : '';
                                        const estimatedCloseDateText = estimatedCloseDateFormatted ? ` (Est. Close: ${estimatedCloseDateFormatted})` : '';
                                        // Use lastEditedDate as the main display date if available
                                        const displayDate = prop.lastEditedDate || prop.initiationDate;
                                        timelineEvents.push({
                                          date: displayDate,
                                          action: 'Proposal Submitted',
                                          description: `Proposal submitted for review - ACV: ₹${(prop.annualContractValue || 0).toLocaleString()}${estimatedCloseDateText}`,
                                          user: prop.leadSupporter || prop.accountOwner,
                                          icon: Send,
                                          color: 'green'
                                        });
                                      }

                                      // Add contract event
                                      if (prop.salesStage === 'Contract' || prop.salesStage === 'Closed-Won') {
                                        const estimatedCloseDateFormatted = prop.estimatedCloseDate ?
                                          new Date(prop.estimatedCloseDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          }) : 'N/A';
                                        // Use lastEditedDate as the display date
                                        const displayDate = prop.lastEditedDate || prop.initiationDate;
                                        timelineEvents.push({
                                          date: displayDate,
                                          action: 'Contract Phase',
                                          description: `Entered contract phase with decision makers. Estimated Close Date - ${estimatedCloseDateFormatted}`,
                                          user: prop.executiveSponsor || prop.accountOwner,
                                          icon: MessageSquare,
                                          color: 'yellow'
                                        });
                                      }

                                      // Add engagement history entries if available
                                      if (prop.engagementHistory) {
                                        const historyEntries = prop.engagementHistory
                                          .split('\n')
                                          .filter(entry => entry.trim());

                                        historyEntries.forEach(entry => {
                                          const timestampMatch = entry.match(/\[(.*?)\]/);
                                          let dateStr;
                                          let content;

                                          if (timestampMatch) {
                                            const timestamp = timestampMatch[1];
                                            content = entry.substring(entry.indexOf(']') + 1).trim();

                                            try {
                                              const entryDate = new Date(timestamp);
                                              if (!isNaN(entryDate.getTime())) {
                                                dateStr = entryDate.toISOString().split('T')[0];
                                              } else {
                                                dateStr = prop.initiationDate || new Date().toISOString().split('T')[0];
                                              }
                                            } catch (e) {
                                              dateStr = prop.initiationDate || new Date().toISOString().split('T')[0];
                                            }
                                          } else {
                                            // Entry without timestamp - use initiation date or today
                                            dateStr = prop.initiationDate || new Date().toISOString().split('T')[0];
                                            content = entry.trim();
                                          }

                                          let action = 'Engagement Update';
                                          let description = content;

                                          if (content && content.includes(':')) {
                                            const parts = content.split(':', 2);
                                            action = parts[0].trim();
                                            description = parts[1] ? parts[1].trim() : content;
                                          }

                                          if (content) {
                                            timelineEvents.push({
                                              date: dateStr,
                                              action: action,
                                              description: description,
                                              user: prop.accountOwner,
                                              icon: MessageSquare,
                                              color: 'purple'
                                            });
                                          }
                                        });
                                      }

                                      // Add closed won event
                                      if (prop.salesStage === 'Closed-Won' && prop.actualCloseDate) {
                                        timelineEvents.push({
                                          date: prop.actualCloseDate,
                                          action: 'Deal Won! 🎉',
                                          description: `Contract signed - Revenue: ₹${(prop.actualRevenue || prop.estimatedRevenue || 0).toLocaleString()}`,
                                          user: prop.accountOwner,
                                          icon: CheckCircle,
                                          color: 'green'
                                        });
                                      }

                                      // Add closed lost event
                                      if (prop.salesStage === 'Closed-Lost' && prop.actualCloseDate) {
                                        timelineEvents.push({
                                          date: prop.actualCloseDate,
                                          action: 'Deal Lost',
                                          description: prop.comments || 'Opportunity closed without conversion',
                                          user: prop.accountOwner,
                                          icon: XCircle,
                                          color: 'red'
                                        });
                                      }

                                      // Add upcoming close date
                                      if (prop.estimatedCloseDate && prop.salesStage !== 'Closed-Won' && prop.salesStage !== 'Closed-Lost') {
                                        timelineEvents.push({
                                          date: prop.estimatedCloseDate,
                                          action: 'Expected Close',
                                          description: `Target close date - ${prop.probabilityToClose} probability`,
                                          user: prop.accountOwner,
                                          icon: Target,
                                          color: 'orange'
                                        });
                                      }

                                      // Sort by date - most recent first
                                      timelineEvents.sort((a, b) => new Date(b.date) - new Date(a.date));

                                      // Render timeline
                                      return timelineEvents.length > 0 ? (
                                        timelineEvents.map((event, idx) => {
                                          const EventIcon = event.icon;
                                          const colorClasses = {
                                            blue: 'bg-pierian-10 text-pierian border-pierian-30',
                                            green: 'bg-sage-tint text-sage border-green-300',
                                            yellow: 'bg-amber-tint text-amber border-yellow-300',
                                            purple: 'bg-amethyst-tint text-amethyst border-purple-300',
                                            orange: 'bg-clay-tint text-orange-600 border-orange-300',
                                            red: 'bg-pierian-20 text-pierian border-red-300'
                                          };

                                          return (
                                            <div key={idx} className="flex gap-2 relative">
                                              {/* Timeline line */}
                                              {idx < timelineEvents.length - 1 && (
                                                <div className="absolute left-3.5 top-7 bottom-0 w-0.5 bg-gray-200" />
                                              )}

                                              {/* Icon */}
                                              <div className={`flex-shrink-0 w-7 h-7 rounded-full border flex items-center justify-center ${colorClasses[event.color]}`}>
                                                <EventIcon size={12} />
                                              </div>

                                              {/* Content */}
                                              <div className="flex-1 pb-2">
                                                <div className="flex items-center justify-between">
                                                  <p className="text-xs font-semibold text-gray-900">{event.action}</p>
                                                  <span className="text-[10px] text-gray-500">
                                                    {event.date ? (
                                                      <>
                                                        {new Date(event.date).toLocaleDateString('en-US', {
                                                          month: 'short',
                                                          day: 'numeric'
                                                        })}
                                                        {event.isEstimatedDate && <span className="text-[9px] ml-1">(Est. Close Date)</span>}
                                                      </>
                                                    ) : ''}
                                                  </span>
                                                </div>
                                                <p className="text-[11px] text-gray-600 leading-tight mt-0.5 w-full" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word'}} title={event.description}>{event.description}</p>
                                              </div>
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <div className="text-center py-8">
                                          <Clock size={32} className="mx-auto text-gray-300 mb-2" />
                                          <p className="text-sm text-gray-500">No engagement history available</p>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Comments Section */}
                            {prop.comments && (
                              <div className="mt-4 bg-white p-3 rounded-lg border border-gray-200 text-xs w-full" style={{maxWidth: '100%', overflow: 'auto'}}>
                                <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                  <MessageSquare size={14} className="text-gray-600" /> Additional Comments
                                </p>
                                <p className="text-gray-600 w-full" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowWrap: 'anywhere'}}>{prop.comments}</p>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to <span className="font-medium">{Math.min(endIndex, sortedProposals.length)}</span> of{' '}
              <span className="font-medium">{sortedProposals.length}</span> results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${
                  currentPage === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border'
                }`}
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${
                  currentPage === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border'
                }`}
              >
                Previous
              </button>
              <span className="px-4 py-1 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${
                  currentPage === totalPages
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border'
                }`}
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${
                  currentPage === totalPages
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border'
                }`}
              >
                Last
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  // Users View Component
  const UsersView = ({ onNewUser }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');

    const filteredUsers = users.filter(user => {
      const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.role.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
      return matchesSearch && matchesRole && matchesStatus;
    });

    const handleDelete = async (userId) => {
      if (!currentUser?.isAdmin) { alert('Only admins can delete users.'); return; }
      if (window.confirm('Are you sure you want to delete this user?')) {
        try {
          const response = await fetch(`${API_BASE_URL}/master/users/${userId}`, {
            method: 'DELETE',
          });

          if (response.ok) {
            setUsers(users.filter(u => u.userId !== userId));
            alert('User deleted successfully!');
          } else {
            const result = await response.json();
            alert(`Failed to delete user: ${result.message || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error deleting user:', error);
          alert(`Error deleting user: ${error.message}`);
        }
      }
    };

    const roles = [...new Set(users.map(u => u.role))];

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-sm font-bold text-gray-900">User Management</h2>
            <p className="text-xs text-gray-500 mt-0.5">{filteredUsers.length} users</p>
          </div>
          <Button onClick={onNewUser} variant="primary" className="flex items-center gap-2">
            <Plus size={16} />
            Add New User
          </Button>
        </div>

        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md"
              />
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md"
            >
              <option value="all">All Roles</option>
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </Card>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-3 text-xs font-semibold text-gray-600">User ID</th>
                  <th className="text-left p-3 text-xs font-semibold text-gray-600">Name</th>
                  <th className="text-left p-3 text-xs font-semibold text-gray-600">Email</th>
                  <th className="text-left p-3 text-xs font-semibold text-gray-600">Role</th>
                  <th className="text-left p-3 text-xs font-semibold text-gray-600">Manager</th>
                  <th className="text-left p-3 text-xs font-semibold text-gray-600">Team</th>
                  <th className="text-left p-3 text-xs font-semibold text-gray-600">Geographies</th>
                  <th className="text-left p-3 text-xs font-semibold text-gray-600">Status</th>
                  <th className="text-left p-3 text-xs font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-900 font-medium">{user.userId}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-pierian rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-medium">
                            {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-gray-600">{user.email}</td>
                    <td className="p-3 text-sm text-gray-600">{user.role}</td>
                    <td className="p-3 text-sm text-gray-600">{user.managerName || '-'}</td>
                    <td className="p-3 text-sm text-gray-600">
                      {Array.isArray(user.team) ? user.team.join(', ') : (user.team || '-')}
                    </td>
                    <td className="p-3">
                      {user.geographies && user.geographies.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.geographies.map((geo, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {geo}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        user.status === 'Active' ? 'bg-sage-tint text-sage-shade' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setUserFormServiceLines(user.serviceLines || []);
                            setShowUserModal(true);
                          }}
                          className="p-1 text-pierian hover:bg-pierian-10 rounded"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        {currentUser?.isAdmin && (
                          <button
                            onClick={() => handleDelete(user.userId)}
                            className="p-1 text-pierian hover:bg-teal-50 rounded"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  };

  // User Form Modal Component
  const UserFormModal = () => {
    const [geographyError, setGeographyError] = useState(false);
    const [formData, setFormData] = useState(selectedUser ? {
      ...selectedUser,
      geographies: selectedUser.geographies || [],
      serviceLines: selectedUser.serviceLines || [],
      isAdmin: selectedUser.isAdmin || false
    } : {
      userId: (() => {
        // Derive next ID from the actual max numeric suffix among existing users
        // (users.length+1 is unsafe when users have been deleted, causing gaps)
        const maxNum = users.reduce((max, u) => {
          const m = u.userId?.match(/^USER-(\d+)$/);
          return m ? Math.max(max, parseInt(m[1], 10)) : max;
        }, 0);
        return `USER-${String(maxNum + 1).padStart(3, '0')}`;
      })(),
      name: '',
      email: '',
      role: 'Sales',
      department: 'Sales',
      managerId: null,
      managerName: null,
      phone: '',
      status: 'Active',
      joinDate: new Date().toISOString().split('T')[0],
      team: ['North America Sales', 'Asia Pacific Sales', 'Europe Sales', 'Latin America Sales', 'Middle East Sales'],
      geographies: geographies?.filter(g => g.isActive).map(g => g.name) || [],
      serviceLines: [],
      isAdmin: false
    });

    const handleSubmit = async () => {
      if (!formData.name || !formData.email || !formData.phone) {
        alert('Please fill in all required fields (Name, Email, Phone)');
        return;
      }

      if (!formData.geographies || formData.geographies.length === 0) {
        setGeographyError(true);
        return;
      }

      // Update manager name if managerId is set
      if (formData.managerId) {
        const manager = users.find(u => u.userId === formData.managerId);
        formData.managerName = manager ? manager.name : null;
      } else {
        formData.managerName = null;
      }

      try {
        // Map frontend format to database format (camelCase to snake_case)
        const dbData = {
          user_id: formData.userId,
          name: formData.name,
          email: formData.email,
          role: formData.role,
          department: formData.department,
          manager_id: formData.managerId,
          manager_name: formData.managerName,
          phone: formData.phone,
          status: formData.status,
          join_date: formData.joinDate,
          team: JSON.stringify(Array.isArray(formData.team) ? formData.team : [formData.team]),
          geography: JSON.stringify(formData.geographies || []),
          service_lines: userFormServiceLines.length ? JSON.stringify(userFormServiceLines) : null,
          is_admin: formData.isAdmin ? 1 : 0
        };

        console.log('Sending user data to API:', dbData);

        let response;
        if (selectedUser) {
          // Update existing user
          response = await fetch(`${API_BASE_URL}/master/users/${selectedUser.userId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dbData)
          });
        } else {
          // Create new user
          response = await fetch(`${API_BASE_URL}/master/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dbData)
          });
        }

        const result = await response.json();
        console.log('API response:', result);

        if (result.success) {
          // Update local state — for new users, map the DB response to frontend format
          // so the real server-generated user_id is reflected (not the client-side preview)
          if (selectedUser) {
            setUsers(users.map(u => u.userId === selectedUser.userId ? { ...formData, serviceLines: userFormServiceLines } : u));
          } else {
            const dbUser = result.data;
            let geographies = [];
            if (dbUser?.geography && typeof dbUser.geography === 'string') {
              try { const p = JSON.parse(dbUser.geography); geographies = Array.isArray(p) ? p : [p]; }
              catch { geographies = [dbUser.geography]; }
            }
            const newUserMapped = {
              id: dbUser?.id,
              userId: dbUser?.user_id || formData.userId,
              name: dbUser?.name || formData.name,
              email: dbUser?.email || formData.email,
              role: dbUser?.role || formData.role,
              department: dbUser?.department || formData.department,
              phone: dbUser?.phone || formData.phone,
              team: dbUser?.team || formData.team,
              geographies,
              managerId: dbUser?.manager_id || formData.managerId,
              managerName: dbUser?.manager_name || formData.managerName,
              joinDate: dbUser?.join_date ? new Date(dbUser.join_date).toISOString().split('T')[0] : formData.joinDate,
              status: dbUser?.status || formData.status,
              isActive: true,
              isAdmin: dbUser?.is_admin === true || dbUser?.is_admin === 1 || formData.isAdmin
            };
            setUsers([...users, newUserMapped]);
          }
          setShowUserModal(false);
          setSelectedUser(null);
          setUserFormServiceLines([]);
          alert(`User ${selectedUser ? 'updated' : 'created'} successfully!`);
        } else {
          alert(`Failed to ${selectedUser ? 'update' : 'create'} user: ${result.error || result.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error saving user:', error);
        alert(`Error saving user: ${error.message}`);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">{selectedUser ? 'Edit User' : 'New User'}</h3>
              <button onClick={() => {setShowUserModal(false); setSelectedUser(null); setUserFormServiceLines([]);}} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID*</label>
                <input
                  type="text"
                  value={formData.userId}
                  disabled
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name*</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                  placeholder="email@pierian.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone*</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                  placeholder="+1-555-0000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role*</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                >
                  <option value="Service Line Leaders">Service Line Leaders</option>
                  <option value="Managing Partner">Managing Partner</option>
                  <option value="Sales">Sales</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department*</label>
                <select
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                >
                  <option value="Sales">Sales</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Operations">Operations</option>
                  <option value="Support">Support</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
                <select
                  value={formData.managerId || ''}
                  onChange={(e) => setFormData({...formData, managerId: e.target.value || null})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">No Manager (Top Level)</option>
                  {users
                    .filter(u => u.userId !== formData.userId)
                    .map(user => (
                      <option key={user.userId} value={user.userId}>
                        {user.name} ({user.role})
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team*</label>
                <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                  {['North America Sales', 'Asia Pacific Sales', 'Europe Sales', 'Latin America Sales', 'Middle East Sales'].map(t => (
                    <div key={t} className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id={`team-${t}`}
                        checked={Array.isArray(formData.team) ? formData.team.includes(t) : formData.team === t}
                        onChange={(e) => {
                          const current = Array.isArray(formData.team) ? formData.team : [formData.team];
                          const updated = e.target.checked ? [...current, t] : current.filter(v => v !== t);
                          setFormData({...formData, team: updated});
                        }}
                        className="w-4 h-4 text-[#00BBA2] border-gray-300 rounded focus:ring-[#00BBA2]"
                      />
                      <label htmlFor={`team-${t}`} className="text-sm text-gray-700 cursor-pointer">{t}</label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Geographies*
                  {geographyError && <span className="text-red-600 ml-2">Please select at least one geography</span>}
                </label>
                <div className={`border rounded-md p-3 max-h-40 overflow-y-auto ${geographyError ? 'border-red-500' : 'border-gray-300'}`}>
                  {geographies?.filter(g => g.isActive).length > 0 ? (
                    geographies.filter(g => g.isActive).map(geo => (
                      <div key={geo.id} className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id={`geo-${geo.id}`}
                          checked={formData.geographies?.includes(geo.name) || false}
                          onChange={(e) => {
                            const newGeographies = e.target.checked
                              ? [...(formData.geographies || []), geo.name]
                              : (formData.geographies || []).filter(g => g !== geo.name);
                            setFormData({...formData, geographies: newGeographies});
                            if (newGeographies.length > 0) {
                              setGeographyError(false);
                            }
                          }}
                          className="w-4 h-4 text-[#00BBA2] border-gray-300 rounded focus:ring-[#00BBA2]"
                        />
                        <label htmlFor={`geo-${geo.id}`} className="text-sm text-gray-700 cursor-pointer">
                          {geo.name}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No active geographies available</p>
                  )}
                </div>
                {!geographyError && <p className="text-xs text-gray-500 mt-1">Select geographies this user can work with</p>}
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Line Access
                </label>
                <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                  {serviceLines?.filter(s => s.isActive).length > 0 ? (
                    serviceLines.filter(s => s.isActive).map(sl => (
                      <div key={sl.id} className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id={`sl-${sl.id}`}
                          checked={userFormServiceLines.includes(sl.name)}
                          onChange={(e) => {
                            const newSL = e.target.checked
                              ? [...userFormServiceLines, sl.name]
                              : userFormServiceLines.filter(s => s !== sl.name);
                            setUserFormServiceLines(newSL);
                          }}
                          className="w-4 h-4 text-[#00BBA2] border-gray-300 rounded focus:ring-[#00BBA2]"
                        />
                        <label htmlFor={`sl-${sl.id}`} className="text-sm text-gray-700 cursor-pointer">
                          {sl.name}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No active service lines available</p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Leave unchecked to allow all service lines</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Join Date*</label>
                <input
                  type="date"
                  value={formData.joinDate}
                  onChange={(e) => setFormData({...formData, joinDate: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status*</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="col-span-2 flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="isAdmin"
                  checked={formData.isAdmin === true}
                  onChange={(e) => setFormData({...formData, isAdmin: e.target.checked})}
                  className="w-4 h-4 text-[#00BBA2] border-gray-300 rounded focus:ring-[#00BBA2]"
                />
                <label htmlFor="isAdmin" className="text-sm font-medium text-gray-700">
                  Is Admin (Can access Settings)
                </label>
              </div>
            </div>

          </div>

          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex justify-end gap-3">
            <Button
              onClick={() => {setShowUserModal(false); setSelectedUser(null); setUserFormServiceLines([]);}}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="primary">
              {selectedUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Settings Page Component (Admin Only)
  // Note: settingsTab state is defined at CRMApplication level to persist across re-renders
  const SettingsPage = () => {
    const [selectedRefData, setSelectedRefData] = useState(null);
    const [showRefDataModal, setShowRefDataModal] = useState(false);
    const [refDataType, setRefDataType] = useState('');

    const settingsTabs = [
      { id: 'users', label: 'Users', icon: User },
      { id: 'sales-stages', label: 'Sales Stages', icon: Target },
      { id: 'industries', label: 'Industries', icon: Building },
      { id: 'service-lines', label: 'Service Lines', icon: Briefcase },
      { id: 'service-offerings', label: 'Service Offerings', icon: Sparkles },
      { id: 'entities', label: 'Entities', icon: Building },
      { id: 'geographies', label: 'Geographies', icon: MapPin },
      { id: 'tags', label: 'Tags', icon: Tag },
      { id: 'data-management', label: 'Data Management', icon: Settings }
    ];

    // Reference Data Management Component
    const ReferenceDataView = ({ title, data, setData, type, hasRegion = false, hasServiceLine = false, hasEntityGroup = false }) => {
      const [search, setSearch] = useState('');
      const [sortField, setSortField] = useState('name');
      const [sortDirection, setSortDirection] = useState('asc');

      const handleSort = (field) => {
        if (sortField === field) {
          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
          setSortField(field);
          setSortDirection('asc');
        }
      };

      const filteredData = data.filter(item =>
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        (hasRegion && item.region?.toLowerCase().includes(search.toLowerCase())) ||
        (hasServiceLine && item.serviceLine?.toLowerCase().includes(search.toLowerCase())) ||
        (hasEntityGroup && item.entityGroup?.toLowerCase().includes(search.toLowerCase()))
      );

      const sortedData = [...filteredData].sort((a, b) => {
        let aVal, bVal;

        if (sortField === 'name') {
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
        } else if (sortField === 'region') {
          aVal = a.region?.toLowerCase() || '';
          bVal = b.region?.toLowerCase() || '';
        } else if (sortField === 'serviceLine') {
          aVal = a.serviceLine?.toLowerCase() || '';
          bVal = b.serviceLine?.toLowerCase() || '';
        } else if (sortField === 'entityGroup') {
          aVal = a.entityGroup?.toLowerCase() || '';
          bVal = b.entityGroup?.toLowerCase() || '';
        } else if (sortField === 'status') {
          aVal = a.isActive ? 1 : 0;
          bVal = b.isActive ? 1 : 0;
        }

        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });

      const handleAdd = () => {
        setRefDataType(type);
        setSelectedRefData(null);
        setShowRefDataModal(true);
      };

      const handleEdit = (item) => {
        console.log('✏️ handleEdit - Editing item:', item);
        console.log('   - item.serviceLine:', item.serviceLine);
        console.log('   - item.serviceLineId:', item.serviceLineId);
        setRefDataType(type);
        setSelectedRefData(item);
        setShowRefDataModal(true);
      };

      const handleDelete = async (id) => {
        if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
          try {
            const response = await fetch(`${API_BASE_URL}/master/${type}/${id}`, {
              method: 'DELETE'
            });
            const result = await response.json();

            if (result.success) {
              setData(data.filter(item => item.id !== id));
              alert('Deleted successfully!');
            } else {
              alert(`Failed to delete: ${result.error || result.message || 'Unknown error'}`);
            }
          } catch (error) {
            console.error('Error deleting:', error);
            alert(`Error deleting: ${error.message}`);
          }
        }
      };

      const handleToggleActive = async (id) => {
        const item = data.find(item => item.id === id);
        if (!item) return;

        try {
          const response = await fetch(`${API_BASE_URL}/master/${type}/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: item.name,
              is_active: !item.isActive ? 1 : 0,
              stage_order: item.order,
              region: item.region,
              entity_group: item.entityGroup
            })
          });
          const result = await response.json();

          if (result.success) {
            setData(data.map(d =>
              d.id === id ? { ...d, isActive: !d.isActive } : d
            ));
          } else {
            alert(`Failed to update status: ${result.error || result.message || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error toggling active:', error);
          alert(`Error updating status: ${error.message}`);
        }
      };

      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-sm font-bold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-500 mt-0.5">{filteredData.length} items</p>
            </div>
            <Button onClick={handleAdd} variant="primary" className="flex items-center gap-2">
              <Plus size={16} />
              Add New
            </Button>
          </div>

          <Card className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        Name
                        {sortField === 'name' && (
                          <span className="text-pierian">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                        {sortField !== 'name' && <span className="text-gray-300">↕</span>}
                      </div>
                    </th>
                    {hasRegion && (
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('region')}
                      >
                        <div className="flex items-center gap-1">
                          Region
                          {sortField === 'region' && (
                            <span className="text-pierian">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                          {sortField !== 'region' && <span className="text-gray-300">↕</span>}
                        </div>
                      </th>
                    )}
                    {hasServiceLine && (
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('serviceLine')}
                      >
                        <div className="flex items-center gap-1">
                          Service Line
                          {sortField === 'serviceLine' && (
                            <span className="text-pierian">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                          {sortField !== 'serviceLine' && <span className="text-gray-300">↕</span>}
                        </div>
                      </th>
                    )}
                    {hasEntityGroup && (
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('entityGroup')}
                      >
                        <div className="flex items-center gap-1">
                          Group
                          {sortField === 'entityGroup' && (
                            <span className="text-pierian">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                          {sortField !== 'entityGroup' && <span className="text-gray-300">↕</span>}
                        </div>
                      </th>
                    )}
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortField === 'status' && (
                          <span className="text-pierian">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                        {sortField !== 'status' && <span className="text-gray-300">↕</span>}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={hasRegion ? 4 : hasServiceLine ? 4 : hasEntityGroup ? 4 : 3} className="px-4 py-8 text-center text-sm text-gray-500">
                        No {title.toLowerCase()} found{search ? ` matching "${search}"` : ''}
                      </td>
                    </tr>
                  ) : (
                    sortedData.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                        {hasRegion && <td className="px-4 py-3 text-sm text-gray-600">{item.region}</td>}
                        {hasServiceLine && <td className="px-4 py-3 text-sm text-gray-600">{item.serviceLine}</td>}
                        {hasEntityGroup && (
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.entityGroup ? (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                'bg-teal-100 text-teal-700'
                              }`}>
                                {item.entityGroup}
                              </span>
                            ) : '-'}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleActive(item.id)}
                            className={`px-2 py-1 text-xs rounded-full ${
                              item.isActive
                                ? 'bg-sage-tint text-sage-shade'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {item.isActive ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-1 text-pierian hover:bg-pierian-10 rounded"
                              title="Edit"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-1 text-pierian hover:bg-teal-50 rounded"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      );
    };

    // Reference Data Modal
    const RefDataModal = () => {
      const [formData, setFormData] = useState(selectedRefData || {
        id: null,
        name: '',
        region: '',
        serviceLine: '',
        entityGroup: '',
        isActive: true
      });

      // Update formData when selectedRefData changes (for edit mode)
      React.useEffect(() => {
        if (selectedRefData) {
          console.log('🔄 RefDataModal - Editing existing record:', selectedRefData);
          console.log('   - serviceLine field:', selectedRefData.serviceLine);
          console.log('   - serviceLineId field:', selectedRefData.serviceLineId);
          setFormData(selectedRefData);
        }
      }, [selectedRefData]);

      const handleSubmit = async () => {
        if (!formData.name) {
          alert('Please enter a name');
          return;
        }

        // Validate service line is selected for service offerings
        if (refDataType === 'service-offerings' && !formData.serviceLine) {
          alert('Please select a Service Line');
          return;
        }

        try {
          // Map frontend format to database format
          const dbData = {
            name: formData.name,
            is_active: formData.isActive ? 1 : 0
          };

          // Add stage_order for sales-stages
          if (refDataType === 'sales-stages') {
            dbData.stage_order = formData.order || (salesStages.length + 1);
          }

          // Add region for geographies
          if (refDataType === 'geographies') {
            dbData.region = formData.region || '';
          }

          // Add entity_group for entities
          if (refDataType === 'entities') {
            dbData.entity_group = formData.entityGroup || null;
          }

          // Add service_line_id for service-offerings
          if (refDataType === 'service-offerings') {
            // Find the service line ID from the serviceLines array
            console.log('🔍 Service Line Debug:');
            console.log('  - formData.serviceLine:', formData.serviceLine);
            console.log('  - formData.serviceLine type:', typeof formData.serviceLine);
            console.log('  - Available serviceLines:', serviceLines);
            const selectedServiceLine = serviceLines.find(sl => {
              console.log(`    Comparing: "${sl.name}" === "${formData.serviceLine}"`, sl.name === formData.serviceLine);
              return sl.name === formData.serviceLine;
            });
            console.log('  - Found selectedServiceLine:', selectedServiceLine);
            dbData.service_line_id = selectedServiceLine ? selectedServiceLine.id : null;
            console.log('  - Final service_line_id:', dbData.service_line_id);
            console.log('  - Final service_line_id type:', typeof dbData.service_line_id);
          }

          // Log the data being sent to API
          console.log('📤 Sending to API:', {
            endpoint: `${API_BASE_URL}/master/${refDataType}`,
            method: selectedRefData ? 'PUT' : 'POST',
            data: dbData
          });
          console.log('📤 JSON.stringify(dbData):', JSON.stringify(dbData, null, 2));

          // Extra validation for service offerings
          if (refDataType === 'service-offerings') {
            if (!dbData.service_line_id) {
              console.error('❌ WARNING: service_line_id is missing or null!');
              console.error('   This will result in NULL in database');
            } else {
              console.log('✅ service_line_id is present:', dbData.service_line_id);
            }
          }

          let response;
          if (selectedRefData) {
            // Edit existing - PUT request
            response = await fetch(`${API_BASE_URL}/master/${refDataType}/${selectedRefData.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(dbData)
            });
          } else {
            // Add new - POST request
            response = await fetch(`${API_BASE_URL}/master/${refDataType}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(dbData)
            });
          }

          const result = await response.json();
          console.log('API response:', result);

          if (result.success) {
            // Map the returned data back to frontend format
            // For service offerings, map service_lines_id to service line name
            let serviceLineName = formData.serviceLine;
            if (refDataType === 'service-offerings' && result.data?.service_lines_id) {
              const foundServiceLine = serviceLines.find(sl => sl.id === result.data.service_lines_id);
              serviceLineName = foundServiceLine ? foundServiceLine.name : formData.serviceLine;
            }

            const savedItem = {
              id: result.data?.id || selectedRefData?.id || Date.now(),
              name: result.data?.name || formData.name,
              isActive: result.data?.is_active === 1 || result.data?.is_active === true,
              order: result.data?.stage_order || formData.order,
              region: result.data?.region || formData.region,
              serviceLine: serviceLineName,
              serviceLineId: result.data?.service_lines_id || null,
              entityGroup: result.data?.entity_group || formData.entityGroup || null
            };

            // Update local state based on type
            if (selectedRefData) {
              // Edit existing
              switch(refDataType) {
                case 'sales-stages':
                  setSalesStages(salesStages.map(item => item.id === selectedRefData.id ? savedItem : item));
                  break;
                case 'industries':
                  setIndustries(industries.map(item => item.id === selectedRefData.id ? savedItem : item));
                  break;
                case 'service-lines':
                  setServiceLines(serviceLines.map(item => item.id === selectedRefData.id ? savedItem : item));
                  break;
                case 'service-offerings':
                  setServiceOfferings(serviceOfferings.map(item => item.id === selectedRefData.id ? savedItem : item));
                  break;
                case 'entities':
                  setEntities(entities.map(item => item.id === selectedRefData.id ? savedItem : item));
                  break;
                case 'geographies':
                  setGeographies(geographies.map(item => item.id === selectedRefData.id ? savedItem : item));
                  break;
              }
            } else {
              // Add new
              switch(refDataType) {
                case 'sales-stages':
                  setSalesStages([...salesStages, savedItem]);
                  break;
                case 'industries':
                  setIndustries([...industries, savedItem]);
                  break;
                case 'service-lines':
                  setServiceLines([...serviceLines, savedItem]);
                  break;
                case 'service-offerings':
                  setServiceOfferings([...serviceOfferings, savedItem]);
                  break;
                case 'entities':
                  setEntities([...entities, savedItem]);
                  break;
                case 'geographies':
                  setGeographies([...geographies, savedItem]);
                  break;
              }
            }

            setShowRefDataModal(false);
            setSelectedRefData(null);
            alert(`${selectedRefData ? 'Updated' : 'Created'} successfully!`);
          } else {
            alert(`Failed to save: ${result.error || result.message || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error saving:', error);
          alert(`Error saving: ${error.message}`);
        }
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="border-b border-gray-200 p-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold">
                  {selectedRefData ? 'Edit' : 'Add New'} {refDataType}
                </h3>
                <button
                  onClick={() => {setShowRefDataModal(false); setSelectedRefData(null);}}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name*</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                  placeholder="Enter name"
                />
              </div>

              {refDataType === 'geographies' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Region*</label>
                  <select
                    value={formData.region}
                    onChange={(e) => setFormData({...formData, region: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="">Select Region</option>
                    <option value="North America">North America</option>
                    <option value="Europe">Europe</option>
                    <option value="Asia Pacific">Asia Pacific</option>
                    <option value="Latin America">Latin America</option>
                    <option value="Middle East">Middle East</option>
                    <option value="Africa">Africa</option>
                  </select>
                </div>
              )}

              {refDataType === 'service-offerings' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Line*</label>
                  <select
                    value={formData.serviceLine || ''}
                    onChange={(e) => setFormData({...formData, serviceLine: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="">Select Service Line</option>
                    {serviceLines?.filter(sl => sl.isActive).map(sl => (
                      <option key={sl.id} value={sl.name}>{sl.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {refDataType === 'entities' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entity Group</label>
                  <input
                    list="entity-group-options"
                    value={formData.entityGroup || ''}
                    onChange={(e) => setFormData({...formData, entityGroup: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                    placeholder="Type or select group"
                  />
                  <datalist id="entity-group-options">
                    {[...new Set(entities.filter(e => e.isActive && e.entityGroup).map(e => e.entityGroup))].map(g => (
                      <option key={g} value={g} />
                    ))}
                  </datalist>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Active</label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  onClick={() => {setShowRefDataModal(false); setSelectedRefData(null);}}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button onClick={handleSubmit} variant="primary">
                  {selectedRefData ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    };

    // Tags Management View Component
    const TagsManagementView = () => {
      const [search, setSearch] = useState('');
      const [showTagModal, setShowTagModal] = useState(false);
      const [selectedTag, setSelectedTag] = useState(null);
      const [tagForm, setTagForm] = useState({ name: '', color: '#6A7CA5', isActive: true });

      // Default color palette for tags (Pierian brand colors)
      const colorPalette = [
        '#00BBA2', // Pierian Red
        '#6A7CA5', // Slate Blue
        '#9CAF88', // Mid Sage
        '#D9A441', // Amber Glow
        '#D8A39D', // Clay Pink
        '#481F89', // Capital Amethyst
        '#6B4C47', // Deep Taupe
        '#009E8A', // Capital Scarlet
      ];

      const filteredTags = (tags || []).filter(tag =>
        tag.name.toLowerCase().includes(search.toLowerCase())
      );

      const handleAdd = () => {
        setSelectedTag(null);
        setTagForm({ name: '', color: '#6A7CA5', isActive: true });
        setShowTagModal(true);
      };

      const handleEdit = (tag) => {
        setSelectedTag(tag);
        setTagForm({ name: tag.name, color: tag.color, isActive: tag.isActive });
        setShowTagModal(true);
      };

      const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this tag? It will be removed from all opportunities.')) {
          try {
            const response = await fetch(`${API_BASE_URL}/master/tags/${id}`, { method: 'DELETE' });
            const result = await response.json();
            if (result.success) {
              setTags(tags.filter(t => t.id !== id));
              alert('Tag deleted successfully!');
            } else {
              alert(`Failed to delete: ${result.error || result.message}`);
            }
          } catch (error) {
            console.error('Error deleting tag:', error);
            alert(`Error deleting tag: ${error.message}`);
          }
        }
      };

      const handleToggleActive = async (tag) => {
        try {
          const response = await fetch(`${API_BASE_URL}/master/tags/${tag.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: tag.name, color: tag.color, is_active: !tag.isActive })
          });
          const result = await response.json();
          if (result.success) {
            setTags(tags.map(t => t.id === tag.id ? { ...t, isActive: !t.isActive } : t));
          } else {
            alert(`Failed to update: ${result.error || result.message}`);
          }
        } catch (error) {
          console.error('Error toggling tag status:', error);
          alert(`Error: ${error.message}`);
        }
      };

      const handleSave = async () => {
        if (!tagForm.name.trim()) {
          alert('Please enter a tag name');
          return;
        }

        try {
          const url = selectedTag
            ? `${API_BASE_URL}/master/tags/${selectedTag.id}`
            : `${API_BASE_URL}/master/tags`;
          const method = selectedTag ? 'PUT' : 'POST';

          const response = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: tagForm.name.trim(),
              color: tagForm.color,
              is_active: tagForm.isActive ? 1 : 0
            })
          });
          const result = await response.json();

          if (result.success) {
            const savedTag = {
              id: result.data.id,
              name: result.data.name,
              color: result.data.color,
              isActive: Boolean(result.data.is_active)
            };
            if (selectedTag) {
              setTags(tags.map(t => t.id === selectedTag.id ? savedTag : t));
            } else {
              setTags([...tags, savedTag]);
            }
            setShowTagModal(false);
            alert(`Tag ${selectedTag ? 'updated' : 'created'} successfully!`);
          } else {
            alert(`Failed to save: ${result.error || result.message}`);
          }
        } catch (error) {
          console.error('Error saving tag:', error);
          alert(`Error saving tag: ${error.message}`);
        }
      };

      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-sm font-bold text-gray-900">Tags</h2>
              <p className="text-sm text-gray-500 mt-1">{filteredTags.length} tags</p>
            </div>
            <Button onClick={handleAdd} variant="primary" className="flex items-center gap-2">
              <Plus size={16} />
              Add New Tag
            </Button>
          </div>

          <Card className="p-4">
            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search tags..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian"
              />
            </div>

            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Color</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Preview</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTags.map(tag => (
                  <tr key={tag.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div
                        className="w-6 h-6 rounded-full border border-gray-300"
                        style={{ backgroundColor: tag.color }}
                      />
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">{tag.name}</td>
                    <td className="py-3 px-4">
                      <span
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                      >
                        {tag.name}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleToggleActive(tag)}
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          tag.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {tag.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleEdit(tag)}
                        className="p-1 text-gray-500 hover:text-[#00BBA2] mr-2"
                        title="Edit"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(tag.id)}
                        className="p-1 text-gray-500 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredTags.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500">
                      No tags found. Click "Add New Tag" to create one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>

          {/* Tag Modal */}
          {showTagModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-md w-full">
                <div className="border-b border-gray-200 p-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold">{selectedTag ? 'Edit Tag' : 'Add New Tag'}</h3>
                    <button onClick={() => setShowTagModal(false)} className="text-gray-400 hover:text-gray-600">
                      <X size={20} />
                    </button>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tag Name</label>
                    <input
                      type="text"
                      value={tagForm.name}
                      onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pierian focus:border-pierian"
                      placeholder="e.g., High Priority"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {colorPalette.map(color => (
                        <button
                          key={color}
                          onClick={() => setTagForm({ ...tagForm, color })}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            tagForm.color === color ? 'border-gray-900 scale-110' : 'border-transparent hover:border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={tagForm.color}
                        onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                        className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={tagForm.color}
                        onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                    <span
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
                      style={{ backgroundColor: `${tagForm.color}20`, color: tagForm.color }}
                    >
                      {tagForm.name || 'Tag Name'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="tagActive"
                      checked={tagForm.isActive}
                      onChange={(e) => setTagForm({ ...tagForm, isActive: e.target.checked })}
                      className="h-4 w-4 text-pierian focus:ring-pierian border-gray-300 rounded"
                    />
                    <label htmlFor="tagActive" className="ml-2 text-sm text-gray-700">Active</label>
                  </div>
                </div>
                <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
                  <Button onClick={() => setShowTagModal(false)} variant="secondary">Cancel</Button>
                  <Button onClick={handleSave} variant="primary">{selectedTag ? 'Update' : 'Create'}</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };

    // Data Management View Component
    // Note: importing, importType, uploadProgress, uploadStatus, and isUnloadingRef
    // are now at CRMApplication level to survive re-renders when parent state changes
    const DataManagementView = () => {

      // Helper function to convert Excel date (serial or string) to JavaScript Date
      // Uses local date to preserve exact date from Excel (no timezone shift)
      const excelDateToJSDate = (dateValue) => {
        if (!dateValue || dateValue === '') return null;

        // If it's already a valid date string
        if (typeof dateValue === 'string') {
          // Try to parse DD-MM-YYYY or DD/MM/YYYY format first
          const ddmmyyyyMatch = dateValue.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
          if (ddmmyyyyMatch) {
            const day = parseInt(ddmmyyyyMatch[1], 10);
            const month = parseInt(ddmmyyyyMatch[2], 10);
            const year = parseInt(ddmmyyyyMatch[3], 10);

            // Check if it's DD-MM-YYYY (day > 12 or month <= 12 and makes sense)
            if (day > 12 || (day <= 12 && month <= 12)) {
              // Assume DD-MM-YYYY format
              const date = new Date(year, month - 1, day);
              if (!isNaN(date.getTime())) {
                return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              }
            }
          }

          // Fall back to standard Date parsing
          const parsed = new Date(dateValue);
          if (!isNaN(parsed.getTime())) {
            // Use local date components to get exact date from Excel
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
          return null;
        }

        // If it's a number, treat it as Excel serial date
        if (typeof dateValue === 'number' && !isNaN(dateValue)) {
          // Excel serial date: days since Dec 30, 1899
          const excelEpoch = new Date(1899, 11, 30);
          const date = new Date(excelEpoch.getTime() + dateValue * 86400 * 1000);
          // Use local date components to get exact date
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }

        return null;
      };

      // Helper function to get value from row with multiple possible column names
      const getFieldValue = (row, ...possibleNames) => {
        for (const name of possibleNames) {
          if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
            return row[name];
          }
        }
        return '';
      };

      // Intelligent stage matching function
      const matchStageToClosest = (inputStage) => {
        if (!inputStage || !salesStages || salesStages.length === 0) {
          return 'Qualified';
        }

        const input = inputStage.toLowerCase().trim();
        const availableStages = salesStages.map(s => s.name);

        // Direct match (case-insensitive)
        const directMatch = availableStages.find(stage => stage.toLowerCase() === input);
        if (directMatch) return directMatch;

        // Define comprehensive stage mapping patterns (using valid stage names)
        const stagePatterns = {
          'Qualified': ['qualified', 'qualification', 'lead', 'new', 'prospecting', 'prospect', 'initial', 'contact', 'outreach'],
          'Discovery': ['discovery', 'needs', 'analysis', 'assessment', 'requirement', 'discovery & needs', 'discovery completed'],
          'Proposal': ['proposal', 'submitted', 'under review', 'review', 'evaluation', 'pending', 'proposal sent', 'solution', 'design', 'solutioning', 'rfp', 'quote', 'discussion', 'negotiation', 'negotiate', 'pricing', 'terms'],
          'Closed-Won': ['won', 'closed won', 'closed-won', 'win', 'success', 'signed', 'awarded', 'accepted', 'end game', 'endgame', 'closing'],
          'Closed-Lost': ['lost', 'closed lost', 'closed-lost', 'lose', 'rejected', 'declined', 'unsuccessful', 'failed'],
          'Contract': ['contracting', 'contracting complete', 'contracted', 'contract', 'payment', 'complete']
        };

        // Find best match based on keywords
        let bestMatch = null;
        let maxMatches = 0;

        for (const [stage, keywords] of Object.entries(stagePatterns)) {
          if (availableStages.includes(stage)) {
            let matchCount = 0;
            for (const keyword of keywords) {
              if (input.includes(keyword.toLowerCase())) {
                matchCount++;
              }
            }
            if (matchCount > maxMatches) {
              maxMatches = matchCount;
              bestMatch = stage;
            }
          }
        }

        if (bestMatch && maxMatches > 0) {
          return bestMatch;
        }

        // If no match found, try partial matching with available stages
        const partialMatch = availableStages.find(stage => {
          const stageLower = stage.toLowerCase();
          return input.includes(stageLower) || stageLower.includes(input);
        });

        if (partialMatch) return partialMatch;

        // Default to first stage (Qualified) or fallback
        return availableStages[0] || 'Qualified';
      };

      // Export to Excel
      const exportToExcel = (data, filename, sheetName) => {
        if (!window.XLSX) {
          alert('Excel library not loaded. Please refresh and try again.');
          return;
        }

        const worksheet = window.XLSX.utils.json_to_sheet(data);
        const workbook = window.XLSX.utils.book_new();
        window.XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        window.XLSX.writeFile(workbook, filename);
      };

      // Export Leads
      const handleExportLeads = () => {
        const exportData = leads.map(lead => ({
          'Lead ID': lead.leadId,
          'Company Name': lead.companyName,
          'Contact Person': lead.contactPerson,
          'Designation': lead.designation,
          'Email': lead.email,
          'Contact Number': lead.contactNumber,
          'Lead Source': lead.leadSource,
          'Lead Status': lead.leadStatus,
          'Lead Owner': lead.leadOwner,
          'Lead Supporter 1': lead.leadSupporter ? String(lead.leadSupporter).split(',')[0].trim() : '',
          'Lead Supporter 2': lead.leadSupporter && String(lead.leadSupporter).split(',').length > 1 ? String(lead.leadSupporter).split(',').slice(1).join(',').trim() : '',
          'Lead Generator': lead.leadGenerator || '',
          'Lead Rating': lead.leadRating || '',
          'Business Type': lead.businessType || '',
          'Geography': lead.geography || '',
          'Entity': lead.entity || '',
          'Service Line': lead.serviceLine,
          'Industry Vertical': lead.industryVertical,
          'Industry Sub Vertical': lead.industrySubVertical,
          'Pain Points': lead.painPoints,
          'Budget': lead.budget,
          'Estimated Revenue': lead.estimatedRevenue,
          'Timeline (months)': lead.timeline,
          'Priority Level': lead.priorityLevel,
          'Date Captured': lead.dateCaptured,
          'Company Size': lead.companySize,
          'Company Revenue': lead.companyRevenue,
          'City': lead.city,
          'Region': lead.region,
          'Engagement History': lead.engagementHistory,
          'Comments': lead.comments
        }));
        exportToExcel(exportData, 'leads_export.xlsx', 'Leads');
        alert(`Successfully exported ${exportData.length} leads!`);
      };

      // Export Opportunities
      const handleExportOpportunities = () => {
        const exportData = opportunities.map(opp => ({
          'Opportunity ID': opp.opportunityId,
          'Opportunity Name': opp.opportunityName,
          'Opportunity Type': opp.opportunityType,
          'Geography': opp.geography,
          'Account Owner': opp.accountOwner,
          'Associated Account': opp.associatedAccount,
          'Primary Contact': opp.primaryContact,
          'Email': opp.email,
          'Influencers': opp.influencers,
          'Decision Making Team': opp.decisionMakingTeam,
          'Industry Vertical': opp.industryVertical,
          'Industry Sub Vertical': opp.industrySubVertical,
          'Service Line': opp.serviceLine,
          'Service Offering - L2': opp.serviceOffering,
          'Annual Contract Value': opp.annualContractValue,
          'Total Contract Value': opp.totalContractValue,
          'Priority Level': opp.priorityLevel,
          'Initiation Date': opp.initiationDate,
          'Aging': opp.aging,
          'Estimated Close Date': opp.estimatedCloseDate,
          'Actual Close Date': opp.actualCloseDate,
          'Sales Status': opp.salesStatus,
          'Sales Stage': opp.salesStage,
          'Solution Scope': opp.solutionScope,
          'Engagement History': opp.engagementHistory,
          'Lead Supporter': opp.leadSupporter,
          'Executive Sponsor': opp.executiveSponsor,
          'Probability to Close': opp.probabilityToClose,
          'Probability %': opp.probabilityPercent,
          'Forecast Multiplier': opp.forecastMultiplier,
          'Actual Value': opp.actualValue,
          'Probability Value': opp.probabilityValue,
          'Budget': opp.budget,
          'Estimated Revenue': opp.estimatedRevenue,
          'Actual Revenue': opp.actualRevenue,
          'Timeline': opp.timeline,
          'Pain Points': opp.painPoints,
          'Competitors': opp.competitors,
          'Comments': opp.comments
        }));
        exportToExcel(exportData, 'opportunities_export.xlsx', 'Opportunities');
        alert(`Successfully exported ${exportData.length} opportunities!`);
      };

      // Export Customers
      const handleExportCustomers = () => {
        const exportData = customers.map(customer => ({
          'Customer ID': customer.customerId,
          'Company Name': customer.companyName,
          'Primary Contact': customer.primaryContact,
          'Contact Designation': customer.contactDesignation || '',
          'Email': customer.email,
          'Phone': customer.phone,
          'Secondary Contact': customer.secondaryContact || '',
          'Secondary Contact Designation': customer.secondaryContactDesignation || '',
          'Secondary Contact Email': customer.secondaryContactEmail || '',
          'Secondary Contact Phone': customer.secondaryContactPhone || '',
          'Service Line': customer.serviceLine,
          'Geography': customer.geography,
          'Contract Value': customer.contractValue,
          'Start Date': customer.startDate,
          'Renewal Date': customer.renewalDate,
          'Status': customer.status,
          'Health Score': customer.healthScore,
          'Project Count': customer.projectCount,
          'Account Manager': customer.accountManager || ''
        }));
        exportToExcel(exportData, 'customers_export.xlsx', 'Customers');
        alert(`Successfully exported ${exportData.length} customers!`);
      };

      // Generate Sample Templates
      const generateLeadsTemplate = () => {
        const sampleData = [{
          'Lead ID': 'CRM-Lead-Jan26-01',
          'Company Name': 'Sample Company Pvt Ltd',
          'Contact Person': 'Rajesh Kumar',
          'Designation': 'CTO',
          'Email': 'rajesh.kumar@samplecompany.in',
          'Contact Number': '+91-9876543210',
          'Lead Source': 'Inbound Leads',
          'Lead Status': 'Open',
          'Lead Owner': 'Sales Rep Name',
          'Lead Supporter 1': 'Supporter Name',
          'Lead Supporter 2': '',
          'Lead Generator': 'Generator Name',
          'Lead Rating': 'Hot',
          'Business Type': 'New Logo',
          'Geography': 'India',
          'Entity': 'PSPL',
          'Service Line': 'Technology Risk Advisory',
          'Industry Vertical': 'Technology',
          'Industry Sub Vertical': 'SaaS',
          'Pain Points': 'Manual reporting, no real-time visibility',
          'Budget': 500000,
          'Estimated Revenue': 600000,
          'Timeline (months)': 6,
          'Priority Level': 'High',
          'Date Captured': '2026-01-15',
          'Company Size': '100-500 employees',
          'Company Revenue': 50000000,
          'City': 'Bengaluru',
          'Region': 'South India',
          'Engagement History': 'Initial LinkedIn outreach — positive response; discovery call scheduled',
          'Comments': 'Strong interest in analytics platform'
        }];
        exportToExcel(sampleData, 'leads_template.xlsx', 'Leads');
        alert('Leads template downloaded successfully!');
      };

      const generateOpportunitiesTemplate = () => {
        const sampleData = [{
          'Opportunity ID': 'CRM-Opp-Feb26-01',
          'Opportunity Name': 'Sample Opportunity',
          'Opportunity Type': 'New Logo',
          'Geography': 'United States',
          'Account Owner': 'Sales Rep Name',
          'Associated Account': 'Sample Company Inc',
          'Primary Contact': 'John Doe',
          'Email': 'john@samplecompany.com',
          'Influencers': 'Jane Smith',
          'Decision Making Team': 'John Doe, Jane Smith',
          'Industry Vertical': 'Technology',
          'Industry Sub Vertical': 'SaaS',
          'Service Line': 'Business Risk Advisory',
          'Service Offering - L2': 'Technical Support',
          'Annual Contract Value': 150000,
          'Total Contract Value': 150000,
          'Priority Level': 'High',
          'Initiation Date': '2025-01-01',
          'Aging': 30,
          'Estimated Close Date': '2025-06-01',
          'Actual Close Date': '',
          'Sales Status': 'Warm',
          'Sales Stage': 'Proposal',
          'Solution Scope': 'Full customer support solution',
          'Engagement History': 'Multiple meetings conducted',
          'Lead Supporter': 'Manager Name',
          'Executive Sponsor': 'VP Name',
          'Probability to Close': 'High',
          'Probability %': '51% - 75%',
          'Forecast Multiplier': 0.65,
          'Actual Value': 0,
          'Probability Value': 97500,
          'Budget': 150000,
          'Estimated Revenue': 150000,
          'Actual Revenue': '',
          'Timeline': 6,
          'Pain Points': 'Current support is inadequate',
          'Competitors': 'Competitor A, Competitor B',
          'Comments': 'Strong opportunity'
        }];
        exportToExcel(sampleData, 'opportunities_template.xlsx', 'Opportunities');
        alert('Opportunities template downloaded successfully!');
      };

      const generateCustomersTemplate = () => {
        const sampleData = [{
          'Customer ID': 'CUST-001',
          'Company Name': 'Sample Customer Inc',
          'Primary Contact': 'John Doe',
          'Contact Designation': 'CTO',
          'Email': 'john@samplecustomer.com',
          'Phone': '+1-555-1234',
          'Secondary Contact': 'Jane Smith',
          'Secondary Contact Designation': 'CFO',
          'Secondary Contact Email': 'jane@samplecustomer.com',
          'Secondary Contact Phone': '+1-555-5678',
          'Service Line': 'Business Risk Advisory',
          'Geography': 'United States',
          'Contract Value': 200000,
          'Start Date': '2025-01-01',
          'Renewal Date': '2026-01-01',
          'Status': 'Active',
          'Health Score': 90,
          'Project Count': 2,
          'Account Manager': 'Sales Rep Name'
        }];
        exportToExcel(sampleData, 'customers_template.xlsx', 'Customers');
        alert('Customers template downloaded successfully!');
      };

      // Import from Excel
      const handleImport = (type) => {
        setImportType(type);
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.xlsx,.xls';
        input.onchange = (e) => handleFileImport(e, type);
        input.click();
      };

      const handleFileImport = async (event, type) => {
        const file = event.target.files[0];
        if (!file) return;

        setImporting(true);
        setUploadProgress(0);
        setUploadStatus('Reading file...');

        try {
          const reader = new FileReader();

          // Track upload progress
          reader.onprogress = (e) => {
            if (e.lengthComputable) {
              const progress = Math.round((e.loaded / e.total) * 50); // First 50% for file reading
              setUploadProgress(progress);
              setUploadStatus(`Reading file... ${Math.round((e.loaded / e.total) * 100)}%`);
            }
          };

          reader.onload = async (e) => {
            try {
              setUploadProgress(50);
              setUploadStatus('Processing Excel data...');

              const data = new Uint8Array(e.target.result);

              if (!window.XLSX) {
                setUploadStatus('Loading Excel library...');
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
                script.onload = async () => {
                  setUploadProgress(60);
                  setUploadStatus('Parsing Excel file...');
                  await processImportData(data, type);
                };
                document.head.appendChild(script);
              } else {
                setUploadProgress(60);
                setUploadStatus('Parsing Excel file...');
                await processImportData(data, type);
              }
            } catch (error) {
              console.error('Error parsing file:', error);
              alert('Error parsing Excel file. Please check the file format.');
              setImporting(false);
              setUploadProgress(0);
              setUploadStatus('');
            }
          };

          reader.onerror = () => {
            console.error('FileReader error');
            alert('Error reading file');
            setImporting(false);
            setUploadProgress(0);
            setUploadStatus('');
          };

          reader.readAsArrayBuffer(file);
        } catch (error) {
          console.error('Import error:', error);
          alert('Error importing file');
          setImporting(false);
          setUploadProgress(0);
          setUploadStatus('');
        }
      };

      const processImportData = async (data, type) => {
        try {
          setUploadProgress(70);
          setUploadStatus('Reading Excel workbook...');

          const workbook = window.XLSX.read(data, { type: 'array' });

          // For Pierian opportunities, look for "Opps Tracker" sheet
          let sheetName;
          let worksheet;

          if (type === 'pierian-opportunities') {
            sheetName = workbook.SheetNames.find(name => name.trim() === 'Opps Tracker') || workbook.SheetNames[0];
            worksheet = workbook.Sheets[sheetName];
            console.log(`Using sheet: ${sheetName} for Pierian opportunities`);
          } else {
            sheetName = workbook.SheetNames[0];
            worksheet = workbook.Sheets[sheetName];
          }

          setUploadProgress(80);
          setUploadStatus('Converting data...');

          const jsonData = window.XLSX.utils.sheet_to_json(worksheet, { raw: false, defval: '' });

          console.log(`Importing ${type}:`, jsonData);
          console.log('Sample row:', jsonData[0]);

          setUploadProgress(90);
          setUploadStatus(`Importing ${jsonData.length} records...`);

          if (type === 'leads') {
            importLeads(jsonData);
            setUploadProgress(100);
            setUploadStatus('Import complete!');
            setTimeout(() => {
              setImporting(false);
              setUploadProgress(0);
              setUploadStatus('');
              setActiveTab('leads');
            }, 500);
          } else if (type === 'opportunities') {
            // Await the async import function to ensure DB inserts complete before hiding progress
            // NOTE: Opportunities import now goes to leads table
            await importOpportunities(jsonData);
            setUploadProgress(100);
            setUploadStatus('Import complete!');
            setTimeout(() => {
              setImporting(false);
              setUploadProgress(0);
              setUploadStatus('');
              setActiveTab('leads');
            }, 500);
          } else if (type === 'opportunities-direct') {
            await importOpportunitiesDirect(jsonData);
            setUploadProgress(100);
            setUploadStatus('Import complete!');
            setTimeout(() => {
              setImporting(false);
              setUploadProgress(0);
              setUploadStatus('');
              setActiveTab('opportunities');
            }, 500);
          } else if (type === 'customers') {
            importCustomers(jsonData);
            setUploadProgress(100);
            setUploadStatus('Import complete!');
            setTimeout(() => {
              setImporting(false);
              setUploadProgress(0);
              setUploadStatus('');
              setActiveTab('customers');
            }, 500);
          } else if (type === 'pierian-opportunities') {
            // Await the async import function - it handles its own progress status internally
            await importPierianOpportunities(jsonData);
          }
        } catch (error) {
          console.error('Error processing data:', error);
          alert('Error processing Excel file: ' + error.message);
          setImporting(false);
          setUploadProgress(0);
          setUploadStatus('');
        }
      };

      const importLeads = (jsonData) => {
        const newLeads = jsonData.map((row, index) => ({
          leadId: getFieldValue(row, 'Lead ID') || 'NEW',
          companyName: getFieldValue(row, 'Company Name') || '',
          contactPerson: getFieldValue(row, 'Contact Person') || '',
          designation: getFieldValue(row, 'Designation') || '',
          email: getFieldValue(row, 'Email') || '',
          contactNumber: getFieldValue(row, 'Contact Number') || '',
          leadSource: getFieldValue(row, 'Lead Source') || 'Direct',
          leadStatus: getFieldValue(row, 'Lead Status') || 'Open',
          leadOwner: getFieldValue(row, 'Lead Owner') || '',
          leadSupporter: [getFieldValue(row, 'Lead Supporter 1') || getFieldValue(row, 'Lead Supporter'), getFieldValue(row, 'Lead Supporter 2')].filter(Boolean).join(', '),
          leadGenerator: getFieldValue(row, 'Lead Generator') || '',
          leadRating: getFieldValue(row, 'Lead Rating') || '',
          businessType: getFieldValue(row, 'Business Type') || '',
          geography: getFieldValue(row, 'Geography') || '',
          entity: getFieldValue(row, 'Entity') || '',
          serviceLine: getFieldValue(row, 'Service Line') || '',
          industryVertical: getFieldValue(row, 'Industry Vertical') || '',
          industrySubVertical: getFieldValue(row, 'Industry Sub Vertical') || '',
          painPoints: getFieldValue(row, 'Pain Points') || '',
          budget: parseFloat(String(getFieldValue(row, 'Budget') || '').trim().replace(/[₹$,\s]/g, '')) || 0,
          estimatedRevenue: parseFloat(String(getFieldValue(row, 'Estimated Revenue') || '').trim().replace(/[₹$,\s]/g, '')) || 0,
          timeline: parseInt(getFieldValue(row, 'Timeline (months)', 'Timeline')) || 6,
          priorityLevel: getFieldValue(row, 'Priority Level') || 'Medium',
          dateCaptured: excelDateToJSDate(getFieldValue(row, 'Date Captured')) || new Date().toISOString().split('T')[0],
          companySize: getFieldValue(row, 'Company Size') || '',
          companyRevenue: parseFloat(String(getFieldValue(row, 'Company Revenue') || '').trim().replace(/[₹$,\s]/g, '')) || 0,
          city: getFieldValue(row, 'City') || '',
          region: getFieldValue(row, 'Region') || '',
          engagementHistory: getFieldValue(row, 'Engagement History') || '',
          comments: getFieldValue(row, 'Comments') || ''
        })).filter(lead => lead.companyName);

        setLeads([...leads, ...newLeads]);
        alert(`Successfully imported ${newLeads.length} leads!`);
      };

      const importOpportunities = async (jsonData) => {
         // Using global API_BASE_URL defined at top of file
         // NOTE: All opportunity imports now go to the LEADS table

        // Fetch existing leads from database for duplicate checking
        let existingLeads = [];
        try {
          const response = await fetch(`${API_BASE_URL}/leads`);
          const result = await response.json();
          if (result.success && result.data) {
            existingLeads = result.data;
          }
        } catch (error) {
          console.error('Error fetching existing leads:', error);
        }

        const parsedRecords = jsonData.map((row, index) => ({
          sn: leads.length + index + 1,
          companyName: getFieldValue(row, 'Opportunity Name', 'Company Name', 'Account Name') || '',
          opportunityType: getFieldValue(row, 'Opportunity Type\n(Farming / Cross-Selling / New Logo)', 'Opportunity Type\r\n(Farming / Cross-Selling / New Logo)', 'Opportunity Type', 'Type') || 'New Logo',
          geography: getFieldValue(row, 'Geography') || '',
          accountOwner: getFieldValue(row, 'Account Owner') || '',
          associatedAccount: getFieldValue(row, 'Associated Account') || '',
          contactPerson: getFieldValue(row, 'Primary Contact', 'Contact Person') || '',
          email: getFieldValue(row, 'Email') || '',
          phone: getFieldValue(row, 'Phone Number', 'Phone', 'Contact Phone') || '',
          industry: getFieldValue(row, 'Industry Vertical', 'Industry') || '',
          serviceLine: getFieldValue(row, 'Service Line') || '',
          serviceOffering: getFieldValue(row, 'Service Offering - L2', 'Service Offering') || '',
          annualContractValue: parseFloat(String(getFieldValue(row, 'Annual Contract Value') || '').trim().replace(/[$,\s]/g, '')) || 0,
          totalContractValue: parseFloat(String(getFieldValue(row, 'Total Contract Value') || '').trim().replace(/[$,\s]/g, '')) || 0,
          priority: getFieldValue(row, 'Priority Level', 'Priority') || 'Medium',
          dateCaptured: excelDateToJSDate(getFieldValue(row, 'Initiation Date')) || new Date().toISOString().split('T')[0],
          expectedCloseDate: excelDateToJSDate(getFieldValue(row, 'Estimated Close Date')) || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          actualCloseDate: excelDateToJSDate(getFieldValue(row, 'Actual Close Date')),
          status: getFieldValue(row, 'Sales Status') || 'Warm',
          salesStatusOverride: getFieldValue(row, 'Sales Status') || null,
          salesStage: (() => {
            const stage = getFieldValue(row, 'Sales Stage') || 'Discovery';
            return matchStageToClosest(stage);
          })(),
          solutionScope: getFieldValue(row, 'Solution Scope') || '',
          engagementHistory: getFieldValue(row, 'Engagement History') || '',
          leadSupporter: getFieldValue(row, 'Lead Supporter') || getFieldValue(row, 'Bid Manager') || '',
          executiveSponsor: getFieldValue(row, 'Executive Sponsor') || '',
          probability: String((() => {
            const rawProb = getFieldValue(row, 'Probability %') || '';
            const probNum = parseInt(String(rawProb).replace(/[^0-9]/g, ''), 10);
            if (!isNaN(probNum) && probNum >= 0 && probNum <= 100) {
              return probNum;
            }
            return calculateProbabilityByStage(matchStageToClosest(getFieldValue(row, 'Sales Stage') || 'Discovery')).percent;
          })()),
          budget: parseFloat(String(getFieldValue(row, 'Budget') || '').trim().replace(/[$,\s]/g, '')) || 0,
          estimatedRevenue: parseFloat(String(getFieldValue(row, 'Estimated Revenue') || '').trim().replace(/[$,\s]/g, '')) || 0,
          painPoints: getFieldValue(row, 'Pain Points') || '',
          comments: getFieldValue(row, 'Comments') || '',
          timeline: String(parseInt(getFieldValue(row, 'Timeline')) || 6)
        })).filter(rec => rec.companyName);

        // Duplicate checking: Filter out leads that already exist
        const newLeads = [];
        const duplicateLeads = [];
        const totalCount = parsedRecords.length;

        parsedRecords.forEach((rec) => {
          const isDuplicate = existingLeads.find(existing => {
            if (existing.company_name && rec.companyName) {
              return existing.company_name.toLowerCase().trim() === rec.companyName.toLowerCase().trim();
            }
            return false;
          });

          if (isDuplicate) {
            console.log(`⚠️ Duplicate skipped: ${rec.companyName}`);
            duplicateLeads.push(rec);
          } else {
            newLeads.push(rec);
            console.log(`✅ New lead: ${rec.companyName}`);
          }
        });

        console.log(`\n📊 Import Summary:`);
        console.log(`✅ New leads to add: ${newLeads.length}`);
        console.log(`⚠️ Duplicates skipped: ${duplicateLeads.length}`);

        // Save new leads to database
        if (newLeads.length > 0) {
          // Convert to API format (camelCase → snake_case for leads table)
          const apiLeads = newLeads.map((rec) => ({
            company_name: rec.companyName,
            associated_account: rec.associatedAccount || null,
            industry: rec.industry,
            geography: rec.geography,
            service_line: rec.serviceLine,
            service_offering: rec.serviceOffering || null,
            contact_person: rec.contactPerson || null,
            email: rec.email || null,
            phone: rec.phone || null,
            lead_owner: rec.accountOwner,
            sales_representative: rec.accountOwner,
            priority: rec.priority,
            date_captured: rec.dateCaptured,
            engagement_history: rec.engagementHistory || null,
            comments: rec.comments || null,
            estimated_revenue: rec.estimatedRevenue,
            budget: rec.budget,
            pain_points: rec.painPoints || null,
            timeline: rec.timeline,
            // New import fields
            opportunity_type: rec.opportunityType,
            lead_supporter: rec.leadSupporter || null,
            executive_sponsor: rec.executiveSponsor || null,
            solution_scope: rec.solutionScope || null,
            expected_close_date: rec.expectedCloseDate || null,
            actual_close_date: rec.actualCloseDate || null,
            probability: rec.probability,
            annual_contract_value: rec.annualContractValue,
            total_contract_value: rec.totalContractValue,
            sales_stage: rec.salesStage,
            status: rec.status,
            sales_status_override: rec.salesStatusOverride || null,
            lead_status: 'Open',
            latest_comment: null,
            last_edited_date: null
          }));

          try {
            // Send in chunks of 25 to avoid gateway timeout
            const CHUNK_SIZE = 25;
            let savedCount = 0;
            for (let i = 0; i < apiLeads.length; i += CHUNK_SIZE) {
              const chunk = apiLeads.slice(i, i + CHUNK_SIZE);
              console.log(`📤 Sending chunk ${Math.floor(i / CHUNK_SIZE) + 1}/${Math.ceil(apiLeads.length / CHUNK_SIZE)} (${chunk.length} leads)`);

              const response = await fetch(`${API_BASE_URL}/leads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(chunk)
              });

              if (!response.ok) {
                let errorMsg = `Failed to save chunk: ${response.status}`;
                try {
                  const errorData = await response.json();
                  errorMsg = errorData.message || errorMsg;
                } catch { /* response body is not JSON */ }
                throw new Error(errorMsg);
              }

              const result = await response.json();
              if (!result.success) throw new Error(result.message || 'Save failed');
              savedCount += chunk.length;
            }

            console.log(`✅ Saved ${savedCount} new leads`);
            // Refresh leads from database using proper field mapping
            if (_fetchLeads) await _fetchLeads();
          } catch (error) {
            console.error('Error saving leads:', error);
            alert('Error saving leads to database: ' + error.message);
            return;
          }
        }

        // Show summary message
        let message = `Import Complete!\n\n`;
        message += `• ${newLeads.length} new leads imported\n`;
        message += `• ${duplicateLeads.length} duplicates skipped\n`;
        message += `• Total processed: ${totalCount}`;

        alert(message);
      };

      // Import directly into the opportunities table.
      // Mirrors the qualify-lead-to-opportunity flow: per row, creates a Lead
      // (lead_status='Converted') and an Opportunity (sales_stage='Qualified',
      // status='Warm') with source_lead_id linking opp -> lead.
      const importOpportunitiesDirect = async (jsonData) => {
        // Fetch existing opportunities for duplicate checking
        let existingOpps = [];
        try {
          const response = await fetch(`${API_BASE_URL}/opportunities`);
          const result = await response.json();
          if (result.success && result.data) {
            existingOpps = result.data;
          }
        } catch (error) {
          console.error('Error fetching existing opportunities:', error);
        }

        // Re-use the same flexible column mapping as importOpportunities
        const parsedRecords = jsonData.map((row) => ({
          companyName: getFieldValue(row, 'Opportunity Name', 'Company Name', 'Account Name') || '',
          opportunityType: getFieldValue(row, 'Opportunity Type\n(Farming / Cross-Selling / New Logo)', 'Opportunity Type\r\n(Farming / Cross-Selling / New Logo)', 'Opportunity Type', 'Type') || 'New Logo',
          geography: getFieldValue(row, 'Geography') || '',
          accountOwner: getFieldValue(row, 'Account Owner') || '',
          associatedAccount: getFieldValue(row, 'Associated Account') || '',
          contactPerson: getFieldValue(row, 'Primary Contact', 'Contact Person') || '',
          email: getFieldValue(row, 'Email') || '',
          phone: getFieldValue(row, 'Phone Number', 'Phone', 'Contact Phone') || '',
          industry: getFieldValue(row, 'Industry Vertical', 'Industry') || '',
          serviceLine: getFieldValue(row, 'Service Line') || '',
          serviceOffering: getFieldValue(row, 'Service Offering - L2', 'Service Offering') || '',
          annualContractValue: parseFloat(String(getFieldValue(row, 'Annual Contract Value') || '').trim().replace(/[$,\s]/g, '')) || 0,
          totalContractValue: parseFloat(String(getFieldValue(row, 'Total Contract Value') || '').trim().replace(/[$,\s]/g, '')) || 0,
          priority: getFieldValue(row, 'Priority Level', 'Priority') || 'Medium',
          dateCaptured: excelDateToJSDate(getFieldValue(row, 'Initiation Date')) || new Date().toISOString().split('T')[0],
          expectedCloseDate: excelDateToJSDate(getFieldValue(row, 'Estimated Close Date')) || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          actualCloseDate: excelDateToJSDate(getFieldValue(row, 'Actual Close Date')),
          status: getFieldValue(row, 'Sales Status') || 'Warm',
          salesStatusOverride: getFieldValue(row, 'Sales Status') || null,
          salesStage: (() => {
            const stage = getFieldValue(row, 'Sales Stage') || 'Qualified';
            return matchStageToClosest(stage);
          })(),
          solutionScope: getFieldValue(row, 'Solution Scope') || '',
          engagementHistory: getFieldValue(row, 'Engagement History') || '',
          leadSupporter: getFieldValue(row, 'Lead Supporter') || getFieldValue(row, 'Bid Manager') || '',
          executiveSponsor: getFieldValue(row, 'Executive Sponsor') || '',
          probability: String((() => {
            const rawProb = getFieldValue(row, 'Probability %') || '';
            const probNum = parseInt(String(rawProb).replace(/[^0-9]/g, ''), 10);
            if (!isNaN(probNum) && probNum >= 0 && probNum <= 100) {
              return probNum;
            }
            return calculateProbabilityByStage(matchStageToClosest(getFieldValue(row, 'Sales Stage') || 'Qualified')).percent;
          })()),
          budget: parseFloat(String(getFieldValue(row, 'Budget') || '').trim().replace(/[$,\s]/g, '')) || 0,
          estimatedRevenue: parseFloat(String(getFieldValue(row, 'Estimated Revenue') || '').trim().replace(/[$,\s]/g, '')) || 0,
          painPoints: getFieldValue(row, 'Pain Points') || '',
          comments: getFieldValue(row, 'Comments') || '',
          timeline: String(parseInt(getFieldValue(row, 'Timeline')) || 6)
        })).filter(rec => rec.companyName);

        // Skip duplicates by opportunity_name + associated_account against existing opportunities
        const newRecords = [];
        const duplicateRecords = [];
        const totalCount = parsedRecords.length;

        parsedRecords.forEach((rec) => {
          const isDuplicate = existingOpps.find(existing => {
            const sameName = existing.opportunity_name &&
              existing.opportunity_name.toLowerCase().trim() === rec.companyName.toLowerCase().trim();
            const sameAccount = (existing.associated_account || '').toLowerCase().trim() ===
              (rec.associatedAccount || '').toLowerCase().trim();
            return sameName && sameAccount;
          });

          if (isDuplicate) {
            console.log(`⚠️ Duplicate opportunity skipped: ${rec.companyName} / ${rec.associatedAccount}`);
            duplicateRecords.push(rec);
          } else {
            newRecords.push(rec);
          }
        });

        console.log(`\n📊 Direct Opportunities Import Summary:`);
        console.log(`✅ New opportunities to add: ${newRecords.length}`);
        console.log(`⚠️ Duplicates skipped: ${duplicateRecords.length}`);

        if (newRecords.length === 0) {
          alert(`Import Complete!\n\n• 0 new opportunities imported\n• ${duplicateRecords.length} duplicates skipped\n• Total processed: ${totalCount}`);
          return;
        }

        let createdLeads = 0;
        let createdOpps = 0;

        try {
          const CHUNK_SIZE = 25;
          for (let i = 0; i < newRecords.length; i += CHUNK_SIZE) {
            const chunk = newRecords.slice(i, i + CHUNK_SIZE);
            const chunkLabel = `chunk ${Math.floor(i / CHUNK_SIZE) + 1}/${Math.ceil(newRecords.length / CHUNK_SIZE)}`;

            // Phase 1: insert leads with lead_status='Converted'
            const leadPayloads = chunk.map((rec) => ({
              company_name: rec.companyName,
              associated_account: rec.associatedAccount || null,
              industry: rec.industry,
              geography: rec.geography,
              service_line: rec.serviceLine,
              service_offering: rec.serviceOffering || null,
              contact_person: rec.contactPerson || null,
              email: rec.email || null,
              phone: rec.phone || null,
              lead_owner: rec.accountOwner,
              sales_representative: rec.accountOwner,
              priority: rec.priority,
              date_captured: rec.dateCaptured,
              engagement_history: rec.engagementHistory || null,
              comments: rec.comments || null,
              estimated_revenue: rec.estimatedRevenue,
              budget: rec.budget,
              pain_points: rec.painPoints || null,
              timeline: rec.timeline,
              opportunity_type: rec.opportunityType,
              lead_supporter: rec.leadSupporter || null,
              executive_sponsor: rec.executiveSponsor || null,
              solution_scope: rec.solutionScope || null,
              expected_close_date: rec.expectedCloseDate || null,
              actual_close_date: rec.actualCloseDate || null,
              probability: rec.probability,
              annual_contract_value: rec.annualContractValue,
              total_contract_value: rec.totalContractValue,
              sales_stage: rec.salesStage,
              status: rec.status,
              sales_status_override: rec.salesStatusOverride || null,
              lead_status: 'Converted',
              latest_comment: null,
              last_edited_date: null
            }));

            console.log(`📤 Leads ${chunkLabel} (${leadPayloads.length})`);
            const leadResp = await fetch(`${API_BASE_URL}/leads`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(leadPayloads)
            });
            if (!leadResp.ok) {
              let errorMsg = `Failed to save leads chunk: ${leadResp.status}`;
              try { const e = await leadResp.json(); errorMsg = e.message || errorMsg; } catch { /* not JSON */ }
              throw new Error(errorMsg);
            }
            const leadResult = await leadResp.json();
            if (!leadResult.success) throw new Error(leadResult.message || 'Lead chunk save failed');
            const createdLeadRows = Array.isArray(leadResult.data) ? leadResult.data : [leadResult.data];
            createdLeads += createdLeadRows.length;

            // Phase 2: insert opportunities, link source_lead_id by index
            const oppPayloads = chunk.map((rec, idx) => {
              const linkedLead = createdLeadRows[idx] || {};
              const linkedLeadId = linkedLead.lead_id || linkedLead.leadId || null;
              return {
                opportunity_name: rec.companyName,
                associated_account: rec.associatedAccount || null,
                sales_stage: rec.salesStage || 'Qualified',
                probability: rec.probability,
                estimated_revenue: rec.estimatedRevenue,
                annual_contract_value: rec.annualContractValue,
                total_contract_value: rec.totalContractValue,
                service_line: rec.serviceLine,
                service_offering: rec.serviceOffering || null,
                geography: rec.geography,
                industry: rec.industry,
                opportunity_type: rec.opportunityType,
                priority: rec.priority,
                status: rec.status || 'Warm',
                sales_status_override: rec.salesStatusOverride || null,
                account_owner: rec.accountOwner,
                lead_supporter: rec.leadSupporter || null,
                executive_sponsor: rec.executiveSponsor || null,
                primary_contact: rec.contactPerson || null,
                primary_contact_email: rec.email || null,
                phone_number: rec.phone || null,
                solution_scope: rec.solutionScope || null,
                initiation_date: rec.dateCaptured || null,
                expected_close_date: rec.expectedCloseDate || null,
                actual_close_date: rec.actualCloseDate || null,
                engagement_history: rec.engagementHistory || null,
                comments: rec.comments || null,
                source_lead_id: linkedLeadId
              };
            });

            console.log(`📤 Opportunities ${chunkLabel} (${oppPayloads.length})`);
            const oppResp = await fetch(`${API_BASE_URL}/opportunities`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(oppPayloads)
            });
            if (!oppResp.ok) {
              let errorMsg = `Failed to save opportunities chunk: ${oppResp.status}`;
              try { const e = await oppResp.json(); errorMsg = e.message || errorMsg; } catch { /* not JSON */ }
              throw new Error(errorMsg);
            }
            const oppResult = await oppResp.json();
            if (!oppResult.success) throw new Error(oppResult.message || 'Opportunity chunk save failed');
            createdOpps += Array.isArray(oppResult.data) ? oppResult.data.length : 1;
          }

          console.log(`✅ Created ${createdLeads} leads (Converted) and ${createdOpps} opportunities`);

          if (_fetchLeads) await _fetchLeads();
          if (_fetchOpportunities) await _fetchOpportunities();
        } catch (error) {
          console.error('Error during direct opportunity import:', error);
          alert(`Error importing opportunities: ${error.message}\n\nPartial progress: ${createdLeads} leads, ${createdOpps} opportunities created so far.`);
          return;
        }

        let message = `Import Complete!\n\n`;
        message += `• ${createdOpps} new opportunities imported\n`;
        message += `• ${createdLeads} matching leads created (status=Converted)\n`;
        message += `• ${duplicateRecords.length} duplicates skipped\n`;
        message += `• Total processed: ${totalCount}`;
        alert(message);
      };

      const importCustomers = (jsonData) => {
        const newCustomers = jsonData.map((row, index) => ({
          customerId: getFieldValue(row, 'Customer ID') || `CUST-${String(customers.length + index + 1).padStart(3, '0')}`,
          companyName: getFieldValue(row, 'Company Name') || '',
          primaryContact: getFieldValue(row, 'Primary Contact') || '',
          contactDesignation: getFieldValue(row, 'Contact Designation', 'Designation', 'Title') || '',
          email: getFieldValue(row, 'Email') || '',
          phone: getFieldValue(row, 'Phone') || '',
          secondaryContact: getFieldValue(row, 'Secondary Contact', 'Secondary Contact Name') || '',
          secondaryContactDesignation: getFieldValue(row, 'Secondary Contact Designation', 'Secondary Designation') || '',
          secondaryContactEmail: getFieldValue(row, 'Secondary Contact Email', 'Secondary Email') || '',
          secondaryContactPhone: getFieldValue(row, 'Secondary Contact Phone', 'Secondary Phone') || '',
          serviceLine: getFieldValue(row, 'Service Line') || '',
          geography: getFieldValue(row, 'Geography') || '',
          contractValue: parseFloat(String(getFieldValue(row, 'Contract Value') || '').trim().replace(/[$,\s]/g, '')) || 0,
          startDate: excelDateToJSDate(getFieldValue(row, 'Start Date')) || new Date().toISOString().split('T')[0],
          renewalDate: excelDateToJSDate(getFieldValue(row, 'Renewal Date')) || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          status: getFieldValue(row, 'Status') || 'Active',
          healthScore: parseInt(getFieldValue(row, 'Health Score')) || 85,
          projectCount: parseInt(getFieldValue(row, 'Project Count')) || 1,
          accountManager: getFieldValue(row, 'Account Manager') || ''
        })).filter(customer => customer.companyName);

        setCustomers([...customers, ...newCustomers]);
        alert(`Successfully imported ${newCustomers.length} customers!`);
      };

      const importPierianOpportunities = async(jsonData) => {
         // Using global API_BASE_URL defined at top of file

        // Helper function to normalize sales stage to proper case (first letter uppercase, rest lowercase)
        const normalizeSalesStage = (stage) => {
          if (!stage || stage.length === 0) return stage;
          return stage.charAt(0).toUpperCase() + stage.substring(1).toLowerCase();
        };

        // Helper function to add missing master data values
        const addMissingMasterData = async (endpoint, existingValues, newValues, setStateFunc, stateName) => {
          const existingLower = existingValues.map(v => v.name.toLowerCase().trim());
          const missingValues = [...new Set(newValues)]
            .filter(v => v && v.trim())
            .filter(v => !existingLower.includes(v.toLowerCase().trim()));

          if (missingValues.length > 0) {
            console.log(`📥 Adding ${missingValues.length} missing ${stateName}:`, missingValues);
            const newItems = [];

            for (const name of missingValues) {
              try {
                const response = await fetch(`${API_BASE_URL}/master/${endpoint}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ name: name.trim() })
                });

                if (response.ok) {
                  const result = await response.json();
                  if (result.success && result.data) {
                    newItems.push({ id: result.data.id, name: result.data.name, isActive: true });
                    console.log(`  ✅ Added ${stateName}: "${name}"`);
                  }
                } else {
                  console.warn(`  ⚠️ Failed to add ${stateName}: "${name}"`);
                }
              } catch (error) {
                console.error(`  ❌ Error adding ${stateName} "${name}":`, error);
              }
            }

            // Update state with new items
            if (newItems.length > 0) {
              setStateFunc(prev => [...prev, ...newItems]);
            }

            return newItems;
          }
          return [];
        };

        console.log('Starting Pierian Import (to Leads table)...');
        console.log('Total rows:', jsonData.length);

        setUploadStatus(`Processing ${jsonData.length} records...`);

        // DEBUG: Log all column names from first row
        if (jsonData.length > 0) {
          console.log('\n🔍 EXCEL COLUMN NAMES DETECTED:');
          console.log(Object.keys(jsonData[0]));
          console.log('\n');
        }

        setUploadProgress(92);
        setUploadStatus('Checking for duplicates...');

        // Fetch existing leads from database for duplicate checking
        let existingLeadsData = [];
        try {
          const response = await fetch(`${API_BASE_URL}/leads`);
          const result = await response.json();
          if (result.success && result.data) {
            existingLeadsData = result.data;
          }
        } catch (error) {
          console.error('Error fetching existing leads:', error);
        }

        // Track unique users to create (Account Owners, Bid Managers, etc.)
        const usersToCreate = new Map();
        const customersToCreate = new Map();
        const stageMappings = [];

        const parsedOpportunities = jsonData.map((row, index) => {
          // Get all possible field values
          const companyName = getFieldValue(row, 'Opportunity Name', 'Account Name', 'Company Name', 'Associated Account');

          // Debug: Log all column names to find exact Opportunity Type column name
          if (index === 0) {
            console.log('📋 Excel Column Names:', Object.keys(row));
            const oppTypeColumns = Object.keys(row).filter(k => k.toLowerCase().includes('type') || k.toLowerCase().includes('opportunity'));
            console.log('🔍 Columns containing "type" or "opportunity":', oppTypeColumns);
          }

          const opportunityType = getFieldValue(row, 'Opportunity Type\n(Farming / Cross-Selling / New Logo)', 'Opportunity Type\r\n(Farming / Cross-Selling / New Logo)', 'Opportunity Type', 'Type', 'Opp Type') || 'New Logo';

          // Debug: Log opportunity type for first few records
          if (index < 5) {
            console.log(`📊 Row ${index + 1} - ${companyName}: opportunityType = "${opportunityType}"`);
          }

          const geography = getFieldValue(row, 'Geography', 'Region', 'Country');
          const accountOwner = getFieldValue(row, 'Account Owner', 'Owner', 'Sales Rep');
          const serviceLine = getFieldValue(row, 'Service Line - L1', 'Service Line', 'Services') || 'Customer Support';
          const industryVertical = getFieldValue(row, 'Industry Vertical', 'Industry', 'Vertical');
          const industrySubVertical = getFieldValue(row, 'Industry Sub Vertical', 'Sub Vertical', 'Industry Segment');
          const primaryContact = getFieldValue(row, 'Primary Contact', 'Contact Person', 'Contact Name');
          const email = getFieldValue(row, 'Email', 'Contact Email', 'Primary Email');
          const leadSupporter = getFieldValue(row, 'Lead Supporter') || getFieldValue(row, 'Bid Manager', 'Manager');
          const executiveSponsor = getFieldValue(row, 'Executive Sponsor', 'Sponsor');

          // Extract and create users
          [accountOwner, leadSupporter, executiveSponsor].forEach(userName => {
            if (userName && userName.trim() && !usersToCreate.has(userName)) {
              // Check if user already exists
              const existingUser = users.find(u => u.name.toLowerCase() === userName.toLowerCase());
              if (!existingUser) {
                usersToCreate.set(userName, {
                  userId: `USER-${String(users.length + usersToCreate.size + 1).padStart(3, '0')}`,
                  name: userName,
                  email: `${userName.toLowerCase().replace(/\s+/g, '.')}@company.com`,
                  role: 'Sales Representative',
                  department: 'Sales',
                  status: 'Active',
                  isAdmin: false
                });
              }
            }
          });

          // Handle date fields
          const initiationDateValue = getFieldValue(row, 'Initiation Date', 'Start Date', 'Date Created');
          const initiationDate = excelDateToJSDate(initiationDateValue) || new Date().toISOString().split('T')[0];

          const estimatedCloseDateValue = getFieldValue(row, 'Estimated Close Date', 'Close Date', 'Expected Close');
          const estimatedCloseDate = excelDateToJSDate(estimatedCloseDateValue) || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

          const actualCloseDateValue = getFieldValue(row, 'Actual Close Date', 'Closed Date');
          const actualCloseDate = excelDateToJSDate(actualCloseDateValue);

          // Handle numeric fields - remove $ and commas before parsing
          const acvRaw = getFieldValue(row, 'Annual Contract Value', 'ACV', 'Annual Contract Value (ACV)', 'Contract Value');
          const acvCleaned = String(acvRaw || '').trim().replace(/[$,\s]/g, '');
          const acv = acvCleaned ? parseFloat(acvCleaned) : 0;

          const tcvRaw = getFieldValue(row, 'Total Contract Value', 'TCV', 'Total Contract Value (TCV)', 'Total Value');
          const tcvCleaned = String(tcvRaw || '').trim().replace(/[$,\s]/g, '');
          const tcv = tcvCleaned ? parseFloat(tcvCleaned) : 0;

          // Debug logging for currency values
          if (index < 3) { // Log first 3 records
            console.log(`\n📊 Record ${index + 1}: ${companyName}`);
            console.log(`   ACV Raw: "${acvRaw}" → Cleaned: "${acvCleaned}" → Parsed: ${acv}`);
            console.log(`   TCV Raw: "${tcvRaw}" → Cleaned: "${tcvCleaned}" → Parsed: ${tcv}`);
          }

          const probMultiplier = parseFloat(getFieldValue(row, 'Forecast Multiplier', 'Probability Multiplier')) || 0.45;

          // Create customer from opportunity if doesn't exist
          if (companyName && !customersToCreate.has(companyName)) {
            const existingCustomer = customers.find(c => c.companyName === companyName);
            if (!existingCustomer) {
              customersToCreate.set(companyName, {
                customerId: `CUST-${String(customers.length + customersToCreate.size + 1).padStart(3, '0')}`,
                companyName: companyName,
                primaryContact: primaryContact || 'N/A',
                email: email || 'N/A',
                phone: 'N/A',
                serviceLine: serviceLine,
                geography: geography,
                contractValue: acv,
                startDate: initiationDate,
                renewalDate: estimatedCloseDate,
                status: 'Active',
                healthScore: 85,
                projectCount: 1
              });
            }
          }

          // Smart stage matching
          const originalStage = getFieldValue(row, 'Sales Stage', 'Stage', 'Opportunity Stage');
          const matchedStage = matchStageToClosest(originalStage);

          // Track stage mapping
          if (originalStage) {
            stageMappings.push({
              original: originalStage,
              matched: matchedStage,
              opportunityName: companyName || 'Unknown'
            });
          }

          return {
            sn: leads.length + index + 1,
            opportunityName: companyName,
            opportunityType: opportunityType,
            geography: geography ? geography.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : '',
            accountOwner: accountOwner || '',
            associatedAccount: companyName,
            primaryContact: primaryContact,
            primaryContactEmail: email,
            phoneNumber: getFieldValue(row, 'Phone Number', 'Phone', 'Contact Phone') || '',
            email: email,
            influencers: getFieldValue(row, 'Influencers', 'Influencer'),
            decisionMakingTeam: getFieldValue(row, 'Decision Making Team', 'Decision Makers') || primaryContact,
            industryVertical: industryVertical || '',
            industrySubVertical: industrySubVertical || '',
            serviceLine: serviceLine,
            serviceOffering: getFieldValue(row, 'Service Offering - L2', 'Service Offering') || '',
            annualContractValue: acv,
            totalContractValue: tcv,
            priorityLevel: getFieldValue(row, 'Priority Level', 'Priority') || 'Medium',
            initiationDate: initiationDate,
            aging: parseInt(getFieldValue(row, 'Aging', 'Days Open', 'Age')) || 0,
            estimatedCloseDate: estimatedCloseDate,
            actualCloseDate: actualCloseDate,
            salesStatus: normalizeSalesStage(getFieldValue(row, 'Sales Status', 'Status')) || 'Warm',
            salesStatusOverride: getFieldValue(row, 'Sales Status', 'Status') || null,  // Preserve imported value as override
            lastEditedDate: null,  // Explicitly set to null for Excel imports
            salesStage: normalizeSalesStage(matchedStage),
            solutionScope: getFieldValue(row, 'Solution Scope', 'Scope', 'Solution'),
            engagementHistory: getFieldValue(row, 'Engagement History', 'History', 'Notes'),
            leadSupporter: leadSupporter || '',
            executiveSponsor: executiveSponsor || '',
            probabilityToClose: getFieldValue(row, 'Probability to Close', 'Probability') || 'Medium',
            probabilityPercent: getFieldValue(row, 'Probability %', 'Win Probability') || '36% - 50%',
            forecastMultiplier: probMultiplier,
            actualValue: parseFloat(getFieldValue(row, 'Actual Value', 'Actual Revenue')) || 0,
            probabilityValue: acv * probMultiplier,
            budget: parseFloat(getFieldValue(row, 'Budget')) || acv,
            estimatedRevenue: acv,
            actualRevenue: actualCloseDate ? acv : null,
            timeline: parseInt(getFieldValue(row, 'Timeline', 'Duration')) || 6,
            painPoints: getFieldValue(row, 'Pain Points/Needs', 'Pain Points', 'Needs'),
            competitors: getFieldValue(row, 'Competitors', 'Competition'),
            notes: getFieldValue(row, 'Engagement History', 'History', 'Notes') ? String(getFieldValue(row, 'Engagement History', 'History', 'Notes')).substring(0, 100) : '',
            comments: getFieldValue(row, 'Comments/Notes', 'Comments', 'Notes'),
            assessmentScore: parseInt(getFieldValue(row, 'Assessment Score', 'Score')) || 0
          };
        }).filter(opp => opp.opportunityName);

        console.log('Valid records:', parsedOpportunities.length);

        // ========== AUTO-ADD MISSING MASTER DATA ==========
        setUploadStatus('Checking for missing master data...');
        console.log('\n📋 Checking for missing master data values...');

        // Extract unique values from parsed records
        const importedGeographies = parsedOpportunities.map(o => o.geography).filter(Boolean);
        const importedServiceLines = parsedOpportunities.map(o => o.serviceLine).filter(Boolean);
        const importedIndustries = parsedOpportunities.map(o => o.industryVertical).filter(Boolean);
        const importedServiceOfferings = parsedOpportunities.map(o => o.serviceOffering).filter(Boolean);

        // Add missing values to master tables
        await addMissingMasterData('geographies', geographies, importedGeographies, setGeographies, 'geographies');
        await addMissingMasterData('service-lines', serviceLines, importedServiceLines, setServiceLines, 'service lines');
        await addMissingMasterData('industries', industries, importedIndustries, setIndustries, 'industries');
        await addMissingMasterData('service-offerings', serviceOfferings, importedServiceOfferings, setServiceOfferings, 'service offerings');

        console.log('✅ Master data check complete\n');
        setUploadStatus('Processing leads...');

        // Duplicate checking: Filter out leads that already exist (by company_name)
        const newOpportunities = [];
        const updatedOpportunities = [];
        const duplicateCount = parsedOpportunities.length;

        parsedOpportunities.forEach((opp, index) => {
          // Check for duplicate based on company name (PRIMARY CHECK for leads)
          const isDuplicate = existingLeadsData.find(existing => {
            if (existing.company_name && opp.opportunityName) {
              return existing.company_name.toLowerCase().trim() === opp.opportunityName.toLowerCase().trim();
            }
            return false;
          });

          if (isDuplicate) {
            console.log(`⚠️ Duplicate skipped: ${opp.opportunityName}`);
            updatedOpportunities.push({ ...opp, dbId: isDuplicate.id, existingId: isDuplicate.lead_id });
          } else {
            newOpportunities.push(opp);
            console.log(`✅ New lead: ${opp.opportunityName}`);
          }
        });

        console.log(`\n📊 Import Summary:`);
        console.log(`✅ New leads: ${newOpportunities.length}`);
        console.log(`⚠️ Duplicates skipped: ${updatedOpportunities.length}`);

        // Create new users
        const newUsersList = Array.from(usersToCreate.values());
        if (newUsersList.length > 0) {
          setUsers([...users, ...newUsersList]);
          console.log(`Created ${newUsersList.length} new users/team members`);
        }

        // Create new customers
        const newCustomersList = Array.from(customersToCreate.values());
        if (newCustomersList.length > 0) {
          setCustomers([...customers, ...newCustomersList]);
          console.log(`Created ${newCustomersList.length} new customers`);
        }

        // Note: leads state will be refreshed from DB after save

        // Log stage mappings
        if (stageMappings.length > 0) {
          console.log('Stage Mappings:', stageMappings);
          const uniqueMappings = [...new Map(stageMappings.map(item => [item.original, item])).values()];
          console.log('Unique mappings:', uniqueMappings.map(m => `"${m.original}" → "${m.matched}"`).join(', '));
        }
         // Convert to API format for leads table (camelCase → snake_case)
        const apiLeads = newOpportunities.map((opp) => ({
          company_name: opp.opportunityName,
          associated_account: opp.associatedAccount,
          industry: opp.industryVertical,
          geography: opp.geography,
          service_line: opp.serviceLine,
          service_offering: opp.serviceOffering || null,
          entity: opp.entity || null,
          contact_person: opp.primaryContact || null,
          email: opp.primaryContactEmail || null,
          phone: opp.phoneNumber || null,
          lead_owner: opp.accountOwner,
          sales_representative: opp.accountOwner,
          priority: opp.priorityLevel,
          date_captured: opp.initiationDate,
          engagement_history: opp.engagementHistory || null,
          comments: opp.comments || null,
          estimated_revenue: opp.estimatedRevenue,
          budget: opp.budget,
          pain_points: opp.painPoints || null,
          timeline: String(opp.timeline || ''),
          // New import fields
          opportunity_type: opp.opportunityType,
          lead_supporter: opp.leadSupporter || null,
          executive_sponsor: opp.executiveSponsor || null,
          solution_architect: null,
          solution_scope: opp.solutionScope || null,
          expected_close_date: opp.estimatedCloseDate || null,
          actual_close_date: opp.actualCloseDate || null,
          probability: String((() => {
            const rawProb = opp.probability || opp.probabilityPercent || '';
            const probNum = parseInt(String(rawProb).replace(/[^0-9]/g, ''), 10);
            if (!isNaN(probNum) && probNum >= 0 && probNum <= 100) {
              return probNum;
            }
            return calculateProbabilityByStage(opp.salesStage).percent;
          })()),
          probability_override: opp.probabilityOverride != null ? Number(parseInt(String(opp.probabilityOverride).replace(/[^0-9]/g, ''), 10)) : null,
          annual_contract_value: opp.annualContractValue,
          total_contract_value: opp.totalContractValue,
          sales_stage: opp.salesStage,
          status: opp.salesStatus,
          sales_status_override: opp.salesStatusOverride || null,
          lead_status: 'Open',
          opportunity_category: null,
          opportunity_classification: null,
          win_loss_reason: null,
          latest_comment: null,
          last_edited_date: null,
          lead_history: opp.leadHistory || null,
          source_lead_id: opp.sourceLeadId || null
        }));

        console.log('\n╔════════════════════════════════════════════════════════╗');
        console.log('║       IMPORTING TO LEADS TABLE VIA API                 ║');
        console.log('╚════════════════════════════════════════════════════════╝');
        console.log('📊 Total leads to import:', apiLeads.length);
        if (apiLeads.length > 0) {
          console.log('🔍 First lead sample:');
          console.log(JSON.stringify(apiLeads[0], null, 2));
        }

        console.log('\n📤 ABOUT TO SEND TO:', `${API_BASE_URL}/leads`);
        console.log('════════════════════════════════════════════════════════\n');

        // ========= SAVE TO DATABASE =========
        // Only save new leads (non-duplicates)
        if (newOpportunities.length > 0) {
          setUploadProgress(95);
          setUploadStatus(`Saving ${newOpportunities.length} leads to database...`);
          try {
            // Send in chunks of 25 to avoid gateway timeout
            const CHUNK_SIZE = 25;
            let savedCount = 0;
            for (let i = 0; i < apiLeads.length; i += CHUNK_SIZE) {
              const chunk = apiLeads.slice(i, i + CHUNK_SIZE);
              console.log(`📤 Sending chunk ${Math.floor(i / CHUNK_SIZE) + 1}/${Math.ceil(apiLeads.length / CHUNK_SIZE)} (${chunk.length} leads)`);
              setUploadStatus(`Saving leads... ${savedCount}/${apiLeads.length}`);

              const response = await fetch(`${API_BASE_URL}/leads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(chunk)
              });

              if (!response.ok) {
                const errorData = await response.json();
                console.error('\n❌ IMPORT FAILED - Database Save Error');
                console.error('Status:', response.status);
                console.error('Error:', errorData);

                alert(`⚠️ Failed to save to database!\n\nError: ${errorData.error || errorData.message}\n\nSaved ${savedCount} of ${apiLeads.length} leads before failure.`);
                setImporting(false);
                setUploadProgress(0);
                setUploadStatus('');
                return;
              }

              const result = await response.json();
              if (!result.success) throw new Error(result.message || 'Save failed');
              savedCount += chunk.length;
              console.log(`✅ Chunk saved (${savedCount}/${apiLeads.length})`);
            }

            console.log('✅ All leads saved successfully!');

            // Refresh leads from database using proper field mapping
            if (_fetchLeads) await _fetchLeads();

          } catch (error) {
            console.error('\n❌ API CALL FAILED');
            console.error('Error:', error);

            if (isUnloadingRef.current || error.name === 'AbortError') {
              console.log('ℹ️ Request was aborted due to page unload - not showing error alert');
              return;
            }

            alert(`⚠️ Failed to save to database!\n\nError: ${error.message}\n\nPlease check:\n1. Backend server is running\n2. API URL is correct: ${API_BASE_URL}/leads`);
            setImporting(false);
            setUploadProgress(0);
            setUploadStatus('');
            return;
          }
        } else {
          console.log('ℹ️ No new leads to save - all were duplicates');
        }
        // ========= END DATABASE SAVE =========

        // Show success message
        let message = `Import Complete!\n\n`;
        message += `📊 Summary:\n`;
        message += `  • New Leads: ${newOpportunities.length}\n`;
        message += `  • Duplicates Skipped: ${updatedOpportunities.length}\n`;
        message += `  • Total Processed: ${duplicateCount}\n`;
        message += `  • New Team Members: ${newUsersList.length}\n`;
        message += `  • New Customers: ${newCustomersList.length}\n`;

        if (stageMappings.length > 0) {
          const uniqueMappings = [...new Map(stageMappings.map(item => [item.original, item])).values()];
          if (uniqueMappings.some(m => m.original.toLowerCase() !== m.matched.toLowerCase())) {
            message += `\n💡 Stage Mapping: Sales stages were intelligently mapped to CRM stages. Check console for details.`;
          }
        }

        setUploadProgress(100);
        setUploadStatus('Import complete!');

        alert(message);

        // Hide loader and redirect to leads page after all leads are loaded
        setTimeout(() => {
          setImporting(false);
          setUploadProgress(0);
          setUploadStatus('');
          setActiveTab('leads');
        }, 500);
      };

       

      // Clear All Data
      const handleClearAllData = () => {
        if (window.confirm('⚠️ WARNING: This will permanently delete ALL leads, opportunities, and customers from the system. This action cannot be undone!\n\nAre you absolutely sure you want to continue?')) {
          if (window.confirm('Final confirmation: Type "DELETE" in the next prompt to proceed.')) {
            const confirmation = prompt('Type DELETE to confirm:');
            if (confirmation === 'DELETE') {
              setLeads([]);
              setOpportunities([]);
              setCustomers([]);
              alert('✓ All data has been cleared successfully.');
            } else {
              alert('Deletion cancelled - confirmation text did not match.');
            }
          }
        }
      };

      return (
        <div className="space-y-6 relative">
          {/* Loading Overlay with Progress */}
          {importing && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-8 max-w-md mx-auto text-center shadow-2xl">
                <div className="relative mb-6">
                  {uploadProgress < 100 ? (
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pierian mx-auto"></div>
                  ) : (
                    <div className="rounded-full h-16 w-16 bg-sage-tint flex items-center justify-center mx-auto">
                      <svg className="h-10 w-10 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">
                  {uploadProgress < 100 ? 'Importing Data...' : 'Import Complete!'}
                </h3>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-3 mb-3 overflow-hidden">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ease-out ${uploadProgress < 100 ? 'bg-pierian' : 'bg-sage-tint0'}`}
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>

                {/* Progress Percentage */}
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>{uploadStatus || 'Processing...'}</span>
                  <span className="font-semibold">{uploadProgress}%</span>
                </div>

                <p className="text-sm text-gray-500 mt-2">
                  {uploadProgress < 100
                    ? 'Please wait while we process your Excel file and store the data.'
                    : 'Redirecting...'}
                </p>
              </div>
            </div>
          )}

          <div>
            <h2 className="text-sm font-bold text-gray-900">Data Management</h2>
            <p className="text-xs text-gray-500 mt-0.5">Import, export, and manage your CRM data</p>
          </div>

          {/* Export Section */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Upload size={16} style={{ color: '#00BBA2' }} />
              <h3 className="text-sm font-semibold text-gray-900">Export Data</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3">Export your data to Excel format for backup or analysis.</p>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
              <button
                onClick={handleExportOpportunities}
                className="flex items-center gap-2 justify-center px-4 py-2 rounded-md font-medium text-sm"
                style={{ backgroundColor: '#00BBA2', color: '#ffffff' }}
              >
                <Target size={16} />
                Export Opportunities ({opportunities.length})
              </button>
            </div>
          </Card>

          {/* Import Section */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Upload size={16} style={{ color: '#9CAF88' }} />
              <h3 className="text-sm font-semibold text-gray-900">Import Data</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3">Import data from Excel files. Use the templates below for correct format.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => handleImport('opportunities')}
                disabled={false}
                className="flex items-center gap-2 justify-center px-4 py-2 rounded-md font-medium text-sm"
                style={{ backgroundColor: '#6A7CA5', color: '#ffffff' }}
              >
                <Upload size={16} />
                {importing && importType === 'opportunities' ? 'Importing...' : 'Import Leads'}
              </button>
              <button
                onClick={() => handleImport('opportunities-direct')}
                disabled={false}
                className="flex items-center gap-2 justify-center px-4 py-2 rounded-md font-medium text-sm"
                style={{ backgroundColor: '#00BBA2', color: '#ffffff' }}
              >
                <Target size={16} />
                {importing && importType === 'opportunities-direct' ? 'Importing...' : 'Import Opportunities'}
              </button>
            </div>
          </Card>

          {/* Sample Templates Section */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={16} style={{ color: '#481F89' }} />
              <h3 className="text-sm font-semibold text-gray-900">Sample Templates</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3">Download Excel templates with sample data and correct column headers for importing.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={generateLeadsTemplate}
                className="flex items-center gap-2 justify-center px-4 py-2 rounded-md font-medium text-sm"
                style={{ backgroundColor: '#6A7CA5', color: '#ffffff' }}
              >
                <FileText size={16} />
                Download Leads Template
              </button>
              <button
                onClick={generateOpportunitiesTemplate}
                className="flex items-center gap-2 justify-center px-4 py-2 rounded-md font-medium text-sm"
                style={{ backgroundColor: '#D9A441', color: '#ffffff' }}
              >
                <FileText size={16} />
                Download Opportunities Template
              </button>
            </div>
          </Card>

          {/* Clear Data Section */}
          <Card className="p-4" style={{ borderColor: 'rgba(209,86,86,0.12)' }}>
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={16} style={{ color: '#00BBA2' }} />
              <h3 className="text-sm font-semibold" style={{ color: '#00BBA2' }}>Danger Zone</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3">Permanently delete all data from the system. This action cannot be undone!</p>
            <button
              onClick={handleClearAllData}
              className="flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm"
              style={{ backgroundColor: '#009E8A', color: '#ffffff' }}
            >
              <Trash2 size={16} />
              Clear All Data
            </button>
          </Card>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200">
          <div className="flex gap-1">
            {settingsTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setSettingsTab(tab.id)}
                  className="flex items-center gap-1 py-2 px-2 border-b-2 font-medium text-xs transition-colors whitespace-nowrap"
                  style={{
                    borderBottomColor: settingsTab === tab.id ? '#00BBA2' : 'transparent',
                    color: settingsTab === tab.id ? '#00BBA2' : (isDark ? '#D4D4D4' : '#6B7280')
                  }}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {settingsTab === 'users' && <UsersView onNewUser={() => { setSelectedUser(null); setUserFormServiceLines([]); setShowUserModal(true); }} />}
        {settingsTab === 'sales-stages' && (
          <ReferenceDataView
            title="Sales Stages"
            data={salesStages}
            setData={setSalesStages}
            type="sales-stages"
          />
        )}
        {settingsTab === 'industries' && (
          <ReferenceDataView
            title="Industries"
            data={industries}
            setData={setIndustries}
            type="industries"
          />
        )}
        {settingsTab === 'service-lines' && (
          <ReferenceDataView
            title="Service Lines"
            data={serviceLines}
            setData={setServiceLines}
            type="service-lines"
          />
        )}
        {settingsTab === 'service-offerings' && (
          <ReferenceDataView
            title="Service Offerings"
            data={serviceOfferings}
            setData={setServiceOfferings}
            type="service-offerings"
            hasServiceLine={true}
          />
        )}
        {settingsTab === 'entities' && (
          <ReferenceDataView
            title="Entities"
            data={entities}
            setData={setEntities}
            type="entities"
            hasEntityGroup={true}
          />
        )}
        {settingsTab === 'geographies' && (
          <ReferenceDataView
            title="Geographies"
            data={geographies}
            setData={setGeographies}
            type="geographies"
            hasRegion={true}
          />
        )}
        {settingsTab === 'tags' && <TagsManagementView />}
        {settingsTab === 'data-management' && <DataManagementView />}

        {showRefDataModal && <RefDataModal />}
      </div>
    );
  };

  // ── Compact context for AICopilot (same shape as CopilotView's prepareCRMContext) ──
  const copilotContext = React.useMemo(() => {
    const today = new Date();
    const todayTime = today.getTime();
    const daysSince = (d: string | null | undefined): number | null => {
      if (!d) return null;
      const t = new Date(d).getTime();
      return isNaN(t) ? null : Math.floor((todayTime - t) / 86400000);
    };
    const daysUntil = (d: string | null | undefined): number | null => {
      if (!d) return null;
      const t = new Date(d).getTime();
      return isNaN(t) ? null : Math.floor((t - todayTime) / 86400000);
    };

    // Filter by geography
    const accessible = opportunities.filter((opp: any) =>
      currentUser?.isAdmin ||
      (currentUser?.geographies?.length > 0 && opp.geography && currentUser.geographies.includes(opp.geography))
    );

    // Single-pass: summaries + aggregations + attention candidates
    const byStage: Record<string, number> = {};
    const byStatus: Record<string, number> = {};
    const revByStage: Record<string, number> = {};
    const ownerAgg: Record<string, { count: number; acv: number }> = {};
    const geoAgg: Record<string, { count: number; acv: number }> = {};
    const slAgg: Record<string, { count: number; acv: number }> = {};
    let activeCount = 0, pipelineVal = 0, staleCount = 0, overdueCount = 0;
    const attCandidates: any[] = [];

    accessible.forEach((opp: any) => {
      const stage = opp.salesStage || 'Unknown';
      const status = getEffectiveSalesStatus(opp);
      const acv = opp.annualContractValue || 0;
      byStage[stage] = (byStage[stage] || 0) + 1;
      byStatus[status] = (byStatus[status] || 0) + 1;
      revByStage[stage] = (revByStage[stage] || 0) + acv;

      const isActive = stage !== 'Closed-Won' && stage !== 'Closed-Lost' && status !== 'Inactive';
      if (isActive) {
        activeCount++;
        pipelineVal += acv;
        const owner = opp.leadSupporter || opp.accountOwner || 'Unassigned';
        const geo = opp.geography || 'Unknown';
        const sl = opp.serviceLine || 'Unknown';
        if (!ownerAgg[owner]) ownerAgg[owner] = { count: 0, acv: 0 };
        ownerAgg[owner].count++; ownerAgg[owner].acv += acv;
        if (!geoAgg[geo]) geoAgg[geo] = { count: 0, acv: 0 };
        geoAgg[geo].count++; geoAgg[geo].acv += acv;
        if (!slAgg[sl]) slAgg[sl] = { count: 0, acv: 0 };
        slAgg[sl].count++; slAgg[sl].acv += acv;

        const dsa = daysSince(opp.lastEditedDate);
        const dtc = daysUntil(opp.expectedCloseDate);
        if (dsa !== null && dsa > 30) staleCount++;
        if (dtc !== null && dtc < 0) overdueCount++;
        if ((dtc !== null && dtc < 0) || (dsa !== null && dsa > 30) || (dtc !== null && dtc <= 14)) {
          attCandidates.push({ name: opp.opportunityName, account: opp.associatedAccount, stage, value: acv, owner, daysToClose: dtc, daysSinceActivity: dsa, status });
        }
      }
    });

    const fmtAcv = (v: number) => v >= 1000000 ? `₹${(v / 1000000).toFixed(1)}M` : `₹${(v / 1000).toFixed(0)}K`;
    const fmtAgg = (agg: Record<string, { count: number; acv: number }>) =>
      Object.entries(agg).sort((a, b) => b[1].acv - a[1].acv).map(([k, v]) => `${k}:${v.count}/${fmtAcv(v.acv)}`).join(', ');

    // Lead aggregations (geo-filtered)
    const accessibleLeads = leads.filter((l: any) =>
      currentUser?.isAdmin ||
      (currentUser?.geographies?.length > 0 && l.geography && currentUser.geographies.includes(l.geography))
    );
    const leadByStatus: Record<string, number> = {};
    const leadBySource: Record<string, number> = {};
    accessibleLeads.forEach((l: any) => {
      leadByStatus[l.leadStatus || l.status || 'Unknown'] = (leadByStatus[l.leadStatus || l.status || 'Unknown'] || 0) + 1;
      leadBySource[l.leadSource || l.source || 'Unknown'] = (leadBySource[l.leadSource || l.source || 'Unknown'] || 0) + 1;
    });

    // Customer aggregations (geo-filtered)
    const accessibleCustomers = customers.filter((c: any) =>
      currentUser?.isAdmin ||
      (currentUser?.geographies?.length > 0 && c.geography && currentUser.geographies.includes(c.geography))
    );
    let activeCust = 0, totalHS = 0, hsCount = 0;
    accessibleCustomers.forEach((c: any) => {
      if (c.status === 'Active') activeCust++;
      if (c.healthScore != null) { totalHS += c.healthScore; hsCount++; }
    });

    // Task aggregations (geo-filtered)
    const accessibleTasks = tasks.filter((t: any) =>
      currentUser?.isAdmin ||
      (currentUser?.geographies?.length > 0 && t.geography && currentUser.geographies.includes(t.geography))
    );
    const openTasks = accessibleTasks.filter((t: any) => t.status !== 'Completed' && t.status !== 'Done');
    const ovrTasks = openTasks.filter((t: any) => t.dueDate && new Date(t.dueDate) < today);

    return {
      summary: {
        totalOpportunities: accessible.length,
        activeOpportunities: activeCount,
        totalPipelineValue: pipelineVal,
        opportunitiesByStage: byStage,
        opportunitiesByStatus: byStatus,
        revenueByStage: revByStage,
        staleOpportunitiesCount: staleCount,
        overdueOpportunitiesCount: overdueCount,
        todayDate: today.toISOString().split('T')[0],
        byOwner: fmtAgg(ownerAgg),
        byGeography: fmtAgg(geoAgg),
        byServiceLine: fmtAgg(slAgg)
      },
      attentionDeals: attCandidates
        .sort((a, b) => ((b.daysToClose !== null && b.daysToClose < 0) ? 1 : 0) - ((a.daysToClose !== null && a.daysToClose < 0) ? 1 : 0) || (b.value || 0) - (a.value || 0))
        .slice(0, 5),
      // Compact list of ALL active opportunities so Copilot can answer per-person/per-deal queries
      allDeals: accessible
        .filter((opp: any) => {
          const stage = opp.salesStage || 'Unknown';
          const status = getEffectiveSalesStatus(opp);
          return stage !== 'Closed-Won' && stage !== 'Closed-Lost' && status !== 'Inactive';
        })
        .map((opp: any) => ({
          id: opp.opportunityId,
          name: opp.opportunityName,
          account: opp.associatedAccount || opp.opportunityName,
          stage: opp.salesStage,
          acv: opp.annualContractValue || 0,
          owner: opp.accountOwner || 'Unassigned',
          leadSupporter: opp.leadSupporter || '',
          geo: opp.geography || '',
          sl: opp.serviceLine || '',
          status: getEffectiveSalesStatus(opp),
          closeDate: opp.expectedCloseDate || null,
          lastActivity: opp.lastEditedDate || null
        })),
      leads: { total: accessibleLeads.length, byStatus: leadByStatus, bySource: leadBySource },
      customers: { total: accessibleCustomers.length, active: activeCust, avgHealthScore: hsCount > 0 ? Math.round(totalHS / hsCount) : 0 },
      tasks: {
        total: accessibleTasks.length,
        open: openTasks.length,
        overdue: ovrTasks.length,
        topOverdue: ovrTasks.slice(0, 3).map((t: any) => ({
          task: (t.taskDescription || t.task || '').substring(0, 80),
          owner: t.assignedTo || t.owner || 'Unassigned',
          dueDate: t.dueDate
        }))
      },
      currentUser: currentUser ? { name: currentUser.name, role: currentUser.role, email: currentUser.email, isAdmin: currentUser.isAdmin, geographies: currentUser.geographies } : null
    };
  }, [opportunities, leads, customers, tasks, currentUser]);

  // Show loading while checking authentication
  if (authChecking) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-pierian mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} apiBaseUrl={API_BASE_URL} />;
  }

  return (
      <div className="h-screen flex flex-col crm-shell overflow-hidden" data-theme={theme.mode}>

      {/* ── SESSION EXPIRED BANNER (sticky, never auto-redirects) ── */}
      {sessionExpired && (
        <div
          role="alert"
          style={{
            background: '#dc2626',
            color: '#ffffff',
            padding: '0.625rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.875rem',
            fontSize: '0.8125rem',
            fontWeight: 500,
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            zIndex: 9999,
            flexShrink: 0,
          }}
        >
          <AlertCircle size={16} />
          <span>Your session has expired. Save any unsaved work, then click to re-login.</span>
          <button
            onClick={handleLogout}
            style={{
              padding: '0.25rem 0.875rem',
              background: '#ffffff',
              color: '#dc2626',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Re-login
          </button>
        </div>
      )}

      {/* ── IMPORT PROGRESS POPUP ──────────────────── */}
      {importing && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100]" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center shadow-2xl">
            <div className="relative mb-6">
              {uploadProgress < 100 ? (
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pierian mx-auto"></div>
              ) : (
                <div className="rounded-full h-16 w-16 bg-green-50 flex items-center justify-center mx-auto">
                  <svg className="h-10 w-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              {uploadProgress < 100 ? 'Importing Data...' : 'Import Complete!'}
            </h3>
            <div className="w-full bg-gray-100 rounded-full h-2 mb-3 overflow-hidden">
              <div className="h-2 rounded-full bg-pierian transition-all duration-300 ease-out" style={{ width: `${uploadProgress}%` }}></div>
            </div>
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>{uploadStatus || 'Processing...'}</span>
              <span className="font-semibold text-gray-700">{uploadProgress}%</span>
            </div>
            <p className="text-sm text-gray-400 mt-2">
              {uploadProgress < 100 ? 'Please wait while we process your file.' : 'Redirecting...'}
            </p>
          </div>
        </div>
      )}

      {/* ── HEADER ─────────────────────────────────── */}
      <header className="crm-header">
        <div className="crm-header-inner">

          {/* Logo */}
          <div className="crm-logo-area">
            <img src={pierianLogo} alt="Pierian" className="crm-logo-img" />
            <div className="crm-logo-divider"></div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <img src={vaultLogoNew} alt="Vault" className="crm-logo-img" style={{ height: '46px' }} />
              <span style={{ fontSize: '0.6rem', color: '#00BBA2', letterSpacing: '0.04em', marginTop: '-4px', whiteSpace: 'nowrap' }}>Sales &amp; Customer Management Platform</span>
            </div>
          </div>

          {/* Right actions */}
          <div className="crm-header-actions">
            <div className="relative notification-dropdown-container">
              <button
                className={`crm-action-btn relative${showNotificationDropdown ? ' crm-action-btn-active' : ''}`}
                title="Notifications"
                onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
              >
                <Bell size={17} />
                {(() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const cutoff = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                  const overdueCount = tasks.filter((t: any) => {
                    if (t.status === 'completed') return false;
                    if (dismissedNotifIds.has(`ov-${t.id}`)) return false;
                    const due = t.dueDate ? new Date(t.dueDate) : null;
                    if (!due) return false;
                    due.setHours(0, 0, 0, 0);
                    return due < today;
                  }).length;
                  const dueTodayCount = tasks.filter((t: any) => {
                    if (t.status === 'completed') return false;
                    if (dismissedNotifIds.has(`dt-${t.id}`)) return false;
                    const due = t.dueDate ? new Date(t.dueDate) : null;
                    if (!due) return false;
                    due.setHours(0, 0, 0, 0);
                    return due.getTime() === today.getTime();
                  }).length;
                  const newLeadsCount = leads.filter((l: any) => l.createdDate && new Date(l.createdDate) >= cutoff && !dismissedNotifIds.has(`rl-${l.leadId}`)).length;
                  const newOppsCount = opportunities.filter((o: any) => o.createdAt && new Date(o.createdAt) >= cutoff && !dismissedNotifIds.has(`ro-${o.opportunityId}`)).length;
                  const newCustsCount = customers.filter((c: any) => c.createdAt && new Date(c.createdAt) >= cutoff && !dismissedNotifIds.has(`rc-${c.customerId}`)).length;
                  const total = overdueCount + dueTodayCount + newLeadsCount + newOppsCount + newCustsCount;
                  return total > 0 ? (
                    <span className="crm-notif-badge">{total > 9 ? '9+' : total}</span>
                  ) : (
                    <span className="crm-notif-dot"></span>
                  );
                })()}
              </button>

              {showNotificationDropdown && (() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const cutoff = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';

                const overdueTasks = tasks.filter((t: any) => {
                  if (t.status === 'completed') return false;
                  if (dismissedNotifIds.has(`ov-${t.id}`)) return false;
                  const due = t.dueDate ? new Date(t.dueDate) : null;
                  if (!due) return false;
                  due.setHours(0, 0, 0, 0);
                  return due < today;
                });
                const dueTodayTasks = tasks.filter((t: any) => {
                  if (t.status === 'completed') return false;
                  if (dismissedNotifIds.has(`dt-${t.id}`)) return false;
                  const due = t.dueDate ? new Date(t.dueDate) : null;
                  if (!due) return false;
                  due.setHours(0, 0, 0, 0);
                  return due.getTime() === today.getTime();
                });
                const recentLeads = leads
                  .filter((l: any) => l.createdDate && new Date(l.createdDate) >= cutoff && !dismissedNotifIds.has(`rl-${l.leadId}`))
                  .sort((a: any, b: any) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
                  .slice(0, 5);
                const recentOpps = opportunities
                  .filter((o: any) => o.createdAt && new Date(o.createdAt) >= cutoff && !dismissedNotifIds.has(`ro-${o.opportunityId}`))
                  .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .slice(0, 5);
                const recentCusts = customers
                  .filter((c: any) => c.createdAt && new Date(c.createdAt) >= cutoff && !dismissedNotifIds.has(`rc-${c.customerId}`))
                  .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .slice(0, 5);

                const totalNew = recentLeads.length + recentOpps.length + recentCusts.length;
                const hasAny = overdueTasks.length > 0 || dueTodayTasks.length > 0 || totalNew > 0;

                const allVisibleKeys = [
                  ...overdueTasks.slice(0, 5).map((t: any) => `ov-${t.id}`),
                  ...dueTodayTasks.slice(0, 5).map((t: any) => `dt-${t.id}`),
                  ...recentLeads.map((l: any) => `rl-${l.leadId}`),
                  ...recentOpps.map((o: any) => `ro-${o.opportunityId}`),
                  ...recentCusts.map((c: any) => `rc-${c.customerId}`),
                ];

                return (
                  <div className="crm-notif-dropdown">
                    <div className="crm-notif-dropdown-header">
                      <span className="crm-notif-dropdown-title">Notifications</span>
                      <div className="flex items-center gap-2">
                        {hasAny && (
                          <span className="crm-notif-count-badge">
                            {overdueTasks.length + dueTodayTasks.length + totalNew} new
                          </span>
                        )}
                        {hasAny && (
                          <button
                            className="crm-notif-clear-btn"
                            onClick={() => clearAllNotifs(allVisibleKeys)}
                            title="Clear all notifications"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="crm-notif-list">
                      {overdueTasks.slice(0, 5).map((t: any) => (
                        <div key={`ov-${t.id}`} className="crm-notif-item crm-notif-item-danger">
                          <div className="crm-notif-icon-wrap crm-notif-icon-danger">
                            <AlertCircle size={13} />
                          </div>
                          <div className="crm-notif-body">
                            <p className="crm-notif-text">Overdue task: <strong>{t.task || t.title || 'Task'}</strong></p>
                            <p className="crm-notif-sub">Due {fmtDate(t.dueDate)}{t.owner ? ` · ${t.owner}` : ''}</p>
                          </div>
                          <button className="crm-notif-dismiss" onClick={(e) => { e.stopPropagation(); dismissNotif(`ov-${t.id}`); }} title="Dismiss"><X size={11} /></button>
                        </div>
                      ))}
                      {dueTodayTasks.slice(0, 5).map((t: any) => (
                        <div key={`dt-${t.id}`} className="crm-notif-item crm-notif-item-warning">
                          <div className="crm-notif-icon-wrap crm-notif-icon-warning">
                            <Clock size={13} />
                          </div>
                          <div className="crm-notif-body">
                            <p className="crm-notif-text">Due today: <strong>{t.task || t.title || 'Task'}</strong></p>
                            <p className="crm-notif-sub">{t.owner || ''}{t.priority ? ` · ${t.priority}` : ''}</p>
                          </div>
                          <button className="crm-notif-dismiss" onClick={(e) => { e.stopPropagation(); dismissNotif(`dt-${t.id}`); }} title="Dismiss"><X size={11} /></button>
                        </div>
                      ))}
                      {recentLeads.map((l: any) => (
                        <div key={`rl-${l.leadId}`} className="crm-notif-item crm-notif-item-info"
                          style={{ cursor: 'pointer' }}
                          onClick={() => { setActiveTab('leads'); setShowNotificationDropdown(false); }}
                        >
                          <div className="crm-notif-icon-wrap crm-notif-icon-info">
                            <Users size={13} />
                          </div>
                          <div className="crm-notif-body">
                            <p className="crm-notif-text">New Lead: <strong>{l.companyName || l.contactPerson || 'Lead'}</strong></p>
                            <p className="crm-notif-sub">{fmtDate(l.createdDate)}{l.leadStatus ? ` · ${l.leadStatus}` : ''}</p>
                          </div>
                          <button className="crm-notif-dismiss" onClick={(e) => { e.stopPropagation(); dismissNotif(`rl-${l.leadId}`); }} title="Dismiss"><X size={11} /></button>
                        </div>
                      ))}
                      {recentOpps.map((o: any) => (
                        <div key={`ro-${o.opportunityId}`} className="crm-notif-item crm-notif-item-success"
                          style={{ cursor: 'pointer' }}
                          onClick={() => { setActiveTab('opportunities'); setShowNotificationDropdown(false); }}
                        >
                          <div className="crm-notif-icon-wrap crm-notif-icon-success">
                            <Target size={13} />
                          </div>
                          <div className="crm-notif-body">
                            <p className="crm-notif-text">New Opportunity: <strong>{o.opportunityName || o.associatedAccount || 'Opportunity'}</strong></p>
                            <p className="crm-notif-sub">{fmtDate(o.createdAt)}{o.salesStage ? ` · ${o.salesStage}` : ''}</p>
                          </div>
                          <button className="crm-notif-dismiss" onClick={(e) => { e.stopPropagation(); dismissNotif(`ro-${o.opportunityId}`); }} title="Dismiss"><X size={11} /></button>
                        </div>
                      ))}
                      {recentCusts.map((c: any) => (
                        <div key={`rc-${c.customerId}`} className="crm-notif-item crm-notif-item-purple"
                          style={{ cursor: 'pointer' }}
                          onClick={() => { setActiveTab('customers'); setShowNotificationDropdown(false); }}
                        >
                          <div className="crm-notif-icon-wrap crm-notif-icon-purple">
                            <Building size={13} />
                          </div>
                          <div className="crm-notif-body">
                            <p className="crm-notif-text">New Customer: <strong>{c.companyName || 'Customer'}</strong></p>
                            <p className="crm-notif-sub">{fmtDate(c.createdAt)}{c.serviceLine ? ` · ${c.serviceLine}` : ''}</p>
                          </div>
                          <button className="crm-notif-dismiss" onClick={(e) => { e.stopPropagation(); dismissNotif(`rc-${c.customerId}`); }} title="Dismiss"><X size={11} /></button>
                        </div>
                      ))}
                      {!hasAny && (
                        <div className="crm-notif-empty">
                          <CheckCircle size={28} className="crm-notif-empty-icon" />
                          <p>You're all caught up!</p>
                          <p className="crm-notif-empty-sub">No new activity in the last 7 days.</p>
                        </div>
                      )}
                    </div>
                    <div className="crm-notif-footer">
                      <button
                        className="crm-notif-footer-btn"
                        onClick={() => { setActiveTab('calendar'); setShowNotificationDropdown(false); }}
                      >
                        View all tasks
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
            <button onClick={toggleTheme} className="crm-action-btn" title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              {isDark ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <button onClick={() => setShowHelpModal(true)} className="crm-action-btn" title="Help">
              <HelpCircle size={17} />
            </button>
            {currentUser?.isAdmin && (
              <button
                onClick={() => setActiveTab('settings')}
                className={`crm-action-btn${activeTab === 'settings' ? ' crm-action-btn-active' : ''}`}
                title="Settings"
              >
                <Settings size={17} />
              </button>
            )}

            {/* User profile */}
            <div className="relative profile-dropdown-container">
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="crm-profile-btn"
              >
                {currentUser?.photoUrl ? (
                  <img src={currentUser.photoUrl} alt={currentUser.name} className="crm-avatar-img" />
                ) : (
                  <div className="crm-avatar-fallback">
                    <span>{currentUser ? currentUser.name.split(' ').map((n: string) => n[0]).join('') : 'U'}</span>
                  </div>
                )}
                <div className="crm-profile-info">
                  <p className="crm-profile-name">{currentUser?.name || 'CRM User'}</p>
                  <p className="crm-profile-role">{currentUser?.role || 'Sales'}</p>
                </div>
                <ChevronDown size={14} className={`crm-profile-chevron ${showProfileDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showProfileDropdown && (
                <div className="crm-profile-dropdown">
                  <div className="crm-profile-dropdown-header">
                    {currentUser?.photoUrl ? (
                      <img src={currentUser.photoUrl} alt={currentUser.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-pierian flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm font-semibold">
                          {currentUser ? currentUser.name.split(' ').map((n: string) => n[0]).join('') : 'U'}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{currentUser?.name || 'CRM User'}</p>
                      <p className="text-xs text-gray-500 truncate">{currentUser?.email || ''}</p>
                      <p className="text-xs font-medium" style={{ color: '#00BBA2' }}>{currentUser?.role || 'Sales'}</p>
                    </div>
                  </div>
                  <div className="p-2">
                    <button className="crm-dropdown-item" onClick={() => { setShowMyProfileModal(true); setShowProfileDropdown(false); }}>
                      <User size={15} className="text-gray-400" /><span>My Profile</span>
                    </button>
                    <div className="crm-dropdown-item" style={{ cursor: 'default' }}>
                      <Type size={15} className="text-gray-400 flex-shrink-0" />
                      <span className="flex-shrink-0">Display Size</span>
                      <div className="ml-auto flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                        {(['small', 'medium', 'large'] as const).map((s) => (
                          <button key={s} onClick={(e) => { e.stopPropagation(); setFontSize(s); }}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${fontSize === s ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                            style={fontSize === s ? { color: '#00BBA2' } : {}}>
                            <span style={{ fontSize: s === 'small' ? '10px' : s === 'medium' ? '12px' : '14px' }}>A</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="px-3 py-1.5 flex items-center gap-2 text-xs" style={{ color: '#00BBA2' }}>
                      <CheckCircle size={13} /><span className="font-medium">Connected to Microsoft 365</span>
                    </div>
                  </div>
                  <div className="border-t border-gray-100 p-2">
                    <button onClick={handleLogout} className="crm-dropdown-item text-pierian hover:bg-teal-50">
                      <LogOut size={15} /><span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── NAV BAR ─────────────────────────────────── */}
      <nav className="crm-navbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            ref={tab.id === 'dashboard' ? dashboardTabRef : undefined}
            onClick={() => setActiveTab(tab.id)}
            className={`crm-nav-item${activeTab === tab.id ? ' crm-nav-item-active' : ''}`}
          >
            <tab.icon size={15} />
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>

      {/* ── MAIN CONTENT ───────────────────────────── */}
      <main className={`crm-main ${activeTab === 'leads' ? 'flex flex-col overflow-hidden' : activeTab === 'opportunities' ? 'flex flex-col overflow-hidden' : 'overflow-auto'}`}>
        {activeTab === 'leads' ? (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden crm-content-pad overflow-y-auto">
            {leadsViewContent}
          </div>
        ) : activeTab === 'opportunities' ? (
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto crm-content-pad">
            <OpportunitiesView
              currentUser={currentUser}
              onNewOpportunity={() => setShowOpportunityModal(true)}
              salesStages={salesStages}
              fetchOpportunityById={fetchOpportunityById}
              sortField={oppSortField}
              setSortField={setOppSortField}
              sortDirection={oppSortDirection}
              setSortDirection={setOppSortDirection}
              currentPage={oppCurrentPage}
              setCurrentPage={setOppCurrentPage}
              showMyOppsOnly={showMyOppsOnly}
              setShowMyOppsOnly={setShowMyOppsOnly}
              selectedOwners={oppSelectedOwners}
              setSelectedOwners={setOppSelectedOwners}
              selectedGeographies={oppSelectedGeographies}
              setSelectedGeographies={setOppSelectedGeographies}
              selectedServiceLines={oppSelectedServiceLines}
              setSelectedServiceLines={setOppSelectedServiceLines}
              selectedSalesStages={oppSelectedSalesStages}
              setSelectedSalesStages={setOppSelectedSalesStages}
              selectedLeadRatings={oppSelectedLeadRatings}
              setSelectedLeadRatings={setOppSelectedLeadRatings}
              leads={leads}
              showReviewOnly={oppShowReviewOnly}
              setShowReviewOnly={setOppShowReviewOnly}
              searchOpp={oppSearchTerm}
              setSearchOpp={setOppSearchTerm}
              activeFilterType={oppActiveFilterType}
              setActiveFilterType={setOppActiveFilterType}
              expandedFilterOptions={oppExpandedFilterOptions}
              setExpandedFilterOptions={setOppExpandedFilterOptions}
              summaryStageFilter={oppSummaryStageFilter}
              setSummaryStageFilter={setOppSummaryStageFilter}
              opportunities={opportunities}
              setOpportunities={setOpportunities}
              tasks={tasks}
              setTasks={setTasks}
              setSelectedOpportunity={setSelectedOpportunity}
              setShowOpportunityModal={setShowOpportunityModal}
              users={users}
              geographies={geographies}
                            serviceLines={serviceLines}
              industries={industries}
              serviceOfferings={serviceOfferings}
              entities={entities}
              tags={tags}
              selectedTags={oppSelectedTags}
              setSelectedTags={setOppSelectedTags}
              customers={customers}
              setCustomers={setCustomers}
            />
          </div>
        ) : (
          <div className="w-full crm-content-pad">
            {activeTab === 'dashboard' && <DashboardView />}
            {activeTab === 'customers' && <CustomersView />}
            {activeTab === 'settings' && currentUser?.isAdmin && <SettingsPage />}
            {activeTab === 'calendar' && <CalendarView
              currentUser={currentUser}
              tasks={tasks}
              setTasks={setTasks}
              opportunities={opportunities}
              users={users}
                            serviceLines={serviceLines}
              salesStages={salesStages}
              geographies={geographies}
            />}
            {activeTab === 'copilot' && (
              <div className="h-[calc(100vh-130px)]">
                <AICopilot context={copilotContext} isVisible={true} onToggle={() => {}} />
              </div>
            )}
          </div>
        )}
      </main>

      {/* ── FOOTER ─────────────────────────────────── */}
      <footer className="crm-footer">
        <div className="crm-footer-inner">
          {/* Left — Powered by Pierian */}
          <div className="crm-footer-powered">
            <span className="crm-footer-powered-text">Powered by</span>
            <img src={pieLogoFooter} alt="Pierian" className="crm-footer-powered-logo" />
          </div>

          {/* Right — Social icons + copyright */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5">
              <a href="https://www.linkedin.com/company/pierag-consulting" target="_blank" rel="noopener noreferrer" className="crm-footer-link" title="LinkedIn">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://www.instagram.com/pieragconsulting/" target="_blank" rel="noopener noreferrer" className="crm-footer-link" title="Instagram">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="https://www.youtube.com/@PieragConsulting" target="_blank" rel="noopener noreferrer" className="crm-footer-link" title="YouTube">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
            <span className="crm-footer-copy">&copy; 2026 All rights reserved.</span>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {showICPWizard && (
        <ICPQualifierWizard
          onClose={() => setShowICPWizard(false)}
          onComplete={(payload) => {
            setPrefilledLead(payload);
            setShowICPWizard(false);
            setSelectedLead(null);
            setLeadViewMode(null);
            setShowLeadModal(true);
          }}
          industries={industries}
          geographies={geographies}
          leadSources={leadSources}
        />
      )}
      {showLeadModal && <LeadFormModal
        selectedLead={selectedLead} setSelectedLead={setSelectedLead}
        leadViewMode={leadViewMode} setLeadViewMode={setLeadViewMode}
        setShowLeadModal={setShowLeadModal} leads={leads} currentUser={currentUser}
        setActiveTab={setActiveTab} setSelectedOpportunity={setSelectedOpportunity}
        setShowOpportunityModal={setShowOpportunityModal}
        setConvertFromLead={setConvertFromLead} openLostModal={openLostModal}
        onDeleteLead={handleDeleteLeadAction} openDisqualifyModal={openDisqualifyModal}
        leadSources={leadSources} users={users} geographies={geographies}
        industries={industries} serviceLines={serviceLines} entities={entities} tags={tags}
        prefilledLead={prefilledLead}
        clearPrefilledLead={() => setPrefilledLead(null)}
      />}
      {/* Mark as Lost Modal */}
      {showLostModal && lostLeadTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) { setShowLostModal(false); setLostLeadTarget(null); } }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Mark Lead as Lost</h3>
            <p className="text-sm text-gray-500 mb-4">"{lostLeadTarget.companyName}" will be marked as Lost.</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <select
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian mb-4"
            >
              <option value="">Select a reason...</option>
              <option value="Budget Constraints">Budget Constraints</option>
              <option value="Chose Competitor">Chose Competitor</option>
              <option value="No Longer Needed">No Longer Needed</option>
              <option value="Non Responsive">Non Responsive</option>
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">Comments <span className="text-gray-400">(optional)</span></label>
            <textarea
              value={lostComments}
              onChange={(e) => setLostComments(e.target.value)}
              placeholder="Additional details about why this lead was lost..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian mb-4 resize-none"
            />

            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowLostModal(false); setLostLeadTarget(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={handleMarkLost} disabled={lostSaving || !lostReason}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50">
                {lostSaving ? 'Saving...' : 'Mark as Lost'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Disqualify Lead Modal */}
      {showDisqualifyModal && disqualifyLeadTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) { setShowDisqualifyModal(false); setDisqualifyLeadTarget(null); } }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Disqualify Lead</h3>
            <p className="text-sm text-gray-500 mb-4">"{disqualifyLeadTarget.companyName}" will be marked as Unqualified.</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <select
              value={disqualifyReason}
              onChange={(e) => setDisqualifyReason(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian mb-4"
            >
              <option value="">Select a reason...</option>
              <option value="Not a Good Fit">Not a Good Fit</option>
              <option value="No Budget / Authority">No Budget / Authority</option>
              <option value="Duplicate Lead">Duplicate Lead</option>
              <option value="Incomplete / Invalid Data">Incomplete / Invalid Data</option>
              <option value="Wrong Contact / Company">Wrong Contact / Company</option>
              <option value="Not in Target Market">Not in Target Market</option>
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">Comments <span className="text-gray-400">(optional)</span></label>
            <textarea
              value={disqualifyComments}
              onChange={(e) => setDisqualifyComments(e.target.value)}
              placeholder="Additional details about why this lead is being unqualified..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pierian focus:border-pierian mb-4 resize-none"
            />

            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowDisqualifyModal(false); setDisqualifyLeadTarget(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={handleDisqualifyLeadAction} disabled={disqualifySaving || !disqualifyReason}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50">
                {disqualifySaving ? 'Saving...' : 'Disqualify'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showOpportunityModal && <OpportunityFormModal
        selectedOpportunity={selectedOpportunity}
        setSelectedOpportunity={setSelectedOpportunity}
        opportunities={opportunities}
        setOpportunities={setOpportunities}
        users={users}
        setShowOpportunityModal={setShowOpportunityModal}
        geographies={geographies}
                serviceLines={serviceLines}
        industries={industries}
        salesStages={salesStages}
        serviceOfferings={serviceOfferings}
        entities={entities}
        tags={tags}
        convertFromLead={convertFromLead}
        setConvertFromLead={setConvertFromLead}
        currentUser={currentUser}
      />}
      {showCustomerModal && <CustomerFormModal
        serviceLines={serviceLines}
        geographies={geographies}
        customers={customers}
        setCustomers={setCustomers}
        setShowCustomerModal={setShowCustomerModal}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        users={users}
      />}
      {showUserModal && <UserFormModal />}
      {showHelpModal && <HelpModal onClose={() => setShowHelpModal(false)} />}

      {showMyProfileModal && (() => {
        const profileUser = users.find((u: any) => u.email === currentUser?.email || u.name === currentUser?.name) || {};
        const initials = currentUser?.name ? currentUser.name.split(' ').map((n: string) => n[0]).join('') : 'U';
        const fields = [
          { label: 'Full Name', value: currentUser?.name || '-' },
          { label: 'Email', value: currentUser?.email || '-' },
          { label: 'Role', value: currentUser?.role || (profileUser as any).role || '-' },
          { label: 'Department', value: (profileUser as any).department || '-' },
          { label: 'Phone', value: (profileUser as any).phone || '-' },
          { label: 'Team', value: Array.isArray((profileUser as any).team) ? (profileUser as any).team.join(', ') : ((profileUser as any).team || '-') },
          { label: 'Geography', value: currentUser?.geographies?.join(', ') || '-' },
          { label: 'Manager', value: (profileUser as any).managerName || '-' },
          { label: 'Join Date', value: (profileUser as any).joinDate ? new Date((profileUser as any).joinDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-' },
          { label: 'Status', value: (profileUser as any).status || 'Active' },
        ];
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl overflow-hidden">
              <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">My Profile</h3>
                <button onClick={() => setShowMyProfileModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
              <div className="px-5 py-5">
                <div className="flex items-center gap-4 mb-5 pb-4 border-b border-gray-100">
                  {currentUser?.photoUrl ? (
                    <img src={currentUser.photoUrl} alt={currentUser.name} className="w-16 h-16 rounded-full object-cover border-2 border-teal-200" />
                  ) : (
                    <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#00BBA2' }}>
                      <span className="text-white text-xl font-bold">{initials}</span>
                    </div>
                  )}
                  <div>
                    <p className="text-base font-semibold text-gray-900">{currentUser?.name || 'CRM User'}</p>
                    <p className="text-xs text-gray-500">{currentUser?.email || ''}</p>
                    <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,187,162,0.12)', color: '#00BBA2' }}>
                      {currentUser?.role || 'Sales'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  {fields.map(f => (
                    <div key={f.label}>
                      <p className="text-xs text-gray-400 mb-0.5">{f.label}</p>
                      <p className="text-sm font-medium text-gray-800 truncate" title={f.value}>{f.value}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setShowMyProfileModal(false)}
                  className="px-4 py-1.5 text-sm font-medium rounded-md text-white"
                  style={{ background: '#00BBA2' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      </div>
  );
}
